import React, { useEffect, useCallback, useState, useMemo, useRef, useLayoutEffect, useContext, useImperativeHandle } from 'react';
import { ThemeProvider, useTheme } from 'emotion-theming';
import { jsx, Global, css } from '@emotion/core';
import Color$1 from 'color';
import { motion, useMotionValue, useTransform, animate, AnimatePresence, useElementScroll, AnimateSharedLayout, useAnimation } from 'framer-motion';
import ReactCountryFlag from 'react-country-flag';
import fastDeepEqual from 'fast-deep-equal';
import useResizeObserver from 'use-resize-observer';
import ContentLoader from 'react-content-loader';
import InfiniteScroll from 'react-infinite-scroller';
import { Page as Page$1, AnimatePresence as AnimatePresence$1 } from 'framer';
import { HexColorPicker } from 'react-colorful';
import { EyeDropper } from 'react-eyedrop';
import { useDropzone } from 'react-dropzone';
import InfiniteScroll$1 from 'react-infinite-scroll-component';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : new P(function (resolve) { resolve(result.value); }).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

function __generator(thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
}

function __spreadArrays() {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
}
function __makeTemplateObject(cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
}

var computeColorSwatch = function (hexValue) {
  var HSL = Color$1(hexValue).hsl();
  var h = Math.round(HSL.array()[0]);
  var s = Math.round(HSL.array()[1]);
  var l = Math.round(HSL.array()[2]);
  var baseColor = Color$1.hsl([h, s, l]).toString();
  var hslColors = computeHSLColors(h, s, computeLScale(l));
  return mapColorsToKeys(hslColors, baseColor);
};
var computeHSLColors = function (h, s, lScaleValues) {
  var hslColors = lScaleValues.map(function (l) {
    var hslColor = Color$1.hsl([h, s, l]).toString();
    return hslColor;
  });
  return hslColors;
};
var computeLScale = function (l) {
  var lastDigit = l % 10;
  var scaleLimit = lastDigit === 0 ? 10 : 9;
  var scaleValues = [1];
  var counter = 0;
  while (counter < scaleLimit) {
    counter++;
    var scaleValue = counter * 10 + lastDigit;
    scaleValues.push(scaleValue);
  }
  return scaleValues;
};
var mapColorsToKeys = function (hslColors, baseColor) {
  var colorSwatchObject = {
    base: ''
  };
  hslColors.forEach(function (hslColor, index) {
    if (baseColor === hslColor) {
      colorSwatchObject['base'] = hslColor;
      colorSwatchObject["_" + (index + 1) * 100] = hslColor;
    } else {
      colorSwatchObject["_" + (index + 1) * 100] = hslColor;
    }
  });
  return colorSwatchObject;
};

// EXTERNAL VALUES
var PINK$1 = '#FF04A0';
var LIGHT_BLUE$3 = '#02C3E2';
var GREEN$9 = '#44D5A4';
var YELLOW$8 = '#F4E467';
var RED$8 = '#F26666';
// TODO change ACCENT
var ACCENT$2 = '#FFF';
var WHITE$9 = '#FFF';
var BLUE$8 = '#32abe4';
var PURPLE$9 = '#703a96';
var pinkSwatch$1 = computeColorSwatch(PINK$1);
var brightBlueSwatch$2 = computeColorSwatch(LIGHT_BLUE$3);
var greenSwatch$9 = computeColorSwatch(GREEN$9);
var yellowSwatch$9 = computeColorSwatch(YELLOW$8);
var redSwatch$9 = computeColorSwatch(RED$8);
var accentSwatch$2 = computeColorSwatch(ACCENT$2);
var blueSwatch$8 = computeColorSwatch(BLUE$8);
var purpleSwatch$9 = computeColorSwatch(PURPLE$9);
var lightGray$2 = '#f5f6f7';
var colors$j = {
  pinkSwatch: pinkSwatch$1,
  brightBlueSwatch: brightBlueSwatch$2,
  greenSwatch: greenSwatch$9,
  redSwatch: redSwatch$9,
  yellowSwatch: yellowSwatch$9,
  accentSwatch: accentSwatch$2,
  WHITE: WHITE$9,
  blueSwatch: blueSwatch$8,
  purpleSwatch: purpleSwatch$9,
  lightGray: lightGray$2
};

var radius$1 = {
  none: '0px',
  small: '6px',
  smallMedium: '8px',
  medium: '12px',
  mediumLarge: '16px',
  large: '28px',
  circle: '1000rem'
};

var SHADOW_DEFAULT_COLOR$1 = '#000000';
var shadows = {
  dark: [0, 16, 32, 0],
  mediumDark: [0, 8, 16, 0],
  mediumLight: [0, 4, 8, 0],
  light: [0, 2, 4, 0],
  xLight: [0, 18, 40, -12]
};

var borders$b = {
  xSmall: 1,
  small: 1.5,
  medium: 2,
  large: 2.5,
  xLarge: 3
};

var BASE_VALUE$b = 0.5;
var BASE_UNITS$b = 'rem';

var mediaQuerySizes$1 = {
  small: 576,
  medium: 768,
  large: 992,
  xLarge: 1200
};

var BASE_VALUE$a = 1;
var BASE_UNITS$a = 'rem';
// Rename to typography
var computeFontSize$9 = function (multiplier) {
  return BASE_VALUE$a * multiplier;
};
var XSMALL_FONT$9 = computeFontSize$9(0.75) + BASE_UNITS$a;
var SMALL_FONT$9 = computeFontSize$9(0.875) + BASE_UNITS$a;
var MEDIUM_FONT$9 = computeFontSize$9(1) + BASE_UNITS$a;
var MEDIUM_PLUS_FONT$9 = computeFontSize$9(1.125) + BASE_UNITS$a;
var LARGE_FONT$9 = computeFontSize$9(1.5) + BASE_UNITS$a;
var XLARGE_FONT$9 = computeFontSize$9(2) + BASE_UNITS$a;
var XXLARGE_FONT$9 = computeFontSize$9(4) + BASE_UNITS$a;
var XSMALL_LINE_HEIGHT$9 = computeFontSize$9(1.5) + BASE_UNITS$a;
var SMALL_LINE_HEIGHT$9 = computeFontSize$9(1.75) + BASE_UNITS$a;
var MEDIUM_LINE_HEIGHT$9 = computeFontSize$9(2) + BASE_UNITS$a;
var LARGE_LINE_HEIGHT$9 = computeFontSize$9(2.5) + BASE_UNITS$a;
var XLARGE_LINE_HEIGHT$9 = computeFontSize$9(3) + BASE_UNITS$a;
var XXLARGE_LINE_HEIGHT$9 = computeFontSize$9(3.5) + BASE_UNITS$a;
var HEADING_FONT_FAMILY$9 = "'Montserrat', sans-serif";
var BODY_FONT_FAMILY$9 = "'Montserrat', sans-serif";
var HEADING_FONT_IMPORT$9 = 'https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&display=swap';
var BODY_FONT_IMPORT$9 = 'https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&display=swap';
var WEIGHT_THIN$9 = 300;
var WEIGHT_REGULAR$9 = 400;
var WEIGHT_SEMI_BOLD$9 = 500;
var WEIGHT_BOLD$9 = 600;
var WEIGHT_EXTRA_BOLD$9 = 800;

var icons$b = {
  xSmall: 18,
  small: 24,
  medium: 32,
  large: 64,
  xLarge: 88
};
var sphere$b = {
  xSmall: 64,
  small: 80,
  medium: 96,
  large: 104,
  xLarge: 128
};

var computeShadow = function (shadow, color, reverseOffset) {
  var shadowArray = typeof shadow === 'string' ? shadows[shadow] : shadow;
  var formattedShadow = shadowArray.map(function (item, index) {
    if (index === 1 && reverseOffset) {
      return "-" + item + "px";
    }
    return item + "px";
  }).toString().split(',').join(' ');
  return formattedShadow + " " + color;
};

var computeGrayScale = function (swatch) {
  var grayScale = {
    _100: undefined,
    _200: undefined,
    _300: undefined,
    _400: undefined,
    _500: undefined,
    _600: undefined,
    _700: undefined,
    _800: undefined,
    _900: undefined,
    _1000: undefined,
    base: ''
  };
  Object.keys(swatch).forEach(function (key) {
    return grayScale[key] = Color$1(swatch[key]).grayscale().hsl().toString();
  });
  return grayScale;
};

var colors$i = {
  primaryBrand: colors$j.pinkSwatch,
  secondaryBrand: colors$j.brightBlueSwatch,
  accent: colors$j.accentSwatch,
  success: colors$j.greenSwatch,
  warning: colors$j.yellowSwatch,
  danger: colors$j.redSwatch,
  background: colors$j.lightGray,
  foreground: colors$j.WHITE,
  grayScale: computeGrayScale(colors$j.pinkSwatch),
  blueScale: colors$j.blueSwatch,
  purpleScale: colors$j.purpleSwatch,
  white: colors$j.WHITE,
  defaultShadowColor: SHADOW_DEFAULT_COLOR$1
};
var concepts$9 = {
  colors: __assign({}, colors$i),
  radius: {
    none: radius$1.none,
    small: radius$1.small,
    medium: radius$1.mediumLarge,
    default: radius$1.medium,
    large: radius$1.large,
    circle: radius$1.circle
  },
  shadows: {
    light: computeShadow('light', Color$1(colors$i.defaultShadowColor).rgb().alpha(0.06).toString()),
    mediumLight: computeShadow('mediumLight', Color$1(colors$i.defaultShadowColor).rgb().alpha(0.08).toString()),
    mediumDark: computeShadow('mediumDark', Color$1(colors$i.defaultShadowColor).rgb().alpha(0.1).toString()),
    dark: computeShadow('dark', Color$1(colors$i.defaultShadowColor).rgb().alpha(0.12).toString()),
    generate: computeShadow
  },
  typography: {
    imports: {
      body: BODY_FONT_IMPORT$9,
      heading: HEADING_FONT_IMPORT$9
    },
    colors: {
      primaryHeading: colors$i.grayScale._200,
      primaryBody: colors$i.grayScale.base,
      inverseHeading: colors$i.white,
      inverseBody: colors$i.white
    },
    families: {
      heading: BODY_FONT_FAMILY$9,
      body: HEADING_FONT_FAMILY$9
    },
    sizes: {
      xSmall: XSMALL_FONT$9,
      small: SMALL_FONT$9,
      medium: MEDIUM_FONT$9,
      mediumPlus: MEDIUM_PLUS_FONT$9,
      large: LARGE_FONT$9,
      xLarge: XLARGE_FONT$9,
      xxLarge: XXLARGE_FONT$9
    },
    weights: {
      thin: WEIGHT_THIN$9,
      regular: WEIGHT_REGULAR$9,
      semiBold: WEIGHT_SEMI_BOLD$9,
      bold: WEIGHT_BOLD$9,
      extraBold: WEIGHT_EXTRA_BOLD$9
    },
    lineHeight: {
      xSmall: XSMALL_LINE_HEIGHT$9,
      small: SMALL_LINE_HEIGHT$9,
      medium: MEDIUM_LINE_HEIGHT$9,
      large: LARGE_LINE_HEIGHT$9,
      xLarge: XLARGE_LINE_HEIGHT$9,
      xxLarge: XXLARGE_LINE_HEIGHT$9
    }
  },
  borders: {
    thin: borders$b.xSmall,
    small: borders$b.small,
    default: borders$b.medium,
    large: borders$b.large,
    bold: borders$b.xLarge
  },
  spacing: {
    baseValue: BASE_VALUE$b,
    baseUnits: BASE_UNITS$b
  },
  mediaQueries: {
    small: mediaQuerySizes$1.small,
    medium: mediaQuerySizes$1.medium,
    large: mediaQuerySizes$1.large,
    xLarge: mediaQuerySizes$1.xLarge
  },
  sizing: {
    icons: {
      xSmall: icons$b.xSmall,
      small: icons$b.small,
      medium: icons$b.medium,
      large: icons$b.large,
      xLarge: icons$b.xLarge
    },
    sphere: {
      xSmall: sphere$b.xSmall,
      small: sphere$b.small,
      medium: sphere$b.medium,
      large: sphere$b.large,
      xLarge: sphere$b.xLarge
    }
  }
};

var computeSpace = function (baseValue, baseUnits) {
  return function (multiplier) {
    return baseValue * multiplier + baseUnits;
  };
};

var space$9 = computeSpace(concepts$9.spacing.baseValue, concepts$9.spacing.baseUnits);
var spacing$9 = {
  x0: space$9(0),
  x_25: space$9(0.25),
  x_5: space$9(0.5),
  x_75: space$9(0.75),
  x1: space$9(1),
  x1_5: space$9(1.5),
  x2: space$9(2),
  x2_5: space$9(2.5),
  x3: space$9(3),
  x4: space$9(4),
  x5: space$9(5),
  x6: space$9(6),
  x7: space$9(7),
  x8: space$9(8),
  x9: space$9(9)
};
var borders$a = {
  thin: concepts$9.borders.thin + "px",
  default: concepts$9.borders.default + "px",
  bold: concepts$9.borders.bold + "px"
};
var text$9 = {
  imports: {
    body: concepts$9.typography.imports.body,
    heading: concepts$9.typography.imports.heading
  },
  families: {
    body: concepts$9.typography.families.body,
    heading: concepts$9.typography.families.heading
  },
  types: {
    heroTitle: {
      color: concepts$9.typography.colors.primaryHeading,
      size: concepts$9.typography.sizes.xxLarge,
      fontWeight: concepts$9.typography.weights.extraBold,
      lineHeight: concepts$9.typography.lineHeight.xxLarge,
      fontFamily: concepts$9.typography.families.heading
    },
    title: {
      color: concepts$9.typography.colors.primaryHeading,
      size: concepts$9.typography.sizes.xLarge,
      fontWeight: concepts$9.typography.weights.bold,
      lineHeight: concepts$9.typography.lineHeight.xLarge,
      fontFamily: concepts$9.typography.families.heading
    },
    subtitle: {
      color: concepts$9.typography.colors.primaryHeading,
      size: concepts$9.typography.sizes.large,
      fontWeight: concepts$9.typography.weights.regular,
      lineHeight: concepts$9.typography.lineHeight.medium,
      fontFamily: concepts$9.typography.families.heading
    },
    largeBody: {
      color: concepts$9.typography.colors.primaryBody,
      size: concepts$9.typography.sizes.mediumPlus,
      fontWeight: concepts$9.typography.weights.regular,
      lineHeight: concepts$9.typography.lineHeight.small,
      fontFamily: concepts$9.typography.families.body
    },
    body: {
      color: concepts$9.typography.colors.primaryBody,
      size: concepts$9.typography.sizes.medium,
      fontWeight: concepts$9.typography.weights.regular,
      lineHeight: concepts$9.typography.lineHeight.small,
      fontFamily: concepts$9.typography.families.body
    },
    caption: {
      color: concepts$9.typography.colors.primaryBody,
      size: concepts$9.typography.sizes.small,
      fontWeight: concepts$9.typography.weights.regular,
      lineHeight: concepts$9.typography.lineHeight.small,
      fontFamily: concepts$9.typography.families.body
    },
    footnote: {
      color: concepts$9.typography.colors.primaryBody,
      size: concepts$9.typography.sizes.xSmall,
      fontWeight: concepts$9.typography.weights.regular,
      lineHeight: concepts$9.typography.lineHeight.xSmall,
      fontFamily: concepts$9.typography.families.body
    }
  },
  colors: {
    primaryBrand: concepts$9.colors.primaryBrand.base,
    secondaryBrand: concepts$9.colors.secondaryBrand.base,
    primaryHeading: concepts$9.typography.colors.primaryHeading,
    primaryBody: concepts$9.typography.colors.primaryBody,
    inverseHeading: concepts$9.typography.colors.inverseHeading,
    inverseBody: concepts$9.typography.colors.inverseBody,
    accent: concepts$9.colors.accent.base,
    success: concepts$9.colors.success.base,
    warning: concepts$9.colors.warning.base,
    danger: concepts$9.colors.danger.base,
    anchorBlue: concepts$9.colors.blueScale.base
  },
  weights: {
    thin: concepts$9.typography.weights.thin,
    regular: concepts$9.typography.weights.regular,
    semiBold: concepts$9.typography.weights.semiBold,
    bold: concepts$9.typography.weights.bold,
    extraBold: concepts$9.typography.weights.extraBold
  }
};
var mediaQueries$d = {
  queries: {
    xSmall: "@media screen and (min-width: 0px)",
    // Large Mobile
    small: "@media screen and (min-width: " + concepts$9.mediaQueries.small + "px)",
    // Tablet
    medium: "@media screen and (min-width: " + concepts$9.mediaQueries.medium + "px)",
    // Large Tablet
    large: "@media screen and (min-width: " + concepts$9.mediaQueries.large + "px)",
    // Desktop
    xLarge: "@media screen and (min-width: " + concepts$9.mediaQueries.xLarge + "px)"
  },
  rawValues: {
    xSmall: 0,
    small: concepts$9.mediaQueries.small,
    medium: concepts$9.mediaQueries.medium,
    large: concepts$9.mediaQueries.large,
    xLarge: concepts$9.mediaQueries.xLarge
  }
};
var hocs$9 = {
  colors: {
    swatches: {
      primaryBrand: concepts$9.colors.primaryBrand,
      secondaryBrand: concepts$9.colors.secondaryBrand,
      success: concepts$9.colors.success,
      danger: concepts$9.colors.danger,
      warning: concepts$9.colors.warning,
      accent: concepts$9.colors.accent,
      grayScale: concepts$9.colors.grayScale,
      blueScale: concepts$9.colors.blueScale,
      purpleScale: concepts$9.colors.purpleScale
    },
    background: concepts$9.colors.background,
    foreground: concepts$9.colors.foreground
  },
  textColors: text$9.colors,
  spacing: spacing$9,
  borderRadius: concepts$9.radius,
  shadows: concepts$9.shadows,
  borders: borders$a
};
// TODO - revisit buttons for contrast and font color
var buttons$9 = {
  primary: {
    backgroundColor: concepts$9.colors.primaryBrand.base,
    fontColor: concepts$9.typography.colors.inverseBody,
    fontFamily: concepts$9.typography.families.body
  },
  secondary: {
    backgroundColor: concepts$9.colors.secondaryBrand.base,
    fontColor: concepts$9.typography.colors.inverseBody,
    fontFamily: concepts$9.typography.families.body
  },
  warning: {
    backgroundColor: concepts$9.colors.warning.base,
    fontColor: concepts$9.typography.colors.inverseBody,
    fontFamily: concepts$9.typography.families.body
  },
  success: {
    backgroundColor: concepts$9.colors.success.base,
    fontColor: concepts$9.typography.colors.inverseBody,
    fontFamily: concepts$9.typography.families.body
  },
  danger: {
    backgroundColor: concepts$9.colors.danger.base,
    fontColor: concepts$9.typography.colors.inverseBody,
    fontFamily: concepts$9.typography.families.body
  },
  shadow: {
    backgroundColor: concepts$9.colors.foreground,
    fontColor: concepts$9.typography.colors.primaryBody,
    fontFamily: concepts$9.typography.families.body
  },
  accent: {
    backgroundColor: concepts$9.colors.accent.base,
    fontColor: concepts$9.typography.colors.primaryBody,
    fontFamily: concepts$9.typography.families.body
  },
  smoke: {
    backgroundColor: concepts$9.colors.background,
    fontColor: concepts$9.typography.colors.primaryBody,
    fontFamily: concepts$9.typography.families.body
  },
  boxShadow: concepts$9.shadows.generate,
  borderSize: borders$a.default,
  outlinedBackgroundColor: concepts$9.colors.foreground,
  fontWeight: concepts$9.typography.weights.semiBold,
  sizes: {
    xSmall: {
      inset: spacing$9.x1 + " " + spacing$9.x2,
      outlinedInset: {
        vertical: "calc(" + spacing$9.x1 + " - 2px)",
        horizontal: "calc(" + spacing$9.x2 + " - 2px)"
      },
      fontSize: text$9.types.footnote.size,
      borderRadius: concepts$9.radius.default
    },
    small: {
      inset: spacing$9.x1_5 + " " + spacing$9.x2_5,
      outlinedInset: {
        vertical: "calc(" + spacing$9.x1_5 + " - 2px)",
        horizontal: "calc(" + spacing$9.x2_5 + " - 2px)"
      },
      fontSize: text$9.types.caption.size,
      borderRadius: concepts$9.radius.default
    },
    medium: {
      inset: spacing$9.x1_5 + " " + spacing$9.x3,
      outlinedInset: {
        vertical: "calc(" + spacing$9.x1_5 + " - 2px)",
        horizontal: "calc(" + spacing$9.x3 + " - 2px)"
      },
      fontSize: text$9.types.body.size,
      borderRadius: concepts$9.radius.default
    },
    large: {
      inset: spacing$9.x2 + " " + spacing$9.x4,
      outlinedInset: {
        vertical: "calc(" + spacing$9.x2 + " - 2px)",
        horizontal: "calc(" + spacing$9.x4 + " - 2px)"
      },
      fontSize: text$9.types.largeBody.size,
      borderRadius: concepts$9.radius.default
    },
    xLarge: {
      inset: spacing$9.x3 + " " + spacing$9.x5,
      outlinedInset: {
        vertical: "calc(" + spacing$9.x3 + " - 2px)",
        horizontal: "calc(" + spacing$9.x5 + " - 2px)"
      },
      fontSize: text$9.types.subtitle.size,
      borderRadius: concepts$9.radius.default
    }
  }
};
var inputs$9 = {
  checkbox: {
    border: borders$a.default,
    borderRadius: concepts$9.radius.small,
    unCheckedBackgroundColor: concepts$9.colors.grayScale._1000,
    checkmarkColor: concepts$9.typography.colors.inverseBody,
    labelColor: concepts$9.typography.colors.primaryBody
  },
  text: {
    borderRadius: concepts$9.radius.default,
    labelColor: concepts$9.colors.accent,
    placeholderColor: concepts$9.typography.colors.primaryBody,
    labelFontSize: text$9.types.caption.size,
    textFontSize: text$9.types.caption.size,
    borderWidth: borders$a.default,
    defaultFocusColor: concepts$9.colors.blueScale.base
  },
  select: {
    dropDownShadow: concepts$9.shadows.mediumDark,
    dropDownBackgroundColor: concepts$9.colors.foreground,
    boxShadow: concepts$9.shadows.light,
    borderRadius: concepts$9.radius.default
  },
  number: {
    backgroundColor: concepts$9.colors.grayScale._200,
    fontColor: concepts$9.typography.colors.inverseBody,
    fontSize: text$9.types.body.size,
    borderRadius: concepts$9.radius.default
  },
  backgroundColor: concepts$9.colors.background,
  fontSize: text$9.types.caption.size,
  fontColor: concepts$9.typography.colors.primaryBody,
  fontFamily: concepts$9.typography.families.body,
  colors: {
    primary: concepts$9.colors.primaryBrand.base,
    secondary: concepts$9.colors.secondaryBrand.base,
    success: concepts$9.colors.success.base,
    warning: concepts$9.colors.warning.base,
    danger: concepts$9.colors.danger.base,
    accent: concepts$9.colors.accent.base,
    dark: concepts$9.colors.primaryBrand._100,
    blue: concepts$9.colors.blueScale.base
  }
};
var icons$a = {
  sizes: {
    xSmall: concepts$9.sizing.icons.xSmall,
    small: concepts$9.sizing.icons.small,
    medium: concepts$9.sizing.icons.medium,
    large: concepts$9.sizing.icons.large,
    xLarge: concepts$9.sizing.icons.xLarge
  },
  colors: {
    default: concepts$9.colors.grayScale._800,
    primaryBodyText: concepts$9.typography.colors.primaryBody,
    primaryHeadingText: concepts$9.typography.colors.primaryHeading,
    inverseBodyText: concepts$9.typography.colors.inverseBody,
    inverseHeadingText: concepts$9.typography.colors.inverseHeading,
    primaryBrand: concepts$9.colors.primaryBrand.base,
    secondaryBrand: concepts$9.colors.secondaryBrand.base,
    warning: concepts$9.colors.warning.base,
    danger: concepts$9.colors.danger.base,
    success: concepts$9.colors.success.base,
    background: concepts$9.colors.background,
    accent: concepts$9.colors.background
  }
};
var animatedCheckmark$9 = {
  sizeMap: {
    xSmall: concepts$9.sizing.icons.xSmall,
    small: concepts$9.sizing.icons.small,
    medium: concepts$9.sizing.icons.medium,
    large: concepts$9.sizing.icons.large,
    xLarge: concepts$9.sizing.icons.xLarge
  },
  colors: {
    primary: concepts$9.colors.primaryBrand.base,
    secondary: concepts$9.colors.secondaryBrand.base,
    success: concepts$9.colors.success.base,
    accent: concepts$9.colors.accent.base
  },
  strokeWidth: {
    small: concepts$9.borders.small,
    default: concepts$9.borders.default,
    large: concepts$9.borders.large
  },
  borderRadius: concepts$9.radius.circle,
  checkmarkFilledColor: concepts$9.colors.foreground
};
var avatar$9 = {
  borderWidth: borders$a.thin,
  borderColor: concepts$9.colors.grayScale._1000,
  borderRadius: concepts$9.radius.circle,
  boxShadow: concepts$9.shadows.mediumLight,
  backgroundColor: concepts$9.colors.foreground,
  bubbleBackgroundColors: {
    isActive: concepts$9.colors.success._600,
    default: concepts$9.colors.grayScale._1000
  },
  defaultBackgroundColors: {
    primary: concepts$9.colors.primaryBrand.base,
    secondary: concepts$9.colors.secondaryBrand.base,
    success: concepts$9.colors.success.base,
    accent: concepts$9.colors.accent.base,
    warning: concepts$9.colors.warning.base,
    danger: concepts$9.colors.danger.base
  },
  sizes: {
    xSmall: {
      avatarSize: 34,
      bubbleLeftPosition: 22,
      bubbleSize: 12
    },
    small: {
      avatarSize: 44,
      bubbleLeftPosition: 28,
      bubbleSize: 14
    },
    medium: {
      avatarSize: 60,
      bubbleLeftPosition: 40,
      bubbleSize: 16
    },
    large: {
      avatarSize: 76,
      bubbleLeftPosition: 52,
      bubbleSize: 20
    },
    xLarge: {
      avatarSize: 96,
      bubbleLeftPosition: 64,
      bubbleSize: 28
    }
  }
};
var loadingSpinner$9 = {
  size: {
    xSmall: concepts$9.sizing.icons.xSmall,
    small: concepts$9.sizing.icons.small,
    medium: concepts$9.sizing.icons.medium,
    large: concepts$9.sizing.icons.large,
    xLarge: concepts$9.sizing.icons.xLarge
  },
  colors: {
    primary: concepts$9.colors.primaryBrand.base,
    secondary: concepts$9.colors.secondaryBrand.base,
    success: concepts$9.colors.success.base,
    warning: concepts$9.colors.warning.base,
    danger: concepts$9.colors.danger.base
  }
};
var separator$9 = {
  border: borders$a.default,
  color: concepts$9.colors.grayScale._1000
};
var card$9 = {
  backgroundColor: concepts$9.colors.warning.base,
  borderRadius: concepts$9.radius.default,
  boxShadow: concepts$9.shadows.mediumDark,
  defaultInset: spacing$9.x4 + " " + spacing$9.x2 + " " + spacing$9.x2 + " " + spacing$9.x2
};
var dialog$9 = {
  backgroundColor: concepts$9.colors.foreground,
  boxShadow: concepts$9.shadows.mediumDark,
  defaultInset: spacing$9.x6 + " " + spacing$9.x4 + " " + spacing$9.x4 + " " + spacing$9.x4
};
var drawer$9 = {
  backgroundColor: concepts$9.colors.foreground,
  borderRadius: concepts$9.radius.small,
  boxShadow: concepts$9.shadows.mediumLight,
  bottomDirectionShadow: concepts$9.shadows.generate('mediumLight', Color$1(concepts$9.colors.defaultShadowColor).rgb().alpha(0.1).toString(), true),
  hoverBoxShadow: concepts$9.shadows.mediumDark,
  hoverBottomDirectionShadow: concepts$9.shadows.generate('mediumDark', Color$1(concepts$9.colors.defaultShadowColor).rgb().alpha(0.1).toString(), true),
  defaultInset: "" + spacing$9.x1,
  tab: {
    dark: {
      backgroundColor: concepts$9.colors.grayScale._600,
      arrowColor: concepts$9.colors.foreground
    },
    light: {
      backgroundColor: concepts$9.colors.foreground,
      arrowColor: icons$a.colors.default
    }
  }
};
var navigation$9 = {
  backgroundColor: concepts$9.colors.foreground,
  border: borders$a.thin,
  borderColor: concepts$9.colors.grayScale._1000
};
var sheet$9 = {
  mobileBorderRadius: concepts$9.radius.large,
  desktopBorderRadius: concepts$9.radius.default,
  backgroundColor: concepts$9.colors.foreground,
  boxShadow: concepts$9.shadows.mediumDark
};
var slider$9 = {
  backgroundColor: concepts$9.colors.background,
  borderRadius: concepts$9.radius.small,
  handleRadius: concepts$9.radius.circle,
  handleColor: concepts$9.colors.foreground,
  handleShadow: concepts$9.shadows.light
};
var toggle$9 = {
  isActiveColor: concepts$9.colors.success.base,
  isNotActiveColor: concepts$9.colors.grayScale._900
};
var tooltip$9 = {
  lightBackgroundColor: concepts$9.colors.foreground,
  darkBackgroundColor: concepts$9.colors.grayScale._100,
  boxShadow: concepts$9.shadows.mediumLight,
  borderColor: concepts$9.colors.background
};
var colorPicker$9 = {
  backgroundColor: concepts$9.colors.background,
  borderColor: 'transparent',
  boxShadow: concepts$9.shadows.mediumLight,
  borderRadius: concepts$9.radius.small
};
var sphere$a = {
  xSmall: {
    circumference: concepts$9.sizing.sphere.xSmall,
    textType: 'title'
  },
  small: {
    circumference: concepts$9.sizing.sphere.small,
    textType: 'title'
  },
  medium: {
    circumference: concepts$9.sizing.sphere.medium,
    textType: 'heroTitle'
  },
  large: {
    circumference: concepts$9.sizing.sphere.large,
    textType: 'heroTitle'
  },
  xLarge: {
    circumference: concepts$9.sizing.sphere.xLarge,
    textType: 'heroTitle'
  }
};
var widget$9 = {
  primary: {
    backgroundColor: concepts$9.colors.primaryBrand.base,
    fontColor: 'inverseBody'
  },
  secondary: {
    backgroundColor: concepts$9.colors.secondaryBrand.base,
    fontColor: 'inverseBody'
  },
  warning: {
    backgroundColor: concepts$9.colors.warning.base,
    fontColor: 'primaryBody'
  },
  success: {
    backgroundColor: concepts$9.colors.success.base,
    fontColor: 'inverseBody'
  },
  danger: {
    backgroundColor: concepts$9.colors.danger.base,
    fontColor: 'primaryBody'
  },
  shadow: {
    backgroundColor: concepts$9.colors.foreground,
    fontColor: 'primaryBody'
  },
  accent: {
    backgroundColor: concepts$9.colors.accent.base,
    fontColor: 'primaryBody'
  }
};
var colorOption$9 = {
  defaults: {
    circleFill: concepts$9.shadows.generate([0, 0, 6, 1], Color$1(concepts$9.colors.defaultShadowColor).rgb().alpha(0.1).toString()),
    innerRing: concepts$9.shadows.generate([0, 0, 0, 11], Color$1([20, 30, 50]).rgb().alpha(0.03).toString()),
    outerRing: concepts$9.shadows.generate([0, 0, 6, 1], Color$1(concepts$9.colors.defaultShadowColor).rgb().alpha(0.06).toString())
  }
};
var entities$9 = {
  mediaQueries: mediaQueries$d,
  hocs: hocs$9,
  text: text$9,
  spacing: spacing$9,
  buttons: buttons$9,
  inputs: inputs$9,
  icons: icons$a,
  card: card$9,
  separator: separator$9,
  loadingSpinner: loadingSpinner$9,
  animatedCheckmark: animatedCheckmark$9,
  avatar: avatar$9,
  dialog: dialog$9,
  drawer: drawer$9,
  sheet: sheet$9,
  slider: slider$9,
  navigation: navigation$9,
  toggle: toggle$9,
  tooltip: tooltip$9,
  colorPicker: colorPicker$9,
  sphere: sphere$a,
  widget: widget$9,
  colorOption: colorOption$9
};

// TODO the current Sendogo theme has a base purple and orange, and then an alternative 'gradient' shade that we should calculate from the base instead
var PURPLE$8 = '#8D7EAE';
var ORANGE$1 = '#F36D2C';
var GREEN$8 = '#1DE6B9';
var YELLOW$7 = '#F7A14D';
var RED$7 = '#FB2E4B';
var ACCENT$1 = '#FFF';
var WHITE$8 = '#FFF';
var BLUE$7 = '#1DC5E6';
// const GREEN = '#1DE9B6' // This green is almost identical to DOGO_GREEN, probably don't need both
var purpleSwatch$8 = computeColorSwatch(PURPLE$8);
var orangeSwatch$1 = computeColorSwatch(ORANGE$1);
var greenSwatch$8 = computeColorSwatch(GREEN$8);
var yellowSwatch$8 = computeColorSwatch(YELLOW$7);
var redSwatch$8 = computeColorSwatch(RED$7);
var accentSwatch$1 = computeColorSwatch(ACCENT$1);
var blueSwatch$7 = computeColorSwatch(BLUE$7);
var lightGray$1 = '#EAEAEA';
var colors$h = {
  purpleSwatch: purpleSwatch$8,
  orangeSwatch: orangeSwatch$1,
  greenSwatch: greenSwatch$8,
  redSwatch: redSwatch$8,
  yellowSwatch: yellowSwatch$8,
  accentSwatch: accentSwatch$1,
  WHITE: WHITE$8,
  blueSwatch: blueSwatch$7,
  lightGray: lightGray$1
};

var radius = {
  none: '0px',
  small: '6px',
  smallMedium: '8px',
  medium: '12px',
  mediumLarge: '16px',
  large: '28px',
  circle: '1000rem'
};

var SHADOW_DEFAULT_COLOR = '#000000';

var borders$9 = {
  xSmall: 1,
  small: 1.5,
  medium: 2,
  large: 2.5,
  xLarge: 3
};

var BASE_VALUE$9 = 0.5;
var BASE_UNITS$9 = 'rem';

var mediaQuerySizes = {
  small: 576,
  medium: 768,
  large: 992,
  xLarge: 1200
};

var BASE_VALUE$8 = 1;
var BASE_UNITS$8 = 'rem';
// Rename to typography
var computeFontSize$8 = function (multiplier) {
  return BASE_VALUE$8 * multiplier;
};
var XSMALL_FONT$8 = computeFontSize$8(0.75) + BASE_UNITS$8;
var SMALL_FONT$8 = computeFontSize$8(0.875) + BASE_UNITS$8;
var MEDIUM_FONT$8 = computeFontSize$8(1) + BASE_UNITS$8;
var MEDIUM_PLUS_FONT$8 = computeFontSize$8(1.25) + BASE_UNITS$8;
var LARGE_FONT$8 = computeFontSize$8(1.75) + BASE_UNITS$8;
var XLARGE_FONT$8 = computeFontSize$8(2.125) + BASE_UNITS$8;
var XXLARGE_FONT$8 = computeFontSize$8(3) + BASE_UNITS$8;
var XSMALL_LINE_HEIGHT$8 = computeFontSize$8(1.5) + BASE_UNITS$8;
var SMALL_LINE_HEIGHT$8 = computeFontSize$8(1.75) + BASE_UNITS$8;
var MEDIUM_LINE_HEIGHT$8 = computeFontSize$8(2) + BASE_UNITS$8;
var LARGE_LINE_HEIGHT$8 = computeFontSize$8(2.5) + BASE_UNITS$8;
var XLARGE_LINE_HEIGHT$8 = computeFontSize$8(3) + BASE_UNITS$8;
var XXLARGE_LINE_HEIGHT$8 = computeFontSize$8(3.5) + BASE_UNITS$8;
var HEADING_FONT_FAMILY$8 = "'Poppins', 'Open Sans',  sans-serif";
var BODY_FONT_FAMILY$8 = "'Poppins', 'Open Sans', sans-serif";
var BODY_FONT_IMPORT$8 = 'https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&display=swap';
var HEADING_FONT_IMPORT$8 = 'https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&display=swap';
var WEIGHT_THIN$8 = 300;
var WEIGHT_REGULAR$8 = 400;
var WEIGHT_SEMI_BOLD$8 = 600;
var WEIGHT_BOLD$8 = 700;
var WEIGHT_EXTRA_BOLD$8 = 800;

var icons$9 = {
  xSmall: 18,
  small: 24,
  medium: 32,
  large: 64,
  xLarge: 88
};
var sphere$9 = {
  xSmall: 64,
  small: 80,
  medium: 96,
  large: 104,
  xLarge: 128
};

var colors$g = {
  primaryBrand: colors$h.purpleSwatch,
  secondaryBrand: colors$h.orangeSwatch,
  accent: colors$h.accentSwatch,
  success: colors$h.greenSwatch,
  warning: colors$h.yellowSwatch,
  danger: colors$h.redSwatch,
  background: colors$h.lightGray,
  foreground: colors$h.WHITE,
  grayScale: computeGrayScale(colors$h.purpleSwatch),
  blueScale: colors$h.blueSwatch,
  purpleScale: colors$h.purpleSwatch,
  white: colors$h.WHITE,
  defaultShadowColor: SHADOW_DEFAULT_COLOR
};
var concepts$8 = {
  colors: __assign({}, colors$g),
  radius: {
    none: radius.none,
    small: radius.small,
    medium: radius.mediumLarge,
    default: radius.medium,
    large: radius.large,
    circle: radius.circle
  },
  shadows: {
    light: computeShadow('light', Color$1(colors$g.defaultShadowColor).rgb().alpha(0.08).toString()),
    mediumLight: computeShadow('mediumLight', Color$1(colors$g.defaultShadowColor).rgb().alpha(0.08).toString()),
    mediumDark: computeShadow('mediumDark', Color$1(colors$g.defaultShadowColor).rgb().alpha(0.1).toString()),
    dark: computeShadow('dark', Color$1(colors$g.defaultShadowColor).rgb().alpha(0.12).toString()),
    generate: computeShadow
  },
  typography: {
    imports: {
      body: BODY_FONT_IMPORT$8,
      heading: HEADING_FONT_IMPORT$8
    },
    colors: {
      primaryHeading: colors$g.grayScale._200,
      primaryBody: colors$g.grayScale.base,
      inverseHeading: colors$g.white,
      inverseBody: colors$g.white
    },
    families: {
      heading: BODY_FONT_FAMILY$8,
      body: HEADING_FONT_FAMILY$8
    },
    sizes: {
      xSmall: XSMALL_FONT$8,
      small: SMALL_FONT$8,
      medium: MEDIUM_FONT$8,
      mediumPlus: MEDIUM_PLUS_FONT$8,
      large: LARGE_FONT$8,
      xLarge: XLARGE_FONT$8,
      xxLarge: XXLARGE_FONT$8
    },
    weights: {
      thin: WEIGHT_THIN$8,
      regular: WEIGHT_REGULAR$8,
      semiBold: WEIGHT_SEMI_BOLD$8,
      bold: WEIGHT_BOLD$8,
      extraBold: WEIGHT_EXTRA_BOLD$8
    },
    lineHeight: {
      xSmall: XSMALL_LINE_HEIGHT$8,
      small: SMALL_LINE_HEIGHT$8,
      medium: MEDIUM_LINE_HEIGHT$8,
      large: LARGE_LINE_HEIGHT$8,
      xLarge: XLARGE_LINE_HEIGHT$8,
      xxLarge: XXLARGE_LINE_HEIGHT$8
    }
  },
  borders: {
    thin: borders$9.xSmall,
    small: borders$9.small,
    default: borders$9.medium,
    large: borders$9.large,
    bold: borders$9.xLarge
  },
  spacing: {
    baseValue: BASE_VALUE$9,
    baseUnits: BASE_UNITS$9
  },
  mediaQueries: {
    small: mediaQuerySizes.small,
    medium: mediaQuerySizes.medium,
    large: mediaQuerySizes.large,
    xLarge: mediaQuerySizes.xLarge
  },
  sizing: {
    icons: {
      xSmall: icons$9.xSmall,
      small: icons$9.small,
      medium: icons$9.medium,
      large: icons$9.large,
      xLarge: icons$9.xLarge
    },
    sphere: {
      xSmall: sphere$9.xSmall,
      small: sphere$9.small,
      medium: sphere$9.medium,
      large: sphere$9.large,
      xLarge: sphere$9.xLarge
    }
  }
};

var space$8 = computeSpace(concepts$8.spacing.baseValue, concepts$8.spacing.baseUnits);
var spacing$8 = {
  x0: space$8(0),
  x_25: space$8(0.25),
  x_5: space$8(0.5),
  x_75: space$8(0.75),
  x1: space$8(1),
  x1_5: space$8(1.5),
  x2: space$8(2),
  x2_5: space$8(2.5),
  x3: space$8(3),
  x4: space$8(4),
  x5: space$8(5),
  x6: space$8(6),
  x7: space$8(7),
  x8: space$8(8),
  x9: space$8(9)
};
var borders$8 = {
  thin: concepts$8.borders.thin + "px",
  default: concepts$8.borders.default + "px",
  bold: concepts$8.borders.bold + "px"
};
var text$8 = {
  imports: {
    body: concepts$8.typography.imports.body,
    heading: concepts$8.typography.imports.heading
  },
  families: {
    body: concepts$8.typography.families.body,
    heading: concepts$8.typography.families.heading
  },
  types: {
    heroTitle: {
      color: concepts$8.typography.colors.primaryHeading,
      size: concepts$8.typography.sizes.xxLarge,
      fontWeight: concepts$8.typography.weights.extraBold,
      lineHeight: concepts$8.typography.lineHeight.xxLarge,
      fontFamily: concepts$8.typography.families.heading
    },
    title: {
      color: concepts$8.typography.colors.primaryHeading,
      size: concepts$8.typography.sizes.xLarge,
      fontWeight: concepts$8.typography.weights.bold,
      lineHeight: concepts$8.typography.lineHeight.xLarge,
      fontFamily: concepts$8.typography.families.heading
    },
    subtitle: {
      color: concepts$8.typography.colors.primaryHeading,
      size: concepts$8.typography.sizes.large,
      fontWeight: concepts$8.typography.weights.regular,
      lineHeight: concepts$8.typography.lineHeight.medium,
      fontFamily: concepts$8.typography.families.heading
    },
    largeBody: {
      color: concepts$8.typography.colors.primaryBody,
      size: concepts$8.typography.sizes.mediumPlus,
      fontWeight: concepts$8.typography.weights.regular,
      lineHeight: concepts$8.typography.lineHeight.small,
      fontFamily: concepts$8.typography.families.body
    },
    body: {
      color: concepts$8.typography.colors.primaryBody,
      size: concepts$8.typography.sizes.medium,
      fontWeight: concepts$8.typography.weights.regular,
      lineHeight: concepts$8.typography.lineHeight.small,
      fontFamily: concepts$8.typography.families.body
    },
    caption: {
      color: concepts$8.typography.colors.primaryBody,
      size: concepts$8.typography.sizes.small,
      fontWeight: concepts$8.typography.weights.regular,
      lineHeight: concepts$8.typography.lineHeight.small,
      fontFamily: concepts$8.typography.families.body
    },
    footnote: {
      color: concepts$8.typography.colors.primaryBody,
      size: concepts$8.typography.sizes.xSmall,
      fontWeight: concepts$8.typography.weights.regular,
      lineHeight: concepts$8.typography.lineHeight.xSmall,
      fontFamily: concepts$8.typography.families.body
    }
  },
  colors: {
    primaryBrand: concepts$8.colors.primaryBrand.base,
    secondaryBrand: concepts$8.colors.secondaryBrand.base,
    primaryHeading: concepts$8.typography.colors.primaryHeading,
    primaryBody: concepts$8.typography.colors.primaryBody,
    inverseHeading: concepts$8.typography.colors.inverseHeading,
    inverseBody: concepts$8.typography.colors.inverseBody,
    accent: concepts$8.colors.accent.base,
    success: concepts$8.colors.success.base,
    warning: concepts$8.colors.warning.base,
    danger: concepts$8.colors.danger.base,
    anchorBlue: concepts$8.colors.blueScale.base
  },
  weights: {
    thin: concepts$8.typography.weights.thin,
    regular: concepts$8.typography.weights.regular,
    semiBold: concepts$8.typography.weights.semiBold,
    bold: concepts$8.typography.weights.bold,
    extraBold: concepts$8.typography.weights.extraBold
  }
};
var mediaQueries$c = {
  queries: {
    xSmall: "@media screen and (min-width: 0px)",
    // Large Mobile
    small: "@media screen and (min-width: " + concepts$8.mediaQueries.small + "px)",
    // Tablet
    medium: "@media screen and (min-width: " + concepts$8.mediaQueries.medium + "px)",
    // Large Tablet
    large: "@media screen and (min-width: " + concepts$8.mediaQueries.large + "px)",
    // Desktop
    xLarge: "@media screen and (min-width: " + concepts$8.mediaQueries.xLarge + "px)"
  },
  rawValues: {
    xSmall: 0,
    small: concepts$8.mediaQueries.small,
    medium: concepts$8.mediaQueries.medium,
    large: concepts$8.mediaQueries.large,
    xLarge: concepts$8.mediaQueries.xLarge
  }
};
var hocs$8 = {
  colors: {
    swatches: {
      primaryBrand: concepts$8.colors.primaryBrand,
      secondaryBrand: concepts$8.colors.secondaryBrand,
      success: concepts$8.colors.success,
      danger: concepts$8.colors.danger,
      warning: concepts$8.colors.warning,
      accent: concepts$8.colors.accent,
      grayScale: concepts$8.colors.grayScale,
      blueScale: concepts$8.colors.blueScale,
      purpleScale: concepts$8.colors.purpleScale
    },
    background: concepts$8.colors.background,
    foreground: concepts$8.colors.foreground
  },
  textColors: text$8.colors,
  spacing: spacing$8,
  borderRadius: concepts$8.radius,
  shadows: concepts$8.shadows,
  borders: borders$8
};
// TODO - revisit buttons for contrast and font color
var buttons$8 = {
  boxShadow: concepts$8.shadows.generate,
  borderSize: borders$8.default,
  outlinedBackgroundColor: concepts$8.colors.foreground,
  fontWeight: concepts$8.typography.weights.semiBold,
  sizes: {
    xSmall: {
      inset: spacing$8.x1 + " " + spacing$8.x2,
      outlinedInset: {
        vertical: "calc(" + spacing$8.x1 + " - 2px)",
        horizontal: "calc(" + spacing$8.x2 + " - 2px)"
      },
      fontSize: text$8.types.footnote.size,
      borderRadius: concepts$8.radius.default
    },
    small: {
      inset: spacing$8.x1_5 + " " + spacing$8.x2_5,
      outlinedInset: {
        vertical: "calc(" + spacing$8.x1_5 + " - 2px)",
        horizontal: "calc(" + spacing$8.x2_5 + " - 2px)"
      },
      fontSize: text$8.types.caption.size,
      borderRadius: concepts$8.radius.default
    },
    medium: {
      inset: spacing$8.x1_5 + " " + spacing$8.x3,
      outlinedInset: {
        vertical: "calc(" + spacing$8.x1_5 + " - 2px)",
        horizontal: "calc(" + spacing$8.x3 + " - 2px)"
      },
      fontSize: text$8.types.body.size,
      borderRadius: concepts$8.radius.default
    },
    large: {
      inset: spacing$8.x2 + " " + spacing$8.x4,
      outlinedInset: {
        vertical: "calc(" + spacing$8.x2 + " - 2px)",
        horizontal: "calc(" + spacing$8.x4 + " - 2px)"
      },
      fontSize: text$8.types.largeBody.size,
      borderRadius: concepts$8.radius.default
    },
    xLarge: {
      inset: spacing$8.x3 + " " + spacing$8.x5,
      outlinedInset: {
        vertical: "calc(" + spacing$8.x3 + " - 2px)",
        horizontal: "calc(" + spacing$8.x5 + " - 2px)"
      },
      fontSize: text$8.types.subtitle.size,
      borderRadius: concepts$8.radius.default
    }
  },
  primary: {
    backgroundColor: concepts$8.colors.primaryBrand.base,
    fontColor: concepts$8.typography.colors.inverseBody,
    fontFamily: concepts$8.typography.families.body
  },
  secondary: {
    backgroundColor: concepts$8.colors.secondaryBrand.base,
    fontColor: concepts$8.typography.colors.inverseBody,
    fontFamily: concepts$8.typography.families.body
  },
  warning: {
    backgroundColor: concepts$8.colors.warning.base,
    fontColor: concepts$8.typography.colors.inverseBody,
    fontFamily: concepts$8.typography.families.body
  },
  success: {
    backgroundColor: concepts$8.colors.success.base,
    fontColor: concepts$8.typography.colors.inverseBody,
    fontFamily: concepts$8.typography.families.body
  },
  danger: {
    backgroundColor: concepts$8.colors.danger.base,
    fontColor: concepts$8.typography.colors.inverseBody,
    fontFamily: concepts$8.typography.families.body
  },
  shadow: {
    backgroundColor: concepts$8.colors.foreground,
    fontColor: concepts$8.typography.colors.primaryBody,
    fontFamily: concepts$8.typography.families.body
  },
  accent: {
    backgroundColor: concepts$8.colors.accent.base,
    fontColor: concepts$8.typography.colors.primaryBody,
    fontFamily: concepts$8.typography.families.body
  },
  smoke: {
    backgroundColor: concepts$8.colors.background,
    fontColor: concepts$8.typography.colors.primaryBody,
    fontFamily: concepts$8.typography.families.body
  }
};
var inputs$8 = {
  checkbox: {
    border: borders$8.default,
    borderRadius: concepts$8.radius.small,
    unCheckedBackgroundColor: concepts$8.colors.grayScale._1000,
    checkmarkColor: concepts$8.typography.colors.inverseBody,
    labelColor: concepts$8.typography.colors.primaryBody
  },
  text: {
    borderRadius: concepts$8.radius.default,
    labelColor: concepts$8.colors.accent,
    placeholderColor: concepts$8.typography.colors.primaryBody,
    labelFontSize: text$8.types.caption.size,
    textFontSize: text$8.types.caption.size,
    borderWidth: borders$8.default,
    defaultFocusColor: concepts$8.colors.blueScale.base
  },
  select: {
    dropDownShadow: concepts$8.shadows.mediumDark,
    dropDownBackgroundColor: concepts$8.colors.foreground,
    boxShadow: concepts$8.shadows.light,
    borderRadius: concepts$8.radius.default
  },
  number: {
    backgroundColor: concepts$8.colors.grayScale._200,
    fontColor: concepts$8.typography.colors.inverseBody,
    fontSize: text$8.types.body.size,
    borderRadius: concepts$8.radius.default
  },
  backgroundColor: concepts$8.colors.background,
  fontSize: text$8.types.caption.size,
  fontColor: concepts$8.typography.colors.primaryBody,
  fontFamily: concepts$8.typography.families.body,
  colors: {
    primary: concepts$8.colors.primaryBrand.base,
    secondary: concepts$8.colors.secondaryBrand.base,
    success: concepts$8.colors.success.base,
    warning: concepts$8.colors.warning.base,
    danger: concepts$8.colors.danger.base,
    accent: concepts$8.colors.accent.base,
    dark: concepts$8.colors.primaryBrand._100,
    blue: concepts$8.colors.blueScale.base
  }
};
var icons$8 = {
  sizes: {
    xSmall: concepts$8.sizing.icons.xSmall,
    small: concepts$8.sizing.icons.small,
    medium: concepts$8.sizing.icons.medium,
    large: concepts$8.sizing.icons.large,
    xLarge: concepts$8.sizing.icons.xLarge
  },
  colors: {
    default: concepts$8.colors.grayScale._800,
    primaryBodyText: concepts$8.typography.colors.primaryBody,
    primaryHeadingText: concepts$8.typography.colors.primaryHeading,
    inverseBodyText: concepts$8.typography.colors.inverseBody,
    inverseHeadingText: concepts$8.typography.colors.inverseHeading,
    primaryBrand: concepts$8.colors.primaryBrand.base,
    secondaryBrand: concepts$8.colors.secondaryBrand.base,
    warning: concepts$8.colors.warning.base,
    danger: concepts$8.colors.danger.base,
    success: concepts$8.colors.success.base,
    background: concepts$8.colors.background,
    accent: concepts$8.colors.background
  }
};
var animatedCheckmark$8 = {
  sizeMap: {
    xSmall: concepts$8.sizing.icons.xSmall,
    small: concepts$8.sizing.icons.small,
    medium: concepts$8.sizing.icons.medium,
    large: concepts$8.sizing.icons.large,
    xLarge: concepts$8.sizing.icons.xLarge
  },
  colors: {
    primary: concepts$8.colors.primaryBrand.base,
    secondary: concepts$8.colors.secondaryBrand.base,
    success: concepts$8.colors.success.base,
    accent: concepts$8.colors.accent.base
  },
  strokeWidth: {
    small: concepts$8.borders.small,
    default: concepts$8.borders.default,
    large: concepts$8.borders.large
  },
  borderRadius: concepts$8.radius.circle,
  checkmarkFilledColor: concepts$8.colors.foreground
};
var avatar$8 = {
  borderWidth: borders$8.thin,
  borderColor: concepts$8.colors.grayScale._1000,
  borderRadius: concepts$8.radius.circle,
  boxShadow: concepts$8.shadows.mediumLight,
  backgroundColor: concepts$8.colors.foreground,
  bubbleBackgroundColors: {
    isActive: concepts$8.colors.success.base,
    default: concepts$8.colors.grayScale._1000
  },
  defaultBackgroundColors: {
    primary: concepts$8.colors.primaryBrand.base,
    secondary: concepts$8.colors.secondaryBrand.base,
    success: concepts$8.colors.success.base,
    accent: concepts$8.colors.accent.base,
    warning: concepts$8.colors.warning.base,
    danger: concepts$8.colors.danger.base
  },
  sizes: {
    xSmall: {
      avatarSize: 34,
      bubbleLeftPosition: 22,
      bubbleSize: 12
    },
    small: {
      avatarSize: 44,
      bubbleLeftPosition: 28,
      bubbleSize: 14
    },
    medium: {
      avatarSize: 60,
      bubbleLeftPosition: 40,
      bubbleSize: 16
    },
    large: {
      avatarSize: 76,
      bubbleLeftPosition: 52,
      bubbleSize: 20
    },
    xLarge: {
      avatarSize: 96,
      bubbleLeftPosition: 64,
      bubbleSize: 28
    }
  }
};
var loadingSpinner$8 = {
  size: {
    xSmall: concepts$8.sizing.icons.xSmall,
    small: concepts$8.sizing.icons.small,
    medium: concepts$8.sizing.icons.medium,
    large: concepts$8.sizing.icons.large,
    xLarge: concepts$8.sizing.icons.xLarge
  },
  colors: {
    primary: concepts$8.colors.primaryBrand.base,
    secondary: concepts$8.colors.secondaryBrand.base,
    success: concepts$8.colors.success.base,
    warning: concepts$8.colors.warning.base,
    danger: concepts$8.colors.danger.base
  }
};
var slider$8 = {
  backgroundColor: concepts$8.colors.background,
  borderRadius: concepts$8.radius.small,
  handleRadius: concepts$8.radius.circle,
  handleColor: concepts$8.colors.foreground,
  handleShadow: concepts$8.shadows.light
};
var separator$8 = {
  border: borders$8.default,
  color: concepts$8.colors.grayScale._1000
};
var card$8 = {
  backgroundColor: concepts$8.colors.warning.base,
  borderRadius: concepts$8.radius.default,
  boxShadow: concepts$8.shadows.mediumDark,
  defaultInset: spacing$8.x4 + " " + spacing$8.x2 + " " + spacing$8.x2 + " " + spacing$8.x2
};
var dialog$8 = {
  backgroundColor: concepts$8.colors.foreground,
  borderRadius: concepts$8.radius.default,
  boxShadow: concepts$8.shadows.mediumDark,
  defaultInset: spacing$8.x6 + " " + spacing$8.x4 + " " + spacing$8.x4 + " " + spacing$8.x4
};
var navigation$8 = {
  backgroundColor: concepts$8.colors.foreground,
  border: borders$8.thin,
  borderColor: concepts$8.colors.grayScale._1000
};
var drawer$8 = {
  backgroundColor: concepts$8.colors.foreground,
  borderRadius: concepts$8.radius.small,
  boxShadow: concepts$8.shadows.mediumLight,
  bottomDirectionShadow: concepts$8.shadows.generate('mediumLight', Color$1(concepts$8.colors.defaultShadowColor).rgb().alpha(0.1).toString(), true),
  hoverBoxShadow: concepts$8.shadows.mediumDark,
  hoverBottomDirectionShadow: concepts$8.shadows.generate('mediumDark', Color$1(concepts$8.colors.defaultShadowColor).rgb().alpha(0.1).toString(), true),
  defaultInset: "" + spacing$8.x1,
  tab: {
    dark: {
      backgroundColor: concepts$8.colors.grayScale._600,
      arrowColor: concepts$8.colors.foreground
    },
    light: {
      backgroundColor: concepts$8.colors.foreground,
      arrowColor: icons$8.colors.default
    }
  }
};
var sheet$8 = {
  mobileBorderRadius: concepts$8.radius.large,
  desktopBorderRadius: concepts$8.radius.default,
  backgroundColor: concepts$8.colors.foreground,
  boxShadow: concepts$8.shadows.mediumDark
};
var toggle$8 = {
  isActiveColor: concepts$8.colors.success.base,
  isNotActiveColor: concepts$8.colors.grayScale._900
};
var tooltip$8 = {
  lightBackgroundColor: concepts$8.colors.foreground,
  darkBackgroundColor: concepts$8.colors.grayScale._100,
  boxShadow: concepts$8.shadows.mediumLight,
  borderColor: concepts$8.colors.background
};
var colorPicker$8 = {
  backgroundColor: concepts$8.colors.background,
  borderColor: 'transparent',
  boxShadow: concepts$8.shadows.mediumLight,
  borderRadius: concepts$8.radius.small
};
var sphere$8 = {
  xSmall: {
    circumference: concepts$8.sizing.sphere.xSmall,
    textType: 'title'
  },
  small: {
    circumference: concepts$8.sizing.sphere.small,
    textType: 'title'
  },
  medium: {
    circumference: concepts$8.sizing.sphere.medium,
    textType: 'heroTitle'
  },
  large: {
    circumference: concepts$8.sizing.sphere.large,
    textType: 'heroTitle'
  },
  xLarge: {
    circumference: concepts$8.sizing.sphere.xLarge,
    textType: 'heroTitle'
  }
};
var widget$8 = {
  primary: {
    backgroundColor: concepts$8.colors.primaryBrand.base,
    fontColor: 'inverseBody'
  },
  secondary: {
    backgroundColor: concepts$8.colors.secondaryBrand.base,
    fontColor: 'inverseBody'
  },
  warning: {
    backgroundColor: concepts$8.colors.warning.base,
    fontColor: 'inverseBody'
  },
  success: {
    backgroundColor: concepts$8.colors.success.base,
    fontColor: 'primaryBody'
  },
  danger: {
    backgroundColor: concepts$8.colors.danger.base,
    fontColor: 'inverseBody'
  },
  shadow: {
    backgroundColor: concepts$8.colors.foreground,
    fontColor: 'primaryBody'
  },
  accent: {
    backgroundColor: concepts$8.colors.accent.base,
    fontColor: 'primaryBody'
  }
};
var colorOption$8 = {
  defaults: {
    circleFill: concepts$8.shadows.generate([0, 0, 6, 1], Color$1(concepts$8.colors.defaultShadowColor).rgb().alpha(0.1).toString()),
    innerRing: concepts$8.shadows.generate([0, 0, 0, 11], Color$1([20, 30, 50]).rgb().alpha(0.03).toString()),
    outerRing: concepts$8.shadows.generate([0, 0, 6, 1], Color$1(concepts$8.colors.defaultShadowColor).rgb().alpha(0.06).toString())
  }
};
var entities$8 = {
  mediaQueries: mediaQueries$c,
  hocs: hocs$8,
  text: text$8,
  spacing: spacing$8,
  buttons: buttons$8,
  inputs: inputs$8,
  icons: icons$8,
  card: card$8,
  separator: separator$8,
  loadingSpinner: loadingSpinner$8,
  animatedCheckmark: animatedCheckmark$8,
  avatar: avatar$8,
  dialog: dialog$8,
  sheet: sheet$8,
  navigation: navigation$8,
  toggle: toggle$8,
  tooltip: tooltip$8,
  drawer: drawer$8,
  slider: slider$8,
  colorPicker: colorPicker$8,
  sphere: sphere$8,
  widget: widget$8,
  colorOption: colorOption$8
};

var BRIGHT_GREEN$1 = '#74C468';
var LIGHT_BLUE$2 = '#52C1D1';
var BASE_GRAY$6 = '#696767';
var COPPER = '#BB7E3D';
var GREEN$7 = '#1DE6B9';
var YELLOW$6 = '#F7A14D';
var RED$6 = '#FB2E4B';
var WHITE$7 = '#FFF';
var BLUE$6 = '#1DC5E6';
var PURPLE$7 = '#703A96';
var brightGreenSwatch$1 = computeColorSwatch(BRIGHT_GREEN$1);
var lightBlueSwatch$1 = computeColorSwatch(LIGHT_BLUE$2);
var baseGraySwatch$5 = computeColorSwatch(BASE_GRAY$6);
var greenSwatch$7 = computeColorSwatch(GREEN$7);
var yellowSwatch$7 = computeColorSwatch(YELLOW$6);
var redSwatch$7 = computeColorSwatch(RED$6);
var copperSwatch = computeColorSwatch(COPPER);
var blueSwatch$6 = computeColorSwatch(BLUE$6);
var purpleSwatch$7 = computeColorSwatch(PURPLE$7);
var BASE$6 = '#FBF9F4';
var colors$f = {
  brightGreenSwatch: brightGreenSwatch$1,
  lightBlueSwatch: lightBlueSwatch$1,
  greenSwatch: greenSwatch$7,
  copperSwatch: copperSwatch,
  baseGraySwatch: baseGraySwatch$5,
  redSwatch: redSwatch$7,
  yellowSwatch: yellowSwatch$7,
  blueSwatch: blueSwatch$6,
  purpleSwatch: purpleSwatch$7,
  BASE: BASE$6,
  WHITE: WHITE$7
};

var BASE_VALUE$7 = 1;
var BASE_UNITS$7 = 'rem';
// Rename to typography
var computeFontSize$7 = function (multiplier) {
  return BASE_VALUE$7 * multiplier;
};
var XSMALL_FONT$7 = computeFontSize$7(0.75) + BASE_UNITS$7;
var SMALL_FONT$7 = computeFontSize$7(0.875) + BASE_UNITS$7;
var MEDIUM_FONT$7 = computeFontSize$7(1) + BASE_UNITS$7;
var MEDIUM_PLUS_FONT$7 = computeFontSize$7(1.25) + BASE_UNITS$7;
var LARGE_FONT$7 = computeFontSize$7(1.75) + BASE_UNITS$7;
var XLARGE_FONT$7 = computeFontSize$7(2.125) + BASE_UNITS$7;
var XXLARGE_FONT$7 = computeFontSize$7(3) + BASE_UNITS$7;
var XSMALL_LINE_HEIGHT$7 = computeFontSize$7(1.5) + BASE_UNITS$7;
var SMALL_LINE_HEIGHT$7 = computeFontSize$7(1.75) + BASE_UNITS$7;
var MEDIUM_LINE_HEIGHT$7 = computeFontSize$7(2) + BASE_UNITS$7;
var LARGE_LINE_HEIGHT$7 = computeFontSize$7(2.5) + BASE_UNITS$7;
var XLARGE_LINE_HEIGHT$7 = computeFontSize$7(3) + BASE_UNITS$7;
var XXLARGE_LINE_HEIGHT$7 = computeFontSize$7(3.5) + BASE_UNITS$7;
var HEADING_FONT_FAMILY$7 = "'Poppins', 'Open Sans',  sans-serif";
var BODY_FONT_FAMILY$7 = "'Poppins', 'Open Sans', sans-serif";
var BODY_FONT_IMPORT$7 = 'https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&display=swap';
var HEADING_FONT_IMPORT$7 = 'https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&display=swap';
var WEIGHT_THIN$7 = 300;
var WEIGHT_REGULAR$7 = 400;
var WEIGHT_SEMI_BOLD$7 = 600;
var WEIGHT_BOLD$7 = 700;
var WEIGHT_EXTRA_BOLD$7 = 800;

var colors$e = {
  primaryBrand: colors$f.brightGreenSwatch,
  secondaryBrand: colors$f.lightBlueSwatch,
  accent: colors$f.copperSwatch,
  success: colors$f.greenSwatch,
  warning: colors$f.yellowSwatch,
  danger: colors$f.redSwatch,
  background: colors$f.BASE,
  foreground: colors$f.WHITE,
  grayScale: colors$f.baseGraySwatch,
  blueScale: colors$f.blueSwatch,
  purpleScale: colors$f.purpleSwatch,
  white: colors$f.WHITE,
  defaultShadowColor: SHADOW_DEFAULT_COLOR
};
var concepts$7 = {
  colors: __assign({}, colors$e),
  radius: {
    none: radius.none,
    small: radius.small,
    medium: radius.mediumLarge,
    default: radius.medium,
    large: radius.large,
    circle: radius.circle
  },
  shadows: {
    light: computeShadow('light', Color$1(colors$e.defaultShadowColor).rgb().alpha(0.08).toString()),
    mediumLight: computeShadow('mediumLight', Color$1(colors$e.defaultShadowColor).rgb().alpha(0.08).toString()),
    mediumDark: computeShadow('mediumDark', Color$1(colors$e.defaultShadowColor).rgb().alpha(0.1).toString()),
    dark: computeShadow('dark', Color$1(colors$e.defaultShadowColor).rgb().alpha(0.12).toString()),
    generate: computeShadow
  },
  typography: {
    imports: {
      body: BODY_FONT_IMPORT$7,
      heading: HEADING_FONT_IMPORT$7
    },
    colors: {
      primaryHeading: colors$e.grayScale._200,
      primaryBody: colors$e.grayScale.base,
      inverseHeading: colors$e.white,
      inverseBody: colors$e.white
    },
    families: {
      heading: BODY_FONT_FAMILY$7,
      body: HEADING_FONT_FAMILY$7
    },
    sizes: {
      xSmall: XSMALL_FONT$7,
      small: SMALL_FONT$7,
      medium: MEDIUM_FONT$7,
      mediumPlus: MEDIUM_PLUS_FONT$7,
      large: LARGE_FONT$7,
      xLarge: XLARGE_FONT$7,
      xxLarge: XXLARGE_FONT$7
    },
    weights: {
      thin: WEIGHT_THIN$7,
      regular: WEIGHT_REGULAR$7,
      semiBold: WEIGHT_SEMI_BOLD$7,
      bold: WEIGHT_BOLD$7,
      extraBold: WEIGHT_EXTRA_BOLD$7
    },
    lineHeight: {
      xSmall: XSMALL_LINE_HEIGHT$7,
      small: SMALL_LINE_HEIGHT$7,
      medium: MEDIUM_LINE_HEIGHT$7,
      large: LARGE_LINE_HEIGHT$7,
      xLarge: XLARGE_LINE_HEIGHT$7,
      xxLarge: XXLARGE_LINE_HEIGHT$7
    }
  },
  borders: {
    thin: borders$9.xSmall,
    small: borders$9.small,
    default: borders$9.medium,
    large: borders$9.large,
    bold: borders$9.xLarge
  },
  spacing: {
    baseValue: BASE_VALUE$9,
    baseUnits: BASE_UNITS$9
  },
  mediaQueries: {
    small: mediaQuerySizes.small,
    medium: mediaQuerySizes.medium,
    large: mediaQuerySizes.large,
    xLarge: mediaQuerySizes.xLarge
  },
  sizing: {
    icons: {
      xSmall: icons$9.xSmall,
      small: icons$9.small,
      medium: icons$9.medium,
      large: icons$9.large,
      xLarge: icons$9.xLarge
    },
    sphere: {
      xSmall: sphere$9.xSmall,
      small: sphere$9.small,
      medium: sphere$9.medium,
      large: sphere$9.large,
      xLarge: sphere$9.xLarge
    }
  }
};

var space$7 = computeSpace(concepts$7.spacing.baseValue, concepts$7.spacing.baseUnits);
var spacing$7 = {
  x0: space$7(0),
  x_25: space$7(0.25),
  x_5: space$7(0.5),
  x_75: space$7(0.75),
  x1: space$7(1),
  x1_5: space$7(1.5),
  x2: space$7(2),
  x2_5: space$7(2.5),
  x3: space$7(3),
  x4: space$7(4),
  x5: space$7(5),
  x6: space$7(6),
  x7: space$7(7),
  x8: space$7(8),
  x9: space$7(9)
};
var borders$7 = {
  thin: concepts$7.borders.thin + "px",
  default: concepts$7.borders.default + "px",
  bold: concepts$7.borders.bold + "px"
};
var text$7 = {
  imports: {
    body: concepts$7.typography.imports.body,
    heading: concepts$7.typography.imports.heading
  },
  families: {
    body: concepts$7.typography.families.body,
    heading: concepts$7.typography.families.heading
  },
  types: {
    heroTitle: {
      color: concepts$7.typography.colors.primaryHeading,
      size: concepts$7.typography.sizes.xxLarge,
      fontWeight: concepts$7.typography.weights.extraBold,
      lineHeight: concepts$7.typography.lineHeight.xxLarge,
      fontFamily: concepts$7.typography.families.heading
    },
    title: {
      color: concepts$7.typography.colors.primaryHeading,
      size: concepts$7.typography.sizes.xLarge,
      fontWeight: concepts$7.typography.weights.bold,
      lineHeight: concepts$7.typography.lineHeight.xLarge,
      fontFamily: concepts$7.typography.families.heading
    },
    subtitle: {
      color: concepts$7.typography.colors.primaryHeading,
      size: concepts$7.typography.sizes.large,
      fontWeight: concepts$7.typography.weights.regular,
      lineHeight: concepts$7.typography.lineHeight.medium,
      fontFamily: concepts$7.typography.families.heading
    },
    largeBody: {
      color: concepts$7.typography.colors.primaryBody,
      size: concepts$7.typography.sizes.mediumPlus,
      fontWeight: concepts$7.typography.weights.regular,
      lineHeight: concepts$7.typography.lineHeight.small,
      fontFamily: concepts$7.typography.families.body
    },
    body: {
      color: concepts$7.typography.colors.primaryBody,
      size: concepts$7.typography.sizes.medium,
      fontWeight: concepts$7.typography.weights.regular,
      lineHeight: concepts$7.typography.lineHeight.small,
      fontFamily: concepts$7.typography.families.body
    },
    caption: {
      color: concepts$7.typography.colors.primaryBody,
      size: concepts$7.typography.sizes.small,
      fontWeight: concepts$7.typography.weights.regular,
      lineHeight: concepts$7.typography.lineHeight.small,
      fontFamily: concepts$7.typography.families.body
    },
    footnote: {
      color: concepts$7.typography.colors.primaryBody,
      size: concepts$7.typography.sizes.xSmall,
      fontWeight: concepts$7.typography.weights.regular,
      lineHeight: concepts$7.typography.lineHeight.xSmall,
      fontFamily: concepts$7.typography.families.body
    }
  },
  colors: {
    primaryBrand: concepts$7.colors.primaryBrand.base,
    secondaryBrand: concepts$7.colors.secondaryBrand.base,
    primaryHeading: concepts$7.typography.colors.primaryHeading,
    primaryBody: concepts$7.typography.colors.primaryBody,
    inverseHeading: concepts$7.typography.colors.inverseHeading,
    inverseBody: concepts$7.typography.colors.inverseBody,
    accent: concepts$7.colors.accent.base,
    success: concepts$7.colors.success.base,
    warning: concepts$7.colors.warning.base,
    danger: concepts$7.colors.danger.base,
    anchorBlue: concepts$7.colors.blueScale.base
  },
  weights: {
    thin: concepts$7.typography.weights.thin,
    regular: concepts$7.typography.weights.regular,
    semiBold: concepts$7.typography.weights.semiBold,
    bold: concepts$7.typography.weights.bold,
    extraBold: concepts$7.typography.weights.extraBold
  }
};
var mediaQueries$b = {
  queries: {
    xSmall: "@media screen and (min-width: 0px)",
    // Large Mobile
    small: "@media screen and (min-width: " + concepts$7.mediaQueries.small + "px)",
    // Tablet
    medium: "@media screen and (min-width: " + concepts$7.mediaQueries.medium + "px)",
    // Large Tablet
    large: "@media screen and (min-width: " + concepts$7.mediaQueries.large + "px)",
    // Desktop
    xLarge: "@media screen and (min-width: " + concepts$7.mediaQueries.xLarge + "px)"
  },
  rawValues: {
    xSmall: 0,
    small: concepts$7.mediaQueries.small,
    medium: concepts$7.mediaQueries.medium,
    large: concepts$7.mediaQueries.large,
    xLarge: concepts$7.mediaQueries.xLarge
  }
};
var hocs$7 = {
  colors: {
    swatches: {
      primaryBrand: concepts$7.colors.primaryBrand,
      secondaryBrand: concepts$7.colors.secondaryBrand,
      success: concepts$7.colors.success,
      danger: concepts$7.colors.danger,
      warning: concepts$7.colors.warning,
      accent: concepts$7.colors.accent,
      grayScale: concepts$7.colors.grayScale,
      blueScale: concepts$7.colors.blueScale,
      purpleScale: concepts$7.colors.purpleScale
    },
    background: concepts$7.colors.background,
    foreground: concepts$7.colors.foreground
  },
  textColors: text$7.colors,
  spacing: spacing$7,
  borderRadius: concepts$7.radius,
  shadows: concepts$7.shadows,
  borders: borders$7
};
// TODO - revisit buttons for contrast and font color
var buttons$7 = {
  boxShadow: concepts$7.shadows.generate,
  borderSize: borders$7.default,
  outlinedBackgroundColor: concepts$7.colors.foreground,
  fontWeight: concepts$7.typography.weights.semiBold,
  sizes: {
    xSmall: {
      inset: spacing$7.x1 + " " + spacing$7.x2,
      outlinedInset: {
        vertical: "calc(" + spacing$7.x1 + " - 2px)",
        horizontal: "calc(" + spacing$7.x2 + " - 2px)"
      },
      fontSize: text$7.types.footnote.size,
      borderRadius: concepts$7.radius.default
    },
    small: {
      inset: spacing$7.x1_5 + " " + spacing$7.x2_5,
      outlinedInset: {
        vertical: "calc(" + spacing$7.x1_5 + " - 2px)",
        horizontal: "calc(" + spacing$7.x2_5 + " - 2px)"
      },
      fontSize: text$7.types.caption.size,
      borderRadius: concepts$7.radius.default
    },
    medium: {
      inset: spacing$7.x1_5 + " " + spacing$7.x3,
      outlinedInset: {
        vertical: "calc(" + spacing$7.x1_5 + " - 2px)",
        horizontal: "calc(" + spacing$7.x3 + " - 2px)"
      },
      fontSize: text$7.types.body.size,
      borderRadius: concepts$7.radius.default
    },
    large: {
      inset: spacing$7.x2 + " " + spacing$7.x4,
      outlinedInset: {
        vertical: "calc(" + spacing$7.x2 + " - 2px)",
        horizontal: "calc(" + spacing$7.x4 + " - 2px)"
      },
      fontSize: text$7.types.largeBody.size,
      borderRadius: concepts$7.radius.default
    },
    xLarge: {
      inset: spacing$7.x3 + " " + spacing$7.x5,
      outlinedInset: {
        vertical: "calc(" + spacing$7.x3 + " - 2px)",
        horizontal: "calc(" + spacing$7.x5 + " - 2px)"
      },
      fontSize: text$7.types.subtitle.size,
      borderRadius: concepts$7.radius.default
    }
  },
  primary: {
    backgroundColor: concepts$7.colors.primaryBrand.base,
    fontColor: concepts$7.typography.colors.inverseBody,
    fontFamily: concepts$7.typography.families.body
  },
  secondary: {
    backgroundColor: concepts$7.colors.secondaryBrand.base,
    fontColor: concepts$7.typography.colors.inverseBody,
    fontFamily: concepts$7.typography.families.body
  },
  warning: {
    backgroundColor: concepts$7.colors.warning.base,
    fontColor: concepts$7.typography.colors.inverseBody,
    fontFamily: concepts$7.typography.families.body
  },
  success: {
    backgroundColor: concepts$7.colors.success.base,
    fontColor: concepts$7.typography.colors.inverseBody,
    fontFamily: concepts$7.typography.families.body
  },
  danger: {
    backgroundColor: concepts$7.colors.danger.base,
    fontColor: concepts$7.typography.colors.inverseBody,
    fontFamily: concepts$7.typography.families.body
  },
  shadow: {
    backgroundColor: concepts$7.colors.foreground,
    fontColor: concepts$7.typography.colors.primaryBody,
    fontFamily: concepts$7.typography.families.body
  },
  accent: {
    backgroundColor: concepts$7.colors.accent.base,
    fontColor: concepts$7.typography.colors.primaryBody,
    fontFamily: concepts$7.typography.families.body
  },
  smoke: {
    backgroundColor: concepts$7.colors.background,
    fontColor: concepts$7.typography.colors.primaryBody,
    fontFamily: concepts$7.typography.families.body
  }
};
var slider$7 = {
  backgroundColor: concepts$7.colors.background,
  borderRadius: concepts$7.radius.small,
  handleRadius: concepts$7.radius.circle,
  handleColor: concepts$7.colors.foreground,
  handleShadow: concepts$7.shadows.light
};
var inputs$7 = {
  checkbox: {
    border: borders$7.default,
    borderRadius: concepts$7.radius.small,
    unCheckedBackgroundColor: concepts$7.colors.grayScale._800,
    checkmarkColor: concepts$7.typography.colors.inverseBody,
    labelColor: concepts$7.typography.colors.primaryBody
  },
  text: {
    borderRadius: concepts$7.radius.default,
    labelColor: concepts$7.colors.accent,
    placeholderColor: concepts$7.typography.colors.primaryBody,
    labelFontSize: text$7.types.caption.size,
    textFontSize: text$7.types.caption.size,
    borderWidth: borders$7.default,
    defaultFocusColor: concepts$7.colors.blueScale.base
  },
  select: {
    dropDownShadow: concepts$7.shadows.mediumDark,
    dropDownBackgroundColor: concepts$7.colors.foreground,
    boxShadow: concepts$7.shadows.light,
    borderRadius: concepts$7.radius.default
  },
  number: {
    backgroundColor: concepts$7.colors.grayScale._200,
    fontColor: concepts$7.typography.colors.inverseBody,
    fontSize: text$7.types.body.size,
    borderRadius: concepts$7.radius.default
  },
  backgroundColor: concepts$7.colors.background,
  fontSize: text$7.types.caption.size,
  fontColor: concepts$7.typography.colors.primaryBody,
  fontFamily: concepts$7.typography.families.body,
  colors: {
    primary: concepts$7.colors.primaryBrand.base,
    secondary: concepts$7.colors.secondaryBrand.base,
    success: concepts$7.colors.success.base,
    warning: concepts$7.colors.warning.base,
    danger: concepts$7.colors.danger.base,
    accent: concepts$7.colors.accent.base,
    dark: concepts$7.colors.primaryBrand._100,
    blue: concepts$7.colors.blueScale.base
  }
};
var icons$7 = {
  sizes: {
    xSmall: concepts$7.sizing.icons.xSmall,
    small: concepts$7.sizing.icons.small,
    medium: concepts$7.sizing.icons.medium,
    large: concepts$7.sizing.icons.large,
    xLarge: concepts$7.sizing.icons.xLarge
  },
  colors: {
    default: concepts$7.colors.grayScale._800,
    primaryBodyText: concepts$7.typography.colors.primaryBody,
    primaryHeadingText: concepts$7.typography.colors.primaryHeading,
    inverseBodyText: concepts$7.typography.colors.inverseBody,
    inverseHeadingText: concepts$7.typography.colors.inverseHeading,
    primaryBrand: concepts$7.colors.primaryBrand.base,
    secondaryBrand: concepts$7.colors.secondaryBrand.base,
    warning: concepts$7.colors.warning.base,
    danger: concepts$7.colors.danger.base,
    success: concepts$7.colors.success.base,
    background: concepts$7.colors.background,
    accent: concepts$7.colors.background
  }
};
var animatedCheckmark$7 = {
  sizeMap: {
    xSmall: concepts$7.sizing.icons.xSmall,
    small: concepts$7.sizing.icons.small,
    medium: concepts$7.sizing.icons.medium,
    large: concepts$7.sizing.icons.large,
    xLarge: concepts$7.sizing.icons.xLarge
  },
  colors: {
    primary: concepts$7.colors.primaryBrand.base,
    secondary: concepts$7.colors.secondaryBrand.base,
    success: concepts$7.colors.success.base,
    accent: concepts$7.colors.accent.base
  },
  strokeWidth: {
    small: concepts$7.borders.small,
    default: concepts$7.borders.default,
    large: concepts$7.borders.large
  },
  borderRadius: concepts$7.radius.circle,
  checkmarkFilledColor: concepts$7.colors.foreground
};
var avatar$7 = {
  borderWidth: borders$7.thin,
  borderColor: concepts$7.colors.grayScale._800,
  borderRadius: concepts$7.radius.circle,
  boxShadow: concepts$7.shadows.mediumLight,
  backgroundColor: concepts$7.colors.foreground,
  bubbleBackgroundColors: {
    isActive: concepts$7.colors.success.base,
    default: concepts$7.colors.grayScale._800
  },
  defaultBackgroundColors: {
    primary: concepts$7.colors.primaryBrand.base,
    secondary: concepts$7.colors.secondaryBrand.base,
    success: concepts$7.colors.success.base,
    accent: concepts$7.colors.accent.base,
    warning: concepts$7.colors.warning.base,
    danger: concepts$7.colors.danger.base
  },
  sizes: {
    xSmall: {
      avatarSize: 34,
      bubbleLeftPosition: 22,
      bubbleSize: 12
    },
    small: {
      avatarSize: 44,
      bubbleLeftPosition: 28,
      bubbleSize: 14
    },
    medium: {
      avatarSize: 60,
      bubbleLeftPosition: 40,
      bubbleSize: 16
    },
    large: {
      avatarSize: 76,
      bubbleLeftPosition: 52,
      bubbleSize: 20
    },
    xLarge: {
      avatarSize: 96,
      bubbleLeftPosition: 64,
      bubbleSize: 28
    }
  }
};
var loadingSpinner$7 = {
  size: {
    xSmall: concepts$7.sizing.icons.xSmall,
    small: concepts$7.sizing.icons.small,
    medium: concepts$7.sizing.icons.medium,
    large: concepts$7.sizing.icons.large,
    xLarge: concepts$7.sizing.icons.xLarge
  },
  colors: {
    primary: concepts$7.colors.primaryBrand.base,
    secondary: concepts$7.colors.secondaryBrand.base,
    success: concepts$7.colors.success.base,
    warning: concepts$7.colors.warning.base,
    danger: concepts$7.colors.danger.base
  }
};
var separator$7 = {
  border: borders$7.default,
  color: concepts$7.colors.grayScale._800
};
var card$7 = {
  backgroundColor: concepts$7.colors.warning.base,
  borderRadius: concepts$7.radius.default,
  boxShadow: concepts$7.shadows.mediumDark,
  defaultInset: spacing$7.x4 + " " + spacing$7.x2 + " " + spacing$7.x2 + " " + spacing$7.x2
};
var dialog$7 = {
  backgroundColor: concepts$7.colors.foreground,
  borderRadius: concepts$7.radius.default,
  boxShadow: concepts$7.shadows.mediumDark,
  defaultInset: spacing$7.x6 + " " + spacing$7.x4 + " " + spacing$7.x4 + " " + spacing$7.x4
};
var navigation$7 = {
  backgroundColor: concepts$7.colors.foreground,
  border: borders$7.thin,
  borderColor: concepts$7.colors.grayScale._800
};
var drawer$7 = {
  backgroundColor: concepts$7.colors.foreground,
  borderRadius: concepts$7.radius.small,
  boxShadow: concepts$7.shadows.mediumLight,
  bottomDirectionShadow: concepts$7.shadows.generate('mediumLight', Color$1(concepts$7.colors.defaultShadowColor).rgb().alpha(0.1).toString(), true),
  hoverBoxShadow: concepts$7.shadows.mediumDark,
  hoverBottomDirectionShadow: concepts$7.shadows.generate('mediumDark', Color$1(concepts$7.colors.defaultShadowColor).rgb().alpha(0.1).toString(), true),
  defaultInset: "" + spacing$7.x1,
  tab: {
    dark: {
      backgroundColor: concepts$7.colors.grayScale._600,
      arrowColor: concepts$7.colors.foreground
    },
    light: {
      backgroundColor: concepts$7.colors.foreground,
      arrowColor: icons$7.colors.default
    }
  }
};
var sheet$7 = {
  mobileBorderRadius: concepts$7.radius.large,
  desktopBorderRadius: concepts$7.radius.default,
  backgroundColor: concepts$7.colors.foreground,
  boxShadow: concepts$7.shadows.mediumDark
};
var toggle$7 = {
  isActiveColor: concepts$7.colors.success.base,
  isNotActiveColor: concepts$7.colors.grayScale._900
};
var tooltip$7 = {
  lightBackgroundColor: concepts$7.colors.foreground,
  darkBackgroundColor: concepts$7.colors.grayScale._100,
  boxShadow: concepts$7.shadows.mediumLight,
  borderColor: concepts$7.colors.background
};
var colorPicker$7 = {
  backgroundColor: concepts$7.colors.background,
  borderColor: 'transparent',
  boxShadow: concepts$7.shadows.mediumLight,
  borderRadius: concepts$7.radius.small
};
var sphere$7 = {
  xSmall: {
    circumference: concepts$7.sizing.sphere.xSmall,
    textType: 'title'
  },
  small: {
    circumference: concepts$7.sizing.sphere.small,
    textType: 'title'
  },
  medium: {
    circumference: concepts$7.sizing.sphere.medium,
    textType: 'heroTitle'
  },
  large: {
    circumference: concepts$7.sizing.sphere.large,
    textType: 'heroTitle'
  },
  xLarge: {
    circumference: concepts$7.sizing.sphere.xLarge,
    textType: 'heroTitle'
  }
};
var widget$7 = {
  primary: {
    backgroundColor: concepts$7.colors.primaryBrand.base,
    fontColor: 'inverseBody'
  },
  secondary: {
    backgroundColor: concepts$7.colors.secondaryBrand.base,
    fontColor: 'inverseBody'
  },
  warning: {
    backgroundColor: concepts$7.colors.warning.base,
    fontColor: 'primaryBody'
  },
  success: {
    backgroundColor: concepts$7.colors.success.base,
    fontColor: 'primaryBody'
  },
  danger: {
    backgroundColor: concepts$7.colors.danger.base,
    fontColor: 'inverseBody'
  },
  shadow: {
    backgroundColor: concepts$7.colors.foreground,
    fontColor: 'primaryBody'
  },
  accent: {
    backgroundColor: concepts$7.colors.accent.base,
    fontColor: 'inverseBody'
  }
};
var colorOption$7 = {
  defaults: {
    circleFill: concepts$7.shadows.generate([0, 0, 6, 1], Color$1(concepts$7.colors.defaultShadowColor).rgb().alpha(0.1).toString()),
    innerRing: concepts$7.shadows.generate([0, 0, 0, 11], Color$1([20, 30, 50]).rgb().alpha(0.03).toString()),
    outerRing: concepts$7.shadows.generate([0, 0, 6, 1], Color$1(concepts$7.colors.defaultShadowColor).rgb().alpha(0.06).toString())
  }
};
var entities$7 = {
  mediaQueries: mediaQueries$b,
  hocs: hocs$7,
  text: text$7,
  spacing: spacing$7,
  buttons: buttons$7,
  inputs: inputs$7,
  icons: icons$7,
  card: card$7,
  separator: separator$7,
  loadingSpinner: loadingSpinner$7,
  animatedCheckmark: animatedCheckmark$7,
  avatar: avatar$7,
  dialog: dialog$7,
  sheet: sheet$7,
  navigation: navigation$7,
  toggle: toggle$7,
  tooltip: tooltip$7,
  drawer: drawer$7,
  slider: slider$7,
  colorPicker: colorPicker$7,
  sphere: sphere$7,
  widget: widget$7,
  colorOption: colorOption$7
};

var CARAMEL$1 = '#CE8E47'; // primary
var BASE_GRAY$5 = '#525A59'; // secondary
var TAN = '#E0D5C0'; // accent
var OFF_WHITE = '#FBF9F4'; //base
var GREEN$6 = '#1DE6B9';
var YELLOW$5 = '#F7A14D';
var RED$5 = '#FB2E4B';
var WHITE$6 = '#FFF';
var BLUE$5 = '#1DC5E6';
var PURPLE$6 = '#703A96';
var caramelSwatch$1 = computeColorSwatch(CARAMEL$1);
var baseGraySwatch$4 = computeColorSwatch(BASE_GRAY$5);
var tanSwatch = computeColorSwatch(TAN);
var greenSwatch$6 = computeColorSwatch(GREEN$6);
var yellowSwatch$6 = computeColorSwatch(YELLOW$5);
var redSwatch$6 = computeColorSwatch(RED$5);
var blueSwatch$5 = computeColorSwatch(BLUE$5);
var purpleSwatch$6 = computeColorSwatch(PURPLE$6);
var BASE$5 = OFF_WHITE;
var baseGray$4 = BASE_GRAY$5;
var colors$d = {
  caramelSwatch: caramelSwatch$1,
  tanSwatch: tanSwatch,
  baseGraySwatch: baseGraySwatch$4,
  greenSwatch: greenSwatch$6,
  redSwatch: redSwatch$6,
  yellowSwatch: yellowSwatch$6,
  blueSwatch: blueSwatch$5,
  baseGray: baseGray$4,
  purpleSwatch: purpleSwatch$6,
  BASE: BASE$5,
  WHITE: WHITE$6
};

var BASE_VALUE$6 = 1;
var BASE_UNITS$6 = 'rem';
// Rename to typography
var computeFontSize$6 = function (multiplier) {
  return BASE_VALUE$6 * multiplier;
};
var XSMALL_FONT$6 = computeFontSize$6(0.75) + BASE_UNITS$6;
var SMALL_FONT$6 = computeFontSize$6(0.875) + BASE_UNITS$6;
var MEDIUM_FONT$6 = computeFontSize$6(1) + BASE_UNITS$6;
var MEDIUM_PLUS_FONT$6 = computeFontSize$6(1.25) + BASE_UNITS$6;
var LARGE_FONT$6 = computeFontSize$6(1.75) + BASE_UNITS$6;
var XLARGE_FONT$6 = computeFontSize$6(2.125) + BASE_UNITS$6;
var XXLARGE_FONT$6 = computeFontSize$6(3) + BASE_UNITS$6;
var XSMALL_LINE_HEIGHT$6 = computeFontSize$6(1.5) + BASE_UNITS$6;
var SMALL_LINE_HEIGHT$6 = computeFontSize$6(1.75) + BASE_UNITS$6;
var MEDIUM_LINE_HEIGHT$6 = computeFontSize$6(2) + BASE_UNITS$6;
var LARGE_LINE_HEIGHT$6 = computeFontSize$6(2.5) + BASE_UNITS$6;
var XLARGE_LINE_HEIGHT$6 = computeFontSize$6(3) + BASE_UNITS$6;
var XXLARGE_LINE_HEIGHT$6 = computeFontSize$6(3.5) + BASE_UNITS$6;
var HEADING_FONT_FAMILY$6 = "'Poppins', 'Open Sans',  sans-serif";
var BODY_FONT_FAMILY$6 = "'Poppins', 'Open Sans', sans-serif";
var BODY_FONT_IMPORT$6 = 'https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&display=swap';
var HEADING_FONT_IMPORT$6 = 'https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&display=swap';
var WEIGHT_THIN$6 = 300;
var WEIGHT_REGULAR$6 = 400;
var WEIGHT_SEMI_BOLD$6 = 600;
var WEIGHT_BOLD$6 = 700;
var WEIGHT_EXTRA_BOLD$6 = 800;

var colors$c = {
  primaryBrand: colors$d.caramelSwatch,
  secondaryBrand: colors$d.baseGraySwatch,
  accent: colors$d.tanSwatch,
  success: colors$d.greenSwatch,
  warning: colors$d.yellowSwatch,
  danger: colors$d.redSwatch,
  background: colors$d.BASE,
  foreground: colors$d.WHITE,
  grayScale: colors$d.baseGraySwatch,
  blueScale: colors$d.blueSwatch,
  purpleScale: colors$d.purpleSwatch,
  white: colors$d.WHITE,
  defaultShadowColor: SHADOW_DEFAULT_COLOR
};
var concepts$6 = {
  colors: __assign({}, colors$c),
  radius: {
    none: radius.none,
    small: radius.small,
    medium: radius.mediumLarge,
    default: radius.medium,
    large: radius.large,
    circle: radius.circle
  },
  shadows: {
    light: computeShadow('light', Color$1(colors$c.defaultShadowColor).rgb().alpha(0.08).toString()),
    mediumLight: computeShadow('mediumLight', Color$1(colors$c.defaultShadowColor).rgb().alpha(0.08).toString()),
    mediumDark: computeShadow('mediumDark', Color$1(colors$c.defaultShadowColor).rgb().alpha(0.1).toString()),
    dark: computeShadow('dark', Color$1(colors$c.defaultShadowColor).rgb().alpha(0.12).toString()),
    generate: computeShadow
  },
  typography: {
    imports: {
      body: BODY_FONT_IMPORT$6,
      heading: HEADING_FONT_IMPORT$6
    },
    colors: {
      primaryHeading: colors$c.grayScale._200,
      primaryBody: colors$c.grayScale.base,
      inverseHeading: colors$c.white,
      inverseBody: colors$c.white
    },
    families: {
      heading: BODY_FONT_FAMILY$6,
      body: HEADING_FONT_FAMILY$6
    },
    sizes: {
      xSmall: XSMALL_FONT$6,
      small: SMALL_FONT$6,
      medium: MEDIUM_FONT$6,
      mediumPlus: MEDIUM_PLUS_FONT$6,
      large: LARGE_FONT$6,
      xLarge: XLARGE_FONT$6,
      xxLarge: XXLARGE_FONT$6
    },
    weights: {
      thin: WEIGHT_THIN$6,
      regular: WEIGHT_REGULAR$6,
      semiBold: WEIGHT_SEMI_BOLD$6,
      bold: WEIGHT_BOLD$6,
      extraBold: WEIGHT_EXTRA_BOLD$6
    },
    lineHeight: {
      xSmall: XSMALL_LINE_HEIGHT$6,
      small: SMALL_LINE_HEIGHT$6,
      medium: MEDIUM_LINE_HEIGHT$6,
      large: LARGE_LINE_HEIGHT$6,
      xLarge: XLARGE_LINE_HEIGHT$6,
      xxLarge: XXLARGE_LINE_HEIGHT$6
    }
  },
  borders: {
    thin: borders$9.xSmall,
    small: borders$9.small,
    default: borders$9.medium,
    large: borders$9.large,
    bold: borders$9.xLarge
  },
  spacing: {
    baseValue: BASE_VALUE$9,
    baseUnits: BASE_UNITS$9
  },
  mediaQueries: {
    small: mediaQuerySizes.small,
    medium: mediaQuerySizes.medium,
    large: mediaQuerySizes.large,
    xLarge: mediaQuerySizes.xLarge
  },
  sizing: {
    icons: {
      xSmall: icons$9.xSmall,
      small: icons$9.small,
      medium: icons$9.medium,
      large: icons$9.large,
      xLarge: icons$9.xLarge
    },
    sphere: {
      xSmall: sphere$9.xSmall,
      small: sphere$9.small,
      medium: sphere$9.medium,
      large: sphere$9.large,
      xLarge: sphere$9.xLarge
    }
  }
};

var space$6 = computeSpace(concepts$6.spacing.baseValue, concepts$6.spacing.baseUnits);
var spacing$6 = {
  x0: space$6(0),
  x_25: space$6(0.25),
  x_5: space$6(0.5),
  x_75: space$6(0.75),
  x1: space$6(1),
  x1_5: space$6(1.5),
  x2: space$6(2),
  x2_5: space$6(2.5),
  x3: space$6(3),
  x4: space$6(4),
  x5: space$6(5),
  x6: space$6(6),
  x7: space$6(7),
  x8: space$6(8),
  x9: space$6(9)
};
var borders$6 = {
  thin: concepts$6.borders.thin + "px",
  default: concepts$6.borders.default + "px",
  bold: concepts$6.borders.bold + "px"
};
var text$6 = {
  imports: {
    body: concepts$6.typography.imports.body,
    heading: concepts$6.typography.imports.heading
  },
  families: {
    body: concepts$6.typography.families.body,
    heading: concepts$6.typography.families.heading
  },
  types: {
    heroTitle: {
      color: concepts$6.typography.colors.primaryHeading,
      size: concepts$6.typography.sizes.xxLarge,
      fontWeight: concepts$6.typography.weights.extraBold,
      lineHeight: concepts$6.typography.lineHeight.xxLarge,
      fontFamily: concepts$6.typography.families.heading
    },
    title: {
      color: concepts$6.typography.colors.primaryHeading,
      size: concepts$6.typography.sizes.xLarge,
      fontWeight: concepts$6.typography.weights.bold,
      lineHeight: concepts$6.typography.lineHeight.xLarge,
      fontFamily: concepts$6.typography.families.heading
    },
    subtitle: {
      color: concepts$6.typography.colors.primaryHeading,
      size: concepts$6.typography.sizes.large,
      fontWeight: concepts$6.typography.weights.regular,
      lineHeight: concepts$6.typography.lineHeight.medium,
      fontFamily: concepts$6.typography.families.heading
    },
    largeBody: {
      color: concepts$6.typography.colors.primaryBody,
      size: concepts$6.typography.sizes.mediumPlus,
      fontWeight: concepts$6.typography.weights.regular,
      lineHeight: concepts$6.typography.lineHeight.small,
      fontFamily: concepts$6.typography.families.body
    },
    body: {
      color: concepts$6.typography.colors.primaryBody,
      size: concepts$6.typography.sizes.medium,
      fontWeight: concepts$6.typography.weights.regular,
      lineHeight: concepts$6.typography.lineHeight.small,
      fontFamily: concepts$6.typography.families.body
    },
    caption: {
      color: concepts$6.typography.colors.primaryBody,
      size: concepts$6.typography.sizes.small,
      fontWeight: concepts$6.typography.weights.regular,
      lineHeight: concepts$6.typography.lineHeight.small,
      fontFamily: concepts$6.typography.families.body
    },
    footnote: {
      color: concepts$6.typography.colors.primaryBody,
      size: concepts$6.typography.sizes.xSmall,
      fontWeight: concepts$6.typography.weights.regular,
      lineHeight: concepts$6.typography.lineHeight.xSmall,
      fontFamily: concepts$6.typography.families.body
    }
  },
  colors: {
    primaryBrand: concepts$6.colors.primaryBrand.base,
    secondaryBrand: concepts$6.colors.secondaryBrand.base,
    primaryHeading: concepts$6.typography.colors.primaryHeading,
    primaryBody: concepts$6.typography.colors.primaryBody,
    inverseHeading: concepts$6.typography.colors.inverseHeading,
    inverseBody: concepts$6.typography.colors.inverseBody,
    accent: concepts$6.colors.accent.base,
    success: concepts$6.colors.success.base,
    warning: concepts$6.colors.warning.base,
    danger: concepts$6.colors.danger.base,
    anchorBlue: concepts$6.colors.blueScale.base
  },
  weights: {
    thin: concepts$6.typography.weights.thin,
    regular: concepts$6.typography.weights.regular,
    semiBold: concepts$6.typography.weights.semiBold,
    bold: concepts$6.typography.weights.bold,
    extraBold: concepts$6.typography.weights.extraBold
  }
};
var mediaQueries$a = {
  queries: {
    xSmall: "@media screen and (min-width: 0px)",
    // Large Mobile
    small: "@media screen and (min-width: " + concepts$6.mediaQueries.small + "px)",
    // Tablet
    medium: "@media screen and (min-width: " + concepts$6.mediaQueries.medium + "px)",
    // Large Tablet
    large: "@media screen and (min-width: " + concepts$6.mediaQueries.large + "px)",
    // Desktop
    xLarge: "@media screen and (min-width: " + concepts$6.mediaQueries.xLarge + "px)"
  },
  rawValues: {
    xSmall: 0,
    small: concepts$6.mediaQueries.small,
    medium: concepts$6.mediaQueries.medium,
    large: concepts$6.mediaQueries.large,
    xLarge: concepts$6.mediaQueries.xLarge
  }
};
var hocs$6 = {
  colors: {
    swatches: {
      primaryBrand: concepts$6.colors.primaryBrand,
      secondaryBrand: concepts$6.colors.secondaryBrand,
      success: concepts$6.colors.success,
      danger: concepts$6.colors.danger,
      warning: concepts$6.colors.warning,
      accent: concepts$6.colors.accent,
      grayScale: concepts$6.colors.grayScale,
      blueScale: concepts$6.colors.blueScale,
      purpleScale: concepts$6.colors.purpleScale
    },
    background: concepts$6.colors.background,
    foreground: concepts$6.colors.foreground
  },
  textColors: text$6.colors,
  spacing: spacing$6,
  borderRadius: concepts$6.radius,
  shadows: concepts$6.shadows,
  borders: borders$6
};
// TODO - revisit buttons for contrast and font color
var buttons$6 = {
  boxShadow: concepts$6.shadows.generate,
  borderSize: borders$6.default,
  outlinedBackgroundColor: concepts$6.colors.foreground,
  fontWeight: concepts$6.typography.weights.semiBold,
  sizes: {
    xSmall: {
      inset: spacing$6.x1 + " " + spacing$6.x2,
      outlinedInset: {
        vertical: "calc(" + spacing$6.x1 + " - 2px)",
        horizontal: "calc(" + spacing$6.x2 + " - 2px)"
      },
      fontSize: text$6.types.footnote.size,
      borderRadius: concepts$6.radius.default
    },
    small: {
      inset: spacing$6.x1_5 + " " + spacing$6.x2_5,
      outlinedInset: {
        vertical: "calc(" + spacing$6.x1_5 + " - 2px)",
        horizontal: "calc(" + spacing$6.x2_5 + " - 2px)"
      },
      fontSize: text$6.types.caption.size,
      borderRadius: concepts$6.radius.default
    },
    medium: {
      inset: spacing$6.x1_5 + " " + spacing$6.x3,
      outlinedInset: {
        vertical: "calc(" + spacing$6.x1_5 + " - 2px)",
        horizontal: "calc(" + spacing$6.x3 + " - 2px)"
      },
      fontSize: text$6.types.body.size,
      borderRadius: concepts$6.radius.default
    },
    large: {
      inset: spacing$6.x2 + " " + spacing$6.x4,
      outlinedInset: {
        vertical: "calc(" + spacing$6.x2 + " - 2px)",
        horizontal: "calc(" + spacing$6.x4 + " - 2px)"
      },
      fontSize: text$6.types.largeBody.size,
      borderRadius: concepts$6.radius.default
    },
    xLarge: {
      inset: spacing$6.x3 + " " + spacing$6.x5,
      outlinedInset: {
        vertical: "calc(" + spacing$6.x3 + " - 2px)",
        horizontal: "calc(" + spacing$6.x5 + " - 2px)"
      },
      fontSize: text$6.types.subtitle.size,
      borderRadius: concepts$6.radius.default
    }
  },
  primary: {
    backgroundColor: concepts$6.colors.primaryBrand.base,
    fontColor: concepts$6.typography.colors.inverseBody,
    fontFamily: concepts$6.typography.families.body
  },
  secondary: {
    backgroundColor: concepts$6.colors.secondaryBrand.base,
    fontColor: concepts$6.typography.colors.inverseBody,
    fontFamily: concepts$6.typography.families.body
  },
  warning: {
    backgroundColor: concepts$6.colors.warning.base,
    fontColor: concepts$6.typography.colors.inverseBody,
    fontFamily: concepts$6.typography.families.body
  },
  success: {
    backgroundColor: concepts$6.colors.success.base,
    fontColor: concepts$6.typography.colors.inverseBody,
    fontFamily: concepts$6.typography.families.body
  },
  danger: {
    backgroundColor: concepts$6.colors.danger.base,
    fontColor: concepts$6.typography.colors.inverseBody,
    fontFamily: concepts$6.typography.families.body
  },
  shadow: {
    backgroundColor: concepts$6.colors.foreground,
    fontColor: concepts$6.typography.colors.primaryBody,
    fontFamily: concepts$6.typography.families.body
  },
  accent: {
    backgroundColor: concepts$6.colors.accent.base,
    fontColor: concepts$6.typography.colors.primaryBody,
    fontFamily: concepts$6.typography.families.body
  },
  smoke: {
    backgroundColor: concepts$6.colors.background,
    fontColor: concepts$6.typography.colors.primaryBody,
    fontFamily: concepts$6.typography.families.body
  }
};
var slider$6 = {
  backgroundColor: concepts$6.colors.background,
  borderRadius: concepts$6.radius.small,
  handleRadius: concepts$6.radius.circle,
  handleColor: concepts$6.colors.foreground,
  handleShadow: concepts$6.shadows.light
};
var inputs$6 = {
  checkbox: {
    border: borders$6.default,
    borderRadius: concepts$6.radius.small,
    unCheckedBackgroundColor: concepts$6.colors.grayScale._800,
    checkmarkColor: concepts$6.typography.colors.inverseBody,
    labelColor: concepts$6.typography.colors.primaryBody
  },
  text: {
    borderRadius: concepts$6.radius.default,
    labelColor: concepts$6.colors.accent,
    placeholderColor: concepts$6.typography.colors.primaryBody,
    labelFontSize: text$6.types.caption.size,
    textFontSize: text$6.types.caption.size,
    borderWidth: borders$6.default,
    defaultFocusColor: concepts$6.colors.blueScale.base
  },
  select: {
    dropDownShadow: concepts$6.shadows.mediumDark,
    dropDownBackgroundColor: concepts$6.colors.foreground,
    boxShadow: concepts$6.shadows.light,
    borderRadius: concepts$6.radius.default
  },
  number: {
    backgroundColor: concepts$6.colors.grayScale._200,
    fontColor: concepts$6.typography.colors.inverseBody,
    fontSize: text$6.types.body.size,
    borderRadius: concepts$6.radius.default
  },
  backgroundColor: concepts$6.colors.background,
  fontSize: text$6.types.caption.size,
  fontColor: concepts$6.typography.colors.primaryBody,
  fontFamily: concepts$6.typography.families.body,
  colors: {
    primary: concepts$6.colors.primaryBrand.base,
    secondary: concepts$6.colors.secondaryBrand.base,
    success: concepts$6.colors.success.base,
    warning: concepts$6.colors.warning.base,
    danger: concepts$6.colors.danger.base,
    accent: concepts$6.colors.accent.base,
    dark: concepts$6.colors.primaryBrand._100,
    blue: concepts$6.colors.blueScale.base
  }
};
var icons$6 = {
  sizes: {
    xSmall: concepts$6.sizing.icons.xSmall,
    small: concepts$6.sizing.icons.small,
    medium: concepts$6.sizing.icons.medium,
    large: concepts$6.sizing.icons.large,
    xLarge: concepts$6.sizing.icons.xLarge
  },
  colors: {
    default: concepts$6.colors.grayScale._800,
    primaryBodyText: concepts$6.typography.colors.primaryBody,
    primaryHeadingText: concepts$6.typography.colors.primaryHeading,
    inverseBodyText: concepts$6.typography.colors.inverseBody,
    inverseHeadingText: concepts$6.typography.colors.inverseHeading,
    primaryBrand: concepts$6.colors.primaryBrand.base,
    secondaryBrand: concepts$6.colors.secondaryBrand.base,
    warning: concepts$6.colors.warning.base,
    danger: concepts$6.colors.danger.base,
    success: concepts$6.colors.success.base,
    background: concepts$6.colors.background,
    accent: concepts$6.colors.background
  }
};
var animatedCheckmark$6 = {
  sizeMap: {
    xSmall: concepts$6.sizing.icons.xSmall,
    small: concepts$6.sizing.icons.small,
    medium: concepts$6.sizing.icons.medium,
    large: concepts$6.sizing.icons.large,
    xLarge: concepts$6.sizing.icons.xLarge
  },
  colors: {
    primary: concepts$6.colors.primaryBrand.base,
    secondary: concepts$6.colors.secondaryBrand.base,
    success: concepts$6.colors.success.base,
    accent: concepts$6.colors.accent.base
  },
  strokeWidth: {
    small: concepts$6.borders.small,
    default: concepts$6.borders.default,
    large: concepts$6.borders.large
  },
  borderRadius: concepts$6.radius.circle,
  checkmarkFilledColor: concepts$6.colors.foreground
};
var avatar$6 = {
  borderWidth: borders$6.thin,
  borderColor: concepts$6.colors.grayScale._800,
  borderRadius: concepts$6.radius.circle,
  boxShadow: concepts$6.shadows.mediumLight,
  backgroundColor: concepts$6.colors.foreground,
  bubbleBackgroundColors: {
    isActive: concepts$6.colors.success.base,
    default: concepts$6.colors.grayScale._800
  },
  defaultBackgroundColors: {
    primary: concepts$6.colors.primaryBrand.base,
    secondary: concepts$6.colors.secondaryBrand.base,
    success: concepts$6.colors.success.base,
    accent: concepts$6.colors.accent.base,
    warning: concepts$6.colors.warning.base,
    danger: concepts$6.colors.danger.base
  },
  sizes: {
    xSmall: {
      avatarSize: 34,
      bubbleLeftPosition: 22,
      bubbleSize: 12
    },
    small: {
      avatarSize: 44,
      bubbleLeftPosition: 28,
      bubbleSize: 14
    },
    medium: {
      avatarSize: 60,
      bubbleLeftPosition: 40,
      bubbleSize: 16
    },
    large: {
      avatarSize: 76,
      bubbleLeftPosition: 52,
      bubbleSize: 20
    },
    xLarge: {
      avatarSize: 96,
      bubbleLeftPosition: 64,
      bubbleSize: 28
    }
  }
};
var loadingSpinner$6 = {
  size: {
    xSmall: concepts$6.sizing.icons.xSmall,
    small: concepts$6.sizing.icons.small,
    medium: concepts$6.sizing.icons.medium,
    large: concepts$6.sizing.icons.large,
    xLarge: concepts$6.sizing.icons.xLarge
  },
  colors: {
    primary: concepts$6.colors.primaryBrand.base,
    secondary: concepts$6.colors.secondaryBrand.base,
    success: concepts$6.colors.success.base,
    warning: concepts$6.colors.warning.base,
    danger: concepts$6.colors.danger.base
  }
};
var separator$6 = {
  border: borders$6.default,
  color: concepts$6.colors.grayScale._800
};
var card$6 = {
  backgroundColor: concepts$6.colors.warning.base,
  borderRadius: concepts$6.radius.default,
  boxShadow: concepts$6.shadows.mediumDark,
  defaultInset: spacing$6.x4 + " " + spacing$6.x2 + " " + spacing$6.x2 + " " + spacing$6.x2
};
var dialog$6 = {
  backgroundColor: concepts$6.colors.foreground,
  borderRadius: concepts$6.radius.default,
  boxShadow: concepts$6.shadows.mediumDark,
  defaultInset: spacing$6.x6 + " " + spacing$6.x4 + " " + spacing$6.x4 + " " + spacing$6.x4
};
var navigation$6 = {
  backgroundColor: concepts$6.colors.foreground,
  border: borders$6.thin,
  borderColor: concepts$6.colors.grayScale._800
};
var drawer$6 = {
  backgroundColor: concepts$6.colors.foreground,
  borderRadius: concepts$6.radius.small,
  boxShadow: concepts$6.shadows.mediumLight,
  bottomDirectionShadow: concepts$6.shadows.generate('mediumLight', Color$1(concepts$6.colors.defaultShadowColor).rgb().alpha(0.1).toString(), true),
  hoverBoxShadow: concepts$6.shadows.mediumDark,
  hoverBottomDirectionShadow: concepts$6.shadows.generate('mediumDark', Color$1(concepts$6.colors.defaultShadowColor).rgb().alpha(0.1).toString(), true),
  defaultInset: "" + spacing$6.x1,
  tab: {
    dark: {
      backgroundColor: concepts$6.colors.grayScale._600,
      arrowColor: concepts$6.colors.foreground
    },
    light: {
      backgroundColor: concepts$6.colors.foreground,
      arrowColor: icons$6.colors.default
    }
  }
};
var sheet$6 = {
  mobileBorderRadius: concepts$6.radius.large,
  desktopBorderRadius: concepts$6.radius.default,
  backgroundColor: concepts$6.colors.foreground,
  boxShadow: concepts$6.shadows.mediumDark
};
var toggle$6 = {
  isActiveColor: concepts$6.colors.success.base,
  isNotActiveColor: concepts$6.colors.grayScale._900
};
var tooltip$6 = {
  lightBackgroundColor: concepts$6.colors.foreground,
  darkBackgroundColor: concepts$6.colors.grayScale._100,
  boxShadow: concepts$6.shadows.mediumLight,
  borderColor: concepts$6.colors.background
};
var colorPicker$6 = {
  backgroundColor: concepts$6.colors.background,
  borderColor: 'transparent',
  boxShadow: concepts$6.shadows.mediumLight,
  borderRadius: concepts$6.radius.small
};
var sphere$6 = {
  xSmall: {
    circumference: concepts$6.sizing.sphere.xSmall,
    textType: 'title'
  },
  small: {
    circumference: concepts$6.sizing.sphere.small,
    textType: 'title'
  },
  medium: {
    circumference: concepts$6.sizing.sphere.medium,
    textType: 'heroTitle'
  },
  large: {
    circumference: concepts$6.sizing.sphere.large,
    textType: 'heroTitle'
  },
  xLarge: {
    circumference: concepts$6.sizing.sphere.xLarge,
    textType: 'heroTitle'
  }
};
var widget$6 = {
  primary: {
    backgroundColor: concepts$6.colors.primaryBrand.base,
    fontColor: 'inverseBody'
  },
  secondary: {
    backgroundColor: concepts$6.colors.secondaryBrand.base,
    fontColor: 'inverseBody'
  },
  warning: {
    backgroundColor: concepts$6.colors.warning.base,
    fontColor: 'primaryBody'
  },
  success: {
    backgroundColor: concepts$6.colors.success.base,
    fontColor: 'primaryBody'
  },
  danger: {
    backgroundColor: concepts$6.colors.danger.base,
    fontColor: 'inverseBody'
  },
  shadow: {
    backgroundColor: concepts$6.colors.foreground,
    fontColor: 'primaryBody'
  },
  accent: {
    backgroundColor: concepts$6.colors.accent.base,
    fontColor: 'primaryBody'
  }
};
var colorOption$6 = {
  defaults: {
    circleFill: concepts$6.shadows.generate([0, 0, 6, 1], Color$1(concepts$6.colors.defaultShadowColor).rgb().alpha(0.1).toString()),
    innerRing: concepts$6.shadows.generate([0, 0, 0, 11], Color$1([20, 30, 50]).rgb().alpha(0.03).toString()),
    outerRing: concepts$6.shadows.generate([0, 0, 6, 1], Color$1(concepts$6.colors.defaultShadowColor).rgb().alpha(0.06).toString())
  }
};
var entities$6 = {
  mediaQueries: mediaQueries$a,
  hocs: hocs$6,
  text: text$6,
  spacing: spacing$6,
  buttons: buttons$6,
  inputs: inputs$6,
  icons: icons$6,
  card: card$6,
  separator: separator$6,
  loadingSpinner: loadingSpinner$6,
  animatedCheckmark: animatedCheckmark$6,
  avatar: avatar$6,
  dialog: dialog$6,
  sheet: sheet$6,
  navigation: navigation$6,
  toggle: toggle$6,
  tooltip: tooltip$6,
  drawer: drawer$6,
  slider: slider$6,
  colorPicker: colorPicker$6,
  sphere: sphere$6,
  widget: widget$6,
  colorOption: colorOption$6
};

var BRIGHT_BLUE = '#140FEE';
var TEAL$2 = '#35A4BC';
var BRIGHT_GREEN = '#74C468';
var BASE_GRAY$4 = '#696767';
var GREEN$5 = '#1DE6B9';
var YELLOW$4 = '#F7A14D';
var RED$4 = '#FB2E4B';
var WHITE$5 = '#FFF';
var BLUE$4 = '#1DC5E6';
var PURPLE$5 = '#703A96';
var brightBlueSwatch$1 = computeColorSwatch(BRIGHT_BLUE);
var tealSwatch$2 = computeColorSwatch(TEAL$2);
var brightGreenSwatch = computeColorSwatch(BRIGHT_GREEN);
var baseGraySwatch$3 = computeColorSwatch(BASE_GRAY$4);
var greenSwatch$5 = computeColorSwatch(GREEN$5);
var yellowSwatch$5 = computeColorSwatch(YELLOW$4);
var redSwatch$5 = computeColorSwatch(RED$4);
var blueSwatch$4 = computeColorSwatch(BLUE$4);
var purpleSwatch$5 = computeColorSwatch(PURPLE$5);
var BASE$4 = '#F5F6F7';
var baseGray$3 = BASE_GRAY$4;
var colors$b = {
  brightBlueSwatch: brightBlueSwatch$1,
  tealSwatch: tealSwatch$2,
  greenSwatch: greenSwatch$5,
  redSwatch: redSwatch$5,
  yellowSwatch: yellowSwatch$5,
  brightGreenSwatch: brightGreenSwatch,
  blueSwatch: blueSwatch$4,
  baseGraySwatch: baseGraySwatch$3,
  baseGray: baseGray$3,
  purpleSwatch: purpleSwatch$5,
  BASE: BASE$4,
  WHITE: WHITE$5
};

var BASE_VALUE$5 = 1;
var BASE_UNITS$5 = 'rem';
// Rename to typography
var computeFontSize$5 = function (multiplier) {
  return BASE_VALUE$5 * multiplier;
};
var XSMALL_FONT$5 = computeFontSize$5(0.75) + BASE_UNITS$5;
var SMALL_FONT$5 = computeFontSize$5(0.875) + BASE_UNITS$5;
var MEDIUM_FONT$5 = computeFontSize$5(1) + BASE_UNITS$5;
var MEDIUM_PLUS_FONT$5 = computeFontSize$5(1.25) + BASE_UNITS$5;
var LARGE_FONT$5 = computeFontSize$5(1.75) + BASE_UNITS$5;
var XLARGE_FONT$5 = computeFontSize$5(2.125) + BASE_UNITS$5;
var XXLARGE_FONT$5 = computeFontSize$5(3) + BASE_UNITS$5;
var XSMALL_LINE_HEIGHT$5 = computeFontSize$5(1.5) + BASE_UNITS$5;
var SMALL_LINE_HEIGHT$5 = computeFontSize$5(1.75) + BASE_UNITS$5;
var MEDIUM_LINE_HEIGHT$5 = computeFontSize$5(2) + BASE_UNITS$5;
var LARGE_LINE_HEIGHT$5 = computeFontSize$5(2.5) + BASE_UNITS$5;
var XLARGE_LINE_HEIGHT$5 = computeFontSize$5(3) + BASE_UNITS$5;
var XXLARGE_LINE_HEIGHT$5 = computeFontSize$5(3.5) + BASE_UNITS$5;
var HEADING_FONT_FAMILY$5 = "'Poppins', 'Open Sans',  sans-serif";
var BODY_FONT_FAMILY$5 = "'Poppins', 'Open Sans', sans-serif";
var BODY_FONT_IMPORT$5 = 'https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&display=swap';
var HEADING_FONT_IMPORT$5 = 'https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&display=swap';
var WEIGHT_THIN$5 = 300;
var WEIGHT_REGULAR$5 = 400;
var WEIGHT_SEMI_BOLD$5 = 600;
var WEIGHT_BOLD$5 = 700;
var WEIGHT_EXTRA_BOLD$5 = 800;

var colors$a = {
  primaryBrand: colors$b.brightBlueSwatch,
  secondaryBrand: colors$b.tealSwatch,
  accent: colors$b.brightGreenSwatch,
  success: colors$b.greenSwatch,
  warning: colors$b.yellowSwatch,
  danger: colors$b.redSwatch,
  background: colors$b.BASE,
  foreground: colors$b.WHITE,
  grayScale: colors$b.baseGraySwatch,
  blueScale: colors$b.blueSwatch,
  purpleScale: colors$b.purpleSwatch,
  white: colors$b.WHITE,
  defaultShadowColor: SHADOW_DEFAULT_COLOR
};
var concepts$5 = {
  colors: __assign({}, colors$a),
  radius: {
    none: radius.none,
    small: radius.small,
    medium: radius.mediumLarge,
    default: radius.medium,
    large: radius.large,
    circle: radius.circle
  },
  shadows: {
    light: computeShadow('light', Color$1(colors$a.defaultShadowColor).rgb().alpha(0.08).toString()),
    mediumLight: computeShadow('mediumLight', Color$1(colors$a.defaultShadowColor).rgb().alpha(0.08).toString()),
    mediumDark: computeShadow('mediumDark', Color$1(colors$a.defaultShadowColor).rgb().alpha(0.1).toString()),
    dark: computeShadow('dark', Color$1(colors$a.defaultShadowColor).rgb().alpha(0.12).toString()),
    generate: computeShadow
  },
  typography: {
    imports: {
      body: BODY_FONT_IMPORT$5,
      heading: HEADING_FONT_IMPORT$5
    },
    colors: {
      primaryHeading: colors$a.grayScale._200,
      primaryBody: colors$a.grayScale.base,
      inverseHeading: colors$a.white,
      inverseBody: colors$a.white
    },
    families: {
      heading: BODY_FONT_FAMILY$5,
      body: HEADING_FONT_FAMILY$5
    },
    sizes: {
      xSmall: XSMALL_FONT$5,
      small: SMALL_FONT$5,
      medium: MEDIUM_FONT$5,
      mediumPlus: MEDIUM_PLUS_FONT$5,
      large: LARGE_FONT$5,
      xLarge: XLARGE_FONT$5,
      xxLarge: XXLARGE_FONT$5
    },
    weights: {
      thin: WEIGHT_THIN$5,
      regular: WEIGHT_REGULAR$5,
      semiBold: WEIGHT_SEMI_BOLD$5,
      bold: WEIGHT_BOLD$5,
      extraBold: WEIGHT_EXTRA_BOLD$5
    },
    lineHeight: {
      xSmall: XSMALL_LINE_HEIGHT$5,
      small: SMALL_LINE_HEIGHT$5,
      medium: MEDIUM_LINE_HEIGHT$5,
      large: LARGE_LINE_HEIGHT$5,
      xLarge: XLARGE_LINE_HEIGHT$5,
      xxLarge: XXLARGE_LINE_HEIGHT$5
    }
  },
  borders: {
    thin: borders$9.xSmall,
    small: borders$9.small,
    default: borders$9.medium,
    large: borders$9.large,
    bold: borders$9.xLarge
  },
  spacing: {
    baseValue: BASE_VALUE$9,
    baseUnits: BASE_UNITS$9
  },
  mediaQueries: {
    small: mediaQuerySizes.small,
    medium: mediaQuerySizes.medium,
    large: mediaQuerySizes.large,
    xLarge: mediaQuerySizes.xLarge
  },
  sizing: {
    icons: {
      xSmall: icons$9.xSmall,
      small: icons$9.small,
      medium: icons$9.medium,
      large: icons$9.large,
      xLarge: icons$9.xLarge
    },
    sphere: {
      xSmall: sphere$9.xSmall,
      small: sphere$9.small,
      medium: sphere$9.medium,
      large: sphere$9.large,
      xLarge: sphere$9.xLarge
    }
  }
};

var space$5 = computeSpace(concepts$5.spacing.baseValue, concepts$5.spacing.baseUnits);
var spacing$5 = {
  x0: space$5(0),
  x_25: space$5(0.25),
  x_5: space$5(0.5),
  x_75: space$5(0.75),
  x1: space$5(1),
  x1_5: space$5(1.5),
  x2: space$5(2),
  x2_5: space$5(2.5),
  x3: space$5(3),
  x4: space$5(4),
  x5: space$5(5),
  x6: space$5(6),
  x7: space$5(7),
  x8: space$5(8),
  x9: space$5(9)
};
var borders$5 = {
  thin: concepts$5.borders.thin + "px",
  default: concepts$5.borders.default + "px",
  bold: concepts$5.borders.bold + "px"
};
var text$5 = {
  imports: {
    body: concepts$5.typography.imports.body,
    heading: concepts$5.typography.imports.heading
  },
  families: {
    body: concepts$5.typography.families.body,
    heading: concepts$5.typography.families.heading
  },
  types: {
    heroTitle: {
      color: concepts$5.typography.colors.primaryHeading,
      size: concepts$5.typography.sizes.xxLarge,
      fontWeight: concepts$5.typography.weights.extraBold,
      lineHeight: concepts$5.typography.lineHeight.xxLarge,
      fontFamily: concepts$5.typography.families.heading
    },
    title: {
      color: concepts$5.typography.colors.primaryHeading,
      size: concepts$5.typography.sizes.xLarge,
      fontWeight: concepts$5.typography.weights.bold,
      lineHeight: concepts$5.typography.lineHeight.xLarge,
      fontFamily: concepts$5.typography.families.heading
    },
    subtitle: {
      color: concepts$5.typography.colors.primaryHeading,
      size: concepts$5.typography.sizes.large,
      fontWeight: concepts$5.typography.weights.regular,
      lineHeight: concepts$5.typography.lineHeight.medium,
      fontFamily: concepts$5.typography.families.heading
    },
    largeBody: {
      color: concepts$5.typography.colors.primaryBody,
      size: concepts$5.typography.sizes.mediumPlus,
      fontWeight: concepts$5.typography.weights.regular,
      lineHeight: concepts$5.typography.lineHeight.small,
      fontFamily: concepts$5.typography.families.body
    },
    body: {
      color: concepts$5.typography.colors.primaryBody,
      size: concepts$5.typography.sizes.medium,
      fontWeight: concepts$5.typography.weights.regular,
      lineHeight: concepts$5.typography.lineHeight.small,
      fontFamily: concepts$5.typography.families.body
    },
    caption: {
      color: concepts$5.typography.colors.primaryBody,
      size: concepts$5.typography.sizes.small,
      fontWeight: concepts$5.typography.weights.regular,
      lineHeight: concepts$5.typography.lineHeight.small,
      fontFamily: concepts$5.typography.families.body
    },
    footnote: {
      color: concepts$5.typography.colors.primaryBody,
      size: concepts$5.typography.sizes.xSmall,
      fontWeight: concepts$5.typography.weights.regular,
      lineHeight: concepts$5.typography.lineHeight.xSmall,
      fontFamily: concepts$5.typography.families.body
    }
  },
  colors: {
    primaryBrand: concepts$5.colors.primaryBrand.base,
    secondaryBrand: concepts$5.colors.secondaryBrand.base,
    primaryHeading: concepts$5.typography.colors.primaryHeading,
    primaryBody: concepts$5.typography.colors.primaryBody,
    inverseHeading: concepts$5.typography.colors.inverseHeading,
    inverseBody: concepts$5.typography.colors.inverseBody,
    accent: concepts$5.colors.accent.base,
    success: concepts$5.colors.success.base,
    warning: concepts$5.colors.warning.base,
    danger: concepts$5.colors.danger.base,
    anchorBlue: concepts$5.colors.blueScale.base
  },
  weights: {
    thin: concepts$5.typography.weights.thin,
    regular: concepts$5.typography.weights.regular,
    semiBold: concepts$5.typography.weights.semiBold,
    bold: concepts$5.typography.weights.bold,
    extraBold: concepts$5.typography.weights.extraBold
  }
};
var mediaQueries$9 = {
  queries: {
    xSmall: "@media screen and (min-width: 0px)",
    // Large Mobile
    small: "@media screen and (min-width: " + concepts$5.mediaQueries.small + "px)",
    // Tablet
    medium: "@media screen and (min-width: " + concepts$5.mediaQueries.medium + "px)",
    // Large Tablet
    large: "@media screen and (min-width: " + concepts$5.mediaQueries.large + "px)",
    // Desktop
    xLarge: "@media screen and (min-width: " + concepts$5.mediaQueries.xLarge + "px)"
  },
  rawValues: {
    xSmall: 0,
    small: concepts$5.mediaQueries.small,
    medium: concepts$5.mediaQueries.medium,
    large: concepts$5.mediaQueries.large,
    xLarge: concepts$5.mediaQueries.xLarge
  }
};
var hocs$5 = {
  colors: {
    swatches: {
      primaryBrand: concepts$5.colors.primaryBrand,
      secondaryBrand: concepts$5.colors.secondaryBrand,
      success: concepts$5.colors.success,
      danger: concepts$5.colors.danger,
      warning: concepts$5.colors.warning,
      accent: concepts$5.colors.accent,
      grayScale: concepts$5.colors.grayScale,
      blueScale: concepts$5.colors.blueScale,
      purpleScale: concepts$5.colors.purpleScale
    },
    background: concepts$5.colors.background,
    foreground: concepts$5.colors.foreground
  },
  textColors: text$5.colors,
  spacing: spacing$5,
  borderRadius: concepts$5.radius,
  shadows: concepts$5.shadows,
  borders: borders$5
};
// TODO - revisit buttons for contrast and font color
var buttons$5 = {
  boxShadow: concepts$5.shadows.generate,
  borderSize: borders$5.default,
  outlinedBackgroundColor: concepts$5.colors.foreground,
  fontWeight: concepts$5.typography.weights.semiBold,
  sizes: {
    xSmall: {
      inset: spacing$5.x1 + " " + spacing$5.x2,
      outlinedInset: {
        vertical: "calc(" + spacing$5.x1 + " - 2px)",
        horizontal: "calc(" + spacing$5.x2 + " - 2px)"
      },
      fontSize: text$5.types.footnote.size,
      borderRadius: concepts$5.radius.default
    },
    small: {
      inset: spacing$5.x1_5 + " " + spacing$5.x2_5,
      outlinedInset: {
        vertical: "calc(" + spacing$5.x1_5 + " - 2px)",
        horizontal: "calc(" + spacing$5.x2_5 + " - 2px)"
      },
      fontSize: text$5.types.caption.size,
      borderRadius: concepts$5.radius.default
    },
    medium: {
      inset: spacing$5.x1_5 + " " + spacing$5.x3,
      outlinedInset: {
        vertical: "calc(" + spacing$5.x1_5 + " - 2px)",
        horizontal: "calc(" + spacing$5.x3 + " - 2px)"
      },
      fontSize: text$5.types.body.size,
      borderRadius: concepts$5.radius.default
    },
    large: {
      inset: spacing$5.x2 + " " + spacing$5.x4,
      outlinedInset: {
        vertical: "calc(" + spacing$5.x2 + " - 2px)",
        horizontal: "calc(" + spacing$5.x4 + " - 2px)"
      },
      fontSize: text$5.types.largeBody.size,
      borderRadius: concepts$5.radius.default
    },
    xLarge: {
      inset: spacing$5.x3 + " " + spacing$5.x5,
      outlinedInset: {
        vertical: "calc(" + spacing$5.x3 + " - 2px)",
        horizontal: "calc(" + spacing$5.x5 + " - 2px)"
      },
      fontSize: text$5.types.subtitle.size,
      borderRadius: concepts$5.radius.default
    }
  },
  primary: {
    backgroundColor: concepts$5.colors.primaryBrand.base,
    fontColor: concepts$5.typography.colors.inverseBody,
    fontFamily: concepts$5.typography.families.body
  },
  secondary: {
    backgroundColor: concepts$5.colors.secondaryBrand.base,
    fontColor: concepts$5.typography.colors.inverseBody,
    fontFamily: concepts$5.typography.families.body
  },
  warning: {
    backgroundColor: concepts$5.colors.warning.base,
    fontColor: concepts$5.typography.colors.inverseBody,
    fontFamily: concepts$5.typography.families.body
  },
  success: {
    backgroundColor: concepts$5.colors.success.base,
    fontColor: concepts$5.typography.colors.inverseBody,
    fontFamily: concepts$5.typography.families.body
  },
  danger: {
    backgroundColor: concepts$5.colors.danger.base,
    fontColor: concepts$5.typography.colors.inverseBody,
    fontFamily: concepts$5.typography.families.body
  },
  shadow: {
    backgroundColor: concepts$5.colors.foreground,
    fontColor: concepts$5.typography.colors.primaryBody,
    fontFamily: concepts$5.typography.families.body
  },
  accent: {
    backgroundColor: concepts$5.colors.accent.base,
    fontColor: concepts$5.typography.colors.primaryBody,
    fontFamily: concepts$5.typography.families.body
  },
  smoke: {
    backgroundColor: concepts$5.colors.background,
    fontColor: concepts$5.typography.colors.primaryBody,
    fontFamily: concepts$5.typography.families.body
  }
};
var slider$5 = {
  backgroundColor: concepts$5.colors.background,
  borderRadius: concepts$5.radius.small,
  handleRadius: concepts$5.radius.circle,
  handleColor: concepts$5.colors.foreground,
  handleShadow: concepts$5.shadows.light
};
var inputs$5 = {
  checkbox: {
    border: borders$5.default,
    borderRadius: concepts$5.radius.small,
    unCheckedBackgroundColor: concepts$5.colors.grayScale._800,
    checkmarkColor: concepts$5.typography.colors.inverseBody,
    labelColor: concepts$5.typography.colors.primaryBody
  },
  text: {
    borderRadius: concepts$5.radius.default,
    labelColor: concepts$5.colors.accent,
    placeholderColor: concepts$5.typography.colors.primaryBody,
    labelFontSize: text$5.types.caption.size,
    textFontSize: text$5.types.caption.size,
    borderWidth: borders$5.default,
    defaultFocusColor: concepts$5.colors.blueScale.base
  },
  select: {
    dropDownShadow: concepts$5.shadows.mediumDark,
    dropDownBackgroundColor: concepts$5.colors.foreground,
    boxShadow: concepts$5.shadows.light,
    borderRadius: concepts$5.radius.default
  },
  number: {
    backgroundColor: concepts$5.colors.grayScale._200,
    fontColor: concepts$5.typography.colors.inverseBody,
    fontSize: text$5.types.body.size,
    borderRadius: concepts$5.radius.default
  },
  backgroundColor: concepts$5.colors.background,
  fontSize: text$5.types.caption.size,
  fontColor: concepts$5.typography.colors.primaryBody,
  fontFamily: concepts$5.typography.families.body,
  colors: {
    primary: concepts$5.colors.primaryBrand.base,
    secondary: concepts$5.colors.secondaryBrand.base,
    success: concepts$5.colors.success.base,
    warning: concepts$5.colors.warning.base,
    danger: concepts$5.colors.danger.base,
    accent: concepts$5.colors.accent.base,
    dark: concepts$5.colors.primaryBrand._100,
    blue: concepts$5.colors.blueScale.base
  }
};
var icons$5 = {
  sizes: {
    xSmall: concepts$5.sizing.icons.xSmall,
    small: concepts$5.sizing.icons.small,
    medium: concepts$5.sizing.icons.medium,
    large: concepts$5.sizing.icons.large,
    xLarge: concepts$5.sizing.icons.xLarge
  },
  colors: {
    default: concepts$5.colors.grayScale._800,
    primaryBodyText: concepts$5.typography.colors.primaryBody,
    primaryHeadingText: concepts$5.typography.colors.primaryHeading,
    inverseBodyText: concepts$5.typography.colors.inverseBody,
    inverseHeadingText: concepts$5.typography.colors.inverseHeading,
    primaryBrand: concepts$5.colors.primaryBrand.base,
    secondaryBrand: concepts$5.colors.secondaryBrand.base,
    warning: concepts$5.colors.warning.base,
    danger: concepts$5.colors.danger.base,
    success: concepts$5.colors.success.base,
    background: concepts$5.colors.background,
    accent: concepts$5.colors.background
  }
};
var animatedCheckmark$5 = {
  sizeMap: {
    xSmall: concepts$5.sizing.icons.xSmall,
    small: concepts$5.sizing.icons.small,
    medium: concepts$5.sizing.icons.medium,
    large: concepts$5.sizing.icons.large,
    xLarge: concepts$5.sizing.icons.xLarge
  },
  colors: {
    primary: concepts$5.colors.primaryBrand.base,
    secondary: concepts$5.colors.secondaryBrand.base,
    success: concepts$5.colors.success.base,
    accent: concepts$5.colors.accent.base
  },
  strokeWidth: {
    small: concepts$5.borders.small,
    default: concepts$5.borders.default,
    large: concepts$5.borders.large
  },
  borderRadius: concepts$5.radius.circle,
  checkmarkFilledColor: concepts$5.colors.foreground
};
var avatar$5 = {
  borderWidth: borders$5.thin,
  borderColor: concepts$5.colors.grayScale._800,
  borderRadius: concepts$5.radius.circle,
  boxShadow: concepts$5.shadows.mediumLight,
  backgroundColor: concepts$5.colors.foreground,
  bubbleBackgroundColors: {
    isActive: concepts$5.colors.success.base,
    default: concepts$5.colors.grayScale._800
  },
  defaultBackgroundColors: {
    primary: concepts$5.colors.primaryBrand.base,
    secondary: concepts$5.colors.secondaryBrand.base,
    success: concepts$5.colors.success.base,
    accent: concepts$5.colors.accent.base,
    warning: concepts$5.colors.warning.base,
    danger: concepts$5.colors.danger.base
  },
  sizes: {
    xSmall: {
      avatarSize: 34,
      bubbleLeftPosition: 22,
      bubbleSize: 12
    },
    small: {
      avatarSize: 44,
      bubbleLeftPosition: 28,
      bubbleSize: 14
    },
    medium: {
      avatarSize: 60,
      bubbleLeftPosition: 40,
      bubbleSize: 16
    },
    large: {
      avatarSize: 76,
      bubbleLeftPosition: 52,
      bubbleSize: 20
    },
    xLarge: {
      avatarSize: 96,
      bubbleLeftPosition: 64,
      bubbleSize: 28
    }
  }
};
var loadingSpinner$5 = {
  size: {
    xSmall: concepts$5.sizing.icons.xSmall,
    small: concepts$5.sizing.icons.small,
    medium: concepts$5.sizing.icons.medium,
    large: concepts$5.sizing.icons.large,
    xLarge: concepts$5.sizing.icons.xLarge
  },
  colors: {
    primary: concepts$5.colors.primaryBrand.base,
    secondary: concepts$5.colors.secondaryBrand.base,
    success: concepts$5.colors.success.base,
    warning: concepts$5.colors.warning.base,
    danger: concepts$5.colors.danger.base
  }
};
var separator$5 = {
  border: borders$5.default,
  color: concepts$5.colors.grayScale._800
};
var card$5 = {
  backgroundColor: concepts$5.colors.warning.base,
  borderRadius: concepts$5.radius.default,
  boxShadow: concepts$5.shadows.mediumDark,
  defaultInset: spacing$5.x4 + " " + spacing$5.x2 + " " + spacing$5.x2 + " " + spacing$5.x2
};
var dialog$5 = {
  backgroundColor: concepts$5.colors.foreground,
  borderRadius: concepts$5.radius.default,
  boxShadow: concepts$5.shadows.mediumDark,
  defaultInset: spacing$5.x6 + " " + spacing$5.x4 + " " + spacing$5.x4 + " " + spacing$5.x4
};
var navigation$5 = {
  backgroundColor: concepts$5.colors.foreground,
  border: borders$5.thin,
  borderColor: concepts$5.colors.grayScale._800
};
var drawer$5 = {
  backgroundColor: concepts$5.colors.foreground,
  borderRadius: concepts$5.radius.small,
  boxShadow: concepts$5.shadows.mediumLight,
  bottomDirectionShadow: concepts$5.shadows.generate('mediumLight', Color$1(concepts$5.colors.defaultShadowColor).rgb().alpha(0.1).toString(), true),
  hoverBoxShadow: concepts$5.shadows.mediumDark,
  hoverBottomDirectionShadow: concepts$5.shadows.generate('mediumDark', Color$1(concepts$5.colors.defaultShadowColor).rgb().alpha(0.1).toString(), true),
  defaultInset: "" + spacing$5.x1,
  tab: {
    dark: {
      backgroundColor: concepts$5.colors.grayScale._600,
      arrowColor: concepts$5.colors.foreground
    },
    light: {
      backgroundColor: concepts$5.colors.foreground,
      arrowColor: icons$5.colors.default
    }
  }
};
var sheet$5 = {
  mobileBorderRadius: concepts$5.radius.large,
  desktopBorderRadius: concepts$5.radius.default,
  backgroundColor: concepts$5.colors.foreground,
  boxShadow: concepts$5.shadows.mediumDark
};
var toggle$5 = {
  isActiveColor: concepts$5.colors.success.base,
  isNotActiveColor: concepts$5.colors.grayScale._900
};
var tooltip$5 = {
  lightBackgroundColor: concepts$5.colors.foreground,
  darkBackgroundColor: concepts$5.colors.grayScale._100,
  boxShadow: concepts$5.shadows.mediumLight,
  borderColor: concepts$5.colors.background
};
var colorPicker$5 = {
  backgroundColor: concepts$5.colors.background,
  borderColor: 'transparent',
  boxShadow: concepts$5.shadows.mediumLight,
  borderRadius: concepts$5.radius.small
};
var sphere$5 = {
  xSmall: {
    circumference: concepts$5.sizing.sphere.xSmall,
    textType: 'title'
  },
  small: {
    circumference: concepts$5.sizing.sphere.small,
    textType: 'title'
  },
  medium: {
    circumference: concepts$5.sizing.sphere.medium,
    textType: 'heroTitle'
  },
  large: {
    circumference: concepts$5.sizing.sphere.large,
    textType: 'heroTitle'
  },
  xLarge: {
    circumference: concepts$5.sizing.sphere.xLarge,
    textType: 'heroTitle'
  }
};
var widget$5 = {
  primary: {
    backgroundColor: concepts$5.colors.primaryBrand.base,
    fontColor: 'inverseBody'
  },
  secondary: {
    backgroundColor: concepts$5.colors.secondaryBrand.base,
    fontColor: 'inverseBody'
  },
  warning: {
    backgroundColor: concepts$5.colors.warning.base,
    fontColor: 'primaryBody'
  },
  success: {
    backgroundColor: concepts$5.colors.success.base,
    fontColor: 'primaryBody'
  },
  danger: {
    backgroundColor: concepts$5.colors.danger.base,
    fontColor: 'inverseBody'
  },
  shadow: {
    backgroundColor: concepts$5.colors.foreground,
    fontColor: 'primaryBody'
  },
  accent: {
    backgroundColor: concepts$5.colors.accent.base,
    fontColor: 'primaryBody'
  }
};
var colorOption$5 = {
  defaults: {
    circleFill: concepts$5.shadows.generate([0, 0, 6, 1], Color$1(concepts$5.colors.defaultShadowColor).rgb().alpha(0.1).toString()),
    innerRing: concepts$5.shadows.generate([0, 0, 0, 11], Color$1([20, 30, 50]).rgb().alpha(0.03).toString()),
    outerRing: concepts$5.shadows.generate([0, 0, 6, 1], Color$1(concepts$5.colors.defaultShadowColor).rgb().alpha(0.06).toString())
  }
};
var entities$5 = {
  mediaQueries: mediaQueries$9,
  hocs: hocs$5,
  text: text$5,
  spacing: spacing$5,
  buttons: buttons$5,
  inputs: inputs$5,
  icons: icons$5,
  card: card$5,
  separator: separator$5,
  loadingSpinner: loadingSpinner$5,
  animatedCheckmark: animatedCheckmark$5,
  avatar: avatar$5,
  dialog: dialog$5,
  sheet: sheet$5,
  navigation: navigation$5,
  toggle: toggle$5,
  tooltip: tooltip$5,
  drawer: drawer$5,
  slider: slider$5,
  colorPicker: colorPicker$5,
  sphere: sphere$5,
  widget: widget$5,
  colorOption: colorOption$5
};

var DARK_BLUE$2 = '#1F4177';
var GOLD$1 = '#A99A67';
var BASE_GRAY$3 = '#313438';
var ORANGE = '#E49944';
var GREEN$4 = '#1DE6B9';
var YELLOW$3 = '#F7A14D';
var RED$3 = '#FB2E4B';
var WHITE$4 = '#FFF';
var BLUE$3 = '#1DC5E6';
var PURPLE$4 = '#703A96';
var darkBlueSwatch$2 = computeColorSwatch(DARK_BLUE$2);
var goldSwatch = computeColorSwatch(GOLD$1);
var baseGraySwatch$2 = computeColorSwatch(BASE_GRAY$3);
var orangeSwatch = computeColorSwatch(ORANGE);
var greenSwatch$4 = computeColorSwatch(GREEN$4);
var yellowSwatch$4 = computeColorSwatch(YELLOW$3);
var redSwatch$4 = computeColorSwatch(RED$3);
var blueSwatch$3 = computeColorSwatch(BLUE$3);
var purpleSwatch$4 = computeColorSwatch(PURPLE$4);
var BASE$3 = '#EAEAEA';
var baseGray$2 = BASE_GRAY$3;
var colors$9 = {
  darkBlueSwatch: darkBlueSwatch$2,
  goldSwatch: goldSwatch,
  baseGraySwatch: baseGraySwatch$2,
  orangeSwatch: orangeSwatch,
  greenSwatch: greenSwatch$4,
  redSwatch: redSwatch$4,
  yellowSwatch: yellowSwatch$4,
  blueSwatch: blueSwatch$3,
  baseGray: baseGray$2,
  purpleSwatch: purpleSwatch$4,
  BASE: BASE$3,
  WHITE: WHITE$4
};

var BASE_VALUE$4 = 1;
var BASE_UNITS$4 = 'rem';
// Rename to typography
var computeFontSize$4 = function (multiplier) {
  return BASE_VALUE$4 * multiplier;
};
var XSMALL_FONT$4 = computeFontSize$4(0.75) + BASE_UNITS$4;
var SMALL_FONT$4 = computeFontSize$4(0.875) + BASE_UNITS$4;
var MEDIUM_FONT$4 = computeFontSize$4(1) + BASE_UNITS$4;
var MEDIUM_PLUS_FONT$4 = computeFontSize$4(1.25) + BASE_UNITS$4;
var LARGE_FONT$4 = computeFontSize$4(1.75) + BASE_UNITS$4;
var XLARGE_FONT$4 = computeFontSize$4(2.125) + BASE_UNITS$4;
var XXLARGE_FONT$4 = computeFontSize$4(3) + BASE_UNITS$4;
var XSMALL_LINE_HEIGHT$4 = computeFontSize$4(1.5) + BASE_UNITS$4;
var SMALL_LINE_HEIGHT$4 = computeFontSize$4(1.75) + BASE_UNITS$4;
var MEDIUM_LINE_HEIGHT$4 = computeFontSize$4(2) + BASE_UNITS$4;
var LARGE_LINE_HEIGHT$4 = computeFontSize$4(2.5) + BASE_UNITS$4;
var XLARGE_LINE_HEIGHT$4 = computeFontSize$4(3) + BASE_UNITS$4;
var XXLARGE_LINE_HEIGHT$4 = computeFontSize$4(3.5) + BASE_UNITS$4;
var HEADING_FONT_FAMILY$4 = "'Poppins', 'Open Sans',  sans-serif";
var BODY_FONT_FAMILY$4 = "'Poppins', 'Open Sans', sans-serif";
var BODY_FONT_IMPORT$4 = 'https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&display=swap';
var HEADING_FONT_IMPORT$4 = 'https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&display=swap';
var WEIGHT_THIN$4 = 300;
var WEIGHT_REGULAR$4 = 400;
var WEIGHT_SEMI_BOLD$4 = 600;
var WEIGHT_BOLD$4 = 700;
var WEIGHT_EXTRA_BOLD$4 = 800;

var colors$8 = {
  primaryBrand: colors$9.darkBlueSwatch,
  secondaryBrand: colors$9.goldSwatch,
  accent: colors$9.orangeSwatch,
  success: colors$9.greenSwatch,
  warning: colors$9.yellowSwatch,
  danger: colors$9.redSwatch,
  background: colors$9.BASE,
  foreground: colors$9.WHITE,
  grayScale: colors$9.baseGraySwatch,
  blueScale: colors$9.blueSwatch,
  purpleScale: colors$9.purpleSwatch,
  white: colors$9.WHITE,
  defaultShadowColor: SHADOW_DEFAULT_COLOR
};
var concepts$4 = {
  colors: __assign({}, colors$8),
  radius: {
    none: radius.none,
    small: radius.small,
    medium: radius.mediumLarge,
    default: radius.medium,
    large: radius.large,
    circle: radius.circle
  },
  shadows: {
    light: computeShadow('light', Color$1(colors$8.defaultShadowColor).rgb().alpha(0.08).toString()),
    mediumLight: computeShadow('mediumLight', Color$1(colors$8.defaultShadowColor).rgb().alpha(0.08).toString()),
    mediumDark: computeShadow('mediumDark', Color$1(colors$8.defaultShadowColor).rgb().alpha(0.1).toString()),
    dark: computeShadow('dark', Color$1(colors$8.defaultShadowColor).rgb().alpha(0.12).toString()),
    generate: computeShadow
  },
  typography: {
    imports: {
      body: BODY_FONT_IMPORT$4,
      heading: HEADING_FONT_IMPORT$4
    },
    colors: {
      primaryHeading: colors$8.grayScale._200,
      primaryBody: colors$8.grayScale.base,
      inverseHeading: colors$8.white,
      inverseBody: colors$8.white
    },
    families: {
      heading: BODY_FONT_FAMILY$4,
      body: HEADING_FONT_FAMILY$4
    },
    sizes: {
      xSmall: XSMALL_FONT$4,
      small: SMALL_FONT$4,
      medium: MEDIUM_FONT$4,
      mediumPlus: MEDIUM_PLUS_FONT$4,
      large: LARGE_FONT$4,
      xLarge: XLARGE_FONT$4,
      xxLarge: XXLARGE_FONT$4
    },
    weights: {
      thin: WEIGHT_THIN$4,
      regular: WEIGHT_REGULAR$4,
      semiBold: WEIGHT_SEMI_BOLD$4,
      bold: WEIGHT_BOLD$4,
      extraBold: WEIGHT_EXTRA_BOLD$4
    },
    lineHeight: {
      xSmall: XSMALL_LINE_HEIGHT$4,
      small: SMALL_LINE_HEIGHT$4,
      medium: MEDIUM_LINE_HEIGHT$4,
      large: LARGE_LINE_HEIGHT$4,
      xLarge: XLARGE_LINE_HEIGHT$4,
      xxLarge: XXLARGE_LINE_HEIGHT$4
    }
  },
  borders: {
    thin: borders$9.xSmall,
    small: borders$9.small,
    default: borders$9.medium,
    large: borders$9.large,
    bold: borders$9.xLarge
  },
  spacing: {
    baseValue: BASE_VALUE$9,
    baseUnits: BASE_UNITS$9
  },
  mediaQueries: {
    small: mediaQuerySizes.small,
    medium: mediaQuerySizes.medium,
    large: mediaQuerySizes.large,
    xLarge: mediaQuerySizes.xLarge
  },
  sizing: {
    icons: {
      xSmall: icons$9.xSmall,
      small: icons$9.small,
      medium: icons$9.medium,
      large: icons$9.large,
      xLarge: icons$9.xLarge
    },
    sphere: {
      xSmall: sphere$9.xSmall,
      small: sphere$9.small,
      medium: sphere$9.medium,
      large: sphere$9.large,
      xLarge: sphere$9.xLarge
    }
  }
};

var space$4 = computeSpace(concepts$4.spacing.baseValue, concepts$4.spacing.baseUnits);
var spacing$4 = {
  x0: space$4(0),
  x_25: space$4(0.25),
  x_5: space$4(0.5),
  x_75: space$4(0.75),
  x1: space$4(1),
  x1_5: space$4(1.5),
  x2: space$4(2),
  x2_5: space$4(2.5),
  x3: space$4(3),
  x4: space$4(4),
  x5: space$4(5),
  x6: space$4(6),
  x7: space$4(7),
  x8: space$4(8),
  x9: space$4(9)
};
var borders$4 = {
  thin: concepts$4.borders.thin + "px",
  default: concepts$4.borders.default + "px",
  bold: concepts$4.borders.bold + "px"
};
var text$4 = {
  imports: {
    body: concepts$4.typography.imports.body,
    heading: concepts$4.typography.imports.heading
  },
  families: {
    body: concepts$4.typography.families.body,
    heading: concepts$4.typography.families.heading
  },
  types: {
    heroTitle: {
      color: concepts$4.typography.colors.primaryHeading,
      size: concepts$4.typography.sizes.xxLarge,
      fontWeight: concepts$4.typography.weights.extraBold,
      lineHeight: concepts$4.typography.lineHeight.xxLarge,
      fontFamily: concepts$4.typography.families.heading
    },
    title: {
      color: concepts$4.typography.colors.primaryHeading,
      size: concepts$4.typography.sizes.xLarge,
      fontWeight: concepts$4.typography.weights.bold,
      lineHeight: concepts$4.typography.lineHeight.xLarge,
      fontFamily: concepts$4.typography.families.heading
    },
    subtitle: {
      color: concepts$4.typography.colors.primaryHeading,
      size: concepts$4.typography.sizes.large,
      fontWeight: concepts$4.typography.weights.regular,
      lineHeight: concepts$4.typography.lineHeight.medium,
      fontFamily: concepts$4.typography.families.heading
    },
    largeBody: {
      color: concepts$4.typography.colors.primaryBody,
      size: concepts$4.typography.sizes.mediumPlus,
      fontWeight: concepts$4.typography.weights.regular,
      lineHeight: concepts$4.typography.lineHeight.small,
      fontFamily: concepts$4.typography.families.body
    },
    body: {
      color: concepts$4.typography.colors.primaryBody,
      size: concepts$4.typography.sizes.medium,
      fontWeight: concepts$4.typography.weights.regular,
      lineHeight: concepts$4.typography.lineHeight.small,
      fontFamily: concepts$4.typography.families.body
    },
    caption: {
      color: concepts$4.typography.colors.primaryBody,
      size: concepts$4.typography.sizes.small,
      fontWeight: concepts$4.typography.weights.regular,
      lineHeight: concepts$4.typography.lineHeight.small,
      fontFamily: concepts$4.typography.families.body
    },
    footnote: {
      color: concepts$4.typography.colors.primaryBody,
      size: concepts$4.typography.sizes.xSmall,
      fontWeight: concepts$4.typography.weights.regular,
      lineHeight: concepts$4.typography.lineHeight.xSmall,
      fontFamily: concepts$4.typography.families.body
    }
  },
  colors: {
    primaryBrand: concepts$4.colors.primaryBrand.base,
    secondaryBrand: concepts$4.colors.secondaryBrand.base,
    primaryHeading: concepts$4.typography.colors.primaryHeading,
    primaryBody: concepts$4.typography.colors.primaryBody,
    inverseHeading: concepts$4.typography.colors.inverseHeading,
    inverseBody: concepts$4.typography.colors.inverseBody,
    accent: concepts$4.colors.accent.base,
    success: concepts$4.colors.success.base,
    warning: concepts$4.colors.warning.base,
    danger: concepts$4.colors.danger.base,
    anchorBlue: concepts$4.colors.blueScale.base
  },
  weights: {
    thin: concepts$4.typography.weights.thin,
    regular: concepts$4.typography.weights.regular,
    semiBold: concepts$4.typography.weights.semiBold,
    bold: concepts$4.typography.weights.bold,
    extraBold: concepts$4.typography.weights.extraBold
  }
};
var mediaQueries$8 = {
  queries: {
    xSmall: "@media screen and (min-width: 0px)",
    // Large Mobile
    small: "@media screen and (min-width: " + concepts$4.mediaQueries.small + "px)",
    // Tablet
    medium: "@media screen and (min-width: " + concepts$4.mediaQueries.medium + "px)",
    // Large Tablet
    large: "@media screen and (min-width: " + concepts$4.mediaQueries.large + "px)",
    // Desktop
    xLarge: "@media screen and (min-width: " + concepts$4.mediaQueries.xLarge + "px)"
  },
  rawValues: {
    xSmall: 0,
    small: concepts$4.mediaQueries.small,
    medium: concepts$4.mediaQueries.medium,
    large: concepts$4.mediaQueries.large,
    xLarge: concepts$4.mediaQueries.xLarge
  }
};
var hocs$4 = {
  colors: {
    swatches: {
      primaryBrand: concepts$4.colors.primaryBrand,
      secondaryBrand: concepts$4.colors.secondaryBrand,
      success: concepts$4.colors.success,
      danger: concepts$4.colors.danger,
      warning: concepts$4.colors.warning,
      accent: concepts$4.colors.accent,
      grayScale: concepts$4.colors.grayScale,
      blueScale: concepts$4.colors.blueScale,
      purpleScale: concepts$4.colors.purpleScale
    },
    background: concepts$4.colors.background,
    foreground: concepts$4.colors.foreground
  },
  textColors: text$4.colors,
  spacing: spacing$4,
  borderRadius: concepts$4.radius,
  shadows: concepts$4.shadows,
  borders: borders$4
};
// TODO - revisit buttons for contrast and font color
var buttons$4 = {
  boxShadow: concepts$4.shadows.generate,
  borderSize: borders$4.default,
  outlinedBackgroundColor: concepts$4.colors.foreground,
  fontWeight: concepts$4.typography.weights.semiBold,
  sizes: {
    xSmall: {
      inset: spacing$4.x1 + " " + spacing$4.x2,
      outlinedInset: {
        vertical: "calc(" + spacing$4.x1 + " - 2px)",
        horizontal: "calc(" + spacing$4.x2 + " - 2px)"
      },
      fontSize: text$4.types.footnote.size,
      borderRadius: concepts$4.radius.default
    },
    small: {
      inset: spacing$4.x1_5 + " " + spacing$4.x2_5,
      outlinedInset: {
        vertical: "calc(" + spacing$4.x1_5 + " - 2px)",
        horizontal: "calc(" + spacing$4.x2_5 + " - 2px)"
      },
      fontSize: text$4.types.caption.size,
      borderRadius: concepts$4.radius.default
    },
    medium: {
      inset: spacing$4.x1_5 + " " + spacing$4.x3,
      outlinedInset: {
        vertical: "calc(" + spacing$4.x1_5 + " - 2px)",
        horizontal: "calc(" + spacing$4.x3 + " - 2px)"
      },
      fontSize: text$4.types.body.size,
      borderRadius: concepts$4.radius.default
    },
    large: {
      inset: spacing$4.x2 + " " + spacing$4.x4,
      outlinedInset: {
        vertical: "calc(" + spacing$4.x2 + " - 2px)",
        horizontal: "calc(" + spacing$4.x4 + " - 2px)"
      },
      fontSize: text$4.types.largeBody.size,
      borderRadius: concepts$4.radius.default
    },
    xLarge: {
      inset: spacing$4.x3 + " " + spacing$4.x5,
      outlinedInset: {
        vertical: "calc(" + spacing$4.x3 + " - 2px)",
        horizontal: "calc(" + spacing$4.x5 + " - 2px)"
      },
      fontSize: text$4.types.subtitle.size,
      borderRadius: concepts$4.radius.default
    }
  },
  primary: {
    backgroundColor: concepts$4.colors.primaryBrand.base,
    fontColor: concepts$4.typography.colors.inverseBody,
    fontFamily: concepts$4.typography.families.body
  },
  secondary: {
    backgroundColor: concepts$4.colors.secondaryBrand.base,
    fontColor: concepts$4.typography.colors.inverseBody,
    fontFamily: concepts$4.typography.families.body
  },
  warning: {
    backgroundColor: concepts$4.colors.warning.base,
    fontColor: concepts$4.typography.colors.inverseBody,
    fontFamily: concepts$4.typography.families.body
  },
  success: {
    backgroundColor: concepts$4.colors.success.base,
    fontColor: concepts$4.typography.colors.inverseBody,
    fontFamily: concepts$4.typography.families.body
  },
  danger: {
    backgroundColor: concepts$4.colors.danger.base,
    fontColor: concepts$4.typography.colors.inverseBody,
    fontFamily: concepts$4.typography.families.body
  },
  shadow: {
    backgroundColor: concepts$4.colors.foreground,
    fontColor: concepts$4.typography.colors.primaryBody,
    fontFamily: concepts$4.typography.families.body
  },
  accent: {
    backgroundColor: concepts$4.colors.accent.base,
    fontColor: concepts$4.typography.colors.primaryBody,
    fontFamily: concepts$4.typography.families.body
  },
  smoke: {
    backgroundColor: concepts$4.colors.background,
    fontColor: concepts$4.typography.colors.primaryBody,
    fontFamily: concepts$4.typography.families.body
  }
};
var slider$4 = {
  backgroundColor: concepts$4.colors.background,
  borderRadius: concepts$4.radius.small,
  handleRadius: concepts$4.radius.circle,
  handleColor: concepts$4.colors.foreground,
  handleShadow: concepts$4.shadows.light
};
var inputs$4 = {
  checkbox: {
    border: borders$4.default,
    borderRadius: concepts$4.radius.small,
    unCheckedBackgroundColor: concepts$4.colors.grayScale._800,
    checkmarkColor: concepts$4.typography.colors.inverseBody,
    labelColor: concepts$4.typography.colors.primaryBody
  },
  text: {
    borderRadius: concepts$4.radius.default,
    labelColor: concepts$4.colors.accent,
    placeholderColor: concepts$4.typography.colors.primaryBody,
    labelFontSize: text$4.types.caption.size,
    textFontSize: text$4.types.caption.size,
    borderWidth: borders$4.default,
    defaultFocusColor: concepts$4.colors.blueScale.base
  },
  select: {
    dropDownShadow: concepts$4.shadows.mediumDark,
    dropDownBackgroundColor: concepts$4.colors.foreground,
    boxShadow: concepts$4.shadows.light,
    borderRadius: concepts$4.radius.default
  },
  number: {
    backgroundColor: concepts$4.colors.grayScale._200,
    fontColor: concepts$4.typography.colors.inverseBody,
    fontSize: text$4.types.body.size,
    borderRadius: concepts$4.radius.default
  },
  backgroundColor: concepts$4.colors.background,
  fontSize: text$4.types.caption.size,
  fontColor: concepts$4.typography.colors.primaryBody,
  fontFamily: concepts$4.typography.families.body,
  colors: {
    primary: concepts$4.colors.primaryBrand.base,
    secondary: concepts$4.colors.secondaryBrand.base,
    success: concepts$4.colors.success.base,
    warning: concepts$4.colors.warning.base,
    danger: concepts$4.colors.danger.base,
    accent: concepts$4.colors.accent.base,
    dark: concepts$4.colors.primaryBrand._100,
    blue: concepts$4.colors.blueScale.base
  }
};
var icons$4 = {
  sizes: {
    xSmall: concepts$4.sizing.icons.xSmall,
    small: concepts$4.sizing.icons.small,
    medium: concepts$4.sizing.icons.medium,
    large: concepts$4.sizing.icons.large,
    xLarge: concepts$4.sizing.icons.xLarge
  },
  colors: {
    default: concepts$4.colors.grayScale._800,
    primaryBodyText: concepts$4.typography.colors.primaryBody,
    primaryHeadingText: concepts$4.typography.colors.primaryHeading,
    inverseBodyText: concepts$4.typography.colors.inverseBody,
    inverseHeadingText: concepts$4.typography.colors.inverseHeading,
    primaryBrand: concepts$4.colors.primaryBrand.base,
    secondaryBrand: concepts$4.colors.secondaryBrand.base,
    warning: concepts$4.colors.warning.base,
    danger: concepts$4.colors.danger.base,
    success: concepts$4.colors.success.base,
    background: concepts$4.colors.background,
    accent: concepts$4.colors.background
  }
};
var animatedCheckmark$4 = {
  sizeMap: {
    xSmall: concepts$4.sizing.icons.xSmall,
    small: concepts$4.sizing.icons.small,
    medium: concepts$4.sizing.icons.medium,
    large: concepts$4.sizing.icons.large,
    xLarge: concepts$4.sizing.icons.xLarge
  },
  colors: {
    primary: concepts$4.colors.primaryBrand.base,
    secondary: concepts$4.colors.secondaryBrand.base,
    success: concepts$4.colors.success.base,
    accent: concepts$4.colors.accent.base
  },
  strokeWidth: {
    small: concepts$4.borders.small,
    default: concepts$4.borders.default,
    large: concepts$4.borders.large
  },
  borderRadius: concepts$4.radius.circle,
  checkmarkFilledColor: concepts$4.colors.foreground
};
var avatar$4 = {
  borderWidth: borders$4.thin,
  borderColor: concepts$4.colors.grayScale._800,
  borderRadius: concepts$4.radius.circle,
  boxShadow: concepts$4.shadows.mediumLight,
  backgroundColor: concepts$4.colors.foreground,
  bubbleBackgroundColors: {
    isActive: concepts$4.colors.success.base,
    default: concepts$4.colors.grayScale._800
  },
  defaultBackgroundColors: {
    primary: concepts$4.colors.primaryBrand.base,
    secondary: concepts$4.colors.secondaryBrand.base,
    success: concepts$4.colors.success.base,
    accent: concepts$4.colors.accent.base,
    warning: concepts$4.colors.warning.base,
    danger: concepts$4.colors.danger.base
  },
  sizes: {
    xSmall: {
      avatarSize: 34,
      bubbleLeftPosition: 22,
      bubbleSize: 12
    },
    small: {
      avatarSize: 44,
      bubbleLeftPosition: 28,
      bubbleSize: 14
    },
    medium: {
      avatarSize: 60,
      bubbleLeftPosition: 40,
      bubbleSize: 16
    },
    large: {
      avatarSize: 76,
      bubbleLeftPosition: 52,
      bubbleSize: 20
    },
    xLarge: {
      avatarSize: 96,
      bubbleLeftPosition: 64,
      bubbleSize: 28
    }
  }
};
var loadingSpinner$4 = {
  size: {
    xSmall: concepts$4.sizing.icons.xSmall,
    small: concepts$4.sizing.icons.small,
    medium: concepts$4.sizing.icons.medium,
    large: concepts$4.sizing.icons.large,
    xLarge: concepts$4.sizing.icons.xLarge
  },
  colors: {
    primary: concepts$4.colors.primaryBrand.base,
    secondary: concepts$4.colors.secondaryBrand.base,
    success: concepts$4.colors.success.base,
    warning: concepts$4.colors.warning.base,
    danger: concepts$4.colors.danger.base
  }
};
var separator$4 = {
  border: borders$4.default,
  color: concepts$4.colors.grayScale._800
};
var card$4 = {
  backgroundColor: concepts$4.colors.warning.base,
  borderRadius: concepts$4.radius.default,
  boxShadow: concepts$4.shadows.mediumDark,
  defaultInset: spacing$4.x4 + " " + spacing$4.x2 + " " + spacing$4.x2 + " " + spacing$4.x2
};
var dialog$4 = {
  backgroundColor: concepts$4.colors.foreground,
  borderRadius: concepts$4.radius.default,
  boxShadow: concepts$4.shadows.mediumDark,
  defaultInset: spacing$4.x6 + " " + spacing$4.x4 + " " + spacing$4.x4 + " " + spacing$4.x4
};
var navigation$4 = {
  backgroundColor: concepts$4.colors.foreground,
  border: borders$4.thin,
  borderColor: concepts$4.colors.grayScale._800
};
var drawer$4 = {
  backgroundColor: concepts$4.colors.foreground,
  borderRadius: concepts$4.radius.small,
  boxShadow: concepts$4.shadows.mediumLight,
  bottomDirectionShadow: concepts$4.shadows.generate('mediumLight', Color$1(concepts$4.colors.defaultShadowColor).rgb().alpha(0.1).toString(), true),
  hoverBoxShadow: concepts$4.shadows.mediumDark,
  hoverBottomDirectionShadow: concepts$4.shadows.generate('mediumDark', Color$1(concepts$4.colors.defaultShadowColor).rgb().alpha(0.1).toString(), true),
  defaultInset: "" + spacing$4.x1,
  tab: {
    dark: {
      backgroundColor: concepts$4.colors.grayScale._600,
      arrowColor: concepts$4.colors.foreground
    },
    light: {
      backgroundColor: concepts$4.colors.foreground,
      arrowColor: icons$4.colors.default
    }
  }
};
var sheet$4 = {
  mobileBorderRadius: concepts$4.radius.large,
  desktopBorderRadius: concepts$4.radius.default,
  backgroundColor: concepts$4.colors.foreground,
  boxShadow: concepts$4.shadows.mediumDark
};
var toggle$4 = {
  isActiveColor: concepts$4.colors.success.base,
  isNotActiveColor: concepts$4.colors.grayScale._900
};
var tooltip$4 = {
  lightBackgroundColor: concepts$4.colors.foreground,
  darkBackgroundColor: concepts$4.colors.grayScale._100,
  boxShadow: concepts$4.shadows.mediumLight,
  borderColor: concepts$4.colors.background
};
var colorPicker$4 = {
  backgroundColor: concepts$4.colors.background,
  borderColor: 'transparent',
  boxShadow: concepts$4.shadows.mediumLight,
  borderRadius: concepts$4.radius.small
};
var sphere$4 = {
  xSmall: {
    circumference: concepts$4.sizing.sphere.xSmall,
    textType: 'title'
  },
  small: {
    circumference: concepts$4.sizing.sphere.small,
    textType: 'title'
  },
  medium: {
    circumference: concepts$4.sizing.sphere.medium,
    textType: 'heroTitle'
  },
  large: {
    circumference: concepts$4.sizing.sphere.large,
    textType: 'heroTitle'
  },
  xLarge: {
    circumference: concepts$4.sizing.sphere.xLarge,
    textType: 'heroTitle'
  }
};
var widget$4 = {
  primary: {
    backgroundColor: concepts$4.colors.primaryBrand.base,
    fontColor: 'inverseBody'
  },
  secondary: {
    backgroundColor: concepts$4.colors.secondaryBrand.base,
    fontColor: 'inverseBody'
  },
  warning: {
    backgroundColor: concepts$4.colors.warning.base,
    fontColor: 'primaryBody'
  },
  success: {
    backgroundColor: concepts$4.colors.success.base,
    fontColor: 'primaryBody'
  },
  danger: {
    backgroundColor: concepts$4.colors.danger.base,
    fontColor: 'inverseBody'
  },
  shadow: {
    backgroundColor: concepts$4.colors.foreground,
    fontColor: 'primaryBody'
  },
  accent: {
    backgroundColor: concepts$4.colors.accent.base,
    fontColor: 'primaryBody'
  }
};
var colorOption$4 = {
  defaults: {
    circleFill: concepts$4.shadows.generate([0, 0, 6, 1], Color$1(concepts$4.colors.defaultShadowColor).rgb().alpha(0.1).toString()),
    innerRing: concepts$4.shadows.generate([0, 0, 0, 11], Color$1([20, 30, 50]).rgb().alpha(0.03).toString()),
    outerRing: concepts$4.shadows.generate([0, 0, 6, 1], Color$1(concepts$4.colors.defaultShadowColor).rgb().alpha(0.06).toString())
  }
};
var entities$4 = {
  mediaQueries: mediaQueries$8,
  hocs: hocs$4,
  text: text$4,
  spacing: spacing$4,
  buttons: buttons$4,
  inputs: inputs$4,
  icons: icons$4,
  card: card$4,
  separator: separator$4,
  loadingSpinner: loadingSpinner$4,
  animatedCheckmark: animatedCheckmark$4,
  avatar: avatar$4,
  dialog: dialog$4,
  sheet: sheet$4,
  navigation: navigation$4,
  toggle: toggle$4,
  tooltip: tooltip$4,
  drawer: drawer$4,
  slider: slider$4,
  colorPicker: colorPicker$4,
  sphere: sphere$4,
  widget: widget$4,
  colorOption: colorOption$4
};

var CARAMEL = '#B38048';
var DARK_TEAL = '#314A51';
var BEIGE = '#E0D5C0';
var BASE_GRAY$2 = '#696767';
var GREEN$3 = '#1DE6B9';
var YELLOW$2 = '#F7A14D';
var RED$2 = '#FB2E4B';
var WHITE$3 = '#FFF';
var BLUE$2 = '#1DC5E6';
var PURPLE$3 = '#703A96';
var caramelSwatch = computeColorSwatch(CARAMEL);
var darkTealSwatch = computeColorSwatch(DARK_TEAL);
var beigeSwatch = computeColorSwatch(BEIGE);
var baseGraySwatch$1 = computeColorSwatch(BASE_GRAY$2);
var greenSwatch$3 = computeColorSwatch(GREEN$3);
var yellowSwatch$3 = computeColorSwatch(YELLOW$2);
var redSwatch$3 = computeColorSwatch(RED$2);
var blueSwatch$2 = computeColorSwatch(BLUE$2);
var purpleSwatch$3 = computeColorSwatch(PURPLE$3);
var BASE$2 = '#FBF9F4';
var baseGray$1 = BASE_GRAY$2;
var colors$7 = {
  caramelSwatch: caramelSwatch,
  beigeSwatch: beigeSwatch,
  darkTealSwatch: darkTealSwatch,
  greenSwatch: greenSwatch$3,
  redSwatch: redSwatch$3,
  yellowSwatch: yellowSwatch$3,
  blueSwatch: blueSwatch$2,
  baseGraySwatch: baseGraySwatch$1,
  baseGray: baseGray$1,
  purpleSwatch: purpleSwatch$3,
  BASE: BASE$2,
  WHITE: WHITE$3
};

var BASE_VALUE$3 = 1;
var BASE_UNITS$3 = 'rem';
// Rename to typography
var computeFontSize$3 = function (multiplier) {
  return BASE_VALUE$3 * multiplier;
};
var XSMALL_FONT$3 = computeFontSize$3(0.75) + BASE_UNITS$3;
var SMALL_FONT$3 = computeFontSize$3(0.875) + BASE_UNITS$3;
var MEDIUM_FONT$3 = computeFontSize$3(1) + BASE_UNITS$3;
var MEDIUM_PLUS_FONT$3 = computeFontSize$3(1.25) + BASE_UNITS$3;
var LARGE_FONT$3 = computeFontSize$3(1.75) + BASE_UNITS$3;
var XLARGE_FONT$3 = computeFontSize$3(2.125) + BASE_UNITS$3;
var XXLARGE_FONT$3 = computeFontSize$3(3) + BASE_UNITS$3;
var XSMALL_LINE_HEIGHT$3 = computeFontSize$3(1.5) + BASE_UNITS$3;
var SMALL_LINE_HEIGHT$3 = computeFontSize$3(1.75) + BASE_UNITS$3;
var MEDIUM_LINE_HEIGHT$3 = computeFontSize$3(2) + BASE_UNITS$3;
var LARGE_LINE_HEIGHT$3 = computeFontSize$3(2.5) + BASE_UNITS$3;
var XLARGE_LINE_HEIGHT$3 = computeFontSize$3(3) + BASE_UNITS$3;
var XXLARGE_LINE_HEIGHT$3 = computeFontSize$3(3.5) + BASE_UNITS$3;
var HEADING_FONT_FAMILY$3 = "'Poppins', 'Open Sans',  sans-serif";
var BODY_FONT_FAMILY$3 = "'Poppins', 'Open Sans', sans-serif";
var BODY_FONT_IMPORT$3 = 'https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&display=swap';
var HEADING_FONT_IMPORT$3 = 'https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&display=swap';
var WEIGHT_THIN$3 = 300;
var WEIGHT_REGULAR$3 = 400;
var WEIGHT_SEMI_BOLD$3 = 600;
var WEIGHT_BOLD$3 = 700;
var WEIGHT_EXTRA_BOLD$3 = 800;

var colors$6 = {
  primaryBrand: colors$7.caramelSwatch,
  secondaryBrand: colors$7.darkTealSwatch,
  accent: colors$7.beigeSwatch,
  success: colors$7.greenSwatch,
  warning: colors$7.yellowSwatch,
  danger: colors$7.redSwatch,
  background: colors$7.BASE,
  foreground: colors$7.WHITE,
  grayScale: colors$7.baseGraySwatch,
  blueScale: colors$7.blueSwatch,
  purpleScale: colors$7.purpleSwatch,
  white: colors$7.WHITE,
  defaultShadowColor: SHADOW_DEFAULT_COLOR
};
var concepts$3 = {
  colors: __assign({}, colors$6),
  radius: {
    none: radius.none,
    small: radius.small,
    medium: radius.mediumLarge,
    default: radius.medium,
    large: radius.large,
    circle: radius.circle
  },
  shadows: {
    light: computeShadow('light', Color$1(colors$6.defaultShadowColor).rgb().alpha(0.08).toString()),
    mediumLight: computeShadow('mediumLight', Color$1(colors$6.defaultShadowColor).rgb().alpha(0.08).toString()),
    mediumDark: computeShadow('mediumDark', Color$1(colors$6.defaultShadowColor).rgb().alpha(0.1).toString()),
    dark: computeShadow('dark', Color$1(colors$6.defaultShadowColor).rgb().alpha(0.12).toString()),
    generate: computeShadow
  },
  typography: {
    imports: {
      body: BODY_FONT_IMPORT$3,
      heading: HEADING_FONT_IMPORT$3
    },
    colors: {
      primaryHeading: colors$6.grayScale._200,
      primaryBody: colors$6.grayScale.base,
      inverseHeading: colors$6.white,
      inverseBody: colors$6.white
    },
    families: {
      heading: BODY_FONT_FAMILY$3,
      body: HEADING_FONT_FAMILY$3
    },
    sizes: {
      xSmall: XSMALL_FONT$3,
      small: SMALL_FONT$3,
      medium: MEDIUM_FONT$3,
      mediumPlus: MEDIUM_PLUS_FONT$3,
      large: LARGE_FONT$3,
      xLarge: XLARGE_FONT$3,
      xxLarge: XXLARGE_FONT$3
    },
    weights: {
      thin: WEIGHT_THIN$3,
      regular: WEIGHT_REGULAR$3,
      semiBold: WEIGHT_SEMI_BOLD$3,
      bold: WEIGHT_BOLD$3,
      extraBold: WEIGHT_EXTRA_BOLD$3
    },
    lineHeight: {
      xSmall: XSMALL_LINE_HEIGHT$3,
      small: SMALL_LINE_HEIGHT$3,
      medium: MEDIUM_LINE_HEIGHT$3,
      large: LARGE_LINE_HEIGHT$3,
      xLarge: XLARGE_LINE_HEIGHT$3,
      xxLarge: XXLARGE_LINE_HEIGHT$3
    }
  },
  borders: {
    thin: borders$9.xSmall,
    small: borders$9.small,
    default: borders$9.medium,
    large: borders$9.large,
    bold: borders$9.xLarge
  },
  spacing: {
    baseValue: BASE_VALUE$9,
    baseUnits: BASE_UNITS$9
  },
  mediaQueries: {
    small: mediaQuerySizes.small,
    medium: mediaQuerySizes.medium,
    large: mediaQuerySizes.large,
    xLarge: mediaQuerySizes.xLarge
  },
  sizing: {
    icons: {
      xSmall: icons$9.xSmall,
      small: icons$9.small,
      medium: icons$9.medium,
      large: icons$9.large,
      xLarge: icons$9.xLarge
    },
    sphere: {
      xSmall: sphere$9.xSmall,
      small: sphere$9.small,
      medium: sphere$9.medium,
      large: sphere$9.large,
      xLarge: sphere$9.xLarge
    }
  }
};

var space$3 = computeSpace(concepts$3.spacing.baseValue, concepts$3.spacing.baseUnits);
var spacing$3 = {
  x0: space$3(0),
  x_25: space$3(0.25),
  x_5: space$3(0.5),
  x_75: space$3(0.75),
  x1: space$3(1),
  x1_5: space$3(1.5),
  x2: space$3(2),
  x2_5: space$3(2.5),
  x3: space$3(3),
  x4: space$3(4),
  x5: space$3(5),
  x6: space$3(6),
  x7: space$3(7),
  x8: space$3(8),
  x9: space$3(9)
};
var borders$3 = {
  thin: concepts$3.borders.thin + "px",
  default: concepts$3.borders.default + "px",
  bold: concepts$3.borders.bold + "px"
};
var text$3 = {
  imports: {
    body: concepts$3.typography.imports.body,
    heading: concepts$3.typography.imports.heading
  },
  families: {
    body: concepts$3.typography.families.body,
    heading: concepts$3.typography.families.heading
  },
  types: {
    heroTitle: {
      color: concepts$3.typography.colors.primaryHeading,
      size: concepts$3.typography.sizes.xxLarge,
      fontWeight: concepts$3.typography.weights.extraBold,
      lineHeight: concepts$3.typography.lineHeight.xxLarge,
      fontFamily: concepts$3.typography.families.heading
    },
    title: {
      color: concepts$3.typography.colors.primaryHeading,
      size: concepts$3.typography.sizes.xLarge,
      fontWeight: concepts$3.typography.weights.bold,
      lineHeight: concepts$3.typography.lineHeight.xLarge,
      fontFamily: concepts$3.typography.families.heading
    },
    subtitle: {
      color: concepts$3.typography.colors.primaryHeading,
      size: concepts$3.typography.sizes.large,
      fontWeight: concepts$3.typography.weights.regular,
      lineHeight: concepts$3.typography.lineHeight.medium,
      fontFamily: concepts$3.typography.families.heading
    },
    largeBody: {
      color: concepts$3.typography.colors.primaryBody,
      size: concepts$3.typography.sizes.mediumPlus,
      fontWeight: concepts$3.typography.weights.regular,
      lineHeight: concepts$3.typography.lineHeight.small,
      fontFamily: concepts$3.typography.families.body
    },
    body: {
      color: concepts$3.typography.colors.primaryBody,
      size: concepts$3.typography.sizes.medium,
      fontWeight: concepts$3.typography.weights.regular,
      lineHeight: concepts$3.typography.lineHeight.small,
      fontFamily: concepts$3.typography.families.body
    },
    caption: {
      color: concepts$3.typography.colors.primaryBody,
      size: concepts$3.typography.sizes.small,
      fontWeight: concepts$3.typography.weights.regular,
      lineHeight: concepts$3.typography.lineHeight.small,
      fontFamily: concepts$3.typography.families.body
    },
    footnote: {
      color: concepts$3.typography.colors.primaryBody,
      size: concepts$3.typography.sizes.xSmall,
      fontWeight: concepts$3.typography.weights.regular,
      lineHeight: concepts$3.typography.lineHeight.xSmall,
      fontFamily: concepts$3.typography.families.body
    }
  },
  colors: {
    primaryBrand: concepts$3.colors.primaryBrand.base,
    secondaryBrand: concepts$3.colors.secondaryBrand.base,
    primaryHeading: concepts$3.typography.colors.primaryHeading,
    primaryBody: concepts$3.typography.colors.primaryBody,
    inverseHeading: concepts$3.typography.colors.inverseHeading,
    inverseBody: concepts$3.typography.colors.inverseBody,
    accent: concepts$3.colors.accent.base,
    success: concepts$3.colors.success.base,
    warning: concepts$3.colors.warning.base,
    danger: concepts$3.colors.danger.base,
    anchorBlue: concepts$3.colors.blueScale.base
  },
  weights: {
    thin: concepts$3.typography.weights.thin,
    regular: concepts$3.typography.weights.regular,
    semiBold: concepts$3.typography.weights.semiBold,
    bold: concepts$3.typography.weights.bold,
    extraBold: concepts$3.typography.weights.extraBold
  }
};
var mediaQueries$7 = {
  queries: {
    xSmall: "@media screen and (min-width: 0px)",
    // Large Mobile
    small: "@media screen and (min-width: " + concepts$3.mediaQueries.small + "px)",
    // Tablet
    medium: "@media screen and (min-width: " + concepts$3.mediaQueries.medium + "px)",
    // Large Tablet
    large: "@media screen and (min-width: " + concepts$3.mediaQueries.large + "px)",
    // Desktop
    xLarge: "@media screen and (min-width: " + concepts$3.mediaQueries.xLarge + "px)"
  },
  rawValues: {
    xSmall: 0,
    small: concepts$3.mediaQueries.small,
    medium: concepts$3.mediaQueries.medium,
    large: concepts$3.mediaQueries.large,
    xLarge: concepts$3.mediaQueries.xLarge
  }
};
var hocs$3 = {
  colors: {
    swatches: {
      primaryBrand: concepts$3.colors.primaryBrand,
      secondaryBrand: concepts$3.colors.secondaryBrand,
      success: concepts$3.colors.success,
      danger: concepts$3.colors.danger,
      warning: concepts$3.colors.warning,
      accent: concepts$3.colors.accent,
      grayScale: concepts$3.colors.grayScale,
      blueScale: concepts$3.colors.blueScale,
      purpleScale: concepts$3.colors.purpleScale
    },
    background: concepts$3.colors.background,
    foreground: concepts$3.colors.foreground
  },
  textColors: text$3.colors,
  spacing: spacing$3,
  borderRadius: concepts$3.radius,
  shadows: concepts$3.shadows,
  borders: borders$3
};
// TODO - revisit buttons for contrast and font color
var buttons$3 = {
  boxShadow: concepts$3.shadows.generate,
  borderSize: borders$3.default,
  outlinedBackgroundColor: concepts$3.colors.foreground,
  fontWeight: concepts$3.typography.weights.semiBold,
  sizes: {
    xSmall: {
      inset: spacing$3.x1 + " " + spacing$3.x2,
      outlinedInset: {
        vertical: "calc(" + spacing$3.x1 + " - 2px)",
        horizontal: "calc(" + spacing$3.x2 + " - 2px)"
      },
      fontSize: text$3.types.footnote.size,
      borderRadius: concepts$3.radius.default
    },
    small: {
      inset: spacing$3.x1_5 + " " + spacing$3.x2_5,
      outlinedInset: {
        vertical: "calc(" + spacing$3.x1_5 + " - 2px)",
        horizontal: "calc(" + spacing$3.x2_5 + " - 2px)"
      },
      fontSize: text$3.types.caption.size,
      borderRadius: concepts$3.radius.default
    },
    medium: {
      inset: spacing$3.x1_5 + " " + spacing$3.x3,
      outlinedInset: {
        vertical: "calc(" + spacing$3.x1_5 + " - 2px)",
        horizontal: "calc(" + spacing$3.x3 + " - 2px)"
      },
      fontSize: text$3.types.body.size,
      borderRadius: concepts$3.radius.default
    },
    large: {
      inset: spacing$3.x2 + " " + spacing$3.x4,
      outlinedInset: {
        vertical: "calc(" + spacing$3.x2 + " - 2px)",
        horizontal: "calc(" + spacing$3.x4 + " - 2px)"
      },
      fontSize: text$3.types.largeBody.size,
      borderRadius: concepts$3.radius.default
    },
    xLarge: {
      inset: spacing$3.x3 + " " + spacing$3.x5,
      outlinedInset: {
        vertical: "calc(" + spacing$3.x3 + " - 2px)",
        horizontal: "calc(" + spacing$3.x5 + " - 2px)"
      },
      fontSize: text$3.types.subtitle.size,
      borderRadius: concepts$3.radius.default
    }
  },
  primary: {
    backgroundColor: concepts$3.colors.primaryBrand.base,
    fontColor: concepts$3.typography.colors.inverseBody,
    fontFamily: concepts$3.typography.families.body
  },
  secondary: {
    backgroundColor: concepts$3.colors.secondaryBrand.base,
    fontColor: concepts$3.typography.colors.inverseBody,
    fontFamily: concepts$3.typography.families.body
  },
  warning: {
    backgroundColor: concepts$3.colors.warning.base,
    fontColor: concepts$3.typography.colors.inverseBody,
    fontFamily: concepts$3.typography.families.body
  },
  success: {
    backgroundColor: concepts$3.colors.success.base,
    fontColor: concepts$3.typography.colors.inverseBody,
    fontFamily: concepts$3.typography.families.body
  },
  danger: {
    backgroundColor: concepts$3.colors.danger.base,
    fontColor: concepts$3.typography.colors.inverseBody,
    fontFamily: concepts$3.typography.families.body
  },
  shadow: {
    backgroundColor: concepts$3.colors.foreground,
    fontColor: concepts$3.typography.colors.primaryBody,
    fontFamily: concepts$3.typography.families.body
  },
  accent: {
    backgroundColor: concepts$3.colors.accent.base,
    fontColor: concepts$3.typography.colors.primaryBody,
    fontFamily: concepts$3.typography.families.body
  },
  smoke: {
    backgroundColor: concepts$3.colors.background,
    fontColor: concepts$3.typography.colors.primaryBody,
    fontFamily: concepts$3.typography.families.body
  }
};
var slider$3 = {
  backgroundColor: concepts$3.colors.background,
  borderRadius: concepts$3.radius.small,
  handleRadius: concepts$3.radius.circle,
  handleColor: concepts$3.colors.foreground,
  handleShadow: concepts$3.shadows.light
};
var inputs$3 = {
  checkbox: {
    border: borders$3.default,
    borderRadius: concepts$3.radius.small,
    unCheckedBackgroundColor: concepts$3.colors.grayScale._800,
    checkmarkColor: concepts$3.typography.colors.inverseBody,
    labelColor: concepts$3.typography.colors.primaryBody
  },
  text: {
    borderRadius: concepts$3.radius.default,
    labelColor: concepts$3.colors.accent,
    placeholderColor: concepts$3.typography.colors.primaryBody,
    labelFontSize: text$3.types.caption.size,
    textFontSize: text$3.types.caption.size,
    borderWidth: borders$3.default,
    defaultFocusColor: concepts$3.colors.blueScale.base
  },
  select: {
    dropDownShadow: concepts$3.shadows.mediumDark,
    dropDownBackgroundColor: concepts$3.colors.foreground,
    boxShadow: concepts$3.shadows.light,
    borderRadius: concepts$3.radius.default
  },
  number: {
    backgroundColor: concepts$3.colors.grayScale._200,
    fontColor: concepts$3.typography.colors.inverseBody,
    fontSize: text$3.types.body.size,
    borderRadius: concepts$3.radius.default
  },
  backgroundColor: concepts$3.colors.background,
  fontSize: text$3.types.caption.size,
  fontColor: concepts$3.typography.colors.primaryBody,
  fontFamily: concepts$3.typography.families.body,
  colors: {
    primary: concepts$3.colors.primaryBrand.base,
    secondary: concepts$3.colors.secondaryBrand.base,
    success: concepts$3.colors.success.base,
    warning: concepts$3.colors.warning.base,
    danger: concepts$3.colors.danger.base,
    accent: concepts$3.colors.accent.base,
    dark: concepts$3.colors.primaryBrand._100,
    blue: concepts$3.colors.blueScale.base
  }
};
var icons$3 = {
  sizes: {
    xSmall: concepts$3.sizing.icons.xSmall,
    small: concepts$3.sizing.icons.small,
    medium: concepts$3.sizing.icons.medium,
    large: concepts$3.sizing.icons.large,
    xLarge: concepts$3.sizing.icons.xLarge
  },
  colors: {
    default: concepts$3.colors.grayScale._800,
    primaryBodyText: concepts$3.typography.colors.primaryBody,
    primaryHeadingText: concepts$3.typography.colors.primaryHeading,
    inverseBodyText: concepts$3.typography.colors.inverseBody,
    inverseHeadingText: concepts$3.typography.colors.inverseHeading,
    primaryBrand: concepts$3.colors.primaryBrand.base,
    secondaryBrand: concepts$3.colors.secondaryBrand.base,
    warning: concepts$3.colors.warning.base,
    danger: concepts$3.colors.danger.base,
    success: concepts$3.colors.success.base,
    background: concepts$3.colors.background,
    accent: concepts$3.colors.background
  }
};
var animatedCheckmark$3 = {
  sizeMap: {
    xSmall: concepts$3.sizing.icons.xSmall,
    small: concepts$3.sizing.icons.small,
    medium: concepts$3.sizing.icons.medium,
    large: concepts$3.sizing.icons.large,
    xLarge: concepts$3.sizing.icons.xLarge
  },
  colors: {
    primary: concepts$3.colors.primaryBrand.base,
    secondary: concepts$3.colors.secondaryBrand.base,
    success: concepts$3.colors.success.base,
    accent: concepts$3.colors.accent.base
  },
  strokeWidth: {
    small: concepts$3.borders.small,
    default: concepts$3.borders.default,
    large: concepts$3.borders.large
  },
  borderRadius: concepts$3.radius.circle,
  checkmarkFilledColor: concepts$3.colors.foreground
};
var avatar$3 = {
  borderWidth: borders$3.thin,
  borderColor: concepts$3.colors.grayScale._800,
  borderRadius: concepts$3.radius.circle,
  boxShadow: concepts$3.shadows.mediumLight,
  backgroundColor: concepts$3.colors.foreground,
  bubbleBackgroundColors: {
    isActive: concepts$3.colors.success.base,
    default: concepts$3.colors.grayScale._800
  },
  defaultBackgroundColors: {
    primary: concepts$3.colors.primaryBrand.base,
    secondary: concepts$3.colors.secondaryBrand.base,
    success: concepts$3.colors.success.base,
    accent: concepts$3.colors.accent.base,
    warning: concepts$3.colors.warning.base,
    danger: concepts$3.colors.danger.base
  },
  sizes: {
    xSmall: {
      avatarSize: 34,
      bubbleLeftPosition: 22,
      bubbleSize: 12
    },
    small: {
      avatarSize: 44,
      bubbleLeftPosition: 28,
      bubbleSize: 14
    },
    medium: {
      avatarSize: 60,
      bubbleLeftPosition: 40,
      bubbleSize: 16
    },
    large: {
      avatarSize: 76,
      bubbleLeftPosition: 52,
      bubbleSize: 20
    },
    xLarge: {
      avatarSize: 96,
      bubbleLeftPosition: 64,
      bubbleSize: 28
    }
  }
};
var loadingSpinner$3 = {
  size: {
    xSmall: concepts$3.sizing.icons.xSmall,
    small: concepts$3.sizing.icons.small,
    medium: concepts$3.sizing.icons.medium,
    large: concepts$3.sizing.icons.large,
    xLarge: concepts$3.sizing.icons.xLarge
  },
  colors: {
    primary: concepts$3.colors.primaryBrand.base,
    secondary: concepts$3.colors.secondaryBrand.base,
    success: concepts$3.colors.success.base,
    warning: concepts$3.colors.warning.base,
    danger: concepts$3.colors.danger.base
  }
};
var separator$3 = {
  border: borders$3.default,
  color: concepts$3.colors.grayScale._800
};
var card$3 = {
  backgroundColor: concepts$3.colors.warning.base,
  borderRadius: concepts$3.radius.default,
  boxShadow: concepts$3.shadows.mediumDark,
  defaultInset: spacing$3.x4 + " " + spacing$3.x2 + " " + spacing$3.x2 + " " + spacing$3.x2
};
var dialog$3 = {
  backgroundColor: concepts$3.colors.foreground,
  borderRadius: concepts$3.radius.default,
  boxShadow: concepts$3.shadows.mediumDark,
  defaultInset: spacing$3.x6 + " " + spacing$3.x4 + " " + spacing$3.x4 + " " + spacing$3.x4
};
var navigation$3 = {
  backgroundColor: concepts$3.colors.foreground,
  border: borders$3.thin,
  borderColor: concepts$3.colors.grayScale._800
};
var drawer$3 = {
  backgroundColor: concepts$3.colors.foreground,
  borderRadius: concepts$3.radius.small,
  boxShadow: concepts$3.shadows.mediumLight,
  bottomDirectionShadow: concepts$3.shadows.generate('mediumLight', Color$1(concepts$3.colors.defaultShadowColor).rgb().alpha(0.1).toString(), true),
  hoverBoxShadow: concepts$3.shadows.mediumDark,
  hoverBottomDirectionShadow: concepts$3.shadows.generate('mediumDark', Color$1(concepts$3.colors.defaultShadowColor).rgb().alpha(0.1).toString(), true),
  defaultInset: "" + spacing$3.x1,
  tab: {
    dark: {
      backgroundColor: concepts$3.colors.grayScale._600,
      arrowColor: concepts$3.colors.foreground
    },
    light: {
      backgroundColor: concepts$3.colors.foreground,
      arrowColor: icons$3.colors.default
    }
  }
};
var sheet$3 = {
  mobileBorderRadius: concepts$3.radius.large,
  desktopBorderRadius: concepts$3.radius.default,
  backgroundColor: concepts$3.colors.foreground,
  boxShadow: concepts$3.shadows.mediumDark
};
var toggle$3 = {
  isActiveColor: concepts$3.colors.success.base,
  isNotActiveColor: concepts$3.colors.grayScale._900
};
var tooltip$3 = {
  lightBackgroundColor: concepts$3.colors.foreground,
  darkBackgroundColor: concepts$3.colors.grayScale._100,
  boxShadow: concepts$3.shadows.mediumLight,
  borderColor: concepts$3.colors.background
};
var colorPicker$3 = {
  backgroundColor: concepts$3.colors.background,
  borderColor: 'transparent',
  boxShadow: concepts$3.shadows.mediumLight,
  borderRadius: concepts$3.radius.small
};
var sphere$3 = {
  xSmall: {
    circumference: concepts$3.sizing.sphere.xSmall,
    textType: 'title'
  },
  small: {
    circumference: concepts$3.sizing.sphere.small,
    textType: 'title'
  },
  medium: {
    circumference: concepts$3.sizing.sphere.medium,
    textType: 'heroTitle'
  },
  large: {
    circumference: concepts$3.sizing.sphere.large,
    textType: 'heroTitle'
  },
  xLarge: {
    circumference: concepts$3.sizing.sphere.xLarge,
    textType: 'heroTitle'
  }
};
var widget$3 = {
  primary: {
    backgroundColor: concepts$3.colors.primaryBrand.base,
    fontColor: 'inverseBody'
  },
  secondary: {
    backgroundColor: concepts$3.colors.secondaryBrand.base,
    fontColor: 'inverseBody'
  },
  warning: {
    backgroundColor: concepts$3.colors.warning.base,
    fontColor: 'primaryBody'
  },
  success: {
    backgroundColor: concepts$3.colors.success.base,
    fontColor: 'primaryBody'
  },
  danger: {
    backgroundColor: concepts$3.colors.danger.base,
    fontColor: 'inverseBody'
  },
  shadow: {
    backgroundColor: concepts$3.colors.foreground,
    fontColor: 'primaryBody'
  },
  accent: {
    backgroundColor: concepts$3.colors.accent.base,
    fontColor: 'primaryBody'
  }
};
var colorOption$3 = {
  defaults: {
    circleFill: concepts$3.shadows.generate([0, 0, 6, 1], Color$1(concepts$3.colors.defaultShadowColor).rgb().alpha(0.1).toString()),
    innerRing: concepts$3.shadows.generate([0, 0, 0, 11], Color$1([20, 30, 50]).rgb().alpha(0.03).toString()),
    outerRing: concepts$3.shadows.generate([0, 0, 6, 1], Color$1(concepts$3.colors.defaultShadowColor).rgb().alpha(0.06).toString())
  }
};
var entities$3 = {
  mediaQueries: mediaQueries$7,
  hocs: hocs$3,
  text: text$3,
  spacing: spacing$3,
  buttons: buttons$3,
  inputs: inputs$3,
  icons: icons$3,
  card: card$3,
  separator: separator$3,
  loadingSpinner: loadingSpinner$3,
  animatedCheckmark: animatedCheckmark$3,
  avatar: avatar$3,
  dialog: dialog$3,
  sheet: sheet$3,
  navigation: navigation$3,
  toggle: toggle$3,
  tooltip: tooltip$3,
  drawer: drawer$3,
  slider: slider$3,
  colorPicker: colorPicker$3,
  sphere: sphere$3,
  widget: widget$3,
  colorOption: colorOption$3
};

var AQUA = '#61DCE8';
var DARK_BLUE$1 = '#1F4177';
var TEAL$1 = '#309D8E';
var BASE_GRAY$1 = '#313438';
var GREEN$2 = '#1DE6B9';
var YELLOW$1 = '#F7A14D';
var RED$1 = '#FB2E4B';
var WHITE$2 = '#FFF';
var BLUE$1 = '#1DC5E6';
var PURPLE$2 = '#703A96';
var aquaSwatch = computeColorSwatch(AQUA);
var darkBlueSwatch$1 = computeColorSwatch(DARK_BLUE$1);
var tealSwatch$1 = computeColorSwatch(TEAL$1);
var baseGraySwatch = computeColorSwatch(BASE_GRAY$1);
var greenSwatch$2 = computeColorSwatch(GREEN$2);
var yellowSwatch$2 = computeColorSwatch(YELLOW$1);
var redSwatch$2 = computeColorSwatch(RED$1);
var blueSwatch$1 = computeColorSwatch(BLUE$1);
var purpleSwatch$2 = computeColorSwatch(PURPLE$2);
var BASE$1 = '#F5F6F7';
var baseGray = BASE_GRAY$1;
var colors$5 = {
  darkBlueSwatch: darkBlueSwatch$1,
  aquaSwatch: aquaSwatch,
  tealSwatch: tealSwatch$1,
  greenSwatch: greenSwatch$2,
  redSwatch: redSwatch$2,
  yellowSwatch: yellowSwatch$2,
  blueSwatch: blueSwatch$1,
  baseGraySwatch: baseGraySwatch,
  baseGray: baseGray,
  purpleSwatch: purpleSwatch$2,
  BASE: BASE$1,
  WHITE: WHITE$2
};

var BASE_VALUE$2 = 1;
var BASE_UNITS$2 = 'rem';
// Rename to typography
var computeFontSize$2 = function (multiplier) {
  return BASE_VALUE$2 * multiplier;
};
var XSMALL_FONT$2 = computeFontSize$2(0.75) + BASE_UNITS$2;
var SMALL_FONT$2 = computeFontSize$2(0.875) + BASE_UNITS$2;
var MEDIUM_FONT$2 = computeFontSize$2(1) + BASE_UNITS$2;
var MEDIUM_PLUS_FONT$2 = computeFontSize$2(1.25) + BASE_UNITS$2;
var LARGE_FONT$2 = computeFontSize$2(1.75) + BASE_UNITS$2;
var XLARGE_FONT$2 = computeFontSize$2(2.125) + BASE_UNITS$2;
var XXLARGE_FONT$2 = computeFontSize$2(3) + BASE_UNITS$2;
var XSMALL_LINE_HEIGHT$2 = computeFontSize$2(1.5) + BASE_UNITS$2;
var SMALL_LINE_HEIGHT$2 = computeFontSize$2(1.75) + BASE_UNITS$2;
var MEDIUM_LINE_HEIGHT$2 = computeFontSize$2(2) + BASE_UNITS$2;
var LARGE_LINE_HEIGHT$2 = computeFontSize$2(2.5) + BASE_UNITS$2;
var XLARGE_LINE_HEIGHT$2 = computeFontSize$2(3) + BASE_UNITS$2;
var XXLARGE_LINE_HEIGHT$2 = computeFontSize$2(3.5) + BASE_UNITS$2;
var HEADING_FONT_FAMILY$2 = "'Poppins', 'Open Sans',  sans-serif";
var BODY_FONT_FAMILY$2 = "'Poppins', 'Open Sans', sans-serif";
var BODY_FONT_IMPORT$2 = 'https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&display=swap';
var HEADING_FONT_IMPORT$2 = 'https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&display=swap';
var WEIGHT_THIN$2 = 300;
var WEIGHT_REGULAR$2 = 400;
var WEIGHT_SEMI_BOLD$2 = 600;
var WEIGHT_BOLD$2 = 700;
var WEIGHT_EXTRA_BOLD$2 = 800;

var colors$4 = {
  primaryBrand: colors$5.aquaSwatch,
  secondaryBrand: colors$5.darkBlueSwatch,
  accent: colors$5.tealSwatch,
  success: colors$5.greenSwatch,
  warning: colors$5.yellowSwatch,
  danger: colors$5.redSwatch,
  background: colors$5.BASE,
  foreground: colors$5.WHITE,
  grayScale: colors$5.baseGraySwatch,
  blueScale: colors$5.blueSwatch,
  purpleScale: colors$5.purpleSwatch,
  white: colors$5.WHITE,
  defaultShadowColor: SHADOW_DEFAULT_COLOR
};
var concepts$2 = {
  colors: __assign({}, colors$4),
  radius: {
    none: radius.none,
    small: radius.small,
    medium: radius.mediumLarge,
    default: radius.medium,
    large: radius.large,
    circle: radius.circle
  },
  shadows: {
    light: computeShadow('light', Color$1(colors$4.defaultShadowColor).rgb().alpha(0.08).toString()),
    mediumLight: computeShadow('mediumLight', Color$1(colors$4.defaultShadowColor).rgb().alpha(0.08).toString()),
    mediumDark: computeShadow('mediumDark', Color$1(colors$4.defaultShadowColor).rgb().alpha(0.1).toString()),
    dark: computeShadow('dark', Color$1(colors$4.defaultShadowColor).rgb().alpha(0.12).toString()),
    generate: computeShadow
  },
  typography: {
    imports: {
      body: BODY_FONT_IMPORT$2,
      heading: HEADING_FONT_IMPORT$2
    },
    colors: {
      primaryHeading: colors$4.grayScale._200,
      primaryBody: colors$4.grayScale.base,
      inverseHeading: colors$4.white,
      inverseBody: colors$4.white
    },
    families: {
      heading: BODY_FONT_FAMILY$2,
      body: HEADING_FONT_FAMILY$2
    },
    sizes: {
      xSmall: XSMALL_FONT$2,
      small: SMALL_FONT$2,
      medium: MEDIUM_FONT$2,
      mediumPlus: MEDIUM_PLUS_FONT$2,
      large: LARGE_FONT$2,
      xLarge: XLARGE_FONT$2,
      xxLarge: XXLARGE_FONT$2
    },
    weights: {
      thin: WEIGHT_THIN$2,
      regular: WEIGHT_REGULAR$2,
      semiBold: WEIGHT_SEMI_BOLD$2,
      bold: WEIGHT_BOLD$2,
      extraBold: WEIGHT_EXTRA_BOLD$2
    },
    lineHeight: {
      xSmall: XSMALL_LINE_HEIGHT$2,
      small: SMALL_LINE_HEIGHT$2,
      medium: MEDIUM_LINE_HEIGHT$2,
      large: LARGE_LINE_HEIGHT$2,
      xLarge: XLARGE_LINE_HEIGHT$2,
      xxLarge: XXLARGE_LINE_HEIGHT$2
    }
  },
  borders: {
    thin: borders$9.xSmall,
    small: borders$9.small,
    default: borders$9.medium,
    large: borders$9.large,
    bold: borders$9.xLarge
  },
  spacing: {
    baseValue: BASE_VALUE$9,
    baseUnits: BASE_UNITS$9
  },
  mediaQueries: {
    small: mediaQuerySizes.small,
    medium: mediaQuerySizes.medium,
    large: mediaQuerySizes.large,
    xLarge: mediaQuerySizes.xLarge
  },
  sizing: {
    icons: {
      xSmall: icons$9.xSmall,
      small: icons$9.small,
      medium: icons$9.medium,
      large: icons$9.large,
      xLarge: icons$9.xLarge
    },
    sphere: {
      xSmall: sphere$9.xSmall,
      small: sphere$9.small,
      medium: sphere$9.medium,
      large: sphere$9.large,
      xLarge: sphere$9.xLarge
    }
  }
};

var space$2 = computeSpace(concepts$2.spacing.baseValue, concepts$2.spacing.baseUnits);
var spacing$2 = {
  x0: space$2(0),
  x_25: space$2(0.25),
  x_5: space$2(0.5),
  x_75: space$2(0.75),
  x1: space$2(1),
  x1_5: space$2(1.5),
  x2: space$2(2),
  x2_5: space$2(2.5),
  x3: space$2(3),
  x4: space$2(4),
  x5: space$2(5),
  x6: space$2(6),
  x7: space$2(7),
  x8: space$2(8),
  x9: space$2(9)
};
var borders$2 = {
  thin: concepts$2.borders.thin + "px",
  default: concepts$2.borders.default + "px",
  bold: concepts$2.borders.bold + "px"
};
var text$2 = {
  imports: {
    body: concepts$2.typography.imports.body,
    heading: concepts$2.typography.imports.heading
  },
  families: {
    body: concepts$2.typography.families.body,
    heading: concepts$2.typography.families.heading
  },
  types: {
    heroTitle: {
      color: concepts$2.typography.colors.primaryHeading,
      size: concepts$2.typography.sizes.xxLarge,
      fontWeight: concepts$2.typography.weights.extraBold,
      lineHeight: concepts$2.typography.lineHeight.xxLarge,
      fontFamily: concepts$2.typography.families.heading
    },
    title: {
      color: concepts$2.typography.colors.primaryHeading,
      size: concepts$2.typography.sizes.xLarge,
      fontWeight: concepts$2.typography.weights.bold,
      lineHeight: concepts$2.typography.lineHeight.xLarge,
      fontFamily: concepts$2.typography.families.heading
    },
    subtitle: {
      color: concepts$2.typography.colors.primaryHeading,
      size: concepts$2.typography.sizes.large,
      fontWeight: concepts$2.typography.weights.regular,
      lineHeight: concepts$2.typography.lineHeight.medium,
      fontFamily: concepts$2.typography.families.heading
    },
    largeBody: {
      color: concepts$2.typography.colors.primaryBody,
      size: concepts$2.typography.sizes.mediumPlus,
      fontWeight: concepts$2.typography.weights.regular,
      lineHeight: concepts$2.typography.lineHeight.small,
      fontFamily: concepts$2.typography.families.body
    },
    body: {
      color: concepts$2.typography.colors.primaryBody,
      size: concepts$2.typography.sizes.medium,
      fontWeight: concepts$2.typography.weights.regular,
      lineHeight: concepts$2.typography.lineHeight.small,
      fontFamily: concepts$2.typography.families.body
    },
    caption: {
      color: concepts$2.typography.colors.primaryBody,
      size: concepts$2.typography.sizes.small,
      fontWeight: concepts$2.typography.weights.regular,
      lineHeight: concepts$2.typography.lineHeight.small,
      fontFamily: concepts$2.typography.families.body
    },
    footnote: {
      color: concepts$2.typography.colors.primaryBody,
      size: concepts$2.typography.sizes.xSmall,
      fontWeight: concepts$2.typography.weights.regular,
      lineHeight: concepts$2.typography.lineHeight.xSmall,
      fontFamily: concepts$2.typography.families.body
    }
  },
  colors: {
    primaryBrand: concepts$2.colors.primaryBrand.base,
    secondaryBrand: concepts$2.colors.secondaryBrand.base,
    primaryHeading: concepts$2.typography.colors.primaryHeading,
    primaryBody: concepts$2.typography.colors.primaryBody,
    inverseHeading: concepts$2.typography.colors.inverseHeading,
    inverseBody: concepts$2.typography.colors.inverseBody,
    accent: concepts$2.colors.accent.base,
    success: concepts$2.colors.success.base,
    warning: concepts$2.colors.warning.base,
    danger: concepts$2.colors.danger.base,
    anchorBlue: concepts$2.colors.blueScale.base
  },
  weights: {
    thin: concepts$2.typography.weights.thin,
    regular: concepts$2.typography.weights.regular,
    semiBold: concepts$2.typography.weights.semiBold,
    bold: concepts$2.typography.weights.bold,
    extraBold: concepts$2.typography.weights.extraBold
  }
};
var mediaQueries$6 = {
  queries: {
    xSmall: "@media screen and (min-width: 0px)",
    // Large Mobile
    small: "@media screen and (min-width: " + concepts$2.mediaQueries.small + "px)",
    // Tablet
    medium: "@media screen and (min-width: " + concepts$2.mediaQueries.medium + "px)",
    // Large Tablet
    large: "@media screen and (min-width: " + concepts$2.mediaQueries.large + "px)",
    // Desktop
    xLarge: "@media screen and (min-width: " + concepts$2.mediaQueries.xLarge + "px)"
  },
  rawValues: {
    xSmall: 0,
    small: concepts$2.mediaQueries.small,
    medium: concepts$2.mediaQueries.medium,
    large: concepts$2.mediaQueries.large,
    xLarge: concepts$2.mediaQueries.xLarge
  }
};
var hocs$2 = {
  colors: {
    swatches: {
      primaryBrand: concepts$2.colors.primaryBrand,
      secondaryBrand: concepts$2.colors.secondaryBrand,
      success: concepts$2.colors.success,
      danger: concepts$2.colors.danger,
      warning: concepts$2.colors.warning,
      accent: concepts$2.colors.accent,
      grayScale: concepts$2.colors.grayScale,
      blueScale: concepts$2.colors.blueScale,
      purpleScale: concepts$2.colors.purpleScale
    },
    background: concepts$2.colors.background,
    foreground: concepts$2.colors.foreground
  },
  textColors: text$2.colors,
  spacing: spacing$2,
  borderRadius: concepts$2.radius,
  shadows: concepts$2.shadows,
  borders: borders$2
};
// TODO - revisit buttons for contrast and font color
var buttons$2 = {
  boxShadow: concepts$2.shadows.generate,
  borderSize: borders$2.default,
  outlinedBackgroundColor: concepts$2.colors.foreground,
  fontWeight: concepts$2.typography.weights.semiBold,
  sizes: {
    xSmall: {
      inset: spacing$2.x1 + " " + spacing$2.x2,
      outlinedInset: {
        vertical: "calc(" + spacing$2.x1 + " - 2px)",
        horizontal: "calc(" + spacing$2.x2 + " - 2px)"
      },
      fontSize: text$2.types.footnote.size,
      borderRadius: concepts$2.radius.default
    },
    small: {
      inset: spacing$2.x1_5 + " " + spacing$2.x2_5,
      outlinedInset: {
        vertical: "calc(" + spacing$2.x1_5 + " - 2px)",
        horizontal: "calc(" + spacing$2.x2_5 + " - 2px)"
      },
      fontSize: text$2.types.caption.size,
      borderRadius: concepts$2.radius.default
    },
    medium: {
      inset: spacing$2.x1_5 + " " + spacing$2.x3,
      outlinedInset: {
        vertical: "calc(" + spacing$2.x1_5 + " - 2px)",
        horizontal: "calc(" + spacing$2.x3 + " - 2px)"
      },
      fontSize: text$2.types.body.size,
      borderRadius: concepts$2.radius.default
    },
    large: {
      inset: spacing$2.x2 + " " + spacing$2.x4,
      outlinedInset: {
        vertical: "calc(" + spacing$2.x2 + " - 2px)",
        horizontal: "calc(" + spacing$2.x4 + " - 2px)"
      },
      fontSize: text$2.types.largeBody.size,
      borderRadius: concepts$2.radius.default
    },
    xLarge: {
      inset: spacing$2.x3 + " " + spacing$2.x5,
      outlinedInset: {
        vertical: "calc(" + spacing$2.x3 + " - 2px)",
        horizontal: "calc(" + spacing$2.x5 + " - 2px)"
      },
      fontSize: text$2.types.subtitle.size,
      borderRadius: concepts$2.radius.default
    }
  },
  primary: {
    backgroundColor: concepts$2.colors.primaryBrand.base,
    fontColor: concepts$2.typography.colors.inverseBody,
    fontFamily: concepts$2.typography.families.body
  },
  secondary: {
    backgroundColor: concepts$2.colors.secondaryBrand.base,
    fontColor: concepts$2.typography.colors.inverseBody,
    fontFamily: concepts$2.typography.families.body
  },
  warning: {
    backgroundColor: concepts$2.colors.warning.base,
    fontColor: concepts$2.typography.colors.inverseBody,
    fontFamily: concepts$2.typography.families.body
  },
  success: {
    backgroundColor: concepts$2.colors.success.base,
    fontColor: concepts$2.typography.colors.inverseBody,
    fontFamily: concepts$2.typography.families.body
  },
  danger: {
    backgroundColor: concepts$2.colors.danger.base,
    fontColor: concepts$2.typography.colors.inverseBody,
    fontFamily: concepts$2.typography.families.body
  },
  shadow: {
    backgroundColor: concepts$2.colors.foreground,
    fontColor: concepts$2.typography.colors.primaryBody,
    fontFamily: concepts$2.typography.families.body
  },
  accent: {
    backgroundColor: concepts$2.colors.accent.base,
    fontColor: concepts$2.typography.colors.primaryBody,
    fontFamily: concepts$2.typography.families.body
  },
  smoke: {
    backgroundColor: concepts$2.colors.background,
    fontColor: concepts$2.typography.colors.primaryBody,
    fontFamily: concepts$2.typography.families.body
  }
};
var slider$2 = {
  backgroundColor: concepts$2.colors.background,
  borderRadius: concepts$2.radius.small,
  handleRadius: concepts$2.radius.circle,
  handleColor: concepts$2.colors.foreground,
  handleShadow: concepts$2.shadows.light
};
var inputs$2 = {
  checkbox: {
    border: borders$2.default,
    borderRadius: concepts$2.radius.small,
    unCheckedBackgroundColor: concepts$2.colors.grayScale._800,
    checkmarkColor: concepts$2.typography.colors.inverseBody,
    labelColor: concepts$2.typography.colors.primaryBody
  },
  text: {
    borderRadius: concepts$2.radius.default,
    labelColor: concepts$2.colors.accent,
    placeholderColor: concepts$2.typography.colors.primaryBody,
    labelFontSize: text$2.types.caption.size,
    textFontSize: text$2.types.caption.size,
    borderWidth: borders$2.default,
    defaultFocusColor: concepts$2.colors.blueScale.base
  },
  select: {
    dropDownShadow: concepts$2.shadows.mediumDark,
    dropDownBackgroundColor: concepts$2.colors.foreground,
    boxShadow: concepts$2.shadows.light,
    borderRadius: concepts$2.radius.default
  },
  number: {
    backgroundColor: concepts$2.colors.grayScale._200,
    fontColor: concepts$2.typography.colors.inverseBody,
    fontSize: text$2.types.body.size,
    borderRadius: concepts$2.radius.default
  },
  backgroundColor: concepts$2.colors.background,
  fontSize: text$2.types.caption.size,
  fontColor: concepts$2.typography.colors.primaryBody,
  fontFamily: concepts$2.typography.families.body,
  colors: {
    primary: concepts$2.colors.primaryBrand.base,
    secondary: concepts$2.colors.secondaryBrand.base,
    success: concepts$2.colors.success.base,
    warning: concepts$2.colors.warning.base,
    danger: concepts$2.colors.danger.base,
    accent: concepts$2.colors.accent.base,
    dark: concepts$2.colors.primaryBrand._100,
    blue: concepts$2.colors.blueScale.base
  }
};
var icons$2 = {
  sizes: {
    xSmall: concepts$2.sizing.icons.xSmall,
    small: concepts$2.sizing.icons.small,
    medium: concepts$2.sizing.icons.medium,
    large: concepts$2.sizing.icons.large,
    xLarge: concepts$2.sizing.icons.xLarge
  },
  colors: {
    default: concepts$2.colors.grayScale._800,
    primaryBodyText: concepts$2.typography.colors.primaryBody,
    primaryHeadingText: concepts$2.typography.colors.primaryHeading,
    inverseBodyText: concepts$2.typography.colors.inverseBody,
    inverseHeadingText: concepts$2.typography.colors.inverseHeading,
    primaryBrand: concepts$2.colors.primaryBrand.base,
    secondaryBrand: concepts$2.colors.secondaryBrand.base,
    warning: concepts$2.colors.warning.base,
    danger: concepts$2.colors.danger.base,
    success: concepts$2.colors.success.base,
    background: concepts$2.colors.background,
    accent: concepts$2.colors.background
  }
};
var animatedCheckmark$2 = {
  sizeMap: {
    xSmall: concepts$2.sizing.icons.xSmall,
    small: concepts$2.sizing.icons.small,
    medium: concepts$2.sizing.icons.medium,
    large: concepts$2.sizing.icons.large,
    xLarge: concepts$2.sizing.icons.xLarge
  },
  colors: {
    primary: concepts$2.colors.primaryBrand.base,
    secondary: concepts$2.colors.secondaryBrand.base,
    success: concepts$2.colors.success.base,
    accent: concepts$2.colors.accent.base
  },
  strokeWidth: {
    small: concepts$2.borders.small,
    default: concepts$2.borders.default,
    large: concepts$2.borders.large
  },
  borderRadius: concepts$2.radius.circle,
  checkmarkFilledColor: concepts$2.colors.foreground
};
var avatar$2 = {
  borderWidth: borders$2.thin,
  borderColor: concepts$2.colors.grayScale._800,
  borderRadius: concepts$2.radius.circle,
  boxShadow: concepts$2.shadows.mediumLight,
  backgroundColor: concepts$2.colors.foreground,
  bubbleBackgroundColors: {
    isActive: concepts$2.colors.success.base,
    default: concepts$2.colors.grayScale._800
  },
  defaultBackgroundColors: {
    primary: concepts$2.colors.primaryBrand.base,
    secondary: concepts$2.colors.secondaryBrand.base,
    success: concepts$2.colors.success.base,
    accent: concepts$2.colors.accent.base,
    warning: concepts$2.colors.warning.base,
    danger: concepts$2.colors.danger.base
  },
  sizes: {
    xSmall: {
      avatarSize: 34,
      bubbleLeftPosition: 22,
      bubbleSize: 12
    },
    small: {
      avatarSize: 44,
      bubbleLeftPosition: 28,
      bubbleSize: 14
    },
    medium: {
      avatarSize: 60,
      bubbleLeftPosition: 40,
      bubbleSize: 16
    },
    large: {
      avatarSize: 76,
      bubbleLeftPosition: 52,
      bubbleSize: 20
    },
    xLarge: {
      avatarSize: 96,
      bubbleLeftPosition: 64,
      bubbleSize: 28
    }
  }
};
var loadingSpinner$2 = {
  size: {
    xSmall: concepts$2.sizing.icons.xSmall,
    small: concepts$2.sizing.icons.small,
    medium: concepts$2.sizing.icons.medium,
    large: concepts$2.sizing.icons.large,
    xLarge: concepts$2.sizing.icons.xLarge
  },
  colors: {
    primary: concepts$2.colors.primaryBrand.base,
    secondary: concepts$2.colors.secondaryBrand.base,
    success: concepts$2.colors.success.base,
    warning: concepts$2.colors.warning.base,
    danger: concepts$2.colors.danger.base
  }
};
var separator$2 = {
  border: borders$2.default,
  color: concepts$2.colors.grayScale._800
};
var card$2 = {
  backgroundColor: concepts$2.colors.warning.base,
  borderRadius: concepts$2.radius.default,
  boxShadow: concepts$2.shadows.mediumDark,
  defaultInset: spacing$2.x4 + " " + spacing$2.x2 + " " + spacing$2.x2 + " " + spacing$2.x2
};
var dialog$2 = {
  backgroundColor: concepts$2.colors.foreground,
  borderRadius: concepts$2.radius.default,
  boxShadow: concepts$2.shadows.mediumDark,
  defaultInset: spacing$2.x6 + " " + spacing$2.x4 + " " + spacing$2.x4 + " " + spacing$2.x4
};
var navigation$2 = {
  backgroundColor: concepts$2.colors.foreground,
  border: borders$2.thin,
  borderColor: concepts$2.colors.grayScale._800
};
var drawer$2 = {
  backgroundColor: concepts$2.colors.foreground,
  borderRadius: concepts$2.radius.small,
  boxShadow: concepts$2.shadows.mediumLight,
  bottomDirectionShadow: concepts$2.shadows.generate('mediumLight', Color$1(concepts$2.colors.defaultShadowColor).rgb().alpha(0.1).toString(), true),
  hoverBoxShadow: concepts$2.shadows.mediumDark,
  hoverBottomDirectionShadow: concepts$2.shadows.generate('mediumDark', Color$1(concepts$2.colors.defaultShadowColor).rgb().alpha(0.1).toString(), true),
  defaultInset: "" + spacing$2.x1,
  tab: {
    dark: {
      backgroundColor: concepts$2.colors.grayScale._600,
      arrowColor: concepts$2.colors.foreground
    },
    light: {
      backgroundColor: concepts$2.colors.foreground,
      arrowColor: icons$2.colors.default
    }
  }
};
var sheet$2 = {
  mobileBorderRadius: concepts$2.radius.large,
  desktopBorderRadius: concepts$2.radius.default,
  backgroundColor: concepts$2.colors.foreground,
  boxShadow: concepts$2.shadows.mediumDark
};
var toggle$2 = {
  isActiveColor: concepts$2.colors.success.base,
  isNotActiveColor: concepts$2.colors.grayScale._900
};
var tooltip$2 = {
  lightBackgroundColor: concepts$2.colors.foreground,
  darkBackgroundColor: concepts$2.colors.grayScale._100,
  boxShadow: concepts$2.shadows.mediumLight,
  borderColor: concepts$2.colors.background
};
var colorPicker$2 = {
  backgroundColor: concepts$2.colors.background,
  borderColor: 'transparent',
  boxShadow: concepts$2.shadows.mediumLight,
  borderRadius: concepts$2.radius.small
};
var sphere$2 = {
  xSmall: {
    circumference: concepts$2.sizing.sphere.xSmall,
    textType: 'title'
  },
  small: {
    circumference: concepts$2.sizing.sphere.small,
    textType: 'title'
  },
  medium: {
    circumference: concepts$2.sizing.sphere.medium,
    textType: 'heroTitle'
  },
  large: {
    circumference: concepts$2.sizing.sphere.large,
    textType: 'heroTitle'
  },
  xLarge: {
    circumference: concepts$2.sizing.sphere.xLarge,
    textType: 'heroTitle'
  }
};
var widget$2 = {
  primary: {
    backgroundColor: concepts$2.colors.primaryBrand.base,
    fontColor: 'inverseBody'
  },
  secondary: {
    backgroundColor: concepts$2.colors.secondaryBrand.base,
    fontColor: 'inverseBody'
  },
  warning: {
    backgroundColor: concepts$2.colors.warning.base,
    fontColor: 'primaryBody'
  },
  success: {
    backgroundColor: concepts$2.colors.success.base,
    fontColor: 'primaryBody'
  },
  danger: {
    backgroundColor: concepts$2.colors.danger.base,
    fontColor: 'inverseBody'
  },
  shadow: {
    backgroundColor: concepts$2.colors.foreground,
    fontColor: 'primaryBody'
  },
  accent: {
    backgroundColor: concepts$2.colors.accent.base,
    fontColor: 'primaryBody'
  }
};
var colorOption$2 = {
  defaults: {
    circleFill: concepts$2.shadows.generate([0, 0, 6, 1], Color$1(concepts$2.colors.defaultShadowColor).rgb().alpha(0.1).toString()),
    innerRing: concepts$2.shadows.generate([0, 0, 0, 11], Color$1([20, 30, 50]).rgb().alpha(0.03).toString()),
    outerRing: concepts$2.shadows.generate([0, 0, 6, 1], Color$1(concepts$2.colors.defaultShadowColor).rgb().alpha(0.06).toString())
  }
};
var entities$2 = {
  mediaQueries: mediaQueries$6,
  hocs: hocs$2,
  text: text$2,
  spacing: spacing$2,
  buttons: buttons$2,
  inputs: inputs$2,
  icons: icons$2,
  card: card$2,
  separator: separator$2,
  loadingSpinner: loadingSpinner$2,
  animatedCheckmark: animatedCheckmark$2,
  avatar: avatar$2,
  dialog: dialog$2,
  sheet: sheet$2,
  navigation: navigation$2,
  toggle: toggle$2,
  tooltip: tooltip$2,
  drawer: drawer$2,
  slider: slider$2,
  colorPicker: colorPicker$2,
  sphere: sphere$2,
  widget: widget$2,
  colorOption: colorOption$2
};

// EXTERNAL VALUES
var PINK = '#FF04A0';
var LIGHT_BLUE$1 = '#02C3E2';
var GREEN$1 = '#44D5A4';
var YELLOW = '#F4E467';
var RED = '#F26666';
// TODO change ACCENT
var ACCENT = '#FFF';
var WHITE$1 = '#FFF';
var BLUE = '#32abe4';
var PURPLE$1 = '#703A96';
var pinkSwatch = computeColorSwatch(PINK);
var brightBlueSwatch = computeColorSwatch(LIGHT_BLUE$1);
var greenSwatch$1 = computeColorSwatch(GREEN$1);
var yellowSwatch$1 = computeColorSwatch(YELLOW);
var redSwatch$1 = computeColorSwatch(RED);
var accentSwatch = computeColorSwatch(ACCENT);
var blueSwatch = computeColorSwatch(BLUE);
var purpleSwatch$1 = computeColorSwatch(PURPLE$1);
var lightGray = '#f5f6f7';
var colors$3 = {
  pinkSwatch: pinkSwatch,
  brightBlueSwatch: brightBlueSwatch,
  greenSwatch: greenSwatch$1,
  redSwatch: redSwatch$1,
  yellowSwatch: yellowSwatch$1,
  accentSwatch: accentSwatch,
  WHITE: WHITE$1,
  blueSwatch: blueSwatch,
  lightGray: lightGray,
  purpleSwatch: purpleSwatch$1
};

var BASE_VALUE$1 = 1;
var BASE_UNITS$1 = 'rem';
var computeFontSize$1 = function (multiplier) {
  return BASE_VALUE$1 * multiplier;
};
var XSMALL_FONT$1 = computeFontSize$1(0.75) + BASE_UNITS$1;
var SMALL_FONT$1 = computeFontSize$1(0.875) + BASE_UNITS$1;
var MEDIUM_FONT$1 = computeFontSize$1(1) + BASE_UNITS$1;
var MEDIUM_PLUS_FONT$1 = computeFontSize$1(1.125) + BASE_UNITS$1;
var LARGE_FONT$1 = computeFontSize$1(1.5) + BASE_UNITS$1;
var XLARGE_FONT$1 = computeFontSize$1(2) + BASE_UNITS$1;
var XXLARGE_FONT$1 = computeFontSize$1(4) + BASE_UNITS$1;
var XSMALL_LINE_HEIGHT$1 = computeFontSize$1(1.5) + BASE_UNITS$1;
var SMALL_LINE_HEIGHT$1 = computeFontSize$1(1.75) + BASE_UNITS$1;
var MEDIUM_LINE_HEIGHT$1 = computeFontSize$1(2) + BASE_UNITS$1;
var LARGE_LINE_HEIGHT$1 = computeFontSize$1(2.5) + BASE_UNITS$1;
var XLARGE_LINE_HEIGHT$1 = computeFontSize$1(3) + BASE_UNITS$1;
var XXLARGE_LINE_HEIGHT$1 = computeFontSize$1(3.5) + BASE_UNITS$1;
var HEADING_FONT_FAMILY$1 = "'Montserrat', sans-serif";
var BODY_FONT_FAMILY$1 = "'Montserrat', sans-serif";
var HEADING_FONT_IMPORT$1 = 'https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&display=swap';
var BODY_FONT_IMPORT$1 = 'https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&display=swap';
var WEIGHT_THIN$1 = 300;
var WEIGHT_REGULAR$1 = 400;
var WEIGHT_SEMI_BOLD$1 = 500;
var WEIGHT_BOLD$1 = 600;
var WEIGHT_EXTRA_BOLD$1 = 800;

var colors$2 = {
  primaryBrand: colors$3.pinkSwatch,
  secondaryBrand: colors$3.brightBlueSwatch,
  accent: colors$3.accentSwatch,
  success: colors$3.greenSwatch,
  warning: colors$3.yellowSwatch,
  danger: colors$3.redSwatch,
  background: colors$3.lightGray,
  foreground: colors$3.WHITE,
  grayScale: computeGrayScale(colors$3.pinkSwatch),
  blueScale: colors$3.blueSwatch,
  purpleScale: colors$3.purpleSwatch,
  white: colors$3.WHITE,
  defaultShadowColor: SHADOW_DEFAULT_COLOR
};
var concepts$1 = {
  colors: __assign({}, colors$2),
  radius: {
    none: radius.none,
    small: radius.small,
    medium: radius.mediumLarge,
    default: radius.medium,
    large: radius.large,
    circle: radius.circle
  },
  shadows: {
    light: computeShadow('light', Color$1(colors$2.defaultShadowColor).rgb().alpha(0.08).toString()),
    mediumLight: computeShadow('mediumLight', Color$1(colors$2.defaultShadowColor).rgb().alpha(0.08).toString()),
    mediumDark: computeShadow('mediumDark', Color$1(colors$2.defaultShadowColor).rgb().alpha(0.1).toString()),
    dark: computeShadow('dark', Color$1(colors$2.defaultShadowColor).rgb().alpha(0.12).toString()),
    generate: computeShadow
  },
  typography: {
    imports: {
      body: BODY_FONT_IMPORT$1,
      heading: HEADING_FONT_IMPORT$1
    },
    colors: {
      primaryHeading: colors$2.grayScale._200,
      primaryBody: colors$2.grayScale.base,
      inverseHeading: colors$2.white,
      inverseBody: colors$2.white
    },
    families: {
      heading: BODY_FONT_FAMILY$1,
      body: HEADING_FONT_FAMILY$1
    },
    sizes: {
      xSmall: XSMALL_FONT$1,
      small: SMALL_FONT$1,
      medium: MEDIUM_FONT$1,
      mediumPlus: MEDIUM_PLUS_FONT$1,
      large: LARGE_FONT$1,
      xLarge: XLARGE_FONT$1,
      xxLarge: XXLARGE_FONT$1
    },
    weights: {
      thin: WEIGHT_THIN$1,
      regular: WEIGHT_REGULAR$1,
      semiBold: WEIGHT_SEMI_BOLD$1,
      bold: WEIGHT_BOLD$1,
      extraBold: WEIGHT_EXTRA_BOLD$1
    },
    lineHeight: {
      xSmall: XSMALL_LINE_HEIGHT$1,
      small: SMALL_LINE_HEIGHT$1,
      medium: MEDIUM_LINE_HEIGHT$1,
      large: LARGE_LINE_HEIGHT$1,
      xLarge: XLARGE_LINE_HEIGHT$1,
      xxLarge: XXLARGE_LINE_HEIGHT$1
    }
  },
  borders: {
    thin: borders$9.xSmall,
    small: borders$9.small,
    default: borders$9.medium,
    large: borders$9.large,
    bold: borders$9.xLarge
  },
  spacing: {
    baseValue: BASE_VALUE$9,
    baseUnits: BASE_UNITS$9
  },
  mediaQueries: {
    small: mediaQuerySizes.small,
    medium: mediaQuerySizes.medium,
    large: mediaQuerySizes.large,
    xLarge: mediaQuerySizes.xLarge
  },
  sizing: {
    icons: {
      xSmall: icons$9.xSmall,
      small: icons$9.small,
      medium: icons$9.medium,
      large: icons$9.large,
      xLarge: icons$9.xLarge
    },
    sphere: {
      xSmall: sphere$9.xSmall,
      small: sphere$9.small,
      medium: sphere$9.medium,
      large: sphere$9.large,
      xLarge: sphere$9.xLarge
    }
  }
};

var space$1 = computeSpace(concepts$1.spacing.baseValue, concepts$1.spacing.baseUnits);
var spacing$1 = {
  x0: space$1(0),
  x_25: space$1(0.25),
  x_5: space$1(0.5),
  x_75: space$1(0.75),
  x1: space$1(1),
  x1_5: space$1(1.5),
  x2: space$1(2),
  x2_5: space$1(2.5),
  x3: space$1(3),
  x4: space$1(4),
  x5: space$1(5),
  x6: space$1(6),
  x7: space$1(7),
  x8: space$1(8),
  x9: space$1(9)
};
var borders$1 = {
  thin: concepts$1.borders.thin + "px",
  default: concepts$1.borders.default + "px",
  bold: concepts$1.borders.bold + "px"
};
var text$1 = {
  imports: {
    body: concepts$1.typography.imports.body,
    heading: concepts$1.typography.imports.heading
  },
  families: {
    body: concepts$1.typography.families.body,
    heading: concepts$1.typography.families.heading
  },
  types: {
    heroTitle: {
      color: concepts$1.typography.colors.primaryHeading,
      size: concepts$1.typography.sizes.xxLarge,
      fontWeight: concepts$1.typography.weights.extraBold,
      lineHeight: concepts$1.typography.lineHeight.xxLarge,
      fontFamily: concepts$1.typography.families.heading
    },
    title: {
      color: concepts$1.typography.colors.primaryHeading,
      size: concepts$1.typography.sizes.xLarge,
      fontWeight: concepts$1.typography.weights.bold,
      lineHeight: concepts$1.typography.lineHeight.xLarge,
      fontFamily: concepts$1.typography.families.heading
    },
    subtitle: {
      color: concepts$1.typography.colors.primaryHeading,
      size: concepts$1.typography.sizes.large,
      fontWeight: concepts$1.typography.weights.regular,
      lineHeight: concepts$1.typography.lineHeight.medium,
      fontFamily: concepts$1.typography.families.heading
    },
    largeBody: {
      color: concepts$1.typography.colors.primaryBody,
      size: concepts$1.typography.sizes.mediumPlus,
      fontWeight: concepts$1.typography.weights.regular,
      lineHeight: concepts$1.typography.lineHeight.small,
      fontFamily: concepts$1.typography.families.body
    },
    body: {
      color: concepts$1.typography.colors.primaryBody,
      size: concepts$1.typography.sizes.medium,
      fontWeight: concepts$1.typography.weights.regular,
      lineHeight: concepts$1.typography.lineHeight.small,
      fontFamily: concepts$1.typography.families.body
    },
    caption: {
      color: concepts$1.typography.colors.primaryBody,
      size: concepts$1.typography.sizes.small,
      fontWeight: concepts$1.typography.weights.regular,
      lineHeight: concepts$1.typography.lineHeight.small,
      fontFamily: concepts$1.typography.families.body
    },
    footnote: {
      color: concepts$1.typography.colors.primaryBody,
      size: concepts$1.typography.sizes.xSmall,
      fontWeight: concepts$1.typography.weights.regular,
      lineHeight: concepts$1.typography.lineHeight.xSmall,
      fontFamily: concepts$1.typography.families.body
    }
  },
  colors: {
    primaryBrand: concepts$1.colors.primaryBrand.base,
    secondaryBrand: concepts$1.colors.secondaryBrand.base,
    primaryHeading: concepts$1.typography.colors.primaryHeading,
    primaryBody: concepts$1.typography.colors.primaryBody,
    inverseHeading: concepts$1.typography.colors.inverseHeading,
    inverseBody: concepts$1.typography.colors.inverseBody,
    accent: concepts$1.colors.accent.base,
    success: concepts$1.colors.success.base,
    warning: concepts$1.colors.warning.base,
    danger: concepts$1.colors.danger.base,
    anchorBlue: concepts$1.colors.blueScale.base
  },
  weights: {
    thin: concepts$1.typography.weights.thin,
    regular: concepts$1.typography.weights.regular,
    semiBold: concepts$1.typography.weights.semiBold,
    bold: concepts$1.typography.weights.bold,
    extraBold: concepts$1.typography.weights.extraBold
  }
};
var mediaQueries$5 = {
  queries: {
    xSmall: "@media screen and (min-width: 0px)",
    // Large Mobile
    small: "@media screen and (min-width: " + concepts$1.mediaQueries.small + "px)",
    // Tablet
    medium: "@media screen and (min-width: " + concepts$1.mediaQueries.medium + "px)",
    // Large Tablet
    large: "@media screen and (min-width: " + concepts$1.mediaQueries.large + "px)",
    // Desktop
    xLarge: "@media screen and (min-width: " + concepts$1.mediaQueries.xLarge + "px)"
  },
  rawValues: {
    xSmall: 0,
    small: concepts$1.mediaQueries.small,
    medium: concepts$1.mediaQueries.medium,
    large: concepts$1.mediaQueries.large,
    xLarge: concepts$1.mediaQueries.xLarge
  }
};
var hocs$1 = {
  colors: {
    swatches: {
      primaryBrand: concepts$1.colors.primaryBrand,
      secondaryBrand: concepts$1.colors.secondaryBrand,
      success: concepts$1.colors.success,
      danger: concepts$1.colors.danger,
      warning: concepts$1.colors.warning,
      accent: concepts$1.colors.accent,
      grayScale: concepts$1.colors.grayScale,
      blueScale: concepts$1.colors.blueScale,
      purpleScale: concepts$1.colors.purpleScale
    },
    background: concepts$1.colors.background,
    foreground: concepts$1.colors.foreground
  },
  textColors: text$1.colors,
  spacing: spacing$1,
  borderRadius: concepts$1.radius,
  shadows: concepts$1.shadows,
  borders: borders$1
};
// TODO - revisit buttons for contrast and font color
var buttons$1 = {
  boxShadow: concepts$1.shadows.generate,
  borderSize: borders$1.default,
  outlinedBackgroundColor: concepts$1.colors.foreground,
  fontWeight: concepts$1.typography.weights.semiBold,
  sizes: {
    xSmall: {
      inset: spacing$1.x1 + " " + spacing$1.x2,
      outlinedInset: {
        vertical: "calc(" + spacing$1.x1 + " - 2px)",
        horizontal: "calc(" + spacing$1.x2 + " - 2px)"
      },
      fontSize: text$1.types.footnote.size,
      borderRadius: concepts$1.radius.default
    },
    small: {
      inset: spacing$1.x1_5 + " " + spacing$1.x2_5,
      outlinedInset: {
        vertical: "calc(" + spacing$1.x1_5 + " - 2px)",
        horizontal: "calc(" + spacing$1.x2_5 + " - 2px)"
      },
      fontSize: text$1.types.caption.size,
      borderRadius: concepts$1.radius.default
    },
    medium: {
      inset: spacing$1.x1_5 + " " + spacing$1.x3,
      outlinedInset: {
        vertical: "calc(" + spacing$1.x1_5 + " - 2px)",
        horizontal: "calc(" + spacing$1.x3 + " - 2px)"
      },
      fontSize: text$1.types.body.size,
      borderRadius: concepts$1.radius.default
    },
    large: {
      inset: spacing$1.x2 + " " + spacing$1.x4,
      outlinedInset: {
        vertical: "calc(" + spacing$1.x2 + " - 2px)",
        horizontal: "calc(" + spacing$1.x4 + " - 2px)"
      },
      fontSize: text$1.types.largeBody.size,
      borderRadius: concepts$1.radius.default
    },
    xLarge: {
      inset: spacing$1.x3 + " " + spacing$1.x5,
      outlinedInset: {
        vertical: "calc(" + spacing$1.x3 + " - 2px)",
        horizontal: "calc(" + spacing$1.x5 + " - 2px)"
      },
      fontSize: text$1.types.subtitle.size,
      borderRadius: concepts$1.radius.default
    }
  },
  primary: {
    backgroundColor: concepts$1.colors.primaryBrand.base,
    fontColor: concepts$1.typography.colors.inverseBody,
    fontFamily: concepts$1.typography.families.body
  },
  secondary: {
    backgroundColor: concepts$1.colors.secondaryBrand.base,
    fontColor: concepts$1.typography.colors.inverseBody,
    fontFamily: concepts$1.typography.families.body
  },
  warning: {
    backgroundColor: concepts$1.colors.warning.base,
    fontColor: concepts$1.typography.colors.inverseBody,
    fontFamily: concepts$1.typography.families.body
  },
  success: {
    backgroundColor: concepts$1.colors.success.base,
    fontColor: concepts$1.typography.colors.inverseBody,
    fontFamily: concepts$1.typography.families.body
  },
  danger: {
    backgroundColor: concepts$1.colors.danger.base,
    fontColor: concepts$1.typography.colors.inverseBody,
    fontFamily: concepts$1.typography.families.body
  },
  shadow: {
    backgroundColor: concepts$1.colors.foreground,
    fontColor: concepts$1.typography.colors.primaryBody,
    fontFamily: concepts$1.typography.families.body
  },
  accent: {
    backgroundColor: concepts$1.colors.accent.base,
    fontColor: concepts$1.typography.colors.primaryBody,
    fontFamily: concepts$1.typography.families.body
  },
  smoke: {
    backgroundColor: concepts$1.colors.background,
    fontColor: concepts$1.typography.colors.primaryBody,
    fontFamily: concepts$1.typography.families.body
  }
};
var inputs$1 = {
  checkbox: {
    border: borders$1.default,
    borderRadius: concepts$1.radius.small,
    unCheckedBackgroundColor: concepts$1.colors.grayScale._1000,
    checkmarkColor: concepts$1.typography.colors.inverseBody,
    labelColor: concepts$1.typography.colors.primaryBody
  },
  text: {
    borderRadius: concepts$1.radius.default,
    labelColor: concepts$1.colors.accent,
    placeholderColor: concepts$1.typography.colors.primaryBody,
    labelFontSize: text$1.types.caption.size,
    textFontSize: text$1.types.caption.size,
    borderWidth: borders$1.default,
    defaultFocusColor: concepts$1.colors.blueScale.base
  },
  select: {
    dropDownShadow: concepts$1.shadows.mediumDark,
    dropDownBackgroundColor: concepts$1.colors.foreground,
    boxShadow: concepts$1.shadows.light,
    borderRadius: concepts$1.radius.default
  },
  number: {
    backgroundColor: concepts$1.colors.grayScale._200,
    fontColor: concepts$1.typography.colors.inverseBody,
    fontSize: text$1.types.body.size,
    borderRadius: concepts$1.radius.default
  },
  backgroundColor: concepts$1.colors.background,
  fontSize: text$1.types.caption.size,
  fontColor: concepts$1.typography.colors.primaryBody,
  fontFamily: concepts$1.typography.families.body,
  colors: {
    primary: concepts$1.colors.primaryBrand.base,
    secondary: concepts$1.colors.secondaryBrand.base,
    success: concepts$1.colors.success.base,
    warning: concepts$1.colors.warning.base,
    danger: concepts$1.colors.danger.base,
    accent: concepts$1.colors.accent.base,
    dark: concepts$1.colors.primaryBrand._100,
    blue: concepts$1.colors.blueScale.base
  }
};
var icons$1 = {
  sizes: {
    xSmall: concepts$1.sizing.icons.xSmall,
    small: concepts$1.sizing.icons.small,
    medium: concepts$1.sizing.icons.medium,
    large: concepts$1.sizing.icons.large,
    xLarge: concepts$1.sizing.icons.xLarge
  },
  colors: {
    default: concepts$1.colors.grayScale._800,
    primaryBodyText: concepts$1.typography.colors.primaryBody,
    primaryHeadingText: concepts$1.typography.colors.primaryHeading,
    inverseBodyText: concepts$1.typography.colors.inverseBody,
    inverseHeadingText: concepts$1.typography.colors.inverseHeading,
    primaryBrand: concepts$1.colors.primaryBrand.base,
    secondaryBrand: concepts$1.colors.secondaryBrand.base,
    warning: concepts$1.colors.warning.base,
    danger: concepts$1.colors.danger.base,
    success: concepts$1.colors.success.base,
    background: concepts$1.colors.background,
    accent: concepts$1.colors.background
  }
};
var animatedCheckmark$1 = {
  sizeMap: {
    xSmall: concepts$1.sizing.icons.xSmall,
    small: concepts$1.sizing.icons.small,
    medium: concepts$1.sizing.icons.medium,
    large: concepts$1.sizing.icons.large,
    xLarge: concepts$1.sizing.icons.xLarge
  },
  colors: {
    primary: concepts$1.colors.primaryBrand.base,
    secondary: concepts$1.colors.secondaryBrand.base,
    success: concepts$1.colors.success.base,
    accent: concepts$1.colors.accent.base
  },
  strokeWidth: {
    small: concepts$1.borders.small,
    default: concepts$1.borders.default,
    large: concepts$1.borders.large
  },
  borderRadius: concepts$1.radius.circle,
  checkmarkFilledColor: concepts$1.colors.foreground
};
var avatar$1 = {
  borderWidth: borders$1.thin,
  borderColor: concepts$1.colors.grayScale._1000,
  borderRadius: concepts$1.radius.circle,
  boxShadow: concepts$1.shadows.mediumLight,
  backgroundColor: concepts$1.colors.foreground,
  bubbleBackgroundColors: {
    isActive: concepts$1.colors.success.base,
    default: concepts$1.colors.grayScale._1000
  },
  defaultBackgroundColors: {
    primary: concepts$1.colors.primaryBrand.base,
    secondary: concepts$1.colors.secondaryBrand.base,
    success: concepts$1.colors.success.base,
    accent: concepts$1.colors.accent.base,
    warning: concepts$1.colors.warning.base,
    danger: concepts$1.colors.danger.base
  },
  sizes: {
    xSmall: {
      avatarSize: 34,
      bubbleLeftPosition: 22,
      bubbleSize: 12
    },
    small: {
      avatarSize: 44,
      bubbleLeftPosition: 28,
      bubbleSize: 14
    },
    medium: {
      avatarSize: 60,
      bubbleLeftPosition: 40,
      bubbleSize: 16
    },
    large: {
      avatarSize: 76,
      bubbleLeftPosition: 52,
      bubbleSize: 20
    },
    xLarge: {
      avatarSize: 96,
      bubbleLeftPosition: 64,
      bubbleSize: 28
    }
  }
};
var loadingSpinner$1 = {
  size: {
    xSmall: concepts$1.sizing.icons.xSmall,
    small: concepts$1.sizing.icons.small,
    medium: concepts$1.sizing.icons.medium,
    large: concepts$1.sizing.icons.large,
    xLarge: concepts$1.sizing.icons.xLarge
  },
  colors: {
    primary: concepts$1.colors.primaryBrand.base,
    secondary: concepts$1.colors.secondaryBrand.base,
    success: concepts$1.colors.success.base,
    warning: concepts$1.colors.warning.base,
    danger: concepts$1.colors.danger.base
  }
};
var separator$1 = {
  border: borders$1.default,
  color: concepts$1.colors.grayScale._1000
};
var card$1 = {
  backgroundColor: concepts$1.colors.warning.base,
  borderRadius: concepts$1.radius.default,
  boxShadow: concepts$1.shadows.mediumDark,
  defaultInset: spacing$1.x4 + " " + spacing$1.x2 + " " + spacing$1.x2 + " " + spacing$1.x2
};
var dialog$1 = {
  backgroundColor: concepts$1.colors.foreground,
  borderRadius: concepts$1.radius.default,
  boxShadow: concepts$1.shadows.mediumDark,
  defaultInset: spacing$1.x6 + " " + spacing$1.x4 + " " + spacing$1.x4 + " " + spacing$1.x4
};
var drawer$1 = {
  backgroundColor: concepts$1.colors.foreground,
  borderRadius: concepts$1.radius.small,
  boxShadow: concepts$1.shadows.mediumLight,
  bottomDirectionShadow: concepts$1.shadows.generate('mediumLight', Color$1(concepts$1.colors.defaultShadowColor).rgb().alpha(0.1).toString(), true),
  hoverBoxShadow: concepts$1.shadows.mediumDark,
  hoverBottomDirectionShadow: concepts$1.shadows.generate('mediumDark', Color$1(concepts$1.colors.defaultShadowColor).rgb().alpha(0.1).toString(), true),
  defaultInset: "" + spacing$1.x1,
  tab: {
    dark: {
      backgroundColor: concepts$1.colors.grayScale._600,
      arrowColor: concepts$1.colors.foreground
    },
    light: {
      backgroundColor: concepts$1.colors.foreground,
      arrowColor: icons$1.colors.default
    }
  }
};
var navigation$1 = {
  backgroundColor: concepts$1.colors.foreground,
  border: borders$1.thin,
  borderColor: concepts$1.colors.grayScale._1000
};
var sheet$1 = {
  mobileBorderRadius: concepts$1.radius.large,
  desktopBorderRadius: concepts$1.radius.default,
  backgroundColor: concepts$1.colors.foreground,
  boxShadow: concepts$1.shadows.mediumDark
};
var slider$1 = {
  backgroundColor: concepts$1.colors.background,
  borderRadius: concepts$1.radius.small,
  handleRadius: concepts$1.radius.circle,
  handleColor: concepts$1.colors.foreground,
  handleShadow: concepts$1.shadows.light
};
var toggle$1 = {
  isActiveColor: concepts$1.colors.success.base,
  isNotActiveColor: concepts$1.colors.grayScale._900
};
var tooltip$1 = {
  lightBackgroundColor: concepts$1.colors.foreground,
  darkBackgroundColor: concepts$1.colors.grayScale._100,
  boxShadow: concepts$1.shadows.mediumLight,
  borderColor: concepts$1.colors.background
};
var colorPicker$1 = {
  backgroundColor: concepts$1.colors.background,
  borderColor: 'transparent',
  boxShadow: concepts$1.shadows.mediumLight,
  borderRadius: concepts$1.radius.small
};
var sphere$1 = {
  xSmall: {
    circumference: concepts$1.sizing.sphere.xSmall,
    textType: 'title'
  },
  small: {
    circumference: concepts$1.sizing.sphere.small,
    textType: 'title'
  },
  medium: {
    circumference: concepts$1.sizing.sphere.medium,
    textType: 'heroTitle'
  },
  large: {
    circumference: concepts$1.sizing.sphere.large,
    textType: 'heroTitle'
  },
  xLarge: {
    circumference: concepts$1.sizing.sphere.xLarge,
    textType: 'heroTitle'
  }
};
var widget$1 = {
  primary: {
    backgroundColor: concepts$1.colors.primaryBrand.base,
    fontColor: 'inverseBody'
  },
  secondary: {
    backgroundColor: concepts$1.colors.secondaryBrand.base,
    fontColor: 'inverseBody'
  },
  warning: {
    backgroundColor: concepts$1.colors.warning.base,
    fontColor: 'primaryBody'
  },
  success: {
    backgroundColor: concepts$1.colors.success.base,
    fontColor: 'primaryBody'
  },
  danger: {
    backgroundColor: concepts$1.colors.danger.base,
    fontColor: 'primaryBody'
  },
  shadow: {
    backgroundColor: concepts$1.colors.foreground,
    fontColor: 'primaryBody'
  },
  accent: {
    backgroundColor: concepts$1.colors.accent.base,
    fontColor: 'primaryBody'
  }
};
var colorOption$1 = {
  defaults: {
    circleFill: concepts$1.shadows.generate([0, 0, 6, 1], Color$1(concepts$1.colors.defaultShadowColor).rgb().alpha(0.1).toString()),
    innerRing: concepts$1.shadows.generate([0, 0, 0, 11], Color$1([20, 30, 50]).rgb().alpha(0.03).toString()),
    outerRing: concepts$1.shadows.generate([0, 0, 6, 1], Color$1(concepts$1.colors.defaultShadowColor).rgb().alpha(0.06).toString())
  }
};
var entities$1 = {
  mediaQueries: mediaQueries$5,
  hocs: hocs$1,
  text: text$1,
  spacing: spacing$1,
  buttons: buttons$1,
  inputs: inputs$1,
  icons: icons$1,
  card: card$1,
  separator: separator$1,
  loadingSpinner: loadingSpinner$1,
  animatedCheckmark: animatedCheckmark$1,
  avatar: avatar$1,
  dialog: dialog$1,
  drawer: drawer$1,
  sheet: sheet$1,
  slider: slider$1,
  navigation: navigation$1,
  toggle: toggle$1,
  tooltip: tooltip$1,
  colorPicker: colorPicker$1,
  sphere: sphere$1,
  widget: widget$1,
  colorOption: colorOption$1
};

var TEAL = '#00E0E0';
var DARK_BLUE = '#023473';
var LIGHT_BLUE = '#00AEEF';
var ACCENT_BLUE = '#02C3E2';
var BASE_GRAY = '#6A7177';
var GREEN = '#0A9392';
var GOLD = '#DFB614';
var SALMON = '#F58795';
var PURPLE = '#703A96';
var BASE = '#FBF9F4';
var WHITE = '#FFF';
var tealSwatch = computeColorSwatch(TEAL);
var darkBlueSwatch = computeColorSwatch(DARK_BLUE);
var graySwatch = computeColorSwatch(BASE_GRAY);
var lightBlueSwatch = computeColorSwatch(LIGHT_BLUE);
var accentBlueSwatch = computeColorSwatch(ACCENT_BLUE);
var greenSwatch = computeColorSwatch(GREEN);
var yellowSwatch = computeColorSwatch(GOLD);
var redSwatch = computeColorSwatch(SALMON);
var purpleSwatch = computeColorSwatch(PURPLE);
var colors$1 = {
  tealSwatch: tealSwatch,
  darkBlueSwatch: darkBlueSwatch,
  lightBlueSwatch: lightBlueSwatch,
  graySwatch: graySwatch,
  accentBlueSwatch: accentBlueSwatch,
  greenSwatch: greenSwatch,
  yellowSwatch: yellowSwatch,
  redSwatch: redSwatch,
  purpleSwatch: purpleSwatch,
  WHITE: WHITE,
  BASE: BASE
};

var BASE_VALUE = 1;
var BASE_UNITS = 'rem';
var computeFontSize = function (multiplier) {
  return BASE_VALUE * multiplier;
};
var XSMALL_FONT = computeFontSize(0.75) + BASE_UNITS;
var SMALL_FONT = computeFontSize(0.875) + BASE_UNITS;
var MEDIUM_FONT = computeFontSize(1) + BASE_UNITS;
var MEDIUM_PLUS_FONT = computeFontSize(1.25) + BASE_UNITS;
var LARGE_FONT = computeFontSize(1.75) + BASE_UNITS;
var XLARGE_FONT = computeFontSize(2.125) + BASE_UNITS;
var XXLARGE_FONT = computeFontSize(3) + BASE_UNITS;
var XSMALL_LINE_HEIGHT = computeFontSize(1.5) + BASE_UNITS;
var SMALL_LINE_HEIGHT = computeFontSize(1.75) + BASE_UNITS;
var MEDIUM_LINE_HEIGHT = computeFontSize(2) + BASE_UNITS;
var LARGE_LINE_HEIGHT = computeFontSize(2.5) + BASE_UNITS;
var XLARGE_LINE_HEIGHT = computeFontSize(3) + BASE_UNITS;
var XXLARGE_LINE_HEIGHT = computeFontSize(3.5) + BASE_UNITS;
var HEADING_FONT_FAMILY = "'Poppins', 'Open Sans',  sans-serif";
var BODY_FONT_FAMILY = "'Poppins', 'Open Sans', sans-serif";
var BODY_FONT_IMPORT = 'https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&display=swap';
var HEADING_FONT_IMPORT = 'https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&display=swap';
var WEIGHT_THIN = 300;
var WEIGHT_REGULAR = 400;
var WEIGHT_SEMI_BOLD = 600;
var WEIGHT_BOLD = 700;
var WEIGHT_EXTRA_BOLD = 800;

var colors = {
  primaryBrand: colors$1.tealSwatch,
  secondaryBrand: colors$1.darkBlueSwatch,
  accent: colors$1.accentBlueSwatch,
  success: colors$1.greenSwatch,
  warning: colors$1.yellowSwatch,
  danger: colors$1.redSwatch,
  background: colors$1.BASE,
  foreground: colors$1.WHITE,
  grayScale: colors$1.graySwatch,
  blueScale: colors$1.lightBlueSwatch,
  purpleScale: colors$1.purpleSwatch,
  white: colors$1.WHITE,
  defaultShadowColor: SHADOW_DEFAULT_COLOR
};
var concepts = {
  colors: __assign({}, colors),
  radius: {
    none: radius.none,
    small: radius.small,
    medium: radius.mediumLarge,
    default: radius.medium,
    large: radius.large,
    circle: radius.circle
  },
  shadows: {
    light: computeShadow('light', Color$1(colors.defaultShadowColor).rgb().alpha(0.08).toString()),
    mediumLight: computeShadow('mediumLight', Color$1(colors.defaultShadowColor).rgb().alpha(0.08).toString()),
    mediumDark: computeShadow('mediumDark', Color$1(colors.defaultShadowColor).rgb().alpha(0.1).toString()),
    dark: computeShadow('dark', Color$1(colors.defaultShadowColor).rgb().alpha(0.12).toString()),
    generate: computeShadow
  },
  typography: {
    imports: {
      body: BODY_FONT_IMPORT,
      heading: HEADING_FONT_IMPORT
    },
    colors: {
      primaryHeading: colors.grayScale._200,
      primaryBody: colors.grayScale.base,
      inverseHeading: colors.white,
      inverseBody: colors.white
    },
    families: {
      heading: BODY_FONT_FAMILY,
      body: HEADING_FONT_FAMILY
    },
    sizes: {
      xSmall: XSMALL_FONT,
      small: SMALL_FONT,
      medium: MEDIUM_FONT,
      mediumPlus: MEDIUM_PLUS_FONT,
      large: LARGE_FONT,
      xLarge: XLARGE_FONT,
      xxLarge: XXLARGE_FONT
    },
    weights: {
      thin: WEIGHT_THIN,
      regular: WEIGHT_REGULAR,
      semiBold: WEIGHT_SEMI_BOLD,
      bold: WEIGHT_BOLD,
      extraBold: WEIGHT_EXTRA_BOLD
    },
    lineHeight: {
      xSmall: XSMALL_LINE_HEIGHT,
      small: SMALL_LINE_HEIGHT,
      medium: MEDIUM_LINE_HEIGHT,
      large: LARGE_LINE_HEIGHT,
      xLarge: XLARGE_LINE_HEIGHT,
      xxLarge: XXLARGE_LINE_HEIGHT
    }
  },
  borders: {
    thin: borders$9.xSmall,
    small: borders$9.small,
    default: borders$9.medium,
    large: borders$9.large,
    bold: borders$9.xLarge
  },
  spacing: {
    baseValue: BASE_VALUE$9,
    baseUnits: BASE_UNITS$9
  },
  mediaQueries: {
    small: mediaQuerySizes.small,
    medium: mediaQuerySizes.medium,
    large: mediaQuerySizes.large,
    xLarge: mediaQuerySizes.xLarge
  },
  sizing: {
    icons: {
      xSmall: icons$9.xSmall,
      small: icons$9.small,
      medium: icons$9.medium,
      large: icons$9.large,
      xLarge: icons$9.xLarge
    },
    sphere: {
      xSmall: sphere$9.xSmall,
      small: sphere$9.small,
      medium: sphere$9.medium,
      large: sphere$9.large,
      xLarge: sphere$9.xLarge
    }
  }
};

var space = computeSpace(concepts.spacing.baseValue, concepts.spacing.baseUnits);
var spacing = {
  x0: space(0),
  x_25: space(0.25),
  x_5: space(0.5),
  x_75: space(0.75),
  x1: space(1),
  x1_5: space(1.5),
  x2: space(2),
  x2_5: space(2.5),
  x3: space(3),
  x4: space(4),
  x5: space(5),
  x6: space(6),
  x7: space(7),
  x8: space(8),
  x9: space(9)
};
var borders = {
  thin: concepts.borders.thin + "px",
  default: concepts.borders.default + "px",
  bold: concepts.borders.bold + "px"
};
var text = {
  imports: {
    body: concepts.typography.imports.body,
    heading: concepts.typography.imports.heading
  },
  families: {
    body: concepts.typography.families.body,
    heading: concepts.typography.families.heading
  },
  types: {
    heroTitle: {
      color: concepts.typography.colors.primaryHeading,
      size: concepts.typography.sizes.xxLarge,
      fontWeight: concepts.typography.weights.extraBold,
      lineHeight: concepts.typography.lineHeight.xxLarge,
      fontFamily: concepts.typography.families.heading
    },
    title: {
      color: concepts.typography.colors.primaryHeading,
      size: concepts.typography.sizes.xLarge,
      fontWeight: concepts.typography.weights.bold,
      lineHeight: concepts.typography.lineHeight.xLarge,
      fontFamily: concepts.typography.families.heading
    },
    subtitle: {
      color: concepts.typography.colors.primaryHeading,
      size: concepts.typography.sizes.large,
      fontWeight: concepts.typography.weights.regular,
      lineHeight: concepts.typography.lineHeight.medium,
      fontFamily: concepts.typography.families.heading
    },
    largeBody: {
      color: concepts.typography.colors.primaryBody,
      size: concepts.typography.sizes.mediumPlus,
      fontWeight: concepts.typography.weights.regular,
      lineHeight: concepts.typography.lineHeight.small,
      fontFamily: concepts.typography.families.body
    },
    body: {
      color: concepts.typography.colors.primaryBody,
      size: concepts.typography.sizes.medium,
      fontWeight: concepts.typography.weights.regular,
      lineHeight: concepts.typography.lineHeight.small,
      fontFamily: concepts.typography.families.body
    },
    caption: {
      color: concepts.typography.colors.primaryBody,
      size: concepts.typography.sizes.small,
      fontWeight: concepts.typography.weights.regular,
      lineHeight: concepts.typography.lineHeight.small,
      fontFamily: concepts.typography.families.body
    },
    footnote: {
      color: concepts.typography.colors.primaryBody,
      size: concepts.typography.sizes.xSmall,
      fontWeight: concepts.typography.weights.regular,
      lineHeight: concepts.typography.lineHeight.xSmall,
      fontFamily: concepts.typography.families.body
    }
  },
  colors: {
    primaryBrand: concepts.colors.primaryBrand.base,
    secondaryBrand: concepts.colors.secondaryBrand.base,
    primaryHeading: concepts.typography.colors.primaryHeading,
    primaryBody: concepts.typography.colors.primaryBody,
    inverseHeading: concepts.typography.colors.inverseHeading,
    inverseBody: concepts.typography.colors.inverseBody,
    accent: concepts.colors.accent.base,
    success: concepts.colors.success.base,
    warning: concepts.colors.warning.base,
    danger: concepts.colors.danger.base,
    anchorBlue: concepts.colors.blueScale.base
  },
  weights: {
    thin: concepts.typography.weights.thin,
    regular: concepts.typography.weights.regular,
    semiBold: concepts.typography.weights.semiBold,
    bold: concepts.typography.weights.bold,
    extraBold: concepts.typography.weights.extraBold
  }
};
var mediaQueries$4 = {
  queries: {
    xSmall: "@media screen and (min-width: 0px)",
    // Large Mobile
    small: "@media screen and (min-width: " + concepts.mediaQueries.small + "px)",
    // Tablet
    medium: "@media screen and (min-width: " + concepts.mediaQueries.medium + "px)",
    // Large Tablet
    large: "@media screen and (min-width: " + concepts.mediaQueries.large + "px)",
    // Desktop
    xLarge: "@media screen and (min-width: " + concepts.mediaQueries.xLarge + "px)"
  },
  rawValues: {
    xSmall: 0,
    small: concepts.mediaQueries.small,
    medium: concepts.mediaQueries.medium,
    large: concepts.mediaQueries.large,
    xLarge: concepts.mediaQueries.xLarge
  }
};
var hocs = {
  colors: {
    swatches: {
      primaryBrand: concepts.colors.primaryBrand,
      secondaryBrand: concepts.colors.secondaryBrand,
      success: concepts.colors.success,
      danger: concepts.colors.danger,
      warning: concepts.colors.warning,
      accent: concepts.colors.accent,
      grayScale: concepts.colors.grayScale,
      blueScale: concepts.colors.blueScale,
      purpleScale: concepts.colors.purpleScale
    },
    background: concepts.colors.background,
    foreground: concepts.colors.foreground
  },
  textColors: text.colors,
  spacing: spacing,
  borderRadius: concepts.radius,
  shadows: concepts.shadows,
  borders: borders
};
// TODO - revisit buttons for contrast and font color
var buttons = {
  boxShadow: concepts.shadows.generate,
  borderSize: borders.default,
  outlinedBackgroundColor: concepts.colors.foreground,
  fontWeight: concepts.typography.weights.semiBold,
  sizes: {
    xSmall: {
      inset: spacing.x1 + " " + spacing.x2,
      outlinedInset: {
        vertical: "calc(" + spacing.x1 + " - 2px)",
        horizontal: "calc(" + spacing.x2 + " - 2px)"
      },
      fontSize: text.types.footnote.size,
      borderRadius: concepts.radius.default
    },
    small: {
      inset: spacing.x1_5 + " " + spacing.x2_5,
      outlinedInset: {
        vertical: "calc(" + spacing.x1_5 + " - 2px)",
        horizontal: "calc(" + spacing.x2_5 + " - 2px)"
      },
      fontSize: text.types.caption.size,
      borderRadius: concepts.radius.default
    },
    medium: {
      inset: spacing.x1_5 + " " + spacing.x3,
      outlinedInset: {
        vertical: "calc(" + spacing.x1_5 + " - 2px)",
        horizontal: "calc(" + spacing.x3 + " - 2px)"
      },
      fontSize: text.types.body.size,
      borderRadius: concepts.radius.default
    },
    large: {
      inset: spacing.x2 + " " + spacing.x4,
      outlinedInset: {
        vertical: "calc(" + spacing.x2 + " - 2px)",
        horizontal: "calc(" + spacing.x4 + " - 2px)"
      },
      fontSize: text.types.largeBody.size,
      borderRadius: concepts.radius.default
    },
    xLarge: {
      inset: spacing.x3 + " " + spacing.x5,
      outlinedInset: {
        vertical: "calc(" + spacing.x3 + " - 2px)",
        horizontal: "calc(" + spacing.x5 + " - 2px)"
      },
      fontSize: text.types.subtitle.size,
      borderRadius: concepts.radius.default
    }
  },
  primary: {
    backgroundColor: concepts.colors.primaryBrand.base,
    fontColor: concepts.typography.colors.inverseBody,
    fontFamily: concepts.typography.families.body
  },
  secondary: {
    backgroundColor: concepts.colors.secondaryBrand.base,
    fontColor: concepts.typography.colors.inverseBody,
    fontFamily: concepts.typography.families.body
  },
  warning: {
    backgroundColor: concepts.colors.warning.base,
    fontColor: concepts.typography.colors.inverseBody,
    fontFamily: concepts.typography.families.body
  },
  success: {
    backgroundColor: concepts.colors.success.base,
    fontColor: concepts.typography.colors.inverseBody,
    fontFamily: concepts.typography.families.body
  },
  danger: {
    backgroundColor: concepts.colors.danger.base,
    fontColor: concepts.typography.colors.inverseBody,
    fontFamily: concepts.typography.families.body
  },
  shadow: {
    backgroundColor: concepts.colors.foreground,
    fontColor: concepts.typography.colors.primaryBody,
    fontFamily: concepts.typography.families.body
  },
  accent: {
    backgroundColor: concepts.colors.accent.base,
    fontColor: concepts.typography.colors.primaryBody,
    fontFamily: concepts.typography.families.body
  },
  smoke: {
    backgroundColor: concepts.colors.background,
    fontColor: concepts.typography.colors.primaryBody,
    fontFamily: concepts.typography.families.body
  }
};
var slider = {
  backgroundColor: concepts.colors.background,
  borderRadius: concepts.radius.small,
  handleRadius: concepts.radius.circle,
  handleColor: concepts.colors.foreground,
  handleShadow: concepts.shadows.light
};
var inputs = {
  checkbox: {
    border: borders.default,
    borderRadius: concepts.radius.small,
    unCheckedBackgroundColor: concepts.colors.grayScale._800,
    checkmarkColor: concepts.typography.colors.inverseBody,
    labelColor: concepts.typography.colors.primaryBody
  },
  text: {
    borderRadius: concepts.radius.default,
    labelColor: concepts.colors.accent,
    placeholderColor: concepts.typography.colors.primaryBody,
    labelFontSize: text.types.caption.size,
    textFontSize: text.types.caption.size,
    borderWidth: borders.default,
    defaultFocusColor: concepts.colors.blueScale.base
  },
  select: {
    dropDownShadow: concepts.shadows.mediumDark,
    dropDownBackgroundColor: concepts.colors.foreground,
    boxShadow: concepts.shadows.light,
    borderRadius: concepts.radius.default
  },
  number: {
    backgroundColor: concepts.colors.grayScale._200,
    fontColor: concepts.typography.colors.inverseBody,
    fontSize: text.types.body.size,
    borderRadius: concepts.radius.default
  },
  backgroundColor: concepts.colors.background,
  fontSize: text.types.caption.size,
  fontColor: concepts.typography.colors.primaryBody,
  fontFamily: concepts.typography.families.body,
  colors: {
    primary: concepts.colors.primaryBrand.base,
    secondary: concepts.colors.secondaryBrand.base,
    success: concepts.colors.success.base,
    warning: concepts.colors.warning.base,
    danger: concepts.colors.danger.base,
    accent: concepts.colors.accent.base,
    dark: concepts.colors.primaryBrand._100,
    blue: concepts.colors.blueScale.base
  }
};
var icons = {
  sizes: {
    xSmall: concepts.sizing.icons.xSmall,
    small: concepts.sizing.icons.small,
    medium: concepts.sizing.icons.medium,
    large: concepts.sizing.icons.large,
    xLarge: concepts.sizing.icons.xLarge
  },
  colors: {
    default: concepts.colors.grayScale._800,
    primaryBodyText: concepts.typography.colors.primaryBody,
    primaryHeadingText: concepts.typography.colors.primaryHeading,
    inverseBodyText: concepts.typography.colors.inverseBody,
    inverseHeadingText: concepts.typography.colors.inverseHeading,
    primaryBrand: concepts.colors.primaryBrand.base,
    secondaryBrand: concepts.colors.secondaryBrand.base,
    warning: concepts.colors.warning.base,
    danger: concepts.colors.danger.base,
    success: concepts.colors.success.base,
    background: concepts.colors.background,
    accent: concepts.colors.background
  }
};
var animatedCheckmark = {
  sizeMap: {
    xSmall: concepts.sizing.icons.xSmall,
    small: concepts.sizing.icons.small,
    medium: concepts.sizing.icons.medium,
    large: concepts.sizing.icons.large,
    xLarge: concepts.sizing.icons.xLarge
  },
  colors: {
    primary: concepts.colors.primaryBrand.base,
    secondary: concepts.colors.secondaryBrand.base,
    success: concepts.colors.success.base,
    accent: concepts.colors.accent.base
  },
  strokeWidth: {
    small: concepts.borders.small,
    default: concepts.borders.default,
    large: concepts.borders.large
  },
  borderRadius: concepts.radius.circle,
  checkmarkFilledColor: concepts.colors.foreground
};
var avatar = {
  borderWidth: borders.thin,
  borderColor: concepts.colors.grayScale._800,
  borderRadius: concepts.radius.circle,
  boxShadow: concepts.shadows.mediumLight,
  backgroundColor: concepts.colors.foreground,
  bubbleBackgroundColors: {
    isActive: concepts.colors.success.base,
    default: concepts.colors.grayScale._800
  },
  defaultBackgroundColors: {
    primary: concepts.colors.primaryBrand.base,
    secondary: concepts.colors.secondaryBrand.base,
    success: concepts.colors.success.base,
    accent: concepts.colors.accent.base,
    warning: concepts.colors.warning.base,
    danger: concepts.colors.danger.base
  },
  sizes: {
    xSmall: {
      avatarSize: 34,
      bubbleLeftPosition: 22,
      bubbleSize: 12
    },
    small: {
      avatarSize: 44,
      bubbleLeftPosition: 28,
      bubbleSize: 14
    },
    medium: {
      avatarSize: 60,
      bubbleLeftPosition: 40,
      bubbleSize: 16
    },
    large: {
      avatarSize: 76,
      bubbleLeftPosition: 52,
      bubbleSize: 20
    },
    xLarge: {
      avatarSize: 96,
      bubbleLeftPosition: 64,
      bubbleSize: 28
    }
  }
};
var loadingSpinner = {
  size: {
    xSmall: concepts.sizing.icons.xSmall,
    small: concepts.sizing.icons.small,
    medium: concepts.sizing.icons.medium,
    large: concepts.sizing.icons.large,
    xLarge: concepts.sizing.icons.xLarge
  },
  colors: {
    primary: concepts.colors.primaryBrand.base,
    secondary: concepts.colors.secondaryBrand.base,
    success: concepts.colors.success.base,
    warning: concepts.colors.warning.base,
    danger: concepts.colors.danger.base
  }
};
var separator = {
  border: borders.default,
  color: concepts.colors.grayScale._800
};
var card = {
  backgroundColor: concepts.colors.warning.base,
  borderRadius: concepts.radius.default,
  boxShadow: concepts.shadows.mediumDark,
  defaultInset: spacing.x4 + " " + spacing.x2 + " " + spacing.x2 + " " + spacing.x2
};
var dialog = {
  backgroundColor: concepts.colors.foreground,
  borderRadius: concepts.radius.default,
  boxShadow: concepts.shadows.mediumDark,
  defaultInset: spacing.x6 + " " + spacing.x4 + " " + spacing.x4 + " " + spacing.x4
};
var navigation = {
  backgroundColor: concepts.colors.foreground,
  border: borders.thin,
  borderColor: concepts.colors.grayScale._800
};
var drawer = {
  backgroundColor: concepts.colors.foreground,
  borderRadius: concepts.radius.small,
  boxShadow: concepts.shadows.mediumLight,
  bottomDirectionShadow: concepts.shadows.generate('mediumLight', Color$1(concepts.colors.defaultShadowColor).rgb().alpha(0.1).toString(), true),
  hoverBoxShadow: concepts.shadows.mediumDark,
  hoverBottomDirectionShadow: concepts.shadows.generate('mediumDark', Color$1(concepts.colors.defaultShadowColor).rgb().alpha(0.1).toString(), true),
  defaultInset: "" + spacing.x1,
  tab: {
    dark: {
      backgroundColor: concepts.colors.grayScale._600,
      arrowColor: concepts.colors.foreground
    },
    light: {
      backgroundColor: concepts.colors.foreground,
      arrowColor: icons.colors.default
    }
  }
};
var sheet = {
  mobileBorderRadius: concepts.radius.large,
  desktopBorderRadius: concepts.radius.default,
  backgroundColor: concepts.colors.foreground,
  boxShadow: concepts.shadows.mediumDark
};
var toggle = {
  isActiveColor: concepts.colors.success.base,
  isNotActiveColor: concepts.colors.grayScale._900
};
var tooltip = {
  lightBackgroundColor: concepts.colors.foreground,
  darkBackgroundColor: concepts.colors.grayScale._100,
  boxShadow: concepts.shadows.mediumLight,
  borderColor: concepts.colors.background
};
var colorPicker = {
  backgroundColor: concepts.colors.background,
  borderColor: 'transparent',
  boxShadow: concepts.shadows.mediumLight,
  borderRadius: concepts.radius.small
};
var sphere = {
  xSmall: {
    circumference: concepts.sizing.sphere.xSmall,
    textType: 'title'
  },
  small: {
    circumference: concepts.sizing.sphere.small,
    textType: 'title'
  },
  medium: {
    circumference: concepts.sizing.sphere.medium,
    textType: 'heroTitle'
  },
  large: {
    circumference: concepts.sizing.sphere.large,
    textType: 'heroTitle'
  },
  xLarge: {
    circumference: concepts.sizing.sphere.xLarge,
    textType: 'heroTitle'
  }
};
var widget = {
  primary: {
    backgroundColor: concepts.colors.primaryBrand.base,
    fontColor: 'inverseBody'
  },
  secondary: {
    backgroundColor: concepts.colors.secondaryBrand.base,
    fontColor: 'inverseBody'
  },
  warning: {
    backgroundColor: concepts.colors.warning.base,
    fontColor: 'primaryBody'
  },
  success: {
    backgroundColor: concepts.colors.success.base,
    fontColor: 'primaryBody'
  },
  danger: {
    backgroundColor: concepts.colors.danger.base,
    fontColor: 'inverseBody'
  },
  shadow: {
    backgroundColor: concepts.colors.foreground,
    fontColor: 'primaryBody'
  },
  accent: {
    backgroundColor: concepts.colors.accent.base,
    fontColor: 'inverseBody'
  }
};
var colorOption = {
  defaults: {
    circleFill: concepts.shadows.generate([0, 0, 6, 1], Color$1(concepts.colors.defaultShadowColor).rgb().alpha(0.1).toString()),
    innerRing: concepts.shadows.generate([0, 0, 0, 11], Color$1([20, 30, 50]).rgb().alpha(0.03).toString()),
    outerRing: concepts.shadows.generate([0, 0, 6, 1], Color$1(concepts.colors.defaultShadowColor).rgb().alpha(0.06).toString())
  }
};
var entities = {
  mediaQueries: mediaQueries$4,
  hocs: hocs,
  text: text,
  spacing: spacing,
  buttons: buttons,
  inputs: inputs,
  icons: icons,
  card: card,
  separator: separator,
  loadingSpinner: loadingSpinner,
  animatedCheckmark: animatedCheckmark,
  avatar: avatar,
  dialog: dialog,
  sheet: sheet,
  navigation: navigation,
  toggle: toggle,
  tooltip: tooltip,
  drawer: drawer,
  slider: slider,
  colorPicker: colorPicker,
  sphere: sphere,
  widget: widget,
  colorOption: colorOption
};

var _emotionSourceMap2$x = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap$Y = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var QuantumThemeWrapper = function (_a) {
  var children = _a.children,
    theme = _a.theme;
  var getTheme = function (theme) {
    switch (theme) {
      case 'sendogo':
        return entities$8;
      case 'bellaCadeaux':
        return entities$6;
      case 'greenerStill':
        return entities$7;
      case 'promptingU':
        return entities$4;
      case 'streamMarketing':
        return entities$3;
      case 'streamVA':
        return entities$2;
      case 'blue42':
        return entities$5;
      case 'sendOutCards':
        return entities$1;
      case 'promptings':
        return entities;
      case 'promptingsAcademy':
        return entities$4;
      default:
        return entities$9;
    }
  };
  var selectedTheme = getTheme(theme);
  return jsx(ThemeProvider, {
    theme: selectedTheme
  }, jsx(Global, {
    styles: /*#__PURE__*/css(templateObject_1$Y || (templateObject_1$Y = __makeTemplateObject(["\n          @import url(", ");\n          @import url(", ");\n          body {\n            font-family: ", ";\n            font-size: 16px;\n            line-height: 1.65;\n            * {\n              box-sizing: border-box;\n            }\n            h1,\n            h2,\n            h3,\n            h4,\n            h5,\n            h6 {\n              font-family: ", ";\n            }\n            button {\n              font-family: ", ";\n            }\n          }\n        ;label:QuantumThemeWrapper;" + _emotionSourceMap$Y], ["\n          @import url(", ");\n          @import url(", ");\n          body {\n            font-family: ", ";\n            font-size: 16px;\n            line-height: 1.65;\n            * {\n              box-sizing: border-box;\n            }\n            h1,\n            h2,\n            h3,\n            h4,\n            h5,\n            h6 {\n              font-family: ", ";\n            }\n            button {\n              font-family: ", ";\n            }\n          }\n        ;label:QuantumThemeWrapper;" + _emotionSourceMap$Y])), selectedTheme.text.imports.body, selectedTheme.text.imports.heading, selectedTheme.text.families.body, selectedTheme.text.families.heading, selectedTheme.text.families.body)
  }), jsx("div", {
    css: /*#__PURE__*/css(templateObject_2$x || (templateObject_2$x = __makeTemplateObject(["\n          font-size: 16px;\n          > div {\n            font-size: inherit;\n          }\n        ;label:QuantumThemeWrapper;" + _emotionSourceMap2$x], ["\n          font-size: 16px;\n          > div {\n            font-size: inherit;\n          }\n        ;label:QuantumThemeWrapper;" + _emotionSourceMap2$x])))
  }, children));
};
var templateObject_1$Y, templateObject_2$x;

var getIconColor = function (color, entity) {
  var _a;
  return (_a = entity.colors[color]) !== null && _a !== void 0 ? _a : color;
};
var getIconSize = function (size, entity) {
  if (typeof size === 'number') return size;
  return entity.sizes[size];
};

var Gift = function (_a) {
  var primaryColor = _a.primaryColor,
    size = _a.size,
    onClick = _a.onClick,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: " 0 0 24 24"
  }, jsx("rect", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    fill: "none",
    rx: "0",
    ry: "0"
  }), jsx("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M3.6 9.0501H11.3999V12.05H4.1L4.09226 12.0501H3.6C3.38 12.0501 3.2 11.8701 3.2 11.6501V9.4501C3.2 9.2301 3.38 9.0501 3.6 9.0501ZM4.7 13.2501H11.3999V20.85H5.1C4.88 20.85 4.7 20.67 4.7 20.45V13.2501ZM12.5999 13.2501V20.85H18.9C19.12 20.85 19.3 20.67 19.3 20.45V13.2501H12.5999ZM18.9 22.05H12.0076L11.9999 22.0501L11.9922 22.05H5.1C4.22 22.05 3.5 21.33 3.5 20.45V13.247C2.6661 13.1949 2 12.4965 2 11.6501V9.4501C2 8.5701 2.72 7.8501 3.6 7.8501H6.79366C6.13032 7.50959 5.57545 7.10571 5.08996 6.62021C4.57996 6.12021 4.27996 5.61021 4.15996 5.08021C3.99996 4.38021 4.17996 3.61021 4.62996 3.02021C5.04996 2.48021 5.68996 2.10021 6.39996 1.99021C7.00996 1.89021 7.60996 1.99021 8.08996 2.27021C8.46996 2.49021 8.79996 2.83021 9.08996 3.30021C9.33169 3.7031 9.50128 4.13004 9.66332 4.53797L9.71996 4.68021C9.7422 4.73583 9.76411 4.79144 9.78598 4.847L9.78599 4.84701C9.88212 5.09111 9.97777 5.33398 10.1 5.57021C10.4517 6.27369 10.9433 6.77109 11.3284 7.11924C11.4521 6.87229 11.7083 6.7002 12 6.7002C12.2907 6.7002 12.5462 6.87112 12.6703 7.11673C13.0556 6.76219 13.5479 6.26447 13.9 5.56021C14.0196 5.3125 14.1246 5.05749 14.2275 4.80765L14.28 4.68021C14.46 4.22021 14.65 3.75021 14.92 3.30021C15.21 2.83021 15.53 2.49021 15.92 2.27021C16.4 1.99021 17 1.89021 17.6 1.99021C18.31 2.11021 18.95 2.48021 19.37 3.03021C19.82 3.62021 20 4.38021 19.84 5.08021C19.72 5.61021 19.42 6.12021 18.91 6.62021C18.4224 7.10781 17.8608 7.50897 17.1933 7.8501H20.4C21.28 7.8501 22 8.5701 22 9.4501V11.6501C22 12.4965 21.3339 13.1949 20.5 13.247V20.45C20.5 21.33 19.78 22.05 18.9 22.05ZM12.5999 9.0501V12.05H19.9L19.9077 12.0501H20.4C20.62 12.0501 20.8 11.8701 20.8 11.6501V9.4501C20.8 9.2301 20.62 9.0501 20.4 9.0501H12.5999ZM6.84996 3.15021C6.76996 3.15021 6.67996 3.16021 6.58996 3.17021C6.17996 3.24021 5.80996 3.45021 5.57996 3.76021C5.34996 4.06021 5.24996 4.47021 5.32996 4.82021C5.39996 5.12021 5.58996 5.43021 5.93996 5.77021C6.56996 6.40021 7.35996 6.86021 8.42996 7.24021C8.99996 7.44021 9.58996 7.59021 10.19 7.69021C9.80996 7.30021 9.36996 6.79021 9.02996 6.10021C8.88371 5.81686 8.76253 5.50845 8.6435 5.20552L8.6435 5.20551L8.6435 5.20551L8.6435 5.2055L8.60996 5.12021C8.43996 4.69021 8.27996 4.29021 8.05996 3.92021C7.87996 3.63021 7.68996 3.42021 7.48996 3.30021C7.30996 3.20021 7.08996 3.15021 6.84996 3.15021ZM16.52 3.31021C16.69 3.20021 16.91 3.15021 17.15 3.15021C17.23 3.15021 17.32 3.16021 17.41 3.17021C17.82 3.24021 18.19 3.46021 18.42 3.76021C18.65 4.07021 18.75 4.47021 18.67 4.82021C18.6 5.13021 18.4 5.44021 18.06 5.78021C17.43 6.41021 16.64 6.88021 15.57 7.25021C14.99 7.45021 14.4 7.60021 13.8 7.70021C14.19 7.31021 14.63 6.79021 14.97 6.11021C15.13 5.79021 15.26 5.46021 15.39 5.13021L15.4031 5.09721C15.5683 4.67924 15.7257 4.28099 15.94 3.93021C16.12 3.64021 16.32 3.43021 16.52 3.31021Z",
    fill: getIconColor(primaryColor, entity)
  }));
};
// gift, hamburger, price tag, user

var Catalog = function (_a) {
  var primaryColor = _a.primaryColor,
    size = _a.size,
    onClick = _a.onClick,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: " 0 0 24 24"
  }, jsx("rect", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    fill: "none",
    rx: "0",
    ry: "0"
  }), jsx("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M9 2C7.89543 2 7 2.89543 7 4V5H6C4.89543 5 4 5.89543 4 7V20C4 21.1046 4.89543 22 6 22H15C16.1046 22 17 21.1046 17 20V19H18C19.1046 19 20 18.1046 20 17V4C20 2.89543 19.1046 2 18 2H9ZM17 7V17.8H17.8C18.3523 17.8 18.8 17.3523 18.8 16.8V4.2C18.8 3.64772 18.3523 3.2 17.8 3.2H9.2C8.64772 3.2 8.2 3.64771 8.2 4.2V5H15C16.1046 5 17 5.89543 17 7ZM6.2 6.2C5.64772 6.2 5.2 6.64771 5.2 7.2V19.8C5.2 20.3523 5.64771 20.8 6.2 20.8H14.8C15.3523 20.8 15.8 20.3523 15.8 19.8V7.2C15.8 6.64772 15.3523 6.2 14.8 6.2H6.2Z",
    fill: getIconColor(primaryColor, entity)
  }));
};

var Award = function (_a) {
  var primaryColor = _a.primaryColor,
    secondaryColor = _a.secondaryColor,
    onClick = _a.onClick,
    entity = _a.entity,
    size = _a.size;
  return jsx("svg", {
    viewBox: " 0 0 24 24",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    onClick: onClick && function () {
      return onClick();
    }
  }, jsx("rect", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    fill: "none",
    rx: "0",
    ry: "0"
  }), jsx("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M16.5737 13.8678L18.455 19.1499L15.935 17.6299C15.795 17.5499 15.625 17.5299 15.465 17.5699C15.305 17.6199 15.175 17.7299 15.095 17.8799L13.975 20.0999L12.7558 14.8696C12.6655 14.8365 12.5681 14.82 12.4652 14.82C12.2352 14.82 12.0152 14.92 11.8652 15.09C11.6382 15.3405 11.3637 15.5235 11.0656 15.6338L10.0251 20.0899L8.90513 17.8699C8.83513 17.7299 8.71513 17.6299 8.58513 17.5799C8.58513 17.5699 8.57513 17.5699 8.56513 17.5699C8.41513 17.5299 8.25513 17.5399 8.11513 17.6099C8.09513 17.6199 8.07513 17.6299 8.06513 17.6399L5.54513 19.1599L7.80508 12.8115C7.71074 12.7793 7.60805 12.7654 7.49517 12.77C7.17903 12.7883 6.8737 12.7337 6.59756 12.6176L3.84513 20.3299C3.76513 20.5699 3.83513 20.8399 4.03513 20.9999C4.23513 21.1599 4.50513 21.1799 4.72513 21.0499L8.13513 18.9899L9.71513 22.0999C9.82513 22.2999 10.0351 22.4299 10.2551 22.4299C10.2651 22.4299 10.2776 22.4274 10.2901 22.4249L10.2901 22.4249L10.2902 22.4249C10.3026 22.4224 10.3151 22.4199 10.3251 22.4199C10.5751 22.3899 10.7751 22.2099 10.8351 21.9599L11.9976 16.9136L13.175 21.9799C13.225 22.2299 13.435 22.4099 13.685 22.4399H13.755C13.975 22.4399 14.185 22.3099 14.275 22.0899L15.855 18.9799L19.265 21.0399C19.485 21.1699 19.755 21.1499 19.955 20.9899C20.155 20.8299 20.225 20.5599 20.145 20.3199L17.4626 12.7814C17.4535 12.781 17.4443 12.7805 17.4352 12.78C17.2052 12.77 16.9752 12.85 16.8152 13.02C16.6452 13.19 16.5652 13.4 16.5752 13.64C16.5787 13.7168 16.5782 13.7928 16.5737 13.8678ZM16.2748 11.9309C16.3414 11.8857 16.4108 11.8444 16.4822 11.8074C16.4798 11.8082 16.4774 11.809 16.475 11.8099C16.3985 11.837 16.3312 11.8788 16.2748 11.9309Z",
    fill: secondaryColor ? getIconColor(secondaryColor, entity) : getIconColor(primaryColor, entity)
  }), jsx("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M13.0752 15.0899C13.4752 15.5299 14.0052 15.7599 14.5652 15.7599C14.8252 15.7599 15.0952 15.7099 15.3352 15.6099C16.1352 15.2799 16.6152 14.5099 16.5752 13.6399C16.5652 13.3999 16.6452 13.1899 16.8152 13.0199C16.9752 12.8499 17.2052 12.7699 17.4352 12.7799C18.2952 12.8299 19.0752 12.3399 19.4052 11.5399C19.7352 10.7399 19.5352 9.84993 18.8952 9.26993C18.7252 9.10993 18.6252 8.88993 18.6252 8.65993C18.6252 8.42993 18.7252 8.20993 18.9052 8.04993C19.5452 7.46993 19.7452 6.57993 19.4152 5.77993C19.0852 4.97993 18.3052 4.49993 17.4452 4.53993C17.2152 4.54993 16.9952 4.46993 16.8252 4.29993C16.6652 4.13993 16.5752 3.91993 16.5852 3.67993C16.6352 2.80993 16.1452 2.03993 15.3452 1.70993C14.5652 1.38993 13.7052 1.56993 13.0752 2.22993C12.9152 2.39993 12.6952 2.49993 12.4652 2.49993C12.2352 2.49993 12.0252 2.39993 11.8652 2.22993C11.2852 1.58993 10.3952 1.38993 9.59517 1.71993C8.79517 2.04993 8.30517 2.81993 8.35517 3.67993C8.36517 3.90993 8.28517 4.12993 8.11517 4.29993C7.95517 4.46993 7.73517 4.53993 7.49517 4.53993C6.58517 4.48993 5.85517 4.97993 5.52517 5.77993C5.19517 6.57993 5.39517 7.46993 5.97517 7.97993C5.99517 8.00992 6.01517 8.02992 6.03517 8.04992L6.03517 8.04993C6.20517 8.20993 6.30517 8.42993 6.30517 8.65993C6.30517 8.88993 6.21517 9.10993 6.03517 9.25993C5.39517 9.83993 5.19517 10.7299 5.52517 11.5299C5.85517 12.3299 6.63517 12.8199 7.49517 12.7699C7.74517 12.7599 7.94517 12.8399 8.11517 13.0099C8.28517 13.1699 8.36517 13.3899 8.35517 13.6299C8.30517 14.4999 8.79517 15.2699 9.59517 15.5999C10.3952 15.9299 11.2852 15.7299 11.8652 15.0899C12.0152 14.9199 12.2352 14.8199 12.4652 14.8199C12.7052 14.8199 12.9152 14.9099 13.0752 15.0899ZM8.96517 12.1699C8.58517 11.7899 8.07517 11.5799 7.53517 11.5799H7.42517C6.88517 11.5999 6.68517 11.2099 6.63517 11.0799C6.58517 10.9599 6.44517 10.5199 6.84517 10.1599C7.27517 9.76993 7.51517 9.22993 7.51517 8.65993C7.51517 8.08993 7.27517 7.53993 6.91517 7.22993L6.84517 7.15993C6.44517 6.79993 6.58517 6.36993 6.63517 6.23993C6.68517 6.11993 6.89517 5.70993 7.43517 5.73993C8.00517 5.77993 8.55517 5.55993 8.96517 5.14993C9.36517 4.74993 9.58517 4.18993 9.55517 3.61993C9.52517 3.08993 9.92517 2.87993 10.0552 2.81993C10.1852 2.76993 10.6152 2.62993 10.9752 3.02993C11.3652 3.45993 11.9052 3.69993 12.4752 3.69993C13.0352 3.69993 13.5852 3.45993 13.9652 3.02993C14.3252 2.62993 14.7652 2.76993 14.8852 2.81993C15.0052 2.86993 15.4152 3.07993 15.3852 3.61993C15.3552 4.18993 15.5752 4.74993 15.9752 5.14993C16.3852 5.55993 16.9252 5.77993 17.5252 5.73993C18.0252 5.69993 18.2652 6.10993 18.3152 6.23993C18.3652 6.35993 18.5052 6.79993 18.1052 7.15993C17.6752 7.53993 17.4352 8.08993 17.4352 8.65993C17.4352 9.22993 17.6852 9.77993 18.1052 10.1599C18.5052 10.5199 18.3652 10.9499 18.3152 11.0799C18.2652 11.1999 18.0652 11.6099 17.5152 11.5799C16.9552 11.5499 16.3852 11.7699 15.9852 12.1699C15.5852 12.5699 15.3652 13.1299 15.3952 13.6999C15.4252 14.2399 15.0152 14.4499 14.8952 14.4999C14.7652 14.5499 14.3352 14.6899 13.9752 14.2899C13.5852 13.8599 13.0452 13.6199 12.4752 13.6199C11.9052 13.6199 11.3552 13.8699 10.9752 14.2899C10.6152 14.6899 10.1852 14.5499 10.0552 14.4999C9.93517 14.4499 9.52517 14.2399 9.55517 13.6999C9.58517 13.1299 9.36517 12.5699 8.96517 12.1699ZM9.125 8.66006C9.125 10.5101 10.625 12.0101 12.475 12.0101C14.315 12.0101 15.825 10.5101 15.825 8.66006C15.825 6.81006 14.325 5.31006 12.475 5.31006C10.625 5.31006 9.125 6.81006 9.125 8.66006ZM10.325 8.66006C10.325 7.47006 11.285 6.51006 12.475 6.51006C13.655 6.51006 14.625 7.47006 14.625 8.66006C14.625 9.85006 13.665 10.8101 12.475 10.8101C11.285 10.8101 10.325 9.85006 10.325 8.66006Z",
    fill: getIconColor(primaryColor, entity)
  }), jsx("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M12.475 12.0101C10.625 12.0101 9.125 10.5101 9.125 8.66006C9.125 6.81006 10.625 5.31006 12.475 5.31006C14.325 5.31006 15.825 6.81006 15.825 8.66006C15.825 10.5101 14.315 12.0101 12.475 12.0101ZM12.475 6.51006C11.285 6.51006 10.325 7.47006 10.325 8.66006C10.325 9.85006 11.285 10.8101 12.475 10.8101C13.665 10.8101 14.625 9.85006 14.625 8.66006C14.625 7.47006 13.655 6.51006 12.475 6.51006Z",
    fill: getIconColor(primaryColor, entity),
    fillOpacity: "0.3"
  }));
};

var Contact = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: " 0 0 24 24"
  }, jsx("rect", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    fill: "none",
    rx: "0",
    ry: "0"
  }), jsx("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M16.15 3.85002H8.84998C8.54998 3.85002 8.25 3.55002 8.25 3.25002C8.25 2.95002 8.54998 2.65002 8.84998 2.65002H16.15C16.45 2.65002 16.75 2.95002 16.75 3.25002C16.75 3.55002 16.45 3.85002 16.15 3.85002ZM6.24994 6.04998H18.7499C19.0499 6.04998 19.3499 5.74998 19.3499 5.44998C19.3499 5.14998 19.0499 4.84998 18.7499 4.84998H6.24994C5.94994 4.84998 5.6499 5.14998 5.6499 5.44998C5.6499 5.74998 5.94994 6.04998 6.24994 6.04998ZM21.65 7.05005H3.34998C3.04998 7.05005 2.75 7.35005 2.75 7.65005V20.75C2.75 21.05 3.04998 21.35 3.34998 21.35H21.65C21.95 21.35 22.25 21.05 22.25 20.75V7.65005C22.25 7.35005 21.95 7.05005 21.65 7.05005ZM21.05 8.25005V18.65C20.55 17.75 19.75 16.95 18.75 16.55C19.25 16.05 19.55 15.35 19.55 14.65C19.55 13.15 18.25 11.85 16.75 11.85C15.25 11.85 13.95 13.15 13.95 14.65C13.95 15.35 14.25 16.05 14.75 16.55C14.5369 16.6566 14.25 16.8622 14.05 17.05C13.45 16.35 12.65 15.75 11.75 15.45C12.45 14.85 12.85 13.95 12.85 12.95C12.85 11.05 11.35 9.55005 9.44995 9.55005C7.54995 9.55005 6.04999 11.05 6.04999 12.95C6.04999 13.95 6.44996 14.85 7.14996 15.45C5.84996 15.95 4.54995 17.15 3.94995 18.45V8.25005H21.05ZM9.44995 15.15C8.24995 15.15 7.25 14.15 7.25 12.95C7.25 11.75 8.24995 10.75 9.44995 10.75C10.65 10.75 11.65 11.75 11.65 12.95C11.65 14.15 10.65 15.15 9.44995 15.15ZM9.44995 16.35H10.25C12.05 16.35 14.15 18.35 14.25 20.15H4.64996C4.84996 18.35 6.84996 16.35 8.64996 16.35H9.44995ZM14.65 17.85C15.1406 17.4688 15.5822 17.35 16.15 17.35H16.75H17.35C18.65 17.35 20.15 18.75 20.35 20.05H15.45C15.35 19.35 15.15 18.65 14.65 17.85ZM16.75 16.25C15.85 16.25 15.15 15.55 15.15 14.65C15.15 13.75 15.85 13.05 16.75 13.05C17.65 13.05 18.35 13.75 18.35 14.65C18.35 15.55 17.65 16.25 16.75 16.25Z",
    fill: getIconColor(primaryColor, entity)
  }));
};

var Campaigns = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: " 0 0 24 24"
  }, jsx("rect", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    fill: "none",
    rx: "0",
    ry: "0"
  }), jsx("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M8.6282 2.6499C7.4682 2.6499 6.5282 3.5899 6.5282 4.7499V5.1499H4.1282C3.2482 5.1499 2.5282 5.8699 2.5282 6.7499V11.7499V19.7499C2.5282 20.6299 3.2482 21.3499 4.1282 21.3499H20.1282C21.0082 21.3499 21.7282 20.6299 21.7282 19.7499V11.7499V6.7499C21.7282 5.8699 21.0082 5.1499 20.1282 5.1499H17.7282V4.7499C17.7282 3.5899 16.7882 2.6499 15.6282 2.6499C14.4682 2.6499 13.5282 3.5899 13.5282 4.7499V5.1499H10.7282V4.7499C10.7282 3.5899 9.7882 2.6499 8.6282 2.6499ZM9.5282 5.7499V4.7499C9.5282 4.2499 9.1282 3.8499 8.6282 3.8499C8.1282 3.8499 7.7282 4.2499 7.7282 4.7499V5.7499V6.7499C7.7282 7.2499 8.1282 7.6499 8.6282 7.6499C9.1282 7.6499 9.5282 7.2499 9.5282 6.7499V5.7499ZM6.5282 6.3499V6.7499C6.5282 7.9099 7.4682 8.8499 8.6282 8.8499C9.7882 8.8499 10.7282 7.9099 10.7282 6.7499V6.3499H13.5282V6.7499C13.5282 7.9099 14.4682 8.8499 15.6282 8.8499C16.7882 8.8499 17.7282 7.9099 17.7282 6.7499V6.3499H20.1282C20.3482 6.3499 20.5282 6.5299 20.5282 6.7499V11.1499H3.7282V6.7499C3.7282 6.5299 3.9082 6.3499 4.1282 6.3499H6.5282ZM16.5282 5.7499V4.7499C16.5282 4.2499 16.1282 3.8499 15.6282 3.8499C15.1282 3.8499 14.7282 4.2499 14.7282 4.7499V5.7499V6.7499C14.7282 7.2499 15.1282 7.6499 15.6282 7.6499C16.1282 7.6499 16.5282 7.2499 16.5282 6.7499V5.7499ZM3.7282 12.3499H20.5282V19.7499C20.5282 19.9699 20.3482 20.1499 20.1282 20.1499H4.1282C3.9082 20.1499 3.7282 19.9699 3.7282 19.7499V12.3499Z",
    fill: getIconColor(primaryColor, entity)
  }));
};

var SOCLogo$1 = function (_a) {
  var primaryColor = _a.primaryColor,
    size = _a.size,
    onClick = _a.onClick,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: " 0 0 28 28"
  }, jsx("path", {
    d: "M 4.76 7.118 C 13.595 2.857 26.137 -1.241 27.761 0.351 C 29.329 1.888 22.681 23.451 22.681 23.451 C 20.753 28.548 21.569 24.326 21.827 23.122 C 24.251 11.708 28.928 3.469 26.347 1.844 C 23.766 0.22 11.967 4.756 4.729 7.544 C 2.68 8.621 1.855 8.667 4.76 7.118 Z M 25.126 3.783 C 20.978 9.288 15.71 16.645 11.237 18.226 C 8.648 19.147 6.556 16.586 4.538 12.817 C 4.442 12.665 3.378 11.649 4.418 13.562 C 10.249 22.969 11.994 19.297 14.058 17.675 C 18.962 14.1 22.924 9.382 25.601 3.929 C 25.869 3.392 25.126 3.783 25.126 3.783 Z M 19.053 25.104 C 6.102 20.414 4.295 25.373 1.083 21.702 C 0.435 21.018 0.159 20.061 0.343 19.136 C 0.542 17.911 -0.003 18.687 0.023 19.136 C -0.103 20.388 0.344 21.63 1.239 22.513 C 4.887 25.208 3.341 22.519 20.359 25.998 C 21.179 26.082 19.053 25.104 19.053 25.104 Z",
    fill: getIconColor(primaryColor, entity)
  }));
};

var RightChevron = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: " 0 0 24 24"
  }, jsx("rect", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    fill: "none",
    rx: "0",
    ry: "0"
  }), jsx("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M8.99503 18.6012C8.84503 18.6012 8.68503 18.5412 8.57503 18.4212C8.34503 18.1912 8.34503 17.8112 8.57503 17.5712L14.145 12.0012L8.57503 6.42118C8.34503 6.19118 8.34503 5.81118 8.57503 5.57118C8.80503 5.34118 9.18503 5.34118 9.42503 5.57118L15.425 11.5712C15.655 11.8012 15.655 12.1812 15.425 12.4212L9.42503 18.4212C9.30503 18.5412 9.14503 18.6012 8.99503 18.6012Z",
    fill: getIconColor(primaryColor, entity)
  }));
};

var Close = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    onClick: onClick && function () {
      return onClick();
    }
  }, jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "2.5",
    stroke: getIconColor(primaryColor, entity),
    d: "M2 2L22 22"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "2.5",
    stroke: getIconColor(primaryColor, entity),
    d: "M22 2L2 22"
  }));
};

var TenDoor = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: " 0 0 24 24"
  }, jsx("path", {
    d: "M 4.25 3 C 4.25 1.481 5.481 0.25 7 0.25 L 17 0.25 C 18.519 0.25 19.75 1.481 19.75 3 L 19.75 18.581 C 19.75 19.75 19.012 20.791 17.909 21.177 L 11.909 23.277 C 11.079 23.568 10.16 23.444 9.436 22.945 C 8.713 22.445 8.272 21.629 8.25 20.75 L 7 20.75 C 5.481 20.75 4.25 19.519 4.25 18 Z M 8.25 19.25 L 8.25 5.919 C 8.25 4.75 8.988 3.709 10.091 3.323 L 14.587 1.75 L 7 1.75 C 6.31 1.75 5.75 2.31 5.75 3 L 5.75 18 C 5.75 18.69 6.31 19.25 7 19.25 Z M 18.25 3.819 C 18.25 3.414 18.054 3.034 17.723 2.799 C 17.393 2.565 16.969 2.505 16.587 2.639 L 10.587 4.739 C 10.086 4.915 9.75 5.388 9.75 5.919 L 9.75 20.681 C 9.75 21.542 10.6 22.145 11.413 21.861 L 17.413 19.761 C 17.914 19.585 18.25 19.112 18.25 18.581 Z M 13 11.25 C 13.414 11.25 13.75 11.586 13.75 12 L 13.75 14 C 13.75 14.414 13.414 14.75 13 14.75 C 12.586 14.75 12.25 14.414 12.25 14 L 12.25 12 C 12.25 11.586 12.586 11.25 13 11.25 Z",
    fill: getIconColor(primaryColor, entity)
  }));
};

var MultipleUsers2x = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: " 0 0 24 24"
  }, jsx("rect", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    fill: "none",
    rx: "0",
    ry: "0"
  }), jsx("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M18.686 9.16748C18.686 10.3091 18.1641 11.3211 17.3584 12.0115C19.6428 12.8196 21.6519 15.2485 21.6519 17.5698C21.6519 18.1343 21.1929 18.5933 20.6284 18.5933H9.22119C8.65674 18.5933 8.19775 18.1343 8.19775 17.5698C8.19775 15.2484 10.2063 12.8195 12.4905 12.0115C11.6853 11.321 11.1636 10.3091 11.1636 9.16748C11.1636 7.09326 12.8501 5.40576 14.9243 5.40576C16.9985 5.40576 18.686 7.09326 18.686 9.16748ZM17.4868 9.16748C17.4868 7.75439 16.3374 6.60498 14.9243 6.60498C13.5122 6.60498 12.3628 7.75439 12.3628 9.16748C12.3628 10.5806 13.5122 11.73 14.9243 11.73C16.3374 11.73 17.4868 10.5806 17.4868 9.16748ZM14.0366 12.9292C11.9272 12.9292 9.53076 15.27 9.40283 17.394H20.4468C20.3188 15.27 17.9214 12.9292 15.812 12.9292H14.0366ZM7.20813 17.3942H3.56812C3.75806 15.8743 5.45813 14.2542 6.96814 14.2542H8.23816C8.82812 13.3242 9.64819 12.4742 10.5981 11.8542V11.8442C10.6382 11.6542 10.6582 11.4542 10.6582 11.2542C10.3982 10.7443 10.2382 10.1743 10.1782 9.59424C9.63818 8.76422 8.69812 8.21423 7.63818 8.21423C5.97815 8.21423 4.61816 9.57422 4.61816 11.2343C4.61816 12.0742 4.96814 12.8342 5.51807 13.3842C3.80811 14.0942 2.34814 15.9142 2.34814 17.6642C2.34814 18.1743 2.76807 18.5942 3.27808 18.5942H7.48816C7.2981 18.2943 7.19812 17.9343 7.19812 17.5643C7.19812 17.5042 7.19812 17.4542 7.20813 17.3942ZM7.63818 9.41425C8.64819 9.41425 9.46814 10.2343 9.46814 11.2343C9.46814 12.2443 8.64819 13.0543 7.63818 13.0543C6.63818 13.0543 5.81812 12.2443 5.81812 11.2343C5.81812 10.2343 6.63818 9.41425 7.63818 9.41425ZM11.0381 15.8343C11.1682 16.0143 11.2881 16.2043 11.3882 16.3943H12.6882C12.4982 15.8743 12.1981 15.3843 11.8281 14.9343C11.5281 15.2043 11.2581 15.5043 11.0381 15.8343Z\n",
    fill: getIconColor(primaryColor, entity)
  }));
};

var MultipleUsers3x = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: " 0 0 24 24"
  }, jsx("rect", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    fill: "none",
    rx: "0",
    ry: "0"
  }), jsx("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M14.9988 9.85107C14.9988 10.681 14.6599 11.4324 14.1138 11.9758C15.8055 12.6845 17.2488 14.4863 17.2488 16.2271C17.2488 16.7349 16.8357 17.1479 16.3279 17.1479H7.67163C7.16284 17.1479 6.74976 16.7349 6.74976 16.2271C6.74976 14.4861 8.19312 12.6843 9.88562 11.9757C9.3396 11.4323 9.00073 10.6809 9.00073 9.85107C9.00073 8.19678 10.3455 6.85205 11.9998 6.85205C13.6541 6.85205 14.9988 8.19678 14.9988 9.85107ZM13.7996 9.85107C13.7996 8.85889 12.9919 8.05127 11.9998 8.05127C11.0076 8.05127 10.2 8.85889 10.2 9.85107C10.2 10.8433 11.0076 11.6509 11.9998 11.6509C12.9919 11.6509 13.7996 10.8433 13.7996 9.85107ZM11.3259 12.8501C9.84058 12.8501 8.15894 14.4448 7.96753 15.9487H16.031C15.8396 14.4448 14.1589 12.8501 12.6736 12.8501H11.3259ZM5.95496 13.8553H6.41504C6.88501 12.9954 7.58496 12.2153 8.42493 11.6453C8.14502 11.0953 8.00488 10.4854 8.00488 9.85535C8.00488 9.74536 8.00488 9.63531 8.0249 9.53534C7.60498 9.19531 7.06494 8.98535 6.47498 8.98535C5.13501 8.98535 4.03491 10.0753 4.03491 11.4254C4.03491 12.0153 4.26489 12.5554 4.61499 12.9854C3.34497 13.5853 2.31494 14.9553 2.31494 16.3054C2.31494 16.7653 2.69495 17.1453 3.16504 17.1453H5.98486C5.83496 16.8654 5.74487 16.5554 5.74487 16.2253C5.74487 16.1353 5.74487 16.0453 5.75488 15.9453H3.55493C3.7749 14.9053 4.93494 13.8553 5.95496 13.8553ZM6.47498 10.1854C7.15503 10.1854 7.71497 10.7354 7.71497 11.4254C7.71497 12.1053 7.15503 12.6553 6.47498 12.6553C5.79492 12.6553 5.23486 12.1053 5.23486 11.4254C5.23486 10.7354 5.79492 10.1854 6.47498 10.1854ZM9.49487 14.9453H10.3049C10.225 14.7653 10.125 14.5953 10.0149 14.4353C9.82495 14.5753 9.64502 14.7552 9.49487 14.9453ZM19.965 11.4254C19.965 12.0153 19.7349 12.5554 19.385 12.9854C20.655 13.5853 21.6849 14.9553 21.6849 16.3054C21.6849 16.7653 21.3049 17.1453 20.835 17.1453H18.0049C18.165 16.8754 18.2449 16.5554 18.2449 16.2253C18.2449 16.1353 18.2449 16.0453 18.2349 15.9453H20.4449C20.225 14.9053 19.0649 13.8553 18.0449 13.8553H17.595C17.115 12.9954 16.415 12.2153 15.575 11.6453C15.855 11.0953 15.9949 10.4854 15.9949 9.85535C15.9949 9.74536 15.9949 9.63531 15.975 9.53534C16.395 9.19531 16.9349 8.98535 17.5249 8.98535C18.865 8.98535 19.965 10.0753 19.965 11.4254ZM16.2849 11.4254C16.2849 12.1053 16.845 12.6553 17.5249 12.6553C18.205 12.6553 18.7649 12.1053 18.7649 11.4254C18.7649 10.7354 18.205 10.1854 17.5249 10.1854C16.845 10.1854 16.2849 10.7354 16.2849 11.4254ZM13.985 14.4353C13.8751 14.5953 13.775 14.7653 13.6951 14.9453H14.505C14.3551 14.7552 14.175 14.5753 13.985 14.4353Z\n",
    fill: getIconColor(primaryColor, entity)
  }));
};

var Hamburger = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    onClick: onClick && function () {
      return onClick();
    }
  }, jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M3 18H21"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M3 12H21"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M3 6H21"
  }));
};

var LeftChevron = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: " 0 0 24 24"
  }, jsx("rect", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    fill: "none",
    rx: "0",
    ry: "0"
  }), jsx("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M15.005 18.6012C15.155 18.6012 15.315 18.5412 15.425 18.4212C15.655 18.1912 15.655 17.8112 15.425 17.5712L9.85503 12.0012L15.425 6.42118C15.655 6.19118 15.655 5.81118 15.425 5.57118C15.195 5.34118 14.815 5.34118 14.575 5.57118L8.57503 11.5712C8.34503 11.8012 8.34503 12.1812 8.57503 12.4212L14.575 18.4212C14.695 18.5412 14.855 18.6012 15.005 18.6012Z",
    fill: getIconColor(primaryColor, entity)
  }));
};

var Chevron = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity,
    orientation = _a.orientation;
  return jsx(motion.svg, {
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: " 0 0 24 24",
    style: {
      transform: orientation === 'up' ? 'rotate(90deg)' : orientation === 'down' ? 'rotate(270deg)' : orientation === 'right' ? 'rotate(180deg)' : 'rotate(0deg)'
    }
  }, jsx("rect", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    fill: "none",
    rx: "0",
    ry: "0"
  }), jsx("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M15.005 18.6012C15.155 18.6012 15.315 18.5412 15.425 18.4212C15.655 18.1912 15.655 17.8112 15.425 17.5712L9.85503 12.0012L15.425 6.42118C15.655 6.19118 15.655 5.81118 15.425 5.57118C15.195 5.34118 14.815 5.34118 14.575 5.57118L8.57503 11.5712C8.34503 11.8012 8.34503 12.1812 8.57503 12.4212L14.575 18.4212C14.695 18.5412 14.855 18.6012 15.005 18.6012Z",
    fill: getIconColor(primaryColor, entity)
  }));
};

var Information = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: " 0 0 24 24"
  }, jsx("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12ZM19.8 12C19.8 16.3078 16.3078 19.8 12 19.8C7.69218 19.8 4.2 16.3078 4.2 12C4.2 7.69218 7.69218 4.2 12 4.2C16.3078 4.2 19.8 7.69218 19.8 12ZM13 7V9H11V7H13ZM13 17V10H11V17H13Z",
    fill: getIconColor(primaryColor, entity)
  }));
};

var Plus = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    viewBox: " 0 0 24 24",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    onClick: onClick && function () {
      return onClick();
    }
  }, jsx("rect", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    fill: "none",
    rx: "0",
    ry: "0"
  }), jsx("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M11 21C11 21.55 11.45 22 12 22C12.55 22 13 21.55 13 21V13H21C21.55 13 22 12.55 22 12C22 11.45 21.55 11 21 11H13V3C13 2.45 12.55 2 12 2C11.45 2 11 2.45 11 3V11H3C2.45 11 2 11.45 2 12C2 12.55 2.45 13 3 13H11V21Z",
    fill: getIconColor(primaryColor, entity)
  }));
};

var Minus = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    viewBox: " 0 0 24 24",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    onClick: onClick && function () {
      return onClick();
    }
  }, jsx("rect", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    fill: "none",
    rx: "0",
    ry: "0"
  }), jsx("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M21 13H3C2.45 13 2 12.55 2 12C2 11.45 2.45 11 3 11H21C21.55 11 22 11.45 22 12C22 12.55 21.55 13 21 13Z",
    fill: getIconColor(primaryColor, entity)
  }));
};

var FilterBy = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: " 0 0 24 24"
  }, jsx("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M13.6 21.5C13.49 21.5 13.38 21.47 13.28 21.41L10.08 19.41C9.91 19.3 9.8 19.11 9.8 18.9V10.65L2.68 3.52C2.5 3.35 2.45 3.09 2.55 2.87C2.65 2.65 2.86 2.5 3.1 2.5H20.9C21.14 2.5 21.36 2.65 21.45 2.87C21.54 3.09 21.5 3.35 21.32 3.52L14.2 10.65V20.9C14.2 21.12 14.08 21.32 13.89 21.42C13.8 21.48 13.7 21.5 13.6 21.5ZM11 18.57L13 19.82V10.4C13 10.24 13.06 10.09 13.18 9.98L19.46 3.7H4.55L10.83 9.98C10.94 10.09 11 10.24 11 10.4V18.57Z",
    fill: getIconColor(primaryColor, entity)
  }));
};

var Groups = function (_a) {
  var primaryColor = _a.primaryColor,
    size = _a.size,
    onClick = _a.onClick,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: " 0 0 24 24"
  }, jsx("rect", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    fill: "none",
    rx: "0",
    ry: "0"
  }), jsx("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M17 7C17 9.76142 14.7614 12 12 12C9.23858 12 7 9.76142 7 7C7 4.23858 9.23858 2 12 2C14.7614 2 17 4.23858 17 7ZM15.8 7C15.8 9.09868 14.0987 10.8 12 10.8C9.90132 10.8 8.2 9.09868 8.2 7C8.2 4.90132 9.90132 3.2 12 3.2C14.0987 3.2 15.8 4.90132 15.8 7ZM3 21C3 16.5817 6.58172 13 11 13H13C17.4183 13 21 16.5817 21 21C21 21 18 22 12 22C6 22 3 21 3 21ZM10.9958 14.2C7.65616 14.2 4.81083 16.6251 4.28168 19.9226L4.25 20.12C4.25 20.12 7 20.8 12 20.8C17 20.8 19.75 20.12 19.75 20.12L19.7073 19.8674C19.1546 16.5953 16.3208 14.2 13.0023 14.2H10.9958Z",
    fill: getIconColor(primaryColor, entity)
  }));
};

var IDCard = function (_a) {
  var primaryColor = _a.primaryColor,
    size = _a.size,
    onClick = _a.onClick,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: " 0 0 24 24"
  }, jsx("rect", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    fill: "none",
    rx: "0",
    ry: "0"
  }), jsx("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M4 5C2.89543 5 2 5.89543 2 7V17C2 18.1046 2.89543 19 4 19H20C21.1046 19 22 18.1046 22 17V7C22 5.89543 21.1046 5 20 5H4ZM6 13C4.89543 13 4 13.8954 4 15V17H12V15C12 13.8954 11.1046 13 10 13H6ZM8 12C9.38071 12 10.5 10.8807 10.5 9.5C10.5 8.11929 9.38071 7 8 7C6.61929 7 5.5 8.11929 5.5 9.5C5.5 10.8807 6.61929 12 8 12ZM14.6 8C14.2686 8 14 8.26863 14 8.6C14 8.93137 14.2686 9.2 14.6 9.2H19.4C19.7314 9.2 20 8.93137 20 8.6C20 8.26863 19.7314 8 19.4 8H14.6ZM13 11.8C13 11.4686 13.2686 11.2 13.6 11.2H19.4C19.7314 11.2 20 11.4686 20 11.8C20 12.1314 19.7314 12.4 19.4 12.4H13.6C13.2686 12.4 13 12.1314 13 11.8ZM15.6 14.4C15.2686 14.4 15 14.6686 15 15C15 15.3314 15.2686 15.6 15.6 15.6H19.4C19.7314 15.6 20 15.3314 20 15C20 14.6686 19.7314 14.4 19.4 14.4H15.6Z",
    fill: getIconColor(primaryColor, entity)
  }));
};

var Star = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: " 0 0 24 24"
  }, jsx("rect", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    fill: "none",
    rx: "0",
    ry: "0"
  }), jsx("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M21.9692 9.67997C21.8992 9.44997 21.6892 9.28997 21.4492 9.26997L15.0492 8.71997L12.5492 2.79997C12.3592 2.35997 11.6292 2.35997 11.4392 2.79997L8.93918 8.71997L2.54918 9.26997C2.30918 9.28997 2.09918 9.44997 2.02918 9.67997C1.95918 9.90997 2.02918 10.16 2.20918 10.32L7.05918 14.53L5.60918 20.79C5.55918 21.02 5.64918 21.27 5.83918 21.41C6.02918 21.55 6.29918 21.56 6.49918 21.44L11.9992 18.12L17.4992 21.44C17.5992 21.5 17.6992 21.53 17.8092 21.53C17.9292 21.53 18.0592 21.49 18.1592 21.42C18.3592 21.28 18.4492 21.03 18.3892 20.8L16.9392 14.54L21.7892 10.33C21.9792 10.16 22.0392 9.90997 21.9692 9.67997ZM15.8792 13.86C15.7092 14.01 15.6392 14.23 15.6892 14.45L16.9092 19.68L12.3192 16.91C12.2192 16.85 12.1192 16.82 12.0092 16.82C11.8992 16.82 11.7992 16.85 11.6992 16.91L7.09918 19.67L8.31918 14.44C8.36918 14.22 8.29918 14 8.12918 13.85L4.06918 10.34L9.40918 9.87997C9.62918 9.85997 9.81918 9.71997 9.90918 9.51997L11.9992 4.57997L14.0892 9.51997C14.1792 9.72997 14.3692 9.86997 14.5892 9.87997L19.9292 10.34L15.8792 13.86Z",
    fill: getIconColor(primaryColor, entity)
  }));
};

var PlusSquare = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    viewBox: " 0 0 24 24",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    onClick: onClick && function () {
      return onClick();
    }
  }, jsx("rect", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    fill: "none",
    rx: "0",
    ry: "0"
  }), jsx("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M6 2H18C20.21 2 22 3.79 22 6V18C22 20.21 20.21 22 18 22H6C3.79 22 2 20.21 2 18V6C2 3.79 3.79 2 6 2ZM12 17.5C11.45 17.5 11 17.05 11 16.5V13H7.5C6.95 13 6.5 12.55 6.5 12C6.5 11.45 6.95 11 7.5 11H11V7.5C11 6.95 11.45 6.5 12 6.5C12.55 6.5 13 6.95 13 7.5V11H16.5C17.05 11 17.5 11.45 17.5 12C17.5 12.55 17.05 13 16.5 13H13V16.5C13 17.05 12.55 17.5 12 17.5Z",
    fill: getIconColor(primaryColor, entity)
  }));
};

var Search = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    viewBox: " 0 0 24 24",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    onClick: onClick && function () {
      return onClick();
    }
  }, jsx("rect", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    fill: "none",
    rx: "0",
    ry: "0"
  }), jsx("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M10 18C11.8411 18 13.537 17.3781 14.889 16.3328L20.2631 21.7069C20.6536 22.0974 21.2868 22.0974 21.6773 21.7069L21.7069 21.6773C22.0974 21.2868 22.0974 20.6536 21.7069 20.2631L16.3328 14.889C17.3781 13.537 18 11.8411 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18ZM16.7995 9.99947C16.7995 13.755 13.755 16.7995 9.99947 16.7995C6.24393 16.7995 3.19947 13.755 3.19947 9.99947C3.19947 6.24393 6.24393 3.19947 9.99947 3.19947C13.755 3.19947 16.7995 6.24393 16.7995 9.99947Z",
    fill: getIconColor(primaryColor, entity)
  }));
};

var Cart = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: "0 0 24 24"
  }, jsx("path", {
    fill: getIconColor(primaryColor, entity),
    d: "M 0.25 3 C 0.25 2.586 0.586 2.25 1 2.25 L 3.477 2.25 C 4.714 2.25 5.799 3.076 6.128 4.269 L 6.675 6.25 L 20.827 6.25 C 21.694 6.25 22.51 6.659 23.029 7.353 C 23.548 8.047 23.71 8.945 23.465 9.776 L 22.073 14.509 C 21.776 15.52 20.927 16.273 19.887 16.446 L 10.901 17.943 C 9.512 18.175 8.173 17.32 7.798 15.963 L 4.682 4.667 C 4.533 4.125 4.039 3.749 3.477 3.749 L 1 3.749 C 0.586 3.749 0.251 3.414 0.25 3 Z M 7.088 7.75 L 9.244 15.563 C 9.414 16.18 10.023 16.568 10.654 16.463 L 19.64 14.966 C 20.113 14.887 20.499 14.546 20.634 14.086 L 22.026 9.353 C 22.137 8.975 22.064 8.567 21.828 8.251 C 21.592 7.936 21.221 7.75 20.827 7.75 Z M 9.25 21 C 9.25 20.586 9.586 20.25 10 20.25 L 11 20.25 C 11.414 20.25 11.75 20.586 11.75 21 C 11.75 21.414 11.414 21.75 11 21.75 L 10 21.75 C 9.586 21.75 9.25 21.414 9.25 21 Z M 17.25 21 C 17.25 20.586 17.586 20.25 18 20.25 L 19 20.25 C 19.414 20.25 19.75 20.586 19.75 21 C 19.75 21.414 19.414 21.75 19 21.75 L 18 21.75 C 17.586 21.75 17.25 21.414 17.25 21 Z"
  }));
};

var Slider$1 = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: " 0 0 24 24"
  }, jsx("rect", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    fill: "none",
    rx: "0",
    ry: "0"
  }), jsx("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M5.07259 4.8999H2.6C2.27 4.8999 2 5.1699 2 5.4999C2 5.8299 2.27 6.0999 2.6 6.0999H5.07254C5.34146 7.19048 6.32677 8 7.5 8C8.67323 8 9.65854 7.19048 9.92746 6.0999H21.4C21.73 6.0999 22 5.8299 22 5.4999C22 5.1699 21.73 4.8999 21.4 4.8999H9.92741C9.65842 3.80942 8.67316 3 7.5 3C6.32684 3 5.34158 3.80942 5.07259 4.8999ZM7.5 4.2C6.78 4.2 6.2 4.78 6.2 5.5C6.2 6.22 6.78 6.8 7.5 6.8C8.22 6.8 8.8 6.22 8.8 5.5C8.8 4.78 8.22 4.2 7.5 4.2ZM5.07259 17.8999H2.6C2.27 17.8999 2 18.1699 2 18.4999C2 18.8299 2.27 19.0999 2.6 19.0999H5.07254C5.34146 20.1905 6.32677 21 7.5 21C8.67323 21 9.65854 20.1905 9.92746 19.0999H21.4C21.73 19.0999 22 18.8299 22 18.4999C22 18.1699 21.73 17.8999 21.4 17.8999H9.92741C9.65842 16.8094 8.67316 16 7.5 16C6.32684 16 5.34158 16.8094 5.07259 17.8999ZM7.5 17.2C6.78 17.2 6.2 17.78 6.2 18.5C6.2 19.22 6.78 19.8 7.5 19.8C8.22 19.8 8.8 19.22 8.8 18.5C8.8 17.78 8.22 17.2 7.5 17.2ZM14.0725 12.5999C14.3415 13.6905 15.3268 14.5 16.5 14.5C17.6732 14.5 18.6585 13.6905 18.9275 12.5999H21.4C21.73 12.5999 22 12.3299 22 11.9999C22 11.6699 21.73 11.3999 21.4 11.3999H18.9274C18.6584 10.3094 17.6732 9.5 16.5 9.5C15.3268 9.5 14.3416 10.3094 14.0726 11.3999H2.6C2.27 11.3999 2 11.6699 2 11.9999C2 12.3299 2.27 12.5999 2.6 12.5999H14.0725ZM15.2 12C15.2 11.28 15.78 10.7 16.5 10.7C17.22 10.7 17.8 11.28 17.8 12C17.8 12.72 17.22 13.3 16.5 13.3C15.78 13.3 15.2 12.72 15.2 12",
    fill: getIconColor(primaryColor, entity)
  }));
};

var PriceTag = function (_a) {
  var primaryColor = _a.primaryColor,
    size = _a.size,
    onClick = _a.onClick,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: " 0 0 24 24"
  }, jsx("rect", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    fill: "none",
    rx: "0",
    ry: "0"
  }), jsx("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M9.64504 21.575C9.86504 21.785 10.145 21.895 10.425 21.895C10.715 21.895 10.995 21.795 11.205 21.575L21.585 11.205C21.795 10.995 21.905 10.725 21.905 10.435V3.70498C21.905 2.82498 21.185 2.10498 20.305 2.10498H13.785C13.355 2.10498 12.955 2.26498 12.655 2.56498L2.42504 12.805C1.99504 13.225 1.99504 13.925 2.42504 14.355L9.64504 21.575ZM10.425 20.655L3.34504 13.575L13.495 3.41498C13.575 3.34498 13.665 3.30498 13.775 3.30498H20.295C20.515 3.30498 20.695 3.48498 20.695 3.70498L20.725 10.355L10.425 20.655ZM14.0751 9.92505C14.5851 10.435 15.2451 10.685 15.9151 10.685C16.5851 10.685 17.2551 10.435 17.7551 9.92505C18.7651 8.90505 18.7651 7.25505 17.7551 6.24505C16.7351 5.23505 15.0851 5.23505 14.0751 6.24505C13.0651 7.26505 13.0651 8.91505 14.0751 9.92505ZM14.9251 7.09505C15.1951 6.82505 15.5551 6.68505 15.9151 6.68505C16.2751 6.68505 16.6351 6.82505 16.9051 7.09505C17.4551 7.64505 17.4551 8.52505 16.9051 9.07505C16.3651 9.62505 15.4751 9.62505 14.9251 9.07505C14.3751 8.52505 14.3751 7.64505 14.9251 7.09505Z",
    fill: getIconColor(primaryColor, entity)
  }));
};

var Layer = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    viewBox: " 0 0 24 24",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    onClick: onClick && function () {
      return onClick();
    },
    fill: "none"
  }, jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M22 12L12 18L2 12"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M22 16L12 22L2 16"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    d: "M22 8L12 14L2 8L12 2L22 8Z"
  }));
};

var Loading = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && onClick,
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: " 0 0 24 24"
  }, jsx("rect", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    fill: "none",
    rx: "0",
    ry: "0"
  }), jsx("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M13.5 3.5C13.5 4.32843 12.8284 5 12 5C11.1716 5 10.5 4.32843 10.5 3.5C10.5 2.67157 11.1716 2 12 2C12.8284 2 13.5 2.67157 13.5 3.5ZM13.5 20.5C13.5 21.3284 12.8284 22 12 22C11.1716 22 10.5 21.3284 10.5 20.5C10.5 19.6716 11.1716 19 12 19C12.8284 19 13.5 19.6716 13.5 20.5ZM16.25 20.8601C17.0784 20.8601 17.75 20.1885 17.75 19.3601C17.75 18.5317 17.0784 17.8601 16.25 17.8601C15.4216 17.8601 14.75 18.5317 14.75 19.3601C14.75 20.1885 15.4216 20.8601 16.25 20.8601ZM9.25 4.63989C9.25 5.46832 8.57843 6.13989 7.75 6.13989C6.92157 6.13989 6.25 5.46832 6.25 4.63989C6.25 3.81147 6.92157 3.13989 7.75 3.13989C8.57843 3.13989 9.25 3.81147 9.25 4.63989ZM19.3601 17.75C20.1885 17.75 20.8601 17.0784 20.8601 16.25C20.8601 15.4216 20.1885 14.75 19.3601 14.75C18.5317 14.75 17.8601 15.4216 17.8601 16.25C17.8601 17.0784 18.5317 17.75 19.3601 17.75ZM6.13989 7.75C6.13989 8.57843 5.46832 9.25 4.63989 9.25C3.81147 9.25 3.13989 8.57843 3.13989 7.75C3.13989 6.92157 3.81147 6.25 4.63989 6.25C5.46832 6.25 6.13989 6.92157 6.13989 7.75ZM20.5 13.5C21.3284 13.5 22 12.8284 22 12C22 11.1716 21.3284 10.5 20.5 10.5C19.6716 10.5 19 11.1716 19 12C19 12.8284 19.6716 13.5 20.5 13.5ZM5 12C5 12.8284 4.32843 13.5 3.5 13.5C2.67157 13.5 2 12.8284 2 12C2 11.1716 2.67157 10.5 3.5 10.5C4.32843 10.5 5 11.1716 5 12ZM19.3601 9.25C20.1885 9.25 20.8601 8.57843 20.8601 7.75C20.8601 6.92157 20.1885 6.25 19.3601 6.25C18.5317 6.25 17.8601 6.92157 17.8601 7.75C17.8601 8.57843 18.5317 9.25 19.3601 9.25ZM6.13989 16.25C6.13989 17.0784 5.46832 17.75 4.63989 17.75C3.81147 17.75 3.13989 17.0784 3.13989 16.25C3.13989 15.4216 3.81147 14.75 4.63989 14.75C5.46832 14.75 6.13989 15.4216 6.13989 16.25ZM16.25 6.13989C17.0784 6.13989 17.75 5.46832 17.75 4.63989C17.75 3.81147 17.0784 3.13989 16.25 3.13989C15.4216 3.13989 14.75 3.81147 14.75 4.63989C14.75 5.46832 15.4216 6.13989 16.25 6.13989ZM9.25 19.3601C9.25 20.1885 8.57843 20.8601 7.75 20.8601C6.92157 20.8601 6.25 20.1885 6.25 19.3601C6.25 18.5317 6.92157 17.8601 7.75 17.8601C8.57843 17.8601 9.25 18.5317 9.25 19.3601Z",
    fill: getIconColor(primaryColor, entity)
  }));
};

var Play = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    viewBox: " 0 0 24 24",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    onClick: onClick && function () {
      return onClick();
    }
  }, jsx("rect", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    fill: "none",
    rx: "0",
    ry: "0"
  }), jsx("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M7.54076 3.99049C6.87525 3.56266 6 4.0405 6 4.83167V19.1683C6 19.9595 6.87525 20.4373 7.54076 20.0095L18.6915 12.8412C19.3038 12.4475 19.3038 11.5525 18.6915 11.1588L7.54076 3.99049ZM7.96998 5.69395C7.6372 5.48047 7.2 5.71943 7.2 6.11479V17.8852C7.2 18.2806 7.6372 18.5195 7.96998 18.3061L17.144 12.4208C17.4507 12.2241 17.4507 11.7759 17.144 11.5792L7.96998 5.69395Z",
    fill: getIconColor(primaryColor, entity)
  }));
};

var Lock = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    viewBox: " 0 0 24 24",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    onClick: onClick && function () {
      return onClick();
    }
  }, jsx("rect", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    fill: "none",
    rx: "0",
    ry: "0"
  }), jsx("path", {
    d: "M 18 10.75 C 17.59 10.75 17.25 10.41 17.25 10 L 17.25 8 C 17.25 4.85 16.36 2.75 12 2.75 C 7.64 2.75 6.75 4.85 6.75 8 L 6.75 10 C 6.75 10.41 6.41 10.75 6 10.75 C 5.59 10.75 5.25 10.41 5.25 10 L 5.25 8 C 5.25 5.1 5.95 1.25 12 1.25 C 18.05 1.25 18.75 5.1 18.75 8 L 18.75 10 C 18.75 10.41 18.41 10.75 18 10.75 Z M 12 19.25 C 10.21 19.25 8.75 17.79 8.75 16 C 8.75 14.21 10.21 12.75 12 12.75 C 13.79 12.75 15.25 14.21 15.25 16 C 15.25 17.79 13.79 19.25 12 19.25 Z M 12 14.25 C 11.04 14.25 10.25 15.04 10.25 16 C 10.25 16.96 11.04 17.75 12 17.75 C 12.96 17.75 13.75 16.96 13.75 16 C 13.75 15.04 12.96 14.25 12 14.25 Z",
    fill: getIconColor(primaryColor, entity)
  }), jsx("path", {
    d: "M 17 22.75 L 7 22.75 C 2.59 22.75 1.25 21.41 1.25 17 L 1.25 15 C 1.25 10.59 2.59 9.25 7 9.25 L 17 9.25 C 21.41 9.25 22.75 10.59 22.75 15 L 22.75 17 C 22.75 21.41 21.41 22.75 17 22.75 Z M 7 10.75 C 3.42 10.75 2.75 11.43 2.75 15 L 2.75 17 C 2.75 20.57 3.42 21.25 7 21.25 L 17 21.25 C 20.58 21.25 21.25 20.57 21.25 17 L 21.25 15 C 21.25 11.43 20.58 10.75 17 10.75 Z",
    fill: getIconColor(primaryColor, entity)
  }));
};

var User = function (_a) {
  var primaryColor = _a.primaryColor,
    size = _a.size,
    onClick = _a.onClick,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: " 0 0 24 24"
  }, jsx("rect", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    fill: "none",
    rx: "0",
    ry: "0"
  }), jsx("path", {
    d: "M 12 12.75 C 8.83 12.75 6.25 10.17 6.25 7 C 6.25 3.83 8.83 1.25 12 1.25 C 15.17 1.25 17.75 3.83 17.75 7 C 17.75 10.17 15.17 12.75 12 12.75 Z M 12 2.75 C 9.655 2.755 7.755 4.655 7.75 7 C 7.755 9.345 9.655 11.245 12 11.25 C 14.345 11.245 16.245 9.345 16.25 7 C 16.245 4.655 14.345 2.755 12 2.75 Z M 20.59 22.75 C 20.18 22.75 19.84 22.41 19.84 22 C 19.84 18.55 16.32 15.75 12 15.75 C 7.68 15.75 4.16 18.55 4.16 22 C 4.16 22.41 3.82 22.75 3.41 22.75 C 3 22.75 2.66 22.41 2.66 22 C 2.66 17.73 6.85 14.25 12 14.25 C 17.15 14.25 21.34 17.73 21.34 22 C 21.34 22.41 21 22.75 20.59 22.75 Z",
    fill: getIconColor(primaryColor, entity)
  }));
};

var NoUsers = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    viewBox: " 0 0 24 24",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    onClick: onClick && function () {
      return onClick();
    }
  }, jsx("rect", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    fill: "none",
    rx: "0",
    ry: "0"
  }), jsx("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M4.17017 3.94458C4.40454 3.71021 4.78345 3.71021 5.01782 3.94458L21.0383 19.9661C21.2727 20.2004 21.2727 20.5803 21.0383 20.8137C20.9211 20.9309 20.7678 20.9895 20.6145 20.9895C20.4612 20.9895 20.3079 20.9309 20.1907 20.8137L4.17017 4.79224C3.93579 4.55786 3.93579 4.17798 4.17017 3.94458ZM15.015 7.29211C13.695 7.29211 12.615 8.37207 12.615 9.69202C12.615 9.89209 12.635 10.0822 12.685 10.2621L11.475 9.04211C11.785 7.37207 13.255 6.09204 15.015 6.09204C17.005 6.09204 18.615 7.71204 18.615 9.69202C18.615 10.7621 18.135 11.722 17.385 12.3821C19.545 13.172 21.435 15.462 21.435 17.662C21.435 18.0221 21.235 18.342 20.945 18.5221L19.885 17.462H20.225C20.085 15.472 17.835 13.2921 15.855 13.2921H15.715L14.445 12.0221C14.625 12.0721 14.815 12.092 15.015 12.092C16.345 12.092 17.415 11.0121 17.415 9.69202C17.415 8.37207 16.345 7.29211 15.015 7.29211ZM7.69495 17.4619H4.28491C4.47491 16.042 6.06494 14.552 7.4649 14.552H8.65491C9.13495 13.782 9.78491 13.072 10.5349 12.512L9.79492 11.772C9.73492 12.6619 9.00494 13.3519 8.11493 13.3519C7.17493 13.3519 6.41492 12.5919 6.41492 11.652C6.41492 10.762 7.10492 10.032 7.99493 9.97192L7.00494 8.98193C5.95496 9.42188 5.2149 10.4519 5.2149 11.652C5.2149 12.4419 5.53491 13.152 6.04492 13.6819C4.43494 14.3719 3.06494 16.0919 3.06494 17.752C3.06494 18.262 3.4649 18.6619 3.9649 18.6619H7.9649C7.78491 18.3719 7.68494 18.032 7.68494 17.6619C7.68494 17.5919 7.68494 17.532 7.69495 17.4619ZM11.6349 16.5521H12.9249C12.7549 16.0521 12.4649 15.5721 12.1149 15.1521C11.8049 15.4021 11.5349 15.712 11.3149 16.0421C11.4349 16.212 11.5449 16.3821 11.6349 16.5521ZM9.815 17.4619C9.90497 16.1819 10.855 14.832 12.055 14.032L11.195 13.1719C9.71497 14.2419 8.60498 15.9819 8.60498 17.6619C8.60498 18.2119 9.05499 18.6619 9.60498 18.6619H16.685L15.485 17.4619H9.815Z",
    fill: getIconColor(primaryColor, entity)
  }));
};

var Add = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    viewBox: " 0 0 24 24",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    onClick: onClick && function () {
      return onClick();
    }
  }, jsx("rect", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    fill: "none",
    rx: "0",
    ry: "0"
  }), jsx("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M10.5 20.5C10.5 21.33 11.17 22 12 22C12.83 22 13.5 21.33 13.5 20.5V13.5H20.5C21.33 13.5 22 12.83 22 12C22 11.17 21.33 10.5 20.5 10.5H13.5V3.5C13.5 2.67 12.83 2 12 2C11.17 2 10.5 2.67 10.5 3.5V10.5H3.5C2.67 10.5 2 11.17 2 12C2 12.83 2.67 13.5 3.5 13.5H10.5V20.5Z",
    fill: getIconColor(primaryColor, entity)
  }));
};

var Check = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    viewBox: " 0 0 24 24",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    onClick: onClick && function () {
      return onClick();
    }
  }, jsx("rect", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    fill: "none",
    rx: "0",
    ry: "0"
  }), jsx("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M20.6992 5.06267C20.3086 4.67214 19.6755 4.67214 19.2849 5.06267L9.36846 14.9791L5.12944 10.7401C4.73891 10.3496 4.10575 10.3496 3.71522 10.7401L3.00707 11.4483C2.61654 11.8388 2.61654 12.472 3.00707 12.8625L7.96034 17.8158C8.02223 17.8777 8.08717 17.9346 8.15468 17.9867L8.67304 18.5051C9.06357 18.8956 9.69673 18.8956 10.0873 18.5051L10.6506 17.9418C10.6981 17.9023 10.7442 17.8603 10.7888 17.8158L11.4969 17.1076L11.4908 17.1015L21.4073 7.18504C21.7978 6.79451 21.7978 6.16135 21.4073 5.77083L20.6992 5.06267Z",
    fill: getIconColor(primaryColor, entity)
  }));
};

var Exclamation = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    viewBox: " 0 0 24 24",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    onClick: onClick && function () {
      return onClick();
    }
  }, jsx("rect", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    fill: "none",
    rx: "0",
    ry: "0"
  }), jsx("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M12.5198 5.30249C13.0398 5.30249 13.5198 5.52249 13.8798 5.91249C14.2498 6.33249 14.4298 6.89249 14.3698 7.45249L13.8598 12.4425C13.7498 13.4325 12.9598 14.1825 11.9998 14.1825C11.0398 14.1825 10.2398 13.4325 10.1398 12.4425L9.62982 7.45249C9.56982 6.89249 9.75982 6.32249 10.1298 5.91249C10.4798 5.52249 10.9798 5.30249 11.4898 5.30249H12.5198ZM12.0098 12.9825C12.3498 12.9825 12.6298 12.7025 12.6698 12.3225L13.1998 7.33249C13.2198 7.10249 13.1498 6.87249 12.9998 6.71249C12.8698 6.57249 12.7098 6.49249 12.5298 6.49249H11.4998C11.3198 6.49249 11.1598 6.57249 11.0298 6.71249C10.8898 6.88249 10.8198 7.10249 10.8398 7.33249L11.3498 12.3225C11.3898 12.7025 11.6698 12.9825 12.0098 12.9825ZM10.1998 17.0124C10.1998 16.0224 11.0098 15.2224 11.9998 15.2224C12.9998 15.2224 13.7998 16.0224 13.7998 17.0124C13.7998 18.0024 12.9898 18.8024 11.9998 18.8024C11.0098 18.8024 10.1998 18.0024 10.1998 17.0124ZM11.1998 17.0124C11.1998 17.4424 11.5598 17.8024 11.9998 17.8024C12.4498 17.8024 12.7998 17.4524 12.7998 17.0124C12.7998 16.5824 12.4398 16.2224 11.9998 16.2224C11.5598 16.2224 11.1998 16.5724 11.1998 17.0124Z",
    fill: getIconColor(primaryColor, entity)
  }));
};

var Envelope = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    onClick: onClick && function () {
      return onClick();
    },
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    d: "M20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20Z"
  }), jsx("path", {
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    d: "M20 4H4C2.9 4 2 4.9 2 6L12 13L22 6C22 4.9 21.1 4 20 4Z"
  }));
};

var UserSuccess = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    secondaryColor = _a.secondaryColor,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: " 0 0 24 24"
  }, jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: secondaryColor && getIconColor(secondaryColor, entity),
    fill: getIconColor(primaryColor, entity),
    d: "M22 12C22 13.02 21.85 14.01 21.56 14.94C20.77 13.21 19.03 12 17 12C14.24 12 12 14.24 12 17C12 19.03 13.21 20.77 14.94 21.56C14.01 21.85 13.02 22 12 22C6.48 22 2 17.52 2 12C2 6.48 6.48 2 12 2C17.52 2 22 6.48 22 12Z"
  }), jsx("path", {
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: secondaryColor && getIconColor(secondaryColor, entity),
    fill: getIconColor(primaryColor, entity),
    d: "M14.93 21.55C13.2 20.76 12 19.02 12 17C12 15.9 12.35 14.89 12.96 14.06C12.64 14.02 12.32 14 12 14C8.71998 14 5.90998 15.97 4.66998 18.79C6.49998 20.76 9.09998 22 12 22C13.02 22 14 21.85 14.93 21.55Z"
  }), jsx("path", {
    fill: primaryColor,
    d: "M13.12 13.84H13.13C13.68 13.16 14.42 12.62 15.26 12.31C15.73 11.65 16 10.86 16 10C16 7.79 14.21 6 12 6C9.79 6 8 7.79 8 10C8 12.21 9.79 14 12 14C12.39 14 12.77 13.94 13.13 13.84"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: secondaryColor && getIconColor(secondaryColor, entity),
    fill: getIconColor(primaryColor, entity),
    d: "M13.12 13.84H13.13C13.68 13.16 14.42 12.62 15.26 12.31C15.73 11.65 16 10.86 16 10C16 7.79 14.21 6 12 6C9.79 6 8 7.79 8 10C8 12.21 9.79 14 12 14C12.39 14 12.77 13.94 13.13 13.84"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: secondaryColor && getIconColor(secondaryColor, entity),
    fill: getIconColor(primaryColor, entity),
    d: "M17 22C19.7614 22 22 19.7614 22 17C22 14.2386 19.7614 12 17 12C14.2386 12 12 14.2386 12 17C12 19.7614 14.2386 22 17 22Z"
  }), jsx("path", {
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(secondaryColor !== null && secondaryColor !== void 0 ? secondaryColor : primaryColor, entity),
    fill: "transparent",
    d: "M14.6 16.35L16.4 18.15L19.4 15.15"
  }));
};

var Image$2 = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    d: "M20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20Z",
    clipRule: "evenodd",
    fillRule: "evenodd"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    d: "M22 15L17 9L11 17L6 13L2 16V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V15Z",
    clipRule: "evenodd",
    fillRule: "evenodd"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    d: "M7 9C7.55228 9 8 8.55228 8 8C8 7.44772 7.55228 7 7 7C6.44772 7 6 7.44772 6 8C6 8.55228 6.44772 9 7 9Z"
  }));
};

var Text$1 = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: " 0 0 24 24",
    fill: "none"
  }, jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M16 22H8H12V2"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M2 6V2H22V6"
  }));
};

var Color = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    d: "M17.94 13.89C16.71 14.59 14.41 15.88 12.01 13.84C9.08 11.37 7.29 13.05 6.09 13.78C5.5 17.4 8.15 21 12 21C15.81 21 18.45 17.47 17.94 13.89Z"
  }), ' ', jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    d: "M17.2399 11.75L12.8199 3.51C12.4599 2.83 11.5299 2.83 11.1699 3.51L6.74995 11.75C6.39995 12.41 6.17995 13.09 6.06995 13.78C7.27995 13.05 9.06995 11.37 11.9899 13.84C14.3899 15.88 16.6999 14.59 17.9199 13.89C17.8399 13.17 17.6099 12.45 17.2399 11.75Z"
  }));
};

var Template = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: " 0 0 24 24"
  }, jsx("rect", {
    strokeLinecap: "round",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    transform: "rotate(-90 2 22)",
    rx: "2",
    height: "20",
    width: "20",
    y: "22",
    x: "2"
  }), jsx("path", {
    strokeLinecap: "round",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    d: "M12 13L12 2L20 2C21.1046 2 22 2.89543 22 4L22 13L12 13Z"
  }), jsx("path", {
    strokeLinecap: "round",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    d: "M15 22L15 13L22 13L22 20C22 21.1046 21.1046 22 20 22L15 22Z"
  }), jsx("rect", {
    strokeLinecap: "round",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    transform: "rotate(-90 9 22)",
    height: "6",
    width: "9",
    y: "22",
    x: "9"
  }), jsx("path", {
    strokeLinecap: "round",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    d: "M4 22C2.89543 22 2 21.1046 2 20L2 13L9 13L9 22L4 22Z"
  }), jsx("rect", {
    strokeLinecap: "round",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    transform: "rotate(-90 2 13)",
    height: "10",
    width: "5",
    y: "13",
    x: "2"
  }));
};

var Redo = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: " 0 0 24 24",
    fill: "none"
  }, jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M4.88 22C3.73 20.56 3 18.74 3 16.73C3 11.93 7.29 8 12.52 8H21"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M15 2L21 8L15 14"
  }));
};

var Undo = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: " 0 0 24 24",
    fill: "none"
  }, jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1.5",
    stroke: getIconColor(primaryColor, entity),
    d: "M19.12 22C20.27 20.56 21 18.74 21 16.73C21 11.93 16.71 8 11.48 8H3"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1.5",
    stroke: getIconColor(primaryColor, entity),
    d: "M9 2L3 8L9 14"
  }));
};

var Copy = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    viewBox: " 0 0 24 24",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    onClick: onClick && function () {
      return onClick();
    }
  }, jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    d: "M8 6V3C8 2.45 8.45 2 9 2H20C20.55 2 21 2.45 21 3V17C21 17.55 20.55 18 20 18H16V7C16 6.45 15.55 6 15 6H8Z",
    clipRule: "evenodd",
    fillRule: "evenodd"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    d: "M15 22H4C3.45 22 3 21.55 3 21V7C3 6.45 3.45 6 4 6H15C15.55 6 16 6.45 16 7V21C16 21.55 15.55 22 15 22Z",
    clipRule: "evenodd",
    fillRule: "evenodd"
  }));
};

var Drag = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    viewBox: " 0 0 24 24",
    fill: "none",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    onClick: onClick && function () {
      return onClick();
    }
  }, jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M22 12H2"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M19 9L22 12L19 15"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M5 9L2 12L5 15"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M12 22V2"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M15 19L12 22L9 19"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M15 5L12 2L9 5"
  }));
};

var Delete = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    viewBox: " 0 0 24 24",
    fill: "none",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    onClick: onClick && function () {
      return onClick();
    }
  }, jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    d: "M16.13 22H7.87C7.37 22 6.95 21.63 6.88 21.14L5 8H19L17.12 21.14C17.05 21.63 16.63 22 16.13 22Z"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M3.5 8H20.5"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M10 12V18"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M14 12V18"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    d: "M16 5H8L9.7 2.45C9.89 2.17 10.2 2 10.54 2H13.47C13.8 2 14.12 2.17 14.3 2.45L16 5Z"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M3 5H21"
  }));
};

var Crop = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    fill: 'none',
    viewBox: " 0 0 24 24"
  }, jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M2 6H6V2"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M22 18H18V22"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    d: "M18 6H6V18H18V6Z"
  }));
};

var AlignCenter = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: " 0 0 24 24"
  }, jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M17 18H7"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M20 14H4"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M17 10H7"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M20 6H4"
  }));
};

var AlignLeft = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: " 0 0 24 24"
  }, jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M4 18H14"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M4 14H20"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M4 10H14"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M4 6H20"
  }));
};

var AlignRight = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: " 0 0 24 24"
  }, jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M20 18H10"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M20 14H4"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M20 10H10"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M20 6H4"
  }));
};

var FullBleed = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    onClick: onClick && function () {
      return onClick();
    },
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    fill: "none",
    viewBox: "0 0 24 24"
  }, jsx("path", {
    fill: getIconColor(primaryColor, entity),
    "fill-rule": "evenodd",
    d: "M0.25 6C0.25 4.48122 1.48122 3.25 3 3.25H9C10.2267 3.25 11.3158 3.83901 12 4.74964C12.6842 3.83901 13.7733 3.25 15 3.25H21C22.5188 3.25 23.75 4.48122 23.75 6V16C23.75 17.5188 22.5188 18.75 21 18.75H15C13.7574 18.75 12.75 19.7574 12.75 21H11.25C11.25 19.7574 10.2426 18.75 9 18.75H3C1.48122 18.75 0.25 17.5188 0.25 16V6ZM11.25 17.9997C10.6233 17.529 9.8442 17.25 9 17.25H3C2.30964 17.25 1.75 16.6904 1.75 16V6C1.75 5.30964 2.30964 4.75 3 4.75H9C10.2426 4.75 11.25 5.75736 11.25 7V17.9997ZM12.75 17.9997C13.3767 17.529 14.1558 17.25 15 17.25H21C21.6904 17.25 22.25 16.6904 22.25 16V6C22.25 5.30964 21.6904 4.75 21 4.75H15C13.7574 4.75 12.75 5.75736 12.75 7V17.9997Z",
    "clip-rule": "evenodd"
  }));
};

var FullBleedLandscape = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    onClick: onClick && function () {
      return onClick();
    }
  }, jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    d: "M2 20V4C4.47 4.99 8.03 5.63 12 5.63C15.97 5.63 19.53 4.99 22 4V20C19.53 19.01 15.97 18.37 12 18.37C8.03 18.37 4.47 19.01 2 20Z"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M5 12H19"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M7 14L5 12L7 10"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M17 14L19 12L17 10"
  }));
};

var FullBleedPortrait = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    onClick: onClick && function () {
      return onClick();
    }
  }, jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    d: "M20 22H4C4.99 19.53 5.63 15.97 5.63 12C5.63 8.03 4.99 4.47 4 2H20C19.01 4.47 18.37 8.03 18.37 12C18.37 15.97 19.01 19.53 20 22Z"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M12 19V5"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M14 17L12 19L10 17"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M14 7L12 5L10 7"
  }));
};

var SliderVertical = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: " 0 0 24 24"
  }, jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M12 14L12 2"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M12 22L12 18"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    d: "M10 16C10 17.1046 10.8954 18 12 18C13.1046 18 14 17.1046 14 16C14 14.8954 13.1046 14 12 14C10.8954 14 10 14.8954 10 16Z"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M19 22L19 10"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M19 6L19 2"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    d: "M17 8C17 9.10457 17.8954 10 19 10C20.1046 10 21 9.10457 21 8C21 6.89543 20.1046 6 19 6C17.8954 6 17 6.89543 17 8Z"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M5 22L5 10"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M5 6L5 2"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    d: "M3 8C3 9.10457 3.89543 10 5 10C6.10457 10 7 9.10457 7 8C7 6.89543 6.10457 6 5 6C3.89543 6 3 6.89543 3 8Z"
  }));
};

var Frame = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: " 0 0 24 24",
    fill: "none"
  }, jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M18 2V16C18 17.1 17.1 18 16 18H2"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M22 6H8C6.9 6 6 6.9 6 8V22"
  }));
};

var Cut = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: " 0 0 24 24",
    fill: "none"
  }, jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M22.0002 5L7.97021 15.03"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M7.97021 8.96997L22.0002 19"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    d: "M5.5 21C7.433 21 9 19.433 9 17.5C9 15.567 7.433 14 5.5 14C3.567 14 2 15.567 2 17.5C2 19.433 3.567 21 5.5 21Z"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    d: "M5.5 10C7.433 10 9 8.433 9 6.5C9 4.567 7.433 3 5.5 3C3.567 3 2 4.567 2 6.5C2 8.433 3.567 10 5.5 10Z"
  }));
};

var Wand = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: " 0 0 24 24",
    fill: "none"
  }, jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    d: "M21.49 21.49C20.81 22.17 19.7 22.17 19.02 21.49L9.24999 11.72C8.56999 11.04 8.56999 9.92999 9.24999 9.24999C9.92999 8.56999 11.04 8.56999 11.72 9.24999L21.49 19.02C22.17 19.7 22.17 20.81 21.49 21.49Z"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M9.25 9.25L13.84 13.84"
  }), jsx("path", {
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M11.6 3.1L11.94 3.21L12.22 3L12.21 3.36L12.5 3.56L12.17 3.67L12.06 4L11.86 3.71L11.5 3.72L11.71 3.44L11.6 3.1Z"
  }), jsx("path", {
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M3.1 11.6L3.44 11.71L3.72 11.5L3.71 11.86L4 12.06L3.67 12.17L3.56 12.5L3.36 12.21L3 12.22L3.21 11.94L3.1 11.6Z"
  }), jsx("path", {
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M3.7 3.7L4.37 3.93L4.94 3.5L4.93 4.21L5.5 4.62L4.83 4.83L4.62 5.5L4.21 4.93L3.5 4.94L3.93 4.37L3.7 3.7Z"
  }));
};

var LayerAdd = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: " 0 0 24 24",
    fill: "none"
  }, jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M22 12L12 18L2 12"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M22 16L12 22L2 16"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    d: "M22 8L12 14L2 8L12 2L22 8Z"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M9 8H15"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M12 5V11"
  }));
};

var LayerRemove = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: " 0 0 24 24",
    fill: "none"
  }, jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M22 12L12 18L2 12"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M22 16L12 22L2 16"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    d: "M22 8L12 14L2 8L12 2L22 8Z"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M9 8H15"
  }));
};

var LayerDisabled = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: " 0 0 24 24",
    fill: "none"
  }, jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M22 12L12 18L2 12"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M22 16L12 22L2 16"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    d: "M22 8L12 14L2 8L12 2L22 8Z"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M2 2L22 22"
  }));
};

var Signature = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: " 0 0 24 24",
    fill: "none"
  }, jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M2 15H8"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M18 15H22"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M20.5 12C20.1 8.49998 19.7 6.09998 19.1 6.09998C18.4 6.09998 18.4 9.49998 17.2 9.79998C15.3 10.2 12 1.89998 11.9 1.99998C11.8 2.09998 14.8 6.69998 14.9 12.5C15 17.7 12.9 22.1 12.2 22C11.2 21.8 12.4 10.3 11.3 10.1C10.8 9.99998 10.3 12.3 9.40002 12.2C8.80002 12.2 8.30002 10.9 7.90002 9.19998"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M2 9L5 12"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M5 9L2 12"
  }));
};

var Shadows = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: " 0 0 24 24",
    fill: "none"
  }, jsx("path", {
    strokeLinecap: "round",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    d: "M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M21.8 10H12"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M21.8 14H12"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M19.99 6H12"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M19.99 18H12"
  }));
};

var RotateLeft = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: " 0 0 24 24",
    fill: "none"
  }, jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    d: "M14 12H4V22H14V12Z"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M19 13C19.6 12.2 20 11.1 20 10C20 7.2 17.8 5 15 5H11"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M13.5 2L10.5 5L13.5 8"
  }));
};

var RotateRight = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: " 0 0 24 24",
    fill: "none"
  }, jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    d: "M10 12H20V22H10V12Z"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M5 13C4.4 12.2 4 11.1 4 10C4 7.2 6.2 5 9 5H13"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M10.5 2L13.5 5L10.5 8"
  }));
};

var ColorPalette = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: " 0 0 24 24",
    fill: "none"
  }, jsx("path", {
    strokeLinejoin: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    d: "M2.74 15.61C2.55 15.14 2.39 14.66 2.28 14.16C1.62 11.37 2.13 8.35999 3.76 5.98999C5.41 3.59999 8.08 2.35999 10.93 2.06999C12.75 1.87999 14.63 2.08999 16.35 2.71999C18.52 3.51999 20.5 5.04999 21.49 7.16999C22.18 8.64999 22.27 10.6 21.13 11.76C20.39 12.52 19.29 12.77 18.41 13.35C17.39 14.01 16.66 15.12 16.46 16.33C16.32 17.15 16.4 18.01 16.2 18.82C15.53 21.52 12.23 22.39 9.82 21.85C7.06 21.22 4.65 19.13 3.27 16.7C3.07 16.34 2.9 15.98 2.74 15.61Z",
    clipRule: "evenodd",
    fillRule: "evenodd"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    d: "M13 7C13.5523 7 14 6.55228 14 6C14 5.44772 13.5523 5 13 5C12.4477 5 12 5.44772 12 6C12 6.55228 12.4477 7 13 7Z"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    d: "M18 10C18.5523 10 19 9.55228 19 9C19 8.44772 18.5523 8 18 8C17.4477 8 17 8.44772 17 9C17 9.55228 17.4477 10 18 10Z"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    d: "M8 9C8.55228 9 9 8.55228 9 8C9 7.44772 8.55228 7 8 7C7.44772 7 7 7.44772 7 8C7 8.55228 7.44772 9 8 9Z"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    d: "M6 14C6.55228 14 7 13.5523 7 13C7 12.4477 6.55228 12 6 12C5.44772 12 5 12.4477 5 13C5 13.5523 5.44772 14 6 14Z"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    d: "M11 19C12.1046 19 13 18.3284 13 17.5C13 16.6716 12.1046 16 11 16C9.89543 16 9 16.6716 9 17.5C9 18.3284 9.89543 19 11 19Z"
  }));
};

var Dropper = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: " 0 0 24 24"
  }, jsx("path", {
    d: "M16.9697 1.96973C18.3671 0.572265 20.6329 0.572265 22.0303 1.96973C23.4278 3.36719 23.4278 5.63292 22.0303 7.03039L20.0607 9.00006L21.0303 9.96973C21.3232 10.2626 21.3232 10.7375 21.0303 11.0304C20.7374 11.3233 20.2626 11.3233 19.9697 11.0304L19 10.0607L7.11612 21.9446C6.60039 22.4603 5.90092 22.7501 5.17157 22.7501H2C1.58579 22.7501 1.25 22.4143 1.25 22.0001V18.8285C1.25 18.0991 1.53973 17.3997 2.05546 16.8839L13.9393 5.00006L12.9697 4.03039C12.6768 3.73749 12.6768 3.26262 12.9697 2.96973C13.2626 2.67683 13.7374 2.67683 14.0303 2.96973L15 3.9394L16.9697 1.96973ZM15 6.06072L3.11612 17.9446C2.8817 18.179 2.75 18.497 2.75 18.8285V21.2501H5.17157C5.50309 21.2501 5.82104 21.1184 6.05546 20.8839L17.9393 9.00006L15 6.06072ZM19 7.9394L16.0607 5.00006L18.0303 3.03039C18.842 2.21871 20.158 2.21871 20.9697 3.03039C21.7813 3.84206 21.7813 5.15805 20.9697 5.96973L19 7.9394Z",
    fill: getIconColor(primaryColor, entity),
    fillRule: "evenodd"
  }));
};

var TextBox = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: " 0 0 24 24",
    fill: "none"
  }, jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M22 16.4V20H2V16.4"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M2 9.1V4H22V9.1"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M12 8V16"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M8 8H16"
  }));
};

var FontSize = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: " 0 0 24 24",
    fill: "none"
  }, jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M19.5 22H16.5H18V12"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M14 13.5V12H22V13.5"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M13 22H7H10V2"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M2 5V2H18V5"
  }));
};

var FlipHorizontal = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: " 0 0 24 24",
    fill: "none"
  }, jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M2 5H22"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M5 2L2 5L5 8"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M19 8L22 5L19 2"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    d: "M10 10V22H2L10 10Z"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    d: "M14 10V22H22L14 10Z"
  }));
};

var FlipVertical = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: " 0 0 24 24",
    fill: "none"
  }, jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M19 2V22"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M22 5L19 2L16 5"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M16 19L19 22L22 19"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    d: "M14 10H2V2L14 10Z"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    d: "M14 14H2V22L14 14Z"
  }));
};

var Filter = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: " 0 0 24 24",
    fill: "none"
  }, jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    d: "M12 2C9.20459 2 6.6759 3.14892 4.86068 5C3.09145 6.80419 2 9.27541 2 12C2 16.11 4.48 19.64 8.03 21.17V21.18H8.04L12.2197 18.6716L21.97 12.82C21.99 12.55 22 12.28 22 12C22 8.608 20.3083 5.60871 17.7232 3.80031C16.1014 2.6658 14.128 2 12 2Z"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    d: "M8.03003 21.18C8.22609 18.7806 9.26981 16.6209 10.8601 15C12.6766 13.1486 15.2061 12 18 12C19.41 12 20.75 12.29 21.97 12.82L17.5 15.5L10.5 19.6987L8.03003 21.18Z"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M3.1001 7.46002L8.6701 18.4"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M5.87012 4.09998L11.2401 14.64"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M9.44995 2.33002L14.67 12.57"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M13.8701 2.17999L18.9001 12.04"
  }));
};

var Brightness = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: " 0 0 24 24",
    fill: "none"
  }, jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    d: "M12 17C14.7614 17 17 14.7614 17 12C17 9.23858 14.7614 7 12 7C9.23858 7 7 9.23858 7 12C7 14.7614 9.23858 17 12 17Z"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M12 4V2"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M12 22V20"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M20 12L22 12"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M2 12L4 12"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M17.6568 6.34317L19.071 4.92896"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M4.92893 19.0711L6.34314 17.6569"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M6.34317 6.34317L4.92896 4.92896"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M19.0711 19.0711L17.6569 17.6569"
  }));
};

var AText = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: " 0 0 24 24",
    fill: "none"
  }, jsx("path", {
    fill: "none",
    d: "M18.3 16H5.7L3 22L12 2L21 22",
    stroke: getIconColor(primaryColor, entity)
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M18.3 16H5.7L3 22L12 2L21 22"
  }));
};

var FrameWidth = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: " 0 0 24 24",
    fill: "none"
  }, jsx("rect", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    transform: "matrix(1 0 0 -1 2 20)",
    rx: "2",
    height: "16",
    width: "20"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M15 12L9 12"
  }), jsx("path", {
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M15 11L15 12L15 13L16 12L15 11Z"
  }), jsx("path", {
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M9 13L9 12L9 11L8 12L9 13Z"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M5 10L5 8C5 7.44772 5.44772 7 6 7H8"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M19 14L19 16C19 16.5523 18.5526 17 18.0003 17C17.4553 17 16.7416 17 16 17"
  }));
};

var FrameHeight = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: " 0 0 24 24",
    fill: "none"
  }, jsx("rect", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    transform: "matrix(1 0 0 -1 2 20)",
    rx: "2",
    height: "16",
    width: "20"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M12 9V15"
  }), jsx("path", {
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M11 9H12H13L12 8L11 9Z"
  }), jsx("path", {
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M13 15L12 15L11 15L12 16L13 15Z"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M5 10L5 8C5 7.44772 5.44772 7 6 7H8"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M19 14L19 16C19 16.5523 18.5526 17 18.0003 17C17.4553 17 16.7416 17 16 17"
  }));
};

var CropRotate = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: " 0 0 24 24",
    fill: "none"
  }, jsx("path", {
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M5.00006 8H8.00006V5"
  }), jsx("path", {
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M19.0001 16H16.0001V19"
  }), jsx("path", {
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    d: "M16.0001 16H9.00006C8.45006 16 8.00006 15.55 8.00006 15V8H15.0001C15.5501 8 16.0001 8.45 16.0001 9V16Z"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M13.0001 3H17.0001C18.6601 3 20.0001 4.34 20.0001 6V7"
  }), jsx("path", {
    strokeLinecap: "round",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M19.41 7.00999L20 7.58999L20.59 7.00999H19.41Z"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M11.0001 21H7.00006C5.34006 21 4.00006 19.66 4.00006 18V17"
  }), jsx("path", {
    strokeLinecap: "round",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M4.59003 16.99L4.00003 16.41L3.41003 16.99H4.59003Z"
  }));
};

var LayerUp = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: " 0 0 24 24",
    fill: "none"
  }, jsx("path", {
    d: "M 22 11 L 12 17 L 2 11 L 12 5 Z M 19.5 12.5 L 22 14 L 12 20 L 2 14 L 4.5 12.5",
    fill: "transparent",
    stroke: getIconColor(primaryColor, entity),
    strokeLinecap: "round",
    strokeLinejoin: "round",
    strokeDasharray: ""
  }), jsx("path", {
    d: "M 12.357 9.65 L 12.357 19.65 M 9.357 12.983 L 12.357 9.65 L 15.357 12.983",
    fill: "transparent",
    stroke: getIconColor(primaryColor, entity),
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }));
};

var LayerDown = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: " 0 0 24 24",
    fill: "none"
  }, jsx("path", {
    d: "M 22 11 L 12 17 L 2 11 L 12 5 Z M 22 14 L 19.5 12.5 M 22 14 L 12 20 L 2 14 L 4.5 12.5",
    fill: "transparent",
    stroke: getIconColor(primaryColor, entity),
    strokeLinecap: "round",
    strokeLinejoin: "round",
    strokeDasharray: ""
  }), jsx("path", {
    d: "M 3 0 L 3 8 M 0 2.667 L 3 0 L 6 2.667",
    transform: "translate(9.357 5.65) rotate(180 3 4)",
    fill: "transparent",
    stroke: getIconColor(primaryColor, entity),
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }));
};

var CapitalizedText = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: "0 0 32 32",
    onClick: onClick && function () {
      return onClick();
    }
  }, jsx("path", {
    fill: getIconColor(primaryColor, entity),
    fillRule: "evenodd",
    d: "M18.031 16v-2a1 1 0 0 1 1-1h.005a1 1 0 0 1 1 1v2h2.008a1 1 0 1 1 0 2h-2.008v1.833C20.576 21.36 21.243 22 22.041 22c.825 0 1.508-.682 2.058-2.317l1.901.634C25.206 22.682 23.885 24 22.041 24c-1.843 0-3.164-1.318-3.958-3.682l-.052-.155V18h-1.005a1 1 0 1 1 0-2h1.005zm-6.015 6V9h-4.01v1a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V9c0-.182.049-.353.134-.5A1 1 0 0 1 7 7h12.036a1 1 0 0 1 .867 1.5.995.995 0 0 1 .133.5v1a1 1 0 0 1-1 1h-.005a1 1 0 0 1-1-1V9h-4.01v13h2.008a1 1 0 1 1 0 2h-6.021a1 1 0 0 1 0-2h2.008z"
  }));
};

var UppercaseText = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: "0 0 32 32",
    onClick: onClick && function () {
      return onClick();
    }
  }, jsx("path", {
    fill: getIconColor(primaryColor, entity),
    fillRule: "evenodd",
    d: "M22 22h2a1 1 0 0 1 0 2h-6a1 1 0 0 1 0-2h2V9h-1a1 1 0 0 1 0-2h7a1 1 0 0 1 .866 1.5A.995.995 0 0 1 27 9v1a1 1 0 0 1-2 0V9h-3v13zm-12 0V9H7v1a1 1 0 0 1-2 0V9c0-.182.049-.353.134-.5A1 1 0 0 1 6 7h10a1 1 0 0 1 .866 1.5A.995.995 0 0 1 17 9v1a1 1 0 0 1-2 0V9h-3v13h2a1 1 0 0 1 0 2H8a1 1 0 0 1 0-2h2z"
  }));
};

var LowercaseText = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: "0 0 32 32",
    onClick: onClick && function () {
      return onClick();
    }
  }, jsx("path", {
    fill: getIconColor(primaryColor, entity),
    fillRule: "evenodd",
    d: "M9 14v-3a1 1 0 0 1 2 0v3h2a1 1 0 0 1 0 2h-2v2.833C11.539 20.36 12.203 21 13 21c.822 0 1.504-.682 2.052-2.317.885 1.372 1.204 2.238.96 2.597C15.245 22.399 14.241 23 13 23c-1.838 0-3.157-1.318-3.948-3.682L9 19.163V16H8a1 1 0 0 1 0-2h1zm8 0v-3a1 1 0 0 1 2 0v3h2a1 1 0 0 1 0 2h-2v2.833C19.539 20.36 20.203 21 21 21c.822 0 1.504-.682 2.052-2.317l1.896.634C24.157 21.682 22.838 23 21 23s-3.157-1.318-3.948-3.682L17 19.163V16h-1a1 1 0 0 1 0-2h1z"
  }));
};

var FullBleedImage = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    onClick: onClick && function () {
      return onClick();
    },
    viewBox: " 0 0 24 24"
  }, jsx("path", {
    d: "M 13.208 15.292 L 16.458 15.292 M 14.833 13.667 L 14.833 16.917 M 15.917 12.583 L 15.917 7.708 C 15.917 7.113 15.429 6.625 14.833 6.625 L 6.167 6.625 C 5.571 6.625 5.083 7.113 5.083 7.708 L 5.083 14.208 C 5.083 14.804 5.571 15.292 6.167 15.292 L 11.583 15.292 M 15.917 12.583 L 15.917 7.708 C 15.917 7.113 15.429 6.625 14.833 6.625 L 6.167 6.625 C 5.571 6.625 5.083 7.113 5.083 7.708 L 5.083 14.208 C 5.083 14.804 5.571 15.292 6.167 15.292 L 11.583 15.292 M 15.917 12.583 L 13.208 9.333 L 9.958 13.667 L 7.25 11.5 L 5.083 13.125 L 5.083 14.208 C 5.083 14.804 5.571 15.292 6.167 15.292 L 11.583 15.292 M 15.917 12.583 L 13.208 9.333 L 9.958 13.667 L 7.25 11.5 L 5.083 13.125 L 5.083 14.208 C 5.083 14.804 5.571 15.292 6.167 15.292 L 11.583 15.292 M 7.75 9.25 C 8.026 9.25 8.25 9.026 8.25 8.75 C 8.25 8.474 8.026 8.25 7.75 8.25 C 7.474 8.25 7.25 8.474 7.25 8.75 C 7.25 9.026 7.474 9.25 7.75 9.25 Z",
    fill: "transparent",
    strokeWidth: "0.8",
    stroke: getIconColor(primaryColor, entity),
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), jsx("path", {
    d: "M 21 7 L 21 1 L 15 1 M 7 1 L 1 1 L 1 7 M 1 15 L 1 21 L 7 21 M 15 21 L 21 21 L 21 15",
    fill: "transparent",
    stroke: getIconColor(primaryColor, entity),
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }));
};

var FullBleedText = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    onClick: onClick && function () {
      return onClick();
    },
    viewBox: " 0 0 24 24"
  }, jsx("path", {
    d: "M 21 7 L 21 1 L 15 1 M 7 1 L 1 1 L 1 7 M 1 15 L 1 21 L 7 21 M 15 21 L 21 21 L 21 15",
    fill: "transparent",
    stroke: getIconColor(primaryColor, entity),
    strokeLinecap: "round",
    strokeLinejoin: "round",
    strokeDasharray: ""
  }), jsx("path", {
    d: "M 14.333 4.5 L 14.333 17.5 M 8.333 8.4 L 8.333 14.9 M 11.667 4.5 L 17 4.5 M 5 8.4 L 11.667 8.4 M 11.667 17.5 L 17 17.5",
    fill: "transparent",
    stroke: getIconColor(primaryColor, entity),
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }));
};

var PanelMode = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    onClick: onClick && function () {
      return onClick();
    },
    viewBox: " 0 0 24 24"
  }, jsx("path", {
    d: "M 8 7 L 16 7 L 16 17 L 8 17 Z M 19 7 L 20 7 C 21.105 7 22 7.895 22 9 L 22 15 C 22 16.105 21.105 17 20 17 L 19 17 Z M 2 9 C 2 8.448 2.141 7.948 2.461 7.586 C 2.781 7.224 3.281 7 4 7 L 5 7 L 5 17 L 4 17 C 3.448 17 2.948 16.776 2.586 16.414 C 2.224 16.052 2 15.552 2 15 Z",
    fill: "transparent",
    stroke: getIconColor(primaryColor, entity),
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }));
};

var ViewPanels = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    onClick: onClick && function () {
      return onClick();
    },
    viewBox: " 0 0 24 24"
  }, jsx("path", {
    d: "M 8 6 L 8 3 C 8 2.45 8.45 2 9 2 L 20 2 C 20.55 2 21 2.45 21 3 L 21 17 C 21 17.55 20.55 18 20 18 L 16 18 L 16 7 C 16 6.45 15.55 6 15 6 Z",
    fill: "transparent",
    stroke: getIconColor(primaryColor, entity),
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), jsx("path", {
    d: "M 15 22 L 4 22 C 3.45 22 3 21.55 3 21 L 3 7 C 3 6.45 3.45 6 4 6 L 15 6 C 15.55 6 16 6.45 16 7 L 16 21 C 16 21.55 15.55 22 15 22 Z",
    fill: "transparent",
    stroke: getIconColor(primaryColor, entity),
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }));
};

var Rotate = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    onClick: onClick && function () {
      return onClick();
    },
    viewBox: " 0 0 24 24"
  }, jsx("path", {
    d: "M 16 9 L 21 9 L 21 4",
    fill: "transparent",
    stroke: getIconColor(primaryColor, entity),
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), jsx("path", {
    d: "M 3.22 10 C 3.826 7.383 5.562 5.17 7.96 3.96 C 9.17 3.35 10.55 3 12 3 C 15.92 3 18.5 5.44 21 9 M 8 15 L 3 15 L 3 20",
    fill: "transparent",
    stroke: getIconColor(primaryColor, entity),
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), jsx("path", {
    d: "M 20.78 14 C 20.174 16.617 18.438 18.83 16.04 20.04 C 14.83 20.65 13.45 21 12 21 C 8.08 21 5.5 18.56 3 15",
    fill: "transparent",
    stroke: getIconColor(primaryColor, entity),
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }));
};

var View = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    onClick: onClick && function () {
      return onClick();
    },
    viewBox: " 0 0 24 24"
  }, jsx("path", {
    d: "M 12 18 C 6.48 18 2 12 2 12 C 2 12 6.48 6 12 6 C 17.52 6 22 12 22 12 C 22 12 17.52 18 12 18 Z",
    fill: "transparent",
    stroke: getIconColor(primaryColor, entity),
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), jsx("path", {
    d: "M 12 14.5 C 13.381 14.5 14.5 13.381 14.5 12 C 14.5 10.619 13.381 9.5 12 9.5 C 10.619 9.5 9.5 10.619 9.5 12 C 9.5 13.381 10.619 14.5 12 14.5 Z",
    fill: "transparent",
    stroke: getIconColor(primaryColor, entity),
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }));
};

var ReplaceImage = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    onClick: onClick && function () {
      return onClick();
    },
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: " 0 0 24 24",
    fill: "none"
  }, jsx("path", {
    d: "M 22 8 L 2 8 L 2 22 L 22 22 Z",
    fill: "transparent",
    stroke: getIconColor(primaryColor, entity),
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), jsx("path", {
    d: "M 8 2 L 16 2 M 5 5 L 19 5",
    fill: "transparent",
    stroke: getIconColor(primaryColor, entity),
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), jsx("path", {
    d: "M 13.778 13.667 L 16 13.667 L 16 11.444",
    fill: "transparent",
    stroke: getIconColor(primaryColor, entity),
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), jsx("path", {
    d: "M 8.098 14.111 C 8.367 12.948 9.139 11.965 10.204 11.427 C 10.742 11.156 11.356 11 12 11 C 13.742 11 14.889 12.084 16 13.667 M 10.222 16.333 L 8 16.333 L 8 18.556",
    fill: "transparent",
    stroke: getIconColor(primaryColor, entity),
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), jsx("path", {
    d: "M 15.902 15.889 C 15.633 17.052 14.861 18.035 13.796 18.573 C 13.258 18.844 12.644 19 12 19 C 10.258 19 9.111 17.916 8 16.333",
    fill: "transparent",
    stroke: getIconColor(primaryColor, entity),
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }));
};

var Quotation = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: " 0 0 24 24",
    fill: "none"
  }, jsx("path", {
    d: "M7.85994 7.39708C8.54653 7.13208 9.26874 7 10 7C10.5523 7 11 7.44772 11 8C11 8.55228 10.5523 9 10 9C9.5123 9 9.03548 9.08756 8.58291 9.26184C7.06285 9.86062 5.97501 11.6256 5.99992 13.3859L6.00002 13.4C6.00002 13.5563 6.01393 13.7064 6.04389 13.8711C6.14268 14.4145 5.78228 14.9351 5.2389 15.0339C4.69553 15.1327 4.17494 14.7723 4.07615 14.2289C4.02653 13.956 4.00046 13.6883 4.00002 13.4072C3.96784 10.9306 5.45812 8.34063 7.85436 7.39925L7.85994 7.39706L7.85994 7.39708Z",
    "clip-rule": "evenodd",
    "fill-rule": "evenodd",
    fill: getIconColor(primaryColor, entity)
  }), jsx("path", {
    d: "M7.5 16C8.88071 16 10 14.8807 10 13.5C10 12.1193 8.88071 11 7.5 11C6.11929 11 5 12.1193 5 13.5C5 14.8807 6.11929 16 7.5 16Z",
    fill: getIconColor(primaryColor, entity)
  }), jsx("path", {
    d: "M7.5 12C6.67157 12 6 12.6716 6 13.5C6 14.3284 6.67157 15 7.5 15C8.32843 15 9 14.3284 9 13.5C9 12.6716 8.32843 12 7.5 12ZM4 13.5C4 11.567 5.567 10 7.5 10C9.433 10 11 11.567 11 13.5C11 15.433 9.433 17 7.5 17C5.567 17 4 15.433 4 13.5Z",
    "clip-rule": "evenodd",
    "fill-rule": "evenodd",
    fill: getIconColor(primaryColor, entity)
  }), jsx("path", {
    d: "M16.8599 7.39708C17.5465 7.13208 18.2687 7 19 7C19.5523 7 20 7.44772 20 8C20 8.55228 19.5523 9 19 9C18.5123 9 18.0355 9.08756 17.5829 9.26184C16.0628 9.86062 14.975 11.6256 14.9999 13.3859L15 13.4C15 13.5563 15.0139 13.7064 15.0439 13.8711C15.1427 14.4145 14.7823 14.9351 14.2389 15.0339C13.6955 15.1327 13.1749 14.7723 13.0761 14.2289C13.0265 13.956 13.0005 13.6883 13 13.4072C12.9678 10.9306 14.4581 8.34063 16.8544 7.39925L16.8599 7.39706L16.8599 7.39708Z",
    "clip-rule": "evenodd",
    "fill-rule": "evenodd",
    fill: getIconColor(primaryColor, entity)
  }), jsx("path", {
    d: "M16.5 16C17.8807 16 19 14.8807 19 13.5C19 12.1193 17.8807 11 16.5 11C15.1193 11 14 12.1193 14 13.5C14 14.8807 15.1193 16 16.5 16Z",
    fill: getIconColor(primaryColor, entity)
  }), jsx("path", {
    d: "M16.5 12C15.6716 12 15 12.6716 15 13.5C15 14.3284 15.6716 15 16.5 15C17.3284 15 18 14.3284 18 13.5C18 12.6716 17.3284 12 16.5 12ZM13 13.5C13 11.567 14.567 10 16.5 10C18.433 10 20 11.567 20 13.5C20 15.433 18.433 17 16.5 17C14.567 17 13 15.433 13 13.5Z",
    "clip-rule": "evenodd",
    "fill-rule": "evenodd",
    fill: getIconColor(primaryColor, entity)
  }));
};

var Facebook = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    onClick: onClick && function () {
      return onClick();
    }
  }, ' ', jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    d: "M19.34 22H4.66C3.19 22 2 20.81 2 19.34V4.66C2 3.19 3.19 2 4.66 2H19.33C20.81 2 22 3.19 22 4.66V19.33C22 20.81 20.81 22 19.34 22Z"
  }), ' ', jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M12 18V8.34999C12 6.29999 13.64 5.65999 15.28 6.15999"
  }), ' ', jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M9.5 11H14.5"
  }), ' ');
};

var Instagram = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    onClick: onClick && function () {
      return onClick();
    }
  }, ' ', jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    d: "M19.34 22H4.66C3.19 22 2 20.81 2 19.34V4.66C2 3.19 3.19 2 4.66 2H19.33C20.81 2 22 3.19 22 4.66V19.33C22 20.81 20.81 22 19.34 22Z"
  }), ' ', jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    d: "M16 12C16 14.21 14.21 16 12 16C9.79 16 8 14.21 8 12C8 9.79 9.79 8 12 8C14.21 8 16 9.79 16 12Z"
  }), ' ', jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    d: "M19 5.5C19 5.78 18.78 6 18.5 6C18.22 6 18 5.78 18 5.5C18 5.22 18.22 5 18.5 5C18.78 5 19 5.22 19 5.5Z"
  }), ' ');
};

var Twitter = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    onClick: onClick && function () {
      return onClick();
    }
  }, ' ', jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    d: "M22 5.89C21.26 6.21 20.46 6.42 19.64 6.53C20.49 6.03 21.14 5.24 21.44 4.3C20.65 4.77 19.77 5.09 18.84 5.28C18.09 4.49 17.02 4 15.85 4C13.58 4 11.75 5.81 11.75 8.04C11.75 8.36 11.78 8.67 11.84 8.96C8.43999 8.79 5.41999 7.19 3.38999 4.74C3.03999 5.34 2.82999 6.03 2.82999 6.78C2.82999 8.18 3.55999 9.42 4.64999 10.13C3.98999 10.12 3.33999 9.93 2.79999 9.63V9.67C2.79999 11.63 4.21999 13.26 6.07999 13.64C5.74999 13.73 5.37999 13.77 4.99999 13.77C4.73999 13.77 4.46999 13.76 4.21999 13.7C4.74999 15.3 6.25999 16.48 8.04999 16.51C6.65999 17.59 4.87999 18.23 2.96999 18.23C2.62999 18.23 2.30999 18.21 1.98999 18.17C3.81999 19.34 5.96999 20 8.28999 20C15.84 20 19.96 13.85 19.96 8.51C19.96 8.33 19.95 8.16 19.94 7.99C20.76 7.42 21.44 6.71 22 5.89Z"
  }), ' ');
};

var LinkedIn = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    onClick: onClick && function () {
      return onClick();
    }
  }, ' ', jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    d: "M19.34 22H4.66C3.19 22 2 20.81 2 19.34V4.66C2 3.19 3.19 2 4.66 2H19.33C20.81 2 22 3.19 22 4.66V19.33C22 20.81 20.81 22 19.34 22Z"
  }), ' ', jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M7 10V17"
  }), ' ', jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M17 17C17 17 17 12.45 17 12.44C17 11.87 17.03 11.21 16.76 10.69C16.16 9.51 14.57 10.11 13.72 10.6C12.84 11.1 12.04 11.74 11.28 12.4C11.19 12.48 11.09 12.57 11 12.65V10.01V17"
  }), ' ', jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    d: "M7 7C7.27614 7 7.5 6.77614 7.5 6.5C7.5 6.22386 7.27614 6 7 6C6.72386 6 6.5 6.22386 6.5 6.5C6.5 6.77614 6.72386 7 7 7Z"
  }), ' ');
};

var YouTube = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    onClick: onClick && function () {
      return onClick();
    }
  }, ' ', jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    d: "M19.57 20H4.43C3.09 20 2 18.87 2 17.46V6.53C2 5.13 3.09 4 4.43 4H19.58C20.91 4 22 5.13 22 6.53V17.46C22 18.87 20.91 20 19.57 20Z"
  }), ' ', jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    d: "M9 8V16L16 11.56L9 8Z"
  }), ' ');
};

var Pinterest = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    onClick: onClick && function () {
      return onClick();
    }
  }, ' ', jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    d: "M19.34 22H4.66C3.19 22 2 20.81 2 19.34V4.66C2 3.19 3.19 2 4.66 2H19.33C20.81 2 22 3.19 22 4.66V19.33C22 20.81 20.81 22 19.34 22Z"
  }), ' ', jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    d: "M8.83001 18L9.73001 15.13C9.73001 15.13 10.07 14.23 10.44 13.14C9.64001 12.01 9.47001 10.5 10.07 9.72C10.4 9.3 11.03 8.99 11.31 9.19C11.62 9.4 10.99 11.5 10.44 13.14C10.74 13.57 11.13 13.94 11.6 14.2C12.89 14.9 14.47 14.52 15.49 13.75C17.24 12.45 17.01 10.22 16.97 9.91C16.72 7.82 15.02 6.75 14.8 6.61C14.18 6.22 13.29 6 12.32 6C10.74 6 8.96001 6.59 7.91001 8.04C6.78001 9.59 6.99001 11.41 7.07001 11.93"
  }), ' ');
};

var Visible = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx(React.Fragment, null, jsx("svg", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    onClick: onClick && function () {
      return onClick();
    },
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    "data-reactroot": ""
  }, jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    d: "M12 18C6.48 18 2 12 2 12C2 12 6.48 6 12 6C17.52 6 22 12 22 12C22 12 17.52 18 12 18Z"
  }), jsx("path", {
    strokeLinejoin: "round",
    strokeLinecap: "round",
    strokeMiterlimit: "10",
    strokeWidth: "1",
    stroke: getIconColor(primaryColor, entity),
    fill: "none",
    d: "M12 14.5C13.3807 14.5 14.5 13.3807 14.5 12C14.5 10.6193 13.3807 9.5 12 9.5C10.6193 9.5 9.5 10.6193 9.5 12C9.5 13.3807 10.6193 14.5 12 14.5Z"
  })));
};

var Invisible = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx(React.Fragment, null, jsx("svg", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    onClick: onClick && function () {
      return onClick();
    },
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    "data-Reactroot": ""
  }, jsx("path", {
    stroke: getIconColor(primaryColor, entity),
    strokeLinecap: "round",
    strokeLinejoin: "round",
    strokeMiterlimit: "10",
    "stroke-width": "1.5",
    d: "M12 18c-5.52 0-10-6-10-6s4.48-6 10-6 10 6 10 6-4.48 6-10 6z"
  }), jsx("path", {
    stroke: getIconColor(primaryColor, entity),
    strokeLinecap: "round",
    strokeLinejoin: "round",
    strokeMiterlimit: "10",
    "stroke-width": "1.5",
    d: "M12 14.5c1.3807 0 2.5-1.1193 2.5-2.5S13.3807 9.5 12 9.5 9.5 10.6193 9.5 12s1.1193 2.5 2.5 2.5zM2 2l20 20"
  })));
};

var ElementIcon = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    fill: "none",
    viewBox: " 0 0 24 24"
  }, jsx("path", {
    fill: getIconColor(primaryColor, entity),
    fillRule: "evenodd",
    d: "M12 2.75a5.25 5.25 0 100 10.5 5.25 5.25 0 000-10.5zM5.25 8a6.75 6.75 0 1113.346 1.44A6.75 6.75 0 1112 20.535 6.75 6.75 0 115.404 9.44 6.772 6.772 0 015.25 8zm.638 2.868a5.25 5.25 0 106.212 3.881 6.698 6.698 0 01-.826-.038 6.754 6.754 0 01-5.386-3.843zm7.708 3.692a6.77 6.77 0 01-.695 4.72 5.25 5.25 0 105.211-8.412 6.763 6.763 0 01-4.516 3.692z",
    clipRule: "evenodd"
  }));
};

var Complete = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    fill: "none"
  }, jsx("path", {
    fill: getIconColor(primaryColor, entity),
    d: "M12 1.75C6.34 1.75 1.75 6.34 1.75 12S6.34 22.25 12 22.25 22.25 17.66 22.25 12c0-.637-.058-1.26-.169-1.864a.75.75 0 111.475-.271c.128.692.194 1.406.194 2.135 0 6.49-5.26 11.75-11.75 11.75S.25 18.49.25 12 5.51.25 12 .25c2.363 0 4.566.699 6.41 1.9a.75.75 0 11-.82 1.257A10.199 10.199 0 0012 1.75zm9.53 3.72a.75.75 0 010 1.06l-9 9a.75.75 0 01-1.06 0l-4-4a.75.75 0 111.06-1.06L12 13.94l8.47-8.47a.75.75 0 011.06 0z"
  }));
};

var Download = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    fill: "none"
  }, jsx("path", {
    fill: getIconColor(primaryColor, entity),
    fillRule: "evenodd",
    d: "M12 3.25a.75.75 0 01.75.75v8.19l1.72-1.72a.75.75 0 111.06 1.06l-3 3a.75.75 0 01-1.06 0l-3-3a.75.75 0 111.06-1.06l1.72 1.72V4a.75.75 0 01.75-.75zm-11 10a.75.75 0 01.75.75v4c0 .69.56 1.25 1.25 1.25h18c.69 0 1.25-.56 1.25-1.25v-4a.75.75 0 011.5 0v4A2.75 2.75 0 0121 20.75H3A2.75 2.75 0 01.25 18v-4a.75.75 0 01.75-.75z",
    clipRule: "evenodd"
  }));
};

var AllPanel = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    fill: "none",
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    stroke: getIconColor(primaryColor, entity)
  }, jsx("path", {
    stroke: getIconColor(primaryColor, entity),
    d: "M13 22.75H11C7.7 22.75 5.71 22.09 5.34 18.74C5.28 18.29 5.25 17.75 5.25 17V7C5.25 6.25 5.28 5.71 5.35 5.23C5.71 1.91 7.7 1.25 11 1.25H13C16.3 1.25 18.29 1.91 18.66 5.26C18.72 5.71 18.75 6.25 18.75 7V17C18.75 17.75 18.72 18.29 18.65 18.77C18.29 22.09 16.3 22.75 13 22.75ZM11 2.75C7.69 2.75 7.06 3.42 6.84 5.42C6.78 5.85 6.75 6.32 6.75 7V17C6.75 17.68 6.78 18.15 6.83 18.55C7.05 20.58 7.69 21.25 11 21.25H13C16.31 21.25 16.94 20.58 17.16 18.58C17.22 18.16 17.25 17.68 17.25 17V7C17.25 6.33 17.22 5.85 17.17 5.45C16.95 3.42 16.31 2.75 13 2.75H11Z",
    fill: getIconColor(primaryColor, entity)
  }), jsx("path", {
    stroke: getIconColor(primaryColor, entity),
    d: "M5.67 19.4201H5.33C2.24 19.4201 1.25 18.4301 1.25 15.3301V8.67008C1.25 5.57008 2.24 4.58008 5.33 4.58008H5.67C5.84 4.58008 5.98 4.58008 6.14 4.59008 6.35 4.60008 6.54 4.70008 6.67 4.87008 6.8 5.04008 6.86 5.24008 6.83 5.45008 6.78 5.85008 6.75 6.32008 6.75 7.00008V17.0001C6.75 17.6801 6.78 18.1501 6.83 18.5501 6.86 18.7601 6.8 18.9701 6.67 19.1301 6.54 19.2901 6.35 19.3901 6.14 19.4101 5.98 19.4201 5.84 19.4201 5.67 19.4201zM5.27 6.08008C3.08 6.09008 2.75 6.44008 2.75 8.67008V15.3301C2.75 17.5601 3.08 17.9101 5.27 17.9201 5.26 17.6501 5.25 17.3501 5.25 17.0001V7.00008C5.25 6.65008 5.26 6.35008 5.27 6.08008zM18.6697 19.4201H18.3297C18.1597 19.4201 18.0197 19.4201 17.8597 19.4101 17.6497 19.4001 17.4597 19.3001 17.3297 19.1301 17.1997 18.9701 17.1397 18.7601 17.1697 18.5501 17.2297 18.1501 17.2497 17.6701 17.2497 17.0001V7.00008C17.2497 6.33008 17.2197 5.85008 17.1697 5.45008 17.1397 5.24008 17.1997 5.03008 17.3297 4.87008 17.4597 4.71008 17.6497 4.61008 17.8597 4.59008 18.0197 4.58008 18.1597 4.58008 18.3297 4.58008H18.6697C21.7597 4.58008 22.7497 5.57008 22.7497 8.67008V15.3301C22.7497 18.4301 21.7597 19.4201 18.6697 19.4201zM18.7297 6.08008C18.7397 6.35008 18.7497 6.65008 18.7497 7.00008V17.0001C18.7497 17.3501 18.7397 17.6501 18.7297 17.9201 20.9197 17.9101 21.2497 17.5601 21.2497 15.3301V8.67008C21.2497 6.44008 20.9197 6.09008 18.7297 6.08008z",
    fill: getIconColor(primaryColor, entity)
  }));
};

var useEntities = function () {
  return useTheme();
};

var CloseCircle = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    fill: "none",
    viewBox: "0 0 24 24",
    onClick: onClick && function () {
      return onClick();
    }
  }, jsx("path", {
    fill: getIconColor(primaryColor, entity),
    "fill-rule": "evenodd",
    d: "M12 1.75C6.33908 1.75 1.75 6.33908 1.75 12C1.75 17.6609 6.33908 22.25 12 22.25C17.6609 22.25 22.25 17.6609 22.25 12C22.25 6.33908 17.6609 1.75 12 1.75ZM0.25 12C0.25 5.51065 5.51065 0.25 12 0.25C18.4893 0.25 23.75 5.51065 23.75 12C23.75 18.4893 18.4893 23.75 12 23.75C5.51065 23.75 0.25 18.4893 0.25 12ZM7.46967 7.46967C7.76256 7.17678 8.23744 7.17678 8.53033 7.46967L12 10.9393L15.4697 7.46967C15.7626 7.17678 16.2374 7.17678 16.5303 7.46967C16.8232 7.76256 16.8232 8.23744 16.5303 8.53033L13.0607 12L16.5303 15.4697C16.8232 15.7626 16.8232 16.2374 16.5303 16.5303C16.2374 16.8232 15.7626 16.8232 15.4697 16.5303L12 13.0607L8.53033 16.5303C8.23744 16.8232 7.76256 16.8232 7.46967 16.5303C7.17678 16.2374 7.17678 15.7626 7.46967 15.4697L10.9393 12L7.46967 8.53033C7.17678 8.23744 7.17678 7.76256 7.46967 7.46967Z",
    "clip-rule": "evenodd"
  }));
};

var BackgroundSettings = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    fill: "none",
    viewBox: "0 0 24 24",
    onClick: onClick && function () {
      return onClick();
    }
  }, jsx("path", {
    fill: getIconColor(primaryColor, entity),
    "fill-rule": "evenodd",
    d: "M12 1.25C12.4142 1.25 12.75 1.58579 12.75 2V3.25H20C21.5188 3.25 22.75 4.48122 22.75 6V18C22.75 19.5188 21.5188 20.75 20 20.75H12.75V22C12.75 22.4142 12.4142 22.75 12 22.75C11.5858 22.75 11.25 22.4142 11.25 22V20.75H9C8.58579 20.75 8.25 20.4142 8.25 20C8.25 19.5858 8.58579 19.25 9 19.25H11.25V17C11.25 16.5858 11.5858 16.25 12 16.25C12.4142 16.25 12.75 16.5858 12.75 17V19.25H20C20.6904 19.25 21.25 18.6904 21.25 18V6C21.25 5.30964 20.6904 4.75 20 4.75H12.75V7C12.75 7.41421 12.4142 7.75 12 7.75C11.5858 7.75 11.25 7.41421 11.25 7V4.75H9C8.58579 4.75 8.25 4.41421 8.25 4C8.25 3.58579 8.58579 3.25 9 3.25H11.25V2C11.25 1.58579 11.5858 1.25 12 1.25ZM4 4.75C3.30964 4.75 2.75 5.30964 2.75 6V7C2.75 7.41421 2.41421 7.75 2 7.75C1.58579 7.75 1.25 7.41421 1.25 7V6C1.25 4.48122 2.48122 3.25 4 3.25H5C5.41421 3.25 5.75 3.58579 5.75 4C5.75 4.41421 5.41421 4.75 5 4.75H4ZM2 10.25C2.41421 10.25 2.75 10.5858 2.75 11V13C2.75 13.4142 2.41421 13.75 2 13.75C1.58579 13.75 1.25 13.4142 1.25 13V11C1.25 10.5858 1.58579 10.25 2 10.25ZM12 10.25C12.4142 10.25 12.75 10.5858 12.75 11V13C12.75 13.4142 12.4142 13.75 12 13.75C11.5858 13.75 11.25 13.4142 11.25 13V11C11.25 10.5858 11.5858 10.25 12 10.25ZM2 16.25C2.41421 16.25 2.75 16.5858 2.75 17V18C2.75 18.6904 3.30964 19.25 4 19.25H5C5.41421 19.25 5.75 19.5858 5.75 20C5.75 20.4142 5.41421 20.75 5 20.75H4C2.48122 20.75 1.25 19.5188 1.25 18V17C1.25 16.5858 1.58579 16.25 2 16.25Z",
    "clip-rule": "evenodd"
  }));
};

var Upload = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: "0 0 24 24",
    onClick: onClick && function () {
      return onClick();
    }
  }, jsx("path", {
    fill: getIconColor(primaryColor, entity),
    "fill-rule": "evenodd",
    d: "M11.5 4.75C7.88588 4.75 4.9348 7.59073 4.75836 11.1607C4.74212 11.4894 4.51343 11.7691 4.19447 11.8504C2.78876 12.2086 1.75 13.4839 1.75 15C1.75 16.7949 3.20507 18.25 5 18.25H7C7.41421 18.25 7.75 18.5858 7.75 19C7.75 19.4142 7.41421 19.75 7 19.75H5C2.37665 19.75 0.25 17.6234 0.25 15C0.25 12.9739 1.51792 11.2456 3.30274 10.5624C3.76846 6.44727 7.26064 3.25 11.5 3.25C14.5455 3.25 17.2044 4.90042 18.6331 7.35259C21.5442 7.88605 23.75 10.4349 23.75 13.5C23.75 16.9518 20.9518 19.75 17.5 19.75H17C16.5858 19.75 16.25 19.4142 16.25 19C16.25 18.5858 16.5858 18.25 17 18.25H17.5C20.1234 18.25 22.25 16.1234 22.25 13.5C22.25 11.0687 20.4226 9.06305 18.0668 8.7834C17.8206 8.75417 17.6047 8.60513 17.4901 8.38525C16.3635 6.2234 14.1032 4.75 11.5 4.75ZM11.4697 10.4697C11.7626 10.1768 12.2374 10.1768 12.5303 10.4697L15.5303 13.4697C15.8232 13.7626 15.8232 14.2374 15.5303 14.5303C15.2374 14.8232 14.7626 14.8232 14.4697 14.5303L12.75 12.8107V19C12.75 19.4142 12.4142 19.75 12 19.75C11.5858 19.75 11.25 19.4142 11.25 19V12.8107L9.53033 14.5303C9.23744 14.8232 8.76256 14.8232 8.46967 14.5303C8.17678 14.2374 8.17678 13.7626 8.46967 13.4697L11.4697 10.4697Z",
    "clip-rule": "evenodd"
  }));
};

var Stickers = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    fill: getIconColor(primaryColor, entity),
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    onClick: onClick && function () {
      return onClick();
    }
  }, jsx("path", {
    d: "M10 14.75H6.2C2.78 14.75 1.25 13.22 1.25 9.80005V6.19995C1.25 2.77995 2.78 1.25 6.2 1.25H9.8C13.22 1.25 14.75 2.77995 14.75 6.19995V10C14.75 10.41 14.42 10.74 14.01 10.75C11.61 10.79 10.79 11.62 10.75 14.01C10.74 14.42 10.41 14.75 10 14.75ZM6.2 2.75C3.62 2.75 2.75 3.61995 2.75 6.19995V9.80005C2.75 12.38 3.62 13.25 6.2 13.25H9.29C9.53 10.81 10.81 9.54004 13.25 9.29004V6.19995C13.25 3.61995 12.38 2.75 9.8 2.75H6.2Z",
    fill: getIconColor(primaryColor, entity)
  }), jsx("path", {
    d: "M5.19952 6.63014C4.96952 6.63014 4.72952 6.52019 4.58952 6.31019 4.34952 5.97019 4.42952 5.50015 4.76952 5.26015 5.54952 4.71015 6.59951 4.70013 7.38951 5.24013 7.72951 5.47013 7.81952 5.94017 7.58952 6.28017 7.35952 6.62017 6.88951 6.71012 6.54951 6.48012 6.27951 6.30012 5.91952 6.30013 5.64952 6.49013 5.49952 6.59013 5.34952 6.63014 5.19952 6.63014zM9.19952 6.63014C8.96952 6.63014 8.72952 6.52019 8.58952 6.31019 8.34952 5.97019 8.42952 5.50015 8.76952 5.26015 9.54952 4.71015 10.5995 4.70013 11.3895 5.24013 11.7295 5.47013 11.8195 5.94017 11.5895 6.28017 11.3595 6.62017 10.8895 6.71012 10.5495 6.48012 10.2795 6.30012 9.91952 6.30013 9.64952 6.49013 9.49952 6.59013 9.34952 6.63014 9.19952 6.63014zM8.16078 12.1702H5.84078C5.13078 12.1702 4.55078 11.5901 4.55078 10.8801 4.55078 8.98013 6.10078 7.43018 8.00078 7.43018 8.78078 7.43018 9.55078 7.70006 10.1608 8.19006 10.4808 8.45006 10.5408 8.92011 10.2808 9.24011 10.0208 9.56011 9.55078 9.61011 9.23078 9.36011 8.88078 9.08011 8.46078 8.93018 8.01078 8.93018 7.01078 8.93018 6.18078 9.69017 6.07078 10.6702H8.17078C8.58078 10.6702 8.92078 11.0102 8.92078 11.4202 8.92078 11.8302 8.57078 12.1702 8.16078 12.1702zM17.8 22.75H14.2C10.78 22.75 9.25 21.22 9.25 17.8V14C9.31 10.81 10.81 9.31 13.99 9.25H17.8C21.22 9.25 22.75 10.78 22.75 14.2V17.8C22.75 21.22 21.22 22.75 17.8 22.75zM14 10.75C11.62 10.79 10.79 11.62 10.75 14.01V17.8C10.75 20.38 11.62 21.25 14.2 21.25H17.8C20.38 21.25 21.25 20.38 21.25 17.8V14.2C21.25 11.62 20.38 10.75 17.8 10.75H14z",
    fill: getIconColor(primaryColor, entity)
  }), jsx("path", {
    d: "M14.0895 14.6301C13.6195 14.6301 13.1595 14.4901 12.7695 14.2101 12.4295 13.9701 12.3495 13.5002 12.5895 13.1602 12.8295 12.8202 13.2995 12.7401 13.6395 12.9801 13.9095 13.1701 14.2695 13.1701 14.5395 12.9901 14.8795 12.7601 15.3495 12.8401 15.5795 13.1901 15.8095 13.5301 15.7295 14.0001 15.3795 14.2301 14.9895 14.5001 14.5395 14.6301 14.0895 14.6301zM18.0895 14.6301C17.6195 14.6301 17.1595 14.4901 16.7695 14.2101 16.4295 13.9701 16.3495 13.5002 16.5895 13.1602 16.8295 12.8202 17.2995 12.7401 17.6395 12.9801 17.9095 13.1701 18.2695 13.1701 18.5395 12.9901 18.8795 12.7601 19.3495 12.8401 19.5795 13.1901 19.8095 13.5301 19.7295 14.0001 19.3795 14.2301 18.9895 14.5001 18.5395 14.6301 18.0895 14.6301zM16.0008 20.1702C14.1008 20.1702 12.5508 18.6201 12.5508 16.7201 12.5508 16.0101 13.1308 15.4302 13.8408 15.4302H18.1608C18.8708 15.4302 19.4508 16.0101 19.4508 16.7201 19.4508 18.6201 17.9008 20.1702 16.0008 20.1702zM14.0608 16.9302C14.1608 17.9102 14.9908 18.6702 16.0008 18.6702 17.0108 18.6702 17.8308 17.9102 17.9408 16.9302H14.0608z",
    fill: getIconColor(primaryColor, entity)
  }));
};

var ImageSwap = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    fill: "none",
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    stroke: getIconColor(primaryColor, entity)
  }, jsx("path", {
    stroke: getIconColor(primaryColor, entity),
    d: "M18.85 12.75H16.15C13.49 12.75 12.25 11.51 12.25 8.85V5.15C12.25 2.49 13.49 1.25 16.15 1.25H18.85C21.51 1.25 22.75 2.49 22.75 5.15V8.85C22.75 11.51 21.51 12.75 18.85 12.75zM16.15 2.75C14.31 2.75 13.75 3.31 13.75 5.15V8.85C13.75 10.69 14.31 11.25 16.15 11.25H18.85C20.69 11.25 21.25 10.69 21.25 8.85V5.15C21.25 3.31 20.69 2.75 18.85 2.75H16.15zM7.85 22.75H5.15C2.49 22.75 1.25 21.51 1.25 18.85V15.15C1.25 12.49 2.49 11.25 5.15 11.25H7.85C10.51 11.25 11.75 12.49 11.75 15.15V18.85C11.75 21.51 10.51 22.75 7.85 22.75zM5.15 12.75C3.31 12.75 2.75 13.31 2.75 15.15V18.85C2.75 20.69 3.31 21.25 5.15 21.25H7.85C9.69 21.25 10.25 20.69 10.25 18.85V15.15C10.25 13.31 9.69 12.75 7.85 12.75H5.15zM15.0002 22.75C14.7302 22.75 14.4802 22.6 14.3502 22.37 14.2202 22.14 14.2202 21.85 14.3602 21.62L15.4102 19.87C15.6202 19.51 16.0802 19.4 16.4402 19.61 16.8002 19.82 16.9102 20.28 16.7002 20.64L16.4302 21.09C19.1902 20.44 21.2602 17.96 21.2602 15 21.2602 14.59 21.6002 14.25 22.0102 14.25 22.4202 14.25 22.7602 14.59 22.7602 15 22.7502 19.27 19.2702 22.75 15.0002 22.75zM2 9.75C1.59 9.75 1.25 9.41 1.25 9 1.25 4.73 4.73 1.25 9 1.25 9.27 1.25 9.52 1.4 9.65 1.63 9.78 1.86 9.78 2.15 9.64 2.38L8.59 4.14C8.38 4.49 7.92 4.61 7.56 4.39 7.21 4.18 7.09 3.72 7.31 3.36L7.58 2.91C4.81 3.56 2.75 6.04 2.75 9 2.75 9.41 2.41 9.75 2 9.75z",
    fill: getIconColor(primaryColor, entity)
  }));
};

var ClearPanel = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    fill: getIconColor(primaryColor, entity),
    viewBox: " 0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    onClick: function () {
      return onClick === null || onClick === void 0 ? void 0 : onClick();
    }
  }, jsx("path", {
    d: "M 14.35 21.75 L 8.65 21.75 C 3.46 21.75 1.25 19.54 1.25 14.35 L 1.25 8.65 C 1.25 3.46 3.46 1.25 8.65 1.25 L 14.35 1.25 C 19.54 1.25 21.75 3.46 21.75 8.65 L 21.75 14.35 C 21.75 14.71 21.74 15.05 21.72 15.38 C 21.7 15.68 21.5 15.94 21.21 16.04 C 20.92 16.14 20.61 16.05 20.4 15.83 C 19.8 15.15 18.92 14.75 17.99 14.75 C 16.2 14.75 14.74 16.21 14.74 18 C 14.74 18.59 14.9 19.17 15.21 19.67 C 15.37 19.95 15.58 20.2 15.82 20.41 C 16.05 20.61 16.14 20.93 16.04 21.21 C 15.94 21.5 15.68 21.7 15.38 21.72 C 15.06 21.74 14.72 21.75 14.35 21.75 Z M 8.65 2.75 C 4.29 2.75 2.75 4.29 2.75 8.65 L 2.75 14.35 C 2.75 18.71 4.29 20.25 8.65 20.25 L 13.82 20.25 C 13.445 19.559 13.249 18.786 13.25 18 C 13.25 15.38 15.38 13.25 18 13.25 C 18.8 13.25 19.57 13.45 20.25 13.81 L 20.25 8.65 C 20.25 4.29 18.71 2.75 14.35 2.75 Z",
    fill: getIconColor(primaryColor, entity)
  }), jsx("path", {
    d: "M 18 22.75 C 16.827 22.75 15.696 22.311 14.83 21.52 C 14.48 21.22 14.17 20.85 13.93 20.44 C 13.49 19.72 13.25 18.87 13.25 18 C 13.25 15.38 15.38 13.25 18 13.25 C 19.36 13.25 20.65 13.83 21.53 14.83 C 22.314 15.7 22.749 16.829 22.75 18 C 22.75 18.87 22.51 19.72 22.06 20.45 C 21.22 21.87 19.66 22.75 18 22.75 Z M 18 14.75 C 16.21 14.75 14.75 16.21 14.75 18 C 14.75 18.59 14.91 19.17 15.22 19.67 C 15.38 19.95 15.59 20.2 15.83 20.41 C 16.544 21.067 17.516 21.368 18.476 21.228 C 19.437 21.089 20.283 20.524 20.78 19.69 C 21.09 19.18 21.25 18.61 21.25 18.01 C 21.25 17.21 20.95 16.44 20.41 15.84 C 19.81 15.14 18.93 14.75 18 14.75 Z",
    fill: getIconColor(primaryColor, entity)
  }), jsx("path", {
    d: "M 19.07 19.79 C 18.88 19.79 18.69 19.72 18.54 19.57 L 16.43 17.46 C 16.14 17.166 16.14 16.694 16.43 16.4 C 16.72 16.11 17.2 16.11 17.49 16.4 L 19.6 18.51 C 19.89 18.8 19.89 19.28 19.6 19.57 C 19.45 19.72 19.26 19.79 19.07 19.79 Z",
    fill: getIconColor(primaryColor, entity)
  }), jsx("path", {
    d: "M 16.931 19.82 C 16.741 19.82 16.551 19.75 16.401 19.6 C 16.111 19.306 16.111 18.834 16.401 18.54 L 18.511 16.43 C 18.801 16.14 19.281 16.14 19.571 16.43 C 19.861 16.72 19.861 17.2 19.571 17.49 L 17.461 19.6 C 17.311 19.75 17.121 19.82 16.931 19.82 Z",
    fill: getIconColor(primaryColor, entity)
  }));
};

var ViewCarousel = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    fill: getIconColor(primaryColor, entity),
    viewBox: " 0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    onClick: function () {
      return onClick === null || onClick === void 0 ? void 0 : onClick();
    }
  }, jsx("path", {
    d: "M 13 22.75 L 11 22.75 C 7.7 22.75 5.71 22.09 5.34 18.74 C 5.28 18.29 5.25 17.75 5.25 17 L 5.25 7 C 5.25 6.25 5.28 5.71 5.35 5.23 C 5.71 1.91 7.7 1.25 11 1.25 L 13 1.25 C 16.3 1.25 18.29 1.91 18.66 5.26 C 18.72 5.71 18.75 6.25 18.75 7 L 18.75 17 C 18.75 17.75 18.72 18.29 18.65 18.77 C 18.29 22.09 16.3 22.75 13 22.75 Z M 11 2.75 C 7.69 2.75 7.06 3.42 6.84 5.42 C 6.78 5.85 6.75 6.32 6.75 7 L 6.75 17 C 6.75 17.68 6.78 18.15 6.83 18.55 C 7.05 20.58 7.69 21.25 11 21.25 L 13 21.25 C 16.31 21.25 16.94 20.58 17.16 18.58 C 17.22 18.16 17.25 17.68 17.25 17 L 17.25 7 C 17.25 6.33 17.22 5.85 17.17 5.45 C 16.95 3.42 16.31 2.75 13 2.75 Z",
    fill: getIconColor(primaryColor, entity)
  }), jsx("path", {
    d: "M 5.67 19.42 L 5.33 19.42 C 2.24 19.42 1.25 18.43 1.25 15.33 L 1.25 8.67 C 1.25 5.57 2.24 4.58 5.33 4.58 L 5.67 4.58 C 5.84 4.58 5.98 4.58 6.14 4.59 C 6.35 4.6 6.54 4.7 6.67 4.87 C 6.8 5.04 6.86 5.24 6.83 5.45 C 6.78 5.85 6.75 6.32 6.75 7 L 6.75 17 C 6.75 17.68 6.78 18.15 6.83 18.55 C 6.86 18.76 6.8 18.97 6.67 19.13 C 6.54 19.29 6.35 19.39 6.14 19.41 C 5.98 19.42 5.84 19.42 5.67 19.42 Z M 5.27 6.08 C 3.08 6.09 2.75 6.44 2.75 8.67 L 2.75 15.33 C 2.75 17.56 3.08 17.91 5.27 17.92 C 5.26 17.65 5.25 17.35 5.25 17 L 5.25 7 C 5.25 6.65 5.26 6.35 5.27 6.08 Z M 18.67 19.42 L 18.33 19.42 C 18.16 19.42 18.02 19.42 17.86 19.41 C 17.65 19.4 17.456 19.298 17.33 19.13 C 17.198 18.967 17.14 18.757 17.17 18.55 C 17.23 18.15 17.25 17.67 17.25 17 L 17.25 7 C 17.25 6.33 17.22 5.85 17.17 5.45 C 17.14 5.24 17.2 5.03 17.33 4.87 C 17.46 4.71 17.65 4.61 17.86 4.59 C 18.02 4.58 18.16 4.58 18.33 4.58 L 18.67 4.58 C 21.76 4.58 22.75 5.57 22.75 8.67 L 22.75 15.33 C 22.75 18.43 21.76 19.42 18.67 19.42 Z M 18.73 6.08 C 18.74 6.35 18.75 6.65 18.75 7 L 18.75 17 C 18.75 17.35 18.74 17.65 18.73 17.92 C 20.92 17.91 21.25 17.56 21.25 15.33 L 21.25 8.67 C 21.25 6.44 20.92 6.09 18.73 6.08 Z",
    fill: getIconColor(primaryColor, entity)
  }));
};

var ViewSinglePanel = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    fill: getIconColor(primaryColor, entity),
    viewBox: " 0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    onClick: function () {
      return onClick === null || onClick === void 0 ? void 0 : onClick();
    }
  }, jsx("path", {
    d: "M 15.75 21.5 L 5.75 21.5 C 2.1 21.5 0 19.4 0 15.75 L 0 10.75 C 0 7.1 2.1 5 5.75 5 L 15.75 5 C 19.4 5 21.5 7.1 21.5 10.75 L 21.5 15.75 C 21.5 19.4 19.4 21.5 15.75 21.5 Z M 5.75 6.5 C 2.89 6.5 1.5 7.89 1.5 10.75 L 1.5 15.75 C 1.5 18.61 2.89 20 5.75 20 L 15.75 20 C 18.61 20 20 18.61 20 15.75 L 20 10.75 C 20 7.89 18.61 6.5 15.75 6.5 Z M 16.75 4 L 4.75 4 C 4.34 4 4 3.66 4 3.25 C 4 2.84 4.34 2.5 4.75 2.5 L 16.75 2.5 C 17.16 2.5 17.5 2.84 17.5 3.25 C 17.5 3.66 17.16 4 16.75 4 Z M 13.75 1.5 L 7.75 1.5 C 7.34 1.5 7 1.16 7 0.75 C 7 0.34 7.34 0 7.75 0 L 13.75 0 C 14.16 0 14.5 0.34 14.5 0.75 C 14.5 1.16 14.16 1.5 13.75 1.5 Z",
    transform: "translate(1.25 1.25) rotate(90 10.75 10.75)",
    fill: getIconColor(primaryColor, entity)
  }));
};

var EdtiorText = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity)
  }, jsx("path", {
    d: "M 7.703 1.247 C 7.703 1.247 7.823 1.247 7.823 1.366 L 13.943 16.922 C 14.183 17.278 13.943 17.753 13.583 17.872 L 13.343 17.872 C 12.983 17.872 12.743 17.634 12.623 17.397 L 11.423 14.547 L 2.663 14.547 L 1.463 17.397 C 1.343 17.753 0.983 17.872 0.743 17.872 L 0.503 17.872 C 0.143 17.753 0.023 17.278 0.143 16.922 L 6.383 1.247 L 6.503 1.128 C 6.623 1.128 6.623 1.009 6.623 1.009 L 6.743 1.009 C 6.743 0.891 6.863 0.891 6.863 0.891 L 7.343 0.891 L 7.463 1.009 C 7.463 1.128 7.583 1.128 7.583 1.128 C 7.703 1.128 7.703 1.247 7.703 1.247 Z M 7.103 3.503 L 3.263 13.122 L 10.943 13.122 Z M 22.343 10.153 C 22.343 9.797 22.703 9.441 23.063 9.441 C 23.543 9.441 23.783 9.678 23.903 10.034 L 23.903 17.159 C 23.903 17.516 23.543 17.872 23.183 17.872 C 22.823 17.872 22.463 17.516 22.463 17.159 L 22.463 16.803 C 21.623 17.634 20.423 18.109 19.343 18.109 C 17.063 18.109 15.383 16.447 15.383 13.953 C 15.383 11.697 16.943 10.034 19.223 9.797 C 20.423 9.678 21.503 9.916 22.343 10.509 Z M 16.703 14.072 C 16.703 15.378 17.543 16.803 19.223 16.803 C 20.783 16.803 22.343 15.497 22.343 13.241 C 22.343 12.766 22.103 12.291 21.743 11.934 C 21.263 11.578 20.543 11.341 19.703 11.341 L 19.223 11.341 C 17.663 11.459 16.703 12.528 16.703 14.072 Z",
    fill: getIconColor(primaryColor, entity)
  }));
};

var Layout = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity)
  }, jsx("path", {
    d: "M 8.27 22.75 L 4.23 22.75 C 2.22 22.75 1.25 21.82 1.25 19.9 L 1.25 4.1 C 1.25 2.18 2.23 1.25 4.23 1.25 L 8.27 1.25 C 10.28 1.25 11.25 2.18 11.25 4.1 L 11.25 19.9 C 11.25 21.82 10.27 22.75 8.27 22.75 Z M 4.23 2.75 C 2.96 2.75 2.75 3.09 2.75 4.1 L 2.75 19.9 C 2.75 20.91 2.96 21.25 4.23 21.25 L 8.27 21.25 C 9.54 21.25 9.75 20.91 9.75 19.9 L 9.75 4.1 C 9.75 3.09 9.54 2.75 8.27 2.75 Z M 19.77 13.75 L 15.73 13.75 C 13.72 13.75 12.75 12.82 12.75 10.9 L 12.75 4.1 C 12.75 2.18 13.73 1.25 15.73 1.25 L 19.77 1.25 C 21.78 1.25 22.75 2.18 22.75 4.1 L 22.75 10.9 C 22.75 12.82 21.77 13.75 19.77 13.75 Z M 15.73 2.75 C 14.46 2.75 14.25 3.09 14.25 4.1 L 14.25 10.9 C 14.25 11.91 14.46 12.25 15.73 12.25 L 19.77 12.25 C 21.04 12.25 21.25 11.91 21.25 10.9 L 21.25 4.1 C 21.25 3.09 21.04 2.75 19.77 2.75 Z M 19.77 22.75 L 15.73 22.75 C 13.72 22.75 12.75 21.82 12.75 19.9 L 12.75 18.1 C 12.75 16.18 13.73 15.25 15.73 15.25 L 19.77 15.25 C 21.78 15.25 22.75 16.18 22.75 18.1 L 22.75 19.9 C 22.75 21.82 21.77 22.75 19.77 22.75 Z M 15.73 16.75 C 14.46 16.75 14.25 17.09 14.25 18.1 L 14.25 19.9 C 14.25 20.91 14.46 21.25 15.73 21.25 L 19.77 21.25 C 21.04 21.25 21.25 20.91 21.25 19.9 L 21.25 18.1 C 21.25 17.09 21.04 16.75 19.77 16.75 Z",
    fill: getIconColor(primaryColor, entity)
  }));
};

var Background = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    onClick: onClick && function () {
      return onClick();
    }
  }, jsx("path", {
    d: "M 9.6 18.75 C 7.2 18.75 5.25 16.8 5.25 14.4 C 5.25 12.44 6.57 10.71 8.46 10.2 C 8.83 10.1 9.21 10.29 9.35 10.65 C 9.68 11.48 10.39 12.12 11.25 12.35 C 11.72 12.48 12.28 12.48 12.75 12.35 C 13.12 12.25 13.51 12.44 13.65 12.79 C 14.312 14.485 13.854 16.414 12.5 17.63 C 11.71 18.35 10.68 18.75 9.6 18.75 Z M 8.29 11.87 C 7.344 12.359 6.75 13.335 6.75 14.4 C 6.75 15.97 8.03 17.25 9.6 17.25 C 10.3 17.25 10.98 16.99 11.5 16.52 C 12.231 15.872 12.575 14.893 12.41 13.93 C 11.88 13.98 11.34 13.93 10.85 13.79 C 9.784 13.498 8.869 12.812 8.29 11.87 Z",
    fill: getIconColor(primaryColor, entity)
  }), jsx("path", {
    d: "M 12 13.95 C 11.6 13.95 11.21 13.9 10.85 13.8 C 9.535 13.435 8.467 12.477 7.96 11.21 C 7.76 10.7 7.66 10.16 7.66 9.61 C 7.66 7.21 9.61 5.26 12.01 5.26 C 14.41 5.26 16.36 7.21 16.36 9.61 C 16.36 10.16 16.26 10.7 16.06 11.21 C 15.4 12.873 13.789 13.96 12 13.95 Z M 12 6.75 C 11.057 6.752 10.176 7.22 9.645 8 C 9.115 8.781 9.005 9.772 9.35 10.65 C 9.68 11.48 10.39 12.12 11.25 12.35 C 11.72 12.48 12.28 12.48 12.75 12.35 C 13.62 12.11 14.33 11.48 14.65 10.65 C 14.995 9.772 14.885 8.781 14.355 8 C 13.824 7.22 12.943 6.752 12 6.75 Z",
    fill: getIconColor(primaryColor, entity)
  }), jsx("path", {
    d: "M 14.4 18.75 C 13.32 18.75 12.29 18.35 11.5 17.63 C 11.34 17.488 11.249 17.284 11.249 17.07 C 11.249 16.856 11.34 16.652 11.5 16.51 C 12.392 15.721 12.693 14.456 12.25 13.35 C 12.175 13.151 12.187 12.931 12.282 12.741 C 12.377 12.551 12.546 12.409 12.75 12.35 C 13.613 12.11 14.316 11.481 14.65 10.65 C 14.79 10.296 15.172 10.102 15.54 10.2 C 17.43 10.71 18.75 12.44 18.75 14.4 C 18.75 16.8 16.8 18.75 14.4 18.75 Z M 13.12 16.95 C 14.004 17.391 15.054 17.344 15.895 16.825 C 16.736 16.306 17.248 15.388 17.25 14.4 C 17.25 13.32 16.64 12.35 15.71 11.87 C 15.27 12.59 14.62 13.17 13.86 13.53 C 14.107 14.723 13.838 15.965 13.12 16.95 Z",
    fill: getIconColor(primaryColor, entity)
  }), jsx("path", {
    d: "M 15 22.75 L 9 22.75 C 3.57 22.75 1.25 20.43 1.25 15 L 1.25 9 C 1.25 3.57 3.57 1.25 9 1.25 L 15 1.25 C 20.43 1.25 22.75 3.57 22.75 9 L 22.75 15 C 22.75 20.43 20.43 22.75 15 22.75 Z M 9 2.75 C 4.39 2.75 2.75 4.39 2.75 9 L 2.75 15 C 2.75 19.61 4.39 21.25 9 21.25 L 15 21.25 C 19.61 21.25 21.25 19.61 21.25 15 L 21.25 9 C 21.25 4.39 19.61 2.75 15 2.75 Z",
    fill: getIconColor(primaryColor, entity)
  }));
};

var Save = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    onClick: onClick && function () {
      return onClick();
    }
  }, jsx("path", {
    d: "M 17 22.75 L 7 22.75 C 2.59 22.75 1.25 21.41 1.25 17 L 1.25 7 C 1.25 2.59 2.59 1.25 7 1.25 L 8.5 1.25 C 10.25 1.25 10.8 1.82 11.5 2.75 L 13 4.75 C 13.33 5.19 13.38 5.25 14 5.25 L 17 5.25 C 21.41 5.25 22.75 6.59 22.75 11 L 22.75 17 C 22.75 21.41 21.41 22.75 17 22.75 Z M 7 2.75 C 3.43 2.75 2.75 3.43 2.75 7 L 2.75 17 C 2.75 20.57 3.43 21.25 7 21.25 L 17 21.25 C 20.57 21.25 21.25 20.57 21.25 17 L 21.25 11 C 21.25 7.43 20.57 6.75 17 6.75 L 14 6.75 C 12.72 6.75 12.3 6.31 11.8 5.65 L 10.3 3.65 C 9.78 2.96 9.63 2.75 8.5 2.75 Z",
    fill: getIconColor(primaryColor, entity)
  }));
};

var SignaturePen = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    onClick: onClick && function () {
      return onClick();
    },
    viewBox: "0 0 24 24"
  }, jsx("path", {
    d: "M 15.884 2.944 C 15.65 2.71 15.332 2.578 15 2.578 C 14.668 2.578 14.35 2.71 14.116 2.944 L 10.945 6.116 C 10.711 6.35 10.579 6.668 10.579 7 C 10.579 7.332 10.711 7.65 10.945 7.884 L 16.116 13.055 C 16.35 13.289 16.668 13.421 17 13.421 C 17.332 13.421 17.65 13.289 17.884 13.055 L 21.055 9.884 C 21.289 9.65 21.421 9.332 21.421 9 C 21.421 8.668 21.289 8.35 21.055 8.116 Z M 13.055 1.884 C 13.571 1.368 14.27 1.078 15 1.078 C 15.73 1.078 16.429 1.368 16.945 1.884 L 22.116 7.055 C 22.632 7.571 22.922 8.27 22.922 9 C 22.922 9.73 22.632 10.429 22.116 10.945 L 18.945 14.116 C 17.973 15.088 16.432 15.194 15.337 14.362 L 13.875 18.747 C 13.696 19.285 13.267 19.702 12.725 19.867 L 3.512 22.672 C 2.172 23.079 0.921 21.827 1.328 20.488 L 4.132 11.276 C 4.297 10.734 4.714 10.305 5.252 10.126 L 9.638 8.663 C 8.806 7.568 8.912 6.027 9.884 5.055 Z M 10.797 9.858 L 5.727 11.548 C 5.65 11.574 5.59 11.635 5.567 11.713 L 3.091 19.848 L 7.47 15.47 C 7.657 15.269 7.939 15.187 8.204 15.255 C 8.47 15.322 8.678 15.53 8.745 15.796 C 8.813 16.061 8.731 16.343 8.53 16.53 L 4.152 20.91 L 12.287 18.434 C 12.365 18.411 12.426 18.351 12.452 18.274 L 14.142 13.204 L 10.797 9.859 Z",
    fill: getIconColor(primaryColor, entity)
  }));
};

var Variable = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    onClick: onClick && function () {
      return onClick();
    },
    viewBox: "0 0 24 24"
  }, jsx("path", {
    d: "M 5 21.87 C 4.26 21.87 3.53 21.59 2.97 21.03 C 2.43 20.49 2.13 19.77 2.13 19 C 2.13 18.23 2.43 17.51 2.97 16.97 L 15.97 3.97 C 17.092 2.85 18.908 2.85 20.03 3.97 C 20.57 4.51 20.87 5.23 20.87 6 C 20.87 6.77 20.57 7.49 20.03 8.03 L 7.03 21.03 C 6.47 21.59 5.74 21.87 5 21.87 Z M 18 4.63 C 17.65 4.63 17.3 4.76 17.03 5.03 L 4.03 18.03 C 3.77 18.29 3.63 18.63 3.63 19 C 3.63 19.37 3.77 19.71 4.03 19.97 C 4.56 20.5 5.44 20.5 5.97 19.97 L 18.97 6.97 C 19.23 6.71 19.37 6.37 19.37 6 C 19.37 5.63 19.23 5.29 18.97 5.03 C 18.7 4.76 18.35 4.63 18 4.63 Z",
    fill: getIconColor(primaryColor, entity)
  }), jsx("path", {
    d: "M 18.01 9.74 C 17.82 9.74 17.63 9.67 17.48 9.52 L 14.48 6.52 C 14.19 6.226 14.19 5.754 14.48 5.46 C 14.77 5.17 15.25 5.17 15.54 5.46 L 18.54 8.46 C 18.83 8.75 18.83 9.23 18.54 9.52 C 18.39 9.67 18.2 9.74 18.01 9.74 Z M 10 5.5 C 9.95 5.5 9.91 5.49 9.86 5.48 L 8.5 5.08 L 7.14 5.48 C 6.96 5.53 6.77 5.48 6.65 5.35 C 6.52 5.22 6.47 5.04 6.52 4.86 L 6.92 3.5 L 6.52 2.14 C 6.47 1.96 6.52 1.77 6.65 1.65 C 6.778 1.521 6.965 1.471 7.14 1.52 L 8.5 1.92 L 9.86 1.52 C 10.04 1.47 10.22 1.52 10.35 1.65 C 10.479 1.778 10.529 1.965 10.48 2.14 L 10.08 3.5 L 10.48 4.86 C 10.53 5.04 10.48 5.23 10.35 5.35 C 10.26 5.447 10.133 5.502 10 5.5 Z M 8.5 4.06 C 8.55 4.06 8.59 4.07 8.64 4.08 L 9.26 4.26 L 9.08 3.64 C 9.05 3.549 9.05 3.451 9.08 3.36 L 9.26 2.74 L 8.64 2.92 C 8.55 2.95 8.45 2.95 8.36 2.92 L 7.74 2.74 L 7.92 3.36 C 7.95 3.45 7.95 3.55 7.92 3.64 L 7.74 4.26 L 8.36 4.08 C 8.41 4.07 8.45 4.06 8.5 4.06 Z M 6 11.5 C 5.95 11.5 5.91 11.49 5.86 11.48 L 4.5 11.08 L 3.14 11.48 C 2.96 11.53 2.78 11.48 2.65 11.35 C 2.521 11.222 2.471 11.035 2.52 10.86 L 2.92 9.5 L 2.52 8.14 C 2.47 7.96 2.52 7.77 2.65 7.65 C 2.78 7.53 2.97 7.47 3.14 7.52 L 4.5 7.92 L 5.86 7.52 C 6.035 7.471 6.222 7.521 6.35 7.65 C 6.479 7.778 6.529 7.965 6.48 8.14 L 6.08 9.5 L 6.48 10.86 C 6.53 11.04 6.48 11.23 6.35 11.35 C 6.26 11.447 6.133 11.502 6 11.5 Z M 4.5 10.06 C 4.55 10.06 4.59 10.07 4.64 10.08 L 5.26 10.26 L 5.08 9.64 C 5.05 9.549 5.05 9.451 5.08 9.36 L 5.26 8.74 L 4.64 8.92 C 4.55 8.95 4.45 8.95 4.36 8.92 L 3.74 8.74 L 3.92 9.36 C 3.95 9.45 3.95 9.55 3.92 9.64 L 3.74 10.26 L 4.36 10.08 C 4.41 10.07 4.45 10.06 4.5 10.06 Z M 21 16.5 C 20.95 16.5 20.91 16.49 20.86 16.48 L 19.5 16.08 L 18.14 16.48 C 17.96 16.53 17.78 16.48 17.65 16.35 C 17.521 16.222 17.471 16.035 17.52 15.86 L 17.92 14.5 L 17.52 13.14 C 17.47 12.96 17.52 12.77 17.65 12.65 C 17.78 12.53 17.97 12.47 18.14 12.52 L 19.5 12.92 L 20.86 12.52 C 21.035 12.471 21.222 12.521 21.35 12.65 C 21.48 12.78 21.53 12.97 21.48 13.14 L 21.08 14.5 L 21.48 15.86 C 21.53 16.04 21.48 16.23 21.35 16.35 C 21.26 16.447 21.133 16.502 21 16.5 Z M 19.5 15.06 C 19.55 15.06 19.59 15.07 19.64 15.08 L 20.26 15.26 L 20.08 14.64 C 20.05 14.549 20.05 14.451 20.08 14.36 L 20.26 13.74 L 19.64 13.92 C 19.55 13.95 19.45 13.95 19.36 13.92 L 18.74 13.74 L 18.92 14.36 C 18.95 14.45 18.95 14.55 18.92 14.64 L 18.74 15.26 L 19.36 15.08 C 19.41 15.07 19.45 15.06 19.5 15.06 Z",
    fill: getIconColor(primaryColor, entity)
  }));
};

var ImageUpload = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    onClick: onClick && function () {
      return onClick();
    },
    viewBox: "0 0 24 24"
  }, jsx("path", {
    d: "M 17.134 21.5 L 4.364 21.5 C 2.844 21.5 1.454 20.73 0.654 19.43 C -0.145 18.143 -0.217 16.533 0.464 15.18 L 2.184 11.73 C 2.744 10.61 3.644 9.91 4.654 9.8 C 5.664 9.69 6.694 10.19 7.474 11.16 L 7.694 11.44 C 8.134 11.98 8.644 12.27 9.144 12.22 C 9.644 12.18 10.104 11.82 10.444 11.21 L 12.334 7.8 C 13.114 6.39 14.154 5.66 15.284 5.71 C 16.404 5.77 17.364 6.61 18.004 8.09 L 21.134 15.4 C 21.714 16.748 21.575 18.297 20.764 19.52 C 19.974 20.759 18.604 21.506 17.134 21.5 Z M 4.934 11.3 C 4.894 11.3 4.854 11.3 4.814 11.31 C 4.314 11.36 3.854 11.76 3.524 12.41 L 1.804 15.86 C 1.354 16.75 1.404 17.8 1.924 18.65 C 2.444 19.5 3.364 20.01 4.364 20.01 L 17.124 20.01 C 18.104 20.01 18.974 19.54 19.514 18.72 C 20.054 17.9 20.144 16.92 19.754 16.02 L 16.624 8.71 C 16.244 7.81 15.714 7.26 15.204 7.24 C 14.734 7.21 14.124 7.71 13.644 8.56 L 11.754 11.97 C 11.174 13.01 10.264 13.66 9.274 13.75 C 8.284 13.83 7.274 13.35 6.524 12.41 L 6.304 12.13 C 5.884 11.58 5.404 11.3 4.934 11.3 Z M 5.744 7.5 C 3.684 7.5 1.994 5.82 1.994 3.75 C 1.994 1.68 3.674 0 5.744 0 C 7.814 0 9.494 1.68 9.494 3.75 C 9.494 5.82 7.814 7.5 5.744 7.5 Z M 5.744 1.5 C 4.504 1.5 3.494 2.51 3.494 3.75 C 3.494 4.99 4.504 6 5.744 6 C 6.984 6 7.994 4.99 7.994 3.75 C 7.994 2.51 6.984 1.5 5.744 1.5 Z",
    fill: getIconColor(primaryColor, entity)
  }));
};

var Size = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    onClick: onClick && function () {
      return onClick();
    },
    viewBox: "0 0 24 24"
  }, jsx("path", {
    d: "M 22 18.25 L 15 18.25 C 14.59 18.25 14.25 17.91 14.25 17.5 C 14.25 17.09 14.59 16.75 15 16.75 L 22 16.75 C 22.41 16.75 22.75 17.09 22.75 17.5 C 22.75 17.91 22.41 18.25 22 18.25 Z M 5 18.25 L 2 18.25 C 1.59 18.25 1.25 17.91 1.25 17.5 C 1.25 17.09 1.59 16.75 2 16.75 L 5 16.75 C 5.41 16.75 5.75 17.09 5.75 17.5 C 5.75 17.91 5.41 18.25 5 18.25 Z M 22 7.25 L 19 7.25 C 18.59 7.25 18.25 6.91 18.25 6.5 C 18.25 6.09 18.59 5.75 19 5.75 L 22 5.75 C 22.41 5.75 22.75 6.09 22.75 6.5 C 22.75 6.91 22.41 7.25 22 7.25 Z M 9 7.25 L 2 7.25 C 1.59 7.25 1.25 6.91 1.25 6.5 C 1.25 6.09 1.59 5.75 2 5.75 L 9 5.75 C 9.41 5.75 9.75 6.09 9.75 6.5 C 9.75 6.91 9.41 7.25 9 7.25 Z",
    fill: getIconColor(primaryColor, entity)
  }), jsx("path", {
    d: "M 13 21.25 L 7 21.25 C 5.28 21.25 4.25 20.22 4.25 18.5 L 4.25 16.5 C 4.25 14.78 5.28 13.75 7 13.75 L 13 13.75 C 14.72 13.75 15.75 14.78 15.75 16.5 L 15.75 18.5 C 15.75 20.22 14.72 21.25 13 21.25 Z M 7 15.25 C 6.11 15.25 5.75 15.61 5.75 16.5 L 5.75 18.5 C 5.75 19.39 6.11 19.75 7 19.75 L 13 19.75 C 13.89 19.75 14.25 19.39 14.25 18.5 L 14.25 16.5 C 14.25 15.61 13.89 15.25 13 15.25 Z M 17 10.25 L 11 10.25 C 9.28 10.25 8.25 9.22 8.25 7.5 L 8.25 5.5 C 8.25 3.78 9.28 2.75 11 2.75 L 17 2.75 C 18.72 2.75 19.75 3.78 19.75 5.5 L 19.75 7.5 C 19.75 9.22 18.72 10.25 17 10.25 Z M 11 4.25 C 10.11 4.25 9.75 4.61 9.75 5.5 L 9.75 7.5 C 9.75 8.39 10.11 8.75 11 8.75 L 17 8.75 C 17.89 8.75 18.25 8.39 18.25 7.5 L 18.25 5.5 C 18.25 4.61 17.89 4.25 17 4.25 Z",
    fill: getIconColor(primaryColor, entity)
  }));
};

var SquareCheck = function (_a) {
  var primaryColor = _a.primaryColor,
    size = _a.size,
    onClick = _a.onClick,
    entity = _a.entity;
  return jsx("svg", {
    height: getIconSize(size, entity),
    width: getIconSize(size, entity),
    fill: "none",
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    onClick: onClick && function () {
      return onClick();
    }
  }, jsx("path", {
    d: "M15 22.75H9C3.57 22.75 1.25 20.43 1.25 15V9C1.25 3.57 3.57 1.25 9 1.25H15C20.43 1.25 22.75 3.57 22.75 9V15C22.75 20.43 20.43 22.75 15 22.75ZM9 2.75C4.39 2.75 2.75 4.39 2.75 9V15C2.75 19.61 4.39 21.25 9 21.25H15C19.61 21.25 21.25 19.61 21.25 15V9C21.25 4.39 19.61 2.75 15 2.75H9Z",
    fill: getIconColor(primaryColor, entity)
  }), jsx("path", {
    d: "M10.5795 15.5801C10.3795 15.5801 10.1895 15.5001 10.0495 15.3601L7.21945 12.5301C6.92945 12.2401 6.92945 11.7601 7.21945 11.4701C7.50945 11.1801 7.98945 11.1801 8.27945 11.4701L10.5795 13.7701L15.7195 8.6301C16.0095 8.3401 16.4895 8.3401 16.7795 8.6301C17.0695 8.9201 17.0695 9.4001 16.7795 9.6901L11.1095 15.3601C10.9695 15.5001 10.7795 15.5801 10.5795 15.5801Z",
    fill: getIconColor(primaryColor, entity)
  }));
};

var UserTag = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    onClick: onClick && function () {
      return onClick();
    },
    viewBox: "0 0 24 24"
  }, jsx("path", {
    d: "M 12 22.75 C 11.3 22.75 10.59 22.48 10.06 21.95 L 8.35 20.26 C 7.93 19.84 7.35 19.61 6.76 19.61 L 6 19.61 C 3.93 19.61 2.25 17.94 2.25 15.89 L 2.25 4.98 C 2.25 2.93 3.93 1.26 6 1.26 L 18 1.26 C 20.07 1.26 21.75 2.93 21.75 4.98 L 21.75 15.89 C 21.75 17.94 20.07 19.61 18 19.61 L 17.24 19.61 C 16.646 19.613 16.076 19.846 15.65 20.26 L 13.94 21.95 C 13.41 22.48 12.7 22.75 12 22.75 Z M 6 2.75 C 4.76 2.75 3.75 3.75 3.75 4.97 L 3.75 15.88 C 3.75 17.11 4.76 18.1 6 18.1 L 6.76 18.1 C 7.75 18.1 8.71 18.5 9.41 19.19 L 11.12 20.88 C 11.61 21.36 12.4 21.36 12.89 20.88 L 14.6 19.19 C 15.3 18.5 16.26 18.1 17.25 18.1 L 18 18.1 C 19.24 18.1 20.25 17.1 20.25 15.88 L 20.25 4.97 C 20.25 3.74 19.24 2.75 18 2.75 Z",
    fill: getIconColor(primaryColor, entity)
  }), jsx("path", {
    d: "M 12 10.75 C 10.3 10.75 8.92 9.37 8.92 7.67 C 8.963 6 10.329 4.667 12 4.667 C 13.671 4.667 15.037 6 15.08 7.67 C 15.08 9.37 13.7 10.75 12 10.75 Z M 12 6.09 C 11.13 6.09 10.42 6.8 10.42 7.67 C 10.42 8.54 11.13 9.25 12 9.25 C 12.87 9.25 13.58 8.54 13.58 7.67 C 13.58 6.8 12.87 6.09 12 6.09 Z M 16 16.41 C 15.59 16.41 15.25 16.07 15.25 15.66 C 15.25 14.28 13.79 13.15 12 13.15 C 10.21 13.15 8.75 14.28 8.75 15.66 C 8.75 16.07 8.41 16.41 8 16.41 C 7.59 16.41 7.25 16.07 7.25 15.66 C 7.25 13.45 9.38 11.65 12 11.65 C 14.62 11.65 16.75 13.45 16.75 15.66 C 16.75 16.07 16.41 16.41 16 16.41 Z",
    fill: getIconColor(primaryColor, entity)
  }));
};

var Map = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    viewBox: " 0 0 24 24",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    onClick: onClick && function () {
      return onClick();
    }
  }, jsx("rect", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    fill: "none",
    rx: "0",
    ry: "0"
  }), jsx("path", {
    d: "M 15.65 21.41 C 15.22 21.41 14.79 21.32 14.44 21.15 L 9.19 18.52 C 8.89 18.37 8.3 18.38 8.01 18.55 L 5.65 19.9 C 4.63 20.48 3.58 20.56 2.79 20.09 C 1.99 19.63 1.54 18.69 1.54 17.51 L 1.54 7.79 C 1.54 6.88 2.14 5.85 2.93 5.4 L 7.26 2.92 C 7.99 2.5 9.1 2.47 9.85 2.85 L 15.1 5.48 C 15.4 5.63 15.98 5.61 16.28 5.45 L 18.63 4.11 C 19.65 3.53 20.7 3.45 21.49 3.92 C 22.29 4.38 22.74 5.32 22.74 6.5 L 22.74 16.23 C 22.74 17.14 22.14 18.17 21.35 18.62 L 17.02 21.1 C 16.64 21.3 16.14 21.41 15.65 21.41 Z M 8.64 16.92 C 9.07 16.92 9.5 17.01 9.85 17.18 L 15.1 19.81 C 15.4 19.96 15.98 19.94 16.28 19.78 L 20.61 17.3 C 20.93 17.12 21.24 16.58 21.24 16.22 L 21.24 6.49 C 21.24 5.86 21.06 5.39 20.73 5.21 C 20.41 5.03 19.91 5.1 19.37 5.41 L 17.02 6.75 C 16.29 7.17 15.18 7.2 14.43 6.82 L 9.18 4.19 C 8.88 4.04 8.3 4.06 8 4.22 L 3.67 6.7 C 3.35 6.88 3.04 7.42 3.04 7.79 L 3.04 17.52 C 3.04 18.15 3.22 18.62 3.54 18.8 C 3.86 18.99 4.36 18.91 4.91 18.6 L 7.26 17.26 C 7.65 17.03 8.15 16.92 8.64 16.92 Z",
    fill: getIconColor(primaryColor, entity)
  }), jsx("path", {
    d: "M 8.56 17.75 C 8.15 17.75 7.81 17.41 7.81 17 L 7.81 4 C 7.81 3.59 8.15 3.25 8.56 3.25 C 8.97 3.25 9.31 3.59 9.31 4 L 9.31 17 C 9.31 17.41 8.97 17.75 8.56 17.75 Z M 15.73 20.75 C 15.32 20.75 14.98 20.41 14.98 20 L 14.98 6.62 C 14.98 6.21 15.32 5.87 15.73 5.87 C 16.14 5.87 16.48 6.21 16.48 6.62 L 16.48 20 C 16.48 20.41 16.14 20.75 15.73 20.75 Z",
    fill: getIconColor(primaryColor, entity)
  }));
};

var Location = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    viewBox: " 0 0 24 24",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    onClick: onClick && function () {
      return onClick();
    }
  }, jsx("rect", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    fill: "none",
    rx: "0",
    ry: "0"
  }), jsx("path", {
    d: "M 12 14.17 C 9.87 14.17 8.13 12.44 8.13 10.3 C 8.13 8.16 9.87 6.44 12 6.44 C 14.13 6.44 15.87 8.17 15.87 10.31 C 15.87 12.45 14.13 14.17 12 14.17 Z M 12 7.94 C 10.7 7.94 9.63 9 9.63 10.31 C 9.63 11.62 10.69 12.68 12 12.68 C 13.31 12.68 14.37 11.62 14.37 10.31 C 14.37 9 13.3 7.94 12 7.94 Z",
    fill: getIconColor(primaryColor, entity)
  }), jsx("path", {
    d: "M 12 22.76 C 10.459 22.757 8.98 22.159 7.87 21.09 C 4.92 18.25 1.66 13.72 2.89 8.33 C 4 3.44 8.27 1.25 12 1.25 L 12.01 1.25 C 15.74 1.25 20.01 3.44 21.12 8.34 C 22.34 13.73 19.08 18.25 16.13 21.09 C 15.02 22.159 13.541 22.757 12 22.76 Z M 12 2.75 C 9.09 2.75 5.35 4.3 4.36 8.66 C 3.28 13.37 6.24 17.43 8.92 20 C 10.638 21.67 13.372 21.67 15.09 20 C 17.76 17.43 20.72 13.37 19.66 8.66 C 18.66 4.3 14.91 2.75 12 2.75 Z",
    fill: getIconColor(primaryColor, entity)
  }));
};

var CreditCard = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    viewBox: " 0 0 24 24",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    onClick: onClick && function () {
      return onClick();
    }
  }, jsx("rect", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    fill: "none",
    rx: "0",
    ry: "0"
  }), jsx("path", {
    d: "M 0.25 6 C 0.25 4.481 1.481 3.25 3 3.25 L 21 3.25 C 22.519 3.25 23.75 4.481 23.75 6 L 23.75 18 C 23.75 19.519 22.519 20.75 21 20.75 L 3 20.75 C 1.481 20.75 0.25 19.519 0.25 18 Z M 3 4.75 C 2.31 4.75 1.75 5.31 1.75 6 L 1.75 7.25 L 22.25 7.25 L 22.25 6 C 22.25 5.31 21.69 4.75 21 4.75 Z M 22.25 8.75 L 1.75 8.75 L 1.75 18 C 1.75 18.69 2.31 19.25 3 19.25 L 21 19.25 C 21.69 19.25 22.25 18.69 22.25 18 Z M 4.25 16 C 4.25 15.586 4.586 15.25 5 15.25 L 11 15.25 C 11.414 15.25 11.75 15.586 11.75 16 C 11.75 16.414 11.414 16.75 11 16.75 L 5 16.75 C 4.586 16.75 4.25 16.414 4.25 16 Z",
    fill: getIconColor(primaryColor, entity)
  }));
};

var MapLocation = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    viewBox: " 0 0 24 24",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    onClick: onClick && function () {
      return onClick();
    }
  }, jsx("rect", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    fill: "none",
    rx: "0",
    ry: "0"
  }), jsx("path", {
    d: "M10.864,9.5c1.519,0,2.75-1.231,2.75-2.75S12.383,4,10.864,4c-1.519,0-2.75,1.231-2.75,2.75 S9.345,9.5,10.864,9.5z M10.864,5.5c0.691,0,1.25,0.56,1.25,1.25S11.555,8,10.864,8c-0.69,0-1.25-0.56-1.25-1.25 S10.174,5.5,10.864,5.5z",
    fill: getIconColor(primaryColor, entity)
  }), jsx("path", {
    d: "M21.553,17.784l-1.5-4C19.651,12.711,18.625,12,17.478,12h-1.556c0.945-1.562,1.692-3.382,1.692-5.25 c0-3.728-3.022-6.75-6.75-6.75c-3.728,0-6.75,3.022-6.75,6.75c0,1.868,0.747,3.688,1.692,5.25H4.25 c-1.146,0-2.172,0.711-2.575,1.784l-1.5,4c-0.317,0.845-0.199,1.791,0.315,2.532C1.003,21.058,1.848,21.5,2.75,21.5h16.228 c0.902,0,1.747-0.442,2.261-1.184C21.753,19.575,21.87,18.629,21.553,17.784z M5.614,6.75c0-2.899,2.351-5.25,5.25-5.25 c2.9,0,5.25,2.351,5.25,5.25c0,1.892-0.977,3.863-2.196,5.563c-0.719,0.992-1.517,1.924-2.385,2.789 c-0.218,0.219-0.441,0.434-0.669,0.644c-1.13-1.039-2.153-2.189-3.054-3.433C6.591,10.613,5.614,8.643,5.614,6.75z M20.005,19.462 C19.772,19.799,19.388,20,18.978,20H2.75c-0.41,0-0.793-0.201-1.027-0.538c-0.233-0.337-0.287-0.767-0.143-1.151l1.5-4 C3.263,13.823,3.729,13.5,4.25,13.5h2.57c0.707,0.941,1.48,1.831,2.313,2.662c0.372,0.372,0.757,0.733,1.152,1.08l0.071,0.061 l0.02,0.016l0.005,0.005l0.002,0.002h0.001c0.279,0.232,0.683,0.232,0.961,0v-0.001l0.002-0.001l0.005-0.005l0.02-0.016 c1.309-1.141,2.494-2.415,3.536-3.803h2.57c0.521,0,0.987,0.323,1.17,0.811l1.5,4C20.292,18.695,20.239,19.125,20.005,19.462z",
    fill: getIconColor(primaryColor, entity)
  }));
};

var Calendar = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    viewBox: " 0 0 24 24",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    onClick: onClick && function () {
      return onClick();
    }
  }, jsx("rect", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    fill: "none",
    rx: "0",
    ry: "0"
  }), jsx("path", {
    d: "M 8 5.75 C 7.59 5.75 7.25 5.41 7.25 5 L 7.25 2 C 7.25 1.59 7.59 1.25 8 1.25 C 8.41 1.25 8.75 1.59 8.75 2 L 8.75 5 C 8.75 5.41 8.41 5.75 8 5.75 Z M 16 5.75 C 15.59 5.75 15.25 5.41 15.25 5 L 15.25 2 C 15.25 1.59 15.59 1.25 16 1.25 C 16.41 1.25 16.75 1.59 16.75 2 L 16.75 5 C 16.75 5.41 16.41 5.75 16 5.75 Z M 8.5 14.5 C 8.37 14.5 8.24 14.47 8.12 14.42 C 7.99 14.37 7.89 14.3 7.79 14.21 C 7.61 14.02 7.5 13.77 7.5 13.5 C 7.5 13.37 7.53 13.24 7.58 13.12 C 7.63 13 7.7 12.89 7.79 12.79 C 7.89 12.7 7.99 12.63 8.12 12.58 C 8.48 12.43 8.93 12.51 9.21 12.79 C 9.39 12.98 9.5 13.24 9.5 13.5 C 9.5 13.56 9.49 13.63 9.48 13.7 C 9.47 13.76 9.45 13.82 9.42 13.88 C 9.4 13.94 9.37 14 9.33 14.06 C 9.3 14.11 9.25 14.16 9.21 14.21 C 9.02 14.39 8.76 14.5 8.5 14.5 Z M 12 14.5 C 11.87 14.5 11.74 14.47 11.62 14.42 C 11.49 14.37 11.39 14.3 11.29 14.21 C 11.11 14.02 11 13.77 11 13.5 C 11 13.37 11.03 13.24 11.08 13.12 C 11.13 13 11.2 12.89 11.29 12.79 C 11.39 12.7 11.49 12.63 11.62 12.58 C 11.98 12.42 12.43 12.51 12.71 12.79 C 12.89 12.98 13 13.24 13 13.5 C 13 13.56 12.99 13.63 12.98 13.7 C 12.97 13.76 12.95 13.82 12.92 13.88 C 12.9 13.94 12.87 14 12.83 14.06 C 12.8 14.11 12.75 14.16 12.71 14.21 C 12.52 14.39 12.26 14.5 12 14.5 Z M 15.5 14.5 C 15.37 14.5 15.24 14.47 15.12 14.42 C 14.99 14.37 14.89 14.3 14.79 14.21 L 14.67 14.06 C 14.632 14.004 14.602 13.944 14.58 13.88 C 14.551 13.823 14.531 13.763 14.52 13.7 C 14.51 13.63 14.5 13.56 14.5 13.5 C 14.5 13.24 14.61 12.98 14.79 12.79 C 14.89 12.7 14.99 12.63 15.12 12.58 C 15.49 12.42 15.93 12.51 16.21 12.79 C 16.39 12.98 16.5 13.24 16.5 13.5 C 16.5 13.56 16.49 13.63 16.48 13.7 C 16.47 13.76 16.45 13.82 16.42 13.88 C 16.4 13.94 16.37 14 16.33 14.06 C 16.3 14.11 16.25 14.16 16.21 14.21 C 16.02 14.39 15.76 14.5 15.5 14.5 Z M 8.5 18 C 8.37 18 8.24 17.97 8.12 17.92 C 8 17.87 7.89 17.8 7.79 17.71 C 7.61 17.52 7.5 17.26 7.5 17 C 7.5 16.87 7.53 16.74 7.58 16.62 C 7.63 16.49 7.7 16.38 7.79 16.29 C 8.16 15.92 8.84 15.92 9.21 16.29 C 9.39 16.48 9.5 16.74 9.5 17 C 9.5 17.26 9.39 17.52 9.21 17.71 C 9.02 17.89 8.76 18 8.5 18 Z M 12 18 C 11.74 18 11.48 17.89 11.29 17.71 C 11.11 17.52 11 17.26 11 17 C 11 16.87 11.03 16.74 11.08 16.62 C 11.13 16.49 11.2 16.38 11.29 16.29 C 11.66 15.92 12.34 15.92 12.71 16.29 C 12.8 16.38 12.87 16.49 12.92 16.62 C 12.97 16.74 13 16.87 13 17 C 13 17.26 12.89 17.52 12.71 17.71 C 12.52 17.89 12.26 18 12 18 Z M 15.5 18 C 15.24 18 14.98 17.89 14.79 17.71 C 14.697 17.616 14.626 17.504 14.58 17.38 C 14.529 17.26 14.501 17.131 14.5 17 C 14.5 16.87 14.53 16.74 14.58 16.62 C 14.63 16.49 14.7 16.38 14.79 16.29 C 15.02 16.06 15.37 15.95 15.69 16.02 C 15.76 16.03 15.82 16.05 15.88 16.08 C 15.94 16.1 16 16.13 16.06 16.17 C 16.11 16.2 16.16 16.25 16.21 16.29 C 16.39 16.48 16.5 16.74 16.5 17 C 16.5 17.26 16.39 17.52 16.21 17.71 C 16.02 17.89 15.76 18 15.5 18 Z M 20.5 9.84 L 3.5 9.84 C 3.09 9.84 2.75 9.5 2.75 9.09 C 2.75 8.68 3.09 8.34 3.5 8.34 L 20.5 8.34 C 20.91 8.34 21.25 8.68 21.25 9.09 C 21.25 9.5 20.91 9.84 20.5 9.84 Z",
    fill: getIconColor(primaryColor, entity)
  }), jsx("path", {
    d: "M 16 22.75 L 8 22.75 C 4.35 22.75 2.25 20.65 2.25 17 L 2.25 8.5 C 2.25 4.85 4.35 2.75 8 2.75 L 16 2.75 C 19.65 2.75 21.75 4.85 21.75 8.5 L 21.75 17 C 21.75 20.65 19.65 22.75 16 22.75 Z M 8 4.25 C 5.14 4.25 3.75 5.64 3.75 8.5 L 3.75 17 C 3.75 19.86 5.14 21.25 8 21.25 L 16 21.25 C 18.86 21.25 20.25 19.86 20.25 17 L 20.25 8.5 C 20.25 5.64 18.86 4.25 16 4.25 Z",
    fill: getIconColor(primaryColor, entity)
  }));
};

var Home = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    viewBox: " 0 0 24 24",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    onClick: onClick && function () {
      return onClick();
    }
  }, jsx("rect", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    fill: "none",
    rx: "0",
    ry: "0"
  }), jsx("path", {
    d: "M 17.79 22.75 L 6.21 22.75 C 3.47 22.75 1.25 20.52 1.25 17.78 L 1.25 10.37 C 1.25 9.01 2.09 7.3 3.17 6.46 L 8.56 2.26 C 10.18 1 12.77 0.94 14.45 2.12 L 20.63 6.45 C 21.82 7.28 22.75 9.06 22.75 10.51 L 22.75 17.79 C 22.75 20.52 20.53 22.75 17.79 22.75 Z M 9.48 3.44 L 4.09 7.64 C 3.38 8.2 2.75 9.47 2.75 10.37 L 2.75 17.78 C 2.75 19.693 4.297 21.244 6.21 21.25 L 17.79 21.25 C 19.7 21.25 21.25 19.7 21.25 17.79 L 21.25 10.51 C 21.25 9.55 20.56 8.22 19.77 7.68 L 13.59 3.35 C 12.45 2.55 10.57 2.59 9.48 3.44 Z",
    fill: getIconColor(primaryColor, entity)
  }), jsx("path", {
    d: "M 12 18.75 C 11.59 18.75 11.25 18.41 11.25 18 L 11.25 15 C 11.25 14.59 11.59 14.25 12 14.25 C 12.41 14.25 12.75 14.59 12.75 15 L 12.75 18 C 12.75 18.41 12.41 18.75 12 18.75 Z",
    fill: getIconColor(primaryColor, entity)
  }));
};

var Bag = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    onClick: onClick && function () {
      return onClick();
    },
    viewBox: "0 0 24 24"
  }, jsx("path", {
    d: "M 6 8.318 C 6.415 8.318 6.75 8.623 6.75 9 C 6.75 10.13 7.758 11.045 9 11.045 C 10.243 11.045 11.25 10.13 11.25 9 C 11.25 8.623 11.586 8.318 12 8.318 C 12.415 8.318 12.75 8.623 12.75 9 C 12.75 10.883 11.071 12.409 9 12.409 C 6.929 12.409 5.25 10.883 5.25 9 C 5.25 8.623 5.586 8.318 6 8.318 Z",
    fill: getIconColor(primaryColor, entity)
  }), jsx("path", {
    d: "M 12.75 5.591 L 14.354 5.591 C 15.534 5.591 16.514 6.42 16.598 7.491 L 17.019 12.848 C 17.174 14.821 15.455 16.5 13.278 16.5 L 4.722 16.5 C 2.546 16.5 0.827 14.821 0.982 12.848 L 1.403 7.491 C 1.487 6.42 2.467 5.591 3.647 5.591 L 5.25 5.591 L 5.25 4.909 C 5.25 3.026 6.929 1.5 9 1.5 C 11.071 1.5 12.75 3.026 12.75 4.909 Z M 6.75 5.591 L 11.25 5.591 L 11.25 4.909 C 11.25 3.779 10.243 2.864 9 2.864 C 7.758 2.864 6.75 3.779 6.75 4.909 Z M 3.647 6.954 C 3.254 6.954 2.927 7.231 2.899 7.588 L 2.478 12.945 C 2.385 14.129 3.416 15.136 4.722 15.136 L 13.278 15.136 C 14.584 15.136 15.616 14.129 15.523 12.945 L 15.102 7.588 C 15.074 7.231 14.747 6.954 14.354 6.954 Z",
    fill: getIconColor(primaryColor, entity)
  }));
};

var Swap = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    fill: getIconColor(primaryColor, entity),
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    onClick: onClick && function () {
      return onClick();
    }
  }, jsx("path", {
    d: "M17.2788 11.2002C17.0888 11.2002 16.8988 11.1302 16.7488 10.9802C16.4588 10.6902 16.4588 10.2102 16.7488 9.9202L19.9388 6.7302L16.7488 3.54019C16.4588 3.25019 16.4588 2.7702 16.7488 2.4802C17.0387 2.1902 17.5187 2.1902 17.8087 2.4802L21.5288 6.20023C21.6688 6.34023 21.7488 6.5302 21.7488 6.7302C21.7488 6.9302 21.6688 7.12022 21.5288 7.26022L17.8087 10.9802C17.6587 11.1202 17.4688 11.2002 17.2788 11.2002Z",
    fill: getIconColor(primaryColor, entity)
  }), jsx("path", {
    d: "M21 7.47998H3C2.59 7.47998 2.25 7.13998 2.25 6.72998 2.25 6.31998 2.59 5.97998 3 5.97998H21C21.41 5.97998 21.75 6.31998 21.75 6.72998 21.75 7.13998 21.41 7.47998 21 7.47998zM6.71997 21.75C6.52997 21.75 6.34 21.68 6.19 21.53L2.46997 17.81C2.32997 17.67 2.25 17.48 2.25 17.28 2.25 17.08 2.32997 16.89 2.46997 16.75L6.19 13.03C6.48 12.74 6.96 12.74 7.25 13.03 7.54 13.32 7.54 13.8 7.25 14.09L4.06 17.28 7.25 20.4699C7.54 20.7599 7.54 21.24 7.25 21.53 7.11 21.68 6.91997 21.75 6.71997 21.75z",
    fill: getIconColor(primaryColor, entity)
  }), jsx("path", {
    d: "M21 18.02H3C2.59 18.02 2.25 17.68 2.25 17.27C2.25 16.86 2.59 16.52 3 16.52H21C21.41 16.52 21.75 16.86 21.75 17.27C21.75 17.68 21.41 18.02 21 18.02Z",
    fill: getIconColor(primaryColor, entity)
  }));
};

var Warning = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    fill: "none",
    stroke: getIconColor(primaryColor, entity),
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, jsx("path", {
    d: "M13.0718 4.24418C12.5863 3.43501 11.4136 3.43501 10.9281 4.24418L2.46046 18.3569C1.96056 19.19 2.56071 20.25 3.53232 20.25H20.4676C21.4392 20.25 22.0393 19.19 21.5394 18.3569L13.0718 4.24418ZM9.64184 3.47243C10.7099 1.69225 13.2899 1.69226 14.358 3.47244L22.8257 17.5851C23.9254 19.4181 22.6051 21.75 20.4676 21.75H3.53232C1.39476 21.75 0.0744533 19.4181 1.17422 17.5851L9.64184 3.47243ZM11.9999 8.25C12.4142 8.25 12.7499 8.58578 12.7499 9V13C12.7499 13.4142 12.4142 13.75 11.9999 13.75C11.5857 13.75 11.2499 13.4142 11.2499 13V9C11.2499 8.58578 11.5857 8.25 11.9999 8.25ZM11.9999 15.25C12.4142 15.25 12.7499 15.5858 12.7499 16V17C12.7499 17.4142 12.4142 17.75 11.9999 17.75C11.5857 17.75 11.2499 17.4142 11.2499 17V16C11.2499 15.5858 11.5857 15.25 11.9999 15.25Z",
    fill: getIconColor(primaryColor, entity),
    fillRule: "evenodd"
  }));
};

var Coaching = function (_a) {
  var primaryColor = _a.primaryColor,
    onClick = _a.onClick,
    size = _a.size,
    entity = _a.entity;
  return jsx("svg", {
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    fill: getIconColor(primaryColor, entity),
    viewBox: "0 0 16 16",
    xmlns: "http://www.w3.org/2000/svg",
    onClick: onClick && function () {
      return onClick();
    }
  }, jsx("path", {
    d: "M4 3.2001C4 2.3176 4.7175 1.6001 5.6 1.6001H14.4C15.2825 1.6001 16 2.3176 16 3.2001V10.4001C16 11.2826 15.2825 12.0001 14.4 12.0001H8.42C8.125 11.3626 7.6725 10.8126 7.11 10.4001H9.6V9.6001C9.6 9.1576 9.9575 8.8001 10.4 8.8001H12C12.4425 8.8001 12.8 9.1576 12.8 9.6001V10.4001H14.4V3.2001H5.6V4.4276C5.13 4.1551 4.5825 4.0001 4 4.0001V3.2001ZM4 9.6001C2.675 9.6001 1.6 8.5251 1.6 7.2001C1.6 5.8751 2.675 4.8001 4 4.8001C5.325 4.8001 6.4 5.8751 6.4 7.2001C6.4 8.5251 5.325 9.6001 4 9.6001ZM3.3325 10.4001H4.665C6.5075 10.4001 8 11.8926 8 13.7326C8 14.1001 7.7025 14.4001 7.3325 14.4001H0.6675C0.2975 14.4001 0 14.1026 0 13.7326C0 11.8926 1.4925 10.4001 3.3325 10.4001Z",
    fill: getIconColor(primaryColor, entity)
  }));
};

var PromptingsIcon$1 = function (_a) {
  var primaryColor = _a.primaryColor,
    size = _a.size,
    onClick = _a.onClick,
    entity = _a.entity;
  return jsx("svg", {
    onClick: onClick && function () {
      return onClick();
    },
    width: getIconSize(size, entity),
    height: getIconSize(size, entity),
    viewBox: "0 0 20 21",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, jsx("g", {
    clipPath: "url(#a)"
  }, jsx("path", {
    d: "M12.416 4.283c-2.512.949-3.08 3.579-3.722 5.696-.92.079-1.72.295-2.745.747l-.021.025c.7-.101 1.866-.05 2.475.207-.387 1.422-.709 2.933-1.551 4.142-.955 1.475-3.41 1.634-4.567.267-1.124-1.263-.318-3.462 1.345-4.01-1.848.515-2.875 2.799-1.812 4.302 1.125 1.656 4.03 1.986 5.691.813 1.598-1.228 1.852-3.228 2.28-4.952.039-.158.075-.316.112-.474 2.515-.099 5.527.087 7.264-1.94 2.63-3.061-1.049-6.518-4.75-4.823Zm2.8 4.744c-1.512.747-3.352.742-5.049.925.276-1.073.61-2.142 1.152-3.105.757-1.35 2.86-2.229 4.388-1.46 1.585.863.945 2.933-.491 3.64Z",
    fill: getIconColor(primaryColor, entity)
  })), jsx("defs", null, jsx("clipPath", {
    id: "a"
  }, jsx("path", {
    fill: "#fff",
    d: "M0 .5h20v20H0z"
  }))));
};

var Icon = function (_a) {
  var primaryColor = _a.primaryColor,
    secondaryColor = _a.secondaryColor,
    onClick = _a.onClick,
    name = _a.name,
    size = _a.size,
    orientation = _a.orientation;
  var icons = useEntities().icons;
  var entity = icons;
  var props = {
    primaryColor: primaryColor,
    secondaryColor: secondaryColor,
    onClick: onClick,
    name: name,
    size: size,
    entity: entity,
    orientation: orientation
  };
  switch (props.name) {
    case 'catalog':
      return jsx(Catalog, props);
    case 'socLogo':
      return jsx(SOCLogo$1, props);
    case 'campaigns':
      return jsx(Campaigns, props);
    case 'gift':
      return jsx(Gift, props);
    case 'tenDoor':
      return jsx(TenDoor, props);
    case 'rightChevron':
      return jsx(RightChevron, props);
    case 'leftChevron':
      return jsx(LeftChevron, props);
    case 'chevron':
      return jsx(Chevron, props);
    case 'close':
      return jsx(Close, props);
    case 'award':
      return jsx(Award, props);
    case 'allPanel':
      return jsx(AllPanel, props);
    case 'contact':
      return jsx(Contact, props);
    case 'multipleUsers2x':
      return jsx(MultipleUsers2x, props);
    case 'multipleUsers3x':
      return jsx(MultipleUsers3x, props);
    case 'information':
      return jsx(Information, props);
    case 'hamburger':
      return jsx(Hamburger, props);
    case 'plus':
      return jsx(Plus, props);
    case 'plusSquare':
      return jsx(PlusSquare, props);
    case 'minus':
      return jsx(Minus, props);
    case 'filterBy':
      return jsx(FilterBy, props);
    case 'fullBleed':
      return jsx(FullBleed, props);
    case 'groups':
      return jsx(Groups, props);
    case 'idCard':
      return jsx(IDCard, props);
    case 'star':
      return jsx(Star, props);
    case 'search':
      return jsx(Search, props);
    case 'cart':
      return jsx(Cart, props);
    case 'sliderHorizontal':
      return jsx(Slider$1, props);
    case 'sliderVertical':
      return jsx(SliderVertical, props);
    case 'priceTag':
      return jsx(PriceTag, props);
    case 'layer':
      return jsx(Layer, props);
    case 'layerUp':
      return jsx(LayerUp, props);
    case 'layerDown':
      return jsx(LayerDown, props);
    case 'loading':
      return jsx(Loading, props);
    case 'play':
      return jsx(Play, props);
    case 'lock':
      return jsx(Lock, props);
    case 'user':
      return jsx(User, props);
    case 'noUsers':
      return jsx(NoUsers, props);
    case 'add':
      return jsx(Add, props);
    case 'check':
      return jsx(Check, props);
    case 'exclamation':
      return jsx(Exclamation, props);
    case 'envelope':
      return jsx(Envelope, props);
    case 'userSuccess':
      return jsx(UserSuccess, props);
    case 'image':
      return jsx(Image$2, props);
    case 'imageSwap':
      return jsx(ImageSwap, props);
    case 'text':
      return jsx(Text$1, props);
    case 'color':
      return jsx(Color, props);
    case 'template':
      return jsx(Template, props);
    case 'redo':
      return jsx(Redo, props);
    case 'undo':
      return jsx(Undo, props);
    case 'copy':
      return jsx(Copy, props);
    case 'drag':
      return jsx(Drag, props);
    case 'delete':
      return jsx(Delete, props);
    case 'crop':
      return jsx(Crop, props);
    case 'alignCenter':
      return jsx(AlignCenter, props);
    case 'alignLeft':
      return jsx(AlignLeft, props);
    case 'alignRight':
      return jsx(AlignRight, props);
    case 'fullBleedLandscape':
      return jsx(FullBleedLandscape, props);
    case 'fullBleedPortrait':
      return jsx(FullBleedPortrait, props);
    case 'cropRotate':
      return jsx(CropRotate, props);
    case 'frame':
      return jsx(Frame, props);
    case 'cut':
      return jsx(Cut, props);
    case 'wand':
      return jsx(Wand, props);
    case 'layerAdd':
      return jsx(LayerAdd, props);
    case 'layerRemove':
      return jsx(LayerRemove, props);
    case 'layerDisabled':
      return jsx(LayerDisabled, props);
    case 'signature':
      return jsx(Signature, props);
    case 'shadows':
      return jsx(Shadows, props);
    case 'rotateLeft':
      return jsx(RotateLeft, props);
    case 'rotateRight':
      return jsx(RotateRight, props);
    case 'colorPalette':
      return jsx(ColorPalette, props);
    case 'dropper':
      return jsx(Dropper, props);
    case 'textBox':
      return jsx(TextBox, props);
    case 'fontSize':
      return jsx(FontSize, props);
    case 'flipHorizontal':
      return jsx(FlipHorizontal, props);
    case 'flipVertical':
      return jsx(FlipVertical, props);
    case 'filter':
      return jsx(Filter, props);
    case 'brightness':
      return jsx(Brightness, props);
    case 'aText':
      return jsx(AText, props);
    case 'frameWidth':
      return jsx(FrameWidth, props);
    case 'frameHeight':
      return jsx(FrameHeight, props);
    case 'capitalizedText':
      return jsx(CapitalizedText, props);
    case 'uppercaseText':
      return jsx(UppercaseText, props);
    case 'lowercaseText':
      return jsx(LowercaseText, props);
    case 'fullBleedImage':
      return jsx(FullBleedImage, props);
    case 'fullBleedText':
      return jsx(FullBleedText, props);
    case 'panelMode':
      return jsx(PanelMode, props);
    case 'viewPanels':
      return jsx(ViewPanels, props);
    case 'rotate':
      return jsx(Rotate, props);
    case 'view':
      return jsx(View, props);
    case 'replaceImage':
      return jsx(ReplaceImage, props);
    case 'quotation':
      return jsx(Quotation, props);
    case 'facebook':
      return jsx(Facebook, props);
    case 'instagram':
      return jsx(Instagram, props);
    case 'twitter':
      return jsx(Twitter, props);
    case 'linkedIn':
      return jsx(LinkedIn, props);
    case 'youtube':
      return jsx(YouTube, props);
    case 'pinterest':
      return jsx(Pinterest, props);
    case 'visible':
      return jsx(Visible, props);
    case 'invisible':
      return jsx(Invisible, props);
    case 'elementIcon':
      return jsx(ElementIcon, props);
    case 'complete':
      return jsx(Complete, props);
    case 'download':
      return jsx(Download, props);
    case 'closeCircle':
      return jsx(CloseCircle, props);
    case 'backgroundSettings':
      return jsx(BackgroundSettings, props);
    case 'upload':
      return jsx(Upload, props);
    case 'stickers':
      return jsx(Stickers, props);
    case 'clearPanel':
      return jsx(ClearPanel, props);
    case 'viewCarousel':
      return jsx(ViewCarousel, props);
    case 'viewSinglePanel':
      return jsx(ViewSinglePanel, props);
    case 'editorText':
      return jsx(EdtiorText, props);
    case 'layout':
      return jsx(Layout, props);
    case 'background':
      return jsx(Background, props);
    case 'save':
      return jsx(Save, props);
    case 'signaturePen':
      return jsx(SignaturePen, props);
    case 'size':
      return jsx(Size, props);
    case 'variable':
      return jsx(Variable, props);
    case 'imageUpload':
      return jsx(ImageUpload, props);
    case 'squareCheck':
      return jsx(SquareCheck, props);
    case 'userTag':
      return jsx(UserTag, props);
    case 'map':
      return jsx(Map, props);
    case 'mapLocation':
      return jsx(MapLocation, props);
    case 'location':
      return jsx(Location, props);
    case 'creditCard':
      return jsx(CreditCard, props);
    case 'calendar':
      return jsx(Calendar, props);
    case 'home':
      return jsx(Home, props);
    case 'bag':
      return jsx(Bag, props);
    case 'swap':
      return jsx(Swap, props);
    case 'warning':
      return jsx(Warning, props);
    case 'coaching':
      return jsx(Coaching, props);
    case 'promptingsIcon':
      return jsx(PromptingsIcon$1, props);
  }
};

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}

var _emotionSourceMap3$k = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFLcXVCIiwiZmlsZSI6InN0eWxlcy50cyIsInNvdXJjZXNDb250ZW50IjpbImltcG9ydCB7IF9fbWFrZVRlbXBsYXRlT2JqZWN0IH0gZnJvbSBcInRzbGliXCI7XHJcbmltcG9ydCB7IGNzcyB9IGZyb20gJ0BlbW90aW9uL2NvcmUnO1xyXG5leHBvcnQgZGVmYXVsdCB7XHJcbiAgICBhbmNob3JTdHlsZXM6IGZ1bmN0aW9uIChnZW5lcmF0ZWRTdHlsZXMsIHRoZW1lLCBjb2xvciwgZGVjb3JhdGVkKSB7IHJldHVybiBjc3ModGVtcGxhdGVPYmplY3RfMyB8fCAodGVtcGxhdGVPYmplY3RfMyA9IF9fbWFrZVRlbXBsYXRlT2JqZWN0KFtcIlxcbiAgICBcIiwgXCI7XFxuICAgIFwiLCBcIlxcbiAgICB3aGl0ZS1zcGFjZTogbm93cmFwO1xcbiAgICBmb250LWZhbWlseTogaW5oZXJpdDtcXG4gICAgY3Vyc29yOiBwb2ludGVyO1xcbiAgXCJdLCBbXCJcXG4gICAgXCIsIFwiO1xcbiAgICBcIixcclxuICAgICAgICBcIlxcbiAgICB3aGl0ZS1zcGFjZTogbm93cmFwO1xcbiAgICBmb250LWZhbWlseTogaW5oZXJpdDtcXG4gICAgY3Vyc29yOiBwb2ludGVyO1xcbiAgXCJdKSksIGdlbmVyYXRlZFN0eWxlcywgZGVjb3JhdGVkXHJcbiAgICAgICAgPyBjc3ModGVtcGxhdGVPYmplY3RfMSB8fCAodGVtcGxhdGVPYmplY3RfMSA9IF9fbWFrZVRlbXBsYXRlT2JqZWN0KFtcIlxcbiAgICAgICAgICB0ZXh0LWRlY29yYXRpb246IHVuZGVybGluZTtcXG4gICAgICAgICAgdGV4dC1kZWNvcmF0aW9uLWNvbG9yOiBcIiwgXCI7XFxuICAgICAgICAgIHNwYW4ge1xcbiAgICAgICAgICAgIGNvbG9yOiBcIiwgXCI7XFxuICAgICAgICAgIH1cXG4gICAgICAgICAgOnZpc2l0ZWQge1xcbiAgICAgICAgICAgIGNvbG9yOiBcIiwgXCI7XFxuICAgICAgICAgICAgPiBzcGFuIHtcXG4gICAgICAgICAgICAgIGNvbG9yOiBcIiwgXCI7XFxuICAgICAgICAgICAgfVxcbiAgICAgICAgICB9XFxuICAgICAgICBcIl0sIFtcIlxcbiAgICAgICAgICB0ZXh0LWRlY29yYXRpb246IHVuZGVybGluZTtcXG4gICAgICAgICAgdGV4dC1kZWNvcmF0aW9uLWNvbG9yOiBcIiwgXCI7XFxuICAgICAgICAgIHNwYW4ge1xcbiAgICAgICAgICAgIGNvbG9yOiBcIiwgXCI7XFxuICAgICAgICAgIH1cXG4gICAgICAgICAgOnZpc2l0ZWQge1xcbiAgICAgICAgICAgIGNvbG9yOiBcIiwgXCI7XFxuICAgICAgICAgICAgPiBzcGFuIHtcXG4gICAgICAgICAgICAgIGNvbG9yOiBcIiwgXCI7XFxuICAgICAgICAgICAgfVxcbiAgICAgICAgICB9XFxuICAgICAgICBcIl0pKSwgY29sb3IsIGNvbG9yLCB0aGVtZS5ob2NzLmNvbG9ycy5zd2F0Y2hlcy5wcmltYXJ5QnJhbmQuYmFzZSwgdGhlbWUuaG9jcy5jb2xvcnMuc3dhdGNoZXMucHJpbWFyeUJyYW5kLmJhc2UpIDogY3NzKHRlbXBsYXRlT2JqZWN0XzIgfHwgKHRlbXBsYXRlT2JqZWN0XzIgPSBfX21ha2VUZW1wbGF0ZU9iamVjdChbXCJcXG4gICAgICAgICAgdGV4dC1kZWNvcmF0aW9uOiBub25lO1xcbiAgICAgICAgICBzcGFuIHtcXG4gICAgICAgICAgICBjb2xvcjogXCIsIFwiO1xcbiAgICAgICAgICB9XFxuICAgICAgICBcIl0sIFtcIlxcbiAgICAgICAgICB0ZXh0LWRlY29yYXRpb246IG5vbmU7XFxuICAgICAgICAgIHNwYW4ge1xcbiAgICAgICAgICAgIGNvbG9yOiBcIiwgXCI7XFxuICAgICAgICAgIH1cXG4gICAgICAgIFwiXSkpLCBjb2xvcikpOyB9LFxyXG59O1xyXG52YXIgdGVtcGxhdGVPYmplY3RfMSwgdGVtcGxhdGVPYmplY3RfMiwgdGVtcGxhdGVPYmplY3RfMztcclxuLy8jIHNvdXJjZU1hcHBpbmdVUkw9c3R5bGVzLmpzLm1hcCJdfQ== */";
var _emotionSourceMap2$w = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap$X = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var styles$T = {
  anchorStyles: function (generatedStyles, theme, color, decorated) {
    return /*#__PURE__*/css(templateObject_3$k || (templateObject_3$k = __makeTemplateObject(["\n    ", ";\n    ", "\n    white-space: nowrap;\n    font-family: inherit;\n    cursor: pointer;\n  " + _emotionSourceMap$X], ["\n    ", ";\n    ", "\n    white-space: nowrap;\n    font-family: inherit;\n    cursor: pointer;\n  " + _emotionSourceMap$X])), generatedStyles, decorated ? /*#__PURE__*/css(templateObject_1$X || (templateObject_1$X = __makeTemplateObject(["\n          text-decoration: underline;\n          text-decoration-color: ", ";\n          span {\n            color: ", ";\n          }\n          :visited {\n            color: ", ";\n            > span {\n              color: ", ";\n            }\n          }\n        " + _emotionSourceMap2$w], ["\n          text-decoration: underline;\n          text-decoration-color: ", ";\n          span {\n            color: ", ";\n          }\n          :visited {\n            color: ", ";\n            > span {\n              color: ", ";\n            }\n          }\n        " + _emotionSourceMap2$w])), color, color, theme.hocs.colors.swatches.primaryBrand.base, theme.hocs.colors.swatches.primaryBrand.base) : /*#__PURE__*/css(templateObject_2$w || (templateObject_2$w = __makeTemplateObject(["\n          text-decoration: none;\n          span {\n            color: ", ";\n          }\n        " + _emotionSourceMap3$k], ["\n          text-decoration: none;\n          span {\n            color: ", ";\n          }\n        " + _emotionSourceMap3$k])), color));
  }
};
var templateObject_1$X, templateObject_2$w, templateObject_3$k;

var mediaQueries$3 = entities$9.mediaQueries;
var makeResponsive = function (property, value, entity) {
  var style = {};
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    style["" + property] = entity && entity["" + value] || "" + value;
  } else {
    Object.keys(value).forEach(function (key) {
      var _a;
      if (key === 'xSmall') {
        style[property] = entity && entity["" + value[key]] || "" + value[key];
      } else {
        style[mediaQueries$3.queries[key]] = (_a = {}, _a[property] = entity && entity["" + value[key]] || "" + value[key], _a);
      }
    });
  }
  return style;
};

var mapArrayToObject = function (input) {
  var output = {};
  input.forEach(function (item) {
    openObject(item, output);
    function openObject(objectToOpen, setOpenObjectTo) {
      if (!objectToOpen) {
        return;
      }
      Object.keys(objectToOpen).forEach(function (key) {
        if (key === undefined) {
          return;
        } else if (typeof setOpenObjectTo[key] === 'object') {
          openObject(objectToOpen[key], setOpenObjectTo[key]);
        } else {
          setOpenObjectTo[key] = objectToOpen[key];
        }
      });
    }
  });
  return output;
};

var isResponsiveType = function (valueToCheck) {
  var responsive = valueToCheck;
  if ((responsive.xSmall || responsive.small || responsive.medium || responsive.large || responsive.xLarge) !== undefined) {
    return true;
  }
  return false;
};
var isBorderRadiusDirectionType = function (valueToCheck) {
  var borderRadius = valueToCheck;
  if ((borderRadius.bottom || borderRadius.bottomLeft || borderRadius.bottomRight || borderRadius.top || borderRadius.topLeft || borderRadius.topRight || borderRadius.right || borderRadius.left) !== undefined) {
    return true;
  }
  return false;
};
var isSpacingDirectionType = function (valueToCheck) {
  var spacing = valueToCheck;
  if ((spacing.bottom || spacing.top || spacing.right || spacing.horizontal || spacing.vertical || spacing.left) !== undefined) {
    return true;
  }
  return false;
};
var isColorSelectionType = function (valueToCheck) {
  return valueToCheck.swatch !== undefined;
};
var isTextType = function (valueToCheck) {
  var textType = valueToCheck;
  if (textType === 'body' || textType === 'caption' || textType === 'footnote' || textType === 'heroTitle' || textType === 'largeBody' || textType === 'subtitle' || textType === 'title') {
    return true;
  }
  return false;
};

var findDirection = function (property, value, entity) {
  var style = [];
  if (typeof value === 'string' || isResponsiveType(value)) {
    style.push(makeResponsive(property, value, entity));
  } else {
    if (isSpacingDirectionType(value)) {
      if (value.horizontal !== undefined) {
        style.push(makeResponsive(property + "Left", value.horizontal, entity));
        style.push(makeResponsive(property + "Right", value.horizontal, entity));
      }
      if (value.vertical !== undefined) {
        style.push(makeResponsive(property + "Top", value.vertical, entity));
        style.push(makeResponsive(property + "Bottom", value.vertical, entity));
      }
      if (value.bottom !== undefined) {
        style.push(makeResponsive(property + "Bottom", value.bottom, entity));
      }
      if (value.top !== undefined) {
        style.push(makeResponsive(property + "Top", value.top, entity));
      }
      if (value.left !== undefined) {
        style.push(makeResponsive(property + "Left", value.left, entity));
      }
      if (value.right !== undefined) {
        style.push(makeResponsive(property + "Right", value.right, entity));
      }
    }
  }
  return mapArrayToObject(style);
};

var isBorderWidthDirectionType = function (valueToCheck) {
  var borderRadius = valueToCheck;
  if ((borderRadius.bottom || borderRadius.top || borderRadius.right || borderRadius.left) !== undefined) {
    return true;
  }
  return false;
};
var findBorderDirection = function (property, value, entity) {
  var propertyCapitalized = property.charAt(0).toUpperCase() + property.slice(1);
  var style = [];
  if (typeof value === 'string' || isResponsiveType(value)) {
    style.push(makeResponsive("border" + propertyCapitalized, value, entity));
  } else {
    if (isBorderWidthDirectionType(value)) {
      if (value.bottom !== undefined) {
        style.push(makeResponsive("borderBottom" + propertyCapitalized, value.bottom, entity));
      }
      if (value.top !== undefined) {
        style.push(makeResponsive("borderTop" + propertyCapitalized, value.top, entity));
      }
      if (value.left !== undefined) {
        style.push(makeResponsive("borderLeft" + propertyCapitalized, value.left, entity));
      }
      if (value.right !== undefined) {
        style.push(makeResponsive("borderRight" + propertyCapitalized, value.right, entity));
      }
    }
  }
  return mapArrayToObject(style);
};

var findBorderRadiusDirection = function (value, entity) {
  var style = [];
  if (typeof value === 'string' || isResponsiveType(value)) {
    style.push(makeResponsive('borderRadius', value, entity));
  } else {
    if (isBorderRadiusDirectionType(value)) {
      if (value.bottom !== undefined) {
        style.push(makeResponsive('borderBottomRightRadius', value.bottom, entity));
        style.push(makeResponsive('borderBottomLeftRadius', value.bottom, entity));
      }
      if (value.bottomLeft !== undefined) {
        style.push(makeResponsive('borderBottomLeftRadius', value.bottomLeft, entity));
      }
      if (value.bottomRight !== undefined) {
        style.push(makeResponsive('borderBottomRightRadius', value.bottomRight, entity));
      }
      if (value.top !== undefined) {
        style.push(makeResponsive('borderTopLeftRadius', value.top, entity));
        style.push(makeResponsive('borderTopRightRadius', value.top, entity));
      }
      if (value.topLeft !== undefined) {
        style.push(makeResponsive('borderTopLeftRadius', value.topLeft, entity));
      }
      if (value.topRight !== undefined) {
        style.push(makeResponsive('borderTopRightRadius', value.topRight, entity));
      }
      if (value.left !== undefined) {
        style.push(makeResponsive('borderTopLeftRadius', value.left, entity));
        style.push(makeResponsive('borderBottomLeftRadius', value.left, entity));
      }
      if (value.right !== undefined) {
        style.push(makeResponsive('borderTopRightRadius', value.right, entity));
        style.push(makeResponsive('borderBottomRightRadius', value.right, entity));
      }
    }
  }
  return mapArrayToObject(style);
};

var findColor = function (property, value, entity) {
  var style = [];
  if (isColorSelectionType(value)) {
    var swatch = entity.swatches[value.swatch];
    var color = swatch[value.shade];
    if (color !== undefined) {
      style.push(makeResponsive(property, color));
    }
  } else {
    if (isResponsiveType(value)) {
      style.push(makeResponsive(property, value, entity));
    } else {
      if (entity[value] !== undefined) {
        style.push(makeResponsive(property, entity[value]));
        return mapArrayToObject(style);
      } else {
        if (typeof value === 'string') {
          style.push(makeResponsive(property, value));
          return mapArrayToObject(style);
        }
      }
    }
  }
  return mapArrayToObject(style);
};

var generateStyles = function (props, entities) {
  var inset = props.inset,
    outset = props.outset,
    borderRadius = props.borderRadius,
    borderWidth = props.borderWidth,
    borderStyle = props.borderStyle,
    backgroundColor = props.backgroundColor,
    borderColor = props.borderColor,
    boxShadow = props.boxShadow,
    height = props.height,
    width = props.width,
    maxHeight = props.maxHeight,
    maxWidth = props.maxWidth,
    minHeight = props.minHeight,
    minWidth = props.minWidth,
    position = props.position,
    top = props.top,
    bottom = props.bottom,
    left = props.left,
    right = props.right,
    justifyContent = props.justifyContent,
    alignContent = props.alignContent,
    alignItems = props.alignItems,
    flexDirection = props.flexDirection,
    flexGrow = props.flexGrow,
    flexShrink = props.flexShrink,
    flexWrap = props.flexWrap,
    rowGap = props.rowGap,
    columnGap = props.columnGap,
    justifyItems = props.justifyItems,
    order = props.order,
    cursor = props.cursor,
    zIndex = props.zIndex,
    opacity = props.opacity,
    display = props.display,
    backgroundImage = props.backgroundImage,
    backgroundPosition = props.backgroundPosition,
    backgroundRepeat = props.backgroundRepeat,
    backgroundSize = props.backgroundSize,
    transform = props.transform,
    transformOrigin = props.transformOrigin,
    overflow = props.overflow,
    overflowX = props.overflowX,
    overflowY = props.overflowY,
    color = props.color,
    gradient = props.gradient;
  var styles = [inset && findDirection('padding', inset, entities.hocs.spacing), outset && findDirection('margin', outset, entities.hocs.spacing), borderRadius && findBorderRadiusDirection(borderRadius, entities.hocs.borderRadius), borderColor && findColor('borderColor', borderColor, entities.hocs.colors), borderWidth && findBorderDirection('width', borderWidth, entities.hocs.borders), borderStyle && findBorderDirection('style', borderStyle), backgroundColor && findColor('backgroundColor', backgroundColor, entities.hocs.colors), color && makeResponsive('color', color, entities.hocs.textColors), boxShadow && makeResponsive('boxShadow', boxShadow, entities.hocs.shadows), height && makeResponsive('height', height, entities.hocs.spacing), width && makeResponsive('width', width, entities.hocs.spacing), maxHeight && makeResponsive('maxHeight', maxHeight, entities.hocs.spacing), maxWidth && makeResponsive('maxWidth', maxWidth, entities.hocs.spacing), minHeight && makeResponsive('minHeight', minHeight, entities.hocs.spacing), minWidth && makeResponsive('minWidth', minWidth, entities.hocs.spacing), top && makeResponsive('top', top, entities.hocs.spacing), bottom && makeResponsive('bottom', bottom, entities.hocs.spacing), left && makeResponsive('left', left, entities.hocs.spacing), right && makeResponsive('right', right, entities.hocs.spacing), justifyContent && makeResponsive('justifyContent', justifyContent), alignContent && makeResponsive('alignContent', alignContent), alignItems && makeResponsive('alignItems', alignItems), flexDirection && makeResponsive('flexDirection', flexDirection), flexGrow && makeResponsive('flexGrow', flexGrow), flexShrink && makeResponsive('flexShrink', flexShrink), flexWrap && makeResponsive('flexWrap', flexWrap), rowGap && makeResponsive('rowGap', rowGap, entities.hocs.spacing), columnGap && makeResponsive('columnGap', columnGap, entities.hocs.spacing), justifyItems && makeResponsive('justifyItems', justifyItems), order && makeResponsive('order', order), cursor && makeResponsive('cursor', cursor), opacity && makeResponsive('opacity', opacity), display && makeResponsive('display', display), backgroundImage && makeResponsive('backgroundImage', "url(" + backgroundImage + ")"), backgroundPosition && makeResponsive('backgroundPosition', backgroundPosition), backgroundRepeat && makeResponsive('backgroundRepeat', backgroundRepeat), backgroundSize && makeResponsive('backgroundSize', backgroundSize), transformOrigin && makeResponsive('transformOrigin', transformOrigin), transform && makeResponsive('transform', transform), overflow && makeResponsive('overflow', overflow), overflowX && makeResponsive('overflowX', overflowX), overflowY && makeResponsive('overflowY', overflowY), position && makeResponsive('position', position), (zIndex || zIndex === 0) && makeResponsive('zIndex', zIndex), gradient && makeResponsive('background', gradient)];
  return mapArrayToObject(styles);
};

var _emotionSourceMap7$2 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap6$3 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap5$6 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap4$a = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap3$j = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap2$v = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap$W = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var styles$S = {
  heroTitle: function (textEntity, color, weight, alignment, italic, strikeThrough, lineHeight, whiteSpace, isInline) {
    return /*#__PURE__*/css(templateObject_1$W || (templateObject_1$W = __makeTemplateObject(["\n    color: ", ";\n    font-size: ", ";\n    font-weight: ", ";\n    line-height: ", ";\n    font-family: ", ";\n    margin: 0;\n    text-align: ", ";\n    font-style: ", ";\n    text-decoration: ", ";\n    white-space: ", ";\n    display: ", ";\n  " + _emotionSourceMap$W], ["\n    color: ", ";\n    font-size: ", ";\n    font-weight: ", ";\n    line-height: ", ";\n    font-family: ", ";\n    margin: 0;\n    text-align: ", ";\n    font-style: ", ";\n    text-decoration: ", ";\n    white-space: ", ";\n    display: ", ";\n  " + _emotionSourceMap$W])), color ? textEntity.colors[color] : textEntity.types.heroTitle.color, textEntity.types.heroTitle.size, weight ? textEntity.weights[weight] : textEntity.types.heroTitle.fontWeight, lineHeight !== null && lineHeight !== void 0 ? lineHeight : textEntity.types.heroTitle.lineHeight, textEntity.types.heroTitle.fontFamily, alignment === 'left' ? 'left' : alignment === 'center' ? 'center' : 'right', italic ? 'italic' : 'normal', strikeThrough && 'line-through', whiteSpace ? whiteSpace : 'normal', isInline ? 'inline' : 'block');
  },
  title: function (textEntity, color, weight, alignment, italic, strikeThrough, lineHeight, whiteSpace, isInline) {
    return /*#__PURE__*/css(templateObject_2$v || (templateObject_2$v = __makeTemplateObject(["\n    color: ", ";\n    font-size: ", ";\n    font-weight: ", ";\n    line-height: ", ";\n    font-family: ", ";\n    margin: 0;\n    text-align: ", ";\n    font-style: ", ";\n    text-decoration: ", ";\n    white-space: ", ";\n    display: ", ";\n  " + _emotionSourceMap2$v], ["\n    color: ", ";\n    font-size: ", ";\n    font-weight: ", ";\n    line-height: ", ";\n    font-family: ", ";\n    margin: 0;\n    text-align: ", ";\n    font-style: ", ";\n    text-decoration: ", ";\n    white-space: ", ";\n    display: ", ";\n  " + _emotionSourceMap2$v])), color ? textEntity.colors[color] : textEntity.types.title.color, textEntity.types.title.size, weight ? textEntity.weights[weight] : textEntity.types.title.fontWeight, lineHeight !== null && lineHeight !== void 0 ? lineHeight : textEntity.types.title.lineHeight, textEntity.types.title.fontFamily, alignment === 'left' ? 'left' : alignment === 'center' ? 'center' : 'right', italic ? 'italic' : 'normal', strikeThrough && 'line-through', whiteSpace ? whiteSpace : 'normal', isInline ? 'inline' : 'block');
  },
  subtitle: function (textEntity, color, weight, alignment, italic, strikeThrough, lineHeight, whiteSpace, isInline) {
    return /*#__PURE__*/css(templateObject_3$j || (templateObject_3$j = __makeTemplateObject(["\n    color: ", ";\n    font-size: ", ";\n    font-weight: ", ";\n    line-height: ", ";\n    font-family: ", ";\n    margin: 0;\n    text-align: ", ";\n    font-style: ", ";\n    text-decoration: ", ";\n    white-space: ", ";\n    display: ", ";\n  " + _emotionSourceMap3$j], ["\n    color: ", ";\n    font-size: ", ";\n    font-weight: ", ";\n    line-height: ", ";\n    font-family: ", ";\n    margin: 0;\n    text-align: ", ";\n    font-style: ", ";\n    text-decoration: ", ";\n    white-space: ", ";\n    display: ", ";\n  " + _emotionSourceMap3$j])), color ? textEntity.colors[color] : textEntity.types.subtitle.color, textEntity.types.subtitle.size, weight ? textEntity.weights[weight] : textEntity.types.subtitle.fontWeight, lineHeight !== null && lineHeight !== void 0 ? lineHeight : textEntity.types.subtitle.lineHeight, textEntity.types.subtitle.fontFamily, alignment === 'left' ? 'left' : alignment === 'center' ? 'center' : 'right', italic ? 'italic' : 'normal', strikeThrough && 'line-through', whiteSpace ? whiteSpace : 'normal', isInline ? 'inline' : 'block');
  },
  largeBody: function (textEntity, color, weight, alignment, italic, strikeThrough, lineHeight, whiteSpace, isInline) {
    return /*#__PURE__*/css(templateObject_4$a || (templateObject_4$a = __makeTemplateObject(["\n    color: ", ";\n    font-size: ", ";\n    font-weight: ", ";\n    line-height: ", ";\n    font-family: ", ";\n    margin: 0;\n    text-align: ", ";\n    font-style: ", ";\n    text-decoration: ", ";\n    white-space: ", ";\n    display: ", ";\n  " + _emotionSourceMap4$a], ["\n    color: ", ";\n    font-size: ", ";\n    font-weight: ", ";\n    line-height: ", ";\n    font-family: ", ";\n    margin: 0;\n    text-align: ", ";\n    font-style: ", ";\n    text-decoration: ", ";\n    white-space: ", ";\n    display: ", ";\n  " + _emotionSourceMap4$a])), color ? textEntity.colors[color] : textEntity.types.largeBody.color, textEntity.types.largeBody.size, weight ? textEntity.weights[weight] : textEntity.types.largeBody.fontWeight, lineHeight !== null && lineHeight !== void 0 ? lineHeight : textEntity.types.largeBody.lineHeight, textEntity.types.largeBody.fontFamily, alignment === 'left' ? 'left' : alignment === 'center' ? 'center' : 'right', italic ? 'italic' : 'normal', strikeThrough && 'line-through', whiteSpace ? whiteSpace : 'normal', isInline ? 'inline' : 'block');
  },
  body: function (textEntity, color, weight, alignment, italic, strikeThrough, lineHeight, whiteSpace, isInline) {
    return /*#__PURE__*/css(templateObject_5$6 || (templateObject_5$6 = __makeTemplateObject(["\n    color: ", ";\n    font-size: ", ";\n    font-weight: ", ";\n    line-height: ", ";\n    font-family: ", ";\n    margin: 0;\n    text-align: ", ";\n    font-style: ", ";\n    text-decoration: ", ";\n    white-space: ", ";\n    display: ", ";\n  " + _emotionSourceMap5$6], ["\n    color: ", ";\n    font-size: ", ";\n    font-weight: ", ";\n    line-height: ", ";\n    font-family: ", ";\n    margin: 0;\n    text-align: ", ";\n    font-style: ", ";\n    text-decoration: ", ";\n    white-space: ", ";\n    display: ", ";\n  " + _emotionSourceMap5$6])), color ? textEntity.colors[color] : textEntity.types.body.color, textEntity.types.body.size, weight ? textEntity.weights[weight] : textEntity.types.body.fontWeight, lineHeight !== null && lineHeight !== void 0 ? lineHeight : textEntity.types.body.lineHeight, textEntity.types.body.fontFamily, alignment === 'left' ? 'left' : alignment === 'center' ? 'center' : 'right', italic ? 'italic' : 'normal', strikeThrough && 'line-through', whiteSpace ? whiteSpace : 'normal', isInline ? 'inline' : 'block');
  },
  caption: function (textEntity, color, weight, alignment, italic, strikeThrough, lineHeight, whiteSpace, isInline) {
    return /*#__PURE__*/css(templateObject_6$3 || (templateObject_6$3 = __makeTemplateObject(["\n    color: ", ";\n    font-size: ", ";\n    font-weight: ", ";\n    line-height: ", ";\n    font-family: ", ";\n    margin: 0;\n    text-align: ", ";\n    font-style: ", ";\n    text-decoration: ", ";\n    white-space: ", ";\n    display: ", ";\n  " + _emotionSourceMap6$3], ["\n    color: ", ";\n    font-size: ", ";\n    font-weight: ", ";\n    line-height: ", ";\n    font-family: ", ";\n    margin: 0;\n    text-align: ", ";\n    font-style: ", ";\n    text-decoration: ", ";\n    white-space: ", ";\n    display: ", ";\n  " + _emotionSourceMap6$3])), color ? textEntity.colors[color] : textEntity.types.caption.color, textEntity.types.caption.size, weight ? textEntity.weights[weight] : textEntity.types.caption.fontWeight, lineHeight !== null && lineHeight !== void 0 ? lineHeight : textEntity.types.caption.lineHeight, textEntity.types.caption.fontFamily, alignment === 'left' ? 'left' : alignment === 'center' ? 'center' : 'right', italic ? 'italic' : 'normal', strikeThrough && 'line-through', whiteSpace ? whiteSpace : 'normal', isInline ? 'inline' : 'block');
  },
  footnote: function (textEntity, color, weight, alignment, italic, strikeThrough, lineHeight, whiteSpace, isInline) {
    return /*#__PURE__*/css(templateObject_7$2 || (templateObject_7$2 = __makeTemplateObject(["\n    color: ", ";\n    font-size: ", ";\n    font-weight: ", ";\n    line-height: ", ";\n    font-family: ", ";\n    margin: 0;\n    text-align: ", ";\n    font-style: ", ";\n    text-decoration: ", ";\n    white-space: ", ";\n    display: ", ";\n  " + _emotionSourceMap7$2], ["\n    color: ", ";\n    font-size: ", ";\n    font-weight: ", ";\n    line-height: ", ";\n    font-family: ", ";\n    margin: 0;\n    text-align: ", ";\n    font-style: ", ";\n    text-decoration: ", ";\n    white-space: ", ";\n    display: ", ";\n  " + _emotionSourceMap7$2])), color ? textEntity.colors[color] : textEntity.types.footnote.color, textEntity.types.footnote.size, weight ? textEntity.weights[weight] : textEntity.types.footnote.fontWeight, lineHeight !== null && lineHeight !== void 0 ? lineHeight : textEntity.types.footnote.lineHeight, textEntity.types.footnote.fontFamily, alignment === 'left' ? 'left' : alignment === 'center' ? 'center' : 'right', italic ? 'italic' : 'normal', strikeThrough && 'line-through', whiteSpace ? whiteSpace : 'normal', isInline ? 'inline' : 'block');
  }
};
var templateObject_1$W, templateObject_2$v, templateObject_3$j, templateObject_4$a, templateObject_5$6, templateObject_6$3, templateObject_7$2;

var _emotionSourceMap$V = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFHbUQiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgX19tYWtlVGVtcGxhdGVPYmplY3QgfSBmcm9tIFwidHNsaWJcIjtcclxuaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vY29yZSc7XHJcbmV4cG9ydCBkZWZhdWx0IHtcclxuICAgIGRpdlN0eWxlczogZnVuY3Rpb24gKGdlbmVyYXRlZFN0eWxlcykgeyByZXR1cm4gY3NzKHRlbXBsYXRlT2JqZWN0XzEgfHwgKHRlbXBsYXRlT2JqZWN0XzEgPSBfX21ha2VUZW1wbGF0ZU9iamVjdChbXCJcXG4gICAgXCIsIFwiXFxuICAgIGZvbnQtc2l6ZTogaW5oZXJpdDtcXG4gIFwiXSwgW1wiXFxuICAgIFwiLCBcIlxcbiAgICBmb250LXNpemU6IGluaGVyaXQ7XFxuICBcIl0pKSwgZ2VuZXJhdGVkU3R5bGVzKTsgfSxcclxufTtcclxudmFyIHRlbXBsYXRlT2JqZWN0XzE7XHJcbi8vIyBzb3VyY2VNYXBwaW5nVVJMPXN0eWxlcy5qcy5tYXAiXX0= */";
var styles$R = {
  divStyles: function (generatedStyles) {
    return /*#__PURE__*/css(templateObject_1$V || (templateObject_1$V = __makeTemplateObject(["\n    ", "\n    font-size: inherit;\n  " + _emotionSourceMap$V], ["\n    ", "\n    font-size: inherit;\n  " + _emotionSourceMap$V])), generatedStyles);
  }
};
var templateObject_1$V;

var useOutsideClick = function (ref, handler, targetIdBypass) {
  useEffect(function () {
    var listener = function (event) {
      // Do nothing if clicking ref's element or descendent elements
      var isIdFound;
      if (event.path !== undefined) {
        var match = event.path.filter(function (pathObj) {
          return pathObj.id !== undefined;
        }).filter(function (pathObj) {
          return pathObj.id === targetIdBypass;
        });
        isIdFound = match.length > 0 ? true : false;
      }
      if (isIdFound) {
        return;
      }
      if (!(ref === null || ref === void 0 ? void 0 : ref.current) || (ref === null || ref === void 0 ? void 0 : ref.current.contains(event.target))) {
        return;
      }
      handler(event);
    };
    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);
    return function () {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  },
  // Add ref and handler to effect dependencies
  // It's worth noting that because passed in handler is a new ...
  // ... function on every render that will cause this effect ...
  // ... callback/cleanup to run every render. It's not a big deal ...
  // ... but to optimize you can wrap handler in useCallback before ...
  // ... passing it into this hook.
  [ref, handler, targetIdBypass]);
};

var Div = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var rest = __rest(props, []);
  var entities = useEntities();
  var generatedDivStyles = generateStyles(props, entities);
  useOutsideClick(ref, function () {
    props.outsideClick && props.outsideClick();
  }, props.clickElementBypass);
  return jsx(motion.div, _extends({}, props, {
    key: props.motionKey,
    ref: ref,
    animate: process.env.NODE_ENV === 'test' ? undefined : props.animate,
    onClick: props.onMouseEvent ? function (e) {
      var _a;
      return (_a = props.onMouseEvent) === null || _a === void 0 ? void 0 : _a.call(props, e);
    } : function () {
      var _a;
      return (_a = props.onClick) === null || _a === void 0 ? void 0 : _a.call(props);
    },
    css: styles$R.divStyles(generatedDivStyles),
    className: "" + (props.className ? props.className : '')
  }, rest), props.children);
});

var useWindowSize = function () {
  var isClient = typeof window === 'object';
  var getSize = useCallback(function () {
    return {
      width: isClient ? window.innerWidth : 0,
      height: isClient ? window.innerHeight : 0
    };
  }, [isClient]);
  var _a = useState(getSize),
    windowSize = _a[0],
    setWindowSize = _a[1];
  useEffect(function () {
    if (!isClient) {
      return;
    }
    function handleResize() {
      setWindowSize(getSize());
    }
    window.addEventListener('resize', handleResize);
    return function () {
      return window.removeEventListener('resize', handleResize);
    };
  }, [getSize, isClient]);
  var width = windowSize.width,
    height = windowSize.height;
  return {
    width: width,
    height: height
  };
};

var mediaQueries$2 = entities$9.mediaQueries;
var makeTextResponsive = function (size, callback, windowSize) {
  // TODO - Change this function to allow cases to fall through, as of now you have to specify every media query size
  var lastQuery = 'body';
  if (isTextType(size)) {
    return callback(size);
  } else {
    if (size.xSmall && windowSize >= mediaQueries$2.rawValues.xSmall) {
      lastQuery = size.xSmall;
    }
    if (size.small && windowSize >= mediaQueries$2.rawValues.small) {
      lastQuery = size.small;
    }
    if (size.medium && windowSize >= mediaQueries$2.rawValues.medium) {
      lastQuery = size.medium;
    }
    if (size.large && windowSize >= mediaQueries$2.rawValues.large) {
      lastQuery = size.large;
    }
    if (size.xLarge && windowSize >= mediaQueries$2.rawValues.xLarge) {
      lastQuery = size.xLarge;
    }
  }
  return callback(lastQuery);
};

var Text = function (_a) {
  var props = __rest(_a, []);
  var type = props.type,
    color = props.color,
    children = props.children,
    content = props.content,
    _b = props.alignment,
    alignment = _b === void 0 ? 'left' : _b,
    isItalic = props.isItalic,
    isStrikeThrough = props.isStrikeThrough,
    outset = props.outset,
    inset = props.inset,
    weight = props.weight,
    lineHeight = props.lineHeight,
    whiteSpace = props.whiteSpace,
    isInline = props.isInline,
    style = props.style,
    rest = __rest(props, ["type", "color", "children", "content", "alignment", "isItalic", "isStrikeThrough", "outset", "inset", "weight", "lineHeight", "whiteSpace", "isInline", "style"]);
  var text = useEntities().text;
  var width = useWindowSize().width;
  var getText = useCallback(function (type) {
    switch (type) {
      case 'heroTitle':
        return jsx("h1", _extends({
          style: style,
          css: styles$S.heroTitle(text, color, weight, alignment, isItalic, isStrikeThrough, lineHeight, whiteSpace, isInline)
        }, rest), children || content);
      case 'title':
        return jsx("h2", _extends({
          style: style,
          css: styles$S.title(text, color, weight, alignment, isItalic, isStrikeThrough, lineHeight, whiteSpace, isInline)
        }, rest), children || content);
      case 'subtitle':
        return jsx("h3", _extends({
          style: style,
          css: styles$S.subtitle(text, color, weight, alignment, isItalic, isStrikeThrough, lineHeight, whiteSpace, isInline)
        }, rest), children || content);
      case 'largeBody':
        return jsx("p", _extends({
          style: style,
          css: styles$S.largeBody(text, color, weight, alignment, isItalic, isStrikeThrough, lineHeight, whiteSpace, isInline)
        }, rest), children || content);
      case 'body':
        return jsx("p", _extends({
          style: style,
          css: styles$S.body(text, color, weight, alignment, isItalic, isStrikeThrough, lineHeight, whiteSpace, isInline)
        }, rest), children || content);
      case 'caption':
        return jsx("p", _extends({
          style: style,
          css: styles$S.caption(text, color, weight, alignment, isItalic, isStrikeThrough, lineHeight, whiteSpace, isInline)
        }, rest), children || content);
      case 'footnote':
        return jsx("p", _extends({
          style: style,
          css: styles$S.footnote(text, color, weight, alignment, isItalic, isStrikeThrough, lineHeight, whiteSpace, isInline)
        }, rest), children || content);
      default:
        return;
    }
  }, [color, weight, alignment, isItalic, isStrikeThrough, lineHeight, content, text, children, whiteSpace, isInline, style, rest]);
  var renderText = useMemo(function () {
    return makeTextResponsive(type, getText, width);
  }, [width, getText, type]);
  return jsx(React.Fragment, null, inset || outset ? jsx(Div, {
    inset: inset,
    outset: outset
  }, renderText) : renderText);
};

var mapSizeType = function (size, sizeMap) {
  return sizeMap[size];
};

var computeTextTypeFromIconLinkSize = function (size) {
  return mapSizeType(size, {
    xSmall: 'footnote',
    small: 'footnote',
    medium: 'body',
    large: 'subtitle',
    xLarge: 'title'
  });
};
var computeTextTypeFromSize = function (size) {
  return mapSizeType(size, {
    xSmall: 'caption',
    small: 'body',
    medium: 'largeBody',
    large: 'largeBody',
    xLarge: 'subtitle'
  });
};
var computeTextTypeFromInfoBlockSize = function (size) {
  return mapSizeType(size, {
    xSmall: {
      heading: 'caption',
      caption: 'footnote'
    },
    small: {
      heading: 'body',
      caption: 'caption'
    },
    medium: {
      heading: 'largeBody',
      caption: 'body'
    },
    large: {
      heading: 'subtitle',
      caption: 'largeBody'
    },
    xLarge: {
      heading: 'title',
      caption: 'subtitle'
    }
  });
};

var Anchor = function (props) {
  var entities = useEntities();
  var _a = props.color,
    color = _a === void 0 ? 'primaryBody' : _a,
    _b = props.size,
    size = _b === void 0 ? 'small' : _b,
    isDecorated = props.isDecorated,
    _c = props.display,
    display = _c === void 0 ? 'inline-block' : _c,
    shouldDownload = props.shouldDownload,
    rest = __rest(props, ["color", "size", "isDecorated", "display", "shouldDownload"]);
  var generatedDivStyles = generateStyles(__assign({
    display: display
  }, rest), entities);
  return jsx(motion.a, _extends({
    rel: "noopener noreferrer",
    target: rest.target
  }, rest, {
    href: rest.href,
    onClick: rest.onMouseEvent ? function (e) {
      var _a;
      return (_a = rest.onMouseEvent) === null || _a === void 0 ? void 0 : _a.call(rest, e);
    } : function () {
      var _a;
      return (_a = rest.onClick) === null || _a === void 0 ? void 0 : _a.call(rest);
    },
    download: shouldDownload,
    css: styles$T.anchorStyles(generatedDivStyles, entities, color, isDecorated)
  }), jsx(Text, {
    type: computeTextTypeFromSize(size),
    color: color
  }, rest.title || rest.children));
};

var _emotionSourceMap$U = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFHb0QiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgX19tYWtlVGVtcGxhdGVPYmplY3QgfSBmcm9tIFwidHNsaWJcIjtcclxuaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vY29yZSc7XHJcbmV4cG9ydCBkZWZhdWx0IHtcclxuICAgIHNwYW5TdHlsZXM6IGZ1bmN0aW9uIChnZW5lcmF0ZWRTdHlsZXMpIHsgcmV0dXJuIGNzcyh0ZW1wbGF0ZU9iamVjdF8xIHx8ICh0ZW1wbGF0ZU9iamVjdF8xID0gX19tYWtlVGVtcGxhdGVPYmplY3QoW1wiXFxuICAgIFwiLCBcIlxcbiAgICBmb250LXNpemU6IGluaGVyaXRcXG4gIFwiXSwgW1wiXFxuICAgIFwiLCBcIlxcbiAgICBmb250LXNpemU6IGluaGVyaXRcXG4gIFwiXSkpLCBnZW5lcmF0ZWRTdHlsZXMpOyB9LFxyXG59O1xyXG52YXIgdGVtcGxhdGVPYmplY3RfMTtcclxuLy8jIHNvdXJjZU1hcHBpbmdVUkw9c3R5bGVzLmpzLm1hcCJdfQ== */";
var styles$Q = {
  spanStyles: function (generatedStyles) {
    return /*#__PURE__*/css(templateObject_1$U || (templateObject_1$U = __makeTemplateObject(["\n    ", "\n    font-size: inherit\n  " + _emotionSourceMap$U], ["\n    ", "\n    font-size: inherit\n  " + _emotionSourceMap$U])), generatedStyles);
  }
};
var templateObject_1$U;

var Span = function (props) {
  var entities = useEntities();
  var generatedDivStyles = generateStyles(props, entities);
  return jsx(motion.span, _extends({}, props, {
    onClick: props.onMouseEvent ? function (e) {
      var _a;
      return (_a = props.onMouseEvent) === null || _a === void 0 ? void 0 : _a.call(props, e);
    } : function () {
      var _a;
      return (_a = props.onClick) === null || _a === void 0 ? void 0 : _a.call(props);
    },
    css: styles$Q.spanStyles(generatedDivStyles),
    className: "" + (props.className ? props.className : '')
  }), props.children);
};

var _emotionSourceMap$T = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFHMEYiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgX19tYWtlVGVtcGxhdGVPYmplY3QgfSBmcm9tIFwidHNsaWJcIjtcclxuaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vY29yZSc7XHJcbmV4cG9ydCBkZWZhdWx0IHtcclxuICAgIGxpc3RDb250YWluZXI6IGZ1bmN0aW9uIChvcmllbnRhdGlvbiwgYWxpZ25tZW50LCBsaXN0U3R5bGUsIGdlbmVyYXRlZFN0eWxlcykgeyByZXR1cm4gY3NzKHRlbXBsYXRlT2JqZWN0XzEgfHwgKHRlbXBsYXRlT2JqZWN0XzEgPSBfX21ha2VUZW1wbGF0ZU9iamVjdChbXCJcXG4gICAgbGlzdC1zdHlsZS10eXBlOiBcIiwgXCI7XFxuICAgIG1hcmdpbjogMDtcXG4gICAgZGlzcGxheTogZmxleDtcXG4gICAgZmxleC1kaXJlY3Rpb246IFwiLCBcIjtcXG4gICAganVzdGlmeS1jb250ZW50OiBcIiwgXCI7XFxuICAgIGFsaWduLWl0ZW1zOiBcIiwgXCI7XFxuICAgIHBhZGRpbmc6IDA7XFxuICAgIFwiLCBcIjtcXG4gICAgZm9udC1zaXplOiBpbmhlcml0O1xcbiAgXCJdLCBbXCJcXG4gICAgbGlzdC1zdHlsZS10eXBlOiBcIiwgXCI7XFxuICAgIG1hcmdpbjogMDtcXG4gICAgZGlzcGxheTogZmxleDtcXG4gICAgZmxleC1kaXJlY3Rpb246IFwiLCBcIjtcXG4gICAganVzdGlmeS1jb250ZW50OiBcIixcclxuICAgICAgICBcIjtcXG4gICAgYWxpZ24taXRlbXM6IFwiLFxyXG4gICAgICAgIFwiO1xcbiAgICBwYWRkaW5nOiAwO1xcbiAgICBcIiwgXCI7XFxuICAgIGZvbnQtc2l6ZTogaW5oZXJpdDtcXG4gIFwiXSkpLCBsaXN0U3R5bGUsIG9yaWVudGF0aW9uID09PSAndmVydGljYWwnID8gJ2NvbHVtbicgOiAncm93Jywgb3JpZW50YXRpb24gPT09ICdob3Jpem9udGFsJyAmJiBhbGlnbm1lbnQgPT09ICdsZWZ0J1xyXG4gICAgICAgID8gJ2ZsZXgtc3RhcnQnXHJcbiAgICAgICAgOiBhbGlnbm1lbnQgPT09ICdjZW50ZXInXHJcbiAgICAgICAgICAgID8gJ2NlbnRlcidcclxuICAgICAgICAgICAgOiAnZmxleC1lbmQnLCBvcmllbnRhdGlvbiA9PT0gJ3ZlcnRpY2FsJyAmJiBhbGlnbm1lbnQgPT09ICdsZWZ0J1xyXG4gICAgICAgID8gJ2ZsZXgtc3RhcnQnXHJcbiAgICAgICAgOiBhbGlnbm1lbnQgPT09ICdjZW50ZXInXHJcbiAgICAgICAgICAgID8gJ2NlbnRlcidcclxuICAgICAgICAgICAgOiAnZmxleC1lbmQnLCBnZW5lcmF0ZWRTdHlsZXMpOyB9LFxyXG59O1xyXG52YXIgdGVtcGxhdGVPYmplY3RfMTtcclxuLy8jIHNvdXJjZU1hcHBpbmdVUkw9c3R5bGVzLmpzLm1hcCJdfQ== */";
var styles$P = {
  listContainer: function (orientation, alignment, listStyle, generatedStyles) {
    return /*#__PURE__*/css(templateObject_1$T || (templateObject_1$T = __makeTemplateObject(["\n    list-style-type: ", ";\n    margin: 0;\n    display: flex;\n    flex-direction: ", ";\n    justify-content: ", ";\n    align-items: ", ";\n    padding: 0;\n    ", ";\n    font-size: inherit;\n  " + _emotionSourceMap$T], ["\n    list-style-type: ", ";\n    margin: 0;\n    display: flex;\n    flex-direction: ", ";\n    justify-content: ", ";\n    align-items: ", ";\n    padding: 0;\n    ", ";\n    font-size: inherit;\n  " + _emotionSourceMap$T])), listStyle, orientation === 'vertical' ? 'column' : 'row', orientation === 'horizontal' && alignment === 'left' ? 'flex-start' : alignment === 'center' ? 'center' : 'flex-end', orientation === 'vertical' && alignment === 'left' ? 'flex-start' : alignment === 'center' ? 'center' : 'flex-end', generatedStyles);
  }
};
var templateObject_1$T;

var List = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var _a = props.listStyle,
    listStyle = _a === void 0 ? 'none' : _a,
    rest = __rest(props, ["listStyle"]);
  var entities = useEntities();
  var generatedStyles = generateStyles(rest, entities);
  return jsx(motion.ul, _extends({}, rest, {
    css: styles$P.listContainer(rest.orientation, rest.alignment, listStyle, generatedStyles),
    className: "\n      " + (rest.className ? rest.className : ''),
    ref: ref
  }), rest.children);
});

var computeAvatarProperties = function (size, sizeMap) {
  return mapSizeType(size, sizeMap);
};

var _emotionSourceMap3$i = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap2$u = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap$S = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFJa0YiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgX19tYWtlVGVtcGxhdGVPYmplY3QgfSBmcm9tIFwidHNsaWJcIjtcclxuaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vY29yZSc7XHJcbmltcG9ydCB7IGNvbXB1dGVBdmF0YXJQcm9wZXJ0aWVzLCB9IGZyb20gXCIuLi8uLi9oZWxwZXJzL3N0eWxlLWZ1bmN0aW9ucy9jb21wdXRlQXZhdGFyUHJvcGVydGllc1wiO1xyXG5leHBvcnQgZGVmYXVsdCB7XHJcbiAgICBhdmF0YXJDb250YWluZXI6IGZ1bmN0aW9uIChzaXplLCBzaXplTWFwLCBhdmF0YXJFbnRpdHksIGF2YXRhckltYWdlKSB7IHJldHVybiBjc3ModGVtcGxhdGVPYmplY3RfMSB8fCAodGVtcGxhdGVPYmplY3RfMSA9IF9fbWFrZVRlbXBsYXRlT2JqZWN0KFtcIlxcbiAgICBwb3NpdGlvbjogcmVsYXRpdmU7XFxuICAgIGJvcmRlci1yYWRpdXM6IDE1JTtcXG4gICAgaGVpZ2h0OiBcIiwgXCJweDtcXG4gICAgd2lkdGg6IFwiLCBcInB4O1xcbiAgICBtaW4td2lkdGg6IFwiLCBcInB4O1xcbiAgICBiYWNrZ3JvdW5kLWNvbG9yOiBcIiwgXCI7XFxuICAgIGJhY2tncm91bmQtaW1hZ2U6IFwiLCBcIjtcXG4gICAgYmFja2dyb3VuZC1wb3NpdGlvbjogY2VudGVyO1xcbiAgICBiYWNrZ3JvdW5kLXNpemU6IGNvdmVyO1xcbiAgICBib3JkZXI6IFwiLCBcIiBzb2xpZCBcIiwgXCI7XFxuICAgIGJveC1zaGFkb3c6IFwiLCBcIjtcXG4gICAgZGlzcGxheTogZmxleDtcXG4gICAganVzdGlmeS1jb250ZW50OiBjZW50ZXI7XFxuICAgIGFsaWduLWl0ZW1zOiBjZW50ZXI7XFxuICBcIl0sIFtcIlxcbiAgICBwb3NpdGlvbjogcmVsYXRpdmU7XFxuICAgIGJvcmRlci1yYWRpdXM6IDE1JTtcXG4gICAgaGVpZ2h0OiBcIiwgXCJweDtcXG4gICAgd2lkdGg6IFwiLCBcInB4O1xcbiAgICBtaW4td2lkdGg6IFwiLCBcInB4O1xcbiAgICBiYWNrZ3JvdW5kLWNvbG9yOiBcIiwgXCI7XFxuICAgIGJhY2tncm91bmQtaW1hZ2U6IFwiLFxyXG4gICAgICAgIFwiO1xcbiAgICBiYWNrZ3JvdW5kLXBvc2l0aW9uOiBjZW50ZXI7XFxuICAgIGJhY2tncm91bmQtc2l6ZTogY292ZXI7XFxuICAgIGJvcmRlcjogXCIsIFwiIHNvbGlkIFwiLCBcIjtcXG4gICAgYm94LXNoYWRvdzogXCIsIFwiO1xcbiAgICBkaXNwbGF5OiBmbGV4O1xcbiAgICBqdXN0aWZ5LWNvbnRlbnQ6IGNlbnRlcjtcXG4gICAgYWxpZ24taXRlbXM6IGNlbnRlcjtcXG4gIFwiXSkpLCBjb21wdXRlQXZhdGFyUHJvcGVydGllcyhzaXplLCBzaXplTWFwKS5hdmF0YXJTaXplLCBjb21wdXRlQXZhdGFyUHJvcGVydGllcyhzaXplLCBzaXplTWFwKS5hdmF0YXJTaXplLCBjb21wdXRlQXZhdGFyUHJvcGVydGllcyhzaXplLCBzaXplTWFwKS5hdmF0YXJTaXplLCBhdmF0YXJFbnRpdHkuYmFja2dyb3VuZENvbG9yLCBhdmF0YXJJbWFnZVxyXG4gICAgICAgID8gXCJ1cmwoXCIgKyBhdmF0YXJJbWFnZSArIFwiKVwiXHJcbiAgICAgICAgOiBcImxpbmVhci1ncmFkaWVudChcIiArIGF2YXRhckVudGl0eS5kZWZhdWx0QmFja2dyb3VuZENvbG9ycy5hY2NlbnQgKyBcIiwgXCIgKyBhdmF0YXJFbnRpdHkuZGVmYXVsdEJhY2tncm91bmRDb2xvcnMuYWNjZW50ICsgXCIpXCIsIGF2YXRhckVudGl0eS5ib3JkZXJXaWR0aCwgYXZhdGFyRW50aXR5LmJvcmRlckNvbG9yLCBhdmF0YXJFbnRpdHkuYm94U2hhZG93KTsgfSxcclxuICAgIGFjdGl2ZUJ1YmJsZTogZnVuY3Rpb24gKGF2YXRhckVudGl0eSwgc2l6ZSwgc2l6ZU1hcCwgaXNBY3RpdmUpIHsgcmV0dXJuIGNzcyh0ZW1wbGF0ZU9iamVjdF8yIHx8ICh0ZW1wbGF0ZU9iamVjdF8yID0gX19tYWtlVGVtcGxhdGVPYmplY3QoW1wiXFxuICAgIHBvc2l0aW9uOiBhYnNvbHV0ZTtcXG4gICAgbGVmdDogXCIsIFwicHg7XFxuICAgIGJvdHRvbTogMHB4O1xcbiAgICBiYWNrZ3JvdW5kOiBcIiwgXCI7XFxuICAgIHdpZHRoOiBcIiwgXCJweDtcXG4gICAgaGVpZ2h0OiBcIiwgXCJweDtcXG4gICAgYm9yZGVyLXJhZGl1czogNTAlO1xcbiAgICB6LWluZGV4OiAxO1xcbiAgICBib3gtc2hhZG93OiBcIiwgXCI7XFxuICBcIl0sIFtcIlxcbiAgICBwb3NpdGlvbjogYWJzb2x1dGU7XFxuICAgIGxlZnQ6IFwiLCBcInB4O1xcbiAgICBib3R0b206IDBweDtcXG4gICAgYmFja2dyb3VuZDogXCIsXHJcbiAgICAgICAgXCI7XFxuICAgIHdpZHRoOiBcIiwgXCJweDtcXG4gICAgaGVpZ2h0OiBcIiwgXCJweDtcXG4gICAgYm9yZGVyLXJhZGl1czogNTAlO1xcbiAgICB6LWluZGV4OiAxO1xcbiAgICBib3gtc2hhZG93OiBcIiwgXCI7XFxuICBcIl0pKSwgY29tcHV0ZUF2YXRhclByb3BlcnRpZXMoc2l6ZSwgc2l6ZU1hcCkuYnViYmxlTGVmdFBvc2l0aW9uLCBpc0FjdGl2ZVxyXG4gICAgICAgID8gYXZhdGFyRW50aXR5LmJ1YmJsZUJhY2tncm91bmRDb2xvcnMuaXNBY3RpdmVcclxuICAgICAgICA6IGF2YXRhckVudGl0eS5idWJibGVCYWNrZ3JvdW5kQ29sb3JzLmRlZmF1bHQsIGNvbXB1dGVBdmF0YXJQcm9wZXJ0aWVzKHNpemUsIHNpemVNYXApLmJ1YmJsZVNpemUsIGNvbXB1dGVBdmF0YXJQcm9wZXJ0aWVzKHNpemUsIHNpemVNYXApLmJ1YmJsZVNpemUsIGF2YXRhckVudGl0eS5ib3hTaGFkb3cpOyB9LFxyXG4gICAgY2hlY2ttYXJrQ29udGFpbmVyOiBmdW5jdGlvbiAoc2l6ZSwgc2l6ZU1hcCkgeyByZXR1cm4gY3NzKHRlbXBsYXRlT2JqZWN0XzMgfHwgKHRlbXBsYXRlT2JqZWN0XzMgPSBfX21ha2VUZW1wbGF0ZU9iamVjdChbXCJcXG4gICAgcG9zaXRpb246IGFic29sdXRlO1xcbiAgICBsZWZ0OiBcIiwgXCJweDtcXG4gICAgYm90dG9tOiAwcHg7XFxuICBcIl0sIFtcIlxcbiAgICBwb3NpdGlvbjogYWJzb2x1dGU7XFxuICAgIGxlZnQ6IFwiLCBcInB4O1xcbiAgICBib3R0b206IDBweDtcXG4gIFwiXSkpLCBjb21wdXRlQXZhdGFyUHJvcGVydGllcyhzaXplLCBzaXplTWFwKS5idWJibGVMZWZ0UG9zaXRpb24pOyB9LFxyXG59O1xyXG52YXIgdGVtcGxhdGVPYmplY3RfMSwgdGVtcGxhdGVPYmplY3RfMiwgdGVtcGxhdGVPYmplY3RfMztcclxuLy8jIHNvdXJjZU1hcHBpbmdVUkw9c3R5bGVzLmpzLm1hcCJdfQ== */";
var styles$O = {
  avatarContainer: function (size, sizeMap, avatarEntity, avatarImage) {
    return /*#__PURE__*/css(templateObject_1$S || (templateObject_1$S = __makeTemplateObject(["\n    position: relative;\n    border-radius: 15%;\n    height: ", "px;\n    width: ", "px;\n    min-width: ", "px;\n    background-color: ", ";\n    background-image: ", ";\n    background-position: center;\n    background-size: cover;\n    border: ", " solid ", ";\n    box-shadow: ", ";\n    display: flex;\n    justify-content: center;\n    align-items: center;\n  " + _emotionSourceMap$S], ["\n    position: relative;\n    border-radius: 15%;\n    height: ", "px;\n    width: ", "px;\n    min-width: ", "px;\n    background-color: ", ";\n    background-image: ", ";\n    background-position: center;\n    background-size: cover;\n    border: ", " solid ", ";\n    box-shadow: ", ";\n    display: flex;\n    justify-content: center;\n    align-items: center;\n  " + _emotionSourceMap$S])), computeAvatarProperties(size, sizeMap).avatarSize, computeAvatarProperties(size, sizeMap).avatarSize, computeAvatarProperties(size, sizeMap).avatarSize, avatarEntity.backgroundColor, avatarImage ? "url(" + avatarImage + ")" : "linear-gradient(" + avatarEntity.defaultBackgroundColors.accent + ", " + avatarEntity.defaultBackgroundColors.accent + ")", avatarEntity.borderWidth, avatarEntity.borderColor, avatarEntity.boxShadow);
  },
  activeBubble: function (avatarEntity, size, sizeMap, isActive) {
    return /*#__PURE__*/css(templateObject_2$u || (templateObject_2$u = __makeTemplateObject(["\n    position: absolute;\n    left: ", "px;\n    bottom: 0px;\n    background: ", ";\n    width: ", "px;\n    height: ", "px;\n    border-radius: 50%;\n    z-index: 1;\n    box-shadow: ", ";\n  " + _emotionSourceMap2$u], ["\n    position: absolute;\n    left: ", "px;\n    bottom: 0px;\n    background: ", ";\n    width: ", "px;\n    height: ", "px;\n    border-radius: 50%;\n    z-index: 1;\n    box-shadow: ", ";\n  " + _emotionSourceMap2$u])), computeAvatarProperties(size, sizeMap).bubbleLeftPosition, isActive ? avatarEntity.bubbleBackgroundColors.isActive : avatarEntity.bubbleBackgroundColors.default, computeAvatarProperties(size, sizeMap).bubbleSize, computeAvatarProperties(size, sizeMap).bubbleSize, avatarEntity.boxShadow);
  },
  checkmarkContainer: function (size, sizeMap) {
    return /*#__PURE__*/css(templateObject_3$i || (templateObject_3$i = __makeTemplateObject(["\n    position: absolute;\n    left: ", "px;\n    bottom: 0px;\n  " + _emotionSourceMap3$i], ["\n    position: absolute;\n    left: ", "px;\n    bottom: 0px;\n  " + _emotionSourceMap3$i])), computeAvatarProperties(size, sizeMap).bubbleLeftPosition);
  }
};
var templateObject_1$S, templateObject_2$u, templateObject_3$i;

var _emotionSourceMap$R = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var AnimatedCheckMark = function (_a) {
  var _b = _a.size,
    size = _b === void 0 ? 'small' : _b,
    _c = _a.color,
    color = _c === void 0 ? 'success' : _c,
    filled = _a.filled,
    sizeMap = _a.sizeMap,
    _d = _a.strokeWidth,
    strokeWidth = _d === void 0 ? 'default' : _d;
  var strokeDashoffset = useMotionValue(0);
  var pathLength = useMotionValue(0);
  var opacity = useTransform(pathLength, [0.15, 0.25], [0, 1]);
  var animatedCheckmark = useEntities().animatedCheckmark;
  var getSizeValues = function (sizeMap, size) {
    switch (size) {
      case 'xSmall':
        return sizeMap.xSmall;
      case 'small':
        return sizeMap.small;
      case 'medium':
        return sizeMap.medium;
      case 'large':
        return sizeMap.large;
      case 'xLarge':
        return sizeMap.xLarge;
    }
  };
  var computeSize = function (size) {
    return getSizeValues(sizeMap || animatedCheckmark.sizeMap, size);
  };
  var circleVariants = {
    unchecked: {
      strokeDashoffset: 82
    },
    checked: {
      strokeDashoffset: 0,
      transition: {
        duration: 0.3,
        ease: [0.65, 0, 0.45, 1]
      }
    }
  };
  var tickVariants = {
    unchecked: {
      pathLength: 0
    },
    checked: {
      pathLength: 1,
      transition: {
        delay: filled ? 0.8 : 0.2,
        duration: 0.4,
        ease: [0.65, 0, 0.45, 1]
      }
    }
  };
  var fillVariants = {
    unchecked: {
      scale: [1, 1.1, 1.1, 1],
      boxShadow: "inset 0px 0px 0px 0px " + animatedCheckmark.colors[color]
    },
    checked: {
      scale: [1, 1.1, 1.1, 1],
      boxShadow: "inset 0px 0px 0px " + (filled ? computeSize(size) : 0) + "px " + animatedCheckmark.colors[color],
      transition: {
        delay: 0.4,
        duration: 0.4,
        ease: 'easeInOut',
        scale: {
          delay: filled ? 0.9 : 0.42,
          duration: 0.4
        }
      }
    }
  };
  return jsx(Div, {
    animate: 'checked',
    initial: 'unchecked',
    style: {
      width: computeSize(size) + "px",
      height: computeSize(size) + "px",
      borderRadius: animatedCheckmark.borderRadius
    },
    variants: fillVariants
  }, jsx(motion.svg, {
    css: /*#__PURE__*/css(templateObject_1$R || (templateObject_1$R = __makeTemplateObject(["\n          position: absolute;\n        ;label:AnimatedCheckMark;" + _emotionSourceMap$R], ["\n          position: absolute;\n        ;label:AnimatedCheckMark;" + _emotionSourceMap$R]))),
    width: computeSize(size) + "px",
    height: computeSize(size) + "px",
    viewBox: "0 0 26 26"
  }, jsx(motion.circle, {
    cx: "13",
    cy: "13",
    r: "12",
    fill: "transparent",
    stroke: "" + animatedCheckmark.colors[color],
    strokeDasharray: 82,
    style: {
      strokeDashoffset: strokeDashoffset
    },
    strokeLinecap: "round",
    strokeLinejoin: "round",
    strokeWidth: "" + animatedCheckmark.strokeWidth[strokeWidth],
    variants: circleVariants,
    strokeMiterlimit: 10
  }), jsx(motion.path, {
    d: "M 0 0 L 4 4 L 12 -6 ",
    transform: "translate(7, 14)",
    fill: "none",
    stroke: "" + (filled ? animatedCheckmark.checkmarkFilledColor : animatedCheckmark.colors[color]),
    strokeLinecap: "round",
    strokeLinejoin: "round",
    strokeWidth: "" + animatedCheckmark.strokeWidth[strokeWidth],
    variants: tickVariants,
    style: {
      pathLength: pathLength,
      opacity: opacity
    }
  })));
};
var templateObject_1$R;

var Avatar = function (_a) {
  var avatarImage = _a.avatarImage,
    size = _a.size,
    isActive = _a.isActive,
    initials = _a.initials,
    isSelected = _a.isSelected;
  var avatar = useEntities().avatar;
  var sizeMap = avatar.sizes;
  return jsx(Div, {
    css: styles$O.avatarContainer(size, sizeMap, avatar, avatarImage)
  }, !avatarImage && jsx(Text, {
    type: computeTextTypeFromSize(size),
    color: "primaryBody"
  }, initials), isSelected ? jsx(Div, {
    css: styles$O.checkmarkContainer(size, sizeMap)
  }, jsx(AnimatedCheckMark, {
    sizeMap: {
      xSmall: avatar.sizes.xSmall.bubbleSize,
      small: avatar.sizes.small.bubbleSize,
      medium: avatar.sizes.medium.bubbleSize,
      large: avatar.sizes.large.bubbleSize,
      xLarge: avatar.sizes.xLarge.bubbleSize
    },
    filled: true,
    size: size
  })) : isActive && jsx("span", {
    css: styles$O.activeBubble(avatar, size, sizeMap, isActive)
  }));
};

var _emotionSourceMap$Q = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFHb0MiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgX19tYWtlVGVtcGxhdGVPYmplY3QgfSBmcm9tIFwidHNsaWJcIjtcclxuaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vY29yZSc7XHJcbmV4cG9ydCBkZWZhdWx0IHtcclxuICAgIGJhZGdlOiBmdW5jdGlvbiAoaXRlbSkgeyByZXR1cm4gY3NzKHRlbXBsYXRlT2JqZWN0XzEgfHwgKHRlbXBsYXRlT2JqZWN0XzEgPSBfX21ha2VUZW1wbGF0ZU9iamVjdChbXCJcXG4gICAgaGVpZ2h0OiBcIiwgXCI7XFxuICAgIHdpZHRoOiBcIiwgXCI7XFxuICAgIGRpc3BsYXk6IGZsZXg7XFxuICAgIGp1c3RpZnktY29udGVudDogY2VudGVyO1xcbiAgICBhbGlnbi1pdGVtczogY2VudGVyO1xcbiAgXCJdLCBbXCJcXG4gICAgaGVpZ2h0OiBcIixcclxuICAgICAgICBcIjtcXG4gICAgd2lkdGg6IFwiLCBcIjtcXG4gICAgZGlzcGxheTogZmxleDtcXG4gICAganVzdGlmeS1jb250ZW50OiBjZW50ZXI7XFxuICAgIGFsaWduLWl0ZW1zOiBjZW50ZXI7XFxuICBcIl0pKSwgaXNOYU4ocGFyc2VJbnQoaXRlbSkpXHJcbiAgICAgICAgPyAnMThweCdcclxuICAgICAgICA6IGl0ZW0gPT09ICcxMCsnXHJcbiAgICAgICAgICAgID8gJzIycHgnXHJcbiAgICAgICAgICAgIDogJzE4cHgnLCBpc05hTihwYXJzZUludChpdGVtKSkgPyAnbm9uZScgOiBpdGVtID09PSAnMTArJyA/ICcyNHB4JyA6ICcxOHB4Jyk7IH0sXHJcbn07XHJcbnZhciB0ZW1wbGF0ZU9iamVjdF8xO1xyXG4vLyMgc291cmNlTWFwcGluZ1VSTD1zdHlsZXMuanMubWFwIl19 */";
var styles$N = {
  badge: function (item) {
    return /*#__PURE__*/css(templateObject_1$Q || (templateObject_1$Q = __makeTemplateObject(["\n    height: ", ";\n    width: ", ";\n    display: flex;\n    justify-content: center;\n    align-items: center;\n  " + _emotionSourceMap$Q], ["\n    height: ", ";\n    width: ", ";\n    display: flex;\n    justify-content: center;\n    align-items: center;\n  " + _emotionSourceMap$Q])), isNaN(parseInt(item)) ? '18px' : item === '10+' ? '22px' : '18px', isNaN(parseInt(item)) ? 'none' : item === '10+' ? '24px' : '18px');
  }
};
var templateObject_1$Q;

var convertTextColorToIconColor = function (textColor) {
  switch (textColor) {
    case 'primaryBody':
      return 'primaryBodyText';
    case 'primaryHeading':
      return 'primaryHeadingText';
    case 'inverseBody':
      return 'inverseBodyText';
    case 'inverseHeading':
      return 'inverseHeadingText';
    default:
      return textColor;
  }
};

var Badge = function (_a) {
  var item = _a.item,
    backgroundColor = _a.backgroundColor,
    icon = _a.icon,
    _b = _a.size,
    size = _b === void 0 ? 'small' : _b,
    _c = _a.color,
    color = _c === void 0 ? 'inverseBody' : _c;
  var parseItem = function (item) {
    var toInt = parseInt(item);
    if (isNaN(toInt)) {
      return item;
    } else {
      if (toInt > 10) {
        return '10+';
      } else {
        return toInt.toString();
      }
    }
  };
  return jsx(Div, {
    css: styles$N.badge(parseItem(item)),
    backgroundColor: backgroundColor !== null && backgroundColor !== void 0 ? backgroundColor : {
      swatch: 'primaryBrand',
      shade: 'base'
    },
    inset: isNaN(parseInt(item)) ? {
      horizontal: 'x_5'
    } : 'x0',
    borderRadius: "small"
  }, icon && jsx(Div, {
    alignItems: "center",
    style: {
      lineHeight: 0
    },
    inset: {
      right: 'x1'
    }
  }, jsx(Icon, {
    name: icon,
    primaryColor: convertTextColorToIconColor(color),
    size: size
  })), jsx(Text, {
    type: "footnote",
    color: color,
    weight: 'semiBold'
  }, parseItem(item)));
};

var _emotionSourceMap$P = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFHeUUiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgX19tYWtlVGVtcGxhdGVPYmplY3QgfSBmcm9tIFwidHNsaWJcIjtcclxuaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vY29yZSc7XHJcbmV4cG9ydCBkZWZhdWx0IHtcclxuICAgIGxpc3RJdGVtOiBmdW5jdGlvbiAoZ2VuZXJhdGVkU3R5bGVzLCBvcmllbnRhdGlvbiwgZW50aXRpZXMpIHsgcmV0dXJuIGNzcyh0ZW1wbGF0ZU9iamVjdF8xIHx8ICh0ZW1wbGF0ZU9iamVjdF8xID0gX19tYWtlVGVtcGxhdGVPYmplY3QoW1wiXFxuICAgIGxpc3Qtc3R5bGU6IGluaGVyaXQ7XFxuICAgIHBhZGRpbmc6IFwiLCBcIjtcXG4gICAgd2lkdGg6IDEwMCU7XFxuICAgIHRyYW5zaXRpb246IGVhc2UtaW4tb3V0IDAuMzVzO1xcbiAgICBmb250LXNpemU6IGluaGVyaXQ7XFxuICAgIFwiLCBcIjtcXG4gIFwiXSwgW1wiXFxuICAgIGxpc3Qtc3R5bGU6IGluaGVyaXQ7XFxuICAgIHBhZGRpbmc6IFwiLFxyXG4gICAgICAgIFwiO1xcbiAgICB3aWR0aDogMTAwJTtcXG4gICAgdHJhbnNpdGlvbjogZWFzZS1pbi1vdXQgMC4zNXM7XFxuICAgIGZvbnQtc2l6ZTogaW5oZXJpdDtcXG4gICAgXCIsIFwiO1xcbiAgXCJdKSksIG9yaWVudGF0aW9uID09PSAndmVydGljYWwnXHJcbiAgICAgICAgPyBlbnRpdGllcy5zcGFjaW5nLngyXHJcbiAgICAgICAgOiBcIjBweCBcIiArIGVudGl0aWVzLnNwYWNpbmcueDEsIGdlbmVyYXRlZFN0eWxlcyk7IH0sXHJcbn07XHJcbnZhciB0ZW1wbGF0ZU9iamVjdF8xO1xyXG4vLyMgc291cmNlTWFwcGluZ1VSTD1zdHlsZXMuanMubWFwIl19 */";
var styles$M = {
  listItem: function (generatedStyles, orientation, entities) {
    return /*#__PURE__*/css(templateObject_1$P || (templateObject_1$P = __makeTemplateObject(["\n    list-style: inherit;\n    padding: ", ";\n    width: 100%;\n    transition: ease-in-out 0.35s;\n    font-size: inherit;\n    ", ";\n  " + _emotionSourceMap$P], ["\n    list-style: inherit;\n    padding: ", ";\n    width: 100%;\n    transition: ease-in-out 0.35s;\n    font-size: inherit;\n    ", ";\n  " + _emotionSourceMap$P])), orientation === 'vertical' ? entities.spacing.x2 : "0px " + entities.spacing.x1, generatedStyles);
  }
};
var templateObject_1$P;

var ListItem = function (props) {
  var entities = useEntities();
  var generatedStyles = generateStyles(props, entities);
  return jsx(motion.li, _extends({}, props, {
    onClick: props.onMouseEvent ? function (e) {
      var _a;
      return (_a = props.onMouseEvent) === null || _a === void 0 ? void 0 : _a.call(props, e);
    } : function () {
      var _a;
      return (_a = props.onClick) === null || _a === void 0 ? void 0 : _a.call(props);
    },
    css: styles$M.listItem(generatedStyles, props.orientation, entities)
  }), props.children);
};

var Bar = function (_a) {
  var height = _a.height,
    width = _a.width,
    _b = _a.overflow,
    overflow = _b === void 0 ? 'visible' : _b,
    position = _a.position,
    backgroundColor = _a.backgroundColor;
  var hocs = useEntities().hocs;
  return jsx(Div, {
    backgroundColor: backgroundColor !== null && backgroundColor !== void 0 ? backgroundColor : "" + hocs.colors.swatches.grayScale._700,
    height: height,
    width: width,
    overflow: overflow,
    position: position
  });
};

var _emotionSourceMap2$t = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFJd0QiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgX19tYWtlVGVtcGxhdGVPYmplY3QgfSBmcm9tIFwidHNsaWJcIjtcclxuaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vY29yZSc7XHJcbmV4cG9ydCBkZWZhdWx0IHtcclxuICAgIGZsZXhTdHlsZXM6IGZ1bmN0aW9uIChnZW5lcmF0ZWRTdHlsZXMpIHsgcmV0dXJuIGNzcyh0ZW1wbGF0ZU9iamVjdF8xIHx8ICh0ZW1wbGF0ZU9iamVjdF8xID0gX19tYWtlVGVtcGxhdGVPYmplY3QoW1wiXFxuICAgIFwiLCBcIlxcbiAgICBmb250LXNpemU6IGluaGVyaXQ7XFxuICBcIl0sIFtcIlxcbiAgICBcIiwgXCJcXG4gICAgZm9udC1zaXplOiBpbmhlcml0O1xcbiAgXCJdKSksIGdlbmVyYXRlZFN0eWxlcyk7IH0sXHJcbiAgICBmbGV4SXRlbVN0eWxlczogZnVuY3Rpb24gKGdlbmVyYXRlZFN0eWxlcykgeyByZXR1cm4gY3NzKHRlbXBsYXRlT2JqZWN0XzIgfHwgKHRlbXBsYXRlT2JqZWN0XzIgPSBfX21ha2VUZW1wbGF0ZU9iamVjdChbXCJcXG4gICAgXCIsIFwiXFxuICAgIGRpc3BsYXk6IGZsZXg7XFxuICAgIHdpZHRoOiAxMDAlO1xcbiAgXCJdLCBbXCJcXG4gICAgXCIsIFwiXFxuICAgIGRpc3BsYXk6IGZsZXg7XFxuICAgIHdpZHRoOiAxMDAlO1xcbiAgXCJdKSksIGdlbmVyYXRlZFN0eWxlcyk7IH0sXHJcbn07XHJcbnZhciB0ZW1wbGF0ZU9iamVjdF8xLCB0ZW1wbGF0ZU9iamVjdF8yO1xyXG4vLyMgc291cmNlTWFwcGluZ1VSTD1zdHlsZXMuanMubWFwIl19 */";
var _emotionSourceMap$O = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFHb0QiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgX19tYWtlVGVtcGxhdGVPYmplY3QgfSBmcm9tIFwidHNsaWJcIjtcclxuaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vY29yZSc7XHJcbmV4cG9ydCBkZWZhdWx0IHtcclxuICAgIGZsZXhTdHlsZXM6IGZ1bmN0aW9uIChnZW5lcmF0ZWRTdHlsZXMpIHsgcmV0dXJuIGNzcyh0ZW1wbGF0ZU9iamVjdF8xIHx8ICh0ZW1wbGF0ZU9iamVjdF8xID0gX19tYWtlVGVtcGxhdGVPYmplY3QoW1wiXFxuICAgIFwiLCBcIlxcbiAgICBmb250LXNpemU6IGluaGVyaXQ7XFxuICBcIl0sIFtcIlxcbiAgICBcIiwgXCJcXG4gICAgZm9udC1zaXplOiBpbmhlcml0O1xcbiAgXCJdKSksIGdlbmVyYXRlZFN0eWxlcyk7IH0sXHJcbiAgICBmbGV4SXRlbVN0eWxlczogZnVuY3Rpb24gKGdlbmVyYXRlZFN0eWxlcykgeyByZXR1cm4gY3NzKHRlbXBsYXRlT2JqZWN0XzIgfHwgKHRlbXBsYXRlT2JqZWN0XzIgPSBfX21ha2VUZW1wbGF0ZU9iamVjdChbXCJcXG4gICAgXCIsIFwiXFxuICAgIGRpc3BsYXk6IGZsZXg7XFxuICAgIHdpZHRoOiAxMDAlO1xcbiAgXCJdLCBbXCJcXG4gICAgXCIsIFwiXFxuICAgIGRpc3BsYXk6IGZsZXg7XFxuICAgIHdpZHRoOiAxMDAlO1xcbiAgXCJdKSksIGdlbmVyYXRlZFN0eWxlcyk7IH0sXHJcbn07XHJcbnZhciB0ZW1wbGF0ZU9iamVjdF8xLCB0ZW1wbGF0ZU9iamVjdF8yO1xyXG4vLyMgc291cmNlTWFwcGluZ1VSTD1zdHlsZXMuanMubWFwIl19 */";
var styles$L = {
  flexStyles: function (generatedStyles) {
    return /*#__PURE__*/css(templateObject_1$O || (templateObject_1$O = __makeTemplateObject(["\n    ", "\n    font-size: inherit;\n  " + _emotionSourceMap$O], ["\n    ", "\n    font-size: inherit;\n  " + _emotionSourceMap$O])), generatedStyles);
  },
  flexItemStyles: function (generatedStyles) {
    return /*#__PURE__*/css(templateObject_2$t || (templateObject_2$t = __makeTemplateObject(["\n    ", "\n    display: flex;\n    width: 100%;\n  " + _emotionSourceMap2$t], ["\n    ", "\n    display: flex;\n    width: 100%;\n  " + _emotionSourceMap2$t])), generatedStyles);
  }
};
var templateObject_1$O, templateObject_2$t;

var Flex = function (props) {
  var entities = useEntities();
  var _a = props.display,
    display = _a === void 0 ? 'flex' : _a,
    rest = __rest(props, ["display"]);
  var generatedDivStyles = generateStyles(__assign({
    display: display
  }, rest), entities);
  var ref = useRef(null);
  useOutsideClick(ref, function () {
    props.outsideClick && props.outsideClick();
  });
  return jsx(motion.main, _extends({}, rest, {
    ref: ref,
    onClick: props.onMouseEvent ? function (e) {
      var _a;
      return (_a = props.onMouseEvent) === null || _a === void 0 ? void 0 : _a.call(props, e);
    } : function () {
      var _a;
      return (_a = props.onClick) === null || _a === void 0 ? void 0 : _a.call(props);
    },
    css: styles$L.flexStyles(generatedDivStyles),
    className: "" + (props.className ? props.className : '')
  }), props.children);
};

var _emotionSourceMap$N = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var LoadingSpinner = function (_a) {
  var size = _a.size,
    _b = _a.hasNoColor,
    hasNoColor = _b === void 0 ? false : _b;
  var _c = useEntities(),
    loadingSpinner = _c.loadingSpinner,
    hocs = _c.hocs;
  var circleVariants = {
    isLoading: {
      strokeDashoffset: [187, 187 / 4, 187],
      rotate: [0, 135, 450],
      stroke: hasNoColor ? [hocs.textColors.inverseHeading, hocs.textColors.inverseHeading, hocs.textColors.inverseHeading, hocs.textColors.inverseHeading, hocs.textColors.inverseHeading] : [loadingSpinner.colors.primary, loadingSpinner.colors.secondary, loadingSpinner.colors.success, loadingSpinner.colors.warning, loadingSpinner.colors.danger],
      transition: {
        duration: 1.4,
        repeat: Infinity
      }
    }
  };
  var rotateVariants = {
    isLoading: {
      rotate: [0, 270],
      transition: {
        duration: 1.4,
        repeat: Infinity
      }
    }
  };
  return jsx(Div, {
    animate: 'isLoading',
    variants: rotateVariants,
    css: /*#__PURE__*/css(templateObject_1$N || (templateObject_1$N = __makeTemplateObject(["\n        display: flex;\n        justify-content: center;\n        align-items: center;\n      ;label:LoadingSpinner;" + _emotionSourceMap$N], ["\n        display: flex;\n        justify-content: center;\n        align-items: center;\n      ;label:LoadingSpinner;" + _emotionSourceMap$N])))
  }, jsx(motion.svg, {
    width: loadingSpinner.size[size] + "px",
    height: loadingSpinner.size[size] + "px",
    viewBox: "0 0 66 66",
    xmlns: "http://www.w3.org/2000/svg"
  }, jsx(motion.circle, {
    strokeDasharray: 187,
    variants: circleVariants,
    fill: "none",
    strokeWidth: "4",
    strokeLinecap: "round",
    cx: "33",
    cy: "33",
    r: "30"
  })));
};
var templateObject_1$N;

var Spacer = function (_a) {
  var space = _a.space,
    _b = _a.orientation,
    orientation = _b === void 0 ? 'vertical' : _b;
  return jsx(Div, {
    minHeight: orientation === 'vertical' ? space : '100%',
    minWidth: orientation === 'vertical' ? '100%' : space
  });
};

var _emotionSourceMap$M = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFHdUQiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgX19tYWtlVGVtcGxhdGVPYmplY3QgfSBmcm9tIFwidHNsaWJcIjtcclxuaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vY29yZSc7XHJcbmV4cG9ydCBkZWZhdWx0IHtcclxuICAgIHNlY3Rpb25TdHlsZXM6IGZ1bmN0aW9uIChnZW5lcmF0ZWRTdHlsZXMpIHsgcmV0dXJuIGNzcyh0ZW1wbGF0ZU9iamVjdF8xIHx8ICh0ZW1wbGF0ZU9iamVjdF8xID0gX19tYWtlVGVtcGxhdGVPYmplY3QoW1wiXFxuICAgIFwiLCBcIlxcbiAgICBmb250LXNpemU6IGluaGVyaXQ7XFxuICBcIl0sIFtcIlxcbiAgICBcIiwgXCJcXG4gICAgZm9udC1zaXplOiBpbmhlcml0O1xcbiAgXCJdKSksIGdlbmVyYXRlZFN0eWxlcyk7IH0sXHJcbn07XHJcbnZhciB0ZW1wbGF0ZU9iamVjdF8xO1xyXG4vLyMgc291cmNlTWFwcGluZ1VSTD1zdHlsZXMuanMubWFwIl19 */";
var styles$K = {
  sectionStyles: function (generatedStyles) {
    return /*#__PURE__*/css(templateObject_1$M || (templateObject_1$M = __makeTemplateObject(["\n    ", "\n    font-size: inherit;\n  " + _emotionSourceMap$M], ["\n    ", "\n    font-size: inherit;\n  " + _emotionSourceMap$M])), generatedStyles);
  }
};
var templateObject_1$M;

var Section = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var entities = useEntities();
  var generatedDivStyles = generateStyles(props, entities);
  useOutsideClick(ref, function () {
    props.outsideClick && props.outsideClick();
  }, props.clickElementBypass);
  return jsx(motion.section, _extends({}, props, {
    ref: ref,
    animate: process.env.NODE_ENV === 'test' ? undefined : props.animate,
    onClick: function () {
      var _a;
      return (_a = props.onClick) === null || _a === void 0 ? void 0 : _a.call(props);
    },
    css: styles$K.sectionStyles(generatedDivStyles),
    className: "" + (props.className ? props.className : '')
  }), props.children);
});

var _emotionSourceMap$L = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFHcUQiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgX19tYWtlVGVtcGxhdGVPYmplY3QgfSBmcm9tIFwidHNsaWJcIjtcclxuaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vY29yZSc7XHJcbmV4cG9ydCBkZWZhdWx0IHtcclxuICAgIGFzaWRlU3R5bGVzOiBmdW5jdGlvbiAoZ2VuZXJhdGVkU3R5bGVzKSB7IHJldHVybiBjc3ModGVtcGxhdGVPYmplY3RfMSB8fCAodGVtcGxhdGVPYmplY3RfMSA9IF9fbWFrZVRlbXBsYXRlT2JqZWN0KFtcIlxcbiAgICBcIiwgXCJcXG4gICAgZm9udC1zaXplOiBpbmhlcml0O1xcbiAgXCJdLCBbXCJcXG4gICAgXCIsIFwiXFxuICAgIGZvbnQtc2l6ZTogaW5oZXJpdDtcXG4gIFwiXSkpLCBnZW5lcmF0ZWRTdHlsZXMpOyB9LFxyXG59O1xyXG52YXIgdGVtcGxhdGVPYmplY3RfMTtcclxuLy8jIHNvdXJjZU1hcHBpbmdVUkw9c3R5bGVzLmpzLm1hcCJdfQ== */";
var styles$J = {
  asideStyles: function (generatedStyles) {
    return /*#__PURE__*/css(templateObject_1$L || (templateObject_1$L = __makeTemplateObject(["\n    ", "\n    font-size: inherit;\n  " + _emotionSourceMap$L], ["\n    ", "\n    font-size: inherit;\n  " + _emotionSourceMap$L])), generatedStyles);
  }
};
var templateObject_1$L;

var Aside = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var entities = useEntities();
  var generatedDivStyles = generateStyles(props, entities);
  useOutsideClick(ref, function () {
    props.outsideClick && props.outsideClick();
  });
  return jsx(motion.aside, _extends({}, props, {
    ref: ref,
    animate: process.env.NODE_ENV === 'test' ? undefined : props.animate,
    onClick: props.onMouseEvent ? function (e) {
      var _a;
      return (_a = props.onMouseEvent) === null || _a === void 0 ? void 0 : _a.call(props, e);
    } : function () {
      var _a;
      return (_a = props.onClick) === null || _a === void 0 ? void 0 : _a.call(props);
    },
    css: styles$J.asideStyles(generatedDivStyles),
    className: "" + (props.className ? props.className : '')
  }), props.children);
});

var VStack = function (_a) {
  var children = _a.children,
    gap = _a.gap,
    justifyContent = _a.justifyContent,
    alignItems = _a.alignItems,
    alignContent = _a.alignContent,
    inset = _a.inset,
    outset = _a.outset,
    _b = _a.width,
    width = _b === void 0 ? '100%' : _b,
    height = _a.height,
    overflow = _a.overflow;
  return jsx(Div, {
    display: "grid",
    width: width,
    height: height,
    overflow: overflow,
    rowGap: gap,
    justifyContent: justifyContent,
    alignContent: alignContent,
    alignItems: alignItems,
    inset: inset,
    outset: outset
  }, children);
};

var HStack = function (_a) {
  var children = _a.children,
    gap = _a.gap,
    _b = _a.justify,
    justify = _b === void 0 ? 'center' : _b,
    _c = _a.alignItems,
    alignItems = _c === void 0 ? 'center' : _c,
    _d = _a.shouldWrap,
    shouldWrap = _d === void 0 ? false : _d,
    inset = _a.inset,
    outset = _a.outset,
    _e = _a.childWidth,
    childWidth = _e === void 0 ? 'initial' : _e;
  var childrenArray = React.Children.toArray(children);
  return jsx(Flex, {
    width: "auto",
    inset: inset,
    outset: outset,
    alignItems: alignItems,
    justifyContent: justify,
    flexWrap: shouldWrap ? 'wrap' : 'nowrap'
  }, childrenArray.map(function (child, i) {
    if (child) {
      var isLastItem = i === childrenArray.length - 1;
      return jsx(Div, {
        outset: typeof gap === 'object' ? gap : {
          right: isLastItem ? 'x0' : gap,
          bottom: shouldWrap ? gap : 'x0'
        },
        width: childWidth,
        inset: inset
      }, child);
    }
    return jsx(React.Fragment, null);
  }));
};

var Card = function (props) {
  var _a, _b;
  var card = useEntities().card;
  return jsx(Div, _extends({}, props, {
    inset: props.inset ? props.inset : card.defaultInset,
    onClick: props.onClick && function () {
      var _a;
      return (_a = props === null || props === void 0 ? void 0 : props.onClick) === null || _a === void 0 ? void 0 : _a.call(props);
    },
    boxShadow: (_a = props.boxShadow) !== null && _a !== void 0 ? _a : card.boxShadow,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: props.borderRadius ? props.borderRadius : card.borderRadius,
    position: (_b = props.position) !== null && _b !== void 0 ? _b : 'relative'
  }), props.children);
};

var Capsule = function (_a) {
  var title = _a.title,
    backgroundColor = _a.backgroundColor,
    fontColor = _a.fontColor,
    boxShadow = _a.boxShadow;
  return jsx(Div, {
    inset: {
      horizontal: 'x2'
    },
    borderRadius: "circle",
    backgroundColor: backgroundColor !== null && backgroundColor !== void 0 ? backgroundColor : {
      swatch: 'success',
      shade: 'base'
    },
    display: "inline-block",
    boxShadow: boxShadow
  }, jsx(Text, {
    type: "footnote",
    content: title,
    color: fontColor !== null && fontColor !== void 0 ? fontColor : 'primaryBody',
    weight: "bold",
    style: {
      letterSpacing: '.2px'
    }
  }));
};

var computeButtonSizeProperties = function (size, sizeMap) {
  return mapSizeType(size, sizeMap);
};

var _emotionSourceMap2$s = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap$K = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var styles$I = {
  buttonStyles: function (type, buttonStyle, entities, size, hover, outlined, fullWidth, backgroundColorOverride, boxShadow) {
    var padding = outlined ? computeButtonSizeProperties(size, entities.buttons.sizes).outlinedInset.vertical : type === 'smoke' ? size === 'xSmall' ? '4.25px' : size === 'small' ? '6.5px' : size === 'medium' ? '3.75px' : size === 'large' ? '9px' : '20.25px' : undefined;
    var boxShadowStyle = boxShadow ? outlined ? 'none' : backgroundColorOverride ? entities.buttons.boxShadow('xLight', Color$1(backgroundColorOverride).rgb().alpha(0.5).toString()) : type === 'shadow' ? entities.buttons.boxShadow('light', Color$1(buttonStyle.fontColor).rgb().alpha(0.5).toString()) : entities.buttons.boxShadow('xLight', Color$1(buttonStyle.backgroundColor).rgb().alpha(0.5).toString()) : 'none';
    return /*#__PURE__*/css(templateObject_1$K || (templateObject_1$K = __makeTemplateObject(["\n      background: ", ";\n      color: ", ";\n      border: ", ";\n      box-shadow: ", ";\n      width: ", ";\n      padding: ", ";\n      padding-top: ", ";\n      padding-bottom: ", ";\n      padding-left: ", ";\n      padding-right: ", ";\n      display: flex;\n      justify-content: center;\n      font-size: inherit;\n      line-height: 1.2;\n      border-radius: ", ";\n      cursor: pointer;\n      outline: none;\n      :disabled {\n        opacity: 0.35;\n        cursor: not-allowed;\n        :hover {\n          box-shadow: none;\n          transform: translateY(0);\n        }\n      }\n      :hover {\n        transform: ", ";\n        box-shadow: ", ";\n        transition: ", ";\n      }\n    " + _emotionSourceMap$K], ["\n      background: ", ";\n      color: ", ";\n      border: ", ";\n      box-shadow: ", ";\n      width: ", ";\n      padding: ", ";\n      padding-top: ", ";\n      padding-bottom: ", ";\n      padding-left: ", ";\n      padding-right: ", ";\n      display: flex;\n      justify-content: center;\n      font-size: inherit;\n      line-height: 1.2;\n      border-radius: ", ";\n      cursor: pointer;\n      outline: none;\n      :disabled {\n        opacity: 0.35;\n        cursor: not-allowed;\n        :hover {\n          box-shadow: none;\n          transform: translateY(0);\n        }\n      }\n      :hover {\n        transform: ", ";\n        box-shadow: ", ";\n        transition: ", ";\n      }\n    " + _emotionSourceMap$K])), outlined ? 'transparent' : backgroundColorOverride ? backgroundColorOverride : buttonStyle.backgroundColor, buttonStyle.fontColor, outlined ? backgroundColorOverride ? entities.buttons.borderSize + " solid " + backgroundColorOverride : type === 'shadow' ? entities.buttons.borderSize + " solid " + buttonStyle.fontColor : entities.buttons.borderSize + " solid " + buttonStyle.backgroundColor : type === 'smoke' ? entities.buttons.borderSize + " solid " + entities.hocs.colors.swatches.grayScale._1000 : 'none', boxShadowStyle, fullWidth && '100%', !outlined && computeButtonSizeProperties(size, entities.buttons.sizes).inset, padding, padding, outlined ? computeButtonSizeProperties(size, entities.buttons.sizes).outlinedInset.vertical : type === 'smoke' ? '16px' : undefined, outlined ? computeButtonSizeProperties(size, entities.buttons.sizes).outlinedInset.vertical : type === 'smoke' ? '24px' : undefined, computeButtonSizeProperties(size, entities.buttons.sizes).borderRadius, hover && 'translateY(3px)', hover && 'none', hover && 'transform 0.2s, box-shadow 0.2s ease-out');
  },
  buttonTitle: function (type, buttonEntity, size, textEntity, fontWeight) {
    return /*#__PURE__*/css(templateObject_2$s || (templateObject_2$s = __makeTemplateObject(["\n    font-family: inherit;\n    font-size: ", ";\n    white-space: nowrap;\n    font-weight: ", ";\n  " + _emotionSourceMap2$s], ["\n    font-family: inherit;\n    font-size: ", ";\n    white-space: nowrap;\n    font-weight: ", ";\n  " + _emotionSourceMap2$s])), computeButtonSizeProperties(size, buttonEntity.sizes).fontSize, fontWeight ? textEntity.weights[fontWeight] : type === 'smoke' ? textEntity.weights.bold : textEntity.weights.regular);
  }
};
var templateObject_1$K, templateObject_2$s;

var _emotionSourceMap$J = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var styles$H = {
  tooltip: function (direction, background, repositionTooltip) {
    return /*#__PURE__*/css(templateObject_1$J || (templateObject_1$J = __makeTemplateObject(["\n    transform: ", "\n    position: relative;\n    left: 0;\n    background: ", ";\n    :after,\n    :before {\n      right: ", ";\n      top: ", ";\n      bottom: ", ";\n      left: ", ";\n      border: solid transparent;\n      content: ' ';\n      height: 0;\n      width: 0;\n      position: absolute;\n      pointer-events: none;\n    }\n    :after {\n      border-color: rgba(254, 254, 254, 0);\n      border-right-color: ", ";\n      border-bottom-color: ", ";\n      border-left-color: ", ";\n      border-top-color: ", ";\n      border-width: 8px;\n      margin-top: ", ";\n      margin-left: ", ";\n    }\n    :before {\n      border-color: rgba(227, 230, 232, 0);\n      border-right-color: ", ";\n      border-bottom-color: ", ";\n      border-left-color: ", ";\n      border-top-color: ", ";\n\n      border-width: 9px;\n      margin-top: ", ";\n      margin-left: ", ";\n    }\n  " + _emotionSourceMap$J], ["\n    transform: ", "\n    position: relative;\n    left: 0;\n    background: ", ";\n    :after,\n    :before {\n      right: ", ";\n      top: ", ";\n      bottom: ", ";\n      left: ", ";\n      border: solid transparent;\n      content: ' ';\n      height: 0;\n      width: 0;\n      position: absolute;\n      pointer-events: none;\n    }\n    :after {\n      border-color: rgba(254, 254, 254, 0);\n      border-right-color: ", ";\n      border-bottom-color: ", ";\n      border-left-color: ", ";\n      border-top-color: ", ";\n      border-width: 8px;\n      margin-top: ", ";\n      margin-left: ", ";\n    }\n    :before {\n      border-color: rgba(227, 230, 232, 0);\n      border-right-color: ", ";\n      border-bottom-color: ", ";\n      border-left-color: ", ";\n      border-top-color: ", ";\n\n      border-width: 9px;\n      margin-top: ", ";\n      margin-left: ", ";\n    }\n  " + _emotionSourceMap$J])), repositionTooltip ? 'translateY(-125%);' : null, background, direction === 'left' && '100%', direction === 'right' || direction === 'left' ? '50%' : direction === 'bottom' && '100%', direction === 'top' && '100%', direction === 'top' || direction === 'bottom' ? '50%' : direction === 'right' && '100%', direction === 'left' && background, direction === 'top' && background, direction === 'right' && background, direction === 'bottom' && background, direction === 'left' || direction === 'right' ? '-8px' : 0, direction === 'top' || direction === 'bottom' ? '-8px' : 0, direction === 'left' && background, direction === 'top' && background, direction === 'right' && background, direction === 'bottom' && background, direction === 'left' || direction === 'right' ? '-9px' : 0, direction === 'top' || direction === 'bottom' ? '-9px' : 0);
  }
};
var templateObject_1$J;

var Tooltip = function (_a) {
  var title = _a.title,
    children = _a.children,
    direction = _a.direction,
    _b = _a.background,
    background = _b === void 0 ? 'light' : _b,
    repositionTooltip = _a.repositionTooltip,
    zIndex = _a.zIndex,
    _c = _a.whiteSpace,
    whiteSpace = _c === void 0 ? 'nowrap' : _c;
  var tooltip = useEntities().tooltip;
  var backgroundColor = background === 'light' ? tooltip.lightBackgroundColor : tooltip.darkBackgroundColor ? tooltip.darkBackgroundColor : tooltip.lightBackgroundColor; // TODO this is because the dark bg color is set from a color swatch which can alter through the theme, come up with fix when colors get sorted out
  return jsx(Span, {
    css: styles$H.tooltip(direction, backgroundColor, repositionTooltip),
    borderRadius: "small",
    inset: {
      vertical: 'x_5',
      horizontal: 'x1'
    },
    boxShadow: tooltip.boxShadow,
    zIndex: zIndex
  }, jsx(Text, {
    type: "footnote",
    color: background === 'light' ? 'primaryBody' : 'inverseBody',
    whiteSpace: whiteSpace
  }, children || title));
};

var getButtonStyle = function (type, buttonMap) {
  switch (type) {
    case 'primary':
      return buttonMap.primary;
    case 'secondary':
      return buttonMap.secondary;
    case 'success':
      return buttonMap.success;
    case 'warning':
      return buttonMap.warning;
    case 'danger':
      return buttonMap.danger;
    case 'shadow':
      return buttonMap.shadow;
    case 'accent':
      return buttonMap.accent;
    case 'smoke':
      return buttonMap.smoke;
  }
};
var Button = function (_a) {
  var _b;
  var title = _a.title,
    _c = _a.type,
    type = _c === void 0 ? 'primary' : _c,
    _d = _a.size,
    size = _d === void 0 ? 'medium' : _d,
    onClick = _a.onClick,
    children = _a.children,
    disabled = _a.disabled,
    hover = _a.hover,
    outlined = _a.outlined,
    onMouseEvent = _a.onMouseEvent,
    fullWidth = _a.fullWidth,
    textColorOverride = _a.textColorOverride,
    backgroundColorOverride = _a.backgroundColorOverride,
    disabledCaption = _a.disabledCaption,
    id = _a.id,
    icon = _a.icon,
    fontWeight = _a.fontWeight,
    _e = _a.boxShadow,
    boxShadow = _e === void 0 ? false : _e;
  var entities = useEntities();
  var buttons = entities.buttons,
    text = entities.text;
  var getColor = function () {
    if (textColorOverride) {
      return textColorOverride;
    }
    if (outlined && backgroundColorOverride) {
      return backgroundColorOverride;
    } else {
      return type === 'shadow' ? 'primaryBody' : type === 'smoke' ? buttons.smoke.fontColor : outlined ? getButtonStyle(type, {
        primary: 'primaryBrand',
        secondary: 'secondaryBrand',
        danger: 'danger',
        success: 'success',
        warning: 'warning',
        shadow: 'inverseBody',
        accent: 'accent',
        smoke: 'primaryBody'
      }) : 'inverseBody';
    }
  };
  var _f = useState(false),
    isHovering = _f[0],
    setIsHovering = _f[1];
  var buttonStyle = buttons[type];
  return jsx(Div, {
    onHoverStart: function () {
      return setIsHovering(true);
    },
    onHoverEnd: function () {
      return setIsHovering(false);
    },
    position: "relative",
    width: fullWidth ? '100%' : undefined
  }, disabled && disabledCaption && isHovering && jsx(Flex, {
    justifyContent: "space-around",
    width: "100%",
    transition: {
      ease: 'easeIn',
      duration: 0.3
    },
    position: "absolute",
    top: "-8px"
  }, jsx(Tooltip, {
    repositionTooltip: !!disabledCaption,
    title: disabledCaption !== null && disabledCaption !== void 0 ? disabledCaption : '',
    direction: 'bottom',
    background: 'dark'
  })), jsx(motion.button, {
    id: id,
    type: typeof onClick === 'string' ? 'submit' : 'button',
    onClick: onMouseEvent ? function (e) {
      return onMouseEvent(e);
    } : function () {
      return typeof onClick !== 'string' && (onClick === null || onClick === void 0 ? void 0 : onClick());
    },
    css: styles$I.buttonStyles(type, buttonStyle, entities, size, hover, outlined, fullWidth, backgroundColorOverride, boxShadow),
    disabled: disabled
  }, jsx(Span, {
    width: "100%",
    display: "flex",
    justifyContent: "space-around",
    alignItems: "center",
    css: styles$I.buttonTitle(type, buttons, size, text, fontWeight),
    color: getColor()
  }, icon && jsx(Flex, {
    alignItems: "center",
    justifyContent: "center",
    outset: {
      right: 'x2'
    }
  }, jsx(Icon, {
    primaryColor: (_b = icon.fill) !== null && _b !== void 0 ? _b : getColor(),
    size: size === 'large' || size === 'xLarge' ? 'medium' : size,
    name: icon.name
  })), title || children)));
};

var _emotionSourceMap3$h = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFLc0IiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgX19tYWtlVGVtcGxhdGVPYmplY3QgfSBmcm9tIFwidHNsaWJcIjtcclxuaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vY29yZSc7XHJcbmV4cG9ydCBkZWZhdWx0IHtcclxuICAgIGluZm9CbG9ja0NvbnRhaW5lcjogY3NzKHRlbXBsYXRlT2JqZWN0XzEgfHwgKHRlbXBsYXRlT2JqZWN0XzEgPSBfX21ha2VUZW1wbGF0ZU9iamVjdChbXCJcXG4gICAgZGlzcGxheTogZmxleDtcXG4gICAgZmxleC1kaXJlY3Rpb246IGNvbHVtbjtcXG4gICAganVzdGlmeS1jb250ZW50OiBzdGFydDtcXG4gICAgYWxpZ24taXRlbXM6IGNlbnRlcjtcXG4gICAgd2hpdGUtc3BhY2U6IG5vd3JhcDtcXG4gICAgc3BhbiB7XFxuICAgICAgZGlzcGxheTogYmxvY2s7XFxuICAgIH1cXG4gIFwiXSwgW1wiXFxuICAgIGRpc3BsYXk6IGZsZXg7XFxuICAgIGZsZXgtZGlyZWN0aW9uOiBjb2x1bW47XFxuICAgIGp1c3RpZnktY29udGVudDogc3RhcnQ7XFxuICAgIGFsaWduLWl0ZW1zOiBjZW50ZXI7XFxuICAgIHdoaXRlLXNwYWNlOiBub3dyYXA7XFxuICAgIHNwYW4ge1xcbiAgICAgIGRpc3BsYXk6IGJsb2NrO1xcbiAgICB9XFxuICBcIl0pKSksXHJcbiAgICB0ZXh0Q29udGFpbmVyOiBjc3ModGVtcGxhdGVPYmplY3RfMiB8fCAodGVtcGxhdGVPYmplY3RfMiA9IF9fbWFrZVRlbXBsYXRlT2JqZWN0KFtcIlwiXSwgW1wiXCJdKSkpLFxyXG4gICAgY2FwdGlvbkNvbnRhaW5lcjogY3NzKHRlbXBsYXRlT2JqZWN0XzMgfHwgKHRlbXBsYXRlT2JqZWN0XzMgPSBfX21ha2VUZW1wbGF0ZU9iamVjdChbXCJcXG4gICAgZGlzcGxheTogZmxleDtcXG4gICAgZmxleC1kaXJlY3Rpb246IHJvdztcXG4gICAganVzdGlmeS1jb250ZW50OiBzdGFydDtcXG4gICAgYWxpZ24taXRlbXM6IGNlbnRlcjtcXG4gICAgd2hpdGUtc3BhY2U6IG5vd3JhcDtcXG4gICAgc3BhbiB7XFxuICAgICAgZGlzcGxheTogYmxvY2s7XFxuICAgIH1cXG4gIFwiXSwgW1wiXFxuICAgIGRpc3BsYXk6IGZsZXg7XFxuICAgIGZsZXgtZGlyZWN0aW9uOiByb3c7XFxuICAgIGp1c3RpZnktY29udGVudDogc3RhcnQ7XFxuICAgIGFsaWduLWl0ZW1zOiBjZW50ZXI7XFxuICAgIHdoaXRlLXNwYWNlOiBub3dyYXA7XFxuICAgIHNwYW4ge1xcbiAgICAgIGRpc3BsYXk6IGJsb2NrO1xcbiAgICB9XFxuICBcIl0pKSksXHJcbn07XHJcbnZhciB0ZW1wbGF0ZU9iamVjdF8xLCB0ZW1wbGF0ZU9iamVjdF8yLCB0ZW1wbGF0ZU9iamVjdF8zO1xyXG4vLyMgc291cmNlTWFwcGluZ1VSTD1zdHlsZXMuanMubWFwIl19 */";
var _emotionSourceMap2$r = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFJbUIiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgX19tYWtlVGVtcGxhdGVPYmplY3QgfSBmcm9tIFwidHNsaWJcIjtcclxuaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vY29yZSc7XHJcbmV4cG9ydCBkZWZhdWx0IHtcclxuICAgIGluZm9CbG9ja0NvbnRhaW5lcjogY3NzKHRlbXBsYXRlT2JqZWN0XzEgfHwgKHRlbXBsYXRlT2JqZWN0XzEgPSBfX21ha2VUZW1wbGF0ZU9iamVjdChbXCJcXG4gICAgZGlzcGxheTogZmxleDtcXG4gICAgZmxleC1kaXJlY3Rpb246IGNvbHVtbjtcXG4gICAganVzdGlmeS1jb250ZW50OiBzdGFydDtcXG4gICAgYWxpZ24taXRlbXM6IGNlbnRlcjtcXG4gICAgd2hpdGUtc3BhY2U6IG5vd3JhcDtcXG4gICAgc3BhbiB7XFxuICAgICAgZGlzcGxheTogYmxvY2s7XFxuICAgIH1cXG4gIFwiXSwgW1wiXFxuICAgIGRpc3BsYXk6IGZsZXg7XFxuICAgIGZsZXgtZGlyZWN0aW9uOiBjb2x1bW47XFxuICAgIGp1c3RpZnktY29udGVudDogc3RhcnQ7XFxuICAgIGFsaWduLWl0ZW1zOiBjZW50ZXI7XFxuICAgIHdoaXRlLXNwYWNlOiBub3dyYXA7XFxuICAgIHNwYW4ge1xcbiAgICAgIGRpc3BsYXk6IGJsb2NrO1xcbiAgICB9XFxuICBcIl0pKSksXHJcbiAgICB0ZXh0Q29udGFpbmVyOiBjc3ModGVtcGxhdGVPYmplY3RfMiB8fCAodGVtcGxhdGVPYmplY3RfMiA9IF9fbWFrZVRlbXBsYXRlT2JqZWN0KFtcIlwiXSwgW1wiXCJdKSkpLFxyXG4gICAgY2FwdGlvbkNvbnRhaW5lcjogY3NzKHRlbXBsYXRlT2JqZWN0XzMgfHwgKHRlbXBsYXRlT2JqZWN0XzMgPSBfX21ha2VUZW1wbGF0ZU9iamVjdChbXCJcXG4gICAgZGlzcGxheTogZmxleDtcXG4gICAgZmxleC1kaXJlY3Rpb246IHJvdztcXG4gICAganVzdGlmeS1jb250ZW50OiBzdGFydDtcXG4gICAgYWxpZ24taXRlbXM6IGNlbnRlcjtcXG4gICAgd2hpdGUtc3BhY2U6IG5vd3JhcDtcXG4gICAgc3BhbiB7XFxuICAgICAgZGlzcGxheTogYmxvY2s7XFxuICAgIH1cXG4gIFwiXSwgW1wiXFxuICAgIGRpc3BsYXk6IGZsZXg7XFxuICAgIGZsZXgtZGlyZWN0aW9uOiByb3c7XFxuICAgIGp1c3RpZnktY29udGVudDogc3RhcnQ7XFxuICAgIGFsaWduLWl0ZW1zOiBjZW50ZXI7XFxuICAgIHdoaXRlLXNwYWNlOiBub3dyYXA7XFxuICAgIHNwYW4ge1xcbiAgICAgIGRpc3BsYXk6IGJsb2NrO1xcbiAgICB9XFxuICBcIl0pKSksXHJcbn07XHJcbnZhciB0ZW1wbGF0ZU9iamVjdF8xLCB0ZW1wbGF0ZU9iamVjdF8yLCB0ZW1wbGF0ZU9iamVjdF8zO1xyXG4vLyMgc291cmNlTWFwcGluZ1VSTD1zdHlsZXMuanMubWFwIl19 */";
var _emotionSourceMap$I = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFHd0IiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgX19tYWtlVGVtcGxhdGVPYmplY3QgfSBmcm9tIFwidHNsaWJcIjtcclxuaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vY29yZSc7XHJcbmV4cG9ydCBkZWZhdWx0IHtcclxuICAgIGluZm9CbG9ja0NvbnRhaW5lcjogY3NzKHRlbXBsYXRlT2JqZWN0XzEgfHwgKHRlbXBsYXRlT2JqZWN0XzEgPSBfX21ha2VUZW1wbGF0ZU9iamVjdChbXCJcXG4gICAgZGlzcGxheTogZmxleDtcXG4gICAgZmxleC1kaXJlY3Rpb246IGNvbHVtbjtcXG4gICAganVzdGlmeS1jb250ZW50OiBzdGFydDtcXG4gICAgYWxpZ24taXRlbXM6IGNlbnRlcjtcXG4gICAgd2hpdGUtc3BhY2U6IG5vd3JhcDtcXG4gICAgc3BhbiB7XFxuICAgICAgZGlzcGxheTogYmxvY2s7XFxuICAgIH1cXG4gIFwiXSwgW1wiXFxuICAgIGRpc3BsYXk6IGZsZXg7XFxuICAgIGZsZXgtZGlyZWN0aW9uOiBjb2x1bW47XFxuICAgIGp1c3RpZnktY29udGVudDogc3RhcnQ7XFxuICAgIGFsaWduLWl0ZW1zOiBjZW50ZXI7XFxuICAgIHdoaXRlLXNwYWNlOiBub3dyYXA7XFxuICAgIHNwYW4ge1xcbiAgICAgIGRpc3BsYXk6IGJsb2NrO1xcbiAgICB9XFxuICBcIl0pKSksXHJcbiAgICB0ZXh0Q29udGFpbmVyOiBjc3ModGVtcGxhdGVPYmplY3RfMiB8fCAodGVtcGxhdGVPYmplY3RfMiA9IF9fbWFrZVRlbXBsYXRlT2JqZWN0KFtcIlwiXSwgW1wiXCJdKSkpLFxyXG4gICAgY2FwdGlvbkNvbnRhaW5lcjogY3NzKHRlbXBsYXRlT2JqZWN0XzMgfHwgKHRlbXBsYXRlT2JqZWN0XzMgPSBfX21ha2VUZW1wbGF0ZU9iamVjdChbXCJcXG4gICAgZGlzcGxheTogZmxleDtcXG4gICAgZmxleC1kaXJlY3Rpb246IHJvdztcXG4gICAganVzdGlmeS1jb250ZW50OiBzdGFydDtcXG4gICAgYWxpZ24taXRlbXM6IGNlbnRlcjtcXG4gICAgd2hpdGUtc3BhY2U6IG5vd3JhcDtcXG4gICAgc3BhbiB7XFxuICAgICAgZGlzcGxheTogYmxvY2s7XFxuICAgIH1cXG4gIFwiXSwgW1wiXFxuICAgIGRpc3BsYXk6IGZsZXg7XFxuICAgIGZsZXgtZGlyZWN0aW9uOiByb3c7XFxuICAgIGp1c3RpZnktY29udGVudDogc3RhcnQ7XFxuICAgIGFsaWduLWl0ZW1zOiBjZW50ZXI7XFxuICAgIHdoaXRlLXNwYWNlOiBub3dyYXA7XFxuICAgIHNwYW4ge1xcbiAgICAgIGRpc3BsYXk6IGJsb2NrO1xcbiAgICB9XFxuICBcIl0pKSksXHJcbn07XHJcbnZhciB0ZW1wbGF0ZU9iamVjdF8xLCB0ZW1wbGF0ZU9iamVjdF8yLCB0ZW1wbGF0ZU9iamVjdF8zO1xyXG4vLyMgc291cmNlTWFwcGluZ1VSTD1zdHlsZXMuanMubWFwIl19 */";
var styles$G = {
  infoBlockContainer: /*#__PURE__*/css(templateObject_1$I || (templateObject_1$I = __makeTemplateObject(["\n    display: flex;\n    flex-direction: column;\n    justify-content: start;\n    align-items: center;\n    white-space: nowrap;\n    span {\n      display: block;\n    }\n  ;label:infoBlockContainer;" + _emotionSourceMap$I], ["\n    display: flex;\n    flex-direction: column;\n    justify-content: start;\n    align-items: center;\n    white-space: nowrap;\n    span {\n      display: block;\n    }\n  ;label:infoBlockContainer;" + _emotionSourceMap$I]))),
  textContainer: /*#__PURE__*/css(templateObject_2$r || (templateObject_2$r = __makeTemplateObject([";label:textContainer;" + _emotionSourceMap2$r], [";label:textContainer;" + _emotionSourceMap2$r]))),
  captionContainer: /*#__PURE__*/css(templateObject_3$h || (templateObject_3$h = __makeTemplateObject(["\n    display: flex;\n    flex-direction: row;\n    justify-content: start;\n    align-items: center;\n    white-space: nowrap;\n    span {\n      display: block;\n    }\n  ;label:captionContainer;" + _emotionSourceMap3$h], ["\n    display: flex;\n    flex-direction: row;\n    justify-content: start;\n    align-items: center;\n    white-space: nowrap;\n    span {\n      display: block;\n    }\n  ;label:captionContainer;" + _emotionSourceMap3$h])))
};
var templateObject_1$I, templateObject_2$r, templateObject_3$h;

var InfoBlock = function (_a) {
  var heading = _a.heading,
    caption = _a.caption,
    size = _a.size,
    _b = _a.alignment,
    alignment = _b === void 0 ? 'left' : _b,
    country = _a.country;
  return jsx("div", {
    css: styles$G.infoBlockContainer
  }, jsx("div", {
    css: styles$G.textContainer
  }, jsx(Text, {
    type: computeTextTypeFromInfoBlockSize(size).heading,
    color: "primaryHeading",
    content: heading,
    alignment: alignment,
    outset: size === 'large' ? {
      bottom: 'x1'
    } : {
      bottom: 'x_5'
    },
    lineHeight: 1,
    weight: "semiBold"
  }), jsx("div", {
    css: styles$G.captionContainer
  }, country && jsx(ReactCountryFlag, {
    countryCode: country.isoCode,
    svg: true,
    style: {
      width: '24px',
      height: '19px',
      borderRadius: '3px',
      marginRight: '4px'
    }
  }), jsx(Text, {
    type: computeTextTypeFromInfoBlockSize(size).caption,
    color: "primaryBody",
    content: caption,
    alignment: alignment,
    lineHeight: 1
  }))));
};

var IconLink = function (_a) {
  var onClick = _a.onClick,
    onMouseEvent = _a.onMouseEvent,
    title = _a.title,
    minimize = _a.minimize,
    icon = _a.icon,
    iconOrientation = _a.iconOrientation,
    _b = _a.fill,
    fill = _b === void 0 ? 'primaryBodyText' : _b,
    _c = _a.isActive,
    isActive = _c === void 0 ? false : _c,
    _d = _a.activeColor,
    activeColor = _d === void 0 ? 'primaryBrand' : _d,
    _e = _a.fontColor,
    fontColor = _e === void 0 ? 'primaryBody' : _e,
    _f = _a.size,
    size = _f === void 0 ? 'medium' : _f,
    _g = _a.isStacked,
    isStacked = _g === void 0 ? false : _g,
    _h = _a.hasBackgroundColor,
    hasBackgroundColor = _h === void 0 ? false : _h,
    gap = _a.gap;
  var variants = {
    open: {
      opacity: 1,
      transition: {
        delay: 0.03
      }
    },
    closed: {
      opacity: 0
    }
  };
  var icons = useEntities().icons;
  return jsx(Flex, {
    flexDirection: isStacked ? 'column' : 'row',
    alignItems: 'center',
    onClick: onClick,
    onMouseEvent: onMouseEvent,
    width: "100%"
  }, jsx(Flex, {
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 'medium',
    backgroundColor: hasBackgroundColor ? icons.colors.background : 'none',
    inset: isStacked || hasBackgroundColor ? 'x1_5' : 'x0',
    cursor: 'pointer'
  }, jsx(Icon, {
    size: size,
    name: icon,
    primaryColor: isActive ? activeColor : fill,
    orientation: iconOrientation && iconOrientation
  })), !minimize && title && jsx(Div, {
    variants: variants,
    animate: minimize ? 'closed' : 'open',
    initial: 'closed',
    transition: {
      duration: 0.1
    },
    inset: !isStacked ? {
      left: hasBackgroundColor ? gap !== null && gap !== void 0 ? gap : 'x1' : gap !== null && gap !== void 0 ? gap : 'x_5'
    } : {
      top: gap !== null && gap !== void 0 ? gap : 'x_0'
    },
    cursor: 'pointer'
  }, jsx(Text, {
    weight: isActive ? 'semiBold' : 'regular',
    color: fontColor,
    content: title,
    type: computeTextTypeFromIconLinkSize(size)
  })));
};

var _emotionSourceMap2$q = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap$H = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var styles$F = {
  grid: function (gap, columnSize, rowSize, maxWidth, height, overflow, isDense) {
    return /*#__PURE__*/css(templateObject_1$H || (templateObject_1$H = __makeTemplateObject(["\n    display: grid;\n    max-width: ", "px;\n    width: 100%;\n    grid-gap: ", "px;\n    grid-template-columns: repeat(auto-fit, minmax(", "px, 1fr));\n    grid-auto-rows: minmax(", "px, auto);\n    grid-auto-flow: ", ";\n    height: ", ";\n    overflow: ", ";\n  " + _emotionSourceMap$H], ["\n    display: grid;\n    max-width: ", "px;\n    width: 100%;\n    grid-gap: ", "px;\n    grid-template-columns: repeat(auto-fit, minmax(", "px, 1fr));\n    grid-auto-rows: minmax(", "px, auto);\n    grid-auto-flow: ", ";\n    height: ", ";\n    overflow: ", ";\n  " + _emotionSourceMap$H])), maxWidth && maxWidth, gap, columnSize, rowSize, isDense ? 'dense' : 'row', height && height, overflow && overflow);
  },
  gridItem: function (gridRowSpan, gridColSpan, gridRowStart, gridColStart, aspectRatio) {
    return /*#__PURE__*/css(templateObject_2$q || (templateObject_2$q = __makeTemplateObject(["\n    width: 100%;\n    height: 100%;\n    display: flex;\n    align-items: flex-end;\n    grid-column-end: span ", ";\n    grid-row-end: span ", ";\n    grid-column-start: ", ";\n    grid-row-start: ", ";\n    aspect-ratio: ", ";\n  " + _emotionSourceMap2$q], ["\n    width: 100%;\n    height: 100%;\n    display: flex;\n    align-items: flex-end;\n    grid-column-end: span ", ";\n    grid-row-end: span ", ";\n    grid-column-start: ", ";\n    grid-row-start: ", ";\n    aspect-ratio: ", ";\n  " + _emotionSourceMap2$q])), gridColSpan ? gridColSpan : 1, gridRowSpan ? gridRowSpan : 1, gridColStart ? gridColStart : 'auto', gridRowStart ? gridRowStart : 'auto', aspectRatio ? aspectRatio : 'auto');
  }
};
var templateObject_1$H, templateObject_2$q;

var Grid = function (_a) {
  var columnSize = _a.columnSize,
    rowSize = _a.rowSize,
    gap = _a.gap,
    maxWidth = _a.maxWidth,
    children = _a.children,
    height = _a.height,
    overflow = _a.overflow,
    _b = _a.isDense,
    isDense = _b === void 0 ? true : _b,
    animate = _a.animate,
    exit = _a.exit,
    initial = _a.initial;
  return jsx(motion.section, {
    animate: animate,
    exit: exit,
    initial: initial,
    css: styles$F.grid(gap, columnSize, rowSize, maxWidth, height, overflow, isDense)
  }, children);
};

var GridItem = function (_a) {
  var gridRowSpan = _a.gridRowSpan,
    gridColSpan = _a.gridColSpan,
    gridRowStart = _a.gridRowStart,
    gridColStart = _a.gridColStart,
    borderRadius = _a.borderRadius,
    aspectRatio = _a.aspectRatio,
    children = _a.children;
  return jsx(Div, {
    borderRadius: borderRadius ? borderRadius : 'none',
    css: styles$F.gridItem(gridRowSpan, gridColSpan, gridRowStart, gridColStart, aspectRatio)
  }, children);
};

var _emotionSourceMap2$p = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFJNkMiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgX19tYWtlVGVtcGxhdGVPYmplY3QgfSBmcm9tIFwidHNsaWJcIjtcclxuaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vY29yZSc7XHJcbmV4cG9ydCBkZWZhdWx0IHtcclxuICAgIHByb2dyZXNzQ29udGFpbmVyOiBjc3ModGVtcGxhdGVPYmplY3RfMSB8fCAodGVtcGxhdGVPYmplY3RfMSA9IF9fbWFrZVRlbXBsYXRlT2JqZWN0KFtcIlxcbiAgICBkaXNwbGF5OiBmbGV4O1xcbiAgICBhbGlnbi1pdGVtczogY2VudGVyO1xcbiAgICB3aWR0aDogMTAwJTtcXG4gIFwiXSwgW1wiXFxuICAgIGRpc3BsYXk6IGZsZXg7XFxuICAgIGFsaWduLWl0ZW1zOiBjZW50ZXI7XFxuICAgIHdpZHRoOiAxMDAlO1xcbiAgXCJdKSkpLFxyXG4gICAgcHJvZ3Jlc3M6IGZ1bmN0aW9uIChwZXJjZW50YWdlKSB7IHJldHVybiBjc3ModGVtcGxhdGVPYmplY3RfMiB8fCAodGVtcGxhdGVPYmplY3RfMiA9IF9fbWFrZVRlbXBsYXRlT2JqZWN0KFtcIlxcbiAgICBoZWlnaHQ6IDhweDtcXG4gICAgcG9zaXRpb246IHJlbGF0aXZlO1xcbiAgICB3aWR0aDogMTAwJTtcXG4gICAgbWFyZ2luLXJpZ2h0OiA4cHg7XFxuICAgID4gZGl2IHtcXG4gICAgICBiYWNrZ3JvdW5kOiBcIiwgXCI7XFxuICAgICAgd2lkdGg6IFwiLCBcIiU7XFxuICAgICAgaGVpZ2h0OiAxMDAlO1xcbiAgICAgIHBvc2l0aW9uOiByZWxhdGl2ZTtcXG4gICAgICBib3JkZXItcmFkaXVzOiBpbmhlcml0O1xcbiAgICB9XFxuICBcIl0sIFtcIlxcbiAgICBoZWlnaHQ6IDhweDtcXG4gICAgcG9zaXRpb246IHJlbGF0aXZlO1xcbiAgICB3aWR0aDogMTAwJTtcXG4gICAgbWFyZ2luLXJpZ2h0OiA4cHg7XFxuICAgID4gZGl2IHtcXG4gICAgICBiYWNrZ3JvdW5kOiBcIixcclxuICAgICAgICBcIjtcXG4gICAgICB3aWR0aDogXCIsIFwiJTtcXG4gICAgICBoZWlnaHQ6IDEwMCU7XFxuICAgICAgcG9zaXRpb246IHJlbGF0aXZlO1xcbiAgICAgIGJvcmRlci1yYWRpdXM6IGluaGVyaXQ7XFxuICAgIH1cXG4gIFwiXSkpLCBwZXJjZW50YWdlID09PSAxMDBcclxuICAgICAgICA/ICcjNGNkOTY0J1xyXG4gICAgICAgIDogJ2xpbmVhci1ncmFkaWVudCh0byByaWdodCwgIzRjZDk2NCwgIzVhYzhmYSwgIzAwN2FmZiwgIzM0YWFkYyknLCBwZXJjZW50YWdlKTsgfSxcclxufTtcclxudmFyIHRlbXBsYXRlT2JqZWN0XzEsIHRlbXBsYXRlT2JqZWN0XzI7XHJcbi8vIyBzb3VyY2VNYXBwaW5nVVJMPXN0eWxlcy5qcy5tYXAiXX0= */";
var _emotionSourceMap$G = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFHdUIiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgX19tYWtlVGVtcGxhdGVPYmplY3QgfSBmcm9tIFwidHNsaWJcIjtcclxuaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vY29yZSc7XHJcbmV4cG9ydCBkZWZhdWx0IHtcclxuICAgIHByb2dyZXNzQ29udGFpbmVyOiBjc3ModGVtcGxhdGVPYmplY3RfMSB8fCAodGVtcGxhdGVPYmplY3RfMSA9IF9fbWFrZVRlbXBsYXRlT2JqZWN0KFtcIlxcbiAgICBkaXNwbGF5OiBmbGV4O1xcbiAgICBhbGlnbi1pdGVtczogY2VudGVyO1xcbiAgICB3aWR0aDogMTAwJTtcXG4gIFwiXSwgW1wiXFxuICAgIGRpc3BsYXk6IGZsZXg7XFxuICAgIGFsaWduLWl0ZW1zOiBjZW50ZXI7XFxuICAgIHdpZHRoOiAxMDAlO1xcbiAgXCJdKSkpLFxyXG4gICAgcHJvZ3Jlc3M6IGZ1bmN0aW9uIChwZXJjZW50YWdlKSB7IHJldHVybiBjc3ModGVtcGxhdGVPYmplY3RfMiB8fCAodGVtcGxhdGVPYmplY3RfMiA9IF9fbWFrZVRlbXBsYXRlT2JqZWN0KFtcIlxcbiAgICBoZWlnaHQ6IDhweDtcXG4gICAgcG9zaXRpb246IHJlbGF0aXZlO1xcbiAgICB3aWR0aDogMTAwJTtcXG4gICAgbWFyZ2luLXJpZ2h0OiA4cHg7XFxuICAgID4gZGl2IHtcXG4gICAgICBiYWNrZ3JvdW5kOiBcIiwgXCI7XFxuICAgICAgd2lkdGg6IFwiLCBcIiU7XFxuICAgICAgaGVpZ2h0OiAxMDAlO1xcbiAgICAgIHBvc2l0aW9uOiByZWxhdGl2ZTtcXG4gICAgICBib3JkZXItcmFkaXVzOiBpbmhlcml0O1xcbiAgICB9XFxuICBcIl0sIFtcIlxcbiAgICBoZWlnaHQ6IDhweDtcXG4gICAgcG9zaXRpb246IHJlbGF0aXZlO1xcbiAgICB3aWR0aDogMTAwJTtcXG4gICAgbWFyZ2luLXJpZ2h0OiA4cHg7XFxuICAgID4gZGl2IHtcXG4gICAgICBiYWNrZ3JvdW5kOiBcIixcclxuICAgICAgICBcIjtcXG4gICAgICB3aWR0aDogXCIsIFwiJTtcXG4gICAgICBoZWlnaHQ6IDEwMCU7XFxuICAgICAgcG9zaXRpb246IHJlbGF0aXZlO1xcbiAgICAgIGJvcmRlci1yYWRpdXM6IGluaGVyaXQ7XFxuICAgIH1cXG4gIFwiXSkpLCBwZXJjZW50YWdlID09PSAxMDBcclxuICAgICAgICA/ICcjNGNkOTY0J1xyXG4gICAgICAgIDogJ2xpbmVhci1ncmFkaWVudCh0byByaWdodCwgIzRjZDk2NCwgIzVhYzhmYSwgIzAwN2FmZiwgIzM0YWFkYyknLCBwZXJjZW50YWdlKTsgfSxcclxufTtcclxudmFyIHRlbXBsYXRlT2JqZWN0XzEsIHRlbXBsYXRlT2JqZWN0XzI7XHJcbi8vIyBzb3VyY2VNYXBwaW5nVVJMPXN0eWxlcy5qcy5tYXAiXX0= */";
var styles$E = {
  progressContainer: /*#__PURE__*/css(templateObject_1$G || (templateObject_1$G = __makeTemplateObject(["\n    display: flex;\n    align-items: center;\n    width: 100%;\n  ;label:progressContainer;" + _emotionSourceMap$G], ["\n    display: flex;\n    align-items: center;\n    width: 100%;\n  ;label:progressContainer;" + _emotionSourceMap$G]))),
  progress: function (percentage) {
    return /*#__PURE__*/css(templateObject_2$p || (templateObject_2$p = __makeTemplateObject(["\n    height: 8px;\n    position: relative;\n    width: 100%;\n    margin-right: 8px;\n    > div {\n      background: ", ";\n      width: ", "%;\n      height: 100%;\n      position: relative;\n      border-radius: inherit;\n    }\n  " + _emotionSourceMap2$p], ["\n    height: 8px;\n    position: relative;\n    width: 100%;\n    margin-right: 8px;\n    > div {\n      background: ", ";\n      width: ", "%;\n      height: 100%;\n      position: relative;\n      border-radius: inherit;\n    }\n  " + _emotionSourceMap2$p])), percentage === 100 ? '#4cd964' : 'linear-gradient(to right, #4cd964, #5ac8fa, #007aff, #34aadc)', percentage);
  }
};
var templateObject_1$G, templateObject_2$p;

var Progress = function (_a) {
  var percentage = _a.percentage,
    _b = _a.hasLabel,
    hasLabel = _b === void 0 ? true : _b;
  return jsx(Div, {
    css: styles$E.progressContainer
  }, jsx(Div, {
    css: styles$E.progress(percentage),
    backgroundColor: {
      swatch: 'grayScale',
      shade: '_1000'
    },
    borderRadius: "circle"
  }, jsx(Div, null)), percentage === 100 ? jsx(Icon, {
    primaryColor: "primaryBodyText",
    name: "award",
    size: "small"
  }) : hasLabel && jsx(Text, {
    type: "body",
    color: "primaryBody"
  }, percentage, "%"));
};

/* Add more transitions here */
var springTransition = {
  type: 'spring',
  damping: 12,
  mass: 0.6,
  stiffness: 80,
  restSpeed: 0.01,
  restDelta: 0.01,
  duration: 0.3
};
var softSpringTransition = {
  damping: 15,
  stiffness: 100,
  type: 'spring'
};

var Toggle = function (_a) {
  var callBack = _a.callBack,
    defaultValue = _a.defaultValue;
  var _b = useState(defaultValue || false),
    isToggled = _b[0],
    setIsToggled = _b[1];
  var toggle = useEntities().toggle;
  var handleToggle = function () {
    setIsToggled(!isToggled);
    callBack(!isToggled);
  };
  var onColor = Color$1(toggle.isActiveColor).rgb().toString();
  var offColor = Color$1(toggle.isNotActiveColor).rgb().toString();
  var variants = {
    on: {
      backgroundColor: onColor
    },
    off: {
      backgroundColor: offColor
    }
  };
  return jsx(Div, {
    inset: "x_5",
    onClick: handleToggle,
    justifyContent: isToggled ? 'flex-end' : 'flex-start',
    width: "52px",
    height: "30px",
    borderRadius: "50px",
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    variants: variants,
    initial: isToggled ? 'on' : 'off',
    animate: isToggled ? 'on' : 'off'
  }, jsx(Div, {
    animate: true,
    layout: true,
    width: "24px",
    height: "24px",
    backgroundColor: "foreground",
    borderRadius: "circle",
    transition: springTransition
  }));
};

var Overlay = function (_a) {
  var children = _a.children,
    zIndex = _a.zIndex,
    animate = _a.animate,
    initial = _a.initial,
    exit = _a.exit,
    variants = _a.variants,
    transition = _a.transition,
    motionKey = _a.motionKey,
    position = _a.position,
    _b = _a.backgroundVariant,
    backgroundVariant = _b === void 0 ? 'solid' : _b,
    _c = _a.pinned,
    pinned = _c === void 0 ? 'center' : _c,
    shouldScroll = _a.shouldScroll,
    rest = __rest(_a, ["children", "zIndex", "animate", "initial", "exit", "variants", "transition", "motionKey", "position", "backgroundVariant", "pinned", "shouldScroll"]);
  var hocs = useEntities().hocs;
  var pinnedPosition = pinned === 'top' ? 'flex-start' : pinned === 'bottom' ? 'flex-end' : 'center';
  return jsx(Div, _extends({
    motionKey: motionKey,
    variants: variants,
    animate: animate,
    initial: initial,
    exit: exit,
    zIndex: zIndex,
    display: "flex",
    justifyContent: "center",
    alignItems: pinnedPosition,
    position: position !== null && position !== void 0 ? position : 'fixed',
    top: "0",
    left: "0",
    width: "100%",
    height: "100%",
    style: backgroundVariant === 'blur' ? {
      background: 'rgba(145, 145, 145, 0.1)',
      backdropFilter: 'blur(20px)'
    } : undefined,
    backgroundColor: backgroundVariant !== 'blur' ? Color$1(hocs.colors.swatches.grayScale._800).rgb().alpha(0.98).toString() : undefined,
    overflow: shouldScroll ? 'scroll' : 'hidden',
    transition: transition
  }, rest), children);
};

var Circle = function (_a) {
  var icon = _a.icon,
    fill = _a.fill,
    _b = _a.size,
    size = _b === void 0 ? 'small' : _b,
    scale = _a.scale,
    layer = _a.layer,
    onClick = _a.onClick,
    animate = _a.animate;
  var getInset = function (size) {
    if (size === 'large' || size === 'xLarge') {
      return 'x2';
    }
    return 'x1';
  };
  var hocs = useEntities().hocs;
  var circleStyles = {
    background: "linear-gradient(150deg, " + hocs.colors.swatches.grayScale._700 + ",\n      " + hocs.colors.swatches.grayScale._200 + "\n      )}"
  };
  return jsx(Div, {
    animate: animate,
    position: 'relative',
    inset: getInset(size),
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "circle",
    onClick: onClick && function () {
      return onClick();
    },
    cursor: "pointer",
    transform: "scale(" + scale + ")",
    zIndex: layer || 1,
    maxWidth: "fit-content",
    maxHeight: "fit-content",
    css: circleStyles
  }, jsx(Icon, {
    name: icon,
    primaryColor: fill,
    size: size
  }));
};

var ContextBar = function (_a) {
  var children = _a.children;
  return jsx(Div, {
    width: "100%",
    height: "48px",
    borderWidth: "thin",
    borderStyle: "solid",
    borderColor: {
      swatch: 'grayScale',
      shade: '_1000'
    },
    backgroundColor: "foreground",
    inset: {
      horizontal: 'x1'
    },
    zIndex: 8
  }, children);
};

var _emotionSourceMap$F = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFHWSIsImZpbGUiOiJzdHlsZXMudHMiLCJzb3VyY2VzQ29udGVudCI6WyJpbXBvcnQgeyBfX21ha2VUZW1wbGF0ZU9iamVjdCB9IGZyb20gXCJ0c2xpYlwiO1xyXG5pbXBvcnQgeyBjc3MgfSBmcm9tICdAZW1vdGlvbi9jb3JlJztcclxuZXhwb3J0IGRlZmF1bHQge1xyXG4gICAgdGFwQm94OiBjc3ModGVtcGxhdGVPYmplY3RfMSB8fCAodGVtcGxhdGVPYmplY3RfMSA9IF9fbWFrZVRlbXBsYXRlT2JqZWN0KFtcIlxcbiAgICBwb3NpdGlvbjogYWJzb2x1dGU7XFxuICAgIGRpc3BsYXk6IGZsZXg7XFxuICAgIGFsaWduLWl0ZW1zOiBjZW50ZXI7XFxuICAgIGxlZnQ6IDA7XFxuICAgIHotaW5kZXg6IDQ7XFxuICAgIHBhZGRpbmctbGVmdDogMTZweDtcXG4gICAgcGFkZGluZy1yaWdodDogNDRweDtcXG4gICAgaGVpZ2h0OiAxMjhweDtcXG4gICAgdG9wOiA1MCU7XFxuICBcIl0sIFtcIlxcbiAgICBwb3NpdGlvbjogYWJzb2x1dGU7XFxuICAgIGRpc3BsYXk6IGZsZXg7XFxuICAgIGFsaWduLWl0ZW1zOiBjZW50ZXI7XFxuICAgIGxlZnQ6IDA7XFxuICAgIHotaW5kZXg6IDQ7XFxuICAgIHBhZGRpbmctbGVmdDogMTZweDtcXG4gICAgcGFkZGluZy1yaWdodDogNDRweDtcXG4gICAgaGVpZ2h0OiAxMjhweDtcXG4gICAgdG9wOiA1MCU7XFxuICBcIl0pKSksXHJcbn07XHJcbnZhciB0ZW1wbGF0ZU9iamVjdF8xO1xyXG4vLyMgc291cmNlTWFwcGluZ1VSTD1zdHlsZXMuanMubWFwIl19 */";
var styles$D = {
  tapBox: /*#__PURE__*/css(templateObject_1$F || (templateObject_1$F = __makeTemplateObject(["\n    position: absolute;\n    display: flex;\n    align-items: center;\n    left: 0;\n    z-index: 4;\n    padding-left: 16px;\n    padding-right: 44px;\n    height: 128px;\n    top: 50%;\n  ;label:tapBox;" + _emotionSourceMap$F], ["\n    position: absolute;\n    display: flex;\n    align-items: center;\n    left: 0;\n    z-index: 4;\n    padding-left: 16px;\n    padding-right: 44px;\n    height: 128px;\n    top: 50%;\n  ;label:tapBox;" + _emotionSourceMap$F])))
};
var templateObject_1$F;

var DragBar = /*#__PURE__*/React.forwardRef(function (_a, ref) {
  var onDragEnd = _a.onDragEnd,
    dragConstraints = _a.dragConstraints,
    x = _a.x;
  return jsx(motion.div, {
    style: {
      x: x,
      translateY: '-50%'
    },
    drag: 'x',
    dragConstraints: ref || dragConstraints,
    onDragEnd: onDragEnd,
    css: styles$D.tapBox
  }, jsx(Bar, {
    height: "120px",
    width: "3px"
  }));
});

var _emotionSourceMap$E = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFHYSIsImZpbGUiOiJzdHlsZXMudHMiLCJzb3VyY2VzQ29udGVudCI6WyJpbXBvcnQgeyBfX21ha2VUZW1wbGF0ZU9iamVjdCB9IGZyb20gXCJ0c2xpYlwiO1xyXG5pbXBvcnQgeyBjc3MgfSBmcm9tICdAZW1vdGlvbi9jb3JlJztcclxuZXhwb3J0IGRlZmF1bHQge1xyXG4gICAgZHJhZ0JveDogY3NzKHRlbXBsYXRlT2JqZWN0XzEgfHwgKHRlbXBsYXRlT2JqZWN0XzEgPSBfX21ha2VUZW1wbGF0ZU9iamVjdChbXCJcXG4gICAgd2lkdGg6IDEwMHB4O1xcbiAgICBoZWlnaHQ6IDEwMHB4O1xcbiAgXCJdLCBbXCJcXG4gICAgd2lkdGg6IDEwMHB4O1xcbiAgICBoZWlnaHQ6IDEwMHB4O1xcbiAgXCJdKSkpLFxyXG59O1xyXG52YXIgdGVtcGxhdGVPYmplY3RfMTtcclxuLy8jIHNvdXJjZU1hcHBpbmdVUkw9c3R5bGVzLmpzLm1hcCJdfQ== */";
var styles$C = {
  dragBox: /*#__PURE__*/css(templateObject_1$E || (templateObject_1$E = __makeTemplateObject(["\n    width: 100px;\n    height: 100px;\n  ;label:dragBox;" + _emotionSourceMap$E], ["\n    width: 100px;\n    height: 100px;\n  ;label:dragBox;" + _emotionSourceMap$E])))
};
var templateObject_1$E;

var DragBox = /*#__PURE__*/React.forwardRef(function (_a, ref) {
  var onDragEnd = _a.onDragEnd,
    x = _a.x;
  return jsx(motion.div, {
    style: {
      x: x
    },
    drag: "x",
    dragConstraints: ref,
    onDragEnd: onDragEnd,
    css: styles$C.dragBox
  });
});

var FlexItem = function (props) {
  var entities = useEntities();
  var generatedDivStyles = generateStyles(props, entities);
  var ref = useRef(null);
  useOutsideClick(ref, function () {
    props.outsideClick && props.outsideClick();
  });
  return jsx(motion.section, _extends({}, props, {
    ref: ref,
    onClick: props.onMouseEvent ? function (e) {
      var _a;
      return (_a = props.onMouseEvent) === null || _a === void 0 ? void 0 : _a.call(props, e);
    } : function () {
      var _a;
      return (_a = props.onClick) === null || _a === void 0 ? void 0 : _a.call(props);
    },
    css: styles$L.flexItemStyles(generatedDivStyles),
    className: "" + (props.className ? props.className : '')
  }), props.children);
};

var _emotionSourceMap11 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap10 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap9 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap8 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap7$1 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap6$2 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap5$5 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap4$9 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap3$g = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap2$o = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap$D = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var styles$B = {
  sideNav: function (navigationEntity) {
    return /*#__PURE__*/css(templateObject_1$D || (templateObject_1$D = __makeTemplateObject(["\n    width: 50px;\n    height: 100%;\n    position: absolute;\n    left: 0;\n    top: 0%;\n    z-index: 5;\n    background-color: white;\n    background: ", ";\n    border: ", " solid ", ";\n  " + _emotionSourceMap$D], ["\n    width: 50px;\n    height: 100%;\n    position: absolute;\n    left: 0;\n    top: 0%;\n    z-index: 5;\n    background-color: white;\n    background: ", ";\n    border: ", " solid ", ";\n  " + _emotionSourceMap$D])), navigationEntity.backgroundColor, navigationEntity.border, navigationEntity.borderColor);
  },
  sideNavContent: /*#__PURE__*/css(templateObject_2$o || (templateObject_2$o = __makeTemplateObject(["\n    width: 100%;\n    height: 100%;\n    display: flex;\n    justify-content: space-between;\n    flex-direction: column;\n    overflow-y: auto;\n  ;label:sideNavContent;" + _emotionSourceMap2$o], ["\n    width: 100%;\n    height: 100%;\n    display: flex;\n    justify-content: space-between;\n    flex-direction: column;\n    overflow-y: auto;\n  ;label:sideNavContent;" + _emotionSourceMap2$o]))),
  logoContainer: /*#__PURE__*/css(templateObject_3$g || (templateObject_3$g = __makeTemplateObject(["\n    width: 100%;\n    display: flex;\n    justify-content: center;\n    align-items: center;\n    box-sizing: border-box;\n    flex-direction: column;\n  ;label:logoContainer;" + _emotionSourceMap3$g], ["\n    width: 100%;\n    display: flex;\n    justify-content: center;\n    align-items: center;\n    box-sizing: border-box;\n    flex-direction: column;\n  ;label:logoContainer;" + _emotionSourceMap3$g]))),
  contentContainer: /*#__PURE__*/css(templateObject_4$9 || (templateObject_4$9 = __makeTemplateObject(["\n    display: flex;\n    flex-direction: column;\n    overflow-y: auto;\n    height: calc(100% - 172px);\n    justify-content: center;\n  ;label:contentContainer;" + _emotionSourceMap4$9], ["\n    display: flex;\n    flex-direction: column;\n    overflow-y: auto;\n    height: calc(100% - 172px);\n    justify-content: center;\n  ;label:contentContainer;" + _emotionSourceMap4$9]))),
  separatorContainer: /*#__PURE__*/css(templateObject_5$5 || (templateObject_5$5 = __makeTemplateObject(["\n    padding: 0px 8px;\n  ;label:separatorContainer;" + _emotionSourceMap5$5], ["\n    padding: 0px 8px;\n  ;label:separatorContainer;" + _emotionSourceMap5$5]))),
  userContainer: /*#__PURE__*/css(templateObject_6$2 || (templateObject_6$2 = __makeTemplateObject(["\n    position: relative;\n    width: 100%;\n    height: 50px;\n    display: flex;\n    justify-content: space-between;\n    padding-left: 8px;\n    align-items: center;\n    box-sizing: border-box;\n    cursor: pointer;\n    z-index: 5;\n  ;label:userContainer;" + _emotionSourceMap6$2], ["\n    position: relative;\n    width: 100%;\n    height: 50px;\n    display: flex;\n    justify-content: space-between;\n    padding-left: 8px;\n    align-items: center;\n    box-sizing: border-box;\n    cursor: pointer;\n    z-index: 5;\n  ;label:userContainer;" + _emotionSourceMap6$2]))),
  toggleContainer: /*#__PURE__*/css(templateObject_7$1 || (templateObject_7$1 = __makeTemplateObject(["\n    padding: 8px;\n  ;label:toggleContainer;" + _emotionSourceMap7$1], ["\n    padding: 8px;\n  ;label:toggleContainer;" + _emotionSourceMap7$1]))),
  switchContainer: /*#__PURE__*/css(templateObject_8 || (templateObject_8 = __makeTemplateObject([";label:switchContainer;" + _emotionSourceMap8], [";label:switchContainer;" + _emotionSourceMap8]))),
  linkContainer: /*#__PURE__*/css(templateObject_9 || (templateObject_9 = __makeTemplateObject([";label:linkContainer;" + _emotionSourceMap9], [";label:linkContainer;" + _emotionSourceMap9]))),
  link: /*#__PURE__*/css(templateObject_10 || (templateObject_10 = __makeTemplateObject(["\n    &:hover {\n      ~ div {\n        opacity: 1;\n      }\n    }\n  ;label:link;" + _emotionSourceMap10], ["\n    &:hover {\n      ~ div {\n        opacity: 1;\n      }\n    }\n  ;label:link;" + _emotionSourceMap10]))),
  tooltipContainer: /*#__PURE__*/css(templateObject_11 || (templateObject_11 = __makeTemplateObject(["\n    opacity: 0;\n    display: flex;\n    transition: 0.3s;\n    position: absolute;\n    left: 115%;\n    width: auto;\n    justify-content: center;\n    align-items: center;\n  ;label:tooltipContainer;" + _emotionSourceMap11], ["\n    opacity: 0;\n    display: flex;\n    transition: 0.3s;\n    position: absolute;\n    left: 115%;\n    width: auto;\n    justify-content: center;\n    align-items: center;\n  ;label:tooltipContainer;" + _emotionSourceMap11])))
};
var templateObject_1$D, templateObject_2$o, templateObject_3$g, templateObject_4$9, templateObject_5$5, templateObject_6$2, templateObject_7$1, templateObject_8, templateObject_9, templateObject_10, templateObject_11;

var Navigation = function (_a) {
  var navLinks = _a.navLinks,
    logo = _a.logo,
    user = _a.user,
    setMenuOpen = _a.setMenuOpen,
    isMenuOpen = _a.isMenuOpen,
    isExpanded = _a.isExpanded,
    setIsExpanded = _a.setIsExpanded;
  var navigation = useEntities().navigation;
  var _b = useState(-1),
    hoverIndex = _b[0],
    setHoverIndex = _b[1];
  var tooltipVariants = {
    hoverOn: {
      opacity: 1
    },
    hoverOff: {
      opacity: 0
    }
  };
  return jsx("aside", {
    css: styles$B.sideNav(navigation)
  }, jsx(Div, {
    css: styles$B.sideNavContent
  }, jsx(Div, {
    height: "100%"
  }, jsx(Div, {
    css: styles$B.logoContainer,
    inset: {
      vertical: 'x3'
    }
  }, jsx(Span, null, jsx(Icon, {
    name: logo,
    size: "medium",
    primaryColor: "default"
  })), jsx(Flex, {
    cursor: "pointer",
    justifyContent: "center",
    alignItems: "center",
    outset: {
      top: 'x2'
    },
    onClick: function () {
      return setIsExpanded(!isExpanded);
    }
  }, isExpanded && jsx(Span, {
    position: "absolute",
    left: "2px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  }, jsx(Icon, {
    name: "chevron",
    primaryColor: "default",
    size: "xSmall",
    orientation: "left"
  })), jsx(Icon, {
    name: "hamburger",
    primaryColor: "default",
    size: "small"
  }), ' ')), jsx(Div, {
    css: styles$B.contentContainer
  }, jsx(Flex, {
    flexDirection: 'column',
    alignItems: 'center',
    width: "100%"
  }, navLinks.map(function (item, index) {
    return jsx(FlexItem, {
      key: index,
      inset: {
        vertical: 'x2'
      },
      alignItems: 'center',
      justifyContent: 'center',
      css: styles$B.linkContainer
    }, jsx(motion.div, {
      onHoverStart: function () {
        return setHoverIndex(index + 1);
      },
      onHoverEnd: function () {
        return setHoverIndex(-1);
      },
      css: styles$B.link
    }, jsx(IconLink, {
      icon: item.icon,
      iconOrientation: item.iconOrientation ? item.iconOrientation : 'left',
      fill: "default",
      onClick: function () {
        item.onClick();
      },
      isActive: item.isActive
    })), hoverIndex === index + 1 && jsx(motion.div, {
      variants: tooltipVariants,
      animate: hoverIndex ? 'hoverOn' : 'hoverOff',
      initial: 'hoverOff',
      transition: {
        ease: 'easeIn',
        duration: 0.2
      },
      css: styles$B.tooltipContainer
    }, jsx(Tooltip, {
      direction: "left",
      title: item.title,
      background: "light"
    })));
  })))), jsx(Div, {
    css: styles$B.switchContainer
  }, jsx(Div, {
    id: "accountMenuClickEvent",
    onClick: function () {
      return setMenuOpen(!isMenuOpen);
    },
    css: styles$B.userContainer,
    backgroundColor: {
      swatch: 'grayScale',
      shade: '_1000'
    }
  }, jsx(Avatar, {
    size: 'xSmall',
    avatarImage: user.profileImage,
    isActive: true
  })))));
};

var QuickLink = function (_a) {
  var link = _a.link;
  return jsx(Card, null, link.title);
};

var _emotionSourceMap4$8 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFja0IiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgX19tYWtlVGVtcGxhdGVPYmplY3QgfSBmcm9tIFwidHNsaWJcIjtcclxuaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vY29yZSc7XHJcbmltcG9ydCB7IGVudGl0aWVzIH0gZnJvbSBcIi4uLy4uL3RoZW1lL3RoZW1pbmcvZW50aXRpZXNcIjtcclxudmFyIG1lZGlhUXVlcmllcyA9IGVudGl0aWVzLm1lZGlhUXVlcmllcztcclxuZXhwb3J0IGRlZmF1bHQge1xyXG4gICAgc2hlZXRXcmFwcGVyOiBmdW5jdGlvbiAobW9iaWxlLCBsYXllcikgeyByZXR1cm4gY3NzKHRlbXBsYXRlT2JqZWN0XzEgfHwgKHRlbXBsYXRlT2JqZWN0XzEgPSBfX21ha2VUZW1wbGF0ZU9iamVjdChbXCJcXG4gICAgcG9zaXRpb246IFwiLCBcIjtcXG4gICAgYm90dG9tOiAwO1xcbiAgICBsZWZ0OiAwO1xcbiAgICB6LWluZGV4OiBcIiwgXCI7XFxuICBcIl0sIFtcIlxcbiAgICBwb3NpdGlvbjogXCIsIFwiO1xcbiAgICBib3R0b206IDA7XFxuICAgIGxlZnQ6IDA7XFxuICAgIHotaW5kZXg6IFwiLCBcIjtcXG4gIFwiXSkpLCBtb2JpbGUgPyAnYWJzb2x1dGUnIDogJ3JlbGF0aXZlJywgbGF5ZXIpOyB9LFxyXG4gICAgc2hlZXQ6IGZ1bmN0aW9uIChzaGVldEVudGl0eSwgaGFzQmFja2dyb3VuZEVsZW1lbnQsIGlzTW9iaWxlLCBiYWNrZ3JvdW5kQ29sb3IpIHsgcmV0dXJuIGNzcyh0ZW1wbGF0ZU9iamVjdF8yIHx8ICh0ZW1wbGF0ZU9iamVjdF8yID0gX19tYWtlVGVtcGxhdGVPYmplY3QoW1wiXFxuICAgIHdpZHRoOiAxMDAlO1xcbiAgICBkaXNwbGF5OiBmbGV4O1xcbiAgICBiYWNrZ3JvdW5kLWNvbG9yOiBcIiwgXCI7XFxuICAgIHBsYWNlLWl0ZW1zOiBzdGFydCBjZW50ZXI7XFxuICAgIHBsYWNlLWNvbnRlbnQ6IHN0YXJ0IGNlbnRlcjtcXG4gICAgZmxleC1kaXJlY3Rpb246IGNvbHVtbjtcXG4gICAgYm9yZGVyLXJhZGl1czogXCIsIFwiO1xcbiAgICBib3gtc2hhZG93OiBcIiwgXCI7XFxuICAgIHBhZGRpbmc6IDQ4cHggMzJweCAzMnB4IDMycHg7XFxuICAgIHBvc2l0aW9uOiBcIiwgXCI7XFxuICAgIGJvdHRvbTogXCIsIFwiO1xcbiAgICB6LWluZGV4OiAzO1xcbiAgICBvcmRlcjogMjtcXG4gICAgQG1lZGlhIHNjcmVlbiBhbmQgKG1pbi13aWR0aDogXCIsIFwiKSB7XFxuICAgICAgYm9yZGVyLXJhZGl1czogXCIsIFwiO1xcbiAgICB9XFxuICAgIEBtZWRpYSBzY3JlZW4gYW5kIChtaW4td2lkdGg6IFwiLCBcIikge1xcbiAgICAgIHdpZHRoOiBcIiwgXCI7XFxuICAgICAgb3JkZXI6IDE7XFxuICAgICAgYm9yZGVyLXJhZGl1czogXCIsIFwiO1xcbiAgICB9XFxuICBcIl0sIFtcIlxcbiAgICB3aWR0aDogMTAwJTtcXG4gICAgZGlzcGxheTogZmxleDtcXG4gICAgYmFja2dyb3VuZC1jb2xvcjogXCIsIFwiO1xcbiAgICBwbGFjZS1pdGVtczogc3RhcnQgY2VudGVyO1xcbiAgICBwbGFjZS1jb250ZW50OiBzdGFydCBjZW50ZXI7XFxuICAgIGZsZXgtZGlyZWN0aW9uOiBjb2x1bW47XFxuICAgIGJvcmRlci1yYWRpdXM6IFwiLCBcIjtcXG4gICAgYm94LXNoYWRvdzogXCIsIFwiO1xcbiAgICBwYWRkaW5nOiA0OHB4IDMycHggMzJweCAzMnB4O1xcbiAgICBwb3NpdGlvbjogXCIsIFwiO1xcbiAgICBib3R0b206IFwiLCBcIjtcXG4gICAgei1pbmRleDogMztcXG4gICAgb3JkZXI6IDI7XFxuICAgIEBtZWRpYSBzY3JlZW4gYW5kIChtaW4td2lkdGg6IFwiLCBcIikge1xcbiAgICAgIGJvcmRlci1yYWRpdXM6IFwiLFxyXG4gICAgICAgIFwiO1xcbiAgICB9XFxuICAgIEBtZWRpYSBzY3JlZW4gYW5kIChtaW4td2lkdGg6IFwiLCBcIikge1xcbiAgICAgIHdpZHRoOiBcIiwgXCI7XFxuICAgICAgb3JkZXI6IDE7XFxuICAgICAgYm9yZGVyLXJhZGl1czogXCIsXHJcbiAgICAgICAgXCI7XFxuICAgIH1cXG4gIFwiXSkpLCBiYWNrZ3JvdW5kQ29sb3IgfHwgJ3doaXRlJywgc2hlZXRFbnRpdHkubW9iaWxlQm9yZGVyUmFkaXVzICsgXCIgXCIgKyBzaGVldEVudGl0eS5tb2JpbGVCb3JkZXJSYWRpdXMgKyBcIiAwIDBcIiwgc2hlZXRFbnRpdHkuYm94U2hhZG93LCBpc01vYmlsZSA/ICdhYnNvbHV0ZScgOiAncmVsYXRpdmUnLCBpc01vYmlsZSAmJiAwLCBtZWRpYVF1ZXJpZXMucmF3VmFsdWVzLnNtYWxsICsgXCJweFwiLCBoYXNCYWNrZ3JvdW5kRWxlbWVudFxyXG4gICAgICAgID8gc2hlZXRFbnRpdHkuZGVza3RvcEJvcmRlclJhZGl1cyArIFwiIFwiICsgc2hlZXRFbnRpdHkuZGVza3RvcEJvcmRlclJhZGl1cyArIFwiIDAgMCBcIlxyXG4gICAgICAgIDogXCJcIiArIHNoZWV0RW50aXR5LmRlc2t0b3BCb3JkZXJSYWRpdXMsIG1lZGlhUXVlcmllcy5yYXdWYWx1ZXMubWVkaXVtICsgXCJweFwiLCBoYXNCYWNrZ3JvdW5kRWxlbWVudCA/ICc1MCUnIDogJzEwMCUnLCBoYXNCYWNrZ3JvdW5kRWxlbWVudFxyXG4gICAgICAgID8gc2hlZXRFbnRpdHkuZGVza3RvcEJvcmRlclJhZGl1cyArIFwiIDAgMCBcIiArIHNoZWV0RW50aXR5LmRlc2t0b3BCb3JkZXJSYWRpdXNcclxuICAgICAgICA6IFwiXCIgKyBzaGVldEVudGl0eS5kZXNrdG9wQm9yZGVyUmFkaXVzKTsgfSxcclxuICAgIGRyYWdCYXI6IGNzcyh0ZW1wbGF0ZU9iamVjdF8zIHx8ICh0ZW1wbGF0ZU9iamVjdF8zID0gX19tYWtlVGVtcGxhdGVPYmplY3QoW1wiXFxuICAgIHRyYW5zZm9ybTogdHJhbnNsYXRlWCgtNTAlKTtcXG4gIFwiXSwgW1wiXFxuICAgIHRyYW5zZm9ybTogdHJhbnNsYXRlWCgtNTAlKTtcXG4gIFwiXSkpKSxcclxuICAgIGNoaWxkV3JhcHBlcjogY3NzKHRlbXBsYXRlT2JqZWN0XzQgfHwgKHRlbXBsYXRlT2JqZWN0XzQgPSBfX21ha2VUZW1wbGF0ZU9iamVjdChbXCJcXG4gICAgOjotd2Via2l0LXNjcm9sbGJhciB7XFxuICAgICAgd2lkdGg6IDBweDtcXG4gICAgICBiYWNrZ3JvdW5kOiB0cmFuc3BhcmVudDsgLyogbWFrZSBzY3JvbGxiYXIgdHJhbnNwYXJlbnQgKi9cXG4gICAgfVxcbiAgXCJdLCBbXCJcXG4gICAgOjotd2Via2l0LXNjcm9sbGJhciB7XFxuICAgICAgd2lkdGg6IDBweDtcXG4gICAgICBiYWNrZ3JvdW5kOiB0cmFuc3BhcmVudDsgLyogbWFrZSBzY3JvbGxiYXIgdHJhbnNwYXJlbnQgKi9cXG4gICAgfVxcbiAgXCJdKSkpLFxyXG59O1xyXG52YXIgdGVtcGxhdGVPYmplY3RfMSwgdGVtcGxhdGVPYmplY3RfMiwgdGVtcGxhdGVPYmplY3RfMywgdGVtcGxhdGVPYmplY3RfNDtcclxuLy8jIHNvdXJjZU1hcHBpbmdVUkw9c3R5bGVzLmpzLm1hcCJdfQ== */";
var _emotionSourceMap3$f = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap2$n = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap$C = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var mediaQueries$1 = entities$9.mediaQueries;
var styles$A = {
  sheetWrapper: function (mobile, layer) {
    return /*#__PURE__*/css(templateObject_1$C || (templateObject_1$C = __makeTemplateObject(["\n    position: ", ";\n    bottom: 0;\n    left: 0;\n    z-index: ", ";\n  " + _emotionSourceMap$C], ["\n    position: ", ";\n    bottom: 0;\n    left: 0;\n    z-index: ", ";\n  " + _emotionSourceMap$C])), mobile ? 'absolute' : 'relative', layer);
  },
  sheet: function (sheetEntity, hasBackgroundElement, isMobile, backgroundColor) {
    return /*#__PURE__*/css(templateObject_2$n || (templateObject_2$n = __makeTemplateObject(["\n    width: 100%;\n    display: flex;\n    background-color: ", ";\n    place-items: start center;\n    place-content: start center;\n    flex-direction: column;\n    border-radius: ", ";\n    box-shadow: ", ";\n    padding: 48px 32px 32px 32px;\n    position: ", ";\n    bottom: ", ";\n    z-index: 3;\n    order: 2;\n    @media screen and (min-width: ", ") {\n      border-radius: ", ";\n    }\n    @media screen and (min-width: ", ") {\n      width: ", ";\n      order: 1;\n      border-radius: ", ";\n    }\n  " + _emotionSourceMap2$n], ["\n    width: 100%;\n    display: flex;\n    background-color: ", ";\n    place-items: start center;\n    place-content: start center;\n    flex-direction: column;\n    border-radius: ", ";\n    box-shadow: ", ";\n    padding: 48px 32px 32px 32px;\n    position: ", ";\n    bottom: ", ";\n    z-index: 3;\n    order: 2;\n    @media screen and (min-width: ", ") {\n      border-radius: ", ";\n    }\n    @media screen and (min-width: ", ") {\n      width: ", ";\n      order: 1;\n      border-radius: ", ";\n    }\n  " + _emotionSourceMap2$n])), backgroundColor || 'white', sheetEntity.mobileBorderRadius + " " + sheetEntity.mobileBorderRadius + " 0 0", sheetEntity.boxShadow, isMobile ? 'absolute' : 'relative', isMobile && 0, mediaQueries$1.rawValues.small + "px", hasBackgroundElement ? sheetEntity.desktopBorderRadius + " " + sheetEntity.desktopBorderRadius + " 0 0 " : "" + sheetEntity.desktopBorderRadius, mediaQueries$1.rawValues.medium + "px", hasBackgroundElement ? '50%' : '100%', hasBackgroundElement ? sheetEntity.desktopBorderRadius + " 0 0 " + sheetEntity.desktopBorderRadius : "" + sheetEntity.desktopBorderRadius);
  },
  dragBar: /*#__PURE__*/css(templateObject_3$f || (templateObject_3$f = __makeTemplateObject(["\n    transform: translateX(-50%);\n  ;label:dragBar;" + _emotionSourceMap3$f], ["\n    transform: translateX(-50%);\n  ;label:dragBar;" + _emotionSourceMap3$f]))),
  childWrapper: /*#__PURE__*/css(templateObject_4$8 || (templateObject_4$8 = __makeTemplateObject(["\n    ::-webkit-scrollbar {\n      width: 0px;\n      background: transparent; /* make scrollbar transparent */\n    }\n  ;label:childWrapper;" + _emotionSourceMap4$8], ["\n    ::-webkit-scrollbar {\n      width: 0px;\n      background: transparent; /* make scrollbar transparent */\n    }\n  ;label:childWrapper;" + _emotionSourceMap4$8])))
};
var templateObject_1$C, templateObject_2$n, templateObject_3$f, templateObject_4$8;

var deviceSize = function (currentWidth, querySizes) {
  if (currentWidth < querySizes.rawValues.small) {
    return 'xSmall';
  } else if (currentWidth < querySizes.rawValues.medium) {
    return 'small';
  } else if (currentWidth < querySizes.rawValues.large) {
    return 'medium';
  } else if (currentWidth < querySizes.rawValues.xLarge) {
    return 'large';
  } else if (currentWidth > querySizes.rawValues.xLarge) {
    return 'xLarge';
  } else {
    return "Couldn't find device";
  }
};
var useDeviceSize = function () {
  var innerWidth = useWindowSize().width;
  var mediaQueries = useEntities().mediaQueries;
  var device = deviceSize(innerWidth, mediaQueries);
  return {
    device: device
  };
};

var Sheet = function (_a) {
  var _b, _c;
  var isOpen = _a.isOpen,
    children = _a.children,
    draggable = _a.draggable,
    backgroundColor = _a.backgroundColor,
    layer = _a.layer,
    setIsOpen = _a.setIsOpen,
    backgroundElement = _a.backgroundElement,
    backgroundElementBackgroundColor = _a.backgroundElementBackgroundColor,
    snapPointPercentages = _a.snapPointPercentages,
    initialSnapPoint = _a.initialSnapPoint,
    outsideClickBypassId = _a.outsideClickBypassId,
    zIndex = _a.zIndex,
    bgElementSelector = _a.bgElementSelector,
    _d = _a.hasOverlay,
    hasOverlay = _d === void 0 ? true : _d,
    snapTo = _a.snapTo,
    setSnapTo = _a.setSnapTo,
    width = _a.width,
    inset = _a.inset,
    outset = _a.outset;
  // TODO's
  /**
   * 1. Hook up child scrolling when scrolling is enabled
   * 2. background component is off when the sheet exceeds the window and stops at the top
   */
  var _e = useState([]),
    snapPoints = _e[0],
    setSnapPoints = _e[1];
  var _f = useState(false),
    setIsScrollingEnabled = _f[1];
  var targetRef = useRef(null);
  var _g = useState(((_b = targetRef.current) === null || _b === void 0 ? void 0 : _b.offsetHeight) || 0),
    sheetHeight = _g[0],
    setSheetHeight = _g[1];
  var device = useDeviceSize().device;
  var isMobile = device === 'small' || device === 'xSmall' ? true : false;
  var sheet = useEntities().sheet;
  var windowHeight = useWindowSize().height;
  var isSheetLargerThanWindow = windowHeight - sheetHeight <= 0;
  var desktopVariants = {
    visible: {
      scale: 1,
      opacity: 1,
      y: 0
    },
    hidden: {
      scale: 0.1,
      opacity: 0,
      y: 0
    }
  };
  var overlayVariants = {
    visible: {
      opacity: 1
    },
    hidden: {
      opacity: 0,
      transition: {
        delay: isMobile ? 0.3 : 0
      }
    }
  };
  /**
   * Takes the current position of the sheet and determines if the sheet is larger than the
   * window size. If it is larger than we reset the position to be the difference of the windowHeight
   * and sheetHeight (sheet consumes full screen but stops at top), otherwise the sheet can be expanded
   * to it's full height
   * @params position - a number represented the current position of the sheet
   * @return the new position of the sheet
   */
  var getMaxOpenPoint = useCallback(function (position) {
    var diff = windowHeight - sheetHeight;
    if (diff >= 0) {
      return position;
    }
    if (position < Math.abs(diff)) {
      return Math.abs(diff);
    } else {
      return position;
    }
  }, [sheetHeight, windowHeight]);
  var getPositionFromPercentile = useCallback(function (percentile) {
    if (percentile > 100) {
      percentile = 100;
    }
    var percentileToPixel = sheetHeight * (percentile / 100);
    var newPoint = sheetHeight - percentileToPixel;
    return getMaxOpenPoint(newPoint);
  }, [sheetHeight, getMaxOpenPoint]);
  var maxOpenPoint = windowHeight - sheetHeight < 0 ? Math.abs(windowHeight - sheetHeight) : 0;
  var getInitialSnapPoint = function (snapPoint) {
    return typeof snapPoint === 'string' ? initialSnapPointMatch !== undefined ? getPositionFromPercentile(initialSnapPointMatch.percentage) : maxOpenPoint : typeof snapPoint === 'number' ? snapPointPercentages ? getPositionFromPercentile(snapPointPercentages[snapPoint]) : maxOpenPoint : maxOpenPoint;
  };
  var initialSnapPointMatch = snapPoints.find(function (snapPoint) {
    return snapPoint.name === snapTo ? snapTo : initialSnapPoint;
  });
  var initialY = snapTo ? getInitialSnapPoint(snapTo) : getInitialSnapPoint(initialSnapPoint); // TODO remove ternary when initialSnapPoint gets removed
  // Motion value for y positioning of the sheet
  var y = useMotionValue(isOpen ? maxOpenPoint : windowHeight);
  // Ranges for the background to specify when the animation start/stop while user is dragging.
  var backgroundInput = [initialY, sheetHeight];
  var backgroundYOutput = [0, sheetHeight];
  // Syncs up the background y position with the drag/spring motion values
  var backgroundY = useTransform(y, backgroundInput, backgroundYOutput);
  // Syncs up the background opacity values with the drag/spring motion values
  var backgroundOpacity = useTransform(y, backgroundInput, [1, 0]);
  // Finds the closest snap point
  var getClosest = function (values, targetValue) {
    return values.reduce(function (previous, current) {
      return Math.abs(previous - targetValue) < Math.abs(current - targetValue) ? previous : current;
    });
  };
  var isHtmlElement = function (element) {
    return element instanceof HTMLElement;
  };
  useLayoutEffect(function () {
    var _a;
    if (targetRef.current && sheetHeight !== ((_a = targetRef.current) === null || _a === void 0 ? void 0 : _a.offsetHeight)) {
      setSheetHeight(targetRef.current.offsetHeight);
    }
    if (!targetRef.current) {
      return;
    }
    var sheetRef = targetRef.current;
    var newSnapPoints = Array.from(sheetRef.getElementsByClassName('snapPoint')).filter(isHtmlElement).map(function (snapPoint) {
      return {
        percentage: snapPoint.offsetTop / sheetRef.offsetHeight * 100,
        name: snapPoint.getAttribute('data-name')
      };
    });
    if (!fastDeepEqual(newSnapPoints, snapPoints)) {
      setSnapPoints(newSnapPoints);
    }
  }, [sheetHeight, snapPoints, backgroundElement, children]);
  useEffect(function () {
    if (isMobile) {
      y.set(0);
    }
  }, [y, isMobile]);
  var handleDrag = useCallback(function (_, _a) {
    var delta = _a.delta;
    y.set(Math.max(y.get() + delta.y, maxOpenPoint));
  }, [y, maxOpenPoint]);
  var handleSetSnapTo = useCallback(function (snapToPoint, sheetHeight) {
    var _a;
    var pixelPoint = sheetHeight - snapToPoint;
    var percentagePoint = pixelPoint / sheetHeight * 100;
    var matchedSnapPointKey = snapPoints.find(function (snapPoint) {
      return snapPoint.percentage === percentagePoint;
    });
    // If there is a snapPoint key, will return the key name otherwise percentage
    var snapTo = (_a = matchedSnapPointKey === null || matchedSnapPointKey === void 0 ? void 0 : matchedSnapPointKey.name) !== null && _a !== void 0 ? _a : percentagePoint;
    setSnapTo === null || setSnapTo === void 0 ? void 0 : setSnapTo(snapTo);
  }, [setSnapTo, snapPoints]);
  var handleDragEnd = useCallback(function (_, _a) {
    var velocity = _a.velocity;
    if (velocity.y > 500) {
      setIsOpen === null || setIsOpen === void 0 ? void 0 : setIsOpen(false);
    } else {
      var defaultSnapPoints = snapPointPercentages ? __spreadArrays([0], snapPointPercentages, [100]) : [0, 100];
      var snapTo_1 = snapPoints ? getClosest(__spreadArrays(defaultSnapPoints, snapPoints.map(function (point) {
        return point.percentage;
      })).map(function (pointPercentile) {
        return getPositionFromPercentile(pointPercentile);
      }), y.get()) : y.get() / sheetHeight > 0.4 ? sheetHeight : maxOpenPoint;
      handleSetSnapTo(snapTo_1, sheetHeight);
      animate(y, snapTo_1, {
        type: 'spring',
        stiffness: 300,
        damping: 30,
        mass: 0.2
      });
      if (snapTo_1 >= sheetHeight) {
        setIsOpen === null || setIsOpen === void 0 ? void 0 : setIsOpen(false);
      }
      var diff = windowHeight - sheetHeight;
      if (diff < 0 && snapTo_1 <= Math.abs(diff)) {
        setIsScrollingEnabled(true);
      } else {
        setIsScrollingEnabled(false);
      }
    }
  }, [windowHeight, sheetHeight, getPositionFromPercentile, setIsOpen, snapPointPercentages, snapPoints, y, maxOpenPoint, handleSetSnapTo]);
  var handleSnapTo = useCallback(function (snapPoint) {
    var _a;
    if (typeof snapPoint === 'string') {
      var snapPointMatch = snapPoints.find(function (snap) {
        return snap.name === snapPoint;
      });
      snapPoint = (_a = snapPointMatch === null || snapPointMatch === void 0 ? void 0 : snapPointMatch.percentage) !== null && _a !== void 0 ? _a : -1;
    }
    if (snapPoint >= 0) {
      animate(y, getPositionFromPercentile(snapPoint), {
        type: 'spring',
        stiffness: 300,
        damping: 30,
        mass: 0.2
      });
    }
  }, [getPositionFromPercentile, y, snapPoints]);
  useEffect(function () {
    if (snapTo) handleSnapTo(snapTo);
  }, [snapTo]); //eslint-disable-line
  var withoutOverlayStyle = {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    minWidth: !isMobile ? '768px' : undefined
  };
  var withOverlayStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'fixed',
    top: '0',
    left: '0',
    bottom: '0',
    right: '0'
  };
  return jsx(AnimatePresence, null, isOpen && jsx(Div, {
    style: __assign({
      zIndex: zIndex !== null && zIndex !== void 0 ? zIndex : 4
    }, isMobile ? {} : hasOverlay ? __assign({}, withOverlayStyle) : __assign({}, withoutOverlayStyle))
  }, hasOverlay && jsx(Overlay, {
    motionKey: "sheetOverlay",
    variants: overlayVariants,
    animate: isOpen ? 'visible' : 'hidden',
    initial: isMobile ? 'visible' : 'hidden',
    exit: 'hidden',
    zIndex: zIndex !== null && zIndex !== void 0 ? zIndex : 4,
    bgElementSelector: bgElementSelector
  }), jsx(Div, {
    id: "sheetWrapper",
    width: {
      xSmall: width !== null && width !== void 0 ? width : '100%',
      medium: width !== null && width !== void 0 ? width : 'calc(100% - 32px)'
    },
    maxWidth: backgroundElement ? '1026px' : '768px',
    minWidth: "320px",
    variants: desktopVariants,
    outsideClick: function () {
      return setIsOpen === null || setIsOpen === void 0 ? void 0 : setIsOpen(false);
    },
    clickElementBypass: outsideClickBypassId,
    css: styles$A.sheetWrapper(isMobile, (_c = layer !== null && layer !== void 0 ? layer : zIndex) !== null && _c !== void 0 ? _c : 4),
    display: "flex",
    flexDirection: {
      xSmall: 'column',
      medium: 'row'
    },
    inset: inset,
    outset: outset
  }, !isMobile && !!setIsOpen && jsx(CloseButton, {
    onClose: function () {
      return setIsOpen(false);
    }
  }), backgroundElement && jsx(Div, {
    display: "flex",
    justifyContent: "center",
    order: isMobile ? 1 : 2,
    width: {
      xSmall: '100%',
      medium: '50%'
    },
    height: isMobile ? "100vh" : 'unset',
    zIndex: 1,
    position: isMobile ? 'absolute' : 'relative',
    bottom: isMobile ? '0' : undefined,
    backgroundColor: backgroundElementBackgroundColor && isMobile ? backgroundElementBackgroundColor : 'transparent'
  }, jsx(Div, {
    overflow: 'hidden',
    borderRadius: {
      top: {
        small: 'default',
        medium: 'none'
      },
      right: {
        medium: 'default'
      }
    },
    height: {
      xSmall: "calc(100vh - (" + (sheetHeight - initialY) + "px) + 48px)",
      medium: 'unset'
    },
    maxHeight: {
      medium: sheetHeight + "px"
    },
    position: "relative",
    zIndex: 2,
    width: "100%",
    style: {
      y: isMobile ? backgroundY : maxOpenPoint,
      opacity: isMobile ? backgroundOpacity : 1
    }
  }, backgroundElement)), isMobile ? jsx(motion.div, {
    ref: targetRef,
    key: "sheet",
    css: styles$A.sheet(sheet, !!backgroundElement, isMobile, backgroundColor),
    transition: softSpringTransition,
    drag: draggable && 'y',
    dragConstraints: {
      top: 0,
      bottom: sheetHeight
    },
    onDrag: handleDrag,
    onDragEnd: handleDragEnd,
    dragPropagation: true,
    style: {
      y: y
    },
    initial: {
      y: windowHeight
    },
    animate: {
      y: initialY,
      transition: {
        type: 'tween'
      }
    },
    exit: {
      y: sheetHeight
    },
    dragElastic: 0.01
  }, jsx(Div, {
    css: styles$A.dragBar,
    position: "absolute",
    top: "x2",
    left: "50%",
    width: "50px",
    height: "4px",
    borderRadius: "large",
    backgroundColor: {
      swatch: 'grayScale',
      shade: '_1000'
    }
  }), jsx(Div, {
    height: isSheetLargerThanWindow ? windowHeight + "px" : 'unset',
    overflowY: isSheetLargerThanWindow ? 'hidden' : undefined,
    width: "100%"
  }, jsx(Div, {
    height: isSheetLargerThanWindow ? windowHeight - maxOpenPoint + "px" : 'unset',
    overflowY: isSheetLargerThanWindow ? 'auto' : undefined,
    css: styles$A.childWrapper,
    width: "100%"
  }, children))) : jsx("div", {
    ref: targetRef,
    css: styles$A.sheet(sheet, !!backgroundElement, isMobile, backgroundColor)
  }, children))));
};

var SnapPoint = function (_a) {
  var name = _a.name;
  return jsx("div", {
    className: "snapPoint",
    "data-name": name
  });
};

var Todo = function (_a) {
  var todo = _a.todo;
  return jsx(Div, {
    width: "100%",
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center"
  }, jsx(Div, {
    borderRadius: "circle",
    width: "12px",
    height: "12px",
    backgroundColor: {
      swatch: 'secondaryBrand',
      shade: 'base'
    },
    outset: {
      right: 'x1'
    }
  }), jsx(Text, {
    isItalic: true,
    type: "caption",
    color: "primaryBody",
    content: todo
  }));
};

var _emotionSourceMap3$e = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFXaUQiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgX19tYWtlVGVtcGxhdGVPYmplY3QgfSBmcm9tIFwidHNsaWJcIjtcclxuaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vY29yZSc7XHJcbmV4cG9ydCBkZWZhdWx0IHtcclxuICAgIHNlcGFyYXRvckNvbnRhaW5lcjogZnVuY3Rpb24gKG9yaWVudGF0aW9uKSB7IHJldHVybiBjc3ModGVtcGxhdGVPYmplY3RfMSB8fCAodGVtcGxhdGVPYmplY3RfMSA9IF9fbWFrZVRlbXBsYXRlT2JqZWN0KFtcIlxcbiAgICB3aWR0aDogXCIsIFwiO1xcbiAgICBoZWlnaHQ6IFwiLCBcIjtcXG4gICAgZGlzcGxheTogZmxleDtcXG4gICAgYWxpZ24taXRlbXM6IGNlbnRlcjtcXG4gICAgZmxleC1kaXJlY3Rpb246IFwiLCBcIjtcXG4gIFwiXSwgW1wiXFxuICAgIHdpZHRoOiBcIiwgXCI7XFxuICAgIGhlaWdodDogXCIsIFwiO1xcbiAgICBkaXNwbGF5OiBmbGV4O1xcbiAgICBhbGlnbi1pdGVtczogY2VudGVyO1xcbiAgICBmbGV4LWRpcmVjdGlvbjogXCIsIFwiO1xcbiAgXCJdKSksIG9yaWVudGF0aW9uID09PSAnaG9yaXpvbnRhbCcgJiYgJzEwMCUnLCBvcmllbnRhdGlvbiA9PT0gJ3ZlcnRpY2FsJyAmJiAnMTAwJScsIG9yaWVudGF0aW9uID09PSAnaG9yaXpvbnRhbCcgPyAncm93JyA6ICdjb2x1bW4nKTsgfSxcclxuICAgIHNlcGFyYXRvcjogZnVuY3Rpb24gKHNlcGFyYXRvckVudGl0eSwgb3JpZW50YXRpb24sIGRhc2hlZCkgeyByZXR1cm4gY3NzKHRlbXBsYXRlT2JqZWN0XzIgfHwgKHRlbXBsYXRlT2JqZWN0XzIgPSBfX21ha2VUZW1wbGF0ZU9iamVjdChbXCJcXG4gICAgYm9yZGVyOiBub25lO1xcbiAgICBib3JkZXItdG9wOiBcIiwgXCI7XFxuXFxuICAgIGJvcmRlci1sZWZ0OiBcIiwgXCI7XFxuICAgIHdpZHRoOiBcIiwgXCI7XFxuICAgIGhlaWdodDogXCIsIFwiO1xcbiAgICBtYXJnaW46IDBweDtcXG4gIFwiXSwgW1wiXFxuICAgIGJvcmRlcjogbm9uZTtcXG4gICAgYm9yZGVyLXRvcDogXCIsXHJcbiAgICAgICAgXCI7XFxuXFxuICAgIGJvcmRlci1sZWZ0OiBcIixcclxuICAgICAgICBcIjtcXG4gICAgd2lkdGg6IFwiLCBcIjtcXG4gICAgaGVpZ2h0OiBcIiwgXCI7XFxuICAgIG1hcmdpbjogMHB4O1xcbiAgXCJdKSksIG9yaWVudGF0aW9uID09PSAnaG9yaXpvbnRhbCdcclxuICAgICAgICA/IHNlcGFyYXRvckVudGl0eS5ib3JkZXIgKyBcIiBcIiArIChkYXNoZWQgPyAnZGFzaGVkJyA6ICdzb2xpZCcpICsgXCIgXCIgKyBzZXBhcmF0b3JFbnRpdHkuY29sb3JcclxuICAgICAgICA6ICdub25lJywgb3JpZW50YXRpb24gPT09ICd2ZXJ0aWNhbCdcclxuICAgICAgICA/IHNlcGFyYXRvckVudGl0eS5ib3JkZXIgKyBcIiBcIiArIChkYXNoZWQgPyAnZGFzaGVkJyA6ICdzb2xpZCcpICsgXCIgXCIgKyBzZXBhcmF0b3JFbnRpdHkuY29sb3JcclxuICAgICAgICA6ICdub25lJywgb3JpZW50YXRpb24gPT09ICdob3Jpem9udGFsJyAmJiAnMTAwJScsIG9yaWVudGF0aW9uID09PSAndmVydGljYWwnICYmICcxMDAlJyk7IH0sXHJcbiAgICBockNvbnRhaW5lcjogZnVuY3Rpb24gKG9yaWVudGF0aW9uKSB7IHJldHVybiBjc3ModGVtcGxhdGVPYmplY3RfMyB8fCAodGVtcGxhdGVPYmplY3RfMyA9IF9fbWFrZVRlbXBsYXRlT2JqZWN0KFtcIlxcbiAgICB3aWR0aDogXCIsIFwiO1xcbiAgICBoZWlnaHQ6IFwiLCBcIjtcXG4gICAgZGlzcGxheTogZmxleDtcXG4gIFwiXSwgW1wiXFxuICAgIHdpZHRoOiBcIiwgXCI7XFxuICAgIGhlaWdodDogXCIsIFwiO1xcbiAgICBkaXNwbGF5OiBmbGV4O1xcbiAgXCJdKSksIG9yaWVudGF0aW9uID09PSAnaG9yaXpvbnRhbCcgJiYgJzEwMCUnLCBvcmllbnRhdGlvbiA9PT0gJ3ZlcnRpY2FsJyAmJiAnMTAwJScpOyB9LFxyXG59O1xyXG52YXIgdGVtcGxhdGVPYmplY3RfMSwgdGVtcGxhdGVPYmplY3RfMiwgdGVtcGxhdGVPYmplY3RfMztcclxuLy8jIHNvdXJjZU1hcHBpbmdVUkw9c3R5bGVzLmpzLm1hcCJdfQ== */";
var _emotionSourceMap2$m = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap$B = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var styles$z = {
  separatorContainer: function (orientation) {
    return /*#__PURE__*/css(templateObject_1$B || (templateObject_1$B = __makeTemplateObject(["\n    width: ", ";\n    height: ", ";\n    display: flex;\n    align-items: center;\n    flex-direction: ", ";\n  " + _emotionSourceMap$B], ["\n    width: ", ";\n    height: ", ";\n    display: flex;\n    align-items: center;\n    flex-direction: ", ";\n  " + _emotionSourceMap$B])), orientation === 'horizontal' && '100%', orientation === 'vertical' && '100%', orientation === 'horizontal' ? 'row' : 'column');
  },
  separator: function (separatorEntity, orientation, dashed) {
    return /*#__PURE__*/css(templateObject_2$m || (templateObject_2$m = __makeTemplateObject(["\n    border: none;\n    border-top: ", ";\n\n    border-left: ", ";\n    width: ", ";\n    height: ", ";\n    margin: 0px;\n  " + _emotionSourceMap2$m], ["\n    border: none;\n    border-top: ", ";\n\n    border-left: ", ";\n    width: ", ";\n    height: ", ";\n    margin: 0px;\n  " + _emotionSourceMap2$m])), orientation === 'horizontal' ? separatorEntity.border + " " + (dashed ? 'dashed' : 'solid') + " " + separatorEntity.color : 'none', orientation === 'vertical' ? separatorEntity.border + " " + (dashed ? 'dashed' : 'solid') + " " + separatorEntity.color : 'none', orientation === 'horizontal' && '100%', orientation === 'vertical' && '100%');
  },
  hrContainer: function (orientation) {
    return /*#__PURE__*/css(templateObject_3$e || (templateObject_3$e = __makeTemplateObject(["\n    width: ", ";\n    height: ", ";\n    display: flex;\n  " + _emotionSourceMap3$e], ["\n    width: ", ";\n    height: ", ";\n    display: flex;\n  " + _emotionSourceMap3$e])), orientation === 'horizontal' && '100%', orientation === 'vertical' && '100%');
  }
};
var templateObject_1$B, templateObject_2$m, templateObject_3$e;

var Separator = function (_a) {
  var orientation = _a.orientation,
    dashed = _a.dashed,
    caption = _a.caption,
    _b = _a.shouldCaptionWrap,
    shouldCaptionWrap = _b === void 0 ? true : _b;
  var separator = useEntities().separator;
  return jsx("div", {
    css: styles$z.separatorContainer(orientation)
  }, jsx(Div, {
    css: styles$z.hrContainer(orientation)
  }, jsx("hr", {
    css: styles$z.separator(separator, orientation, dashed)
  })), jsx(Text, {
    color: "primaryBody",
    type: "caption",
    whiteSpace: shouldCaptionWrap ? 'normal' : 'nowrap',
    inset: caption ? orientation === 'vertical' ? {
      vertical: 'x_25',
      horizontal: 'x0'
    } : {
      vertical: 'x0',
      horizontal: 'x1_5'
    } : dashed ? 'x_25' : 'x0'
  }, caption), jsx(Div, {
    css: styles$z.hrContainer(orientation)
  }, jsx("hr", {
    css: styles$z.separator(separator, orientation, dashed)
  })));
};

var Sidebar = function (_a) {
  var children = _a.children,
    width = _a.width,
    _b = _a.position,
    position = _b === void 0 ? 'left' : _b,
    zIndex = _a.zIndex,
    height = _a.height;
  return jsx(Aside, {
    zIndex: zIndex,
    width: width !== null && width !== void 0 ? width : '60px',
    height: height !== null && height !== void 0 ? height : '100vh',
    backgroundColor: "foreground",
    boxShadow: "mediumLight",
    position: "fixed",
    display: "flex",
    top: "0px",
    left: position === 'left' ? '0px' : undefined,
    right: position === 'right' ? '0px' : undefined
  }, jsx(Flex, {
    width: "100%",
    height: "100%",
    justifyContent: "center",
    alignItems: "center"
  }, children));
};

var _emotionSourceMap$A = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFHbUIiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgX19tYWtlVGVtcGxhdGVPYmplY3QgfSBmcm9tIFwidHNsaWJcIjtcclxuaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vY29yZSc7XHJcbmV4cG9ydCBkZWZhdWx0IHtcclxuICAgIG1lbnVDb250YWluZXI6IGNzcyh0ZW1wbGF0ZU9iamVjdF8xIHx8ICh0ZW1wbGF0ZU9iamVjdF8xID0gX19tYWtlVGVtcGxhdGVPYmplY3QoW1wiXFxuICAgIG1heC13aWR0aDogMjQ2cHg7XFxuICAgIG92ZXJmbG93OiBoaWRkZW47XFxuICBcIl0sIFtcIlxcbiAgICBtYXgtd2lkdGg6IDI0NnB4O1xcbiAgICBvdmVyZmxvdzogaGlkZGVuO1xcbiAgXCJdKSkpLFxyXG59O1xyXG52YXIgdGVtcGxhdGVPYmplY3RfMTtcclxuLy8jIHNvdXJjZU1hcHBpbmdVUkw9c3R5bGVzLmpzLm1hcCJdfQ== */";
var styles$y = {
  menuContainer: /*#__PURE__*/css(templateObject_1$A || (templateObject_1$A = __makeTemplateObject(["\n    max-width: 246px;\n    overflow: hidden;\n  ;label:menuContainer;" + _emotionSourceMap$A], ["\n    max-width: 246px;\n    overflow: hidden;\n  ;label:menuContainer;" + _emotionSourceMap$A])))
};
var templateObject_1$A;

var Menu = function (_a) {
  var children = _a.children;
  return jsx(Div, {
    backgroundColor: "foreground",
    borderRadius: "small",
    boxShadow: "mediumLight",
    css: styles$y.menuContainer
  }, children);
};

var MenuSection = function (_a) {
  var children = _a.children,
    divider = _a.divider;
  return jsx(Div, {
    borderWidth: divider ? {
      bottom: 'thin'
    } : undefined,
    borderColor: divider ? {
      swatch: 'grayScale',
      shade: '_1000'
    } : undefined,
    borderStyle: divider ? {
      bottom: 'solid'
    } : {
      bottom: 'none'
    }
  }, jsx(List, {
    orientation: "vertical",
    alignment: "left"
  }, children));
};

var MenuItem = function (_a) {
  var title = _a.title,
    icon = _a.icon,
    onClick = _a.onClick,
    badge = _a.badge,
    iconOrientation = _a.iconOrientation;
  return jsx(ListItem, {
    orientation: 'vertical'
  }, jsx(Flex, {
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer',
    onClick: onClick
  }, jsx(Text, {
    type: "body"
  }, title), icon && jsx(Icon, {
    size: "small",
    name: icon,
    primaryColor: "primaryBodyText",
    orientation: iconOrientation ? iconOrientation : undefined
  }), badge && jsx(Badge, {
    item: badge
  })));
};

var _emotionSourceMap5$4 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap4$7 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap3$d = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap2$l = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap$z = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var styles$x = {
  inputContainer: function (active, inputEntity, isLabelPlaceholder, height, width, isPhoneInput, icon, isFullWidth, isDisabled) {
    return /*#__PURE__*/css(templateObject_1$z || (templateObject_1$z = __makeTemplateObject(["\n    font-family: ", ";\n    display: flex;\n    align-items: center;\n    height: ", ";\n    border-radius: ", ";\n    background: ", ";\n    padding: ", ";\n    width: ", ";\n    opacity: ", ";\n    cursor: ", ";\n    label {\n      position: absolute;\n      font-size: ", ";\n      top: ", ";\n      right: ", ";\n      color: ", ";\n      width: 100%;\n      z-index: -1;\n      transition: ", ";\n    }\n    input {\n      font-family: inherit;\n      background: transparent;\n      position: absolute;\n      top: 0;\n      left: 0;\n      width: ", ";\n      height: ", ";\n      border-radius: ", ";\n      display: inline-block;\n      border: 0;\n      outline: none;\n      font-size: ", ";\n      padding: ", ";\n      color: ", ";\n    }\n    input::placeholder {\n      color: #6f8394;\n      transition: color 0.3s ease-in;\n      font-family: inherit;\n    }\n    input:invalid {\n      box-shadow: none;\n    }\n    input[type='number'] {\n      -moz-appearance: textfield;\n    }\n    input::-webkit-outer-spin-button,\n    input::-webkit-inner-spin-button {\n      appearance: none;\n    }\n\n    &:focus-within {\n      input::placeholder {\n        color: transparent;\n        transition: color 0.3s;\n      }\n      span {\n        transform: scale(1.1);\n        transition: transform 0.3s;\n      }\n      label {\n        top: -24px;\n        right: 0;\n        z-index: 3;\n        color: ", ";\n        transition: all 0.3s;\n      }\n    }\n  " + _emotionSourceMap$z], ["\n    font-family: ", ";\n    display: flex;\n    align-items: center;\n    height: ", ";\n    border-radius: ", ";\n    background: ", ";\n    padding: ", ";\n    width: ", ";\n    opacity: ", ";\n    cursor: ", ";\n    label {\n      position: absolute;\n      font-size: ", ";\n      top: ", ";\n      right: ", ";\n      color: ", ";\n      width: 100%;\n      z-index: -1;\n      transition: ", ";\n    }\n    input {\n      font-family: inherit;\n      background: transparent;\n      position: absolute;\n      top: 0;\n      left: 0;\n      width: ", ";\n      height: ", ";\n      border-radius: ", ";\n      display: inline-block;\n      border: 0;\n      outline: none;\n      font-size: ", ";\n      padding: ", ";\n      color: ", ";\n    }\n    input::placeholder {\n      color: #6f8394;\n      transition: color 0.3s ease-in;\n      font-family: inherit;\n    }\n    input:invalid {\n      box-shadow: none;\n    }\n    input[type='number'] {\n      -moz-appearance: textfield;\n    }\n    input::-webkit-outer-spin-button,\n    input::-webkit-inner-spin-button {\n      appearance: none;\n    }\n\n    &:focus-within {\n      input::placeholder {\n        color: transparent;\n        transition: color 0.3s;\n      }\n      span {\n        transform: scale(1.1);\n        transition: transform 0.3s;\n      }\n      label {\n        top: -24px;\n        right: 0;\n        z-index: 3;\n        color: ", ";\n        transition: all 0.3s;\n      }\n    }\n  " + _emotionSourceMap$z])), inputEntity.fontFamily, height + "px", inputEntity.text.borderRadius, inputEntity.backgroundColor, icon ? '0px 8px 0px 0px' : '0px 8px', isFullWidth ? '100%' : width + "px", isDisabled && 0.5, isDisabled && 'not-allowed', inputEntity.text.labelFontSize, isLabelPlaceholder ? active ? '-24px' : '10px' : '-24px', isLabelPlaceholder ? active ? 0 : '-8px' : 0, isLabelPlaceholder ? active ? "" + inputEntity.colors.blue : 'transparent' : "" + inputEntity.colors.blue, isLabelPlaceholder && 'all 0.3s ease-out', icon ? 'calc(100% - 2px)' : '100%', height + "px", inputEntity.text.borderRadius, inputEntity.text.textFontSize, isPhoneInput ? "0px 8px 0px 73px" : '0px 8px', inputEntity.fontColor, inputEntity.colors.blue);
  },
  numberInputButtons: function (isDisabled) {
    return /*#__PURE__*/css(templateObject_2$l || (templateObject_2$l = __makeTemplateObject(["\n    display: flex;\n    flex-direction: column;\n    justify-content: center;\n    align-items: center;\n    width: 25px;\n    height: 100%;\n    opacity: ", ";\n    svg {\n      opacity: ", ";\n    }\n  " + _emotionSourceMap2$l], ["\n    display: flex;\n    flex-direction: column;\n    justify-content: center;\n    align-items: center;\n    width: 25px;\n    height: 100%;\n    opacity: ", ";\n    svg {\n      opacity: ", ";\n    }\n  " + _emotionSourceMap2$l])), isDisabled && 0.5, isDisabled && 0.5);
  },
  outerglow: function (inputEntity, width, height, isFullWidth) {
    return /*#__PURE__*/css(templateObject_3$d || (templateObject_3$d = __makeTemplateObject(["\n    width: ", ";\n    height: ", ";\n    position: absolute;\n    top: -1px;\n    left: -1px;\n    z-index: -1;\n    border-radius: calc(", " + 1px);\n    padding: 4px;\n    border: ", " solid transparent;\n  " + _emotionSourceMap3$d], ["\n    width: ", ";\n    height: ", ";\n    position: absolute;\n    top: -1px;\n    left: -1px;\n    z-index: -1;\n    border-radius: calc(", " + 1px);\n    padding: 4px;\n    border: ", " solid transparent;\n  " + _emotionSourceMap3$d])), isFullWidth ? 'calc(100% + 2px)' : width + 2 + "px", height && height + 2 + "px", "" + inputEntity.text.borderRadius, inputEntity.text.borderWidth);
  },
  iconContainer: /*#__PURE__*/css(templateObject_4$7 || (templateObject_4$7 = __makeTemplateObject(["\n    height: 100%;\n    padding-left: 16px;\n  ;label:iconContainer;" + _emotionSourceMap4$7], ["\n    height: 100%;\n    padding-left: 16px;\n  ;label:iconContainer;" + _emotionSourceMap4$7]))),
  inputWrapper: /*#__PURE__*/css(templateObject_5$4 || (templateObject_5$4 = __makeTemplateObject(["\n    font-family: inherit;\n    position: relative;\n    width: 100%;\n    flex-direction: column;\n    align-items: flex-end;\n  ;label:inputWrapper;" + _emotionSourceMap5$4], ["\n    font-family: inherit;\n    position: relative;\n    width: 100%;\n    flex-direction: column;\n    align-items: flex-end;\n  ;label:inputWrapper;" + _emotionSourceMap5$4])))
};
var templateObject_1$z, templateObject_2$l, templateObject_3$d, templateObject_4$7, templateObject_5$4;

var useLongPress = function (onLongPress, onLongPressEnd, onClick, _a) {
  var _b = _a === void 0 ? {} : _a,
    _c = _b.isPreventDefault,
    isPreventDefault = _c === void 0 ? true : _c,
    _d = _b.delay,
    delay = _d === void 0 ? 300 : _d;
  var _e = useState(false),
    hasLongPress = _e[0],
    setHasLongPress = _e[1];
  var timeout = useRef(null);
  var target = useRef(null);
  var start = useCallback(function (event) {
    if (isPreventDefault && event.target) {
      event.target.addEventListener('touchend', preventDefault, {
        passive: false
      });
      target.current = event.target;
    }
    timeout.current = setTimeout(function () {
      onLongPress(event);
      setHasLongPress(true);
    }, delay);
  }, [onLongPress, delay, isPreventDefault]);
  var clear = useCallback(function (event, shouldTriggerClick) {
    if (shouldTriggerClick === void 0) {
      shouldTriggerClick = true;
    }
    timeout.current && clearTimeout(timeout.current);
    shouldTriggerClick && !hasLongPress && onClick();
    setHasLongPress(false);
    onLongPressEnd(event);
    if (isPreventDefault && target.current) {
      target.current.removeEventListener('touchend', preventDefault);
    }
  }, [isPreventDefault, onClick, hasLongPress, onLongPressEnd]);
  var preventDefault = function (event) {
    if (event instanceof TouchEvent && event.touches.length < 2 && event.preventDefault) {
      event.preventDefault();
    }
  };
  return {
    onMouseDown: function (e) {
      return start(e);
    },
    onTouchStart: function (e) {
      return start(e);
    },
    onMouseUp: function (e) {
      return clear(e);
    },
    onMouseLeave: function (e) {
      return clear(e, false);
    },
    onTouchEnd: function (e) {
      return clear(e);
    }
  };
};

var Input = function (_a) {
  var label = _a.label,
    placeholder = _a.placeholder,
    labelPlaceholder = _a.labelPlaceholder,
    name = _a.name,
    type = _a.type,
    _b = _a.focusColor,
    focusColor = _b === void 0 ? 'primaryBrand' : _b,
    icon = _a.icon,
    message = _a.message,
    isLoading = _a.isLoading,
    onChange = _a.onChange,
    _c = _a.value,
    value = _c === void 0 ? '300' : _c,
    defaultValue = _a.defaultValue,
    _d = _a.min,
    min = _d === void 0 ? 0 : _d,
    _e = _a.max,
    max = _e === void 0 ? 100 : _e,
    isDisabled = _a.isDisabled,
    _f = _a.isFullWidth,
    isFullWidth = _f === void 0 ? false : _f,
    _g = _a.isTypingDisabled,
    isTypingDisabled = _g === void 0 ? false : _g,
    _h = _a.width,
    width = _h === void 0 ? 200 : _h,
    _j = _a.height,
    height = _j === void 0 ? 50 : _j,
    _k = _a.isPhoneInput,
    isPhoneInput = _k === void 0 ? false : _k,
    _l = _a.hasSecurePassword,
    hasSecurePassword = _l === void 0 ? false : _l,
    maxLength = _a.maxLength,
    isMaxLengthHit = _a.isMaxLengthHit,
    autoComplete = _a.autoComplete,
    onMaxLengthCap = _a.onMaxLengthCap,
    id = _a.id,
    onBlur = _a.onBlur;
  var _m = useState(0),
    longPressTime = _m[0],
    setLongPressTime = _m[1];
  var _o = useState(false),
    isPasswordVisible = _o[0],
    setIsPasswordVisible = _o[1];
  var _p = useState(false),
    isFocused = _p[0],
    setIsFocused = _p[1];
  var _q = useState(false),
    isOutsideRange = _q[0],
    setIsOutsideRange = _q[1];
  var _r = useState(false),
    isIncrementHold = _r[0],
    setIsIncrementHold = _r[1];
  var _s = useState(false),
    isDecrementHold = _s[0],
    setIsDecrementHold = _s[1];
  var _t = useState(false),
    isHovering = _t[0],
    setIsHovering = _t[1];
  var _u = useEntities(),
    inputs = _u.inputs,
    hocs = _u.hocs;
  var isActive = value ? true : false;
  var hasIcon = icon ? true : false;
  var isLabelPlaceholder = labelPlaceholder ? true : false;
  var color = (message === null || message === void 0 ? void 0 : message.type) ? message === null || message === void 0 ? void 0 : message.type : focusColor;
  var inputWrapperRef = useRef(null);
  var inputRef = useRef(null);
  var isMinInputWidth = width >= 150;
  var handleOnBlur = function (e) {
    setIsFocused(false);
    onBlur === null || onBlur === void 0 ? void 0 : onBlur(e);
  };
  var handleOnChange = useCallback(function (value, direction, step) {
    var checkMax = function (value) {
      return value > max;
    };
    var checkMin = function (value) {
      return value < min;
    };
    var handleDirection = function (value, direction, step) {
      if (direction > 0) {
        return value += step;
      }
      if (direction < 0) {
        return value -= step;
      }
      return value;
    };
    var checkMaxLength = function (value) {
      var valueLength = value.length;
      if (maxLength) {
        if (valueLength < maxLength) {
          onMaxLengthCap === null || onMaxLengthCap === void 0 ? void 0 : onMaxLengthCap(false);
        }
        if (valueLength === maxLength) {
          onMaxLengthCap === null || onMaxLengthCap === void 0 ? void 0 : onMaxLengthCap(true);
        }
        if (valueLength > maxLength) {
          return;
        }
      }
      return onChange(value);
    };
    if (type === 'number') {
      var newValue = handleDirection(parseInt(value), direction, step);
      setIsOutsideRange(false);
      if (checkMin(newValue)) {
        setIsOutsideRange(true);
        return onChange(min.toString());
      }
      if (checkMax(newValue)) {
        setIsOutsideRange(true);
        return onChange(max.toString());
      }
      return onChange(newValue.toString());
    } else {
      if (maxLength) {
        return checkMaxLength(value);
      }
      return onChange(value);
    }
  }, [max, maxLength, min, onChange, type, onMaxLengthCap]);
  var handleArrowKeys = function (event) {
    if (isFocused && !isDisabled && isTypingDisabled) {
      if (event.key === 'ArrowUp') {
        handleOnChange(value, 1, 1);
      }
      if (event.key === 'ArrowDown') {
        handleOnChange(value, -1, 1);
      }
    }
  };
  useEffect(function () {
    document.addEventListener('keydown', handleArrowKeys);
    return function () {
      document.removeEventListener('keydown', handleArrowKeys);
    };
  });
  useEffect(function () {
    var _a, _b;
    if (type === 'number' && (value === '' || value === 'NaN')) {
      onChange((_a = min.toString()) !== null && _a !== void 0 ? _a : '0');
    }
    if (isFocused) {
      (_b = inputRef === null || inputRef === void 0 ? void 0 : inputRef.current) === null || _b === void 0 ? void 0 : _b.focus();
    }
  }, [value, onChange, min, type, isFocused]);
  useOutsideClick(inputWrapperRef, function () {
    setIsFocused(false);
  });
  var onIncrementHold = function () {
    setIsIncrementHold(true);
    setIsFocused(true);
  };
  var onIncrementClick = function () {
    handleOnChange(value, 1, 1);
    setIsFocused(true);
  };
  var onDecrementHold = function () {
    setIsDecrementHold(true);
    setIsFocused(true);
  };
  var onDecrementClick = function () {
    handleOnChange(value, -1, 1);
    setIsFocused(true);
  };
  var onIncrementEnd = function () {
    setIsIncrementHold(false);
  };
  var onDecrementEnd = function () {
    setIsDecrementHold(false);
  };
  var defaultOptions = {
    shouldPreventDefault: true,
    delay: 500
  };
  var incrementEvent = useLongPress(onIncrementHold, onIncrementEnd, onIncrementClick, defaultOptions);
  var decrementEvent = useLongPress(onDecrementHold, onDecrementEnd, onDecrementClick, defaultOptions);
  useEffect(function () {
    var interval = setInterval(function () {
      if ((isIncrementHold || isDecrementHold) && !isDisabled) {
        if (isDecrementHold) {
          setLongPressTime(longPressTime + 1);
          if (longPressTime > 10) {
            handleOnChange(value, -1, 10);
          } else {
            handleOnChange(value, -1, 1);
          }
        }
        if (isIncrementHold) {
          setLongPressTime(longPressTime + 1);
          if (longPressTime > 10) {
            handleOnChange(value, 1, 10);
          } else {
            handleOnChange(value, 1, 1);
          }
        }
      } else {
        clearInterval(interval);
        setLongPressTime(0);
      }
    }, 100);
    return function () {
      clearInterval(interval);
    };
  }, [isIncrementHold, isDecrementHold, value, longPressTime, isDisabled, handleOnChange]);
  return jsx(Flex, {
    flexDirection: "column",
    position: "relative"
  }, jsx("div", {
    css: styles$x.inputContainer(isActive, inputs, isLabelPlaceholder, height, width, isPhoneInput, hasIcon, isFullWidth, isDisabled),
    ref: inputWrapperRef,
    onFocus: function () {
      setIsFocused(true);
    },
    style: {
      backgroundColor: message ? "" + hocs.colors.swatches[message.type]._1000 : ''
    }
  }, icon && jsx(Span, {
    css: styles$x.iconContainer,
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  }, jsx(Icon, {
    name: icon,
    primaryColor: isFocused ? color : 'primaryBodyText',
    size: "small"
  })), jsx(Flex, {
    width: isFullWidth ? '100%' : width + "px",
    height: "100%",
    css: styles$x.inputWrapper
  }, jsx("input", {
    type: type === 'password' ? isPasswordVisible ? 'text' : 'password' : type,
    name: name,
    value: value,
    defaultValue: defaultValue,
    min: min,
    max: max,
    ref: inputRef,
    width: '100%',
    id: id,
    style: {
      cursor: isTypingDisabled ? 'default' : 'auto',
      textShadow: isTypingDisabled ? "0 0 0 " + inputs.fontColor : 'none',
      color: isTypingDisabled ? 'transparent' : 'default'
    },
    disabled: isDisabled,
    placeholder: placeholder || labelPlaceholder,
    onChange: function (e) {
      if (!isTypingDisabled) {
        handleOnChange(e.target.value, 0, 1);
      }
    },
    onBlur: handleOnBlur,
    autoComplete: autoComplete
  }), type === 'number' && jsx(Div, {
    css: styles$x.numberInputButtons(isDisabled),
    zIndex: 1
  }, jsx(Div, _extends({
    id: id ? id.toLowerCase() + "-up" : undefined
  }, incrementEvent, {
    display: "flex",
    alignItems: "center",
    cursor: isDisabled ? 'not-allowed' : 'pointer'
  }), jsx(Icon, {
    name: "chevron",
    orientation: "up",
    primaryColor: 'primaryBodyText',
    size: 'xSmall'
  })), jsx(Div, _extends({
    id: id ? id.toLowerCase() + "-down" : undefined
  }, decrementEvent, {
    display: "flex",
    alignItems: "center",
    cursor: isDisabled ? 'not-allowed' : 'pointer'
  }), jsx(Icon, {
    name: "chevron",
    primaryColor: 'primaryBodyText',
    size: 'xSmall',
    orientation: "down"
  }))), jsx("label", {
    htmlFor: name
  }, label || labelPlaceholder)), isLoading && jsx(LoadingSpinner, {
    size: "medium"
  }), maxLength && !(type === 'number') && !isMaxLengthHit && jsx(Flex, {
    inset: {
      right: 'x1'
    },
    justifyContent: "flex-end",
    width: isFullWidth ? '100%' : width + "px",
    position: "absolute",
    bottom: '-24px',
    style: {
      whiteSpace: 'nowrap'
    }
  }, jsx(Text, {
    inset: {
      top: 'x_5'
    },
    type: "footnote",
    color: value.length >= maxLength ? 'danger' : 'primaryBody'
  }, value.length, "/", maxLength)), !hasSecurePassword && type === 'password' && jsx(Div, {
    onClick: function () {
      return setIsPasswordVisible(!isPasswordVisible);
    },
    position: "relative",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    inset: {
      right: 'x1'
    }
  }, isPasswordVisible ? jsx(Icon, {
    name: "visible",
    size: "small",
    primaryColor: "default"
  }) : jsx(Icon, {
    name: "invisible",
    size: "small",
    primaryColor: "default"
  })), isFocused && jsx(Div, {
    id: "outerglow",
    css: styles$x.outerglow(inputs, width, height, isFullWidth),
    style: {
      border: inputs.text.borderWidth + " solid " + hocs.colors.swatches[focusColor].base,
      transition: 'border 0.3s'
    }
  }), message && jsx(Div, {
    id: "outerglow",
    css: styles$x.outerglow(inputs, width, height, isFullWidth),
    style: {
      border: inputs.text.borderWidth + " solid " + hocs.colors.swatches[message.type].base,
      transition: 'border 0.3s'
    }
  })), message && jsx(HStack, {
    gap: "x_5",
    justify: "flex-start"
  }, jsx(Text, {
    inset: {
      left: 'x1',
      top: 'x_5'
    },
    content: message.content,
    type: "footnote",
    color: message.type
  }), message.icon && jsx(Span, {
    inset: {
      top: 'x_25'
    },
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    onClick: message.onClick,
    cursor: "pointer"
  }, jsx(Icon, {
    name: message.icon,
    primaryColor: message.type,
    size: "xSmall"
  }))), isOutsideRange && isMinInputWidth ? jsx(Flex, {
    width: "100%",
    position: "absolute",
    bottom: '-38px',
    style: {
      whiteSpace: 'nowrap'
    }
  }, jsx(Text, {
    inset: {
      left: 'x_5',
      top: 'x1'
    },
    type: "footnote",
    color: "warning",
    lineHeight: 1.2
  }, "Value must be between ", min, " and ", max)) : isOutsideRange && !isMinInputWidth && jsx(Flex, {
    inset: {
      top: 'x1'
    },
    onHoverStart: function () {
      return setIsHovering(true);
    },
    onHoverEnd: function () {
      return setIsHovering(false);
    },
    cursor: "pointer"
  }, jsx(Icon, {
    name: "information",
    size: "xSmall",
    primaryColor: "danger"
  })), isHovering && jsx(Flex, {
    position: "absolute",
    top: "-50px",
    right: "-55px"
  }, jsx(Tooltip, {
    title: "Value must be between " + min + " and " + max,
    direction: "bottom",
    background: "dark"
  })));
};

var _emotionSourceMap2$k = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFJc0IiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgX19tYWtlVGVtcGxhdGVPYmplY3QgfSBmcm9tIFwidHNsaWJcIjtcclxuaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vY29yZSc7XHJcbmV4cG9ydCBkZWZhdWx0IHtcclxuICAgIGRpYWxvZ0NvbnRhaW5lcjogY3NzKHRlbXBsYXRlT2JqZWN0XzEgfHwgKHRlbXBsYXRlT2JqZWN0XzEgPSBfX21ha2VUZW1wbGF0ZU9iamVjdChbXCJcXG4gICAgbWluLXdpZHRoOiAzMDhweDtcXG4gICAgZGlzcGxheTogZmxleDtcXG4gICAganVzdGlmeS1jb250ZW50OiBmbGV4LXN0YXJ0O1xcbiAgICBmbGV4LWRpcmVjdGlvbjogY29sdW1uO1xcbiAgICBwb3NpdGlvbjogcmVsYXRpdmU7XFxuICBcIl0sIFtcIlxcbiAgICBtaW4td2lkdGg6IDMwOHB4O1xcbiAgICBkaXNwbGF5OiBmbGV4O1xcbiAgICBqdXN0aWZ5LWNvbnRlbnQ6IGZsZXgtc3RhcnQ7XFxuICAgIGZsZXgtZGlyZWN0aW9uOiBjb2x1bW47XFxuICAgIHBvc2l0aW9uOiByZWxhdGl2ZTtcXG4gIFwiXSkpKSxcclxuICAgIGNsb3NlSWNvbldyYXBwZXI6IGNzcyh0ZW1wbGF0ZU9iamVjdF8yIHx8ICh0ZW1wbGF0ZU9iamVjdF8yID0gX19tYWtlVGVtcGxhdGVPYmplY3QoW1wiXFxuICAgIHRvcDogLThweDtcXG4gICAgcmlnaHQ6IC04cHg7XFxuICAgIHRyYW5zZm9ybTogdHJhbnNsYXRlKDBweCwgMHB4KTtcXG4gICAgdHJhbnNpdGlvbjogdHJhbnNmb3JtIDAuMjVzIGVhc2U7XFxuICAgIDpob3ZlciB7XFxuICAgICAgdHJhbnNmb3JtOiB0cmFuc2xhdGUoLTJweCwgMnB4KTtcXG4gICAgICB0cmFuc2l0aW9uOiB0cmFuc2Zvcm0gMC4yNXMgZWFzZTtcXG4gICAgfVxcbiAgXCJdLCBbXCJcXG4gICAgdG9wOiAtOHB4O1xcbiAgICByaWdodDogLThweDtcXG4gICAgdHJhbnNmb3JtOiB0cmFuc2xhdGUoMHB4LCAwcHgpO1xcbiAgICB0cmFuc2l0aW9uOiB0cmFuc2Zvcm0gMC4yNXMgZWFzZTtcXG4gICAgOmhvdmVyIHtcXG4gICAgICB0cmFuc2Zvcm06IHRyYW5zbGF0ZSgtMnB4LCAycHgpO1xcbiAgICAgIHRyYW5zaXRpb246IHRyYW5zZm9ybSAwLjI1cyBlYXNlO1xcbiAgICB9XFxuICBcIl0pKSksXHJcbn07XHJcbnZhciB0ZW1wbGF0ZU9iamVjdF8xLCB0ZW1wbGF0ZU9iamVjdF8yO1xyXG4vLyMgc291cmNlTWFwcGluZ1VSTD1zdHlsZXMuanMubWFwIl19 */";
var _emotionSourceMap$y = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFHcUIiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgX19tYWtlVGVtcGxhdGVPYmplY3QgfSBmcm9tIFwidHNsaWJcIjtcclxuaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vY29yZSc7XHJcbmV4cG9ydCBkZWZhdWx0IHtcclxuICAgIGRpYWxvZ0NvbnRhaW5lcjogY3NzKHRlbXBsYXRlT2JqZWN0XzEgfHwgKHRlbXBsYXRlT2JqZWN0XzEgPSBfX21ha2VUZW1wbGF0ZU9iamVjdChbXCJcXG4gICAgbWluLXdpZHRoOiAzMDhweDtcXG4gICAgZGlzcGxheTogZmxleDtcXG4gICAganVzdGlmeS1jb250ZW50OiBmbGV4LXN0YXJ0O1xcbiAgICBmbGV4LWRpcmVjdGlvbjogY29sdW1uO1xcbiAgICBwb3NpdGlvbjogcmVsYXRpdmU7XFxuICBcIl0sIFtcIlxcbiAgICBtaW4td2lkdGg6IDMwOHB4O1xcbiAgICBkaXNwbGF5OiBmbGV4O1xcbiAgICBqdXN0aWZ5LWNvbnRlbnQ6IGZsZXgtc3RhcnQ7XFxuICAgIGZsZXgtZGlyZWN0aW9uOiBjb2x1bW47XFxuICAgIHBvc2l0aW9uOiByZWxhdGl2ZTtcXG4gIFwiXSkpKSxcclxuICAgIGNsb3NlSWNvbldyYXBwZXI6IGNzcyh0ZW1wbGF0ZU9iamVjdF8yIHx8ICh0ZW1wbGF0ZU9iamVjdF8yID0gX19tYWtlVGVtcGxhdGVPYmplY3QoW1wiXFxuICAgIHRvcDogLThweDtcXG4gICAgcmlnaHQ6IC04cHg7XFxuICAgIHRyYW5zZm9ybTogdHJhbnNsYXRlKDBweCwgMHB4KTtcXG4gICAgdHJhbnNpdGlvbjogdHJhbnNmb3JtIDAuMjVzIGVhc2U7XFxuICAgIDpob3ZlciB7XFxuICAgICAgdHJhbnNmb3JtOiB0cmFuc2xhdGUoLTJweCwgMnB4KTtcXG4gICAgICB0cmFuc2l0aW9uOiB0cmFuc2Zvcm0gMC4yNXMgZWFzZTtcXG4gICAgfVxcbiAgXCJdLCBbXCJcXG4gICAgdG9wOiAtOHB4O1xcbiAgICByaWdodDogLThweDtcXG4gICAgdHJhbnNmb3JtOiB0cmFuc2xhdGUoMHB4LCAwcHgpO1xcbiAgICB0cmFuc2l0aW9uOiB0cmFuc2Zvcm0gMC4yNXMgZWFzZTtcXG4gICAgOmhvdmVyIHtcXG4gICAgICB0cmFuc2Zvcm06IHRyYW5zbGF0ZSgtMnB4LCAycHgpO1xcbiAgICAgIHRyYW5zaXRpb246IHRyYW5zZm9ybSAwLjI1cyBlYXNlO1xcbiAgICB9XFxuICBcIl0pKSksXHJcbn07XHJcbnZhciB0ZW1wbGF0ZU9iamVjdF8xLCB0ZW1wbGF0ZU9iamVjdF8yO1xyXG4vLyMgc291cmNlTWFwcGluZ1VSTD1zdHlsZXMuanMubWFwIl19 */";
var styles$w = {
  dialogContainer: /*#__PURE__*/css(templateObject_1$y || (templateObject_1$y = __makeTemplateObject(["\n    min-width: 308px;\n    display: flex;\n    justify-content: flex-start;\n    flex-direction: column;\n    position: relative;\n  ;label:dialogContainer;" + _emotionSourceMap$y], ["\n    min-width: 308px;\n    display: flex;\n    justify-content: flex-start;\n    flex-direction: column;\n    position: relative;\n  ;label:dialogContainer;" + _emotionSourceMap$y]))),
  closeIconWrapper: /*#__PURE__*/css(templateObject_2$k || (templateObject_2$k = __makeTemplateObject(["\n    top: -8px;\n    right: -8px;\n    transform: translate(0px, 0px);\n    transition: transform 0.25s ease;\n    :hover {\n      transform: translate(-2px, 2px);\n      transition: transform 0.25s ease;\n    }\n  ;label:closeIconWrapper;" + _emotionSourceMap2$k], ["\n    top: -8px;\n    right: -8px;\n    transform: translate(0px, 0px);\n    transition: transform 0.25s ease;\n    :hover {\n      transform: translate(-2px, 2px);\n      transition: transform 0.25s ease;\n    }\n  ;label:closeIconWrapper;" + _emotionSourceMap2$k])))
};
var templateObject_1$y, templateObject_2$k;

var Dialog = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var _a;
  var dialog = useEntities().dialog;
  var dialogVariants = {
    open: {
      scale: 1,
      opacity: 1
    },
    closed: {
      scale: 0.1,
      opacity: 0
    }
  };
  var overlayVariants = {
    open: {
      opacity: 1
    },
    closed: {
      opacity: 0
    }
  };
  var _b = props.zIndex,
    zIndex = _b === void 0 ? 1000 : _b,
    isOpen = props.isOpen,
    onClose = props.onClose,
    _c = props.maxWidth,
    maxWidth = _c === void 0 ? '552px' : _c,
    maxHeight = props.maxHeight,
    bgElementSelector = props.bgElementSelector,
    isFullScreen = props.isFullScreen,
    closeButtonId = props.closeButtonId,
    insetOverride = props.insetOverride,
    outsetOverride = props.outsetOverride,
    height = props.height,
    width = props.width,
    children = props.children,
    _d = props.shouldScroll,
    shouldScroll = _d === void 0 ? false : _d,
    _e = props.borderRadius,
    borderRadius = _e === void 0 ? 'default' : _e,
    _f = props.pinned,
    pinned = _f === void 0 ? 'center' : _f,
    _g = props.backgroundVariant,
    backgroundVariant = _g === void 0 ? 'solid' : _g,
    rest = __rest(props, ["zIndex", "isOpen", "onClose", "maxWidth", "maxHeight", "bgElementSelector", "isFullScreen", "closeButtonId", "insetOverride", "outsetOverride", "height", "width", "children", "shouldScroll", "borderRadius", "pinned", "backgroundVariant"]);
  var pinnedRadius = pinned === 'bottom' ? {
    top: borderRadius,
    bottom: 'none'
  } : pinned === 'top' ? {
    bottom: borderRadius,
    top: 'none'
  } : borderRadius;
  return jsx(AnimatePresence, null, isOpen && jsx(Overlay, _extends({
    variants: overlayVariants,
    animate: isOpen ? 'open' : 'closed',
    initial: 'closed',
    zIndex: zIndex,
    exit: 'closed',
    bgElementSelector: bgElementSelector,
    pinned: pinned,
    backgroundVariant: backgroundVariant,
    shouldScroll: shouldScroll
  }, rest), jsx(Div, {
    ref: ref,
    outsideClick: onClose,
    variants: dialogVariants,
    css: styles$w.dialogContainer,
    backgroundColor: dialog.backgroundColor,
    borderRadius: pinnedRadius,
    boxShadow: dialog.boxShadow,
    inset: insetOverride !== null && insetOverride !== void 0 ? insetOverride : dialog.defaultInset,
    outset: outsetOverride !== null && outsetOverride !== void 0 ? outsetOverride : undefined,
    position: 'absolute',
    bottom: pinned === 'bottom' ? 0 : undefined,
    maxWidth: maxWidth,
    maxHeight: maxHeight ? maxHeight : isFullScreen || pinned === 'center' ? 'calc(100vh - 24px )' : undefined,
    width: (_a = isFullScreen ? '100%' : undefined) !== null && _a !== void 0 ? _a : width,
    height: height ? height : isFullScreen ? 'calc(100vh - 24px )' : undefined
  }, shouldScroll ? jsx(Div, {
    height: {
      xSmall: 'calc(100vh - 96px)',
      medium: 'calc(100vh - 32px)'
    },
    overflowY: "auto"
  }, children, onClose && jsx(CloseButton, {
    id: closeButtonId,
    onClose: onClose
  })) : jsx(React.Fragment, null, children, onClose && jsx(CloseButton, {
    id: closeButtonId,
    onClose: onClose
  })))));
});

var _emotionSourceMap5$3 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap4$6 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap3$c = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap2$j = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFJZ0YiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgX19tYWtlVGVtcGxhdGVPYmplY3QgfSBmcm9tIFwidHNsaWJcIjtcclxuaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vY29yZSc7XHJcbmV4cG9ydCBkZWZhdWx0IHtcclxuICAgIHNlbGVjdENvbnRhaW5lcjogY3NzKHRlbXBsYXRlT2JqZWN0XzEgfHwgKHRlbXBsYXRlT2JqZWN0XzEgPSBfX21ha2VUZW1wbGF0ZU9iamVjdChbXCJcXG4gICAgZGlzcGxheTogZmxleDtcXG4gICAganVzdGlmeS1jb250ZW50OiBjZW50ZXI7XFxuICAgIGZsZXgtZGlyZWN0aW9uOiBjb2x1bW47XFxuICAgIHdpZHRoOiAxMDAlO1xcbiAgICBwb3NpdGlvbjogcmVsYXRpdmU7XFxuICBcIl0sIFtcIlxcbiAgICBkaXNwbGF5OiBmbGV4O1xcbiAgICBqdXN0aWZ5LWNvbnRlbnQ6IGNlbnRlcjtcXG4gICAgZmxleC1kaXJlY3Rpb246IGNvbHVtbjtcXG4gICAgd2lkdGg6IDEwMCU7XFxuICAgIHBvc2l0aW9uOiByZWxhdGl2ZTtcXG4gIFwiXSkpKSxcclxuICAgIHNlbGVjdEJvZHk6IGZ1bmN0aW9uIChob2NzLCBmb2N1c0NvbG9yLCBzZWxlY3RIZWlnaHQsIHNlbGVjdFdpZHRoKSB7IHJldHVybiBjc3ModGVtcGxhdGVPYmplY3RfMiB8fCAodGVtcGxhdGVPYmplY3RfMiA9IF9fbWFrZVRlbXBsYXRlT2JqZWN0KFtcIlxcbiAgICBjdXJzb3I6IHBvaW50ZXI7XFxuICAgIGRpc3BsYXk6IGZsZXg7XFxuICAgIGFsaWduLWl0ZW1zOiBjZW50ZXI7XFxuICAgIGp1c3RpZnktY29udGVudDogc3BhY2UtYmV0d2VlbjtcXG4gICAgaGVpZ2h0OiBcIiwgXCI7XFxuICAgIHdpZHRoOiBcIiwgXCI7XFxuICAgIHBhZGRpbmc6IDBweCA4cHg7XFxuICAgIGJvcmRlcjogbm9uZTtcXG4gICAgb3V0bGluZTogbm9uZTtcXG4gICAgb3ZlcmZsb3c6IGhpZGRlbjtcXG4gICAgJjpmb2N1cyB7XFxuICAgICAgYm9yZGVyOiBzb2xpZCAxLjVweCBcIiwgXCJ9O1xcbiAgICB9XFxuICBcIl0sIFtcIlxcbiAgICBjdXJzb3I6IHBvaW50ZXI7XFxuICAgIGRpc3BsYXk6IGZsZXg7XFxuICAgIGFsaWduLWl0ZW1zOiBjZW50ZXI7XFxuICAgIGp1c3RpZnktY29udGVudDogc3BhY2UtYmV0d2VlbjtcXG4gICAgaGVpZ2h0OiBcIiwgXCI7XFxuICAgIHdpZHRoOiBcIiwgXCI7XFxuICAgIHBhZGRpbmc6IDBweCA4cHg7XFxuICAgIGJvcmRlcjogbm9uZTtcXG4gICAgb3V0bGluZTogbm9uZTtcXG4gICAgb3ZlcmZsb3c6IGhpZGRlbjtcXG4gICAgJjpmb2N1cyB7XFxuICAgICAgYm9yZGVyOiBzb2xpZCAxLjVweCBcIiwgXCJ9O1xcbiAgICB9XFxuICBcIl0pKSwgXCJcIiArIChzZWxlY3RIZWlnaHQgfHwgJzUwcHgnKSwgXCJcIiArIChzZWxlY3RXaWR0aCB8fCAnYXV0bycpLCBob2NzLmNvbG9ycy5zd2F0Y2hlc1tmb2N1c0NvbG9yXS5iYXNlKTsgfSxcclxuICAgIHNlbGVjdERyb3Bkb3duQ29udGVudHM6IGZ1bmN0aW9uIChtYXhIZWlnaHQsIGFsaWduVG9MZWZ0KSB7IHJldHVybiBjc3ModGVtcGxhdGVPYmplY3RfMyB8fCAodGVtcGxhdGVPYmplY3RfMyA9IF9fbWFrZVRlbXBsYXRlT2JqZWN0KFtcIlxcbiAgICBtYXgtaGVpZ2h0OiBcIiwgXCI7XFxuICAgIG92ZXJmbG93LXk6IGF1dG87XFxuICAgIHBvc2l0aW9uOiBhYnNvbHV0ZTtcXG4gICAgbGVmdDogXCIsIFwiO1xcbiAgICB0cmFuc2Zvcm06IFwiLCBcIjtcXG4gIFwiXSwgW1wiXFxuICAgIG1heC1oZWlnaHQ6IFwiLCBcIjtcXG4gICAgb3ZlcmZsb3cteTogYXV0bztcXG4gICAgcG9zaXRpb246IGFic29sdXRlO1xcbiAgICBsZWZ0OiBcIiwgXCI7XFxuICAgIHRyYW5zZm9ybTogXCIsIFwiO1xcbiAgXCJdKSksIFwiXCIgKyBtYXhIZWlnaHQgfHwgJzIyNnB4JywgYWxpZ25Ub0xlZnQgPyBcIjBcIiA6IFwiNTAlXCIsIGFsaWduVG9MZWZ0ID8gXCJpbml0aWFsXCIgOiBcInRyYW5zbGF0ZVgoLTUwJSlcIik7IH0sXHJcbiAgICBhY3RpdmVPcHRpb246IGZ1bmN0aW9uIChhbGlnbm1lbnQpIHsgcmV0dXJuIGNzcyh0ZW1wbGF0ZU9iamVjdF80IHx8ICh0ZW1wbGF0ZU9iamVjdF80ID0gX19tYWtlVGVtcGxhdGVPYmplY3QoW1wiXFxuICAgIHdpZHRoOiAxMDAlO1xcbiAgICBoZWlnaHQ6IDEwMCU7XFxuICAgIGRpc3BsYXk6IGZsZXg7XFxuICAgIGFsaWduLWl0ZW1zOiBjZW50ZXI7XFxuICAgIGp1c3RpZnktY29udGVudDogXCIsIFwiO1xcbiAgICAuaGlkZS1vbi1hY3RpdmUge1xcbiAgICAgIGRpc3BsYXk6IG5vbmU7XFxuICAgIH1cXG4gIFwiXSwgW1wiXFxuICAgIHdpZHRoOiAxMDAlO1xcbiAgICBoZWlnaHQ6IDEwMCU7XFxuICAgIGRpc3BsYXk6IGZsZXg7XFxuICAgIGFsaWduLWl0ZW1zOiBjZW50ZXI7XFxuICAgIGp1c3RpZnktY29udGVudDogXCIsXHJcbiAgICAgICAgXCI7XFxuICAgIC5oaWRlLW9uLWFjdGl2ZSB7XFxuICAgICAgZGlzcGxheTogbm9uZTtcXG4gICAgfVxcbiAgXCJdKSksIGFsaWdubWVudCA9PT0gJ2xlZnQnXHJcbiAgICAgICAgPyAnZmxleC1zdGFydCdcclxuICAgICAgICA6IGFsaWdubWVudCA9PT0gJ3JpZ2h0J1xyXG4gICAgICAgICAgICA/ICdmbGV4LWVuZCdcclxuICAgICAgICAgICAgOiAnY2VudGVyJyk7IH0sXHJcbiAgICBvcHRpb246IGZ1bmN0aW9uIChhbGlnbm1lbnQpIHsgcmV0dXJuIGNzcyh0ZW1wbGF0ZU9iamVjdF81IHx8ICh0ZW1wbGF0ZU9iamVjdF81ID0gX19tYWtlVGVtcGxhdGVPYmplY3QoW1wiXFxuICAgIHdpZHRoOiAxMDAlO1xcbiAgICBwYWRkaW5nOiAxMnB4O1xcbiAgICBib3JkZXI6IG5vbmU7XFxuICAgIGRpc3BsYXk6IGZsZXg7XFxuICAgIGp1c3RpZnktY29udGVudDogXCIsIFwiO1xcbiAgICBvdXRsaW5lOiBub25lO1xcbiAgICBhbGlnbi1pdGVtczogY2VudGVyO1xcbiAgICBjdXJzb3I6IHBvaW50ZXI7XFxuICAgIGZvbnQtc2l6ZTogaW5oZXJpdDtcXG4gIFwiXSwgW1wiXFxuICAgIHdpZHRoOiAxMDAlO1xcbiAgICBwYWRkaW5nOiAxMnB4O1xcbiAgICBib3JkZXI6IG5vbmU7XFxuICAgIGRpc3BsYXk6IGZsZXg7XFxuICAgIGp1c3RpZnktY29udGVudDogXCIsXHJcbiAgICAgICAgXCI7XFxuICAgIG91dGxpbmU6IG5vbmU7XFxuICAgIGFsaWduLWl0ZW1zOiBjZW50ZXI7XFxuICAgIGN1cnNvcjogcG9pbnRlcjtcXG4gICAgZm9udC1zaXplOiBpbmhlcml0O1xcbiAgXCJdKSksIGFsaWdubWVudCA9PT0gJ2xlZnQnXHJcbiAgICAgICAgPyAnZmxleC1zdGFydCdcclxuICAgICAgICA6IGFsaWdubWVudCA9PT0gJ3JpZ2h0J1xyXG4gICAgICAgICAgICA/ICdmbGV4LWVuZCdcclxuICAgICAgICAgICAgOiAnY2VudGVyJyk7IH0sXHJcbn07XHJcbnZhciB0ZW1wbGF0ZU9iamVjdF8xLCB0ZW1wbGF0ZU9iamVjdF8yLCB0ZW1wbGF0ZU9iamVjdF8zLCB0ZW1wbGF0ZU9iamVjdF80LCB0ZW1wbGF0ZU9iamVjdF81O1xyXG4vLyMgc291cmNlTWFwcGluZ1VSTD1zdHlsZXMuanMubWFwIl19 */";
var _emotionSourceMap$x = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var styles$v = {
  selectContainer: /*#__PURE__*/css(templateObject_1$x || (templateObject_1$x = __makeTemplateObject(["\n    display: flex;\n    justify-content: center;\n    flex-direction: column;\n    width: 100%;\n    position: relative;\n  ;label:selectContainer;" + _emotionSourceMap$x], ["\n    display: flex;\n    justify-content: center;\n    flex-direction: column;\n    width: 100%;\n    position: relative;\n  ;label:selectContainer;" + _emotionSourceMap$x]))),
  selectBody: function (hocs, focusColor, selectHeight, selectWidth) {
    return /*#__PURE__*/css(templateObject_2$j || (templateObject_2$j = __makeTemplateObject(["\n    cursor: pointer;\n    display: flex;\n    align-items: center;\n    justify-content: space-between;\n    height: ", ";\n    width: ", ";\n    padding: 0px 8px;\n    border: none;\n    outline: none;\n    overflow: hidden;\n    &:focus {\n      border: solid 1.5px ", "};\n    }\n  " + _emotionSourceMap2$j], ["\n    cursor: pointer;\n    display: flex;\n    align-items: center;\n    justify-content: space-between;\n    height: ", ";\n    width: ", ";\n    padding: 0px 8px;\n    border: none;\n    outline: none;\n    overflow: hidden;\n    &:focus {\n      border: solid 1.5px ", "};\n    }\n  " + _emotionSourceMap2$j])), "" + (selectHeight || '50px'), "" + (selectWidth || 'auto'), hocs.colors.swatches[focusColor].base);
  },
  selectDropdownContents: function (maxHeight, alignToLeft) {
    return /*#__PURE__*/css(templateObject_3$c || (templateObject_3$c = __makeTemplateObject(["\n    max-height: ", ";\n    overflow-y: auto;\n    position: absolute;\n    left: ", ";\n    transform: ", ";\n  " + _emotionSourceMap3$c], ["\n    max-height: ", ";\n    overflow-y: auto;\n    position: absolute;\n    left: ", ";\n    transform: ", ";\n  " + _emotionSourceMap3$c])), "" + maxHeight || '226px', alignToLeft ? "0" : "50%", alignToLeft ? "initial" : "translateX(-50%)");
  },
  activeOption: function (alignment) {
    return /*#__PURE__*/css(templateObject_4$6 || (templateObject_4$6 = __makeTemplateObject(["\n    width: 100%;\n    height: 100%;\n    display: flex;\n    align-items: center;\n    justify-content: ", ";\n    .hide-on-active {\n      display: none;\n    }\n  " + _emotionSourceMap4$6], ["\n    width: 100%;\n    height: 100%;\n    display: flex;\n    align-items: center;\n    justify-content: ", ";\n    .hide-on-active {\n      display: none;\n    }\n  " + _emotionSourceMap4$6])), alignment === 'left' ? 'flex-start' : alignment === 'right' ? 'flex-end' : 'center');
  },
  option: function (alignment) {
    return /*#__PURE__*/css(templateObject_5$3 || (templateObject_5$3 = __makeTemplateObject(["\n    width: 100%;\n    padding: 12px;\n    border: none;\n    display: flex;\n    justify-content: ", ";\n    outline: none;\n    align-items: center;\n    cursor: pointer;\n    font-size: inherit;\n  " + _emotionSourceMap5$3], ["\n    width: 100%;\n    padding: 12px;\n    border: none;\n    display: flex;\n    justify-content: ", ";\n    outline: none;\n    align-items: center;\n    cursor: pointer;\n    font-size: inherit;\n  " + _emotionSourceMap5$3])), alignment === 'left' ? 'flex-start' : alignment === 'right' ? 'flex-end' : 'center');
  }
};
var templateObject_1$x, templateObject_2$j, templateObject_3$c, templateObject_4$6, templateObject_5$3;

var OptionContent = function (props) {
  var boldTextProp = props.shouldBoldText ? {
    weight: 'bold'
  } : {};
  return jsx(React.Fragment, null, props.type === 'text' ? jsx(Text, _extends({
    content: props.label,
    type: "caption",
    whiteSpace: "nowrap"
  }, boldTextProp)) : props.type === 'graphic' ? jsx(Div, {
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  }, props.label.icon ? jsx(Icon, {
    name: props.label.icon,
    size: "small",
    primaryColor: "default"
  }) : jsx(Div, {
    backgroundImage: props.label.url,
    backgroundPosition: "center",
    width: "24px",
    height: "24px",
    borderRadius: "circle"
  })) : props.type === 'reactNode' ? jsx(Div, {
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  }, props.label) : jsx(Div, {
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  }, props.label.icon ? jsx(Icon, {
    name: props.label.icon,
    size: "small",
    primaryColor: "default"
  }) : jsx(Div, {
    backgroundImage: props.label.url,
    width: "24px",
    height: "24px",
    borderRadius: "circle",
    backgroundPosition: "center"
  }), jsx(Text, _extends({
    inset: {
      left: 'x1'
    },
    content: props.label.text,
    type: "caption",
    whiteSpace: "nowrap"
  }, boldTextProp))));
};

function _EMOTION_STRINGIFIED_CSS_ERROR__() { return "You have tried to stringify object returned from `css` function. It isn't supposed to be used directly (e.g. as value of the `className` prop), but rather handed to emotion so it can handle it (e.g. as value of `css` prop)."; }
var _ref = process.env.NODE_ENV === "production" ? {
  name: "u1zqh4-Option",
  styles: "white-space:nowrap;;label:Option;"
} : {
  name: "u1zqh4-Option",
  styles: "white-space:nowrap;;label:Option;",
  map: "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */",
  toString: _EMOTION_STRINGIFIED_CSS_ERROR__
};
var Option = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var alignment = props.alignment,
    isSelected = props.isSelected,
    onClick = props.onClick,
    id = props.id;
  var _a = useState(false),
    isHover = _a[0],
    setIsHover = _a[1];
  return jsx(Div, {
    ref: ref,
    onHoverStart: function () {
      return setIsHover(true);
    },
    onHoverEnd: function () {
      return setIsHover(false);
    },
    onClick: function () {
      return onClick && onClick(id);
    },
    backgroundColor: isSelected ? 'background' : 'foreground',
    borderRadius: "default",
    css: styles$v.option(alignment)
  }, jsx(Div, {
    animate: isHover ? isSelected ? {
      x: 0
    } : {
      x: 6
    } : {
      x: 0
    },
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    css: _ref
  }, jsx(Span, {
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  }, props.type === 'text' ? jsx(OptionContent, {
    type: props.type,
    label: props.label
  }) : props.type === 'graphicText' ? jsx(OptionContent, {
    type: props.type,
    label: props.label
  }) : props.type === 'reactNode' ? jsx(OptionContent, {
    type: props.type,
    label: props.label
  }) : jsx(OptionContent, {
    type: props.type,
    label: props.label
  }))));
});

var chevronVariants = {
  open: {
    transform: 'rotate(90deg)'
  },
  closed: {
    transform: 'rotate(270deg)'
  }
};
var dropDownVariants = {
  open: {
    opacity: 1
  },
  closed: {
    opacity: 0
  }
};
var selectBodyVariants = {
  open: {
    y: -2
  },
  closed: {
    y: 0
  }
};
var Select = function (props) {
  var alignment = props.alignment,
    onChange = props.onChange,
    selectedOptionIndex = props.selectedOptionIndex,
    dropDownMaxHeight = props.dropDownMaxHeight,
    shouldAlignDropDownToLeft = props.shouldAlignDropDownToLeft,
    selectHeight = props.selectHeight,
    selectWidth = props.selectWidth,
    _a = props.isDropDownAbove,
    isDropDownAbove = _a === void 0 ? false : _a,
    _b = props.focusColor,
    focusColor = _b === void 0 ? 'primaryBrand' : _b,
    message = props.message,
    frontLabel = props.frontLabel,
    _c = props.scrollBarWidth,
    scrollBarWidth = _c === void 0 ? 'auto' : _c;
  var _d = useState(false),
    isOpen = _d[0],
    setIsOpen = _d[1];
  var _e = useState(false),
    isHover = _e[0],
    setIsHover = _e[1];
  var _f = useState(selectedOptionIndex),
    currentSelection = _f[0],
    setCurrentSelection = _f[1];
  var listRef = useRef(null);
  var ref = useRef(null);
  useOutsideClick(ref, function () {
    setIsOpen(false);
  });
  var handleOptionSelection = function (id) {
    setIsOpen(false);
    setCurrentSelection(id);
    onChange(id);
  };
  var _g = useEntities(),
    inputs = _g.inputs,
    hocs = _g.hocs;
  var applyCurrentSelection = function () {
    if (currentSelection !== undefined && currentSelection !== selectedOptionIndex) {
      onChange(currentSelection);
    }
  };
  var focusCurrentSelection = function (newSelection) {
    if (listRef.current) {
      listRef.current.children[newSelection].scrollIntoView({
        behavior: 'auto',
        block: 'center'
      });
    }
  };
  var openOrCloseAndFocus = function (shouldOpen) {
    setIsOpen(shouldOpen);
    if (shouldOpen) {
      if (selectedOptionIndex !== undefined) {
        setTimeout(function () {
          focusCurrentSelection(selectedOptionIndex);
        }, 100);
      }
    }
  };
  var selectAndFocus = function (newSelection) {
    setCurrentSelection(newSelection);
    focusCurrentSelection(newSelection);
  };
  var handleArrows = function (direction) {
    if (!isOpen) {
      openOrCloseAndFocus(true);
    } else if (currentSelection !== undefined && (direction === 1 ? props.options.length > currentSelection + 1 : 0 < currentSelection)) {
      selectAndFocus(currentSelection + direction);
    } else {
      var newDirection = direction === 1 ? 0 : props.options.length - 1;
      selectAndFocus(newDirection);
    }
  };
  var onKeyDown = function (e) {
    if (e.code === 'Space') {
      openOrCloseAndFocus(!isOpen);
      applyCurrentSelection();
    } else if (e.code === 'ArrowDown') {
      handleArrows(1);
    } else if (e.code === 'ArrowUp') {
      handleArrows(-1);
    } else if (isOpen) {
      if (e.code === 'Enter') {
        setIsOpen(false);
        applyCurrentSelection();
      } else if (e.code === 'Escape') {
        setIsOpen(false);
        // Do not apply current selection
        setCurrentSelection(selectedOptionIndex);
      } else if (e.code === 'Tab') {
        setIsOpen(false);
        applyCurrentSelection();
      } else {
        var index = props.options.findIndex(function (s) {
          return new RegExp(e.key, 'i').test(s.label[0]);
        });
        if (index > -1) {
          selectAndFocus(index);
        }
      }
    }
  };
  return jsx("div", {
    css: styles$v.selectContainer,
    ref: ref,
    onKeyDown: onKeyDown
  }, jsx(Div, {
    onHoverStart: function () {
      return setIsHover(true);
    },
    onHoverEnd: function () {
      return setIsHover(false);
    },
    variants: selectBodyVariants,
    initial: isOpen ? 'open' : 'closed',
    animate: isOpen ? 'open' : 'closed',
    whileHover: {
      y: -2
    },
    css: styles$v.selectBody(hocs, focusColor, selectHeight, selectWidth),
    onClick: function () {
      openOrCloseAndFocus(!isOpen);
      if (currentSelection !== selectedOptionIndex) {
        isOpen ? applyCurrentSelection() : setCurrentSelection(selectedOptionIndex);
      }
    },
    borderRadius: "default",
    backgroundColor: isHover || isOpen ? 'foreground' : 'background',
    boxShadow: isHover || isOpen ? inputs.select.boxShadow : 'none',
    tabIndex: 0,
    style: {
      border: message ? inputs.text.borderWidth + " solid " + hocs.colors.swatches[message.type].base : ''
    }
  }, frontLabel && jsx(Text, {
    type: "caption",
    outset: {
      horizontal: 'x1'
    },
    content: frontLabel
  }), jsx("div", {
    css: styles$v.activeOption(alignment)
  }, selectedOptionIndex !== undefined && props.options[selectedOptionIndex] !== undefined ? jsx("div", null, props.type === 'text' ? jsx(OptionContent, {
    type: props.type,
    label: props.options[selectedOptionIndex].label,
    shouldBoldText: !!frontLabel
  }) : props.type === 'graphicText' ? jsx(OptionContent, {
    type: props.type,
    label: props.options[selectedOptionIndex].label,
    shouldBoldText: !!frontLabel
  }) : props.type === 'reactNode' ? jsx(OptionContent, {
    type: props.type,
    label: props.options[selectedOptionIndex].label,
    shouldBoldText: !!frontLabel
  }) : jsx(OptionContent, {
    type: props.type,
    label: props.options[selectedOptionIndex].label,
    shouldBoldText: !!frontLabel
  })) : jsx(Text, {
    content: 'Select',
    type: "caption"
  })), jsx(Div, {
    inset: {
      left: 'x0'
    }
  }, jsx(Span, {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    variants: chevronVariants,
    initial: isOpen ? 'open' : 'closed',
    animate: isOpen ? 'open' : 'closed'
  }, jsx(Icon, {
    size: "xSmall",
    name: "chevron",
    primaryColor: "default",
    orientation: 'left'
  })))), message && jsx(HStack, {
    gap: "x_5",
    justify: "flex-start"
  }, jsx(Text, {
    inset: {
      left: 'x1',
      top: 'x_5'
    },
    content: message.content,
    type: "footnote",
    color: message.type
  }), message.icon && jsx(Span, {
    inset: {
      top: 'x_25'
    },
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    onClick: message.onClick,
    cursor: "pointer"
  }, jsx(Icon, {
    name: message.icon,
    primaryColor: message.type,
    size: "xSmall"
  }))), jsx(AnimatePresence, null, isOpen && jsx(Div, {
    exit: 'closed',
    initial: 'closed',
    animate: isOpen ? 'open' : 'closed',
    variants: dropDownVariants,
    css: styles$v.selectDropdownContents(dropDownMaxHeight, shouldAlignDropDownToLeft),
    backgroundColor: inputs.select.dropDownBackgroundColor,
    borderRadius: inputs.select.borderRadius,
    boxShadow: inputs.select.dropDownShadow,
    top: !isDropDownAbove ? '50px' : undefined,
    bottom: isDropDownAbove ? '50px' : undefined,
    zIndex: 50000,
    tabIndex: -1,
    style: {
      scrollbarWidth: scrollBarWidth
    }
  }, jsx(List, {
    orientation: "vertical",
    alignment: alignment || 'center',
    inset: "x1",
    ref: listRef
  }, props.type === 'reactNode' && props.options.map(function (option, index) {
    return jsx(React.Fragment, null, jsx(Option, {
      id: index,
      isSelected: currentSelection === index ? true : false,
      alignment: alignment,
      onClick: function (id) {
        return handleOptionSelection(id);
      },
      key: index,
      type: props.type,
      label: option.label
    }), option.hasSeparator && jsx(Separator, {
      orientation: "horizontal"
    }));
  }), props.type === 'text' && props.options.map(function (option, index) {
    return jsx(React.Fragment, null, jsx(Option, {
      id: index,
      isSelected: currentSelection === index ? true : false,
      alignment: alignment,
      onClick: function (id) {
        return handleOptionSelection(id);
      },
      key: index,
      type: props.type,
      label: option.label
    }), option.hasSeparator && jsx(Separator, {
      orientation: "horizontal"
    }));
  }), props.type === 'graphicText' && props.options.map(function (option, index) {
    return jsx(Option, {
      id: index,
      isSelected: selectedOptionIndex === index ? true : false,
      alignment: alignment,
      onClick: function (id) {
        return handleOptionSelection(id);
      },
      key: index,
      type: props.type,
      label: option.label
    });
  }), props.type === 'graphic' && props.options.map(function (option, index) {
    return jsx(Option, {
      id: index,
      isSelected: selectedOptionIndex === index ? true : false,
      alignment: alignment,
      onClick: function (id) {
        return handleOptionSelection(id);
      },
      key: index,
      type: props.type,
      label: option.label
    });
  })))));
};

var _emotionSourceMap$w = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFHeUIiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgX19tYWtlVGVtcGxhdGVPYmplY3QgfSBmcm9tIFwidHNsaWJcIjtcclxuaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vY29yZSc7XHJcbmV4cG9ydCBkZWZhdWx0IHtcclxuICAgIHRyYW5zaXRpb25Db250YWluZXI6IGNzcyh0ZW1wbGF0ZU9iamVjdF8xIHx8ICh0ZW1wbGF0ZU9iamVjdF8xID0gX19tYWtlVGVtcGxhdGVPYmplY3QoW1wiXFxuICAgIHdpZHRoOiAxMDAlO1xcbiAgXCJdLCBbXCJcXG4gICAgd2lkdGg6IDEwMCU7XFxuICBcIl0pKSksXHJcbn07XHJcbnZhciB0ZW1wbGF0ZU9iamVjdF8xO1xyXG4vLyMgc291cmNlTWFwcGluZ1VSTD1zdHlsZXMuanMubWFwIl19 */";
var styles$u = {
  transitionContainer: /*#__PURE__*/css(templateObject_1$w || (templateObject_1$w = __makeTemplateObject(["\n    width: 100%;\n  ;label:transitionContainer;" + _emotionSourceMap$w], ["\n    width: 100%;\n  ;label:transitionContainer;" + _emotionSourceMap$w])))
};
var templateObject_1$w;

var Transition = function (_a) {
  var title = _a.title,
    subtitle = _a.subtitle,
    isLoading = _a.isLoading,
    loadedStatus = _a.loadedStatus;
  return jsx("div", {
    css: styles$u.transitionContainer
  }, jsx(Div, {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    inset: {
      bottom: 'x2'
    }
  }, isLoading ? jsx(LoadingSpinner, {
    size: 'xLarge'
  }) : loadedStatus === 'success' ? jsx(AnimatedCheckMark, {
    filled: true,
    size: 'xLarge'
  }) : jsx(Div, {
    width: "84px",
    height: "84px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderWidth: "default",
    borderColor: {
      swatch: 'danger',
      shade: 'base'
    },
    borderStyle: "solid",
    borderRadius: "circle"
  }, jsx(Icon, {
    name: "exclamation",
    primaryColor: "danger",
    size: "xLarge"
  }))), jsx(Flex, {
    flexDirection: "column",
    alignItems: "center"
  }, jsx(Text, {
    type: "body",
    color: "primaryBody",
    content: title,
    outset: 'x0',
    alignment: "center",
    inset: {
      bottom: 'x1'
    }
  }), jsx(Text, {
    type: loadedStatus === 'error' ? 'body' : 'largeBody',
    color: "primaryHeading",
    content: subtitle,
    alignment: "center"
  })));
};

var _emotionSourceMap4$5 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap3$b = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFLMEMiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgX19tYWtlVGVtcGxhdGVPYmplY3QgfSBmcm9tIFwidHNsaWJcIjtcclxuaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vY29yZSc7XHJcbmV4cG9ydCBkZWZhdWx0IHtcclxuICAgIGFwcDogY3NzKHRlbXBsYXRlT2JqZWN0XzEgfHwgKHRlbXBsYXRlT2JqZWN0XzEgPSBfX21ha2VUZW1wbGF0ZU9iamVjdChbXCJcXG4gICAgZm9udC1mYW1pbHk6IHNhbnMtc2VyaWY7XFxuICAgIHRleHQtYWxpZ246IGNlbnRlcjtcXG4gIFwiXSwgW1wiXFxuICAgIGZvbnQtZmFtaWx5OiBzYW5zLXNlcmlmO1xcbiAgICB0ZXh0LWFsaWduOiBjZW50ZXI7XFxuICBcIl0pKSksXHJcbiAgICBjaGVja2JveENvbnRhaW5lcjogZnVuY3Rpb24gKGlucHV0c0VudGl0eSwgdHlwZSkgeyByZXR1cm4gY3NzKHRlbXBsYXRlT2JqZWN0XzIgfHwgKHRlbXBsYXRlT2JqZWN0XzIgPSBfX21ha2VUZW1wbGF0ZU9iamVjdChbXCJcXG4gICAgZGlzcGxheTogZmxleDtcXG4gICAganVzdGlmeS1jb250ZW50OiBjZW50ZXI7XFxuICAgIGFsaWduLWl0ZW1zOiBjZW50ZXI7XFxuICAgIGlucHV0W3R5cGU9J2NoZWNrYm94J10ge1xcbiAgICAgIG9wYWNpdHk6IDA7XFxuICAgIH1cXG4gICAgbGFiZWwge1xcbiAgICAgIGRpc3BsYXk6IGZsZXg7XFxuICAgICAgYWxpZ24taXRlbXM6IGNlbnRlcjtcXG4gICAgICBjdXJzb3I6IHBvaW50ZXI7XFxuICAgIH1cXG4gICAgaW5wdXRbdHlwZT0nY2hlY2tib3gnXTpjaGVja2VkICsgbGFiZWwge1xcbiAgICAgID4gZGl2IHtcXG4gICAgICAgIGJvcmRlcjogXCIsIFwiIHNvbGlkXFxuICAgICAgICAgIFwiLCBcIjtcXG4gICAgICAgIHRyYW5zaXRpb246IGJveC1zaGFkb3cgMC4zcyAwLjA1cywgYm9yZGVyIDAuM3MgMC4xNHM7XFxuICAgICAgICBzcGFuIHtcXG4gICAgICAgICAgYmFja2dyb3VuZDogXCIsIFwiO1xcbiAgICAgICAgICB0cmFuc2Zvcm06IHNjYWxlKDEuMSk7XFxuICAgICAgICAgIHRyYW5zaXRpb246IGFsbCAwLjNzO1xcbiAgICAgICAgfVxcbiAgICAgIH1cXG4gICAgfVxcbiAgXCJdLCBbXCJcXG4gICAgZGlzcGxheTogZmxleDtcXG4gICAganVzdGlmeS1jb250ZW50OiBjZW50ZXI7XFxuICAgIGFsaWduLWl0ZW1zOiBjZW50ZXI7XFxuICAgIGlucHV0W3R5cGU9J2NoZWNrYm94J10ge1xcbiAgICAgIG9wYWNpdHk6IDA7XFxuICAgIH1cXG4gICAgbGFiZWwge1xcbiAgICAgIGRpc3BsYXk6IGZsZXg7XFxuICAgICAgYWxpZ24taXRlbXM6IGNlbnRlcjtcXG4gICAgICBjdXJzb3I6IHBvaW50ZXI7XFxuICAgIH1cXG4gICAgaW5wdXRbdHlwZT0nY2hlY2tib3gnXTpjaGVja2VkICsgbGFiZWwge1xcbiAgICAgID4gZGl2IHtcXG4gICAgICAgIGJvcmRlcjogXCIsIFwiIHNvbGlkXFxuICAgICAgICAgIFwiLCBcIjtcXG4gICAgICAgIHRyYW5zaXRpb246IGJveC1zaGFkb3cgMC4zcyAwLjA1cywgYm9yZGVyIDAuM3MgMC4xNHM7XFxuICAgICAgICBzcGFuIHtcXG4gICAgICAgICAgYmFja2dyb3VuZDogXCIsIFwiO1xcbiAgICAgICAgICB0cmFuc2Zvcm06IHNjYWxlKDEuMSk7XFxuICAgICAgICAgIHRyYW5zaXRpb246IGFsbCAwLjNzO1xcbiAgICAgICAgfVxcbiAgICAgIH1cXG4gICAgfVxcbiAgXCJdKSksIGlucHV0c0VudGl0eS5jaGVja2JveC5ib3JkZXIsIGlucHV0c0VudGl0eS5jb2xvcnNbdHlwZV0sIGlucHV0c0VudGl0eS5jb2xvcnNbdHlwZV0pOyB9LFxyXG4gICAgYm94OiBmdW5jdGlvbiAoaW5wdXRzRW50aXR5KSB7IHJldHVybiBjc3ModGVtcGxhdGVPYmplY3RfMyB8fCAodGVtcGxhdGVPYmplY3RfMyA9IF9fbWFrZVRlbXBsYXRlT2JqZWN0KFtcIlxcbiAgICBkaXNwbGF5OiBpbmxpbmUtYmxvY2s7XFxuICAgIHdpZHRoOiAyNHB4O1xcbiAgICBoZWlnaHQ6IDI0cHg7XFxuICAgIGJvcmRlcjogXCIsIFwiIHNvbGlkXFxuICAgICAgXCIsIFwiO1xcbiAgICBib3JkZXItcmFkaXVzOiBcIiwgXCI7XFxuICAgIG1hcmdpbi1yaWdodDogOHB4O1xcbiAgICBvdmVyZmxvdzogdmlzaWJsZTtcXG4gICAgJjpob3ZlciB7XFxuICAgICAgYmFja2dyb3VuZDogXCIsIFwiO1xcbiAgICB9XFxuICBcIl0sIFtcIlxcbiAgICBkaXNwbGF5OiBpbmxpbmUtYmxvY2s7XFxuICAgIHdpZHRoOiAyNHB4O1xcbiAgICBoZWlnaHQ6IDI0cHg7XFxuICAgIGJvcmRlcjogXCIsIFwiIHNvbGlkXFxuICAgICAgXCIsIFwiO1xcbiAgICBib3JkZXItcmFkaXVzOiBcIiwgXCI7XFxuICAgIG1hcmdpbi1yaWdodDogOHB4O1xcbiAgICBvdmVyZmxvdzogdmlzaWJsZTtcXG4gICAgJjpob3ZlciB7XFxuICAgICAgYmFja2dyb3VuZDogXCIsIFwiO1xcbiAgICB9XFxuICBcIl0pKSwgaW5wdXRzRW50aXR5LmNoZWNrYm94LmJvcmRlciwgaW5wdXRzRW50aXR5LmNoZWNrYm94LnVuQ2hlY2tlZEJhY2tncm91bmRDb2xvciwgaW5wdXRzRW50aXR5LmNoZWNrYm94LmJvcmRlclJhZGl1cywgaW5wdXRzRW50aXR5LmNoZWNrYm94LnVuQ2hlY2tlZEJhY2tncm91bmRDb2xvcik7IH0sXHJcbiAgICBib3hJbm5lcjogY3NzKHRlbXBsYXRlT2JqZWN0XzQgfHwgKHRlbXBsYXRlT2JqZWN0XzQgPSBfX21ha2VUZW1wbGF0ZU9iamVjdChbXCJcXG4gICAgd2lkdGg6IDEwMCU7XFxuICAgIGhlaWdodDogMTAwJTtcXG4gICAgdHJhbnNmb3JtOiBzY2FsZSgwLjIpO1xcbiAgICBkaXNwbGF5OiBmbGV4O1xcbiAgICBqdXN0aWZ5LWNvbnRlbnQ6IGNlbnRlcjtcXG4gICAgYWxpZ24taXRlbXM6IGNlbnRlcjtcXG4gICAgYm9yZGVyLXJhZGl1czogaW5oZXJpdDtcXG4gICAgYmFja2dyb3VuZC1jb2xvcjogdHJhbnNwYXJlbnQ7XFxuICAgIHRyYW5zaXRpb246IHRyYW5zZm9ybSAwLjJzIGVhc2UtaW4tb3V0LCBiYWNrZ3JvdW5kLWNvbG9yIDAuMnMgZWFzZS1pbi1vdXQ7XFxuICBcIl0sIFtcIlxcbiAgICB3aWR0aDogMTAwJTtcXG4gICAgaGVpZ2h0OiAxMDAlO1xcbiAgICB0cmFuc2Zvcm06IHNjYWxlKDAuMik7XFxuICAgIGRpc3BsYXk6IGZsZXg7XFxuICAgIGp1c3RpZnktY29udGVudDogY2VudGVyO1xcbiAgICBhbGlnbi1pdGVtczogY2VudGVyO1xcbiAgICBib3JkZXItcmFkaXVzOiBpbmhlcml0O1xcbiAgICBiYWNrZ3JvdW5kLWNvbG9yOiB0cmFuc3BhcmVudDtcXG4gICAgdHJhbnNpdGlvbjogdHJhbnNmb3JtIDAuMnMgZWFzZS1pbi1vdXQsIGJhY2tncm91bmQtY29sb3IgMC4ycyBlYXNlLWluLW91dDtcXG4gIFwiXSkpKSxcclxufTtcclxudmFyIHRlbXBsYXRlT2JqZWN0XzEsIHRlbXBsYXRlT2JqZWN0XzIsIHRlbXBsYXRlT2JqZWN0XzMsIHRlbXBsYXRlT2JqZWN0XzQ7XHJcbi8vIyBzb3VyY2VNYXBwaW5nVVJMPXN0eWxlcy5qcy5tYXAiXX0= */";
var _emotionSourceMap2$i = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap$v = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var styles$t = {
  app: /*#__PURE__*/css(templateObject_1$v || (templateObject_1$v = __makeTemplateObject(["\n    font-family: sans-serif;\n    text-align: center;\n  ;label:app;" + _emotionSourceMap$v], ["\n    font-family: sans-serif;\n    text-align: center;\n  ;label:app;" + _emotionSourceMap$v]))),
  checkboxContainer: function (inputsEntity, type) {
    return /*#__PURE__*/css(templateObject_2$i || (templateObject_2$i = __makeTemplateObject(["\n    display: flex;\n    justify-content: center;\n    align-items: center;\n    input[type='checkbox'] {\n      opacity: 0;\n    }\n    label {\n      display: flex;\n      align-items: center;\n      cursor: pointer;\n    }\n    input[type='checkbox']:checked + label {\n      > div {\n        border: ", " solid\n          ", ";\n        transition: box-shadow 0.3s 0.05s, border 0.3s 0.14s;\n        span {\n          background: ", ";\n          transform: scale(1.1);\n          transition: all 0.3s;\n        }\n      }\n    }\n  " + _emotionSourceMap2$i], ["\n    display: flex;\n    justify-content: center;\n    align-items: center;\n    input[type='checkbox'] {\n      opacity: 0;\n    }\n    label {\n      display: flex;\n      align-items: center;\n      cursor: pointer;\n    }\n    input[type='checkbox']:checked + label {\n      > div {\n        border: ", " solid\n          ", ";\n        transition: box-shadow 0.3s 0.05s, border 0.3s 0.14s;\n        span {\n          background: ", ";\n          transform: scale(1.1);\n          transition: all 0.3s;\n        }\n      }\n    }\n  " + _emotionSourceMap2$i])), inputsEntity.checkbox.border, inputsEntity.colors[type], inputsEntity.colors[type]);
  },
  box: function (inputsEntity) {
    return /*#__PURE__*/css(templateObject_3$b || (templateObject_3$b = __makeTemplateObject(["\n    display: inline-block;\n    width: 24px;\n    height: 24px;\n    border: ", " solid\n      ", ";\n    border-radius: ", ";\n    margin-right: 8px;\n    overflow: visible;\n    &:hover {\n      background: ", ";\n    }\n  " + _emotionSourceMap3$b], ["\n    display: inline-block;\n    width: 24px;\n    height: 24px;\n    border: ", " solid\n      ", ";\n    border-radius: ", ";\n    margin-right: 8px;\n    overflow: visible;\n    &:hover {\n      background: ", ";\n    }\n  " + _emotionSourceMap3$b])), inputsEntity.checkbox.border, inputsEntity.checkbox.unCheckedBackgroundColor, inputsEntity.checkbox.borderRadius, inputsEntity.checkbox.unCheckedBackgroundColor);
  },
  boxInner: /*#__PURE__*/css(templateObject_4$5 || (templateObject_4$5 = __makeTemplateObject(["\n    width: 100%;\n    height: 100%;\n    transform: scale(0.2);\n    display: flex;\n    justify-content: center;\n    align-items: center;\n    border-radius: inherit;\n    background-color: transparent;\n    transition: transform 0.2s ease-in-out, background-color 0.2s ease-in-out;\n  ;label:boxInner;" + _emotionSourceMap4$5], ["\n    width: 100%;\n    height: 100%;\n    transform: scale(0.2);\n    display: flex;\n    justify-content: center;\n    align-items: center;\n    border-radius: inherit;\n    background-color: transparent;\n    transition: transform 0.2s ease-in-out, background-color 0.2s ease-in-out;\n  ;label:boxInner;" + _emotionSourceMap4$5])))
};
var templateObject_1$v, templateObject_2$i, templateObject_3$b, templateObject_4$5;

var Checkbox = function (_a) {
  var isChecked = _a.isChecked,
    onChange = _a.onChange,
    _b = _a.type,
    type = _b === void 0 ? 'primary' : _b,
    label = _a.label,
    _c = _a.id,
    id = _c === void 0 ? 'cb' : _c;
  var tickVariants = {
    unchecked: {
      pathLength: 0
    },
    checked: {
      pathLength: 1,
      transition: {
        delay: 0.15,
        duration: 0.3,
        ease: [0.65, 0, 0.45, 1]
      }
    }
  };
  var pathLength = useMotionValue(1);
  var opacity = useTransform(pathLength, [0.15, 0.25], [0, 1]);
  var inputs = useEntities().inputs;
  return jsx("div", {
    css: styles$t.checkboxContainer(inputs, type)
  }, jsx("input", {
    checked: isChecked,
    onChange: function () {
      return onChange(!isChecked);
    },
    type: "checkbox",
    id: id
  }), jsx("label", {
    htmlFor: id
  }, jsx("div", {
    css: styles$t.box(inputs)
  }, jsx("span", {
    css: styles$t.boxInner
  }, jsx(motion.svg, {
    width: '24px',
    height: '24px',
    viewBox: "0 0 26 26",
    animate: isChecked ? 'checked' : 'unchecked',
    initial: isChecked ? 'checked' : 'unchecked'
  }, jsx(motion.path, {
    d: "M 0 0 L 4 4 L 12 -6 ",
    transform: "translate(7, 14)",
    fill: "none",
    stroke: inputs.checkbox.checkmarkColor,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    strokeWidth: "2",
    variants: tickVariants,
    style: {
      pathLength: pathLength,
      opacity: opacity
    }
  })))), jsx("span", null, label && jsx(Text, {
    type: "caption",
    content: label
  }))));
};

var _emotionSourceMap$u = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFHMEQiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgX19tYWtlVGVtcGxhdGVPYmplY3QgfSBmcm9tIFwidHNsaWJcIjtcclxuaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vY29yZSc7XHJcbmV4cG9ydCBkZWZhdWx0IHtcclxuICAgIHNjcm9sbGFibGVDb250YWluZXI6IGZ1bmN0aW9uIChoYXNTY3JvbGxCYXIpIHsgcmV0dXJuIGNzcyh0ZW1wbGF0ZU9iamVjdF8xIHx8ICh0ZW1wbGF0ZU9iamVjdF8xID0gX19tYWtlVGVtcGxhdGVPYmplY3QoW1wiXFxuICAgIDo6LXdlYmtpdC1zY3JvbGxiYXIge1xcbiAgICAgIGRpc3BsYXk6IFwiLCBcIjtcXG4gICAgfVxcbiAgXCJdLCBbXCJcXG4gICAgOjotd2Via2l0LXNjcm9sbGJhciB7XFxuICAgICAgZGlzcGxheTogXCIsIFwiO1xcbiAgICB9XFxuICBcIl0pKSwgaGFzU2Nyb2xsQmFyID8gJ2F1dG8nIDogJ25vbmUnKTsgfSxcclxufTtcclxudmFyIHRlbXBsYXRlT2JqZWN0XzE7XHJcbi8vIyBzb3VyY2VNYXBwaW5nVVJMPXN0eWxlcy5qcy5tYXAiXX0= */";
var styles$s = {
  scrollableContainer: function (hasScrollBar) {
    return /*#__PURE__*/css(templateObject_1$u || (templateObject_1$u = __makeTemplateObject(["\n    ::-webkit-scrollbar {\n      display: ", ";\n    }\n  " + _emotionSourceMap$u], ["\n    ::-webkit-scrollbar {\n      display: ", ";\n    }\n  " + _emotionSourceMap$u])), hasScrollBar ? 'auto' : 'none');
  }
};
var templateObject_1$u;

var Scrollable = function (_a) {
  var maxHeight = _a.maxHeight,
    onChange = _a.onChange,
    children = _a.children,
    _b = _a.hasScrollBar,
    hasScrollBar = _b === void 0 ? true : _b;
  var ref = useRef(null);
  var elementScroll = useElementScroll(ref);
  var scrollYProgress = (process.env.NODE_ENV === 'test' ? {
    scrollYProgress: {
      onChange: function () {},
      get: function () {
        return 0;
      }
    }
  } : elementScroll).scrollYProgress;
  var handleOnChange = function (scrollYPosition) {
    onChange && onChange(scrollYPosition);
  };
  scrollYProgress.onChange(function () {
    return handleOnChange(scrollYProgress.get());
  });
  return jsx(Div, {
    borderRadius: "default",
    backgroundColor: "foreground",
    ref: ref,
    maxHeight: maxHeight + "px",
    width: "100%",
    inset: {
      bottom: 'x2'
    },
    overflow: "auto",
    css: styles$s.scrollableContainer(hasScrollBar)
  }, children);
};

var _emotionSourceMap4$4 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap3$a = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap2$h = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap$t = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var styles$r = {
  imageContainer: function (isActive, width, height) {
    return /*#__PURE__*/css(templateObject_1$t || (templateObject_1$t = __makeTemplateObject(["\n    p {\n      position: relative;\n      z-index: 100;\n      font-weight: ", ";\n      color: ", ";\n    }\n    display: flex;\n    justify-content: center;\n    align-items: center;\n    width: ", ";\n    height: ", ";\n  " + _emotionSourceMap$t], ["\n    p {\n      position: relative;\n      z-index: 100;\n      font-weight: ", ";\n      color: ", ";\n    }\n    display: flex;\n    justify-content: center;\n    align-items: center;\n    width: ", ";\n    height: ", ";\n  " + _emotionSourceMap$t])), isActive ? 500 : 400, isActive ? '#FF05A7' : '#3c3c3c', width, height);
  },
  imageCss: function (isActive) {
    return /*#__PURE__*/css(templateObject_2$h || (templateObject_2$h = __makeTemplateObject(["\n    width: 100%;\n    height: 100%;\n    transition: all 0.1s ease;\n    position: relative;\n    transform: ", ";\n    transition-timing-function: cubic-bezier(0.785, 0.135, 0.15, 0.86);\n    cursor: pointer;\n    > div {\n      position: absolute;\n      width: 100%;\n      height: 100%;\n      margin: 0;\n      padding: 0;\n      border-radius: inherit;\n    }\n  " + _emotionSourceMap2$h], ["\n    width: 100%;\n    height: 100%;\n    transition: all 0.1s ease;\n    position: relative;\n    transform: ", ";\n    transition-timing-function: cubic-bezier(0.785, 0.135, 0.15, 0.86);\n    cursor: pointer;\n    > div {\n      position: absolute;\n      width: 100%;\n      height: 100%;\n      margin: 0;\n      padding: 0;\n      border-radius: inherit;\n    }\n  " + _emotionSourceMap2$h])), isActive ? "scale(1)" : "scale(.88)");
  },
  image: function (image) {
    return /*#__PURE__*/css(templateObject_3$a || (templateObject_3$a = __makeTemplateObject(["\n    background-repeat: no-repeat;\n    background-position: center;\n    width: 100%;\n    height: 100%;\n    border-radius: inherit;\n    position: absolute;\n    left: 0;\n    top: 0;\n    border-radius: inherit;\n    background-image: url(", ");\n    transition-timing-function: cubic-bezier(0.785, 0.135, 0.15, 0.86);\n    &::before {\n      content: '';\n      background: inherit;\n      width: 100%;\n      height: 100%;\n      display: block;\n      z-index: 1;\n      position: absolute;\n      top: 16px;\n      transform: scale(0.9);\n      filter: blur(10px);\n      opacity: 0.88;\n      border-radius: inherit;\n    }\n    &::after {\n      content: '';\n      background: inherit;\n      width: 100%;\n      height: 100%;\n      display: block;\n      z-index: 2;\n      position: absolute;\n      border-radius: inherit;\n    }\n  " + _emotionSourceMap3$a], ["\n    background-repeat: no-repeat;\n    background-position: center;\n    width: 100%;\n    height: 100%;\n    border-radius: inherit;\n    position: absolute;\n    left: 0;\n    top: 0;\n    border-radius: inherit;\n    background-image: url(", ");\n    transition-timing-function: cubic-bezier(0.785, 0.135, 0.15, 0.86);\n    &::before {\n      content: '';\n      background: inherit;\n      width: 100%;\n      height: 100%;\n      display: block;\n      z-index: 1;\n      position: absolute;\n      top: 16px;\n      transform: scale(0.9);\n      filter: blur(10px);\n      opacity: 0.88;\n      border-radius: inherit;\n    }\n    &::after {\n      content: '';\n      background: inherit;\n      width: 100%;\n      height: 100%;\n      display: block;\n      z-index: 2;\n      position: absolute;\n      border-radius: inherit;\n    }\n  " + _emotionSourceMap3$a])), image);
  },
  filter: function (filter) {
    return /*#__PURE__*/css(templateObject_4$4 || (templateObject_4$4 = __makeTemplateObject(["\n    filter: ", ";\n    transition-timing-function: cubic-bezier(0.785, 0.135, 0.15, 0.86);\n    &:before {\n      content: '';\n      border-radius: inherit;\n      display: block;\n      height: 100%;\n      width: 100%;\n      top: 0;\n      left: 0;\n      position: absolute;\n      pointer-events: none;\n      mix-blend-mode: ", ";\n      opacity: ", ";\n      background: ", ";\n    }\n  " + _emotionSourceMap4$4], ["\n    filter: ", ";\n    transition-timing-function: cubic-bezier(0.785, 0.135, 0.15, 0.86);\n    &:before {\n      content: '';\n      border-radius: inherit;\n      display: block;\n      height: 100%;\n      width: 100%;\n      top: 0;\n      left: 0;\n      position: absolute;\n      pointer-events: none;\n      mix-blend-mode: ", ";\n      opacity: ", ";\n      background: ", ";\n    }\n  " + _emotionSourceMap4$4])), filter.filter, filter.mixBlendMode || 'initial', filter.opacity || 'initial', filter.background || 'initial');
  }
};
var templateObject_1$t, templateObject_2$h, templateObject_3$a, templateObject_4$4;

var getNumberFromString = function (str) {
  var parsedStr;
  if (str.endsWith('px')) {
    parsedStr = str.substring(0, str.length - 2);
  } else if (str.endsWith('%')) {
    parsedStr = str.substring(0, str.length - 1);
  } else {
    parsedStr = str;
  }
  return parseInt(parsedStr);
};

var ImageLoader = function (_a) {
  var width = _a.width,
    height = _a.height,
    isActive = _a.isActive,
    borderRadius = _a.borderRadius;
  var hocs = useEntities().hocs;
  var radius = hocs.borderRadius[borderRadius];
  var scaledWidth = isActive ? width : width * 0.88;
  var scaledHeight = isActive ? height : height * 0.88;
  return jsx(Loader, {
    width: scaledWidth,
    height: scaledHeight
  }, jsx("rect", {
    width: scaledWidth,
    height: scaledHeight,
    rx: getNumberFromString(radius),
    ry: getNumberFromString(radius)
  }));
};

var useProgressiveImage = function (src) {
  var _a = React.useState(null),
    source = _a[0],
    setSource = _a[1];
  useEffect(function () {
    var img = new Image();
    img.src = src;
    img.onload = function () {
      return setSource(src);
    };
  }, [src]);
  return source;
};

var Image$1 = function (_a) {
  var image = _a.image,
    _b = _a.activeFilter,
    activeFilter = _b === void 0 ? {
      name: 'none',
      filter: 'initial',
      background: 'initial'
    } : _b,
    imageWidth = _a.width,
    imageHeight = _a.height,
    isActive = _a.isActive,
    title = _a.title,
    _c = _a.borderRadius,
    borderRadius = _c === void 0 ? 'default' : _c,
    onClick = _a.onClick,
    _d = _a.backgroundSize,
    backgroundSize = _d === void 0 ? 'cover' : _d;
  var _e = useResizeObserver(),
    ref = _e.ref,
    width = _e.width,
    height = _e.height;
  var loaderRadius = isBorderRadiusDirectionType(borderRadius) ? 'default' : borderRadius;
  var src = useProgressiveImage(image.url);
  return jsx(Div, {
    css: styles$r.imageContainer(isActive, imageWidth, imageHeight),
    ref: ref
  }, typeof src === 'string' ? jsx(Div, {
    css: styles$r.imageCss(isActive),
    onClick: onClick && function () {
      return onClick();
    },
    borderRadius: borderRadius
  }, jsx(Div, {
    css: styles$r.filter(activeFilter)
  }, jsx(Div, {
    backgroundSize: backgroundSize,
    css: styles$r.image(image.url)
  }))) : typeof width === 'number' && typeof height === 'number' && jsx(ImageLoader, {
    width: width,
    height: height,
    isActive: isActive,
    borderRadius: loaderRadius
  }), jsx(Text, {
    type: "body"
  }, title));
};

var _emotionSourceMap$s = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var mediaQueries = entities$9.mediaQueries;
var styles$q = {
  closeButtonContainer: function (top, right) {
    return /*#__PURE__*/css(templateObject_1$s || (templateObject_1$s = __makeTemplateObject(["\n    top: ", ";\n    right: ", ";\n    transform: translate(0px, 0px);\n    transition: transform 0.25s ease;\n    z-index: 10;\n    border-radius: 20px;\n    :hover {\n      transform: translate(-2px, 2px);\n      transition: transform 0.25s ease;\n    }\n    @media screen and (max-width: ", ") {\n      border-radius: 11px;\n      top: ", ";\n      right: ", ";\n    }\n  " + _emotionSourceMap$s], ["\n    top: ", ";\n    right: ", ";\n    transform: translate(0px, 0px);\n    transition: transform 0.25s ease;\n    z-index: 10;\n    border-radius: 20px;\n    :hover {\n      transform: translate(-2px, 2px);\n      transition: transform 0.25s ease;\n    }\n    @media screen and (max-width: ", ") {\n      border-radius: 11px;\n      top: ", ";\n      right: ", ";\n    }\n  " + _emotionSourceMap$s])), top !== null && top !== void 0 ? top : '-23px', right !== null && right !== void 0 ? right : '-23px', mediaQueries.rawValues.small + "px", top !== null && top !== void 0 ? top : '7px', right !== null && right !== void 0 ? right : '7px');
  }
};
var templateObject_1$s;

var CloseButton = function (_a) {
  var onClose = _a.onClose,
    backgroundColor = _a.backgroundColor,
    fill = _a.fill,
    top = _a.top,
    right = _a.right,
    size = _a.size,
    id = _a.id;
  return jsx(Span, {
    id: id ? id.toLowerCase() + "-close-button" : undefined,
    onClick: onClose,
    css: styles$q.closeButtonContainer(top, right),
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    inset: {
      xSmall: 'x2',
      small: 'x3'
    },
    boxShadow: 'mediumLight',
    backgroundColor: backgroundColor !== null && backgroundColor !== void 0 ? backgroundColor : '#424242e0',
    position: "absolute",
    cursor: "pointer"
  }, jsx(Icon, {
    name: 'close',
    primaryColor: fill !== null && fill !== void 0 ? fill : 'inverseHeadingText',
    size: size !== null && size !== void 0 ? size : 16
  }));
};

var ContentLoaderComponent = process.env.NODE_ENV === 'test' ? function () {
  return jsx(React.Fragment, null);
} : ContentLoader;
var Loader = function (_a) {
  var children = _a.children,
    width = _a.width,
    height = _a.height,
    _b = _a.speed,
    speed = _b === void 0 ? 2 : _b;
  return jsx(ContentLoaderComponent, {
    speed: speed,
    width: width,
    height: height,
    viewBox: "0 0 " + width + " " + height
  }, children);
};

var _emotionSourceMap3$9 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap2$g = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap$r = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var styles$p = {
  drawerContainer: function (location, drawerEntity) {
    return /*#__PURE__*/css(templateObject_1$r || (templateObject_1$r = __makeTemplateObject(["\n    display: flex;\n    position: absolute;\n    left: ", ";\n    right: ", ";\n    top: ", ";\n    bottom: ", ";\n    background: ", ";\n  " + _emotionSourceMap$r], ["\n    display: flex;\n    position: absolute;\n    left: ", ";\n    right: ", ";\n    top: ", ";\n    bottom: ", ";\n    background: ", ";\n  " + _emotionSourceMap$r])), location === 'left' && 0, location === 'right' && 0, location !== 'bottom' && 0, location === 'bottom' && 0, drawerEntity.backgroundColor);
  },
  childContainer: function (shouldHideScroll) {
    return /*#__PURE__*/css(templateObject_2$g || (templateObject_2$g = __makeTemplateObject(["\n    ::-webkit-scrollbar {\n      width: ", ";\n      background: ", ";\n    }\n    scrollbar-width: ", ";\n  " + _emotionSourceMap2$g], ["\n    ::-webkit-scrollbar {\n      width: ", ";\n      background: ", ";\n    }\n    scrollbar-width: ", ";\n  " + _emotionSourceMap2$g])), shouldHideScroll ? '0px' : undefined, shouldHideScroll ? 'transparent' : undefined, shouldHideScroll ? 'none' : 'initial');
  },
  drawerTab: function (location, openValue) {
    return /*#__PURE__*/css(templateObject_3$9 || (templateObject_3$9 = __makeTemplateObject(["\n    display: flex;\n    justify-content: center;\n    align-content: center;\n    align-self: center;\n    align-items: center;\n    position: absolute;\n    left: ", ";\n    right: ", ";\n    top: ", ";\n    bottom: ", ";\n  " + _emotionSourceMap3$9], ["\n    display: flex;\n    justify-content: center;\n    align-content: center;\n    align-self: center;\n    align-items: center;\n    position: absolute;\n    left: ", ";\n    right: ", ";\n    top: ", ";\n    bottom: ", ";\n  " + _emotionSourceMap3$9])), location === 'left' && openValue + "px", location === 'right' && openValue + "px", location === 'top' && openValue + "px", location === 'bottom' && openValue + "px");
  }
};
var templateObject_1$r, templateObject_2$g, templateObject_3$9;

var Drawer = function (_a) {
  var _b;
  var _c, _d;
  var children = _a.children,
    location = _a.location,
    isOpen = _a.isOpen,
    _e = _a.isTabHidden,
    isTabHidden = _e === void 0 ? false : _e,
    onClick = _a.onClick,
    openValue = _a.openValue,
    _f = _a.offset,
    offset = _f === void 0 ? 0 : _f,
    _g = _a.zIndex,
    zIndex = _g === void 0 ? 0 : _g,
    _h = _a.showTab,
    showTab = _h === void 0 ? true : _h,
    inset = _a.inset,
    _j = _a.overflow,
    overflow = _j === void 0 ? 'auto' : _j,
    closedValue = _a.closedValue,
    _k = _a.tabMode,
    tabMode = _k === void 0 ? 'light' : _k,
    _l = _a.shoudHideScroll,
    shoudHideScroll = _l === void 0 ? false : _l;
  var drawer = useEntities().drawer;
  var isHorizontal = location === 'left' || location === 'right';
  var isVertical = location === 'top' || location === 'bottom';
  var getClosedPoint = function (location) {
    if (location === 'left' || location === 'top') {
      return closedValue ? -openValue + closedValue : -openValue;
    }
    return closedValue ? openValue - closedValue : openValue;
  };
  var getRotationPoints = function (location) {
    switch (location) {
      case 'left':
        return [180, 360];
      case 'right':
        return [0, -180];
      case 'top':
        return [270, 450];
      case 'bottom':
        return [90, 270];
    }
  };
  var handleXY = useMotionValue(getClosedPoint(location));
  var opacity = useTransform(handleXY, [getClosedPoint(location), 0], isTabHidden ? [0, 1] : [1, 1]);
  var rotate = useTransform(handleXY, [getClosedPoint(location), 0], getRotationPoints(location));
  var variants = {
    open: {
      x: 0,
      y: 0
    },
    closed: function (isHorizontal) {
      if (isHorizontal) {
        return {
          x: getClosedPoint(location)
        };
      }
      return {
        y: getClosedPoint(location)
      };
    }
  };
  return jsx(Div, {
    css: styles$p.drawerContainer(location, drawer),
    width: isHorizontal ? openValue + "px" : '100%',
    height: isVertical ? openValue + "px" : '100%',
    flexDirection: isVertical ? 'column' : 'row',
    variants: variants,
    custom: isHorizontal,
    initial: isOpen ? 'open' : 'closed',
    animate: isOpen ? 'open' : 'closed',
    transition: softSpringTransition,
    outset: (_b = {}, _b[location] = offset + "px", _b),
    inset: inset !== null && inset !== void 0 ? inset : drawer.defaultInset,
    zIndex: zIndex,
    style: {
      x: isHorizontal ? handleXY : 0,
      y: isVertical ? handleXY : 0
    },
    boxShadow: location === 'bottom' ? drawer.bottomDirectionShadow : drawer.boxShadow,
    whileHover: {
      boxShadow: location === 'bottom' ? drawer.hoverBottomDirectionShadow : drawer.hoverBoxShadow
    }
  }, jsx(Div, {
    css: styles$p.childContainer(shoudHideScroll),
    overflow: overflow,
    height: "100%",
    width: "100%"
  }, children), showTab && jsx(Div, {
    backgroundColor: drawer.tab[tabMode].backgroundColor,
    css: styles$p.drawerTab(location, openValue),
    borderRadius: location === 'left' ? {
      right: drawer.borderRadius
    } : location === 'right' ? {
      left: drawer.borderRadius
    } : location === 'top' ? {
      bottom: drawer.borderRadius
    } : {
      top: drawer.borderRadius
    },
    onClick: isTabHidden && !isOpen ? undefined : function () {
      return onClick();
    },
    cursor: isTabHidden && !isOpen ? undefined : 'pointer',
    zIndex: zIndex - 1,
    style: {
      opacity: opacity
    },
    height: isVertical ? '18px' : '58px',
    width: isVertical ? '58px' : '18px',
    boxShadow: location === 'bottom' ? drawer.bottomDirectionShadow : drawer.boxShadow,
    transition: {
      ease: 'easeIn',
      duration: 0.2
    },
    whileHover: {
      scale: 1.15
    }
  }, jsx(Span, {
    style: {
      rotate: rotate
    },
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  }, jsx(Icon, {
    name: "chevron",
    orientation: "left",
    primaryColor: (_d = (_c = drawer.tab[tabMode]) === null || _c === void 0 ? void 0 : _c.arrowColor) !== null && _d !== void 0 ? _d : 'default',
    size: "small"
  }))));
};

var _emotionSourceMap3$8 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFLNkUiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgX19tYWtlVGVtcGxhdGVPYmplY3QgfSBmcm9tIFwidHNsaWJcIjtcclxuaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vY29yZSc7XHJcbmV4cG9ydCBkZWZhdWx0IHtcclxuICAgIHNsaWRlclByb2dyZXNzOiBmdW5jdGlvbiAoaXNEaXNhYmxlZCkgeyByZXR1cm4gY3NzKHRlbXBsYXRlT2JqZWN0XzEgfHwgKHRlbXBsYXRlT2JqZWN0XzEgPSBfX21ha2VUZW1wbGF0ZU9iamVjdChbXCJcXG4gICAgb3BhY2l0eTogXCIsIFwiO1xcbiAgICBoZWlnaHQ6IDEwMCU7XFxuICAgIHdpZHRoOiAxMDAlO1xcbiAgXCJdLCBbXCJcXG4gICAgb3BhY2l0eTogXCIsIFwiO1xcbiAgICBoZWlnaHQ6IDEwMCU7XFxuICAgIHdpZHRoOiAxMDAlO1xcbiAgXCJdKSksIGlzRGlzYWJsZWQgPyAwLjUgOiAxKTsgfSxcclxuICAgIHNsaWRlckJhcjogZnVuY3Rpb24gKHNsaWRlckVudGl0eSkgeyByZXR1cm4gY3NzKHRlbXBsYXRlT2JqZWN0XzIgfHwgKHRlbXBsYXRlT2JqZWN0XzIgPSBfX21ha2VUZW1wbGF0ZU9iamVjdChbXCJcXG4gICAgd2lkdGg6IDEwMCU7XFxuICAgIGhlaWdodDogNnB4O1xcbiAgICBib3JkZXItcmFkaXVzOiBcIiwgXCI7XFxuICAgIGJhY2tncm91bmQ6IFwiLCBcIjtcXG4gICAgb3ZlcmZsb3c6IGhpZGRlbjtcXG4gIFwiXSwgW1wiXFxuICAgIHdpZHRoOiAxMDAlO1xcbiAgICBoZWlnaHQ6IDZweDtcXG4gICAgYm9yZGVyLXJhZGl1czogXCIsIFwiO1xcbiAgICBiYWNrZ3JvdW5kOiBcIiwgXCI7XFxuICAgIG92ZXJmbG93OiBoaWRkZW47XFxuICBcIl0pKSwgc2xpZGVyRW50aXR5LmJvcmRlclJhZGl1cywgc2xpZGVyRW50aXR5LmJhY2tncm91bmRDb2xvcik7IH0sXHJcbiAgICBzbGlkZXJIYW5kbGU6IGZ1bmN0aW9uIChpc0Rpc2FibGVkLCBzbGlkZXJFbnRpdHksIGhhbmRsZUhlaWdodCkgeyByZXR1cm4gY3NzKHRlbXBsYXRlT2JqZWN0XzMgfHwgKHRlbXBsYXRlT2JqZWN0XzMgPSBfX21ha2VUZW1wbGF0ZU9iamVjdChbXCJcXG4gICAgd2lkdGg6IFwiLCBcIjtcXG4gICAgaGVpZ2h0OiBcIiwgXCI7XFxuICAgIGJhY2tncm91bmQ6IFwiLCBcIjtcXG4gICAgYm9yZGVyLXJhZGl1czogXCIsIFwiO1xcbiAgICBib3gtc2hhZG93OiBcIiwgXCI7XFxuICAgIHBvc2l0aW9uOiBhYnNvbHV0ZTtcXG4gICAgbGVmdDogMHB4O1xcbiAgICBjdXJzb3I6IFwiLCBcIjtcXG4gIFwiXSwgW1wiXFxuICAgIHdpZHRoOiBcIiwgXCI7XFxuICAgIGhlaWdodDogXCIsIFwiO1xcbiAgICBiYWNrZ3JvdW5kOiBcIiwgXCI7XFxuICAgIGJvcmRlci1yYWRpdXM6IFwiLCBcIjtcXG4gICAgYm94LXNoYWRvdzogXCIsIFwiO1xcbiAgICBwb3NpdGlvbjogYWJzb2x1dGU7XFxuICAgIGxlZnQ6IDBweDtcXG4gICAgY3Vyc29yOiBcIiwgXCI7XFxuICBcIl0pKSwgaGFuZGxlSGVpZ2h0ICsgXCJweFwiLCBoYW5kbGVIZWlnaHQgKyBcInB4XCIsIHNsaWRlckVudGl0eS5oYW5kbGVDb2xvciwgc2xpZGVyRW50aXR5LmhhbmRsZVJhZGl1cywgc2xpZGVyRW50aXR5LmhhbmRsZVNoYWRvdywgaXNEaXNhYmxlZCA/ICdub3QtYWxsb3dlZCcgOiAncG9pbnRlcicpOyB9LFxyXG59O1xyXG52YXIgdGVtcGxhdGVPYmplY3RfMSwgdGVtcGxhdGVPYmplY3RfMiwgdGVtcGxhdGVPYmplY3RfMztcclxuLy8jIHNvdXJjZU1hcHBpbmdVUkw9c3R5bGVzLmpzLm1hcCJdfQ== */";
var _emotionSourceMap2$f = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap$q = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var styles$o = {
  sliderProgress: function (isDisabled) {
    return /*#__PURE__*/css(templateObject_1$q || (templateObject_1$q = __makeTemplateObject(["\n    opacity: ", ";\n    height: 100%;\n    width: 100%;\n  " + _emotionSourceMap$q], ["\n    opacity: ", ";\n    height: 100%;\n    width: 100%;\n  " + _emotionSourceMap$q])), isDisabled ? 0.5 : 1);
  },
  sliderBar: function (sliderEntity) {
    return /*#__PURE__*/css(templateObject_2$f || (templateObject_2$f = __makeTemplateObject(["\n    width: 100%;\n    height: 6px;\n    border-radius: ", ";\n    background: ", ";\n    overflow: hidden;\n  " + _emotionSourceMap2$f], ["\n    width: 100%;\n    height: 6px;\n    border-radius: ", ";\n    background: ", ";\n    overflow: hidden;\n  " + _emotionSourceMap2$f])), sliderEntity.borderRadius, sliderEntity.backgroundColor);
  },
  sliderHandle: function (isDisabled, sliderEntity, handleHeight) {
    return /*#__PURE__*/css(templateObject_3$8 || (templateObject_3$8 = __makeTemplateObject(["\n    width: ", ";\n    height: ", ";\n    background: ", ";\n    border-radius: ", ";\n    box-shadow: ", ";\n    position: absolute;\n    left: 0px;\n    cursor: ", ";\n  " + _emotionSourceMap3$8], ["\n    width: ", ";\n    height: ", ";\n    background: ", ";\n    border-radius: ", ";\n    box-shadow: ", ";\n    position: absolute;\n    left: 0px;\n    cursor: ", ";\n  " + _emotionSourceMap3$8])), handleHeight + "px", handleHeight + "px", sliderEntity.handleColor, sliderEntity.handleRadius, sliderEntity.handleShadow, isDisabled ? 'not-allowed' : 'pointer');
  }
};
var templateObject_1$q, templateObject_2$f, templateObject_3$8;

var Slider = function (_a) {
  var value = _a.value,
    min = _a.min,
    max = _a.max,
    setValue = _a.setValue,
    _b = _a.activeColor,
    activeColor = _b === void 0 ? 'primaryBrand' : _b,
    _c = _a.isDisabled,
    isDisabled = _c === void 0 ? false : _c,
    _d = _a.origin,
    origin = _d === void 0 ? 'left' : _d,
    width = _a.width;
  var _e = useEntities(),
    slider = _e.slider,
    icons = _e.icons;
  var handleHeight = 18;
  var containerRef = useRef(null);
  var widthOffset = width - handleHeight;
  var range = max - min;
  var getInitialValue = useCallback(function (value) {
    var verifiedInitial = value <= min || value > max ? min : value;
    var percentage = (verifiedInitial - min) / range;
    return widthOffset * percentage;
  }, [max, min, range, widthOffset]);
  var handleX = useMotionValue(getInitialValue(value));
  var progressScaleX = useTransform(handleX, [0, width - handleHeight + 2], [0, 1]);
  var progressScaleXLeft = useTransform(handleX, [0, width / 2], [1, 0]);
  var progressScaleXRight = useTransform(handleX, [width, width / 2], [1, 0]);
  var constraintsRef = useRef(null);
  var getValueFromPercentage = useCallback(function (percentage) {
    return min + range * percentage;
  }, [min, range]);
  var getPositionPercentage = useCallback(function (currentPosition) {
    return currentPosition / widthOffset;
  }, [widthOffset]);
  var handleDrag = useCallback(function () {
    var val = Math.round(getValueFromPercentage(getPositionPercentage(handleX.get())));
    setValue(val);
  }, [setValue, getPositionPercentage, handleX, getValueFromPercentage]);
  useEffect(function () {
    if (typeof width === 'number') {
      handleX.set(getInitialValue(value));
    }
  }, [value, handleX, getInitialValue, width]);
  useEffect(function () {
    if (typeof width === 'number') {
      handleDrag();
    }
  }, [handleDrag, value, width]);
  return jsx(React.Fragment, null, jsx(Div, {
    width: width + "px",
    height: '40px',
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    position: "relative",
    ref: containerRef
  }, jsx(Div, {
    css: styles$o.sliderBar(slider),
    ref: constraintsRef,
    display: "flex",
    flexDirection: "row"
  }, origin === 'center' ? jsx(React.Fragment, null, jsx(Div, {
    css: styles$o.sliderProgress(isDisabled),
    backgroundColor: icons.colors[activeColor],
    style: {
      scaleX: progressScaleXLeft,
      position: 'relative',
      left: '2px'
    },
    transformOrigin: "100% 50%"
  }), jsx(Div, {
    css: styles$o.sliderProgress(isDisabled),
    backgroundColor: icons.colors[activeColor],
    style: {
      scaleX: progressScaleXRight,
      position: 'relative',
      left: '2px'
    },
    transformOrigin: "0% 50%"
  })) : jsx(Div, {
    css: styles$o.sliderProgress(isDisabled),
    backgroundColor: icons.colors[activeColor],
    style: {
      scaleX: progressScaleX
    },
    transformOrigin: "0% 50%"
  })), jsx(Div, {
    css: styles$o.sliderHandle(isDisabled, slider, handleHeight),
    drag: isDisabled ? undefined : 'x',
    onDrag: handleDrag,
    onDragEnd: handleDrag,
    dragConstraints: constraintsRef,
    dragElastic: 0,
    dragMomentum: false,
    style: {
      x: handleX
    },
    whileDrag: {
      scale: 1.2
    }
  })));
};

var _emotionSourceMap3$7 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap2$e = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap$p = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var styles$n = {
  table: function (rowCount) {
    return /*#__PURE__*/css(templateObject_1$p || (templateObject_1$p = __makeTemplateObject(["\n    display: grid;\n    grid-template-rows: repeat(", ");\n  " + _emotionSourceMap$p], ["\n    display: grid;\n    grid-template-rows: repeat(", ");\n  " + _emotionSourceMap$p])), rowCount);
  },
  tableRow: function (columns, breakPoint) {
    return /*#__PURE__*/css(templateObject_2$e || (templateObject_2$e = __makeTemplateObject(["\n    display: grid;\n    grid-template-columns: repeat(1, 1fr);\n    @media (min-width: ", "px) {\n      grid-template-columns: repeat(", ", 1fr);\n    }\n  " + _emotionSourceMap2$e], ["\n    display: grid;\n    grid-template-columns: repeat(1, 1fr);\n    @media (min-width: ", "px) {\n      grid-template-columns: repeat(", ", 1fr);\n    }\n  " + _emotionSourceMap2$e])), breakPoint, columns);
  },
  rowItem: function (alignment) {
    return /*#__PURE__*/css(templateObject_3$7 || (templateObject_3$7 = __makeTemplateObject(["\n    display: grid;\n    justify-items: ", ";\n    align-items: center;\n    padding: ", ";\n    width: 100%;\n    height: 100%;\n  " + _emotionSourceMap3$7], ["\n    display: grid;\n    justify-items: ", ";\n    align-items: center;\n    padding: ", ";\n    width: 100%;\n    height: 100%;\n  " + _emotionSourceMap3$7])), alignment === 'left' ? 'start' : alignment === 'right' ? 'end' : 'center', alignment === 'left' ? '0 0 0 8px' : alignment === 'right' ? '0 8px 0 0' : 'initial');
  }
};
var templateObject_1$p, templateObject_2$e, templateObject_3$7;

var TableRow = function (_a) {
  var columnCount = _a.columnCount,
    _b = _a.inset,
    inset = _b === void 0 ? {
      horizontal: 'x2',
      vertical: 'x1_5'
    } : _b,
    _c = _a.outset,
    outset = _c === void 0 ? {
      top: 'x1',
      bottom: 'x2'
    } : _c,
    _d = _a.radius,
    radius = _d === void 0 ? 16 : _d,
    _e = _a.backgroundColor,
    backgroundColor = _e === void 0 ? '#eef0f2' : _e,
    children = _a.children,
    _f = _a.border,
    border = _f === void 0 ? 'none' : _f;
  var mediaQueries = useEntities().mediaQueries;
  return jsx(Div, {
    inset: inset,
    outset: outset,
    style: {
      backgroundColor: backgroundColor,
      borderRadius: radius,
      borderBottom: border
    },
    css: styles$n.tableRow(columnCount, mediaQueries.rawValues.small)
  }, children);
};

var RowItem = function (_a) {
  var _b = _a.alignment,
    alignment = _b === void 0 ? 'center' : _b,
    children = _a.children,
    _c = _a.inset,
    inset = _c === void 0 ? 'x1' : _c;
  return jsx(Div, {
    css: styles$n.rowItem(alignment),
    inset: inset
  }, children);
};

var Table = function (_a) {
  var columnCount = _a.columnCount,
    rowCount = _a.rowCount,
    headers = _a.headers,
    _b = _a.headerAlignment,
    headerAlignment = _b === void 0 ? 'left' : _b,
    _c = _a.headerWeight,
    headerWeight = _c === void 0 ? 'semiBold' : _c,
    _d = _a.headerInset,
    headerInset = _d === void 0 ? {
      vertical: 'x1',
      bottom: 'x2'
    } : _d,
    _e = _a.headerOutset,
    headerOutset = _e === void 0 ? {
      bottom: 'x1'
    } : _e,
    _f = _a.headerBorder,
    headerBorder = _f === void 0 ? '1.5px solid #ECECEC' : _f,
    _g = _a.headerTextColor,
    headerTextColor = _g === void 0 ? '#6B7280' : _g,
    children = _a.children;
  return jsx(Div, {
    css: styles$n.table(rowCount)
  }, headers && jsx(Div, {
    display: {
      xSmall: 'none',
      small: 'inline-block'
    }
  }, jsx(TableRow, {
    backgroundColor: "transparent",
    radius: 0,
    inset: headerInset,
    outset: headerOutset,
    columnCount: columnCount,
    border: headerBorder
  }, headers === null || headers === void 0 ? void 0 : headers.map(function (header, index) {
    return jsx(RowItem, {
      key: header + " " + index,
      alignment: headerAlignment,
      inset: headerInset
    }, jsx(Text, {
      type: "body",
      content: header,
      weight: headerWeight,
      style: {
        color: headerTextColor
      }
    }));
  }))), children);
};

var Tabs = function (_a) {
  var height = _a.height,
    defaultActiveKey = _a.defaultActiveKey,
    _b = _a.activeColor,
    activeColor = _b === void 0 ? 'primaryBrand' : _b,
    _c = _a.gap,
    gap = _c === void 0 ? 'x2' : _c,
    _d = _a.justify,
    justify = _d === void 0 ? 'flex-start' : _d,
    children = _a.children;
  var tabChildren = React.Children.toArray(children);
  var tabKeys = tabChildren.map(function (tabChild) {
    return tabChild.props.tabKey;
  });
  var ref = useRef(null);
  var hocs = useEntities().hocs;
  var _e = useState(tabKeys.indexOf(defaultActiveKey) || 0),
    activeTabKeyIndex = _e[0],
    setActiveTabKeyIndex = _e[1];
  var handleOnDragEnd = function (info) {
    info.offset.x < 0 ? setActiveTabKeyIndex(activeTabKeyIndex === tabKeys.length - 1 ? tabKeys.length - 1 : activeTabKeyIndex + 1) : setActiveTabKeyIndex(activeTabKeyIndex === 0 ? 0 : activeTabKeyIndex - 1);
  };
  return jsx(AnimateSharedLayout, null, jsx(Div, {
    display: "flex",
    flexDirection: "column",
    ref: ref
  }, jsx(HStack, {
    gap: gap,
    justify: justify
  }, tabChildren.map(function (child, i) {
    var isActive = tabKeys[activeTabKeyIndex] === child.props.tabKey;
    return jsx(Flex, {
      flexDirection: "column",
      key: i
    }, jsx(Div, {
      onClick: function () {
        return setActiveTabKeyIndex(i);
      },
      cursor: "pointer",
      layout: true
    }, jsx(Text, {
      type: isActive ? 'caption' : 'footnote',
      color: isActive ? activeColor : 'primaryBody'
    }, child.props.title)), isActive && jsx(Div, {
      height: "x_25",
      width: "100%",
      backgroundColor: hocs.textColors[activeColor],
      borderRadius: "default",
      layoutId: "underline"
    }));
  })), jsx(Spacer, {
    space: "x2"
  }), jsx(Flex, {
    width: "100%",
    height: height,
    drag: "x",
    onDragEnd: function (_, info) {
      return handleOnDragEnd(info);
    },
    dragConstraints: ref,
    overflow: "auto"
  }, tabChildren[activeTabKeyIndex])));
};

var Tab = function (_a) {
  var children = _a.children;
  return jsx(Div, {
    width: "100%"
  }, children);
};

var getSize = function (size) {
  switch (size) {
    case 'xSmall':
      return 'x3';
    case 'small':
      return 'x4';
    case 'medium':
      return 'x5';
    case 'large':
      return 'x6';
    case 'xLarge':
      return 'x7';
    default:
      return 'x5';
  }
};
var ColorOption = function (_a) {
  var hexColor = _a.hexColor,
    _b = _a.size,
    size = _b === void 0 ? 'medium' : _b,
    onClick = _a.onClick;
  var colorOption = useEntities().colorOption;
  var shade = Color$1(hexColor).luminosity();
  var shadow = colorOption.defaults.circleFill;
  return jsx(Flex, {
    borderRadius: "circle",
    width: getSize(size),
    height: getSize(size),
    justifyContent: "center",
    alignItems: "center",
    onClick: function () {
      return onClick();
    }
  }, jsx(Div, {
    borderRadius: "circle",
    backgroundColor: hexColor,
    height: "33%",
    width: "33%",
    boxShadow: shade > 0.9 ? shadow : undefined
  }));
};

var Stoplight = function (_a) {
  var activeIndex = _a.activeIndex,
    setActiveIndex = _a.setActiveIndex,
    _b = _a.isExpanded,
    isExpanded = _b === void 0 ? true : _b,
    setIsExpanded = _a.setIsExpanded,
    type = _a.type,
    onClick = _a.onClick,
    _c = _a.orientation,
    orientation = _c === void 0 ? 'vertical' : _c,
    _d = _a.isCollapsible,
    isCollapsible = _d === void 0 ? false : _d,
    children = _a.children;
  var stoplightChildren = React.Children.toArray(children);
  var stoplightRef = useRef(null);
  var colorOption = useEntities().colorOption;
  var computeBorderRadiusFromSize = function (size) {
    switch (size) {
      case 'xSmall':
        return '12px';
      case 'small':
        return '16px';
      case 'medium':
        return '20px';
      case 'large':
        return '24px';
      case 'xLarge':
        return '28px';
      default:
        return '20px';
    }
  };
  var isIconLargeOrXL = stoplightChildren[0].props.size === 'large' || stoplightChildren[0].props.size === 'xLarge';
  var handleClick = function (index) {
    setActiveIndex(index);
    onClick === null || onClick === void 0 ? void 0 : onClick();
    isCollapsible && setIsExpanded && setIsExpanded(true);
  };
  useOutsideClick(stoplightRef, function () {
    isCollapsible && setIsExpanded && setIsExpanded(false);
  });
  return jsx(AnimateSharedLayout, null, jsx(Div, {
    style: {
      borderRadius: computeBorderRadiusFromSize(stoplightChildren[0].props.size)
    },
    backgroundColor: "foreground",
    boxShadow: "light",
    height: "100%",
    width: "100%",
    display: "flex",
    position: "relative",
    alignItems: orientation === 'vertical' ? 'space-between' : 'center',
    justifyContent: orientation === 'vertical' ? 'center' : 'space-between',
    flexDirection: orientation === 'vertical' ? 'column' : 'row',
    cursor: "pointer",
    ref: stoplightRef,
    layout: true
  }, isExpanded ? jsx(AnimatePresence, null, stoplightChildren.map(function (child, index) {
    var _a = child.props,
      hexColor = _a.hexColor,
      size = _a.size;
    var shade = Color$1(hexColor).luminosity();
    var shadow = "inset " + colorOption.defaults.innerRing + ", " + colorOption.defaults.outerRing;
    return jsx(Flex, {
      onClick: function () {
        handleClick(index);
      },
      inset: type === 'icons' ? isIconLargeOrXL ? 'x1' : 'x_75' : 'x0',
      alignItems: "center",
      justifyContent: "center",
      height: getSize(size),
      width: getSize(size),
      flexDirection: "column",
      initial: isCollapsible ? {
        opacity: 0
      } : {
        opacity: 1
      },
      animate: {
        opacity: 1
      },
      exit: {
        opacity: 0
      },
      key: index,
      layout: true
    }, child, type === 'colors' && activeIndex === index && jsx(Div, {
      height: getSize(size),
      width: getSize(size),
      layoutId: "circle",
      position: "absolute",
      borderRadius: computeBorderRadiusFromSize(size),
      borderStyle: "solid",
      borderWidth: "default",
      borderColor: hexColor,
      boxShadow: shade > 0.9 ? shadow : undefined
    }));
  })) : jsx(Flex, {
    onClick: function () {
      return setIsExpanded && setIsExpanded(true);
    },
    alignItems: "center",
    justifyContent: "center",
    width: getSize(stoplightChildren[0].props.size),
    height: getSize(stoplightChildren[0].props.size),
    inset: type === 'icons' ? isIconLargeOrXL ? 'x1' : 'x_75' : 'x0',
    borderRadius: computeBorderRadiusFromSize(stoplightChildren[0].props.size),
    layout: true
  }, stoplightChildren[activeIndex])));
};

var InfiniteScroller = function (_a) {
  var pageStart = _a.pageStart,
    children = _a.children,
    loader = _a.loader,
    loadMore = _a.loadMore,
    hasMore = _a.hasMore,
    _b = _a.threshold,
    threshold = _b === void 0 ? 250 : _b,
    _c = _a.initialLoad,
    initialLoad = _c === void 0 ? false : _c,
    isLoadingMore = _a.isLoadingMore,
    _d = _a.isScrollingOnWindow,
    isScrollingOnWindow = _d === void 0 ? false : _d;
  var handleLoadMore = function (page) {
    loadMore(page);
  };
  return jsx(InfiniteScroll, {
    useWindow: isScrollingOnWindow,
    initialLoad: initialLoad,
    pageStart: pageStart,
    threshold: threshold,
    loadMore: handleLoadMore,
    hasMore: hasMore
  }, jsx(React.Fragment, null, children, isLoadingMore && loader));
};

var MasonryGrid = function (_a) {
  var _b = _a.columnSize,
    columnSize = _b === void 0 ? 100 : _b,
    _c = _a.rowSize,
    rowSize = _c === void 0 ? 100 : _c,
    _d = _a.gap,
    gap = _d === void 0 ? 16 : _d;
  return jsx(Div, {
    width: "100%",
    height: "100%"
  }, jsx(Grid, {
    maxWidth: 800,
    columnSize: columnSize,
    rowSize: rowSize,
    gap: gap,
    isDense: false
  }, jsx(GridItem, {
    gridColSpan: 3,
    gridColStart: 2
  }, jsx(Div, {
    height: "100%",
    width: "100%",
    borderRadius: "default",
    backgroundColor: {
      swatch: 'primaryBrand',
      shade: 'base'
    }
  })), jsx(GridItem, {
    gridColSpan: 3
  }, jsx(Div, {
    height: "100%",
    width: "100%",
    borderRadius: "default",
    backgroundColor: {
      swatch: 'secondaryBrand',
      shade: 'base'
    }
  })), jsx(GridItem, {
    gridColSpan: 3
  }, jsx(Div, {
    height: "100%",
    width: "100%",
    borderRadius: "default",
    backgroundColor: {
      swatch: 'accent',
      shade: 'base'
    }
  })), jsx(GridItem, {
    gridColSpan: 3
  }, jsx(Div, {
    height: "100%",
    width: "100%",
    borderRadius: "default",
    backgroundColor: {
      swatch: 'success',
      shade: 'base'
    }
  })), jsx(GridItem, {
    gridColSpan: 3,
    gridColStart: 2
  }, jsx(Div, {
    height: "100%",
    width: "100%",
    borderRadius: "default",
    backgroundColor: {
      swatch: 'warning',
      shade: 'base'
    }
  })), jsx(GridItem, {
    gridColSpan: 3
  }, jsx(Div, {
    height: "100%",
    width: "100%",
    borderRadius: "default",
    backgroundColor: {
      swatch: 'danger',
      shade: 'base'
    }
  }))));
};

var BlockQuote = function (_a) {
  var author = _a.author,
    _b = _a.background,
    background = _b === void 0 ? 'background' : _b,
    _c = _a.textColor,
    textColor = _c === void 0 ? 'primaryBody' : _c,
    _d = _a.type,
    type = _d === void 0 ? 'primary' : _d,
    content = _a.content,
    _e = _a.contentTextType,
    contentTextType = _e === void 0 ? 'largeBody' : _e,
    _f = _a.borderRadius,
    borderRadius = _f === void 0 ? 'none' : _f,
    boxShadow = _a.boxShadow,
    children = _a.children;
  var hocs = useEntities().hocs;
  var mapTypeToColor = function (type) {
    switch (type) {
      case 'primary':
        return 'primaryBrand';
      case 'secondary':
        return 'secondaryBrand';
      case 'warning':
        return 'warning';
      case 'success':
        return 'success';
      case 'danger':
        return 'danger';
      default:
        return 'primaryBrand';
    }
  };
  return jsx(Flex, {
    style: {
      borderLeft: "8px solid " + hocs.colors.swatches[mapTypeToColor(type)].base
    },
    height: "100%",
    width: "100%",
    backgroundColor: background,
    inset: {
      vertical: 'x2',
      horizontal: 'x3'
    },
    borderRadius: borderRadius,
    boxShadow: boxShadow
  }, jsx(Flex, {
    height: "100%",
    inset: {
      vertical: 'x2'
    }
  }, jsx(Icon, {
    name: "quotation",
    primaryColor: mapTypeToColor(type),
    size: "medium"
  })), jsx(Flex, {
    width: "100%",
    height: "100%",
    flexDirection: "column",
    inset: "x3",
    justifyContent: "space-between",
    alignItems: "space-between"
  }, jsx(Text, {
    type: contentTextType,
    color: textColor,
    isItalic: true
  }, content !== null && content !== void 0 ? content : children), author && jsx(React.Fragment, null, jsx(Spacer, {
    orientation: "vertical",
    space: "x3"
  }), jsx(Text, {
    type: "subtitle",
    weight: "extraBold",
    isItalic: true
  }, author))));
};

var GreenerStillLogo = function (_a) {
  var color = _a.color,
    width = _a.width;
  return jsx(React.Fragment, null, color === 'color' ? jsx("svg", {
    version: "1.1",
    id: "Layer_1",
    xmlns: "http://www.w3.org/2000/svg",
    x: "0px",
    y: "0px",
    viewBox: "0 0 683.7 125.5",
    width: width
  }, jsx("g", null, jsx("path", {
    fill: "#FFFFFF",
    d: "M7.4,74.4c0-0.2-0.1-0.3-0.1-0.5C7.4,74.1,7.4,74.2,7.4,74.4z"
  }), jsx("path", {
    fill: "#FFFFFF",
    d: "M6.6,68.9c0-0.4-0.1-0.7-0.1-1.1C6.5,68.2,6.5,68.5,6.6,68.9z"
  }), jsx("path", {
    fill: "#FFFFFF",
    d: "M7,71.7c0-0.3-0.1-0.6-0.1-0.9C6.9,71.2,6.9,71.5,7,71.7z"
  }), jsx("path", {
    fill: "#FFFFFF",
    d: "M6.3,66c0-0.5-0.1-0.9-0.1-1.4C6.3,65,6.3,65.5,6.3,66z"
  }), jsx("g", null, jsx("path", {
    fill: "#00CC00",
    d: "M122.9,62.7c0-0.4,0-0.8,0-1.3c0-0.5,0-0.9,0-1.4c0-0.6-0.1-1.2-0.1-1.8c0-0.2,0-0.5-0.1-0.7 c-0.9-10.4-4.5-20.3-10.4-28.8c0-0.1-0.1-0.1-0.1-0.2c-0.4-0.6-0.8-1.1-1.3-1.7c-0.2-0.3-0.5-0.6-0.7-0.9c-0.3-0.3-0.6-0.7-0.8-1 c-0.5-0.6-1-1.1-1.5-1.7c-0.1-0.1-0.1-0.1-0.2-0.2C99.2,13.6,87.6,7,74.5,4.8c-3.3,1.7-6.2,3.6-8.5,5.7c-2.5,3.9-3.8,8.2-3.2,12.8 c1.4,10.6,13,18.4,23.1,24.2c2.3,1.3,4.5,2.5,6.4,3.6c6.8,3.8,13.8,7.6,19.8,12.2c4,3,7.3,6.5,9.7,10.5c0.5-2.7,0.9-5.5,1.1-8.4 c0-0.2,0-0.5,0-0.7C122.9,64.1,122.9,63.4,122.9,62.7z"
  })), jsx("g", null, jsx("path", {
    fill: "#23B00B",
    d: "M6.4,67.8c-0.1-0.6-0.1-1.2-0.1-1.8C6.3,66.6,6.4,67.2,6.4,67.8z"
  })), jsx("g", null, jsx("path", {
    fill: "#23B00B",
    d: "M6.8,70.9c-0.1-0.7-0.2-1.3-0.3-2C6.6,69.6,6.7,70.2,6.8,70.9z"
  })), jsx("g", null, jsx("path", {
    fill: "#23B00B",
    d: "M15.2,93.6c0.1,0,0.1,0,0.2,0.1c-3.7-5.8-6.4-12.3-7.9-19.3C8.9,81.2,11.5,87.7,15.2,93.6z"
  })), jsx("g", null, jsx("path", {
    fill: "#23B00B",
    d: "M7.3,73.9c-0.1-0.7-0.3-1.5-0.4-2.2C7.1,72.5,7.2,73.2,7.3,73.9z"
  })), jsx("g", null, jsx("path", {
    fill: "#FFFFFF",
    d: "M110.5,90.1c-0.4-7.5-3.9-14.6-9.7-20.7c-5-5.4-11.2-10.2-17.2-15c-1.7-1.4-3.6-2.9-5.7-4.6 C69,42.5,59.1,33.1,59.7,22.9c0.3-4.9,2.6-8.9,6.3-12.3c2.4-2.2,5.3-4.1,8.5-5.7c-1.1-0.2-2.1-0.3-3.2-0.5 c-9.4,4.1-16.4,9.8-17.2,18.4C53.2,33,62.9,42.7,71.6,50.2c2,1.7,3.9,3.3,5.5,4.7c5.9,5,11.9,10,16.7,15.5c5.5,6.3,8.9,13.5,9,21 c0.1,5.9-0.2,15-9.8,21.7c4.8-2.7,9.1-6,12.9-9.8C109.4,98.6,110.6,93.8,110.5,90.1z"
  }), jsx("path", {
    fill: "#FFFFFF",
    d: "M53.4,45.6C46.3,33.8,46.5,18.5,60,8.8c1.4-1,6.1-3.7,8.5-4.7c-1.3-0.1-2.6-0.1-4-0.1c-1,0-2,0-2.9,0.1 c-3.3,1.3-6.6,2.7-9.6,4.3c-11.3,6.1-19.9,21.1-14.5,35.8c4.3,11.7,8.8,19.4,14.2,31.7c6,13.9-22.3,23.5-36.4,17.9 c2.2,3.5,4.8,6.7,7.6,9.6c21.8,3.4,44.1-10,39-26.9c-4-13.3-15.5-22.5-20.6-35.6c-4.9-12.3-1-25.3,13.2-33.5 c0.8-0.4,1.5-0.9,2.3-1.3c-0.7,0.5-1.3,1.1-2,1.7c-12.5,11-13.7,25-6.6,36.7c7.5,12.4,18.9,21,25.4,33.9 c8.7,17.2-10.6,29.1-30.6,38.3c3.1,1.2,6.3,2.2,9.7,2.9c20-9.1,39.4-23.6,30.7-40.8C77.1,65.8,61,58,53.4,45.6z"
  })), jsx("g", null, jsx("path", {
    fill: "#00CC00",
    d: "M51.7,75.8c-5.4-12.3-9.9-20-14.2-31.7c-5.4-14.7,3.2-29.8,14.5-35.8c3-1.6,6.2-3,9.6-4.3 c-13.9,0.7-26.6,6.3-36.3,15c0,0-0.1,0.1-0.1,0.1c-2,1.8-3.8,3.7-5.5,5.8c-0.2,0.2-0.4,0.5-0.6,0.7c-0.3,0.4-0.6,0.8-0.9,1.2 c-0.3,0.4-0.5,0.7-0.8,1.1c-0.2,0.2-0.3,0.4-0.5,0.6c-3.3,4.7-6,10-7.9,15.7c-1.4,4.3-2.3,8.6-2.7,12.9c0,0,0,0.1,0,0.1 c-0.1,0.9-0.1,1.9-0.2,2.8c0,0.4,0,0.7,0,1.1c0,0.6,0,1.2,0,1.8c0,0.5,0,1.1,0,1.6c0,0.5,0,0.9,0.1,1.4c0,0.6,0.1,1.2,0.1,1.8 c0,0.4,0.1,0.7,0.1,1.1c0.1,0.7,0.2,1.3,0.3,2c0,0.3,0.1,0.6,0.1,0.9c0.1,0.7,0.2,1.5,0.4,2.2c0,0.2,0.1,0.3,0.1,0.5 c1.5,7,4.2,13.5,7.9,19.3C29.4,99.3,57.8,89.7,51.7,75.8z"
  })), jsx("path", {
    fill: "#0A46BF",
    d: "M112.1,63.4c-6-4.6-13-8.4-19.8-12.2c-2-1.1-4.1-2.3-6.4-3.6C75.7,41.7,64.2,34,62.8,23.4 c-0.6-4.7,0.7-8.9,3.2-12.8c-3.7,3.4-6,7.4-6.3,12.3C59.1,33.1,69,42.5,77.9,49.8c2,1.7,4,3.2,5.7,4.6c6,4.8,12.2,9.6,17.2,15 c5.7,6.1,9.3,13.2,9.7,20.7c0.2,3.8-1,8.5-4.4,13.3c7.9-7.9,13.5-18.1,15.8-29.6C119.3,69.9,116.1,66.4,112.1,63.4z"
  }), jsx("path", {
    fill: "#0A46BF",
    d: "M73.8,78.4c-6.5-12.9-17.9-21.5-25.4-33.9C41.3,32.8,42.5,18.8,55,7.8c0.6-0.6,1.3-1.1,2-1.7 c-0.8,0.4-1.6,0.8-2.3,1.3c-14.3,8.2-18.1,21.2-13.2,33.5C46.5,53.9,58,63.1,62,76.4c5.1,16.8-17.2,30.3-39,26.9 c5.7,5.8,12.6,10.4,20.2,13.4C63.2,107.5,82.5,95.6,73.8,78.4z"
  }), jsx("path", {
    fill: "#0A46BF",
    d: "M102.9,91.5c-0.2-7.5-3.5-14.7-9-21C89,65,83,59.9,77.1,55c-1.7-1.4-3.6-3-5.5-4.7 C62.9,42.7,53.2,33,54.1,22.8c0.7-8.6,7.8-14.4,17.2-18.4c-0.9-0.1-1.8-0.2-2.8-0.2c-2.5,1.1-7.1,3.7-8.5,4.7 c-13.5,9.7-13.7,25-6.6,36.7C61,58,77.1,65.8,83.6,78.7c8.7,17.2-10.7,31.7-30.7,40.8c1.8,0.4,3.6,0.6,5.4,0.8 c2.1,0.2,4.4,0.3,6.6,0.3c10.2-0.1,19.9-2.8,28.2-7.4C102.7,106.5,103,97.4,102.9,91.5z"
  })), jsx("g", null, jsx("path", {
    fill: "#0A46BF",
    d: "M202.6,35.5c0.9,0,1.4-0.7,1.7-0.7l1.4,0.3l-2.3,14.5h-1.7c0-4.5-0.9-7.8-2.9-10c-3.1-3.8-9.5-5.3-14.1-5.3 c-15.2,0-28.6,14.9-28.6,31.3c0,13.3,7.9,25.9,20.5,25.9c7.3,0,12.4-2.1,13.4-8.3l1.4-8.7c0.3-1.8,0.1-4.2-5.9-4.2h-2.6l0.4-2.2 c3.1,0.1,8,0.3,12.7,0.3c4.8,0,8.3-0.3,10.6-0.3l-0.4,2.1h-1.1c-4.7,0-5.8,2.9-6.6,7.8l-2,11.6c-6.1,2.5-14,4.7-20.5,4.7 c-17.1,0-29-11.5-29-28.4c0-19.1,16.4-34.2,36.1-34.2C193.8,31.6,200.7,35.5,202.6,35.5z"
  }), jsx("path", {
    fill: "#0A46BF",
    d: "M235.1,56.9c0,0.8-0.3,2.1-0.9,4.5L231,73.1h0.2c7.3-13.9,12.6-19.5,15.8-19.5c1.7,0,2.6,0.9,2.6,2.9 s-0.9,6.4-3.6,6.4c-1,0-2.2-0.3-3.1-0.3c-5.7,0-11.6,15.3-14.9,25.5c-0.5,1.7-1,3.6-1.5,5.5c-0.3,0.6-1.4,0.7-3,0.7 c-1.3,0-2.9-0.8-2.8-1.7l7.1-24.6c0.6-2.1,0.9-3.7,0.9-4.9c0-0.9-0.5-2.2-1.6-2.2c-1.6,0-4.3,2-7.2,5.8l-1.1-1.4 c4-5.8,9.5-11.5,13-11.5C233.7,53.6,235.1,54.6,235.1,56.9z"
  }), jsx("path", {
    fill: "#0A46BF",
    d: "M288.6,59.8c0,9.1-10.3,15-25.6,16.6c-0.5,2.3-0.7,4.5-0.7,6.6c0,4.5,3.1,6.7,7.1,6.7c5.1,0,10.1-3.3,15-8 l1.2,1.4c-5.7,6.3-11.9,11.2-19.5,11.2c-7,0-10.6-4.8-10.6-11.6c0-13.9,12.4-29,24.8-29C285.1,53.6,288.6,55.6,288.6,59.8z M281.7,60.1c0-2.1-1.2-3.7-3.6-3.7c-6.1,0-12.8,10.5-14.7,17.7C274,73.2,281.7,68,281.7,60.1z"
  }), jsx("path", {
    fill: "#0A46BF",
    d: "M332,59.8c0,9.1-10.3,15-25.6,16.6c-0.5,2.3-0.7,4.5-0.7,6.6c0,4.5,3.1,6.7,7.1,6.7c5.1,0,10.1-3.3,15-8 l1.2,1.4c-5.7,6.3-11.9,11.2-19.5,11.2c-7,0-10.6-4.8-10.6-11.6c0-13.9,12.4-29,24.8-29C328.5,53.6,332,55.6,332,59.8z M325.2,60.1 c0-2.1-1.2-3.7-3.6-3.7c-6.1,0-12.8,10.5-14.7,17.7C317.4,73.2,325.2,68,325.2,60.1z"
  }), jsx("path", {
    fill: "#0A46BF",
    d: "M361.4,56.5c0,1.5-0.7,3.2-1,4.4L356,73.6h0.2c6.8-9.5,18.1-20,25.2-20c2.6,0,3.5,1.5,3.5,3.5 c0,1.4-0.5,3.1-0.8,3.9l-7.6,20.9c-0.4,1.1-0.7,2.1-0.7,2.8c0,1,0.3,1.8,1.5,1.8c1.8,0,5.1-2.6,7.4-6.1l1.5,1.2l-3.1,3.7 c-5.4,6.6-9.5,7.5-11.9,7.5c-2.1,0-3.2-1.2-3.2-2.8c0-1.5,0.3-3,1.2-5.4L376,66c0.4-1.1,1-3.1,1-4c0-1.1-1-1.6-2-1.6 c-4.7,0-9,5.1-12.6,8.9c-4.9,5.3-8.2,10.5-10.7,17.5c-0.6,1.5-1.1,3.3-1.7,4.9c-0.3,0.7-2.1,0.9-3.1,0.9c-0.9,0-2.3-0.5-2.7-1.1 l8.4-25.4c0.8-2.6,1-3.6,1-4.4c0-0.9-0.3-1.5-1.4-1.5c-2.1,0-4,1.7-7.3,5.5l-1.3-1.5c5.4-6.6,10.5-10.8,15-10.8 C360.6,53.6,361.4,54.9,361.4,56.5z"
  }), jsx("path", {
    fill: "#0A46BF",
    d: "M431.7,59.8c0,9.1-10.3,15-25.6,16.6c-0.5,2.3-0.7,4.5-0.7,6.6c0,4.5,3.1,6.7,7.1,6.7c5.1,0,10.1-3.3,15-8 l1.2,1.4c-5.7,6.3-11.9,11.2-19.5,11.2c-7,0-10.6-4.8-10.6-11.6c0-13.9,12.4-29,24.8-29C428.2,53.6,431.7,55.6,431.7,59.8z M424.9,60.1c0-2.1-1.2-3.7-3.6-3.7c-6.1,0-12.8,10.5-14.7,17.7C417.2,73.2,424.9,68,424.9,60.1z"
  }), jsx("path", {
    fill: "#0A46BF",
    d: "M459.6,56.9c0,0.8-0.3,2.1-0.9,4.5l-3.2,11.7h0.2c7.2-13.8,12.5-19.5,15.7-19.5c1.7,0,2.6,0.9,2.6,2.9 s-0.9,6.4-3.6,6.4c-1,0-2.2-0.3-3.1-0.3c-5.7,0-11.6,15.3-14.9,25.5c-0.5,1.7-1,3.6-1.5,5.5c-0.3,0.6-1.4,0.7-3,0.7 c-1.3,0-2.9-0.8-2.8-1.7l7.1-24.6c0.6-2.1,0.9-3.7,0.9-4.9c0-0.9-0.5-2.2-1.6-2.2c-1.6,0-4.3,2-7.2,5.8l-1.1-1.4 c4-5.8,9.5-11.5,13-11.5C458.2,53.6,459.6,54.6,459.6,56.9z"
  }), jsx("path", {
    fill: "#0A46BF",
    d: "M550.4,33.8c0.6,0,0.8-0.1,1.7-0.8h1.4l-2.6,15.2H549l0.2-3.6c0-6.4-4.3-10.1-10.5-10.1c-5.6,0-9.3,3.1-9.3,9 c0,11.8,20.2,19.5,20.2,33.8c0,9.8-8.3,17-18.5,17c-3.1,0-6.6-0.4-12.5-3.1l-2.8,2.6h-2l2.3-18.2h1.7c0.2,4,0.3,6.9,1.4,9.5 c2.2,5.2,7.5,6.6,10.6,6.6c7,0,11.7-4.3,11.7-10.5c0-12.5-19.2-19.3-19.2-33.6c0-9.1,6.4-15.8,16.2-15.8 C545.7,31.6,548.6,33.8,550.4,33.8z"
  }), jsx("path", {
    fill: "#0A46BF",
    d: "M584.8,44.1l-3.5,10.8h9.2l-1,3.5h-9.1l-7.5,24.9c-0.1,0.3-0.5,1.6-0.5,2.6s0.4,1.9,1.7,1.9 c2.2,0,5.8-2.4,10.6-7.5l1.2,1.4c-7.4,8.5-12.9,12.4-17.2,12.4c-2.4,0-3.7-2.1-3.7-4.5c0-1.7,0.5-3.5,1.5-7l7.2-24.4h-8.4l1-3.5 h8.3l3.2-10.8h7V44.1z"
  }), jsx("path", {
    fill: "#0A46BF",
    d: "M615.1,55.8c0,1-0.3,2-0.8,3.5l-7.9,24.5c-0.3,0.9-0.4,1.6-0.4,2.2c0,0.9,0.3,1.3,1.1,1.3 c0.9,0,3.8-2.7,6.6-5.6l1.4,1.1c-5.7,7.7-10.2,11.3-13.7,11.3c-2.5,0-3.2-1.4-3.2-3.4c0-0.8,0.3-2.4,1-4.5l7.6-22.4 c0.3-0.9,0.5-1.8,0.5-2.4s-0.3-1-1-1c-1.7,0-4.9,3.7-6.1,5.5l-1.2-1.2c4.4-6.4,9.7-11.2,13.6-11.2 C614.5,53.6,615.1,54.5,615.1,55.8z M618.3,37.6c0,2-1.9,3.8-3.9,3.8s-3.9-1.9-3.9-3.8c0-2,1.8-4,3.9-4S618.3,35.6,618.3,37.6z"
  }), jsx("path", {
    fill: "#0A46BF",
    d: "M648.8,31c-8.9,35.5-13.4,52.8-13.5,53.2s-0.3,1-0.3,1.6c0,0.9,0.1,1.7,1.1,1.7c1.4,0,4.6-3.6,6.6-6.3l1,1.7 c-2.7,3.3-7.6,11.1-13,11.1c-2.6,0-3.1-1.8-3.1-4c0-1.9,0.5-3.6,0.6-4l12-49.4L635,34l0.4-1.1c4.8-0.5,7-0.9,12.6-2.5L648.8,31z"
  }), jsx("path", {
    fill: "#0A46BF",
    d: "M677.5,31c-8.9,35.6-13.3,52.9-13.4,53.3c-0.1,0.4-0.3,1-0.3,1.6c0,0.9,0.1,1.7,1.1,1.7c1.4,0,4.6-3.6,6.6-6.3 l1,1.7c-2.7,3.3-7.6,11.1-13,11.1c-2.6,0-3.1-1.8-3.1-4c0-1.9,0.5-3.6,0.6-4l12.1-49.3l-5.2-2.6l0.4-1.1c4.8-0.5,7-0.9,12.6-2.5 L677.5,31z"
  }))) : jsx("svg", {
    version: "1.1",
    id: "Layer_1",
    xmlns: "http://www.w3.org/2000/svg",
    x: "0px",
    y: "0px",
    viewBox: "0 0 713.4 150.5"
  }, jsx("g", null, jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M218.3,46.9c0.9,0,1.4-0.7,1.7-0.7l1.4,0.3l-2.3,14.4h-1.7c0-4.4-0.9-7.7-2.9-9.9c-3.1-3.8-9.4-5.2-13.9-5.2 c-15,0-28.3,14.7-28.3,30.9c0,13.1,7.8,25.6,20.3,25.6c7.2,0,12.2-2.1,13.2-8.2l1.4-8.6c0.3-1.8,0.1-4.1-5.8-4.1h-2.6l0.4-2.2 c3.1,0.1,7.9,0.3,12.5,0.3c4.7,0,8.2-0.3,10.5-0.3l-0.4,2.1h-1.1c-4.6,0-5.7,2.9-6.5,7.7l-1.9,11.4c-6,2.5-13.8,4.6-20.3,4.6 c-16.9,0-28.6-11.4-28.6-28.1c0-18.9,16.2-33.8,35.7-33.8C209.6,43.1,216.5,46.9,218.3,46.9z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M250.4,68.1c0,0.8-0.3,2.1-0.9,4.4l-3.2,11.6h0.2c7.2-13.7,12.4-19.3,15.6-19.3c1.7,0,2.6,0.9,2.6,2.9 c0,2-0.9,6.3-3.6,6.3c-1,0-2.2-0.3-3.1-0.3c-5.6,0-11.5,15.1-14.7,25.2c-0.5,1.7-1,3.6-1.5,5.4c-0.3,0.6-1.4,0.7-3,0.7 c-1.3,0-2.9-0.8-2.8-1.7l7-24.3c0.6-2.1,0.9-3.7,0.9-4.8c0-0.9-0.5-2.2-1.6-2.2c-1.6,0-4.2,2-7.1,5.7l-1.1-1.4 c4-5.7,9.4-11.4,12.8-11.4C249.1,64.9,250.4,65.8,250.4,68.1z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M303.3,70.9c0,9-10.2,14.8-25.3,16.4c-0.5,2.3-0.7,4.4-0.7,6.5c0,4.4,3.1,6.6,7,6.6c5,0,10-3.3,14.8-7.9 l1.2,1.4c-5.6,6.2-11.8,11.1-19.3,11.1c-6.9,0-10.5-4.7-10.5-11.5c0-13.7,12.2-28.6,24.5-28.6C299.8,64.9,303.3,66.8,303.3,70.9z M296.5,71.2c0-2.1-1.2-3.7-3.6-3.7c-6,0-12.6,10.4-14.5,17.5C288.8,84.2,296.5,79,296.5,71.2z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M346.1,70.9c0,9-10.2,14.8-25.3,16.4c-0.5,2.3-0.7,4.4-0.7,6.5c0,4.4,3.1,6.6,7,6.6c5,0,10-3.3,14.8-7.9 l1.2,1.4c-5.6,6.2-11.8,11.1-19.3,11.1c-6.9,0-10.5-4.7-10.5-11.5c0-13.7,12.2-28.6,24.5-28.6C342.6,64.9,346.1,66.8,346.1,70.9z M339.3,71.2c0-2.1-1.2-3.7-3.6-3.7c-6,0-12.6,10.4-14.5,17.5C331.7,84.2,339.3,79,339.3,71.2z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M375.2,67.7c0,1.5-0.7,3.2-1,4.3l-4.3,12.6h0.2c6.7-9.4,17.9-19.8,24.9-19.8c2.6,0,3.5,1.5,3.5,3.5 c0,1.4-0.5,3.1-0.8,3.9l-7.5,20.6c-0.4,1.1-0.7,2.1-0.7,2.8c0,1,0.3,1.8,1.5,1.8c1.8,0,5-2.6,7.3-6l1.4,1.1l-3.1,3.7 c-5.3,6.5-9.4,7.4-11.8,7.4c-2.1,0-3.2-1.2-3.2-2.8c0-1.5,0.3-3,1.2-5.3l6.7-18.4c0.4-1.1,1-3.1,1-4c0-1.1-1-1.6-2-1.6 c-4.6,0-8.9,5-12.4,8.8c-4.8,5.2-8.1,10.4-10.6,17.3c-0.6,1.5-1.1,3.3-1.7,4.8c-0.3,0.7-2.1,0.9-3.1,0.9c-0.9,0-2.3-0.5-2.7-1.1 l8.3-25.1c0.8-2.6,1-3.6,1-4.3c0-0.9-0.3-1.5-1.4-1.5c-2.1,0-4,1.7-7.2,5.4l-1.3-1.5c5.3-6.5,10.4-10.7,14.8-10.7 C374.3,64.9,375.2,66.1,375.2,67.7z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M444.7,70.9c0,9-10.2,14.8-25.3,16.4c-0.5,2.3-0.7,4.4-0.7,6.5c0,4.4,3.1,6.6,7,6.6c5,0,10-3.3,14.8-7.9 l1.2,1.4c-5.6,6.2-11.8,11.1-19.3,11.1c-6.9,0-10.5-4.7-10.5-11.5c0-13.7,12.2-28.6,24.5-28.6C441.2,64.9,444.7,66.8,444.7,70.9z M437.9,71.2c0-2.1-1.2-3.7-3.6-3.7c-6,0-12.6,10.4-14.5,17.5C430.3,84.2,437.9,79,437.9,71.2z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M472.2,68.1c0,0.8-0.3,2.1-0.9,4.4l-3.2,11.6h0.2c7.2-13.7,12.4-19.3,15.6-19.3c1.7,0,2.6,0.9,2.6,2.9 c0,2-0.9,6.3-3.6,6.3c-1,0-2.2-0.3-3.1-0.3c-5.6,0-11.5,15.1-14.7,25.2c-0.5,1.7-1,3.6-1.5,5.4c-0.3,0.6-1.4,0.7-3,0.7 c-1.3,0-2.9-0.8-2.8-1.7l7-24.3c0.6-2.1,0.9-3.7,0.9-4.8c0-0.9-0.5-2.2-1.6-2.2c-1.6,0-4.2,2-7.1,5.7l-1.1-1.4 c4-5.7,9.4-11.4,12.8-11.4C470.8,64.9,472.2,65.8,472.2,68.1z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M561.8,45.2c0.6,0,0.8-0.1,1.7-0.8h1.4l-2.6,15h-1.9l0.2-3.6c0-6.3-4.2-10-10.4-10c-5.5,0-9.2,3.1-9.2,8.9 c0,11.7,20,19.3,20,33.4c0,9.7-8.2,16.8-18.3,16.8c-3.1,0-6.5-0.4-12.3-3.1l-2.8,2.6h-2l2.3-18h1.7c0.2,4,0.3,6.8,1.4,9.4 c2.2,5.1,7.4,6.5,10.5,6.5c6.9,0,11.6-4.2,11.6-10.4c0-12.3-19-19.1-19-33.2c0-9,6.3-15.6,16-15.6 C557.3,43.1,560.1,45.2,561.8,45.2z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M595.8,55.4l-3.5,10.7h9.1l-1,3.5h-9L584,94.2c-0.1,0.3-0.5,1.6-0.5,2.6c0,1,0.4,1.9,1.7,1.9 c2.2,0,5.7-2.4,10.5-7.4l1.2,1.4c-7.3,8.4-12.7,12.2-17,12.2c-2.4,0-3.7-2.1-3.7-4.4c0-1.7,0.5-3.5,1.5-6.9l7.1-24.1h-8.3l1-3.5 h8.2l3.2-10.7H595.8z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M625.8,67c0,1-0.3,2-0.8,3.5l-7.8,24.2c-0.3,0.9-0.4,1.6-0.4,2.2c0,0.9,0.3,1.3,1.1,1.3c0.9,0,3.8-2.7,6.5-5.5 l1.4,1.1c-5.6,7.6-10.1,11.2-13.5,11.2c-2.5,0-3.2-1.4-3.2-3.4c0-0.8,0.3-2.4,1-4.4l7.5-22.1c0.3-0.9,0.5-1.8,0.5-2.4 c0-0.6-0.3-1-1-1c-1.7,0-4.8,3.7-6,5.4l-1.2-1.2c4.3-6.3,9.6-11.1,13.4-11.1C625.2,64.9,625.8,65.7,625.8,67z M628.9,49 c0,2-1.9,3.8-3.9,3.8c-2,0-3.9-1.9-3.9-3.8c0-2,1.8-4,3.9-4C627.2,45,628.9,47,628.9,49z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M659.1,42.5c-8.8,35.1-13.2,52.2-13.3,52.6s-0.3,1-0.3,1.6c0,0.9,0.1,1.7,1.1,1.7c1.4,0,4.5-3.6,6.5-6.2l1,1.7 c-2.7,3.3-7.5,11-12.8,11c-2.6,0-3.1-1.8-3.1-4c0-1.9,0.5-3.6,0.6-4l11.9-48.8l-5.1-2.6l0.4-1.1c4.7-0.5,6.9-0.9,12.4-2.5 L659.1,42.5z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M687.5,42.5c-8.8,35.1-13.2,52.2-13.3,52.6s-0.3,1-0.3,1.6c0,0.9,0.1,1.7,1.1,1.7c1.4,0,4.5-3.6,6.5-6.2l1,1.7 c-2.7,3.3-7.5,11-12.8,11c-2.6,0-3.1-1.8-3.1-4c0-1.9,0.5-3.6,0.6-4l11.9-48.8l-5.1-2.6l0.4-1.1c4.7-0.5,6.9-0.9,12.4-2.5 L687.5,42.5z"
  })), jsx("g", null, jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M22.5,85.6c0-0.2-0.1-0.3-0.1-0.5C22.5,85.3,22.5,85.5,22.5,85.6z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M21.7,80.1c0-0.4-0.1-0.7-0.1-1.1C21.6,79.3,21.6,79.7,21.7,80.1z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M22.1,82.9c0-0.3-0.1-0.6-0.1-0.9C22,82.4,22,82.7,22.1,82.9z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M21.4,77.2c0-0.5-0.1-0.9-0.1-1.4C21.4,76.2,21.4,76.7,21.4,77.2z"
  }), jsx("g", null, jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M138.3,73.8c0-0.4,0-0.8,0-1.3c0-0.5,0-1,0-1.4c0-0.6-0.1-1.2-0.1-1.8c0-0.2,0-0.5-0.1-0.7 c-0.9-10.4-4.5-20.4-10.5-28.8c0-0.1-0.1-0.1-0.1-0.2c-0.4-0.6-0.8-1.2-1.3-1.7c-0.2-0.3-0.5-0.6-0.7-0.9c-0.3-0.3-0.6-0.7-0.8-1 c-0.5-0.6-1-1.1-1.5-1.7c-0.1-0.1-0.1-0.1-0.2-0.2c-8.6-9.4-20.2-16-33.3-18.3c-3.3,1.7-4.8,2.7-7.2,4.9 c-2.5,3.9-2.8,7.5-2.1,12.2c1.4,10.7,12,16.8,22.1,22.6c2.3,1.3,5.5,3,7.4,4.1c6.8,3.8,13.3,8.1,19.3,12.7c4,3,6.1,5.1,8.5,9.1 c0.5-2.7,0.3-2,0.4-4.8c0-0.2,0-0.5,0-0.7C138.3,75.2,138.3,74.6,138.3,73.8z"
  })), jsx("g", null, jsx("path", {
    fill: "#23B00B",
    d: "M21.5,79c-0.1-0.6-0.1-1.2-0.1-1.8C21.4,77.8,21.5,78.4,21.5,79z"
  })), jsx("g", null, jsx("path", {
    fill: "#23B00B",
    d: "M21.9,82.1c-0.1-0.7-0.2-1.3-0.3-2C21.7,80.8,21.8,81.4,21.9,82.1z"
  })), jsx("g", null, jsx("path", {
    fill: "#23B00B",
    d: "M30.3,104.9c0.1,0,0.1,0,0.2,0.1c-3.7-5.8-6.4-12.3-7.9-19.3C24,92.5,26.6,99,30.3,104.9z"
  })), jsx("g", null, jsx("path", {
    fill: "#23B00B",
    d: "M22.4,85.2c-0.1-0.7-0.3-1.5-0.4-2.2C22.2,83.7,22.3,84.4,22.4,85.2z"
  })), jsx("g", null, jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M66.9,87C61.6,74.7,57,66.9,52.7,55.3c-5.4-14.8,3.2-29.8,14.5-35.9c3-1.6,6.2-3,9.6-4.3 c-14,0.7-26.6,6.3-36.3,15.1c0,0-0.1,0.1-0.1,0.1c-2,1.8-3.8,3.8-5.6,5.8c-0.2,0.2-0.4,0.5-0.6,0.7c-0.3,0.4-0.6,0.8-0.9,1.2 c-0.3,0.4-0.5,0.7-0.8,1.1c-0.2,0.2-0.3,0.4-0.5,0.6c-3.3,4.7-6,10-7.9,15.8c-1.4,4.3-2.3,8.6-2.7,13c0,0,0,0.1,0,0.1 c-0.1,0.9-0.1,1.9-0.2,2.8c0,0.4,0,0.7,0,1.1c0,0.6,0,1.2,0,1.8c0,0.5,0,1.1,0,1.6c0,0.5,0,0.9,0.1,1.4c0,0.6,0.1,1.2,0.1,1.8 c0,0.4,0.1,0.7,0.1,1.1c0.1,0.7,0.2,1.3,0.3,2c0,0.3,0.1,0.6,0.1,0.9c0.1,0.7,0.2,1.5,0.4,2.2c0,0.2,0.1,0.3,0.1,0.5 c1.5,7,4.2,13.5,7.9,19.3C44.6,110.6,73,100.9,66.9,87z"
  })), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M127.4,74.6c-6-4.6-13-8.4-19.8-12.2c-2-1.1-4.1-2.3-6.5-3.6C91,52.9,79.4,45.1,78,34.5 c-0.6-4.7,2-9.8,4.6-13.7c-3.7,3.4-7.4,8.3-7.7,13.2c-0.6,10.3,9.4,19.7,18.3,27c2,1.7,4,3.2,5.7,4.6c6,4.8,12.2,9.6,17.2,15 c5.7,6.1,9.3,13.2,9.7,20.8c0.2,3.8-1,8.6-4.5,13.3c7.9-8,13.5-18.2,15.8-29.6C134.7,81.1,131.4,77.6,127.4,74.6z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M89.1,89.6c-6.5-12.9-17.9-21.5-25.5-34c-7.1-11.7-5.4-25.7,6.9-36.9c1.7-1.5,5.4-3,6.4-3.7 c-1,0.3-5.8,2.3-7,3C55.7,26.6,51.7,39.6,56.6,52c5.2,13.1,16.6,22.3,20.7,35.6c5.1,16.8-17.3,30.4-39.1,26.9 c5.7,5.8,12.6,10.4,20.3,13.4C78.4,118.8,97.8,106.9,89.1,89.6z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M118.2,102.7c-0.2-7.5-3.5-14.8-9.1-21.1c-4.9-5.5-10.9-10.5-16.8-15.5c-1.7-1.4-3.6-3-5.5-4.8 c-8.7-7.6-18.4-17.2-17.5-27.5c0.7-8.6,7.8-14.4,17.2-18.5c-0.9-0.1-1.8-0.2-2.8-0.2c-2.5,1.1-7.1,3.7-8.5,4.7 c-13.6,9.7-13.7,25.1-6.6,36.8C76.2,69.1,92.4,77,98.9,89.9c8.7,17.3-10.7,31.8-30.8,40.9c1.8,0.4,3.6,0.6,5.4,0.8 c2.1,0.2,4.4,0.3,6.6,0.3c10.3-0.1,19.9-2.8,28.3-7.5C118,117.7,118.4,108.6,118.2,102.7z"
  }))));
};

var GreenerStillIcon = function (_a) {
  var color = _a.color;
  return jsx(React.Fragment, null, color === 'color' ? jsx("svg", {
    version: "1.1",
    id: "Layer_1",
    xmlns: "http://www.w3.org/2000/svg",
    x: "0px",
    y: "0px",
    viewBox: "0 0 144 144"
  }, jsx("g", null, jsx("path", {
    fill: "#FFFFFF",
    d: "M10.4,85.9c0-0.2-0.1-0.3-0.1-0.5C10.4,85.5,10.4,85.7,10.4,85.9z"
  }), jsx("path", {
    fill: "#FFFFFF",
    d: "M9.5,80c0-0.4-0.1-0.8-0.1-1.2C9.4,79.2,9.4,79.6,9.5,80z"
  }), jsx("path", {
    fill: "#FFFFFF",
    d: "M9.9,83c0-0.3-0.1-0.6-0.1-0.9C9.8,82.4,9.8,82.7,9.9,83z"
  }), jsx("path", {
    fill: "#FFFFFF",
    d: "M9.2,76.8c0-0.5-0.1-1-0.1-1.5C9.2,75.8,9.2,76.3,9.2,76.8z"
  }), jsx("g", null, jsx("path", {
    fill: "#00CC00",
    d: "M134.1,73.3c0-0.4,0-0.9,0-1.3c0-0.5,0-1,0-1.5c0-0.6-0.1-1.3-0.1-1.9c0-0.3,0-0.5-0.1-0.8 c-0.9-11.1-4.8-21.8-11.2-30.8c-0.1-0.1-0.1-0.1-0.2-0.2c-0.4-0.6-0.9-1.2-1.4-1.8c-0.2-0.3-0.5-0.6-0.7-0.9 c-0.3-0.4-0.6-0.7-0.9-1.1c-0.5-0.6-1-1.2-1.6-1.8c-0.1-0.1-0.1-0.1-0.2-0.2c-9.2-10.1-21.6-17.1-35.6-19.5 c-3.5,1.8-6.6,3.8-9.1,6.1c-2.7,4.2-4.1,8.7-3.4,13.7c1.5,11.4,13.9,19.7,24.8,25.9c2.5,1.4,4.8,2.7,6.9,3.9 c7.3,4.1,14.8,8.2,21.2,13c4.2,3.2,7.8,7,10.4,11.2c0.6-2.9,1-5.9,1.1-9c0-0.2,0-0.5,0-0.7C134.1,74.8,134.1,74,134.1,73.3z"
  })), jsx("g", null, jsx("path", {
    fill: "#00CC00",
    d: "M9.4,78.8c-0.1-0.6-0.1-1.3-0.1-2C9.2,77.5,9.3,78.1,9.4,78.8z"
  })), jsx("g", null, jsx("path", {
    fill: "#00CC00",
    d: "M9.7,82.1c-0.1-0.7-0.2-1.4-0.3-2.1C9.6,80.7,9.6,81.4,9.7,82.1z"
  })), jsx("g", null, jsx("path", {
    fill: "#00CC00",
    d: "M18.7,106.4c0.1,0,0.1,0,0.2,0.1c-4-6.2-6.9-13.2-8.5-20.7C12,93.2,14.8,100.2,18.7,106.4z"
  })), jsx("g", null, jsx("path", {
    fill: "#00CC00",
    d: "M10.3,85.4c-0.2-0.8-0.3-1.6-0.4-2.4C10,83.8,10.2,84.6,10.3,85.4z"
  })), jsx("g", null, jsx("path", {
    fill: "#FFFFFF",
    d: "M120.8,102.7c-0.4-8.1-4.2-15.7-10.3-22.2c-5.4-5.7-12-10.9-18.4-16.1c-1.8-1.5-3.9-3.1-6.1-4.9 c-9.5-7.8-20.2-17.8-19.6-28.8c0.3-5.2,2.8-9.6,6.7-13.2c2.5-2.3,5.6-4.4,9.1-6.1c-1.1-0.2-2.3-0.4-3.4-0.5 c-10.1,4.4-17.6,10.5-18.4,19.7c-0.9,11,9.4,21.3,18.7,29.4c2.1,1.8,4.1,3.6,5.9,5.1c6.3,5.3,12.7,10.7,17.9,16.6 c5.9,6.7,9.5,14.4,9.7,22.5c0.1,6.3-0.2,16.1-10.5,23.3c5.1-2.9,9.8-6.4,13.8-10.5C119.7,111.8,121,106.7,120.8,102.7z"
  }), jsx("path", {
    fill: "#FFFFFF",
    d: "M59.7,55c-7.6-12.6-7.4-29,7.1-39.3c1.5-1.1,6.5-3.9,9.1-5.1c-1.4-0.1-2.8-0.2-4.3-0.2c-1.1,0-2.1,0-3.1,0.1 c-3.6,1.3-7,2.9-10.2,4.6c-12.1,6.5-21.3,22.6-15.5,38.4c4.6,12.5,9.5,20.8,15.2,33.9c6.5,14.9-23.9,25.2-39,19.2 c2.4,3.7,5.1,7.1,8.2,10.3C50.4,120.5,74.3,106,68.8,88c-4.3-14.2-16.6-24.1-22.1-38.1c-5.2-13.2-1.1-27.1,14.2-35.8 c0.8-0.5,1.7-0.9,2.5-1.4c-0.7,0.6-1.4,1.2-2.1,1.8C48,26.3,46.6,41.3,54.3,53.8c8.1,13.3,20.3,22.5,27.2,36.3 c9.3,18.4-11.4,31.2-32.7,41c3.3,1.3,6.8,2.3,10.4,3.1c21.4-9.8,42.2-25.3,32.9-43.7C85,76.7,67.7,68.3,59.7,55z"
  })), jsx("g", null, jsx("path", {
    fill: "#00CC00",
    d: "M57.9,87.4c-5.7-13.2-10.6-21.4-15.2-33.9c-5.8-15.8,3.4-31.9,15.5-38.4c3.2-1.7,6.7-3.3,10.2-4.6 C53.5,11.2,40,17.2,29.6,26.6c0,0-0.1,0.1-0.1,0.1c-2.1,1.9-4.1,4-5.9,6.2c-0.2,0.2-0.4,0.5-0.6,0.8c-0.3,0.4-0.7,0.8-1,1.2 c-0.3,0.4-0.6,0.8-0.9,1.1c-0.2,0.2-0.3,0.5-0.5,0.7c-3.6,5-6.4,10.7-8.5,16.9c-1.5,4.6-2.4,9.2-2.8,13.9c0,0,0,0.1,0,0.1 c-0.1,1-0.1,2-0.2,3c0,0.4,0,0.8,0,1.1c0,0.7,0,1.3,0,2c0,0.6,0,1.1,0,1.7c0,0.5,0,1,0.1,1.5c0,0.7,0.1,1.3,0.1,2 c0,0.4,0.1,0.8,0.1,1.2c0.1,0.7,0.2,1.4,0.3,2.1c0,0.3,0.1,0.6,0.1,0.9c0.1,0.8,0.3,1.6,0.4,2.4c0,0.2,0.1,0.3,0.1,0.5 c1.6,7.5,4.5,14.4,8.5,20.7C33.9,112.5,64.3,102.2,57.9,87.4z"
  })), jsx("path", {
    fill: "#0A46BF",
    d: "M122.5,74.1c-6.4-4.9-13.9-9-21.2-13c-2.1-1.2-4.4-2.5-6.9-3.9c-10.9-6.2-23.2-14.5-24.8-25.9 c-0.7-5,0.7-9.6,3.4-13.7c-3.9,3.6-6.4,8-6.7,13.2c-0.6,11,10.1,21,19.6,28.8c2.2,1.8,4.2,3.4,6.1,4.9c6.4,5.1,13,10.3,18.4,16.1 c6.1,6.5,9.9,14.1,10.3,22.2c0.2,4-1.1,9.1-4.8,14.2c8.4-8.5,14.4-19.4,16.9-31.6C130.3,81.1,126.8,77.3,122.5,74.1z"
  }), jsx("path", {
    fill: "#0A46BF",
    d: "M81.5,90.1c-7-13.8-19.1-23-27.2-36.3c-7.6-12.6-6.3-27.5,7.1-39.3c0.7-0.6,1.4-1.2,2.1-1.8 c-0.8,0.4-1.7,0.9-2.5,1.4c-15.3,8.8-19.4,22.7-14.2,35.8c5.5,14,17.8,23.9,22.1,38.1c5.4,18-18.5,32.4-41.8,28.8 c6.1,6.2,13.5,11.1,21.7,14.3C70.1,121.3,90.8,108.6,81.5,90.1z"
  }), jsx("path", {
    fill: "#0A46BF",
    d: "M112.7,104.1c-0.2-8.1-3.8-15.8-9.7-22.5C97.8,75.7,91.3,70.4,85.1,65c-1.8-1.5-3.8-3.2-5.9-5.1 c-9.3-8.1-19.6-18.4-18.7-29.4c0.8-9.2,8.3-15.4,18.4-19.7c-1-0.1-2-0.2-3-0.3c-2.6,1.1-7.6,4-9.1,5.1C52.2,26,52,42.4,59.7,55 C67.7,68.3,85,76.7,92,90.4c9.3,18.5-11.5,33.9-32.9,43.7c1.9,0.4,3.8,0.7,5.8,0.9c2.2,0.2,4.7,0.3,7.1,0.4c11-0.1,21.3-3,30.2-8 C112.4,120.2,112.8,110.5,112.7,104.1z"
  }))) :
  // TODO - this SVG was repurposed from the full logo. Get the actual black/white SVG when possible
  jsx("svg", {
    version: "1.1",
    id: "Layer_1",
    xmlns: "http://www.w3.org/2000/svg",
    x: "0px",
    y: "0px",
    viewBox: "8 4 144 144"
  }, jsx("g", null, jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M22.5,85.6c0-0.2-0.1-0.3-0.1-0.5C22.5,85.3,22.5,85.5,22.5,85.6z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M21.7,80.1c0-0.4-0.1-0.7-0.1-1.1C21.6,79.3,21.6,79.7,21.7,80.1z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M22.1,82.9c0-0.3-0.1-0.6-0.1-0.9C22,82.4,22,82.7,22.1,82.9z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M21.4,77.2c0-0.5-0.1-0.9-0.1-1.4C21.4,76.2,21.4,76.7,21.4,77.2z"
  }), jsx("g", null, jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M138.3,73.8c0-0.4,0-0.8,0-1.3c0-0.5,0-1,0-1.4c0-0.6-0.1-1.2-0.1-1.8c0-0.2,0-0.5-0.1-0.7 c-0.9-10.4-4.5-20.4-10.5-28.8c0-0.1-0.1-0.1-0.1-0.2c-0.4-0.6-0.8-1.2-1.3-1.7c-0.2-0.3-0.5-0.6-0.7-0.9c-0.3-0.3-0.6-0.7-0.8-1 c-0.5-0.6-1-1.1-1.5-1.7c-0.1-0.1-0.1-0.1-0.2-0.2c-8.6-9.4-20.2-16-33.3-18.3c-3.3,1.7-4.8,2.7-7.2,4.9 c-2.5,3.9-2.8,7.5-2.1,12.2c1.4,10.7,12,16.8,22.1,22.6c2.3,1.3,5.5,3,7.4,4.1c6.8,3.8,13.3,8.1,19.3,12.7c4,3,6.1,5.1,8.5,9.1 c0.5-2.7,0.3-2,0.4-4.8c0-0.2,0-0.5,0-0.7C138.3,75.2,138.3,74.6,138.3,73.8z"
  })), jsx("g", null, jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M66.9,87C61.6,74.7,57,66.9,52.7,55.3c-5.4-14.8,3.2-29.8,14.5-35.9c3-1.6,6.2-3,9.6-4.3 c-14,0.7-26.6,6.3-36.3,15.1c0,0-0.1,0.1-0.1,0.1c-2,1.8-3.8,3.8-5.6,5.8c-0.2,0.2-0.4,0.5-0.6,0.7c-0.3,0.4-0.6,0.8-0.9,1.2 c-0.3,0.4-0.5,0.7-0.8,1.1c-0.2,0.2-0.3,0.4-0.5,0.6c-3.3,4.7-6,10-7.9,15.8c-1.4,4.3-2.3,8.6-2.7,13c0,0,0,0.1,0,0.1 c-0.1,0.9-0.1,1.9-0.2,2.8c0,0.4,0,0.7,0,1.1c0,0.6,0,1.2,0,1.8c0,0.5,0,1.1,0,1.6c0,0.5,0,0.9,0.1,1.4c0,0.6,0.1,1.2,0.1,1.8 c0,0.4,0.1,0.7,0.1,1.1c0.1,0.7,0.2,1.3,0.3,2c0,0.3,0.1,0.6,0.1,0.9c0.1,0.7,0.2,1.5,0.4,2.2c0,0.2,0.1,0.3,0.1,0.5 c1.5,7,4.2,13.5,7.9,19.3C44.6,110.6,73,100.9,66.9,87z"
  })), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M127.4,74.6c-6-4.6-13-8.4-19.8-12.2c-2-1.1-4.1-2.3-6.5-3.6C91,52.9,79.4,45.1,78,34.5 c-0.6-4.7,2-9.8,4.6-13.7c-3.7,3.4-7.4,8.3-7.7,13.2c-0.6,10.3,9.4,19.7,18.3,27c2,1.7,4,3.2,5.7,4.6c6,4.8,12.2,9.6,17.2,15 c5.7,6.1,9.3,13.2,9.7,20.8c0.2,3.8-1,8.6-4.5,13.3c7.9-8,13.5-18.2,15.8-29.6C134.7,81.1,131.4,77.6,127.4,74.6z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M89.1,89.6c-6.5-12.9-17.9-21.5-25.5-34c-7.1-11.7-5.4-25.7,6.9-36.9c1.7-1.5,5.4-3,6.4-3.7 c-1,0.3-5.8,2.3-7,3C55.7,26.6,51.7,39.6,56.6,52c5.2,13.1,16.6,22.3,20.7,35.6c5.1,16.8-17.3,30.4-39.1,26.9 c5.7,5.8,12.6,10.4,20.3,13.4C78.4,118.8,97.8,106.9,89.1,89.6z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M118.2,102.7c-0.2-7.5-3.5-14.8-9.1-21.1c-4.9-5.5-10.9-10.5-16.8-15.5c-1.7-1.4-3.6-3-5.5-4.8 c-8.7-7.6-18.4-17.2-17.5-27.5c0.7-8.6,7.8-14.4,17.2-18.5c-0.9-0.1-1.8-0.2-2.8-0.2c-2.5,1.1-7.1,3.7-8.5,4.7 c-13.6,9.7-13.7,25.1-6.6,36.8C76.2,69.1,92.4,77,98.9,89.9c8.7,17.3-10.7,31.8-30.8,40.9c1.8,0.4,3.6,0.6,5.4,0.8 c2.1,0.2,4.4,0.3,6.6,0.3c10.3-0.1,19.9-2.8,28.3-7.5C118,117.7,118.4,108.6,118.2,102.7z"
  }))));
};

var BellaCadeauxLogo = function (_a) {
  var color = _a.color,
    width = _a.width;
  return jsx("svg", {
    version: "1.1",
    id: "Layer_1",
    xmlns: "http://www.w3.org/2000/svg",
    x: "0px",
    y: "0px",
    viewBox: "0 0 675 112.9",
    width: width
  }, jsx("g", null, jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M16,112.4H0.1V60.3h15.2c10.4,0,15.7,4.6,15.7,13.3c0,6.6-3.6,10.8-9.6,12.1c6.1,1.1,10.2,6,10.2,12.9 C31.8,107.7,26.3,112.4,16,112.4z M15,63.4H3.4V84h12.1c7.2,0,11.8-3.9,11.8-10.2C27.3,66.5,23.3,63.4,15,63.4z M15.6,87.3H3.4 v21.8h12.2c8.2,0,12.4-3.5,12.4-10.8C28,91.3,23.3,87.3,15.6,87.3z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M52.8,108.9h23.9v3.5H49.2V60.3h27.5v3.1H52.8v20.9h23.1v3.1H52.8V108.9z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M95.2,60.1h3.6v49h24.6v3.5H95.2C95.2,112.5,95.2,60.1,95.2,60.1z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M138.4,60.1h3.6v49h24.6v3.5h-28.3L138.4,60.1L138.4,60.1z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M219.1,99h-33.9l-6.8,13.8l-3.9-1.1l27.6-53.2l27.5,53.2l-3.5,0.9L219.1,99z M186.7,95.9h30.6l-15.2-30.6 L186.7,95.9z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M294.6,112.9c-15.9,0-28.1-11.6-28.1-26.7s12.2-26.7,28.1-26.7c8.6,0,16.8,3.6,23.1,10.7l-2.7,2 c-6-6.3-12.7-9.6-20.4-9.6c-13.8,0-24.5,10.2-24.5,23.4c0,13.3,10.7,23.5,24.5,23.5c8.2,0,14.3-3,20.9-9.6l2.7,2 C310.9,109.7,303.7,112.9,294.6,112.9z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M371.2,99h-34.1l-6.8,13.8l-3.6-1.1l27.6-53.2l27.5,53.2l-3.5,0.9L371.2,99z M339,95.9h30.6l-15.2-30.6 L339,95.9z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M413.1,112.4h-17.9V60.3h17.9c19,0,29.2,11.3,29.2,26.2C442.3,101.1,432.1,112.4,413.1,112.4z M413.1,63.4 h-14.3v45.5h14.3c17,0,25.6-10,25.6-22.9C438.7,73.5,430.1,63.4,413.1,63.4z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M463.3,108.9h23.9v3.5h-27.5V60.3h27.5v3.1h-23.9v20.9h23.2v3.1h-23.1v21.5 C463.4,108.9,463.3,108.9,463.3,108.9z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M543.4,99h-34.1l-6.7,13.8l-3.6-1.1l27.6-53.2l27.5,53.2l-3.5,0.9L543.4,99z M510.9,95.9h30.6l-15.2-30.6 L510.9,95.9z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M587.8,112.9c-15.7,0-23.1-9.1-23.1-25V60.1h3.6v27.8c0,14,6.3,21.5,19.5,21.5s19.5-7.4,19.5-21.5V60.1h3.6 v27.8C611.1,103.7,603.4,112.9,587.8,112.9z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M625.2,111l22.6-24.6l-22.6-25l3.5-1.9l21.5,24.2l21.5-24l3.1,1.9l-22.4,24.8l22.4,24.6l-3.1,2l-21.5-24.2 L628.6,113L625.2,111z"
  })), jsx("rect", {
    x: "0.1",
    y: "29.5",
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    width: "270.5",
    height: "2.5"
  }), jsx("rect", {
    x: "288.9",
    y: "29.5",
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    width: "386.2",
    height: "2.5"
  }), jsx("g", null, jsx("g", null, jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M275.6,33.6c0.9-4.2,0.6-7.7-0.5-9.6l-0.2-0.5c-0.9-1.6-2-3-3.1-4.1C265.2,13.7,257,6.5,248.3,5 c-2-0.5-3.9,0.2-5.5,1.6c-1.4,1.1-2.5,2.7-3.5,4.6c-0.5,0.6-0.6,1.6-0.6,2.5l0,0l0,0c0,0,0,0,0,0.2l0,0l0,0c0,0,0,0.2,0,0l0,0 c-0.2,2.2,0.5,4.6,2,6.3c8,9.6,18.8,11.6,30.1,12.2c-13.8,11.8-23.1,26.7-29.5,43.5c2.7-0.6,6-1.1,9.3-0.6 c5.7-15.2,25.1-40.8,25.1-40.8L275.6,33.6z M240.4,11.6c7.2-5.7,21.8,7.2,32.2,15.4C254.1,24.9,243.6,16.5,240.4,11.6z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M324.7,44.6c-14-3.1-26.2-7.7-35.8-14c15.7-2.7,25.9-8.6,30.1-17.9c0.6-1.6,1.1-3.5,0.9-5l0,0V7.5l0,0 c0,0,0,0,0-0.2l0,0c0,0,0,0,0-0.2l0,0V6.7l0,0c0,0,0,0,0-0.2l0,0c-0.5-2-1.6-3.6-3.5-5c-0.2-0.2-0.6-0.5-0.9-0.6 c-1.9-1.1-4.1-1.4-6-0.5c-5.7,2.2-9.7,6.6-13.8,11.3c0,0-8.2,8-12.4,12.1l0,0c0.5,0.5,0.6,1.4,0.9,2.5c0.2,0.9,0.2,2,0.5,3.1 c0.2,1.9,0.2,4.1,0,5c0,0.2-0.2,0.5-0.2,0.5c10.8,9.6,32.3,19,46,18.1c3-2.5,5.5-5.7,7.7-9.1C334.6,45.5,330.2,45.8,324.7,44.6z M318.8,6.1c-4.2,10.2-18.4,16.3-29.5,19.5C300.2,15.8,313.1,0,318.8,6.1z"
  })), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M282.3,24.5l-6.3-1.4c1.4,2.2,1.6,6.1,0.6,10.4c0.9,0.6,1.4,0.5,2.7,0.5c1.6,0,2.5,0.6,3.9,0.2 C283.8,34.1,283.4,26,282.3,24.5z"
  })));
};

var PromptingULogo = function (_a) {
  var color = _a.color,
    width = _a.width;
  return jsx("svg", {
    version: "1.1",
    id: "Layer_1",
    xmlns: "http://www.w3.org/2000/svg",
    x: "0px",
    y: "0px",
    viewBox: "0 0 324.7 34.2",
    width: width
  }, jsx("g", null, jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M16.6,13.6c0,4.7-3.5,7.5-9.4,7.5H3.8v8.6H0V6.5h7.7C13.6,6.5,16.6,9.3,16.6,13.6z M12.7,13.6c0-2.6-1.8-4-5-4 H3.8V18h3.5C10.8,18,12.7,16.7,12.7,13.6z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M48.8,29.7h-4.1c-3-4.8-1.4-10.1-7.5-10.1h-2v10.1h-3.8V6.5h8.3c5.9,0,8.9,2.7,8.9,6.3c0,3.2-2.8,5.1-6.6,5.7 v0.2C47.2,20.3,45.8,25.4,48.8,29.7z M44.7,13.3c0-2.3-1.9-3.7-5.5-3.7h-4v7.3H39C42.7,16.9,44.7,15.7,44.7,13.3z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M106.3,6.5h4.8l5.2,11.7l1.4,3.4h0.3l1.4-3.4l5.4-11.7h4.9v23.2h-3.8V12.2h-0.2l-1.5,3.6l-5.9,12.8h-1l-5.8-13 l-1.4-3.5h-0.2v17.5h-3.5V6.5z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M162.4,13.6c0,4.7-3.5,7.5-9.4,7.5h-3.5v8.6h-3.8V6.5h7.7C159.4,6.5,162.4,9.3,162.4,13.6z M158.5,13.6 c0-2.6-1.8-4-5-4h-3.9V18h3.5C156.6,18,158.5,16.7,158.5,13.6z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M194,9.8h-7.5v19.9h-3.8V9.8h-7.5V6.5H194V9.8z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M208.3,6.5h3.8v23.2h-3.8V6.5z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M228.2,6.5h4.2l8.9,14.5l1.9,3.4h0.2V6.5h3.5v23.2h-4.4L234,15.6l-2-3.5h-0.2v17.6h-3.5V6.5z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M262.2,18c0-7.2,4.6-12,10.8-12c4.4,0,7.7,2.2,9,5.6l-3.5,1.3c-0.8-2.2-2.7-3.5-5.4-3.5 c-4.1,0-6.9,3.3-6.9,8.5c0,5.2,2.6,8.6,6.6,8.6c4,0,5.5-2.6,5.5-5.6h-4.4v-3h8.2v11.7h-2.4l-0.8-4h-0.3c-1.2,3.1-3.6,4.4-6.4,4.4 C266.3,30.1,262.2,25.3,262.2,18z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M298,18.7V1.8c0-1.1-0.5-1.2-3.4-1.4V0h12v0.4c-2.9,0.3-3.4,0.3-3.4,1.4v19c0,5.8,2.8,8.5,7.8,8.5 c4.6,0,8.5-2.8,8.5-10.8V8.5c0-5-1.1-7.1-4.5-8.1V0h9.5v0.3c-3.5,1.1-4.5,2.9-4.5,8.1v10.1c0,8.2-3.9,11.7-10.5,11.7 C302.6,30.2,298,26.1,298,18.7z"
  })), jsx("g", null, jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M77.5,2.7c-8.7,0-15.8,7.1-15.8,15.8c0,8.7,7.1,15.8,15.8,15.8s15.8-7.1,15.8-15.8C93.2,9.8,86.2,2.7,77.5,2.7 z M77.5,32.5c-7.8,0-14-6.3-14-14c0-7.8,6.3-14,14-14s14,6.3,14,14C91.5,26.2,85.2,32.5,77.5,32.5z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M77.5,7.1c-6.3,0-11.4,5.1-11.4,11.4c0,6.3,5.1,11.4,11.4,11.4c6.3,0,11.4-5.1,11.4-11.4 C88.8,12.2,83.7,7.1,77.5,7.1z M83.2,19.3c0,0-6.9,4.1-8.3,4.9c-1.4,0.8-1.3-0.7-1.3-0.7l0.1-10.4c0-1.2,1.2-0.4,1.2-0.4l8.7,5.2 C84.8,18.6,83.2,19.3,83.2,19.3z"
  })));
};

var PromptingUIcon = function (_a) {
  var color = _a.color;
  return jsx("svg", {
    version: "1.1",
    id: "Layer_1",
    xmlns: "http://www.w3.org/2000/svg",
    x: "0px",
    y: "0px",
    viewBox: "0 0 72 72"
  }, jsx("g", null, jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M36,0C16.1,0,0,16.1,0,36c0,19.9,16.1,36,36,36s36-16.1,36-36C72,16.1,55.9,0,36,0z M36,68.1 C18.3,68.1,3.9,53.7,3.9,36C3.9,18.3,18.3,3.9,36,3.9S68.1,18.3,68.1,36C68.1,53.7,53.7,68.1,36,68.1z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M36,10c-14.3,0-26,11.6-26,26c0,14.3,11.6,26,26,26s26-11.6,26-26C62,21.7,50.3,10,36,10z M49.2,38 c0,0-15.7,9.4-18.9,11.1c-3.2,1.8-3-1.6-3-1.6l0.2-23.7c0-2.8,2.6-0.9,2.6-0.9l20,11.8C52.9,36.2,49.2,38,49.2,38z"
  })));
};

var SOCLogo = function (_a) {
  var color = _a.color,
    width = _a.width;
  return jsx("svg", {
    version: "1.1",
    id: "Layer_1",
    xmlns: "http://www.w3.org/2000/svg",
    x: "0px",
    y: "0px",
    viewBox: "0 0 681.6 63.7",
    width: width
  }, jsx("g", null, jsx("g", null, jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M62.2,4.7c-3-2.2-14.6,1.5-19.5,3.2C32.6,11.6,21.4,16.8,13,22.2c-4.2,3-4.4,3.5-4,4l0.2,0.2h0.2 c0.2,0,0.7,0.2,4.2-2.2C28.8,16.3,53.3,6.7,59,9.4c3.2,1.5,1.7,7.4-0.2,17.3c-1.5,7.2-3.5,15.8-4.4,26.2c-0.5,6.7,0,6.7,0.5,6.9 H55c0.5,0,1,0,2.5-6.2C58.7,45.9,65.9,7.6,62.2,4.7z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M29.1,49.1L29.1,49.1c3,0,4.7-2.2,6.4-4c0.5-0.5,1-1,1.5-1.5c0.7-0.7,1.5-1.5,2.5-2.2 c4.9-4.2,13.3-11.4,19.3-27.7c0.2-0.5,0.2-1-0.2-1.2c-0.5-0.5-1.2,0-2,0.5v0.2c-11.9,20.5-20.3,27.2-23,29.4L33,43 c-0.5,0.5-1,1-1.5,1.5c-1.2,1.2-2.2,2.7-4,2.5c-3-0.2-7.2-4.2-12.6-12.1c0,0-1.5-1.5-2.2-0.7c-0.7,0.5-0.2,1.5,1.2,3.2 C20.5,45.4,25.1,49.1,29.1,49.1z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M49.3,57.5c-16.3-3.7-24.5-2-29.9-1c-4.4,1-6.7,1.5-9.9-1.7c-2.5-2-2.2-4.7-2-4.9c0-1.2,0-1.7-0.5-2 c-0.2-0.2-0.5,0-0.7,0c-0.5,0.2-0.7,1.5-0.7,2c0,0.2,0,4.7,3.7,7.4l0,0c4.4,2.5,5.7,2.5,10.4,2c4.7-0.2,13.1-1,32.6,0.5 c0.5,0,1-0.2,1-0.7C53.3,59,53.1,58.8,49.3,57.5z"
  })), jsx("g", null, jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M100.7,56.3c-6.9,0-12.1-2.2-17.3-6.9l2-2.5c4.7,4.4,9.1,6.4,15.6,6.4c6.4,0,10.9-3.7,10.9-8.6l0,0 c0-4.4-2.5-7.2-12.1-9.1c-10.4-2.2-14.6-5.7-14.6-12.1v-0.2c0-6.4,5.9-11.4,13.8-11.4c6.2,0,10.4,1.7,14.8,5.2l-2,2.5 c-4-3.5-8.2-4.9-12.8-4.9c-6.4,0-10.6,3.7-10.6,8.2V23c0,4.4,2.2,7.4,12.6,9.4c9.9,2,14.1,5.7,14.1,11.9l0,0 C115.1,51.4,109.1,56.3,100.7,56.3z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M128.9,55.6V12.3h30.6v3h-27.4v17h24.7v3h-24.7v17.3H160v3H128.9z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M206.7,55.6l-30.4-38.3v38.3h-3V12.3h3l29.9,37.5V12.3h3v43.2H206.7z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M239.5,55.6h-14.3V12.3h14.3c13.6,0,23,9.4,23,21.5l0,0C262.5,46.2,253.1,55.6,239.5,55.6z M259,34.1 c0-10.1-7.7-18.5-19.5-18.5h-11.1v37.3h11.1C251.4,52.6,259,44.4,259,34.1L259,34.1z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M296.1,56.3c-13.1,0-21.7-10.4-21.7-22.2l0,0c0-11.9,8.6-22.2,21.7-22.2c13.1,0,21.7,10.4,21.7,22.2l0,0 C318.1,45.7,309.2,56.3,296.1,56.3z M314.6,34.1c0-10.6-7.7-19.5-18.5-19.5c-10.6,0-18.3,8.6-18.3,19.3l0,0 c0,10.6,7.7,19.5,18.5,19.5C307,53.3,314.6,44.7,314.6,34.1L314.6,34.1z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M349,56.3c-10.4,0-17.8-6.4-17.8-18.5V12.3h3.2v24.9c0,10.1,5.4,16.1,14.6,16.1c8.6,0,14.3-5.2,14.3-15.8 V12.3h3.2v24.9C366.7,49.6,359.6,56.3,349,56.3z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M397.1,15.3v40.3h-3.2V15.3h-15.1v-3h33.3v3H397.1z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M441.3,56.3c-12.3,0-21.5-9.9-21.5-22.2l0,0c0-12.1,9.1-22.2,21.5-22.2c7.7,0,12.3,2.7,16.8,6.9l-2.2,2.2 c-3.7-3.7-8.2-6.4-14.6-6.4c-10.4,0-18.3,8.4-18.3,19.3l0,0c0,10.9,7.9,19.5,18.3,19.5c6.2,0,10.4-2.5,14.8-6.7l2.2,2 C453.9,53.3,449,56.3,441.3,56.3z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M506.5,55.6l-5.4-12.1h-25.4l-5.4,12.1H467l20-43.5h3l20,43.5H506.5z M488.3,15.5l-11.4,24.9h22.7L488.3,15.5 z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M553.5,55.6l-13.8-17.8h-14.1v17.8h-3.2V12.3h18c9.4,0,15.3,4.9,15.3,12.3l0,0c0,7.2-5.4,11.4-12.6,12.3 l14.3,18.5H553.5z M552.7,24.7c0-5.7-4.4-9.4-12.3-9.4h-14.8v19.5h14.6C547.3,34.8,552.7,31.1,552.7,24.7L552.7,24.7z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M584.3,55.6H570V12.3h14.3c13.6,0,23,9.4,23,21.5l0,0C607.3,46.2,597.9,55.6,584.3,55.6z M604.1,34.1 c0-10.1-7.7-18.5-19.5-18.5h-11.4v37.3h11.1C596.4,52.6,604.1,44.4,604.1,34.1L604.1,34.1z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M635.5,56.3c-6.9,0-12.1-2.2-17.3-6.9l2-2.5c4.7,4.4,9.1,6.4,15.6,6.4c6.4,0,10.9-3.7,10.9-8.6l0,0 c0-4.4-2.5-7.2-12.1-9.1c-10.4-2.2-14.6-5.7-14.6-12.1v-0.2c0-6.4,5.9-11.4,13.8-11.4c6.2,0,10.4,1.7,14.8,5.2l-2,2.5 c-4-3.5-8.2-4.9-12.8-4.9c-6.4,0-10.6,3.7-10.6,8.2V23c0,4.4,2.2,7.4,12.6,9.4c9.9,2,14.1,5.7,14.1,11.9l0,0 C649.8,51.4,643.6,56.3,635.5,56.3z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M667.1,27.2c-4.9,0-8.9-4-8.9-8.9s4-8.9,8.9-8.9s8.9,4,8.9,8.9S672,27.2,667.1,27.2z M667.1,10.1 c-4.7,0-8.2,3.7-8.2,8.2s3.5,8.2,8.2,8.2s8.2-3.7,8.2-8.2S671.8,10.1,667.1,10.1z M668.6,18.8l3,3.7H670l-3-3.7h-2.7v3.7h-1v-8.9 h4c2,0,3.2,1,3.2,2.7C671,17.5,670,18.5,668.6,18.8z M667.6,14.6h-3V18h2.7c1.2,0,2.5-0.5,2.5-1.7C670,15.1,669.1,14.6,667.6,14.6 z"
  }))));
};

var SOCIcon = function (_a) {
  var color = _a.color;
  return jsx("svg", {
    version: "1.1",
    id: "Layer_1",
    xmlns: "http://www.w3.org/2000/svg",
    x: "0px",
    y: "0px",
    viewBox: "0 0 316 295.3"
  }, jsx("g", null, jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M290.5,20.5C271.7,6,146.7,48.7,60.3,103.7c-28.2,19.6-19.7,18.1,0.2,4.4c71.6-37.8,188.4-84,217.1-70.4 c28.8,13.6-9.5,89.3-20.6,210.4c-1.2,12.8-4.4,57.5,9.3,2.3C266.3,250.4,308.6,34.5,290.5,20.5z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M169.8,201.4c19.4-19.3,69.2-49.2,102.7-141.2c2.1-5.9-5.1-0.9-5.1-0.9c-60.2,103.2-100.8,133-112.3,142.6 c-15.5,12.8-25.7,52.8-90-38.9c-1.2-1.4-13.5-10.6-0.3,7.9C136.8,260.9,150.4,220.7,169.8,201.4z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M230.8,272.1C90.7,239.6,78.1,293.1,40.2,259.1c-13-11.4-10.8-25.7-10.8-25.7c0.5-12.9-4.1-4.2-3.3,0.4 c0,0-0.6,20.6,16.8,33.4c41.2,23.4,21.8-2.5,202.6,12.4C254,279.5,230.8,272.1,230.8,272.1z"
  })));
};

var StreamVALogo = function (_a) {
  var color = _a.color,
    width = _a.width;
  return jsx("svg", {
    version: "1.1",
    id: "Layer_1",
    xmlns: "http://www.w3.org/2000/svg",
    x: "0px",
    y: "0px",
    viewBox: "0 0 260.1 67.5",
    width: width
  }, jsx("g", null, jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M29.9,67.2c1.3,0.2,2.7,0.3,4.1,0.3c10.1,0,19.1-4.4,25.3-11.3l-4-12h-19L29.9,67.2z M53,36.6l-4.4-14.5 l-2.8-10h-0.6l-2.7,10l-3.7,12.5l-0.5,1.9H53z M2.5,21C0.9,24.9,0,29.2,0,33.7C0,45.6,6.2,56,15.5,62L2.5,21z M40.1,0.6 C38.1,0.2,36.1,0,34,0C24.2,0,15.4,4.1,9.2,10.7l11,38.2l2.9,10.7h0.5l0.5-1.9H24l1.7-6.2l0.7-2.6l0.7-2.5L40.1,0.6z M65.5,46.3 c1.6-3.9,2.5-8.1,2.5-12.6c0-11.9-6.3-22.4-15.7-28.4L65.5,46.3z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M259.5,53.5c-0.5,0.4-1,0.6-1.5,0.6c-1,0-1.4-0.6-1.4-1.6v-5h3.2V46h-3.2v-3H255l-0.1,2.1 c0,0.6-0.3,0.9-0.8,0.9l-1.1,0.1v1.4h1.7v5.2c0,2,1.1,3.1,2.8,3.1c0.9,0,1.7-0.2,2.4-0.8L259.5,53.5z M246.5,45.8 c-1.4,0-2.4,0.9-3.1,1.9l-0.1-0.1l0-1.6h-1.6v9.5h1.8v-6.8c0.6-0.7,1.5-1.2,2.5-1.2c1,0,1.8,0.6,1.8,2.3v5.7h1.7v-6.2 C249.5,46.8,248,45.8,246.5,45.8 M232.2,54.3c-0.8,0-1.2-0.4-1.2-1.1c0-0.7,0.4-1.2,1.8-1.6l2-0.6v2.2 C234,53.8,233.1,54.3,232.2,54.3 M237.1,54.2c-0.5,0-0.7-0.2-0.7-1v-4.6c0-1.7-1-2.8-3.5-2.8c-2.1,0-3.6,1.1-3.5,2.9L231,49 c0-1,0.6-1.7,2-1.7c1.3,0,1.7,0.6,1.7,1.6v0.9l-2.5,0.7c-2.1,0.6-3,1.5-3,2.8c0,1.5,0.9,2.5,2.6,2.5c1.5,0,2.2-0.6,3-1.8l0.1,0 c0.2,1.2,0.7,1.7,1.8,1.7c0.5,0,1-0.1,1.3-0.3V54C237.6,54.1,237.4,54.2,237.1,54.2 M225.3,53.5c-0.5,0.4-1,0.6-1.5,0.6 c-1,0-1.4-0.6-1.4-1.6v-5h3.2V46h-3.2v-3h-1.6l-0.1,2.1c0,0.6-0.3,0.9-0.8,0.9l-1.1,0.1v1.4h1.7v5.2c0,2,1.1,3.1,2.9,3.1 c0.9,0,1.7-0.2,2.4-0.8L225.3,53.5z M212.2,55.7c2.1,0,3.5-1,3.5-2.9c0-4-5.4-1.8-5.4-4.2c0-0.7,0.7-1.4,1.8-1.4 c1.2,0,1.8,0.6,1.9,1.6l1.6-0.2c-0.1-1.5-1.1-2.6-3.4-2.6c-2.4,0-3.5,1.4-3.5,2.9c0,4,5.5,1.8,5.5,4.3c0,0.8-0.7,1.3-1.9,1.3 c-1.2,0-2-0.6-2.2-1.7l-1.6,0.3C208.6,54.6,209.9,55.7,212.2,55.7 M202.3,55.5h1.8V46h-1.8V55.5z M203.2,44.2 c0.7,0,1.2-0.5,1.2-1.1c0-0.6-0.5-1.1-1.2-1.1S202,42.4,202,43C202,43.7,202.5,44.2,203.2,44.2 M194.4,55.7c2.1,0,3.5-1,3.5-2.9 c0-4-5.4-1.8-5.4-4.2c0-0.7,0.7-1.4,1.8-1.4c1.2,0,1.8,0.6,1.9,1.6l1.6-0.2c-0.1-1.5-1.1-2.6-3.4-2.6c-2.4,0-3.5,1.4-3.5,2.9 c0,4,5.5,1.8,5.5,4.3c0,0.8-0.7,1.3-1.9,1.3c-1.2,0-2-0.6-2.2-1.7l-1.6,0.3C190.8,54.6,192.1,55.7,194.4,55.7 M183.4,55.7 c2.1,0,3.5-1,3.5-2.9c0-4-5.4-1.8-5.4-4.2c0-0.7,0.7-1.4,1.8-1.4c1.2,0,1.8,0.6,1.9,1.6l1.6-0.2c-0.1-1.5-1.1-2.6-3.4-2.6 c-2.4,0-3.5,1.4-3.5,2.9c0,4,5.5,1.8,5.5,4.3c0,0.8-0.7,1.3-1.9,1.3c-1.2,0-2-0.6-2.2-1.7l-1.6,0.3 C179.8,54.6,181.1,55.7,183.4,55.7 M171,54.3c-0.8,0-1.2-0.4-1.2-1.1c0-0.7,0.4-1.2,1.8-1.6l2-0.6v2.2 C172.8,53.8,171.9,54.3,171,54.3 M175.9,54.2c-0.5,0-0.7-0.2-0.7-1v-4.6c0-1.7-1-2.8-3.5-2.8c-2.1,0-3.6,1.1-3.5,2.9l1.6,0.2 c0-1,0.6-1.7,2-1.7c1.3,0,1.7,0.6,1.7,1.6v0.9l-2.5,0.7c-2.1,0.6-3,1.5-3,2.8c0,1.5,0.9,2.5,2.6,2.5c1.5,0,2.2-0.6,3-1.8l0.1,0 c0.2,1.2,0.7,1.7,1.8,1.7c0.5,0,1-0.1,1.3-0.3V54C176.4,54.1,176.2,54.2,175.9,54.2 M155.7,55.5h1.8V42.1h-1.8V55.5z M146.1,54.3 c-0.8,0-1.2-0.4-1.2-1.1c0-0.7,0.4-1.2,1.8-1.6l2-0.6v2.2C147.9,53.8,147.1,54.3,146.1,54.3 M151.1,54.2c-0.5,0-0.7-0.2-0.7-1v-4.6 c0-1.7-1-2.8-3.5-2.8c-2.1,0-3.6,1.1-3.5,2.9l1.6,0.2c0-1,0.6-1.7,2-1.7c1.3,0,1.7,0.6,1.7,1.6v0.9l-2.5,0.7c-2.1,0.6-3,1.5-3,2.8 c0,1.5,0.9,2.5,2.6,2.5c1.5,0,2.2-0.6,3-1.8l0.1,0c0.2,1.2,0.7,1.7,1.8,1.7c0.5,0,1-0.1,1.3-0.3V54 C151.5,54.1,151.3,54.2,151.1,54.2 M136.9,46v6.8c-0.6,0.7-1.5,1.2-2.5,1.2c-1.1,0-1.8-0.6-1.8-2.3V46h-1.7v6.2 c0,2.5,1.5,3.5,3,3.5c1.5,0,2.4-0.9,3.1-1.9l0.1,0l0,1.6h1.6V46H136.9z M126.5,53.5c-0.5,0.4-1,0.6-1.5,0.6c-1,0-1.4-0.6-1.4-1.6 v-5h3.2V46h-3.2v-3h-1.6l-0.1,2.1c0,0.6-0.3,0.9-0.8,0.9l-1.1,0.1v1.4h1.7v5.2c0,2,1.1,3.1,2.8,3.1c0.9,0,1.7-0.2,2.4-0.8 L126.5,53.5z M116.2,45.8c-1.3,0-2.1,0.9-2.8,2.3l-0.1-0.1l0-2h-1.6v9.5h1.8v-6.4c0.7-1,1.7-1.6,2.7-1.6c0.2,0,0.4,0,0.7,0.1 l0.2-1.6C116.7,45.9,116.5,45.8,116.2,45.8 M104.9,55.5h1.8V46h-1.8V55.5z M105.7,44.2c0.7,0,1.2-0.5,1.2-1.1 c0-0.6-0.5-1.1-1.2-1.1c-0.7,0-1.2,0.5-1.2,1.1C104.5,43.7,105.1,44.2,105.7,44.2 M95.5,55.5h1.9l3.4-9.5h-2L97,52.2l-0.4,1.2h-0.1 l-0.3-1.2l-2-6.2h-2.1L95.5,55.5z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M236.8,36.3h4.2V21.4h0.2l1.2,3.2l4.9,11.7h1.2l5-11.4l1.2-3.4h0.3v14.8h4.5V14.6h-5.8l-4.3,10l-1.2,3.3h-0.3 l-1.2-3.3l-4-10h-5.7V36.3z M212.6,27.8l1.2-4.7l1-4.1h0.3l1,4.1l1.3,4.7H212.6z M219.6,36.3h5L218,14.6h-5.8l-6.7,21.7h4.8 l1.3-4.7h6.8L219.6,36.3z M184.5,32.3V27h8.3v-3.9h-8.3v-4.5h9.5v-4h-14v21.7h14.5v-4H184.5z M158.1,24.2h-3.2v-5.9h3.2 c2.9,0,4.3,1.1,4.3,3C162.4,23.3,160.9,24.2,158.1,24.2 M161.6,26.4v-0.3c3.2-0.8,5.4-2.6,5.4-5.4c0-3.4-2.7-6-8.4-6h-8.4v21.7h4.5 v-8.9h1.5c5.1,0,3.4,4.8,5.9,8.9h4.9C164.5,32.5,166.1,28,161.6,26.4 M138,14.6h-17.3v4h6.4v17.7h4.5V18.6h6.4V14.6z M101.5,36.7 c4.9,0,8.1-2.2,8.1-6.8c0-9.1-11.8-4.4-11.8-9.4c0-1.5,1.3-2.6,3.5-2.6c2.5,0,3.6,1.4,4,3.1l4.2-0.7c-0.5-3.3-2.7-6.1-8.1-6.1 c-5.5,0-8,3.3-8,6.5c0,9.4,11.7,4.4,11.7,9.7c0,1.5-1.3,2.4-3.6,2.4c-2.2,0-3.8-1-4.4-3.5l-4.2,0.9C93.3,34,95.9,36.7,101.5,36.7"
  })));
};

var StreamVAIcon = function (_a) {
  var color = _a.color;
  return jsx("svg", {
    version: "1.1",
    id: "Layer_1",
    xmlns: "http://www.w3.org/2000/svg",
    x: "0px",
    y: "0px",
    viewBox: "0 0 72 71.5"
  }, jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M31.7,71.2c1.4,0.2,2.9,0.3,4.3,0.3c10.7,0,20.2-4.6,26.8-12l-4.3-12.7H38.4L31.7,71.2z M56.2,38.8l-4.6-15.3l-2.9-10.6H48 l-2.9,10.6l-3.9,13.3l-0.6,2.1H56.2z M2.7,22.2C1,26.4,0,31,0,35.8c0,12.6,6.5,23.6,16.4,30L2.7,22.2z M42.5,0.6 C40.4,0.2,38.2,0,36,0C25.6,0,16.3,4.4,9.7,11.4l11.6,40.5l3.1,11.3H25l0.5-2h-0.1l1.8-6.5l0.8-2.8l0.8-2.6L42.5,0.6z M69.4,49.1 C71.1,45,72,40.5,72,35.8C72,23.1,65.4,12,55.4,5.7L69.4,49.1z"
  }));
};

var StreamMarketingLogo = function (_a) {
  var color = _a.color,
    width = _a.width;
  return jsx("svg", {
    version: "1.1",
    id: "Layer_1",
    xmlns: "http://www.w3.org/2000/svg",
    x: "0px",
    y: "0px",
    viewBox: "0 0 597.6 172.8",
    width: width
  }, jsx("g", null, jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M9.1,85.7L22.2,70c8.4,6.7,17.8,10.2,27.6,10.2c6.3,0,9.7-2.2,9.7-5.8v-0.2c0-3.5-2.8-5.5-14.4-8.2 c-18.1-4.1-32.2-9.3-32.2-26.8v-0.2c0-15.8,12.5-27.3,33-27.3c14.5,0,25.8,3.9,35.1,11.3L69.4,39.7c-7.8-5.5-16.3-8.4-23.9-8.4 c-5.7,0-8.5,2.4-8.5,5.5V37c0,3.9,2.9,5.6,14.7,8.3c19.6,4.3,31.8,10.6,31.8,26.6v0.2c0,17.4-13.8,27.8-34.5,27.8 C33.9,99.8,19.5,95.1,9.1,85.7z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M115.5,33.8H89.9V13.1h74.8v20.7h-25.6v64.5h-23.6V33.8z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M178.1,13.1h40.3c13,0,22,3.4,27.8,9.3c5,4.9,7.6,11.4,7.6,19.9v0.2c0,13-6.9,21.7-17.5,26.2l20.3,29.7h-27.3l-17.2-25.8 h-0.2h-10.1v25.8h-23.6V13.1z M217.3,54c8,0,12.7-3.9,12.7-10.1v-0.2c0-6.7-4.9-10.1-12.8-10.1h-15.5V54H217.3z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M268.7,13.1h68.6v20.1h-45.2v12.9H333v18.6h-40.9v13.5h45.8v20.1h-69.2V13.1z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M382.4,12.5h22.8l36.3,85.9h-25.3L410,83.1h-32.9L371,98.4h-24.8L382.4,12.5z M403.1,64.7l-9.5-24.2L384,64.7H403.1z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M450.7,13.1h25l20.3,33l20.3-33h25v85.3h-23.5v-49L496,82.8h-0.5l-21.7-33.1v48.7h-23.1V13.1z"
  })), jsx("g", null, jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M9.2,122.1h8.2l6.7,10.8l6.7-10.8H39v28h-7.7V134l-7.2,11h-0.2l-7.1-10.9v16H9.2V122.1z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M55.6,121.9h7.5l11.9,28.2h-8.3l-2-5H53.9l-2,5h-8.2L55.6,121.9z M62.5,139.1l-3.1-8l-3.2,8H62.5z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M80.8,122.1h13.3c4.3,0,7.3,1.1,9.1,3c1.6,1.6,2.5,3.8,2.5,6.5v0.1c0,4.3-2.3,7.1-5.8,8.6l6.7,9.8h-9l-5.6-8.5h-0.1h-3.3 v8.5h-7.8V122.1z M93.7,135.6c2.6,0,4.2-1.3,4.2-3.3v-0.1c0-2.2-1.6-3.3-4.2-3.3h-5.1v6.7H93.7z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M115.4,122.1h7.8v11.4l9.7-11.4h9.2l-10.6,12l11,16h-9.3l-7.1-10.4l-2.8,3.2v7.2h-7.8V122.1z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M148.9,122.1h22.6v6.6h-14.9v4.2H170v6.1h-13.5v4.4h15.1v6.6h-22.8V122.1z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M186.4,128.9H178v-6.8h24.6v6.8h-8.4v21.2h-7.8V128.9z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M210.2,122.1h7.8v28h-7.8V122.1z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M229.7,122.1h7.3l11.5,14.8v-14.8h7.7v28h-6.8l-12-15.4v15.4h-7.7V122.1z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M264.2,136.2L264.2,136.2c0-8.2,6.4-14.7,15.1-14.7c4.9,0,8.4,1.5,11.4,4.1l-4.6,5.5c-2-1.7-4-2.6-6.8-2.6 c-4,0-7.2,3.4-7.2,7.7v0.1c0,4.5,3.2,7.8,7.6,7.8c1.9,0,3.3-0.4,4.5-1.2v-3.4h-5.5v-5.7h12.9v12.6c-3,2.4-7.1,4.3-12.2,4.3 C270.8,150.7,264.2,144.7,264.2,136.2z"
  })), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M310.5,113.2c0.3-0.8-0.4-1.7,0.7-2.1c1-0.3,1.4,0.5,1.8,1.1c0.7,1,1.4,2.2,2.1,3.3c5.2,8.1,12.9,13,21.5,16.9 c13.9,6.3,28.5,8.6,43.6,8.5c21.2-0.2,41.8-4.6,62.2-9.8c16.6-4.2,33.1-9.1,49.9-12.4c25.7-5,51.1-4.5,76.3,3.2 c5.1,1.6,10,3.8,14.9,5.9c0.6,0.3,1.8,0.4,1.5,1.3c-0.4,1-1.5,0.6-2.3,0.3c-5.8-1.9-11.7-3.5-17.8-4.6c-14.1-2.7-28.2-3.3-42.5-2.1 c-19.6,1.6-38.2,7.5-56.9,13c-19.1,5.6-38.4,10.6-58.2,13c-22.5,2.7-44.6,1.8-66.1-6.5c-8.6-3.3-16.3-8.2-22.9-14.8 c-3.5-3.5-5.3-8.1-7.6-12.3C310.5,114.6,310.6,113.8,310.5,113.2z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M558.9,133c-27.2-0.1-52.7,8.4-78.4,15.6c-17,4.8-34.2,9.1-51.8,11.1c-12.4,1.4-24.8,1.7-37.2,0.1c-1.2-0.2-2.4-0.3-3.5-1.5 c2.7-0.9,5.4-0.7,7.9-0.7c26.6-0.3,51.8-7.7,77.1-14.5c13.8-3.7,27.6-7.7,41.8-9.9c12.4-1.9,24.8-3.2,37.4-1.9 C554.4,131.5,556.7,131.9,558.9,133z"
  }));
};

var Blue42Logo = function (_a) {
  var color = _a.color,
    width = _a.width;
  return jsx("svg", {
    version: "1.1",
    id: "Layer_1",
    xmlns: "http://www.w3.org/2000/svg",
    x: "0px",
    y: "0px",
    viewBox: "0 0 390.9 70.4",
    width: width
  }, jsx("g", null, jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M390.2,31.6c-0.3,0-0.5,0.1-0.6,0.3c-0.1,0.2-0.2,0.7-0.3,1.5c-0.4,2.4-0.8,3.9-1.4,4.4 c-0.6,0.5-2,0.8-4.3,0.9c-2.8,0.1-6,0.1-9.8-0.1c-0.7,0-1.1-0.1-1.1-0.2c0-0.1,0.2-0.2,0.6-0.2c0.7-0.2,2-0.9,4.1-2 c2.1-1.1,3.5-1.9,4.3-2.5c4.1-2.7,6.1-5.7,6.1-9c0-2.4-1-4.5-3-6.4c-2-1.9-4.6-2.8-8-2.8c-2.9,0-5.4,0.8-7.4,2.3 c-2,1.5-3,3.4-3,5.6c0,1.2,0.4,2.3,1.1,3.1c0.7,0.9,1.6,1.3,2.6,1.3c0.7,0,1.3-0.3,1.9-0.8c0.5-0.5,0.8-1.1,0.8-1.9 c0-1.1-0.6-1.7-1.9-2c-1.3-0.3-2-0.5-2.2-0.6c-0.2-0.1-0.3-0.4-0.3-0.8c0-1,0.6-2,1.9-3c1.3-1.1,2.9-1.6,4.9-1.6 c2.1,0,3.9,0.7,5.2,2c1.3,1.4,2,3.1,2,5.3c0,2.4-0.9,4.9-2.8,7.3c-1.9,2.4-4.3,4.4-7.4,6c-5.1,2.6-7.7,4.1-7.7,4.4 c0,0.4,0.3,0.5,0.8,0.5h0.4c2.4-0.2,6.3-0.4,11.6-0.4c0.9,0,2.4,0,4.4,0.1c4,0.2,6.7,0.2,8.1,0.2c0-1.2,0.2-2.9,0.5-5.3 c0.4-2.3,0.5-4.1,0.5-5.2C390.9,31.8,390.7,31.6,390.2,31.6 M359.7,41.1c0,0-0.1,0-0.5,0s-0.9,0.1-1.8,0.1h-2.3V25.4H350v15.9h-7.4 c-2.3,0-4.6,0-6.8-0.1l2.1-2.7c2.3-3.1,4.5-6.1,6.6-8.9c2.7-3.7,5.1-6.9,7.3-9.5c2.5-3.1,3.8-4.8,3.8-5.1c0-0.4-0.2-0.5-0.5-0.5 c-0.3,0-0.9,0.5-1.6,1.4l-18.2,23.8c-1,1.3-1.5,2.1-1.5,2.4c0,0.6,0.4,0.9,1.2,0.9h15v13.4h5.1V42.7h4.4c0.6,0,1-0.2,1-0.7 C360.5,41.4,360.2,41.1,359.7,41.1"
  }), jsx("line", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    stroke: color === 'white' ? '#FFFFFF' : '#000000',
    strokeWidth: "1.5183",
    x1: "301.2",
    y1: "0",
    x2: "301.2",
    y2: "70.4"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M261,46.5c-6.4,16-9.1,19.3-17.4,19.3h-15.6V33.5l20.3,0.4v-2.2l-20.3,0.4V3.5h13.3c7.9,0,10.2,2.5,17.4,18.2 h1l-0.8-19.4h-49.7V3c6.2,0.6,7.3,0.7,7.3,3.1V63c0,2.4-1.1,2.5-7.3,3.1v0.8h52.1l0.7-20.5H261z M164.1,68.1 c14.4,0,22.9-7.5,22.9-25.5v-22c0-11.3,2.2-15.3,9.7-17.8V2.2h-20.8v0.7c7.4,2.2,9.7,6.8,9.7,17.8v22c0,17.4-8.6,23.5-18.6,23.5 c-11,0-17-6-17-18.6V6.2c0-2.4,1.1-2.5,7.3-3.1V2.2h-26.1V3c6.2,0.6,7.3,0.7,7.3,3.1V43C138.6,59.1,148.5,68.1,164.1,68.1 M118.5,42.5c-7.6,20.3-9.6,23.3-18.3,23.3H88.8V6.2c0-2.4,1.2-2.5,8.7-3.1V2.2H70.1V3c6.2,0.6,7.3,0.7,7.3,3.1V63 c0,2.4-1.1,2.5-7.3,3.1v0.8h48.6l0.7-24.5H118.5z M28,65.9h-9.2V33.4h8.5c11.9,0,16.2,4.3,16.2,16.3S38.8,65.9,28,65.9 M26,3.2 c9.9,0,14.1,3.7,14.1,14.4c0,10.6-4,14.5-14.6,14.5h-6.7v-29H26z M31.7,32.7c14.1-1.1,20.9-7.8,20.9-15.3c0-8.7-7.8-15.1-26.5-15.1 H0V3c6.2,0.6,7.3,0.7,7.3,3.1V63c0,2.4-1.1,2.5-7.3,3.1v0.8h28.1c18.9,0,27.8-7.1,27.8-16.9C55.9,40.7,48,33.3,31.7,32.7"
  })));
};

var PromptingsAcademyLogo = function (_a) {
  var color = _a.color,
    width = _a.width;
  return jsx("svg", {
    version: "1.1",
    id: "Layer_1",
    xmlns: "http://www.w3.org/2000/svg",
    x: "0px",
    y: "0px",
    viewBox: "0 0 379 118.22",
    width: width
  }, jsx("g", null, jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M114.53,50.9h-4.43v7.96h-5.83v-26.52h10.84c6.33,0,10.15,3.75,10.15,9.17v.08c0,6.14-4.77,9.32-10.72,9.32Zm4.81-9.28c0-2.61-1.82-4.02-4.74-4.02h-4.51v8.11h4.62c2.92,0,4.62-1.74,4.62-4.02v-.08Z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M145.48,58.86l-5.68-8.49h-4.58v8.49h-5.83v-26.52h12.12c6.25,0,10,3.3,10,8.75v.08c0,4.28-2.31,6.97-5.68,8.22l6.48,9.47h-6.82Zm.11-17.47c0-2.5-1.74-3.79-4.58-3.79h-5.8v7.62h5.91c2.84,0,4.47-1.52,4.47-3.75v-.08Z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M169.17,59.32c-8.18,0-14.06-6.1-14.06-13.64v-.08c0-7.54,5.95-13.71,14.13-13.71s14.06,6.1,14.06,13.64v.08c0,7.54-5.95,13.72-14.13,13.72Zm8.03-13.72c0-4.55-3.33-8.33-8.03-8.33s-7.96,3.71-7.96,8.26v.08c0,4.55,3.33,8.33,8.03,8.33s7.96-3.71,7.96-8.26v-.08Z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M209.06,58.86v-17.31l-7.46,11.33h-.15l-7.39-11.21v17.2h-5.72v-26.52h6.29l6.97,11.21,6.97-11.21h6.29v26.52h-5.8Z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M231.58,50.9h-4.43v7.96h-5.83v-26.52h10.83c6.33,0,10.15,3.75,10.15,9.17v.08c0,6.14-4.77,9.32-10.72,9.32Zm4.81-9.28c0-2.61-1.82-4.02-4.74-4.02h-4.51v8.11h4.62c2.92,0,4.62-1.74,4.62-4.02v-.08Z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M258.81,37.72v21.14h-5.83v-21.14h-8.07v-5.38h21.97v5.38h-8.07Z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M270.88,58.86v-26.52h5.83v26.52h-5.83Z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M270.88,58.86v-26.52h5.83v26.52h-5.83Z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M301.82,58.86l-12.84-16.86v16.86h-5.76v-26.52h5.38l12.43,16.33v-16.33h5.76v26.52h-4.96Z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M325.81,59.32c-8.26,0-13.94-5.8-13.94-13.64v-.08c0-7.54,5.87-13.71,13.91-13.71,4.77,0,7.65,1.29,10.42,3.64l-3.67,4.43c-2.05-1.7-3.87-2.69-6.93-2.69-4.24,0-7.62,3.75-7.62,8.26v.08c0,4.85,3.33,8.41,8.03,8.41,2.12,0,4.02-.53,5.49-1.59v-3.79h-5.87v-5.04h11.52v11.52c-2.73,2.31-6.48,4.21-11.33,4.21Z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M351.96,59.24c-4.02,0-8.07-1.4-11.25-4.24l3.45-4.13c2.39,1.97,4.89,3.22,7.92,3.22,2.39,0,3.83-.95,3.83-2.5v-.08c0-1.48-.91-2.24-5.34-3.37-5.34-1.36-8.79-2.84-8.79-8.11v-.08c0-4.81,3.86-7.99,9.28-7.99,3.87,0,7.16,1.21,9.85,3.37l-3.03,4.4c-2.35-1.63-4.66-2.61-6.9-2.61s-3.41,1.02-3.41,2.31v.08c0,1.74,1.14,2.31,5.72,3.49,5.38,1.4,8.41,3.33,8.41,7.96v.08c0,5.27-4.02,8.22-9.74,8.22Z"
  })), jsx("g", null, jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M128.54,90.5h-3.11l-3-6.77h-13.95l-3.04,6.77h-2.96l11.68-25.8h2.71l11.68,25.8Zm-13.07-22.44l-5.86,13.07h11.68l-5.82-13.07Z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M151.12,90.94c-7.39,0-12.85-5.82-12.85-13.21s5.42-13.29,12.96-13.29c4.65,0,7.43,1.65,9.99,4.06l-1.98,2.12c-2.16-2.05-4.58-3.51-8.05-3.51-5.67,0-9.92,4.57-9.92,10.54s4.28,10.62,9.92,10.62c3.51,0,5.82-1.35,8.27-3.7l1.9,1.87c-2.67,2.71-5.6,4.5-10.25,4.5Z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M197.61,90.5h-3.11l-3-6.77h-13.95l-3.04,6.77h-2.96l11.68-25.8h2.71l11.68,25.8Zm-13.07-22.44l-5.86,13.07h11.68l-5.82-13.07Z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M218.88,90.5h-8.89v-25.62h8.89c8.05,0,13.62,5.53,13.62,12.77s-5.56,12.85-13.62,12.85Zm0-22.95h-6v20.28h6c6.48,0,10.61-4.39,10.61-10.1s-4.14-10.18-10.61-10.18Z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M264.45,67.51h-15.63v8.75h13.98v2.64h-13.98v8.97h15.81v2.64h-18.7v-25.62h18.52v2.64Z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M290.08,83.47h-.15l-9.33-13.69v20.72h-2.82v-25.62h2.93l9.33,13.98,9.33-13.98h2.93v25.62h-2.89v-20.75l-9.33,13.73Z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M327.82,90.5h-2.93v-10.14l-10.65-15.48h3.51l8.64,12.81,8.71-12.81h3.37l-10.65,15.45v10.18Z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M345.73,70.26h-.75v-4.54h-1.69v-.72h4.13v.72h-1.69v4.54Zm5.56-1.18h-.1l-1.86-2.85v4.02h-.75v-5.25h.8l1.88,2.88,1.86-2.88h.78v5.25h-.73v-4.02l-1.88,2.85Z"
  })), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M95.81,46.32c-.65-4.3-2.61-8.44-5.92-11.74-7.61-7.62-19.61-8.18-27.87-1.71l8.38,8.32c3.6-1.99,8.23-1.46,11.28,1.59,1.03,1.03,1.43,2.24,1.88,3.53l.07,3.14c-4.77-2.23-10.38-2.71-15.76-.87l-28.93,9.89c-4.95,1.69-10.35-.95-12.04-5.91-1.69-4.96,.96-10.35,5.91-12.05,4.95-1.69,10.35,.95,12.04,5.91l2.09,7.34,10.99-3.76-2.1-7.34c-3.75-11.03-15.75-16.91-26.77-13.14-8.3,2.83-13.69,10.33-14.24,18.57l-.05-.02v41.55h12.27v-20.99c4.75,2.19,10.31,2.65,15.65,.82l28.95-9.88c4.96-1.69,10.35,.96,12.04,5.91,1.69,4.96-.96,10.35-5.91,12.05-4.95,1.69-10.35-.96-12.04-5.91l-2.1-7.35-10.99,3.77,1.59,4.67c4.26,13.69,16.25,19.57,27.29,15.8,8.97-3.06,14.54-11.57,14.28-20.57h.01v-21.64Z"
  }));
};

var PromptingsAcademyIcon = function (_a) {
  var color = _a.color;
  return jsx("svg", {
    version: "1.1",
    id: "Layer_1",
    xmlns: "http://www.w3.org/2000/svg",
    x: "0px",
    y: "0px",
    viewBox: "10 13 90 90"
  }, jsx("g", null, jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    xmlns: "http://www.w3.org/2000/svg",
    d: "M95.81,46.32c-.65-4.3-2.61-8.44-5.92-11.74-7.61-7.62-19.61-8.18-27.87-1.71l8.38,8.32c3.6-1.99,8.23-1.46,11.28,1.59,1.03,1.03,1.43,2.24,1.88,3.53l.07,3.14c-4.77-2.23-10.38-2.71-15.76-.87l-28.93,9.89c-4.95,1.69-10.35-.95-12.04-5.91-1.69-4.96,.96-10.35,5.91-12.05,4.95-1.69,10.35,.95,12.04,5.91l2.09,7.34,10.99-3.76-2.1-7.34c-3.75-11.03-15.75-16.91-26.77-13.14-8.3,2.83-13.69,10.33-14.24,18.57l-.05-.02v41.55h12.27v-20.99c4.75,2.19,10.31,2.65,15.65,.82l28.95-9.88c4.96-1.69,10.35,.96,12.04,5.91,1.69,4.96-.96,10.35-5.91,12.05-4.95,1.69-10.35-.96-12.04-5.91l-2.1-7.35-10.99,3.77,1.59,4.67c4.26,13.69,16.25,19.57,27.29,15.8,8.97-3.06,14.54-11.57,14.28-20.57h.01v-21.64Z"
  })));
};

var PromptingsLogo = function (_a) {
  var color = _a.color,
    width = _a.width;
  return jsx("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    version: "1.1",
    x: "0px",
    y: "0px",
    viewBox: "0 0 1925.3 393",
    width: width
  }, jsx("g", null, jsx("g", null, jsx("g", null, jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M555.4,123.7c9.4,0,17.5,1.3,24.3,4c6.8,2.7,12.4,6.4,16.9,11.1c4.4,4.7,7.7,10.3,9.9,16.7c2.2,6.4,3.3,13.5,3.3,21.2     c0,13.3-2,26.1-6,38.5c-4,12.4-10.2,23.4-18.6,33c-8.4,9.6-18.9,17.3-31.7,23c-12.8,5.8-27.9,8.6-45.4,8.6     c-2.1,0-4.4-0.1-6.9-0.3c-2.5-0.2-4.7-0.5-6.6-0.9l-12.4,51.9h-26.8l47.2-196.8c8.1-3.3,16.9-5.8,26.4-7.5     C538.6,124.6,547.3,123.7,555.4,123.7z M500.1,256.3c1.9,0.4,4,0.6,6.2,0.7c2.2,0.1,4.4,0.1,6.5,0.1c10.9,0,20.7-2.1,29.4-6.3     c8.6-4.2,16-9.9,22-17.1c6-7.2,10.7-15.5,14-24.8c3.3-9.3,4.9-19.1,4.9-29.2c0-4.2-0.5-8.4-1.4-12.4c-1-4-2.7-7.6-5.3-10.7     c-2.6-3.1-6.1-5.5-10.5-7.3c-4.4-1.8-10.1-2.7-17-2.7c-4.8,0-9.2,0.4-13.3,1.3c-4,0.9-7.4,1.7-10.1,2.4L500.1,256.3z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M708.5,124.3c2.3,0,4.9,0.1,7.8,0.3c2.9,0.2,5.7,0.5,8.5,1c2.8,0.5,5.3,1,7.6,1.4c2.3,0.5,4,1,5.2,1.6l-7.8,23.3     c-3.8-1.5-8.3-2.7-13.3-3.5c-5-0.8-9.8-1.2-14.4-1.2c-4,0-8,0.3-12,0.9c-3.9,0.6-7.3,1.3-10.2,2.3l-30.5,126.8h-26.8l34.6-144     c7.7-2.7,15.9-4.8,24.8-6.5C690.8,125.1,699.7,124.3,708.5,124.3z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M781.9,281c-15.8,0-28.1-4.7-37-14c-8.9-9.3-13.4-22.1-13.4-38.5c0-11.1,1.6-22.9,4.8-35.1c3.2-12.3,8.2-23.6,15.1-34     c6.9-10.4,15.8-18.9,26.8-25.6c10.9-6.7,24.2-10.1,39.8-10.1c15.7,0,28.1,4.7,37,14c8.9,9.3,13.4,22.1,13.4,38.5     c0,11.1-1.6,22.9-4.8,35.1c-3.2,12.3-8.2,23.6-15.1,34s-15.8,18.9-26.8,25.6C810.7,277.7,797.5,281,781.9,281z M786.5,258.3     c8.6,0,16.4-2.5,23.2-7.5c6.8-5,12.6-11.4,17.3-19.2c4.7-7.8,8.3-16.4,10.8-25.8c2.5-9.4,3.7-18.5,3.7-27.4     c0-9.8-2.1-17.6-6.3-23.3c-4.2-5.8-11.5-8.6-21.9-8.6c-8.6,0-16.4,2.5-23.2,7.5c-6.8,5-12.6,11.4-17.3,19.2     c-4.7,7.8-8.3,16.4-10.8,25.8c-2.5,9.4-3.7,18.5-3.7,27.4c0,9.8,2.1,17.6,6.3,23.3C768.9,255.4,776.2,258.3,786.5,258.3z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M1055,124.3c8.4,0,15.6,1.3,21.5,3.9c5.9,2.6,10.6,6.1,14.1,10.5c3.6,4.4,6.1,9.6,7.6,15.4c1.5,5.9,2.3,12.1,2.3,18.6     c0,4.2-0.3,8.8-1,13.7c-0.7,4.9-1.6,9.7-2.7,14.3l-18.4,76.6h-26.8l17-70.9c1-4.4,2.1-9.4,3.3-14.8c1.2-5.5,1.9-10.9,1.9-16.3     c0-3.6-0.4-7.2-1.3-10.7c-0.9-3.5-2.3-6.4-4.3-8.9c-2-2.5-4.9-4.5-8.5-6c-3.7-1.5-8.2-2.3-13.5-2.3c-6.1,0-12.1,1.2-17.9,3.6     c-5.8,2.4-10.6,4.9-14.4,7.3c0.4,2.7,0.7,5.3,1,7.9c0.3,2.6,0.4,5.1,0.4,7.6c0,4.4-0.3,8.9-1,13.4c-0.7,4.5-1.5,9-2.4,13.4     l-18.4,76.6h-26.8l17-70.9c1-4.4,2-9.4,3.2-15c1.2-5.6,1.7-11.1,1.7-16.7c0-3.6-0.4-7.2-1.2-10.5c-0.8-3.4-2.1-6.3-3.9-8.8     c-1.8-2.5-4.3-4.5-7.3-5.9c-3.1-1.4-7-2.2-11.8-2.2c-4.8,0-9.5,0.3-14,0.9c-4.5,0.6-8.3,1.3-11.4,2.3l-30.5,126.8h-26.8l34.6-144     c8.6-2.7,18.1-4.8,28.4-6.5c10.3-1.6,19.4-2.4,27.5-2.4s14.9,1.4,20.5,4.2c5.6,2.8,10,6.5,13.3,11.1c1.9-1.3,4.6-2.9,7.9-4.6     c3.4-1.7,7.2-3.4,11.5-4.9c4.3-1.5,9-2.9,14.1-4C1044.4,124.9,1049.6,124.3,1055,124.3z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M1204.1,123.7c9.4,0,17.5,1.3,24.3,4c6.8,2.7,12.4,6.4,16.9,11.1c4.4,4.7,7.7,10.3,9.9,16.7c2.2,6.4,3.3,13.5,3.3,21.2     c0,13.3-2,26.1-6,38.5c-4,12.4-10.2,23.4-18.6,33c-8.4,9.6-18.9,17.3-31.7,23c-12.8,5.8-27.9,8.6-45.4,8.6     c-2.1,0-4.4-0.1-6.9-0.3c-2.5-0.2-4.7-0.5-6.6-0.9l-12.4,51.9h-26.8l47.2-196.8c8.1-3.3,16.9-5.8,26.4-7.5     C1187.2,124.6,1196,123.7,1204.1,123.7z M1148.8,256.3c1.9,0.4,4,0.6,6.2,0.7c2.2,0.1,4.4,0.1,6.5,0.1c10.9,0,20.7-2.1,29.4-6.3     c8.6-4.2,16-9.9,22-17.1c6-7.2,10.7-15.5,14-24.8c3.3-9.3,4.9-19.1,4.9-29.2c0-4.2-0.5-8.4-1.4-12.4c-1-4-2.7-7.6-5.3-10.7     c-2.6-3.1-6.1-5.5-10.5-7.3c-4.4-1.8-10.1-2.7-17-2.7c-4.8,0-9.2,0.4-13.3,1.3c-4,0.9-7.4,1.7-10.1,2.4L1148.8,256.3z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M1321.9,281.3c-14.4,0-25-3.2-31.7-9.5c-6.7-6.3-10.1-15.6-10.1-27.7c0-7.9,1.3-18,4-30.2L1315,85.7l27.9-4.6l-11.2,46.4     h51l-5.5,22.5h-51l-15.8,66.3c-2.3,9-3.5,16.9-3.5,23.6c0,6.3,1.7,10.9,5.2,13.7c3.5,2.8,9.2,4.2,17.3,4.2c5.6,0,11-0.9,16.4-2.7     c5.4-1.8,9.5-3.5,12.4-5l2,22.8c-2.9,1.7-7.8,3.6-14.7,5.6C1338.6,280.3,1330.7,281.3,1321.9,281.3z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M1407.6,277.3h-26.8l36-149.8h26.8L1407.6,277.3z M1438.7,100.4c-4.4,0-8.1-1.3-11.1-4c-3-2.7-4.5-6.4-4.5-11.2     c0-6.1,1.9-11,5.8-14.5c3.8-3.6,8.2-5.3,13-5.3c4.4,0,8.2,1.4,11.2,4.2c3.1,2.8,4.6,6.7,4.6,11.7c0,5.8-2,10.4-6,14     C1447.6,98.6,1443.3,100.4,1438.7,100.4z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M1476.7,277.3h-26.8l34.6-144c8.8-2.7,18.4-4.8,28.7-6.5c10.3-1.6,19.4-2.4,27.5-2.4c8.6,0,16.1,1.2,22.3,3.6     c6.2,2.4,11.3,5.7,15.1,9.8c3.8,4.1,6.7,9.1,8.5,15c1.8,5.9,2.7,12.2,2.7,19.2c0,4.4-0.4,9.1-1.2,14c-0.8,4.9-1.7,9.8-2.9,14.8     l-18.4,76.6h-26.8l17-70.9c1.3-5.4,2.6-10.8,3.9-16.4c1.2-5.6,1.9-10.8,1.9-15.8c0-7.7-2.1-14.1-6.3-19.2     c-4.2-5.1-12.1-7.6-23.6-7.6c-4.8,0-9.5,0.3-14.1,0.9c-4.6,0.6-8.5,1.3-11.5,2.3L1476.7,277.3z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M1640.4,308.4c7.9,0,14.8-1,20.7-2.9c6-1.9,11.1-4.7,15.4-8.4c4.3-3.7,7.8-8,10.5-13c2.7-5,4.8-10.7,6.3-17l1.2-5.5     c-3.8,2.1-9,4.2-15.6,6.3c-6.5,2.1-13.3,3.2-20.2,3.2c-15.6,0-27.5-3.9-35.9-11.7c-8.4-7.8-12.5-18.7-12.5-32.7     c0-12.9,2.1-25.4,6.3-37.7c4.2-12.3,10.7-23.2,19.3-32.8c8.6-9.6,19.4-17.3,32.3-23.2c12.9-5.9,27.9-8.8,45.2-8.8     c2.7,0,5.7,0.2,9.1,0.6c3.4,0.4,6.8,0.9,10.4,1.6c3.6,0.7,7,1.6,10.4,2.7c3.4,1.2,6.3,2.5,8.8,4l-31.4,131.1     c-2.5,10.4-5.6,19.7-9.2,27.9c-3.7,8.3-8.6,15.3-15,21c-6.3,5.8-14.5,10.2-24.5,13.4c-10,3.2-22.5,4.8-37.5,4.8     c-8.3,0-16.4-1.1-24.5-3.3c-8.1-2.2-14.9-4.9-20.5-8.2l10.1-22.5c4.4,3.3,10.3,5.9,17.6,7.9     C1624.7,307.4,1632.4,308.4,1640.4,308.4z M1664.6,248.8c7.7,0,14.7-1.4,21.2-4.2c6.4-2.8,11.4-5.4,14.8-7.9l21-87.6     c-1.9-0.4-4.1-0.8-6.6-1.2c-2.5-0.4-4.9-0.6-7.2-0.6c-10.4,0-19.9,2-28.5,5.9c-8.6,3.9-16.1,9.2-22.3,15.8     c-6.2,6.6-11.1,14.3-14.5,23c-3.5,8.7-5.2,17.8-5.2,27.2c0,10.4,2.3,17.9,6.8,22.5C1648.5,246.5,1655.4,248.8,1664.6,248.8z"
  }), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M1798.6,258.6c4,0,8.1-0.4,12.2-1.2c4.1-0.8,7.8-2,10.9-3.7c3.2-1.7,5.8-4.1,7.8-7.1c2-3,3-6.7,3-11.1     c0-2.9-0.5-5.4-1.4-7.6c-1-2.2-2.5-4.3-4.6-6.2c-2.1-1.9-4.8-3.8-7.9-5.6c-3.2-1.8-7-3.9-11.4-6.2c-4.4-2.1-8.5-4.2-12.1-6.3     c-3.7-2.1-6.9-4.6-9.7-7.5c-2.8-2.9-4.9-6.3-6.5-10.2c-1.5-3.9-2.3-8.7-2.3-14.3c0-14.4,5.2-26,15.6-34.7     c10.4-8.7,25.4-13.1,45.2-13.1c9.2,0,17.3,0.9,24.3,2.7c7,1.8,11.9,3.5,14.5,5l-10.1,21.9c-2.7-1.5-7-3.2-13-4.9     c-6-1.7-12.6-2.6-19.9-2.6c-3.7,0-7.3,0.4-10.9,1.2c-3.7,0.8-7,2.1-9.9,3.9c-3,1.8-5.4,4.2-7.2,7.2c-1.8,3-2.7,6.6-2.7,10.8     c0,5.2,2,9.5,6,12.8c4,3.4,10.1,6.9,18.2,10.5c5,2.3,9.5,4.7,13.4,7.1c3.9,2.4,7.3,5.1,10.1,8.1c2.8,3,4.9,6.5,6.3,10.5     c1.4,4,2.2,8.8,2.2,14.4c0,8.1-1.5,15.2-4.6,21.3c-3.1,6.1-7.3,11.2-12.8,15.3c-5.5,4-12,7.1-19.4,9.1c-7.5,2-15.7,3-24.5,3     c-6.5,0-12.4-0.4-17.7-1.3c-5.3-0.9-9.9-1.9-13.8-3c-3.9-1.2-7.2-2.3-9.8-3.6c-2.6-1.2-4.6-2.3-5.9-3l10.4-21.9     c1.3,0.8,3.2,1.7,5.6,2.9c2.4,1.2,5.3,2.3,8.6,3.5c3.4,1.2,7.1,2.1,11.1,2.9C1790,258.2,1794.2,258.6,1798.6,258.6z"
  })), jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M249.9,72.4c-45.8,19.2-56.1,72.4-67.8,115.3c-16.8,1.6-31.4,6-50.1,15.1l-0.4,0.5c12.8-2,34-1,45.1,4.2l0,0    c-7.1,28.8-12.9,59.4-28.3,83.9c-17.4,29.9-62.1,33.1-83.2,5.4c-20.5-25.6-5.8-70.1,24.5-81.2c-33.7,10.4-52.4,56.7-33,87.1    c20.5,33.5,73.5,40.2,103.8,16.5c29.1-24.9,33.7-65.3,41.5-100.3c0.7-3.2,1.4-6.4,2.1-9.6l0,0c45.9-2,100.7,1.7,132.4-39.3    C384.4,108.1,317.4,38.1,249.9,72.4z M301,168.5c-27.6,15.2-61.1,15-92,18.7c5-21.7,11.1-43.4,21-62.9    c13.8-27.4,52.2-45.1,80-29.6C338.8,112.2,327.2,154.2,301,168.5z"
  }))));
};

var PromptingsIcon = function (_a) {
  var color = _a.color;
  return jsx("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    version: "1.1",
    x: "0px",
    y: "0px",
    viewBox: "0 0 600 600"
  }, jsx("g", null, jsx("path", {
    fill: color === 'white' ? '#FFFFFF' : '#000000',
    d: "M388.9,72C306,106.8,287.3,203.2,266.1,280.8c-30.4,2.9-56.8,10.8-90.6,27.4l-0.7,0.9c23.1-3.7,61.6-1.8,81.7,7.6l0,0   c-12.8,52.1-23.4,107.5-51.2,151.8c-31.5,54.1-112.5,59.9-150.7,9.8C17.5,432,44.1,351.4,99,331.3C38,350.2,4.1,433.9,39.2,489   c37.1,60.7,133,72.8,187.8,29.8c52.7-45,61.1-118.3,75.2-181.5c1.3-5.8,2.5-11.6,3.7-17.4l0,0c83-3.6,182.4,3.2,239.7-71.1   C632.4,136.6,511,9.9,388.9,72z M481.3,245.9c-49.9,27.4-110.6,27.2-166.6,33.9c9.1-39.3,20.1-78.5,38-113.8   c25-49.5,94.4-81.7,144.8-53.5C549.8,144.1,528.7,220,481.3,245.9z"
  })));
};

var Logo = function (_a) {
  var _b, _c, _d;
  var brand = _a.brand,
    color = _a.color,
    _e = _a.type,
    type = _e === void 0 ? 'fullLogo' : _e,
    _f = _a.width,
    width = _f === void 0 ? '100px' : _f,
    href = _a.href,
    onClick = _a.onClick,
    secondaryLogo = _a.secondaryLogo,
    shouldShowParentBrand = _a.shouldShowParentBrand;
  var selectLogo = {
    bellaCadeaux: {
      fullLogo: BellaCadeauxLogo
    },
    blue42: {
      fullLogo: Blue42Logo
    },
    greenerStill: {
      fullLogo: GreenerStillLogo,
      icon: GreenerStillIcon
    },
    soc: {
      fullLogo: SOCLogo,
      icon: SOCIcon
    },
    promptingU: {
      fullLogo: PromptingULogo,
      icon: PromptingUIcon
    },
    streamVA: {
      fullLogo: StreamVALogo,
      icon: StreamVAIcon
    },
    promptingsAcademy: {
      fullLogo: PromptingsAcademyLogo,
      icon: PromptingsAcademyIcon
    },
    streamMarketing: {
      fullLogo: StreamMarketingLogo
    },
    promptings: {
      fullLogo: PromptingsLogo,
      icon: PromptingsIcon
    }
  };
  var SelectedLogo = ((_b = selectLogo[brand]) === null || _b === void 0 ? void 0 : _b[type]) || PromptingsLogo;
  return jsx(Span, {
    style: {
      lineHeight: 0
    },
    display: "inline-block",
    width: 'fit-content'
  }, jsx(Flex, {
    flexDirection: "column",
    columnGap: "x_5",
    alignItems: "center",
    style: {
      color: '#000'
    }
  }, jsx("a", {
    href: href,
    onClick: onClick,
    style: {
      cursor: 'pointer'
    }
  }, jsx(SelectedLogo, {
    color: color,
    width: width
  })), shouldShowParentBrand && jsx(Flex, {
    alignItems: "center",
    style: {
      fontFamily: 'Ubuntu',
      fontStyle: 'italic',
      fontSize: (_c = secondaryLogo === null || secondaryLogo === void 0 ? void 0 : secondaryLogo.fontSize) !== null && _c !== void 0 ? _c : '12px'
    },
    justifyContent: "flex-end",
    flexDirection: "row",
    columnGap: "x_25",
    opacity: 0.5,
    width: "100%",
    inset: {
      right: 'x1_5'
    }
  }, "The", jsx("a", {
    href: href,
    onClick: onClick,
    style: {
      cursor: 'pointer'
    }
  }, jsx(PromptingsLogo, {
    brand: "promptings",
    color: color,
    width: (_d = secondaryLogo === null || secondaryLogo === void 0 ? void 0 : secondaryLogo.width) !== null && _d !== void 0 ? _d : width,
    type: "fullLogo"
  })), "mechanism")));
};

var getDifference = function (currentDate, endDate) {
  return Math.abs(currentDate - endDate);
};
var checkValidDateStr = function (str) {
  return str.match(/^\d{4}-\d{2}-\d{2}$/) !== null;
};
var toDateStr = function (date) {
  if (typeof date === 'string') {
    if (checkValidDateStr(date)) {
      return date;
    } else {
      throw new Error("Invalid date string: " + date + ", please use format YYYY-MM-DD");
    }
  }
  throw new Error("Shouldn't get here (invalid toDateStr provided): " + date);
};
var isLeapYear = function () {
  var fullYear = new Date().getFullYear();
  if (fullYear % 4 === 0) {
    return true;
  }
  return false;
};
var getMonthDetails = function (index) {
  switch (index) {
    case 0:
      return {
        index: 0,
        name: 'January',
        dayCount: 31
      };
    case 1:
      return {
        index: 1,
        name: 'February',
        dayCount: isLeapYear() ? 29 : 28
      };
    case 2:
      return {
        index: 2,
        name: 'March',
        dayCount: 30
      };
    case 3:
      return {
        index: 3,
        name: 'April',
        dayCount: 30
      };
    case 4:
      return {
        index: 4,
        name: 'May',
        dayCount: 31
      };
    case 5:
      return {
        index: 5,
        name: 'June',
        dayCount: 30
      };
    case 6:
      return {
        index: 6,
        name: 'July',
        dayCount: 31
      };
    case 7:
      return {
        index: 7,
        name: 'August',
        dayCount: 31
      };
    case 8:
      return {
        index: 8,
        name: 'September',
        dayCount: 30
      };
    case 9:
      return {
        index: 9,
        name: 'October',
        dayCount: 31
      };
    case 10:
      return {
        index: 10,
        name: 'November',
        dayCount: 30
      };
    case 11:
      return {
        index: 11,
        name: 'December',
        dayCount: 31
      };
    default:
      return null;
  }
};
var CountdownTimer = function (_a) {
  var date = _a.endDate,
    endTime = _a.endTime,
    _b = _a.size,
    size = _b === void 0 ? 'medium' : _b,
    _c = _a.timeReachedMessage,
    timeReachedMessage = _c === void 0 ? 'No time left' : _c;
  var _d = useState(new Date()),
    currentDate = _d[0],
    setCurrentDate = _d[1];
  var endDate = useState(new Date("" + toDateStr(date) + (endTime ? "T" + endTime.hour + ":" + endTime.minute + ":" + endTime.second : 'T00:00:00')))[0];
  var getRemainingYears = function () {
    return getDifference(currentDate.getFullYear(), endDate.getFullYear());
  };
  var getRemainingMonths = function () {
    return getDifference(currentDate.getMonth(), endDate.getMonth());
  };
  var getRemainingDays = function () {
    var currentDateDate = currentDate.getDate();
    var endDateDate = endDate.getDate();
    return getDifference(currentDateDate, endDateDate);
  };
  var compareDates = function () {
    return currentDate.getDate() - endDate.getDate();
  };
  var computeRemainingDates = function () {
    var _a, _b;
    if (compareDates() > 1) {
      // Run the logic to calculate month - 1 and date difference by future month - 1 remaining days plus month days
      return {
        monthsRemaing: getRemainingMonths() - 1,
        daysRemaining: getDifference((_b = (_a = getMonthDetails(endDate.getMonth())) === null || _a === void 0 ? void 0 : _a.dayCount) !== null && _b !== void 0 ? _b : 0, currentDate.getDate()) + endDate.getDate(),
        yearsRemaining: getRemainingYears()
      };
    } else {
      return {
        monthsRemaing: getRemainingMonths(),
        daysRemaining: getRemainingDays(),
        yearsRemaining: getRemainingYears()
      };
    }
  };
  var computeRemainingTime = function () {
    var difference = +endDate - +currentDate;
    var timeLeft = {
      hoursRemaining: Math.floor(difference / (1000 * 60 * 60) % 24),
      minutesRemaining: Math.floor(difference / 1000 / 60 % 60),
      secondsRemaining: Math.floor(difference / 1000 % 60)
    };
    return timeLeft;
  };
  var _e = computeRemainingDates(),
    monthsRemaing = _e.monthsRemaing,
    daysRemaining = _e.daysRemaining,
    yearsRemaining = _e.yearsRemaining;
  var _f = computeRemainingTime(),
    hoursRemaining = _f.hoursRemaining,
    minutesRemaining = _f.minutesRemaining,
    secondsRemaining = _f.secondsRemaining;
  useEffect(function () {
    var timer = setTimeout(function () {
      setCurrentDate(new Date());
    }, 1000);
    return function () {
      return clearInterval(timer);
    };
  });
  if (yearsRemaining <= 0 && monthsRemaing <= 0 && daysRemaining <= 0 && hoursRemaining <= 0 && minutesRemaining <= 0 && secondsRemaining <= 0) {
    return jsx(Text, {
      type: "subtitle"
    }, timeReachedMessage);
  }
  return jsx(HStack, {
    justify: "center",
    gap: "x1"
  }, daysRemaining < 1 && yearsRemaining < 1 && monthsRemaing < 1 ? jsx(HStack, {
    justify: "center",
    gap: "x1"
  }, hoursRemaining > 0 && jsx(Text, {
    type: computeTextTypeFromSize(size)
  }, hoursRemaining, " ", hoursRemaining > 1 ? 'Hours' : 'Hour'), minutesRemaining > 0 && jsx(Text, {
    type: computeTextTypeFromSize(size)
  }, minutesRemaining, " ", minutesRemaining > 1 ? 'Minutes' : 'Minute'), secondsRemaining > 0 && jsx(Text, {
    type: computeTextTypeFromSize(size)
  }, secondsRemaining, " ", secondsRemaining > 1 ? 'Seconds' : 'Second')) : jsx(HStack, {
    justify: "center",
    gap: "x1"
  }, yearsRemaining > 0 && jsx(Text, {
    type: computeTextTypeFromSize(size)
  }, yearsRemaining, " ", yearsRemaining > 1 ? 'Years' : 'Year'), monthsRemaing > 0 && jsx(Text, {
    type: computeTextTypeFromSize(size)
  }, monthsRemaing, " ", monthsRemaing > 1 ? 'Months' : 'Month'), daysRemaining > 0 && jsx(Text, {
    type: computeTextTypeFromSize(size)
  }, daysRemaining, " ", daysRemaining > 1 ? 'Days' : 'Day')), jsx(Text, {
    type: computeTextTypeFromSize(size)
  }, "Left"));
};

var _emotionSourceMap$o = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var ArticleCard = function (_a) {
  var imageURL = _a.imageURL,
    category = _a.category,
    categoryColor = _a.categoryColor,
    title = _a.title,
    date = _a.date,
    onClick = _a.onClick,
    href = _a.href;
  return jsx(Flex, {
    borderRadius: "medium",
    width: "100%",
    height: "100%",
    backgroundColor: "foreground",
    borderColor: "background",
    borderStyle: "solid",
    borderWidth: "thin",
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    flexDirection: 'column',
    overflow: "hidden",
    boxShadow: "mediumLight",
    onClick: function () {
      return onClick === null || onClick === void 0 ? void 0 : onClick();
    }
  }, jsx("a", {
    css: /*#__PURE__*/css(templateObject_1$o || (templateObject_1$o = __makeTemplateObject(["\n          width: 100%;\n          height: 100%;\n          text-decoration: none;\n          font-family: 'inherit';\n        ;label:ArticleCard;" + _emotionSourceMap$o], ["\n          width: 100%;\n          height: 100%;\n          text-decoration: none;\n          font-family: 'inherit';\n        ;label:ArticleCard;" + _emotionSourceMap$o]))),
    href: href
  }, jsx(Div, {
    width: "100%",
    backgroundImage: imageURL,
    height: "280px",
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat"
  }), jsx(Card, {
    borderRadius: "none",
    boxShadow: "none",
    width: "100%",
    inset: {
      top: 'x3',
      bottom: 'x4',
      horizontal: 'x3'
    }
  }, jsx(VStack, {
    gap: "x_5"
  }, category && jsx(Text, {
    type: "footnote",
    color: categoryColor,
    weight: "semiBold",
    lineHeight: 1.6
  }, category), jsx(Text, {
    type: "body",
    color: "primaryHeading",
    weight: "semiBold",
    lineHeight: 1.6
  }, title), date && jsx(Text, {
    type: "footnote",
    color: "primaryBody",
    weight: "regular",
    lineHeight: 1.6
  }, date)))));
};
var templateObject_1$o;

var _emotionSourceMap$n = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFHeUIiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgX19tYWtlVGVtcGxhdGVPYmplY3QgfSBmcm9tIFwidHNsaWJcIjtcclxuaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vY29yZSc7XHJcbmV4cG9ydCBkZWZhdWx0IHtcclxuICAgIGxpbmtDb2x1bW5Db250YWluZXI6IGNzcyh0ZW1wbGF0ZU9iamVjdF8xIHx8ICh0ZW1wbGF0ZU9iamVjdF8xID0gX19tYWtlVGVtcGxhdGVPYmplY3QoW1wiXCJdLCBbXCJcIl0pKSksXHJcbn07XHJcbnZhciB0ZW1wbGF0ZU9iamVjdF8xO1xyXG4vLyMgc291cmNlTWFwcGluZ1VSTD1zdHlsZXMuanMubWFwIl19 */";
var styles$m = {
  linkColumnContainer: /*#__PURE__*/css(templateObject_1$n || (templateObject_1$n = __makeTemplateObject([";label:linkColumnContainer;" + _emotionSourceMap$n], [";label:linkColumnContainer;" + _emotionSourceMap$n])))
};
var templateObject_1$n;

var LinkColumn = function (_a) {
  var linkColumn = _a.linkColumn,
    linkTitleColor = _a.linkTitleColor,
    subLinkColor = _a.subLinkColor;
  return jsx("div", {
    css: styles$m.linkColumnContainer
  }, jsx(VStack, {
    gap: "x2"
  }, jsx(Text, {
    type: "body",
    weight: "semiBold",
    color: linkTitleColor
  }, linkColumn.title), jsx(Flex, {
    justifyContent: "space-between",
    width: "100%"
  }, jsx(VStack, {
    gap: "x1_5"
  }, linkColumn.links.map(function (subLink, index) {
    return typeof subLink.title === 'string' ? jsx(Anchor, {
      key: index,
      color: subLinkColor,
      cursor: 'pointer',
      title: subLink.title,
      href: subLink.href,
      target: subLink.target,
      size: "small",
      onClick: subLink.onClick
    }) : jsx(Anchor, {
      key: index,
      cursor: 'pointer',
      href: subLink.href,
      target: subLink.target,
      size: "small",
      onClick: subLink.onClick
    }, subLink.title);
  })))));
};

var DropDownMenu = function (_a) {
  var linkColumn = _a.linkColumn,
    isOpen = _a.isOpen,
    top = _a.top,
    right = _a.right,
    left = _a.left;
  return jsx(AnimatePresence, null, isOpen && jsx(Card, {
    inset: {
      top: 'x2',
      horizontal: 'x2',
      bottom: 'x4'
    },
    backgroundColor: 'foreground',
    animate: {
      opacity: 1
    },
    initial: {
      opacity: 0
    },
    exit: {
      opacity: 0
    },
    position: "absolute",
    top: top + "px",
    right: right ? right + "px" : undefined,
    left: left ? left + "px" : undefined,
    minWidth: '216px'
  }, jsx(VStack, {
    gap: "x2"
  }, jsx(Text, {
    type: "body",
    alignment: "left",
    color: "primaryHeading",
    content: linkColumn.title,
    weight: "semiBold",
    lineHeight: 1.2
  }), linkColumn.links.map(function (link) {
    return typeof link.title === 'string' ? link.icon ? jsx(IconLink, {
      icon: link.icon,
      title: link.title,
      size: 'small',
      fill: 'primaryHeadingText'
    }) : jsx(Anchor, {
      title: link.title,
      href: link.href,
      onClick: link.onClick,
      color: 'primaryBody'
    }) : jsx(Anchor, {
      href: link.href,
      onClick: link.onClick
    }, link.title);
  }))));
};

var _emotionSourceMap2$d = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFJd0UiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgX19tYWtlVGVtcGxhdGVPYmplY3QgfSBmcm9tIFwidHNsaWJcIjtcclxuaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vY29yZSc7XHJcbmV4cG9ydCBkZWZhdWx0IHtcclxuICAgIHNjcm9sbGluZ0luZGljYXRvckNvbnRhaW5lcjogY3NzKHRlbXBsYXRlT2JqZWN0XzEgfHwgKHRlbXBsYXRlT2JqZWN0XzEgPSBfX21ha2VUZW1wbGF0ZU9iamVjdChbXCJcXG4gICAgcG9zaXRpb246IGZpeGVkO1xcbiAgICBib3R0b206IDhweDtcXG4gICAgbGVmdDogNTAlO1xcbiAgICB0cmFuc2Zvcm06IHRyYW5zbGF0ZVgoLTUwJSk7XFxuICBcIl0sIFtcIlxcbiAgICBwb3NpdGlvbjogZml4ZWQ7XFxuICAgIGJvdHRvbTogOHB4O1xcbiAgICBsZWZ0OiA1MCU7XFxuICAgIHRyYW5zZm9ybTogdHJhbnNsYXRlWCgtNTAlKTtcXG4gIFwiXSkpKSxcclxuICAgIGNpcmNsZUluZGljYXRvckNvbnRhaW5lcjogZnVuY3Rpb24gKHNpemUsIGJhY2tncm91bmRDb2xvcikgeyByZXR1cm4gY3NzKHRlbXBsYXRlT2JqZWN0XzIgfHwgKHRlbXBsYXRlT2JqZWN0XzIgPSBfX21ha2VUZW1wbGF0ZU9iamVjdChbXCJcXG4gICAgaGVpZ2h0OiBcIiwgXCI7XFxuICAgIHdpZHRoOiBcIiwgXCI7XFxuICAgIGJhY2tncm91bmQtY29sb3I6IFwiLCBcIjtcXG4gICAgYm9yZGVyLXJhZGl1czogMTAwJTtcXG4gICAgZGlzcGxheTogZmxleDtcXG4gICAganVzdGlmeS1jb250ZW50OiBjZW50ZXI7XFxuICAgIGFsaWduLWl0ZW1zOiBjZW50ZXI7XFxuICBcIl0sIFtcIlxcbiAgICBoZWlnaHQ6IFwiLCBcIjtcXG4gICAgd2lkdGg6IFwiLCBcIjtcXG4gICAgYmFja2dyb3VuZC1jb2xvcjogXCIsIFwiO1xcbiAgICBib3JkZXItcmFkaXVzOiAxMDAlO1xcbiAgICBkaXNwbGF5OiBmbGV4O1xcbiAgICBqdXN0aWZ5LWNvbnRlbnQ6IGNlbnRlcjtcXG4gICAgYWxpZ24taXRlbXM6IGNlbnRlcjtcXG4gIFwiXSkpLCBzaXplIHx8ICc1MHB4Jywgc2l6ZSB8fCAnNTBweCcsIGJhY2tncm91bmRDb2xvciB8fCAnIzAwMDAwMDdkJyk7IH0sXHJcbn07XHJcbnZhciB0ZW1wbGF0ZU9iamVjdF8xLCB0ZW1wbGF0ZU9iamVjdF8yO1xyXG4vLyMgc291cmNlTWFwcGluZ1VSTD1zdHlsZXMuanMubWFwIl19 */";
var _emotionSourceMap$m = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFHaUMiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgX19tYWtlVGVtcGxhdGVPYmplY3QgfSBmcm9tIFwidHNsaWJcIjtcclxuaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vY29yZSc7XHJcbmV4cG9ydCBkZWZhdWx0IHtcclxuICAgIHNjcm9sbGluZ0luZGljYXRvckNvbnRhaW5lcjogY3NzKHRlbXBsYXRlT2JqZWN0XzEgfHwgKHRlbXBsYXRlT2JqZWN0XzEgPSBfX21ha2VUZW1wbGF0ZU9iamVjdChbXCJcXG4gICAgcG9zaXRpb246IGZpeGVkO1xcbiAgICBib3R0b206IDhweDtcXG4gICAgbGVmdDogNTAlO1xcbiAgICB0cmFuc2Zvcm06IHRyYW5zbGF0ZVgoLTUwJSk7XFxuICBcIl0sIFtcIlxcbiAgICBwb3NpdGlvbjogZml4ZWQ7XFxuICAgIGJvdHRvbTogOHB4O1xcbiAgICBsZWZ0OiA1MCU7XFxuICAgIHRyYW5zZm9ybTogdHJhbnNsYXRlWCgtNTAlKTtcXG4gIFwiXSkpKSxcclxuICAgIGNpcmNsZUluZGljYXRvckNvbnRhaW5lcjogZnVuY3Rpb24gKHNpemUsIGJhY2tncm91bmRDb2xvcikgeyByZXR1cm4gY3NzKHRlbXBsYXRlT2JqZWN0XzIgfHwgKHRlbXBsYXRlT2JqZWN0XzIgPSBfX21ha2VUZW1wbGF0ZU9iamVjdChbXCJcXG4gICAgaGVpZ2h0OiBcIiwgXCI7XFxuICAgIHdpZHRoOiBcIiwgXCI7XFxuICAgIGJhY2tncm91bmQtY29sb3I6IFwiLCBcIjtcXG4gICAgYm9yZGVyLXJhZGl1czogMTAwJTtcXG4gICAgZGlzcGxheTogZmxleDtcXG4gICAganVzdGlmeS1jb250ZW50OiBjZW50ZXI7XFxuICAgIGFsaWduLWl0ZW1zOiBjZW50ZXI7XFxuICBcIl0sIFtcIlxcbiAgICBoZWlnaHQ6IFwiLCBcIjtcXG4gICAgd2lkdGg6IFwiLCBcIjtcXG4gICAgYmFja2dyb3VuZC1jb2xvcjogXCIsIFwiO1xcbiAgICBib3JkZXItcmFkaXVzOiAxMDAlO1xcbiAgICBkaXNwbGF5OiBmbGV4O1xcbiAgICBqdXN0aWZ5LWNvbnRlbnQ6IGNlbnRlcjtcXG4gICAgYWxpZ24taXRlbXM6IGNlbnRlcjtcXG4gIFwiXSkpLCBzaXplIHx8ICc1MHB4Jywgc2l6ZSB8fCAnNTBweCcsIGJhY2tncm91bmRDb2xvciB8fCAnIzAwMDAwMDdkJyk7IH0sXHJcbn07XHJcbnZhciB0ZW1wbGF0ZU9iamVjdF8xLCB0ZW1wbGF0ZU9iamVjdF8yO1xyXG4vLyMgc291cmNlTWFwcGluZ1VSTD1zdHlsZXMuanMubWFwIl19 */";
var styles$l = {
  scrollingIndicatorContainer: /*#__PURE__*/css(templateObject_1$m || (templateObject_1$m = __makeTemplateObject(["\n    position: fixed;\n    bottom: 8px;\n    left: 50%;\n    transform: translateX(-50%);\n  ;label:scrollingIndicatorContainer;" + _emotionSourceMap$m], ["\n    position: fixed;\n    bottom: 8px;\n    left: 50%;\n    transform: translateX(-50%);\n  ;label:scrollingIndicatorContainer;" + _emotionSourceMap$m]))),
  circleIndicatorContainer: function (size, backgroundColor) {
    return /*#__PURE__*/css(templateObject_2$d || (templateObject_2$d = __makeTemplateObject(["\n    height: ", ";\n    width: ", ";\n    background-color: ", ";\n    border-radius: 100%;\n    display: flex;\n    justify-content: center;\n    align-items: center;\n  " + _emotionSourceMap2$d], ["\n    height: ", ";\n    width: ", ";\n    background-color: ", ";\n    border-radius: 100%;\n    display: flex;\n    justify-content: center;\n    align-items: center;\n  " + _emotionSourceMap2$d])), size || '50px', size || '50px', backgroundColor || '#0000007d');
  }
};
var templateObject_1$m, templateObject_2$d;

var ScrollingIndicator = function (_a) {
  var size = _a.size,
    backgroundColor = _a.backgroundColor;
  return jsx(Div, {
    css: styles$l.scrollingIndicatorContainer
  }, jsx(Div, {
    css: styles$l.circleIndicatorContainer(size, backgroundColor)
  }, jsx("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink",
    x: "0",
    y: "0",
    style: {
      height: "calc(" + (size !== null && size !== void 0 ? size : '50px') + " / 1.5)",
      width: 17,
      position: 'relative',
      top: 2
    },
    enableBackground: "new 0 0 17 46",
    version: "1.1",
    viewBox: "0 0 17 46",
    xmlSpace: "preserve"
  }, jsx("defs", null, jsx("linearGradient", {
    id: "grad2",
    x1: "0%",
    x2: "0%",
    y1: "0%",
    y2: "100%"
  }, jsx("stop", {
    offset: "0%",
    stopColor: "#d9d9d9"
  }), jsx("stop", {
    offset: "33%",
    stopColor: "#d9d9d9",
    stopOpacity: "0.7"
  }), jsx("stop", {
    offset: "50%",
    stopColor: "#fff"
  }), jsx("stop", {
    offset: "66%",
    stopColor: "#d9d9d9",
    stopOpacity: "0.7"
  }), jsx("stop", {
    offset: "100%",
    stopColor: "#d9d9d9"
  }))), jsx("g", null, jsx("animateTransform", {
    attributeName: "gradientTransform",
    attributeType: "XML",
    dur: "2s",
    from: "0 -1",
    repeatCount: "indefinite",
    restart: "whenNotActive",
    to: "0 1",
    type: "translate",
    xlinkHref: "#grad2"
  }), jsx("path", {
    d: "M8.5 2C4.9 2 2 4.9 2 8.4v9.1C2 21.1 4.9 24 8.5 24s6.5-2.9 6.5-6.4V8.4C15 4.9 12.1 2 8.5 2z",
    style: {
      fill: 'none'
    }
  }), jsx("path", {
    d: "M8.5 0C3.8 0 0 3.8 0 8.4v9.1C0 22.2 3.8 26 8.5 26s8.5-3.8 8.5-8.4V8.4C17 3.8 13.2 0 8.5 0zM15 17.6c0 3.5-2.9 6.4-6.5 6.4S2 21.1 2 17.6V8.4C2 4.9 4.9 2 8.5 2S15 4.9 15 8.4v9.2zM8.5 5.1c-.5 0-1 .5-1 1V9c0 .5.5 1 1 1s1-.5 1-1V6.1c0-.5-.5-1-1-1zm4.6 27L8 36.6l-5.1-4.5c-.2-.2-.6-.2-.8.1-.2.2-.1.6.1.8l5.5 4.8c.1.1.2.1.3.1l.3-.1 5.5-4.8c.2-.2.3-.6",
    style: {
      fill: "url(#grad2)"
    }
  })))));
};

var Sphere = function (_a) {
  var _b;
  var backgroundColor = _a.backgroundColor,
    step = _a.step,
    size = _a.size;
  var sphere = useEntities().sphere;
  var sphereProperties = sphere[size];
  return jsx(Flex, {
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: backgroundColor !== null && backgroundColor !== void 0 ? backgroundColor : {
      swatch: 'primaryBrand',
      shade: 'base'
    },
    width: sphereProperties.circumference + "px",
    height: sphereProperties.circumference + "px",
    borderRadius: "circle"
  }, jsx(Text, {
    type: sphereProperties.textType,
    content: step.content,
    weight: "bold",
    color: (_b = step.color) !== null && _b !== void 0 ? _b : 'inverseBody'
  }));
};

var Clipboard = function (_a) {
  var copyText = _a.copyText,
    _b = _a.width,
    width = _b === void 0 ? '100%' : _b,
    _c = _a.type,
    type = _c === void 0 ? 'link' : _c;
  var inputs = useEntities().inputs;
  var _d = React.useState(false),
    isCopied = _d[0],
    setIsCopied = _d[1];
  var copyTextToClipboard = function (text) {
    return __awaiter(void 0, void 0, void 0, function () {
      return __generator(this, function (_a) {
        switch (_a.label) {
          case 0:
            if (!('clipboard' in navigator)) return [3 /*break*/, 2];
            return [4 /*yield*/, navigator.clipboard.writeText(text)];
          case 1:
            return [2 /*return*/, _a.sent()];
          case 2:
            return [2 /*return*/, document.execCommand('copy', true, text)];
        }
      });
    });
  };
  var handleCopy = function () {
    copyTextToClipboard(copyText)
    // eslint-disable-next-line
    .then(function () {
      setIsCopied(true);
      setTimeout(function () {
        setIsCopied(false);
      }, 4000);
    })
    // eslint-disable-next-line
    .catch(function (err) {
      console.log(err);
    });
  };
  return jsx(Div, {
    position: "relative",
    width: width,
    backgroundColor: inputs.backgroundColor,
    borderRadius: inputs.text.borderRadius
  }, jsx(Div, {
    width: "calc(100% - 100px)",
    style: {
      userSelect: 'none',
      pointerEvents: 'none'
    }
  }, jsx(Input, {
    type: "text",
    value: copyText,
    onChange: function () {},
    icon: "catalog",
    isFullWidth: true
  })), jsx(Div, {
    position: "absolute",
    top: "10px",
    right: "10px"
  }, jsx(Button, {
    type: isCopied ? 'success' : 'secondary',
    size: "xSmall",
    title: isCopied ? "Copied " + (type === 'text' ? 'Text' : 'Link') : "Copy " + (type === 'text' ? 'Text' : 'Link'),
    onClick: handleCopy
  })));
};

var DownloadButton = function (_a) {
  var buttonProps = _a.buttonProps,
    href = _a.href,
    id = _a.id;
  return jsx(Div, null, jsx(Anchor, {
    href: href,
    shouldDownload: true,
    id: id
  }, jsx(Button, {
    type: buttonProps.type,
    title: buttonProps.title,
    size: buttonProps.size,
    onClick: buttonProps.onClick,
    onMouseEvent: buttonProps.onMouseEvent,
    disabled: buttonProps.disabled,
    hover: buttonProps.hover,
    outlined: buttonProps.outlined,
    fullWidth: buttonProps.fullWidth,
    textColorOverride: buttonProps.textColorOverride,
    disabledCaption: buttonProps.disabledCaption,
    disabledDirection: buttonProps.disabledDirection
  })));
};

var StackedCards = function (_a) {
  var width = _a.width,
    height = _a.height,
    _b = _a.stackCount,
    stackCount = _b === void 0 ? 5 : _b,
    children = _a.children,
    backgroundImage = _a.backgroundImage,
    _c = _a.borderRadius,
    borderRadius = _c === void 0 ? 'small' : _c;
  var stackCountArr = Array.from(Array(stackCount - 1).keys());
  return jsx(Flex, {
    position: "relative",
    justifyContent: 'flex-start',
    alignContent: "center",
    width: width + "px",
    height: height + "px",
    outset: {
      horizontal: 'auto'
    }
  }, jsx(Card, {
    width: width + "px",
    height: height + "px",
    position: "absolute",
    top: '50%',
    boxShadow: "mediumLight",
    backgroundColor: !backgroundImage ? 'foreground' : undefined,
    backgroundImage: backgroundImage,
    backgroundPosition: "center",
    borderStyle: "solid",
    borderColor: "foreground",
    backgroundSize: "cover",
    borderRadius: borderRadius,
    style: {
      scale: 1,
      translateY: '-50%'
    },
    zIndex: stackCountArr.length
  }, !backgroundImage && children), stackCountArr.map(function (_, index) {
    index += 1;
    return jsx(Card, {
      borderRadius: borderRadius,
      key: index,
      backgroundImage: backgroundImage,
      borderStyle: "solid",
      backgroundPosition: "right center",
      borderColor: "background",
      width: width + "px",
      height: height + "px",
      style: {
        scale: 1 - index / 16,
        translateY: '-50%'
      },
      position: "absolute",
      top: '50%',
      boxShadow: "mediumLight",
      zIndex: stackCountArr.length - index,
      transformOrigin: '145% 50%'
    });
  }));
};

var _emotionSourceMap2$c = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFJdUIiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgX19tYWtlVGVtcGxhdGVPYmplY3QgfSBmcm9tIFwidHNsaWJcIjtcclxuaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vY29yZSc7XHJcbmV4cG9ydCBkZWZhdWx0IHtcclxuICAgIHVzZXJDYXJkQ29udGFpbmVyOiBjc3ModGVtcGxhdGVPYmplY3RfMSB8fCAodGVtcGxhdGVPYmplY3RfMSA9IF9fbWFrZVRlbXBsYXRlT2JqZWN0KFtcIlxcbiAgICBkaXNwbGF5OiBmbGV4O1xcbiAgICBqdXN0aWZ5LWNvbnRlbnQ6IGZsZXgtc3RhcnQ7XFxuICAgIGFsaWduLWl0ZW1zOiBjZW50ZXI7XFxuICBcIl0sIFtcIlxcbiAgICBkaXNwbGF5OiBmbGV4O1xcbiAgICBqdXN0aWZ5LWNvbnRlbnQ6IGZsZXgtc3RhcnQ7XFxuICAgIGFsaWduLWl0ZW1zOiBjZW50ZXI7XFxuICBcIl0pKSksXHJcbiAgICB1c2VySW5mb0NvbnRhaW5lcjogY3NzKHRlbXBsYXRlT2JqZWN0XzIgfHwgKHRlbXBsYXRlT2JqZWN0XzIgPSBfX21ha2VUZW1wbGF0ZU9iamVjdChbXCJcXG4gICAgcGFkZGluZy1sZWZ0OiAxNnB4O1xcbiAgXCJdLCBbXCJcXG4gICAgcGFkZGluZy1sZWZ0OiAxNnB4O1xcbiAgXCJdKSkpLFxyXG59O1xyXG52YXIgdGVtcGxhdGVPYmplY3RfMSwgdGVtcGxhdGVPYmplY3RfMjtcclxuLy8jIHNvdXJjZU1hcHBpbmdVUkw9c3R5bGVzLmpzLm1hcCJdfQ== */";
var _emotionSourceMap$l = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFHdUIiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgX19tYWtlVGVtcGxhdGVPYmplY3QgfSBmcm9tIFwidHNsaWJcIjtcclxuaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vY29yZSc7XHJcbmV4cG9ydCBkZWZhdWx0IHtcclxuICAgIHVzZXJDYXJkQ29udGFpbmVyOiBjc3ModGVtcGxhdGVPYmplY3RfMSB8fCAodGVtcGxhdGVPYmplY3RfMSA9IF9fbWFrZVRlbXBsYXRlT2JqZWN0KFtcIlxcbiAgICBkaXNwbGF5OiBmbGV4O1xcbiAgICBqdXN0aWZ5LWNvbnRlbnQ6IGZsZXgtc3RhcnQ7XFxuICAgIGFsaWduLWl0ZW1zOiBjZW50ZXI7XFxuICBcIl0sIFtcIlxcbiAgICBkaXNwbGF5OiBmbGV4O1xcbiAgICBqdXN0aWZ5LWNvbnRlbnQ6IGZsZXgtc3RhcnQ7XFxuICAgIGFsaWduLWl0ZW1zOiBjZW50ZXI7XFxuICBcIl0pKSksXHJcbiAgICB1c2VySW5mb0NvbnRhaW5lcjogY3NzKHRlbXBsYXRlT2JqZWN0XzIgfHwgKHRlbXBsYXRlT2JqZWN0XzIgPSBfX21ha2VUZW1wbGF0ZU9iamVjdChbXCJcXG4gICAgcGFkZGluZy1sZWZ0OiAxNnB4O1xcbiAgXCJdLCBbXCJcXG4gICAgcGFkZGluZy1sZWZ0OiAxNnB4O1xcbiAgXCJdKSkpLFxyXG59O1xyXG52YXIgdGVtcGxhdGVPYmplY3RfMSwgdGVtcGxhdGVPYmplY3RfMjtcclxuLy8jIHNvdXJjZU1hcHBpbmdVUkw9c3R5bGVzLmpzLm1hcCJdfQ== */";
var styles$k = {
  userCardContainer: /*#__PURE__*/css(templateObject_1$l || (templateObject_1$l = __makeTemplateObject(["\n    display: flex;\n    justify-content: flex-start;\n    align-items: center;\n  ;label:userCardContainer;" + _emotionSourceMap$l], ["\n    display: flex;\n    justify-content: flex-start;\n    align-items: center;\n  ;label:userCardContainer;" + _emotionSourceMap$l]))),
  userInfoContainer: /*#__PURE__*/css(templateObject_2$c || (templateObject_2$c = __makeTemplateObject(["\n    padding-left: 16px;\n  ;label:userInfoContainer;" + _emotionSourceMap2$c], ["\n    padding-left: 16px;\n  ;label:userInfoContainer;" + _emotionSourceMap2$c])))
};
var templateObject_1$l, templateObject_2$c;

var UserCard = function (_a) {
  var profileImage = _a.profileImage,
    firstName = _a.firstName,
    lastName = _a.lastName,
    size = _a.size,
    caption = _a.caption,
    country = _a.country,
    minimize = _a.minimize,
    active = _a.active,
    heading = _a.heading;
  var variants = {
    expand: {
      opacity: 1,
      transition: {
        delay: 0.03
      }
    },
    minimize: {
      opacity: 0
    }
  };
  return jsx("div", {
    css: styles$k.userCardContainer
  }, jsx(Avatar, {
    isActive: active,
    size: size,
    avatarImage: profileImage,
    initials: firstName || lastName ? "" + (firstName && firstName[0].toUpperCase()) + (lastName && lastName[0].toUpperCase()) : undefined
  }), !minimize && jsx(Div, {
    css: styles$k.userInfoContainer,
    variants: variants,
    animate: minimize ? 'minimize' : 'expand',
    initial: 'minimize'
  }, jsx(InfoBlock, {
    size: size,
    caption: caption,
    heading: heading ? heading : firstName + " " + lastName,
    country: country
  })));
};

var UserCardLoader = function (_a) {
  var size = _a.size;
  var returnNumberFromSize = function (size, small, medium, large) {
    switch (size) {
      case 'small':
        return small;
      case 'medium':
        return medium;
      case 'large':
        return large;
    }
  };
  var avatar = useEntities().avatar;
  var circleDiameter = computeAvatarProperties(size, avatar.sizes).avatarSize;
  var radius = circleDiameter / 2;
  var contentHeight = returnNumberFromSize(size, 8, 10, 12);
  var contentWidth = returnNumberFromSize(size, 100, 132, 164);
  var containerWidth = circleDiameter + contentWidth + 32;
  var borderRadius = returnNumberFromSize(size, 3, 4, 5);
  var computeY = function (circleDiameter, contentHeight) {
    var findYFromTop = function (circleDiameter, contentHeight) {
      return (circleDiameter - contentHeight * 3) / 2;
    };
    var titleY = findYFromTop(circleDiameter, contentHeight);
    var subtitleY = findYFromTop(circleDiameter, contentHeight) + contentHeight * 2;
    return {
      titleY: titleY,
      subtitleY: subtitleY
    };
  };
  return jsx(Loader, {
    width: containerWidth,
    height: circleDiameter
  }, jsx("circle", {
    cx: radius,
    cy: radius,
    r: radius
  }), jsx("rect", {
    x: circleDiameter + 8,
    y: computeY(circleDiameter, contentHeight).titleY,
    rx: borderRadius,
    ry: borderRadius,
    width: contentWidth,
    height: contentHeight
  }), jsx("rect", {
    x: circleDiameter + 8,
    y: computeY(circleDiameter, contentHeight).subtitleY,
    rx: borderRadius,
    ry: borderRadius,
    width: contentWidth + 16,
    height: contentHeight
  }));
};

var _emotionSourceMap2$b = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFJaUIiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgX19tYWtlVGVtcGxhdGVPYmplY3QgfSBmcm9tIFwidHNsaWJcIjtcclxuaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vY29yZSc7XHJcbmV4cG9ydCBkZWZhdWx0IHtcclxuICAgIGFjY291bnRNZW51Q29udGFpbmVyOiBjc3ModGVtcGxhdGVPYmplY3RfMSB8fCAodGVtcGxhdGVPYmplY3RfMSA9IF9fbWFrZVRlbXBsYXRlT2JqZWN0KFtcIlxcbiAgICB3aWR0aDogMjQ2cHg7XFxuICAgIHBvc2l0aW9uOiBhYnNvbHV0ZTtcXG4gICAgYm90dG9tOiAwO1xcbiAgICB6LWluZGV4OiAxMDE7XFxuICBcIl0sIFtcIlxcbiAgICB3aWR0aDogMjQ2cHg7XFxuICAgIHBvc2l0aW9uOiBhYnNvbHV0ZTtcXG4gICAgYm90dG9tOiAwO1xcbiAgICB6LWluZGV4OiAxMDE7XFxuICBcIl0pKSksXHJcbiAgICBpdGVtV3JhcHBlcjogY3NzKHRlbXBsYXRlT2JqZWN0XzIgfHwgKHRlbXBsYXRlT2JqZWN0XzIgPSBfX21ha2VUZW1wbGF0ZU9iamVjdChbXCJcXG4gICAgd2lkdGg6IDEwMCU7XFxuICBcIl0sIFtcIlxcbiAgICB3aWR0aDogMTAwJTtcXG4gIFwiXSkpKSxcclxufTtcclxudmFyIHRlbXBsYXRlT2JqZWN0XzEsIHRlbXBsYXRlT2JqZWN0XzI7XHJcbi8vIyBzb3VyY2VNYXBwaW5nVVJMPXN0eWxlcy5qcy5tYXAiXX0= */";
var _emotionSourceMap$k = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFHMEIiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgX19tYWtlVGVtcGxhdGVPYmplY3QgfSBmcm9tIFwidHNsaWJcIjtcclxuaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vY29yZSc7XHJcbmV4cG9ydCBkZWZhdWx0IHtcclxuICAgIGFjY291bnRNZW51Q29udGFpbmVyOiBjc3ModGVtcGxhdGVPYmplY3RfMSB8fCAodGVtcGxhdGVPYmplY3RfMSA9IF9fbWFrZVRlbXBsYXRlT2JqZWN0KFtcIlxcbiAgICB3aWR0aDogMjQ2cHg7XFxuICAgIHBvc2l0aW9uOiBhYnNvbHV0ZTtcXG4gICAgYm90dG9tOiAwO1xcbiAgICB6LWluZGV4OiAxMDE7XFxuICBcIl0sIFtcIlxcbiAgICB3aWR0aDogMjQ2cHg7XFxuICAgIHBvc2l0aW9uOiBhYnNvbHV0ZTtcXG4gICAgYm90dG9tOiAwO1xcbiAgICB6LWluZGV4OiAxMDE7XFxuICBcIl0pKSksXHJcbiAgICBpdGVtV3JhcHBlcjogY3NzKHRlbXBsYXRlT2JqZWN0XzIgfHwgKHRlbXBsYXRlT2JqZWN0XzIgPSBfX21ha2VUZW1wbGF0ZU9iamVjdChbXCJcXG4gICAgd2lkdGg6IDEwMCU7XFxuICBcIl0sIFtcIlxcbiAgICB3aWR0aDogMTAwJTtcXG4gIFwiXSkpKSxcclxufTtcclxudmFyIHRlbXBsYXRlT2JqZWN0XzEsIHRlbXBsYXRlT2JqZWN0XzI7XHJcbi8vIyBzb3VyY2VNYXBwaW5nVVJMPXN0eWxlcy5qcy5tYXAiXX0= */";
var styles$j = {
  accountMenuContainer: /*#__PURE__*/css(templateObject_1$k || (templateObject_1$k = __makeTemplateObject(["\n    width: 246px;\n    position: absolute;\n    bottom: 0;\n    z-index: 101;\n  ;label:accountMenuContainer;" + _emotionSourceMap$k], ["\n    width: 246px;\n    position: absolute;\n    bottom: 0;\n    z-index: 101;\n  ;label:accountMenuContainer;" + _emotionSourceMap$k]))),
  itemWrapper: /*#__PURE__*/css(templateObject_2$b || (templateObject_2$b = __makeTemplateObject(["\n    width: 100%;\n  ;label:itemWrapper;" + _emotionSourceMap2$b], ["\n    width: 100%;\n  ;label:itemWrapper;" + _emotionSourceMap2$b])))
};
var templateObject_1$k, templateObject_2$b;

var AccountMenu = function (_a) {
  var isOpen = _a.isOpen,
    navOpen = _a.navOpen,
    sections = _a.sections,
    setIsOpen = _a.setIsOpen,
    user = _a.user;
  var offsetVariants = {
    open: {
      x: 200
    },
    closed: {
      x: 50
    }
  };
  var menuVariants = {
    open: {
      opacity: 1,
      transition: {
        staggerChildren: 0.035,
        delayChildren: 0.2
      }
    },
    closed: {
      opacity: 0,
      transition: {
        staggerChildren: 0.05,
        staggerDirection: -1
      }
    }
  };
  var itemVariants = {
    open: {
      y: 0,
      opacity: 1,
      transition: {
        y: {
          stiffness: 1000,
          velocity: -100
        }
      }
    },
    closed: {
      y: 50,
      opacity: 0,
      transition: {
        y: {
          stiffness: 1000
        }
      }
    }
  };
  var ref = useRef(null);
  useOutsideClick(ref, function () {
    setIsOpen(true);
  }, 'accountMenuClickEvent');
  return jsx(motion.div, {
    animate: navOpen ? 'open' : 'closed',
    variants: offsetVariants,
    initial: 'closed',
    css: styles$j.accountMenuContainer,
    transition: springTransition
  }, jsx(AnimatePresence, null, isOpen && jsx(motion.div, {
    ref: ref,
    key: "menu",
    exit: 'closed',
    animate: isOpen ? 'open' : 'closed',
    variants: menuVariants,
    initial: 'closed'
  }, jsx(Menu, null, user && jsx(MenuSection, {
    divider: true
  }, jsx(ListItem, {
    orientation: "vertical"
  }, jsx(Div, {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    cursor: "pointer",
    onClick: function () {
      user.onClick && user.onClick();
      setIsOpen(false);
    }
  }, jsx(UserCard, {
    size: 'small',
    firstName: user.firstName,
    lastName: user.lastName,
    profileImage: user.profileImage,
    caption: 'Manage Account',
    minimize: !isOpen,
    active: true,
    heading: "Hello " + user.firstName
  }), jsx(Icon, {
    size: "small",
    name: "chevron",
    primaryColor: "default",
    orientation: "right"
  })))), sections.map(function (section, index) {
    return jsx(MenuSection, {
      key: index,
      divider: true
    }, section.map(function (item, index) {
      return jsx(motion.div, {
        exit: 'closed',
        key: index,
        css: styles$j.itemWrapper,
        variants: itemVariants
      }, jsx(MenuItem, {
        onClick: function () {
          item.onClick();
          setIsOpen(false);
        },
        title: item.title,
        icon: item.icon,
        badge: item.badge
      }));
    }));
  })))));
};

var Banner = function (_a) {
  var _b = _a.isOutlined,
    isOutlined = _b === void 0 ? true : _b,
    props = __rest(_a, ["isOutlined"]);
  var _c = useState(false),
    isHovering = _c[0],
    setIsHovering = _c[1];
  var device = useDeviceSize().device;
  var isMobile = device === 'xSmall' ? true : false;
  return jsx(Flex, {
    width: "100%",
    backgroundColor: {
      swatch: props.status === 'success' ? 'success' : props.status === 'warning' ? 'warning' : 'danger',
      shade: isOutlined ? '_1000' : 'base'
    },
    borderWidth: isOutlined ? 'default' : undefined,
    borderStyle: isOutlined ? 'solid' : undefined,
    borderColor: isOutlined ? {
      swatch: props.status === 'success' ? 'success' : props.status === 'warning' ? 'warning' : 'danger',
      shade: 'base'
    } : undefined,
    inset: {
      vertical: 'x2',
      horizontal: 'x1'
    },
    borderRadius: "default",
    justifyContent: "flex-start",
    alignItems: "center"
  }, props.bannerTitle ? jsx(Flex, {
    width: "100%"
  }, props.bannerTitle.icon && jsx(Flex, {
    justifyContent: "center",
    alignItems: "center",
    inset: {
      horizontal: 'x1'
    }
  }, jsx(Icon, {
    name: props.bannerTitle.icon,
    size: "small",
    primaryColor: isOutlined ? 'primaryBodyText' : 'inverseHeadingText'
  })), jsx(Flex, {
    width: "100%"
  }, jsx(Text, {
    content: props.bannerTitle.message,
    color: isOutlined ? 'primaryBody' : 'inverseHeading',
    type: {
      xSmall: 'footnote',
      small: 'caption'
    }
  }))) : jsx(Flex, {
    width: "100%",
    alignItems: "center"
  }, props.children), props.onClose && jsx(Flex, {
    justifyContent: "flex-end",
    alignItems: "center",
    outset: {
      right: isMobile ? 'x0' : 'x2',
      left: 'x4'
    },
    onHoverStart: function () {
      return setIsHovering(true);
    },
    onHoverEnd: function () {
      return setIsHovering(false);
    }
  }, jsx(Flex, {
    onClick: props.onClose,
    width: "40px",
    height: "40px",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
    backgroundColor: isHovering ? 'foreground' : undefined,
    borderRadius: "default"
  }, jsx(Icon, {
    primaryColor: "default",
    size: "xSmall",
    name: "close"
  }))));
};

var _emotionSourceMap2$a = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFJYSIsImZpbGUiOiJzdHlsZXMudHMiLCJzb3VyY2VzQ29udGVudCI6WyJpbXBvcnQgeyBfX21ha2VUZW1wbGF0ZU9iamVjdCB9IGZyb20gXCJ0c2xpYlwiO1xyXG5pbXBvcnQgeyBjc3MgfSBmcm9tICdAZW1vdGlvbi9jb3JlJztcclxuZXhwb3J0IGRlZmF1bHQge1xyXG4gICAgY2lyY2xlTWVudUNvbnRhaW5lcjogY3NzKHRlbXBsYXRlT2JqZWN0XzEgfHwgKHRlbXBsYXRlT2JqZWN0XzEgPSBfX21ha2VUZW1wbGF0ZU9iamVjdChbXCJcXG4gICAgY3Vyc29yOiBwb2ludGVyO1xcbiAgICA+IGRpdiA+IHN2ZyB7XFxuICAgICAgdHJhbnNmb3JtOiBzY2FsZSgxLjUpO1xcbiAgICB9XFxuICBcIl0sIFtcIlxcbiAgICBjdXJzb3I6IHBvaW50ZXI7XFxuICAgID4gZGl2ID4gc3ZnIHtcXG4gICAgICB0cmFuc2Zvcm06IHNjYWxlKDEuNSk7XFxuICAgIH1cXG4gIFwiXSkpKSxcclxuICAgIHRyaWdnZXI6IGNzcyh0ZW1wbGF0ZU9iamVjdF8yIHx8ICh0ZW1wbGF0ZU9iamVjdF8yID0gX19tYWtlVGVtcGxhdGVPYmplY3QoW1wiXFxuICAgIGRpc3BsYXk6IG5vbmU7XFxuICBcIl0sIFtcIlxcbiAgICBkaXNwbGF5OiBub25lO1xcbiAgXCJdKSkpLFxyXG59O1xyXG52YXIgdGVtcGxhdGVPYmplY3RfMSwgdGVtcGxhdGVPYmplY3RfMjtcclxuLy8jIHNvdXJjZU1hcHBpbmdVUkw9c3R5bGVzLmpzLm1hcCJdfQ== */";
var _emotionSourceMap$j = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFHeUIiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgX19tYWtlVGVtcGxhdGVPYmplY3QgfSBmcm9tIFwidHNsaWJcIjtcclxuaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vY29yZSc7XHJcbmV4cG9ydCBkZWZhdWx0IHtcclxuICAgIGNpcmNsZU1lbnVDb250YWluZXI6IGNzcyh0ZW1wbGF0ZU9iamVjdF8xIHx8ICh0ZW1wbGF0ZU9iamVjdF8xID0gX19tYWtlVGVtcGxhdGVPYmplY3QoW1wiXFxuICAgIGN1cnNvcjogcG9pbnRlcjtcXG4gICAgPiBkaXYgPiBzdmcge1xcbiAgICAgIHRyYW5zZm9ybTogc2NhbGUoMS41KTtcXG4gICAgfVxcbiAgXCJdLCBbXCJcXG4gICAgY3Vyc29yOiBwb2ludGVyO1xcbiAgICA+IGRpdiA+IHN2ZyB7XFxuICAgICAgdHJhbnNmb3JtOiBzY2FsZSgxLjUpO1xcbiAgICB9XFxuICBcIl0pKSksXHJcbiAgICB0cmlnZ2VyOiBjc3ModGVtcGxhdGVPYmplY3RfMiB8fCAodGVtcGxhdGVPYmplY3RfMiA9IF9fbWFrZVRlbXBsYXRlT2JqZWN0KFtcIlxcbiAgICBkaXNwbGF5OiBub25lO1xcbiAgXCJdLCBbXCJcXG4gICAgZGlzcGxheTogbm9uZTtcXG4gIFwiXSkpKSxcclxufTtcclxudmFyIHRlbXBsYXRlT2JqZWN0XzEsIHRlbXBsYXRlT2JqZWN0XzI7XHJcbi8vIyBzb3VyY2VNYXBwaW5nVVJMPXN0eWxlcy5qcy5tYXAiXX0= */";
var styles$i = {
  circleMenuContainer: /*#__PURE__*/css(templateObject_1$j || (templateObject_1$j = __makeTemplateObject(["\n    cursor: pointer;\n    > div > svg {\n      transform: scale(1.5);\n    }\n  ;label:circleMenuContainer;" + _emotionSourceMap$j], ["\n    cursor: pointer;\n    > div > svg {\n      transform: scale(1.5);\n    }\n  ;label:circleMenuContainer;" + _emotionSourceMap$j]))),
  trigger: /*#__PURE__*/css(templateObject_2$a || (templateObject_2$a = __makeTemplateObject(["\n    display: none;\n  ;label:trigger;" + _emotionSourceMap2$a], ["\n    display: none;\n  ;label:trigger;" + _emotionSourceMap2$a])))
};
var templateObject_1$j, templateObject_2$a;

var CircleMenu = function (_a) {
  var icon = _a.icon,
    size = _a.size,
    isOpen = _a.isOpen,
    setIsSheetOpen = _a.setIsSheetOpen;
  return jsx(motion.div, {
    css: styles$i.circleMenuContainer,
    id: "circleMenuEvent"
  }, jsx(Circle, {
    animate: isOpen ? {
      transform: 'rotate(45deg)'
    } : {
      transform: 'rotate(0deg)'
    },
    onClick: function () {
      return setIsSheetOpen(!isOpen);
    },
    fill: "inverseBodyText",
    icon: icon,
    size: size,
    scale: 0.85,
    layer: 4
  }));
};

var _emotionSourceMap5$2 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap4$3 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap3$6 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap2$9 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap$i = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var styles$h = {
  navigationPillContainer: function (height) {
    return /*#__PURE__*/css(templateObject_1$i || (templateObject_1$i = __makeTemplateObject(["\n    position: absolute;\n    bottom: 0;\n    left: 0;\n    right: 0;\n    height: ", "px;\n    display: flex;\n    text-align: center;\n    border-radius: 24px;\n    z-index: 5;\n    min-width: 320px;\n  " + _emotionSourceMap$i], ["\n    position: absolute;\n    bottom: 0;\n    left: 0;\n    right: 0;\n    height: ", "px;\n    display: flex;\n    text-align: center;\n    border-radius: 24px;\n    z-index: 5;\n    min-width: 320px;\n  " + _emotionSourceMap$i])), height);
  },
  pillBackground: /*#__PURE__*/css(templateObject_2$9 || (templateObject_2$9 = __makeTemplateObject(["\n    position: absolute;\n    top: 0;\n    left: 0;\n    width: 100%;\n    height: 100%;\n    transform: translateX(33.333%);\n  ;label:pillBackground;" + _emotionSourceMap2$9], ["\n    position: absolute;\n    top: 0;\n    left: 0;\n    width: 100%;\n    height: 100%;\n    transform: translateX(33.333%);\n  ;label:pillBackground;" + _emotionSourceMap2$9]))),
  pillCutOut: function (cutOutSize) {
    return /*#__PURE__*/css(templateObject_3$6 || (templateObject_3$6 = __makeTemplateObject(["\n    position: relative;\n    width: ", "px;\n    height: ", "px;\n    left: calc((33.3333% - ", "px) / 2);\n    z-index: 5;\n    border-radius: 50%;\n    box-shadow: ", "px ", "px 0 #fff,\n      ", "px ", "px 0 #fff, 0 ", "px 0 #fff;\n\n    &:before,\n    &:after {\n      content: ' ';\n      position: absolute;\n      width: 100vw;\n      height: 100%;\n      background-color: #fff;\n      top: 30%;\n      border-top: solid 2px #fff;\n    }\n\n    &:before {\n      right: 100%;\n      border-top-right-radius: 24px;\n    }\n\n    &:after {\n      left: 100%;\n      border-top-left-radius: 24px;\n    }\n  " + _emotionSourceMap3$6], ["\n    position: relative;\n    width: ", "px;\n    height: ", "px;\n    left: calc((33.3333% - ", "px) / 2);\n    z-index: 5;\n    border-radius: 50%;\n    box-shadow: ", "px ", "px 0 #fff,\n      ", "px ", "px 0 #fff, 0 ", "px 0 #fff;\n\n    &:before,\n    &:after {\n      content: ' ';\n      position: absolute;\n      width: 100vw;\n      height: 100%;\n      background-color: #fff;\n      top: 30%;\n      border-top: solid 2px #fff;\n    }\n\n    &:before {\n      right: 100%;\n      border-top-right-radius: 24px;\n    }\n\n    &:after {\n      left: 100%;\n      border-top-left-radius: 24px;\n    }\n  " + _emotionSourceMap3$6])), cutOutSize, cutOutSize, cutOutSize, cutOutSize / 5, cutOutSize / 2, -cutOutSize / 5, cutOutSize / 2, cutOutSize);
  },
  pillDot: function (cutOutSize) {
    return /*#__PURE__*/css(templateObject_4$3 || (templateObject_4$3 = __makeTemplateObject(["\n    width: ", "px;\n    height: ", "px;\n    border-radius: 50%;\n    background-color: rgba(50, 50, 50, 0.35);\n    position: absolute;\n    left: calc((33.3333% - ", "px) / 2);\n    top: 0%;\n    z-index: 4;\n    transform: scale(0.85);\n    box-shadow: 0px 0.5vh 1vh rgba(0, 0, 0, 0.3);\n  " + _emotionSourceMap4$3], ["\n    width: ", "px;\n    height: ", "px;\n    border-radius: 50%;\n    background-color: rgba(50, 50, 50, 0.35);\n    position: absolute;\n    left: calc((33.3333% - ", "px) / 2);\n    top: 0%;\n    z-index: 4;\n    transform: scale(0.85);\n    box-shadow: 0px 0.5vh 1vh rgba(0, 0, 0, 0.3);\n  " + _emotionSourceMap4$3])), cutOutSize, cutOutSize, cutOutSize);
  },
  pillItem: function (height) {
    return /*#__PURE__*/css(templateObject_5$2 || (templateObject_5$2 = __makeTemplateObject(["\n    position: relative;\n    display: flex;\n    align-items: center;\n    justify-content: center;\n    cursor: pointer;\n    z-index: 2;\n    top: 20%;\n    width: 100%;\n    height: ", "px;\n    > div {\n      height: 100%;\n    }\n  " + _emotionSourceMap5$2], ["\n    position: relative;\n    display: flex;\n    align-items: center;\n    justify-content: center;\n    cursor: pointer;\n    z-index: 2;\n    top: 20%;\n    width: 100%;\n    height: ", "px;\n    > div {\n      height: 100%;\n    }\n  " + _emotionSourceMap5$2])), height);
  }
};
var templateObject_1$i, templateObject_2$9, templateObject_3$6, templateObject_4$3, templateObject_5$2;

var NavigationPill = function (_a) {
  var circleSize = _a.circleSize,
    height = _a.height,
    links = _a.links,
    user = _a.user,
    accountRouteOnClick = _a.accountRouteOnClick,
    setIsExpanded = _a.setIsExpanded,
    isSheetOpen = _a.isSheetOpen,
    setSheetIsOpen = _a.setSheetIsOpen;
  var itemCountFromLinks = links && links.length;
  var circleLocation = links[Math.floor(itemCountFromLinks / 2)];
  return jsx(motion.div, {
    css: styles$h.navigationPillContainer(height)
  }, jsx(motion.div, {
    css: styles$h.pillBackground
  }, jsx(motion.div, {
    css: styles$h.pillCutOut(circleSize)
  }), jsx(motion.div, {
    css: styles$h.pillDot(circleSize)
  })), links.map(function (link, index) {
    return link.title !== circleLocation.title ? jsx(motion.div, {
      key: index + 1,
      css: styles$h.pillItem(circleSize)
    }, jsx(IconLink, {
      icon: link.icon,
      fill: "primaryBodyText",
      onClick: function () {
        link.onClick();
        setIsExpanded(false);
      },
      isActive: link.isActive
    })) : jsx(CircleMenu, {
      key: index + 1,
      icon: 'plus',
      size: 'large',
      isOpen: isSheetOpen,
      setIsSheetOpen: setSheetIsOpen
    });
  }), jsx(FlexItem, {
    onClick: accountRouteOnClick,
    height: circleSize + "px",
    cursor: "pointer",
    justifyContent: 'center',
    alignItems: 'center',
    top: '20%',
    position: 'relative'
  }, jsx(Avatar, {
    avatarImage: user.profileImage,
    size: 'xSmall'
  })));
};

var _emotionSourceMap3$5 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFLaUIiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgX19tYWtlVGVtcGxhdGVPYmplY3QgfSBmcm9tIFwidHNsaWJcIjtcclxuaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vY29yZSc7XHJcbmV4cG9ydCBkZWZhdWx0IHtcclxuICAgIGV4cGFuZGVkQnV0dG9uQ29udGFpbmVyOiBjc3ModGVtcGxhdGVPYmplY3RfMSB8fCAodGVtcGxhdGVPYmplY3RfMSA9IF9fbWFrZVRlbXBsYXRlT2JqZWN0KFtcIlxcbiAgICBtYXJnaW4tYm90dG9tOiA2NXB4O1xcbiAgICBAbWVkaWEgKG1pbi1oZWlnaHQ6IDYwMHB4KSB7XFxuICAgICAgbWFyZ2luLWJvdHRvbTogaW5pdGlhbDtcXG4gICAgfVxcbiAgXCJdLCBbXCJcXG4gICAgbWFyZ2luLWJvdHRvbTogNjVweDtcXG4gICAgQG1lZGlhIChtaW4taGVpZ2h0OiA2MDBweCkge1xcbiAgICAgIG1hcmdpbi1ib3R0b206IGluaXRpYWw7XFxuICAgIH1cXG4gIFwiXSkpKSxcclxuICAgIGF3YXJkQ2FyZENvbnRhaW5lcjogY3NzKHRlbXBsYXRlT2JqZWN0XzIgfHwgKHRlbXBsYXRlT2JqZWN0XzIgPSBfX21ha2VUZW1wbGF0ZU9iamVjdChbXCJcXG4gICAgJjpob3ZlciB7XFxuICAgICAgPiBkaXY6Zmlyc3Qtb2YtdHlwZSB7XFxuICAgICAgICB0cmFuc2Zvcm06IHNjYWxlKDEuMDYpO1xcbiAgICAgICAgdHJhbnNpdGlvbjogdHJhbnNmb3JtIDAuMnMgZWFzZS1pbi1vdXQ7XFxuICAgICAgfVxcbiAgICB9XFxuICBcIl0sIFtcIlxcbiAgICAmOmhvdmVyIHtcXG4gICAgICA+IGRpdjpmaXJzdC1vZi10eXBlIHtcXG4gICAgICAgIHRyYW5zZm9ybTogc2NhbGUoMS4wNik7XFxuICAgICAgICB0cmFuc2l0aW9uOiB0cmFuc2Zvcm0gMC4ycyBlYXNlLWluLW91dDtcXG4gICAgICB9XFxuICAgIH1cXG4gIFwiXSkpKSxcclxuICAgIGluY3JlbWVudGFsOiBjc3ModGVtcGxhdGVPYmplY3RfMyB8fCAodGVtcGxhdGVPYmplY3RfMyA9IF9fbWFrZVRlbXBsYXRlT2JqZWN0KFtcIlxcbiAgICB3aGl0ZS1zcGFjZTogbm93cmFwO1xcbiAgXCJdLCBbXCJcXG4gICAgd2hpdGUtc3BhY2U6IG5vd3JhcDtcXG4gIFwiXSkpKSxcclxufTtcclxudmFyIHRlbXBsYXRlT2JqZWN0XzEsIHRlbXBsYXRlT2JqZWN0XzIsIHRlbXBsYXRlT2JqZWN0XzM7XHJcbi8vIyBzb3VyY2VNYXBwaW5nVVJMPXN0eWxlcy5qcy5tYXAiXX0= */";
var _emotionSourceMap2$8 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFJd0IiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgX19tYWtlVGVtcGxhdGVPYmplY3QgfSBmcm9tIFwidHNsaWJcIjtcclxuaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vY29yZSc7XHJcbmV4cG9ydCBkZWZhdWx0IHtcclxuICAgIGV4cGFuZGVkQnV0dG9uQ29udGFpbmVyOiBjc3ModGVtcGxhdGVPYmplY3RfMSB8fCAodGVtcGxhdGVPYmplY3RfMSA9IF9fbWFrZVRlbXBsYXRlT2JqZWN0KFtcIlxcbiAgICBtYXJnaW4tYm90dG9tOiA2NXB4O1xcbiAgICBAbWVkaWEgKG1pbi1oZWlnaHQ6IDYwMHB4KSB7XFxuICAgICAgbWFyZ2luLWJvdHRvbTogaW5pdGlhbDtcXG4gICAgfVxcbiAgXCJdLCBbXCJcXG4gICAgbWFyZ2luLWJvdHRvbTogNjVweDtcXG4gICAgQG1lZGlhIChtaW4taGVpZ2h0OiA2MDBweCkge1xcbiAgICAgIG1hcmdpbi1ib3R0b206IGluaXRpYWw7XFxuICAgIH1cXG4gIFwiXSkpKSxcclxuICAgIGF3YXJkQ2FyZENvbnRhaW5lcjogY3NzKHRlbXBsYXRlT2JqZWN0XzIgfHwgKHRlbXBsYXRlT2JqZWN0XzIgPSBfX21ha2VUZW1wbGF0ZU9iamVjdChbXCJcXG4gICAgJjpob3ZlciB7XFxuICAgICAgPiBkaXY6Zmlyc3Qtb2YtdHlwZSB7XFxuICAgICAgICB0cmFuc2Zvcm06IHNjYWxlKDEuMDYpO1xcbiAgICAgICAgdHJhbnNpdGlvbjogdHJhbnNmb3JtIDAuMnMgZWFzZS1pbi1vdXQ7XFxuICAgICAgfVxcbiAgICB9XFxuICBcIl0sIFtcIlxcbiAgICAmOmhvdmVyIHtcXG4gICAgICA+IGRpdjpmaXJzdC1vZi10eXBlIHtcXG4gICAgICAgIHRyYW5zZm9ybTogc2NhbGUoMS4wNik7XFxuICAgICAgICB0cmFuc2l0aW9uOiB0cmFuc2Zvcm0gMC4ycyBlYXNlLWluLW91dDtcXG4gICAgICB9XFxuICAgIH1cXG4gIFwiXSkpKSxcclxuICAgIGluY3JlbWVudGFsOiBjc3ModGVtcGxhdGVPYmplY3RfMyB8fCAodGVtcGxhdGVPYmplY3RfMyA9IF9fbWFrZVRlbXBsYXRlT2JqZWN0KFtcIlxcbiAgICB3aGl0ZS1zcGFjZTogbm93cmFwO1xcbiAgXCJdLCBbXCJcXG4gICAgd2hpdGUtc3BhY2U6IG5vd3JhcDtcXG4gIFwiXSkpKSxcclxufTtcclxudmFyIHRlbXBsYXRlT2JqZWN0XzEsIHRlbXBsYXRlT2JqZWN0XzIsIHRlbXBsYXRlT2JqZWN0XzM7XHJcbi8vIyBzb3VyY2VNYXBwaW5nVVJMPXN0eWxlcy5qcy5tYXAiXX0= */";
var _emotionSourceMap$h = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFHNkIiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgX19tYWtlVGVtcGxhdGVPYmplY3QgfSBmcm9tIFwidHNsaWJcIjtcclxuaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vY29yZSc7XHJcbmV4cG9ydCBkZWZhdWx0IHtcclxuICAgIGV4cGFuZGVkQnV0dG9uQ29udGFpbmVyOiBjc3ModGVtcGxhdGVPYmplY3RfMSB8fCAodGVtcGxhdGVPYmplY3RfMSA9IF9fbWFrZVRlbXBsYXRlT2JqZWN0KFtcIlxcbiAgICBtYXJnaW4tYm90dG9tOiA2NXB4O1xcbiAgICBAbWVkaWEgKG1pbi1oZWlnaHQ6IDYwMHB4KSB7XFxuICAgICAgbWFyZ2luLWJvdHRvbTogaW5pdGlhbDtcXG4gICAgfVxcbiAgXCJdLCBbXCJcXG4gICAgbWFyZ2luLWJvdHRvbTogNjVweDtcXG4gICAgQG1lZGlhIChtaW4taGVpZ2h0OiA2MDBweCkge1xcbiAgICAgIG1hcmdpbi1ib3R0b206IGluaXRpYWw7XFxuICAgIH1cXG4gIFwiXSkpKSxcclxuICAgIGF3YXJkQ2FyZENvbnRhaW5lcjogY3NzKHRlbXBsYXRlT2JqZWN0XzIgfHwgKHRlbXBsYXRlT2JqZWN0XzIgPSBfX21ha2VUZW1wbGF0ZU9iamVjdChbXCJcXG4gICAgJjpob3ZlciB7XFxuICAgICAgPiBkaXY6Zmlyc3Qtb2YtdHlwZSB7XFxuICAgICAgICB0cmFuc2Zvcm06IHNjYWxlKDEuMDYpO1xcbiAgICAgICAgdHJhbnNpdGlvbjogdHJhbnNmb3JtIDAuMnMgZWFzZS1pbi1vdXQ7XFxuICAgICAgfVxcbiAgICB9XFxuICBcIl0sIFtcIlxcbiAgICAmOmhvdmVyIHtcXG4gICAgICA+IGRpdjpmaXJzdC1vZi10eXBlIHtcXG4gICAgICAgIHRyYW5zZm9ybTogc2NhbGUoMS4wNik7XFxuICAgICAgICB0cmFuc2l0aW9uOiB0cmFuc2Zvcm0gMC4ycyBlYXNlLWluLW91dDtcXG4gICAgICB9XFxuICAgIH1cXG4gIFwiXSkpKSxcclxuICAgIGluY3JlbWVudGFsOiBjc3ModGVtcGxhdGVPYmplY3RfMyB8fCAodGVtcGxhdGVPYmplY3RfMyA9IF9fbWFrZVRlbXBsYXRlT2JqZWN0KFtcIlxcbiAgICB3aGl0ZS1zcGFjZTogbm93cmFwO1xcbiAgXCJdLCBbXCJcXG4gICAgd2hpdGUtc3BhY2U6IG5vd3JhcDtcXG4gIFwiXSkpKSxcclxufTtcclxudmFyIHRlbXBsYXRlT2JqZWN0XzEsIHRlbXBsYXRlT2JqZWN0XzIsIHRlbXBsYXRlT2JqZWN0XzM7XHJcbi8vIyBzb3VyY2VNYXBwaW5nVVJMPXN0eWxlcy5qcy5tYXAiXX0= */";
var styles$g = {
  expandedButtonContainer: /*#__PURE__*/css(templateObject_1$h || (templateObject_1$h = __makeTemplateObject(["\n    margin-bottom: 65px;\n    @media (min-height: 600px) {\n      margin-bottom: initial;\n    }\n  ;label:expandedButtonContainer;" + _emotionSourceMap$h], ["\n    margin-bottom: 65px;\n    @media (min-height: 600px) {\n      margin-bottom: initial;\n    }\n  ;label:expandedButtonContainer;" + _emotionSourceMap$h]))),
  awardCardContainer: /*#__PURE__*/css(templateObject_2$8 || (templateObject_2$8 = __makeTemplateObject(["\n    &:hover {\n      > div:first-of-type {\n        transform: scale(1.06);\n        transition: transform 0.2s ease-in-out;\n      }\n    }\n  ;label:awardCardContainer;" + _emotionSourceMap2$8], ["\n    &:hover {\n      > div:first-of-type {\n        transform: scale(1.06);\n        transition: transform 0.2s ease-in-out;\n      }\n    }\n  ;label:awardCardContainer;" + _emotionSourceMap2$8]))),
  incremental: /*#__PURE__*/css(templateObject_3$5 || (templateObject_3$5 = __makeTemplateObject(["\n    white-space: nowrap;\n  ;label:incremental;" + _emotionSourceMap3$5], ["\n    white-space: nowrap;\n  ;label:incremental;" + _emotionSourceMap3$5])))
};
var templateObject_1$h, templateObject_2$8, templateObject_3$5;

var IncrementalAwardLabel = function (_a) {
  var title = _a.title,
    count = _a.count,
    icon = _a.icon,
    isExpanded = _a.isExpanded;
  return jsx(Div, {
    css: styles$g.incremental,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%"
  }, jsx(Span, {
    inset: {
      right: 'x1'
    },
    display: "flex",
    justifyContent: "center",
    alignItems: isExpanded ? 'flex-start' : 'center'
  }, jsx(Icon, {
    name: icon,
    primaryColor: count > 0 ? 'success' : 'primaryBodyText',
    size: isExpanded ? 'medium' : 'small'
  })), jsx(Text, {
    isItalic: true,
    type: isExpanded ? 'caption' : 'footnote',
    content: title + ": " + count,
    color: "primaryBody"
  }));
};

var AwardCard = function (_a) {
  var image = _a.image,
    title = _a.title,
    onClick = _a.onClick,
    progress = _a.progress;
  return jsx(Card, {
    cursor: "pointer",
    onClick: onClick && function () {
      return onClick();
    },
    width: "210px",
    height: "285px",
    inset: {
      vertical: 'x3',
      horizontal: 'x2'
    },
    css: styles$g.awardCardContainer,
    borderRadius: "large"
  }, jsx(Div, {
    outset: {
      bottom: 'x1_5'
    },
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  }, jsx(Image$1, {
    isActive: true,
    image: {
      url: image
    },
    activeFilter: typeof progress === 'number' ? progress === 100 ? {
      name: 'none',
      filter: 'initial',
      background: 'initial'
    } : {
      name: 'Locked',
      filter: 'grayscale(100%) invert(35%) blur(1px)'
    } : progress.count > 0 ? {
      name: 'none',
      filter: 'initial',
      background: 'initial'
    } : {
      name: 'Locked',
      filter: 'grayscale(100%) invert(35%) blur(1px)'
    },
    width: "145px",
    height: "145px"
  })), jsx(Text, {
    type: "body",
    alignment: "center",
    content: title
  }), jsx(Div, {
    width: "100%",
    height: "16px",
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    outset: {
      top: 'x_75'
    }
  }, typeof progress === 'number' ? jsx(Progress, {
    percentage: progress
  }) : jsx(IncrementalAwardLabel, {
    isExpanded: false,
    title: progress.title,
    count: progress.count,
    icon: progress.icon
  })));
};

var ExpandedAwardCard = function (_a) {
  var image = _a.image,
    title = _a.title,
    checklist = _a.checklist,
    checklistTitle = _a.checklistTitle,
    checklistSubtitle = _a.checklistSubtitle,
    description = _a.description,
    close = _a.close,
    progress = _a.progress;
  return jsx(Card, {
    backgroundColor: 'foreground',
    outset: "auto",
    overflow: "auto",
    width: "100%",
    maxWidth: "720px",
    minWidth: "320px",
    minHeight: {
      xSmall: '500px'
    },
    maxHeight: {
      xSmall: '780px',
      small: "" + (typeof progress === 'number' ? progress === 100 ? '702px' : '530px' : progress.count > 0 ? '702px' : '530px')
    },
    inset: {
      vertical: {
        xSmall: 'x4'
      },
      horizontal: {
        xSmall: 'x2',
        small: 'x4'
      }
    },
    outsideClick: function () {
      return close();
    }
  }, jsx(Div, {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
    position: "absolute",
    top: '16px',
    right: '16px',
    onClick: function () {
      return close();
    }
  }, jsx(Icon, {
    size: "xSmall",
    name: "close",
    primaryColor: "primaryHeadingText"
  })), jsx(Div, {
    display: "flex",
    justifyContent: typeof progress === 'number' ? progress === 100 ? 'center' : {
      xSmall: 'center',
      small: 'flex-start'
    } : progress.count > 0 ? 'center' : {
      xSmall: 'center',
      small: 'flex-start'
    },
    alignItems: "center",
    flexDirection: typeof progress === 'number' ? progress === 100 ? 'column' : {
      xSmall: 'column',
      small: 'row'
    } : progress.count > 0 ? 'column' : {
      xSmall: 'column',
      small: 'row'
    },
    outset: {
      bottom: {
        xSmall: 'x2',
        small: 'x4'
      }
    },
    width: "100%"
  }, jsx(Div, {
    outset: {
      bottom: {
        xSmall: 'x2',
        small: 'x0'
      }
    },
    width: {
      xSmall: typeof progress === 'number' ? progress === 100 ? '196px' : '145px' : progress.count > 0 ? '196px' : '145px',
      small: typeof progress === 'number' ? progress === 100 ? '276px' : '145px' : progress.count > 0 ? '276px' : '145px'
    },
    height: {
      xSmall: typeof progress === 'number' ? progress === 100 ? '147px' : '145px' : progress.count > 0 ? '147px' : '145px',
      small: typeof progress === 'number' ? progress === 100 ? '207px' : '145px' : progress.count > 0 ? '207px' : '145px'
    }
  }, jsx(Image$1, {
    isActive: true,
    image: {
      url: image
    },
    activeFilter: typeof progress === 'number' ? progress === 100 ? {
      name: 'none',
      filter: 'initial',
      background: 'initial'
    } : {
      name: 'Locked',
      filter: 'grayscale(100%) invert(35%) blur(1px)'
    } : progress.count > 0 ? {
      name: 'none',
      filter: 'initial',
      background: 'initial'
    } : {
      name: 'Locked',
      filter: 'grayscale(100%) invert(35%) blur(1px)'
    },
    width: typeof progress === 'number' ? progress === 100 ? '100%' : '145px' : progress.count > 0 ? '100%' : '145px',
    height: typeof progress === 'number' ? progress === 100 ? '100%' : '145px' : progress.count > 0 ? '100%' : '145px'
  })), jsx(Div, {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: typeof progress === 'number' ? progress === 100 ? 'center' : {
      xSmall: 'center',
      small: 'flex-start'
    } : progress.count > 0 ? 'center' : {
      xSmall: 'center',
      small: 'flex-start'
    },
    outset: typeof progress === 'number' ? progress === 100 ? {
      top: {
        xSmall: 'x1',
        small: 'x2'
      }
    } : {
      left: {
        xSmall: 'x0',
        small: 'x4'
      }
    } : progress.count > 0 ? {
      top: {
        xSmall: 'x1',
        small: 'x2'
      }
    } : {
      left: {
        xSmall: 'x0',
        small: 'x4'
      }
    }
  }, jsx(Div, {
    outset: {
      top: {
        xSmall: 'x1',
        small: 'x0'
      },
      bottom: {
        xSmall: 'x1_5',
        small: 'x1'
      }
    }
  }, jsx(Text, {
    weight: "semiBold",
    type: "largeBody",
    alignment: "center",
    content: title,
    color: "primaryHeading"
  })), jsx(Text, {
    type: {
      xSmall: 'caption',
      small: 'body',
      medium: 'body',
      large: 'body',
      xLarge: 'body'
    },
    color: "primaryBody",
    content: description
  }))), jsx(Div, {
    width: "100%",
    height: "16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start"
  }, typeof progress === 'number' ? jsx(Progress, {
    percentage: progress
  }) : jsx(IncrementalAwardLabel, {
    isExpanded: true,
    title: progress.title,
    count: progress.count,
    icon: progress.icon
  })), jsx(Div, {
    width: "100%",
    outset: {
      top: {
        xSmall: 'x2',
        small: 'x3'
      },
      bottom: 'x0'
    }
  }, jsx(Text, {
    color: "primaryBody",
    type: "body",
    content: checklistTitle
  })), jsx(Div, {
    width: "100%"
  }, jsx(Div, {
    inset: {
      bottom: {
        xSmall: 'x1',
        small: 'x2'
      }
    }
  }, jsx(Text, {
    isItalic: true,
    type: {
      xSmall: 'footnote',
      small: 'caption',
      medium: 'caption',
      large: 'caption',
      xLarge: 'caption'
    },
    color: "primaryBody",
    content: checklistSubtitle
  }))), jsx(List, {
    outset: {
      bottom: 'x3'
    },
    width: "100%",
    orientation: "vertical",
    alignment: "center"
  }, checklist.map(function (item, i) {
    return jsx(ListItem, {
      key: i,
      inset: {
        top: {
          xSmall: 'x_5'
        },
        right: {
          xSmall: 'x_5'
        },
        bottom: {
          xSmall: 'x_5'
        },
        left: {
          xSmall: 'x0'
        }
      },
      orientation: "vertical"
    }, jsx(Todo, {
      todo: item
    }));
  })), jsx(Div, {
    css: styles$g.expandedButtonContainer
  }, jsx(Button, {
    onClick: function () {
      return close();
    },
    type: "secondary",
    size: "medium",
    title: "Close Details"
  })));
};

var AwardGroupLabel = function (_a) {
  var image = _a.image,
    label = _a.label;
  return jsx(Div, {
    display: "flex",
    alignItems: "center"
  }, jsx(Div, {
    width: "34px",
    height: "34px",
    borderRadius: "small",
    backgroundImage: image,
    backgroundSize: "cover",
    backgroundPosition: "center",
    outset: {
      right: 'x2'
    }
  }), jsx(Text, {
    type: "body"
  }, label));
};

var context = {
  activeIndexes: [0],
  setActiveIndexes: function () {},
  defaultActiveIndexes: [0],
  isExclusive: false
};
var AccordionContext = /*#__PURE__*/React.createContext(context);

var _emotionSourceMap2$7 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFJbUIiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgX19tYWtlVGVtcGxhdGVPYmplY3QgfSBmcm9tIFwidHNsaWJcIjtcclxuaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vY29yZSc7XHJcbmV4cG9ydCBkZWZhdWx0IHtcclxuICAgIGFjY29yZGlvbkNvbnRhaW5lcjogY3NzKHRlbXBsYXRlT2JqZWN0XzEgfHwgKHRlbXBsYXRlT2JqZWN0XzEgPSBfX21ha2VUZW1wbGF0ZU9iamVjdChbXCJcXG4gICAgd2lkdGg6IDEwMCU7XFxuICAgIG92ZXJmbG93OiBoaWRkZW47XFxuICBcIl0sIFtcIlxcbiAgICB3aWR0aDogMTAwJTtcXG4gICAgb3ZlcmZsb3c6IGhpZGRlbjtcXG4gIFwiXSkpKSxcclxuICAgIGFjY29yZGlvbkJvZHk6IGNzcyh0ZW1wbGF0ZU9iamVjdF8yIHx8ICh0ZW1wbGF0ZU9iamVjdF8yID0gX19tYWtlVGVtcGxhdGVPYmplY3QoW1wiXFxuICAgIG92ZXJmbG93OiBhdXRvO1xcbiAgXCJdLCBbXCJcXG4gICAgb3ZlcmZsb3c6IGF1dG87XFxuICBcIl0pKSksXHJcbn07XHJcbnZhciB0ZW1wbGF0ZU9iamVjdF8xLCB0ZW1wbGF0ZU9iamVjdF8yO1xyXG4vLyMgc291cmNlTWFwcGluZ1VSTD1zdHlsZXMuanMubWFwIl19 */";
var _emotionSourceMap$g = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFHd0IiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgX19tYWtlVGVtcGxhdGVPYmplY3QgfSBmcm9tIFwidHNsaWJcIjtcclxuaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vY29yZSc7XHJcbmV4cG9ydCBkZWZhdWx0IHtcclxuICAgIGFjY29yZGlvbkNvbnRhaW5lcjogY3NzKHRlbXBsYXRlT2JqZWN0XzEgfHwgKHRlbXBsYXRlT2JqZWN0XzEgPSBfX21ha2VUZW1wbGF0ZU9iamVjdChbXCJcXG4gICAgd2lkdGg6IDEwMCU7XFxuICAgIG92ZXJmbG93OiBoaWRkZW47XFxuICBcIl0sIFtcIlxcbiAgICB3aWR0aDogMTAwJTtcXG4gICAgb3ZlcmZsb3c6IGhpZGRlbjtcXG4gIFwiXSkpKSxcclxuICAgIGFjY29yZGlvbkJvZHk6IGNzcyh0ZW1wbGF0ZU9iamVjdF8yIHx8ICh0ZW1wbGF0ZU9iamVjdF8yID0gX19tYWtlVGVtcGxhdGVPYmplY3QoW1wiXFxuICAgIG92ZXJmbG93OiBhdXRvO1xcbiAgXCJdLCBbXCJcXG4gICAgb3ZlcmZsb3c6IGF1dG87XFxuICBcIl0pKSksXHJcbn07XHJcbnZhciB0ZW1wbGF0ZU9iamVjdF8xLCB0ZW1wbGF0ZU9iamVjdF8yO1xyXG4vLyMgc291cmNlTWFwcGluZ1VSTD1zdHlsZXMuanMubWFwIl19 */";
var styles$f = {
  accordionContainer: /*#__PURE__*/css(templateObject_1$g || (templateObject_1$g = __makeTemplateObject(["\n    width: 100%;\n    overflow: hidden;\n  ;label:accordionContainer;" + _emotionSourceMap$g], ["\n    width: 100%;\n    overflow: hidden;\n  ;label:accordionContainer;" + _emotionSourceMap$g]))),
  accordionBody: /*#__PURE__*/css(templateObject_2$7 || (templateObject_2$7 = __makeTemplateObject(["\n    overflow: auto;\n  ;label:accordionBody;" + _emotionSourceMap2$7], ["\n    overflow: auto;\n  ;label:accordionBody;" + _emotionSourceMap2$7])))
};
var templateObject_1$g, templateObject_2$7;

var AccordionContent = function (_a) {
  var children = _a.children,
    onAnimationComplete = _a.onAnimationComplete,
    _b = _a.inset,
    inset = _b === void 0 ? 'x2_5' : _b;
  return jsx(Div, {
    key: "content",
    initial: "collapsed",
    animate: "open",
    exit: "collapsed",
    variants: {
      open: {
        opacity: 1,
        height: 'auto'
      },
      collapsed: {
        opacity: 0,
        height: 0
      }
    },
    transition: {
      duration: 0.6,
      ease: [0.04, 0.62, 0.23, 0.98],
      damping: 300
    },
    css: styles$f.accordionBody,
    onAnimationComplete: onAnimationComplete
  }, jsx(Flex, {
    inset: inset,
    justifyContent: "flex-start",
    alignItems: "center",
    variants: {
      collapsed: {
        scale: 0.8
      },
      open: {
        scale: 1
      }
    },
    transition: {
      type: 'spring',
      duration: 0.8
    }
  }, children));
};
var AccordionBody = function (_a) {
  var eventKey = _a.eventKey,
    children = _a.children,
    onAnimationComplete = _a.onAnimationComplete,
    insetBody = _a.insetBody;
  var context = useContext(AccordionContext);
  return jsx(AnimatePresence, null, context.activeIndexes.map(function (activeIndex) {
    return activeIndex === eventKey && jsx(AccordionContent, {
      onAnimationComplete: onAnimationComplete,
      inset: insetBody
    }, children);
  }));
};

var variants = {
  expanded: {
    transform: 'rotate(90deg)'
  },
  collapsed: {
    transform: 'rotate(0deg)'
  }
};
var AccordionHeading = function (_a) {
  var children = _a.children,
    eventKey = _a.eventKey,
    headingSize = _a.headingSize,
    onHeadingClick = _a.onHeadingClick,
    _b = _a.arrowLocation,
    arrowLocation = _b === void 0 ? 'right' : _b;
  var context = useContext(AccordionContext);
  var isActive = context.activeIndexes.some(function (i) {
    return i === eventKey;
  });
  var handleSetActiveIndex = function (eventKey) {
    if (context.isExclusive) {
      if (context.activeIndexes.some(function (activeIndex) {
        return activeIndex === eventKey;
      })) {
        context.setActiveIndexes([-1]);
      } else {
        context.setActiveIndexes([eventKey]);
      }
    } else {
      if (isActive) {
        var filteredIndexes = context.activeIndexes.filter(function (key) {
          return key !== eventKey;
        });
        var newArray = filteredIndexes.length === 0 ? [-1] : filteredIndexes;
        context.setActiveIndexes(newArray);
      } else {
        var newArray = [eventKey];
        context.setActiveIndexes(__spreadArrays(context.activeIndexes.filter(function (index) {
          return index !== -1;
        }), newArray));
      }
    }
  };
  return jsx(Div, {
    onClick: function () {
      handleSetActiveIndex(eventKey);
      onHeadingClick === null || onHeadingClick === void 0 ? void 0 : onHeadingClick();
    },
    display: "flex",
    alignItems: "center",
    width: "100%",
    justifyContent: "space-between",
    cursor: "pointer",
    inset: "x1_5",
    position: "relative",
    zIndex: 5,
    flexDirection: arrowLocation === 'left' ? 'row' : 'row-reverse'
  }, arrowLocation !== 'none' && jsx(Span, {
    cursor: "pointer",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    variants: variants,
    initial: isActive ? 'expanded' : 'collapsed',
    animate: isActive ? 'expanded' : 'collapsed'
  }, jsx(Icon, {
    size: "medium",
    name: "chevron",
    primaryColor: "default",
    orientation: "right"
  })), typeof children === 'string' ? jsx(Text, {
    content: children,
    type: headingSize ? computeTextTypeFromSize(headingSize) : 'body',
    color: "primaryBody",
    weight: isActive ? 'semiBold' : 'regular'
  }) : children);
};

var Accordion = function (_a) {
  var _b, _c;
  var sections = _a.sections,
    defaultActiveKeys = _a.defaultActiveKeys,
    _d = _a.isExclusive,
    isExclusive = _d === void 0 ? false : _d,
    background = _a.background,
    _e = _a.isHeadingBackgroundInverse,
    isHeadingBackgroundInverse = _e === void 0 ? false : _e,
    stateOverride = _a.stateOverride,
    _f = _a.inset,
    inset = _f === void 0 ? {
      vertical: 'x2',
      horizontal: 'x1'
    } : _f,
    insetBody = _a.insetBody,
    arrowLocation = _a.arrowLocation;
  var defaultActiveIndexes = isExclusive ? defaultActiveKeys ? [defaultActiveKeys[0]] : [-1] : defaultActiveKeys ? __spreadArrays(defaultActiveKeys) : [-1];
  var _g = useState(defaultActiveIndexes),
    activeIndexes = _g[0],
    setActiveIndexes = _g[1];
  var defaultEventKey = defaultActiveKeys ? defaultActiveKeys : [-1];
  useEffect(function () {
    /** We only want the default state sent back on initial render */
    stateOverride === null || stateOverride === void 0 ? void 0 : stateOverride.setActiveIndexes(defaultActiveIndexes);
  }, []); //eslint-disable-line
  return jsx(Div, {
    backgroundColor: background,
    borderRadius: "medium",
    inset: inset
  }, jsx(AccordionContext.Provider, {
    value: {
      activeIndexes: (_b = stateOverride === null || stateOverride === void 0 ? void 0 : stateOverride.activeIndexes) !== null && _b !== void 0 ? _b : activeIndexes,
      setActiveIndexes: (_c = stateOverride === null || stateOverride === void 0 ? void 0 : stateOverride.setActiveIndexes) !== null && _c !== void 0 ? _c : setActiveIndexes,
      defaultActiveIndexes: defaultEventKey,
      isExclusive: isExclusive
    }
  }, sections.map(function (section) {
    return jsx("section", {
      key: section.key,
      css: styles$f.accordionContainer
    }, jsx(AccordionHeading, {
      eventKey: section.key,
      arrowLocation: arrowLocation,
      isBackgroundInverse: isHeadingBackgroundInverse,
      onHeadingClick: section.onHeadingClick
    }, section.heading), jsx(AccordionBody, {
      eventKey: section.key,
      onAnimationComplete: section.onAnimationComplete,
      insetBody: insetBody
    }, jsx(Flex, {
      width: "100%"
    }, section.body)));
  })));
};

var _emotionSourceMap7 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap6$1 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap5$1 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFPNkMiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgX19tYWtlVGVtcGxhdGVPYmplY3QgfSBmcm9tIFwidHNsaWJcIjtcclxuaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vY29yZSc7XHJcbmV4cG9ydCBkZWZhdWx0IHtcclxuICAgIGNocm9tZUNvbnRhaW5lcjogZnVuY3Rpb24gKGlubmVySGVpZ2h0KSB7IHJldHVybiBjc3ModGVtcGxhdGVPYmplY3RfMSB8fCAodGVtcGxhdGVPYmplY3RfMSA9IF9fbWFrZVRlbXBsYXRlT2JqZWN0KFtcIlxcbiAgICBwb3NpdGlvbjogZml4ZWQ7XFxuICAgIHdpZHRoOiAxMDAlO1xcbiAgICBoZWlnaHQ6IFwiLCBcInB4O1xcbiAgICBvdmVyZmxvdzogaGlkZGVuO1xcbiAgICBib3gtc2hhZG93OiAwIDNweCA2cHggMCAjY2NjO1xcbiAgICB6LWluZGV4OiAwO1xcbiAgXCJdLCBbXCJcXG4gICAgcG9zaXRpb246IGZpeGVkO1xcbiAgICB3aWR0aDogMTAwJTtcXG4gICAgaGVpZ2h0OiBcIiwgXCJweDtcXG4gICAgb3ZlcmZsb3c6IGhpZGRlbjtcXG4gICAgYm94LXNoYWRvdzogMCAzcHggNnB4IDAgI2NjYztcXG4gICAgei1pbmRleDogMDtcXG4gIFwiXSkpLCBpbm5lckhlaWdodCk7IH0sXHJcbiAgICByZXZlYWxpbmdDb250ZW50OiBjc3ModGVtcGxhdGVPYmplY3RfMiB8fCAodGVtcGxhdGVPYmplY3RfMiA9IF9fbWFrZVRlbXBsYXRlT2JqZWN0KFtcIlxcbiAgICB3aWR0aDogMTAwJTtcXG4gICAgaGVpZ2h0OiAxMDAlO1xcbiAgICBwb3NpdGlvbjogYWJzb2x1dGU7XFxuICAgIHotaW5kZXg6IDI7XFxuICBcIl0sIFtcIlxcbiAgICB3aWR0aDogMTAwJTtcXG4gICAgaGVpZ2h0OiAxMDAlO1xcbiAgICBwb3NpdGlvbjogYWJzb2x1dGU7XFxuICAgIHotaW5kZXg6IDI7XFxuICBcIl0pKSksXHJcbiAgICBjb250ZW50OiBmdW5jdGlvbiAobW9iaWxlKSB7IHJldHVybiBjc3ModGVtcGxhdGVPYmplY3RfMyB8fCAodGVtcGxhdGVPYmplY3RfMyA9IF9fbWFrZVRlbXBsYXRlT2JqZWN0KFtcIlxcbiAgICBoZWlnaHQ6IDEwMCU7XFxuICAgIHBvc2l0aW9uOiBhYnNvbHV0ZTtcXG4gICAgei1pbmRleDogMztcXG4gICAgbWluLXdpZHRoOiBcIiwgXCI7XFxuICAgIG92ZXJmbG93OiBoaWRkZW47XFxuICAgIHdpZHRoOiAxMDAlO1xcbiAgXCJdLCBbXCJcXG4gICAgaGVpZ2h0OiAxMDAlO1xcbiAgICBwb3NpdGlvbjogYWJzb2x1dGU7XFxuICAgIHotaW5kZXg6IDM7XFxuICAgIG1pbi13aWR0aDogXCIsIFwiO1xcbiAgICBvdmVyZmxvdzogaGlkZGVuO1xcbiAgICB3aWR0aDogMTAwJTtcXG4gIFwiXSkpLCBtb2JpbGUgPyAnMzIwcHgnIDogJzU3NnB4Jyk7IH0sXHJcbiAgICBjb250ZXh0V3JhcHBlcjogY3NzKHRlbXBsYXRlT2JqZWN0XzQgfHwgKHRlbXBsYXRlT2JqZWN0XzQgPSBfX21ha2VUZW1wbGF0ZU9iamVjdChbXCJcXG4gICAgd2lkdGg6IDEwMCU7XFxuICAgIGhlaWdodDogMTAwJTtcXG4gICAgZGlzcGxheTogZmxleDtcXG4gICAganVzdGlmeS1jb250ZW50OiBzcGFjZS1iZXR3ZWVuO1xcbiAgICBhbGlnbi1pdGVtczogY2VudGVyO1xcbiAgXCJdLCBbXCJcXG4gICAgd2lkdGg6IDEwMCU7XFxuICAgIGhlaWdodDogMTAwJTtcXG4gICAgZGlzcGxheTogZmxleDtcXG4gICAganVzdGlmeS1jb250ZW50OiBzcGFjZS1iZXR3ZWVuO1xcbiAgICBhbGlnbi1pdGVtczogY2VudGVyO1xcbiAgXCJdKSkpLFxyXG4gICAgaW5uZXJDb250ZW50OiBmdW5jdGlvbiAobW9iaWxlKSB7IHJldHVybiBjc3ModGVtcGxhdGVPYmplY3RfNSB8fCAodGVtcGxhdGVPYmplY3RfNSA9IF9fbWFrZVRlbXBsYXRlT2JqZWN0KFtcIlxcbiAgICBwb3NpdGlvbjogYWJzb2x1dGU7XFxuICAgIHdpZHRoOiAxMDAlO1xcbiAgICBoZWlnaHQ6IDEwMCU7XFxuICAgIG92ZXJmbG93LXg6IGF1dG87XFxuICAgIHotaW5kZXg6IDM7XFxuICAgIHBhZGRpbmctYm90dG9tOiBcIiwgXCI7XFxuICBcIl0sIFtcIlxcbiAgICBwb3NpdGlvbjogYWJzb2x1dGU7XFxuICAgIHdpZHRoOiAxMDAlO1xcbiAgICBoZWlnaHQ6IDEwMCU7XFxuICAgIG92ZXJmbG93LXg6IGF1dG87XFxuICAgIHotaW5kZXg6IDM7XFxuICAgIHBhZGRpbmctYm90dG9tOiBcIiwgXCI7XFxuICBcIl0pKSwgbW9iaWxlICYmICc3MHB4Jyk7IH0sXHJcbiAgICBvdmVybGF5OiBmdW5jdGlvbiAoaW5uZXJXaWR0aCkgeyByZXR1cm4gY3NzKHRlbXBsYXRlT2JqZWN0XzYgfHwgKHRlbXBsYXRlT2JqZWN0XzYgPSBfX21ha2VUZW1wbGF0ZU9iamVjdChbXCJcXG4gICAgd2lkdGg6IDEwMCU7XFxuICAgIGhlaWdodDogXCIsIFwiO1xcbiAgICBwb3NpdGlvbjogYWJzb2x1dGU7XFxuICAgIGJvdHRvbTogMDtcXG4gICAgei1pbmRleDogNDtcXG4gIFwiXSwgW1wiXFxuICAgIHdpZHRoOiAxMDAlO1xcbiAgICBoZWlnaHQ6IFwiLCBcIjtcXG4gICAgcG9zaXRpb246IGFic29sdXRlO1xcbiAgICBib3R0b206IDA7XFxuICAgIHotaW5kZXg6IDQ7XFxuICBcIl0pKSwgaW5uZXJXaWR0aCA+IDU3NiA/ICcxMDAlJyA6ICdjYWxjKDEwMCUgLSA0NnB4KScpOyB9LFxyXG4gICAgZHJhZ0NvbnN0cmFpbnRzOiBmdW5jdGlvbiAobWVudVNpemUpIHsgcmV0dXJuIGNzcyh0ZW1wbGF0ZU9iamVjdF83IHx8ICh0ZW1wbGF0ZU9iamVjdF83ID0gX19tYWtlVGVtcGxhdGVPYmplY3QoW1wiXFxuICAgIHdpZHRoOiBcIiwgXCJweDtcXG4gICAgaGVpZ2h0OiAxMDAlO1xcbiAgICBkaXNwbGF5OiBmbGV4O1xcbiAgICBqdXN0aWZ5LWNvbnRlbnQ6IGZsZXgtZW5kO1xcbiAgICBmbGV4LWRpcmVjdGlvbjogY29sdW1uO1xcbiAgICBhbGlnbi1pdGVtczogY2VudGVyO1xcbiAgICBiYWNrZ3JvdW5kOiB0cmFuc3BhcmVudDtcXG4gICAgcG9zaXRpb246IGFic29sdXRlO1xcbiAgICBib3R0b206IDA7XFxuICAgIHotaW5kZXg6IDE7XFxuICBcIl0sIFtcIlxcbiAgICB3aWR0aDogXCIsIFwicHg7XFxuICAgIGhlaWdodDogMTAwJTtcXG4gICAgZGlzcGxheTogZmxleDtcXG4gICAganVzdGlmeS1jb250ZW50OiBmbGV4LWVuZDtcXG4gICAgZmxleC1kaXJlY3Rpb246IGNvbHVtbjtcXG4gICAgYWxpZ24taXRlbXM6IGNlbnRlcjtcXG4gICAgYmFja2dyb3VuZDogdHJhbnNwYXJlbnQ7XFxuICAgIHBvc2l0aW9uOiBhYnNvbHV0ZTtcXG4gICAgYm90dG9tOiAwO1xcbiAgICB6LWluZGV4OiAxO1xcbiAgXCJdKSksIG1lbnVTaXplKTsgfSxcclxufTtcclxudmFyIHRlbXBsYXRlT2JqZWN0XzEsIHRlbXBsYXRlT2JqZWN0XzIsIHRlbXBsYXRlT2JqZWN0XzMsIHRlbXBsYXRlT2JqZWN0XzQsIHRlbXBsYXRlT2JqZWN0XzUsIHRlbXBsYXRlT2JqZWN0XzYsIHRlbXBsYXRlT2JqZWN0Xzc7XHJcbi8vIyBzb3VyY2VNYXBwaW5nVVJMPXN0eWxlcy5qcy5tYXAiXX0= */";
var _emotionSourceMap4$2 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap3$4 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap2$6 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap$f = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFHcUQiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgX19tYWtlVGVtcGxhdGVPYmplY3QgfSBmcm9tIFwidHNsaWJcIjtcclxuaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vY29yZSc7XHJcbmV4cG9ydCBkZWZhdWx0IHtcclxuICAgIGNocm9tZUNvbnRhaW5lcjogZnVuY3Rpb24gKGlubmVySGVpZ2h0KSB7IHJldHVybiBjc3ModGVtcGxhdGVPYmplY3RfMSB8fCAodGVtcGxhdGVPYmplY3RfMSA9IF9fbWFrZVRlbXBsYXRlT2JqZWN0KFtcIlxcbiAgICBwb3NpdGlvbjogZml4ZWQ7XFxuICAgIHdpZHRoOiAxMDAlO1xcbiAgICBoZWlnaHQ6IFwiLCBcInB4O1xcbiAgICBvdmVyZmxvdzogaGlkZGVuO1xcbiAgICBib3gtc2hhZG93OiAwIDNweCA2cHggMCAjY2NjO1xcbiAgICB6LWluZGV4OiAwO1xcbiAgXCJdLCBbXCJcXG4gICAgcG9zaXRpb246IGZpeGVkO1xcbiAgICB3aWR0aDogMTAwJTtcXG4gICAgaGVpZ2h0OiBcIiwgXCJweDtcXG4gICAgb3ZlcmZsb3c6IGhpZGRlbjtcXG4gICAgYm94LXNoYWRvdzogMCAzcHggNnB4IDAgI2NjYztcXG4gICAgei1pbmRleDogMDtcXG4gIFwiXSkpLCBpbm5lckhlaWdodCk7IH0sXHJcbiAgICByZXZlYWxpbmdDb250ZW50OiBjc3ModGVtcGxhdGVPYmplY3RfMiB8fCAodGVtcGxhdGVPYmplY3RfMiA9IF9fbWFrZVRlbXBsYXRlT2JqZWN0KFtcIlxcbiAgICB3aWR0aDogMTAwJTtcXG4gICAgaGVpZ2h0OiAxMDAlO1xcbiAgICBwb3NpdGlvbjogYWJzb2x1dGU7XFxuICAgIHotaW5kZXg6IDI7XFxuICBcIl0sIFtcIlxcbiAgICB3aWR0aDogMTAwJTtcXG4gICAgaGVpZ2h0OiAxMDAlO1xcbiAgICBwb3NpdGlvbjogYWJzb2x1dGU7XFxuICAgIHotaW5kZXg6IDI7XFxuICBcIl0pKSksXHJcbiAgICBjb250ZW50OiBmdW5jdGlvbiAobW9iaWxlKSB7IHJldHVybiBjc3ModGVtcGxhdGVPYmplY3RfMyB8fCAodGVtcGxhdGVPYmplY3RfMyA9IF9fbWFrZVRlbXBsYXRlT2JqZWN0KFtcIlxcbiAgICBoZWlnaHQ6IDEwMCU7XFxuICAgIHBvc2l0aW9uOiBhYnNvbHV0ZTtcXG4gICAgei1pbmRleDogMztcXG4gICAgbWluLXdpZHRoOiBcIiwgXCI7XFxuICAgIG92ZXJmbG93OiBoaWRkZW47XFxuICAgIHdpZHRoOiAxMDAlO1xcbiAgXCJdLCBbXCJcXG4gICAgaGVpZ2h0OiAxMDAlO1xcbiAgICBwb3NpdGlvbjogYWJzb2x1dGU7XFxuICAgIHotaW5kZXg6IDM7XFxuICAgIG1pbi13aWR0aDogXCIsIFwiO1xcbiAgICBvdmVyZmxvdzogaGlkZGVuO1xcbiAgICB3aWR0aDogMTAwJTtcXG4gIFwiXSkpLCBtb2JpbGUgPyAnMzIwcHgnIDogJzU3NnB4Jyk7IH0sXHJcbiAgICBjb250ZXh0V3JhcHBlcjogY3NzKHRlbXBsYXRlT2JqZWN0XzQgfHwgKHRlbXBsYXRlT2JqZWN0XzQgPSBfX21ha2VUZW1wbGF0ZU9iamVjdChbXCJcXG4gICAgd2lkdGg6IDEwMCU7XFxuICAgIGhlaWdodDogMTAwJTtcXG4gICAgZGlzcGxheTogZmxleDtcXG4gICAganVzdGlmeS1jb250ZW50OiBzcGFjZS1iZXR3ZWVuO1xcbiAgICBhbGlnbi1pdGVtczogY2VudGVyO1xcbiAgXCJdLCBbXCJcXG4gICAgd2lkdGg6IDEwMCU7XFxuICAgIGhlaWdodDogMTAwJTtcXG4gICAgZGlzcGxheTogZmxleDtcXG4gICAganVzdGlmeS1jb250ZW50OiBzcGFjZS1iZXR3ZWVuO1xcbiAgICBhbGlnbi1pdGVtczogY2VudGVyO1xcbiAgXCJdKSkpLFxyXG4gICAgaW5uZXJDb250ZW50OiBmdW5jdGlvbiAobW9iaWxlKSB7IHJldHVybiBjc3ModGVtcGxhdGVPYmplY3RfNSB8fCAodGVtcGxhdGVPYmplY3RfNSA9IF9fbWFrZVRlbXBsYXRlT2JqZWN0KFtcIlxcbiAgICBwb3NpdGlvbjogYWJzb2x1dGU7XFxuICAgIHdpZHRoOiAxMDAlO1xcbiAgICBoZWlnaHQ6IDEwMCU7XFxuICAgIG92ZXJmbG93LXg6IGF1dG87XFxuICAgIHotaW5kZXg6IDM7XFxuICAgIHBhZGRpbmctYm90dG9tOiBcIiwgXCI7XFxuICBcIl0sIFtcIlxcbiAgICBwb3NpdGlvbjogYWJzb2x1dGU7XFxuICAgIHdpZHRoOiAxMDAlO1xcbiAgICBoZWlnaHQ6IDEwMCU7XFxuICAgIG92ZXJmbG93LXg6IGF1dG87XFxuICAgIHotaW5kZXg6IDM7XFxuICAgIHBhZGRpbmctYm90dG9tOiBcIiwgXCI7XFxuICBcIl0pKSwgbW9iaWxlICYmICc3MHB4Jyk7IH0sXHJcbiAgICBvdmVybGF5OiBmdW5jdGlvbiAoaW5uZXJXaWR0aCkgeyByZXR1cm4gY3NzKHRlbXBsYXRlT2JqZWN0XzYgfHwgKHRlbXBsYXRlT2JqZWN0XzYgPSBfX21ha2VUZW1wbGF0ZU9iamVjdChbXCJcXG4gICAgd2lkdGg6IDEwMCU7XFxuICAgIGhlaWdodDogXCIsIFwiO1xcbiAgICBwb3NpdGlvbjogYWJzb2x1dGU7XFxuICAgIGJvdHRvbTogMDtcXG4gICAgei1pbmRleDogNDtcXG4gIFwiXSwgW1wiXFxuICAgIHdpZHRoOiAxMDAlO1xcbiAgICBoZWlnaHQ6IFwiLCBcIjtcXG4gICAgcG9zaXRpb246IGFic29sdXRlO1xcbiAgICBib3R0b206IDA7XFxuICAgIHotaW5kZXg6IDQ7XFxuICBcIl0pKSwgaW5uZXJXaWR0aCA+IDU3NiA/ICcxMDAlJyA6ICdjYWxjKDEwMCUgLSA0NnB4KScpOyB9LFxyXG4gICAgZHJhZ0NvbnN0cmFpbnRzOiBmdW5jdGlvbiAobWVudVNpemUpIHsgcmV0dXJuIGNzcyh0ZW1wbGF0ZU9iamVjdF83IHx8ICh0ZW1wbGF0ZU9iamVjdF83ID0gX19tYWtlVGVtcGxhdGVPYmplY3QoW1wiXFxuICAgIHdpZHRoOiBcIiwgXCJweDtcXG4gICAgaGVpZ2h0OiAxMDAlO1xcbiAgICBkaXNwbGF5OiBmbGV4O1xcbiAgICBqdXN0aWZ5LWNvbnRlbnQ6IGZsZXgtZW5kO1xcbiAgICBmbGV4LWRpcmVjdGlvbjogY29sdW1uO1xcbiAgICBhbGlnbi1pdGVtczogY2VudGVyO1xcbiAgICBiYWNrZ3JvdW5kOiB0cmFuc3BhcmVudDtcXG4gICAgcG9zaXRpb246IGFic29sdXRlO1xcbiAgICBib3R0b206IDA7XFxuICAgIHotaW5kZXg6IDE7XFxuICBcIl0sIFtcIlxcbiAgICB3aWR0aDogXCIsIFwicHg7XFxuICAgIGhlaWdodDogMTAwJTtcXG4gICAgZGlzcGxheTogZmxleDtcXG4gICAganVzdGlmeS1jb250ZW50OiBmbGV4LWVuZDtcXG4gICAgZmxleC1kaXJlY3Rpb246IGNvbHVtbjtcXG4gICAgYWxpZ24taXRlbXM6IGNlbnRlcjtcXG4gICAgYmFja2dyb3VuZDogdHJhbnNwYXJlbnQ7XFxuICAgIHBvc2l0aW9uOiBhYnNvbHV0ZTtcXG4gICAgYm90dG9tOiAwO1xcbiAgICB6LWluZGV4OiAxO1xcbiAgXCJdKSksIG1lbnVTaXplKTsgfSxcclxufTtcclxudmFyIHRlbXBsYXRlT2JqZWN0XzEsIHRlbXBsYXRlT2JqZWN0XzIsIHRlbXBsYXRlT2JqZWN0XzMsIHRlbXBsYXRlT2JqZWN0XzQsIHRlbXBsYXRlT2JqZWN0XzUsIHRlbXBsYXRlT2JqZWN0XzYsIHRlbXBsYXRlT2JqZWN0Xzc7XHJcbi8vIyBzb3VyY2VNYXBwaW5nVVJMPXN0eWxlcy5qcy5tYXAiXX0= */";
var styles$e = {
  chromeContainer: function (innerHeight) {
    return /*#__PURE__*/css(templateObject_1$f || (templateObject_1$f = __makeTemplateObject(["\n    position: fixed;\n    width: 100%;\n    height: ", "px;\n    overflow: hidden;\n    box-shadow: 0 3px 6px 0 #ccc;\n    z-index: 0;\n  " + _emotionSourceMap$f], ["\n    position: fixed;\n    width: 100%;\n    height: ", "px;\n    overflow: hidden;\n    box-shadow: 0 3px 6px 0 #ccc;\n    z-index: 0;\n  " + _emotionSourceMap$f])), innerHeight);
  },
  revealingContent: /*#__PURE__*/css(templateObject_2$6 || (templateObject_2$6 = __makeTemplateObject(["\n    width: 100%;\n    height: 100%;\n    position: absolute;\n    z-index: 2;\n  ;label:revealingContent;" + _emotionSourceMap2$6], ["\n    width: 100%;\n    height: 100%;\n    position: absolute;\n    z-index: 2;\n  ;label:revealingContent;" + _emotionSourceMap2$6]))),
  content: function (mobile) {
    return /*#__PURE__*/css(templateObject_3$4 || (templateObject_3$4 = __makeTemplateObject(["\n    height: 100%;\n    position: absolute;\n    z-index: 3;\n    min-width: ", ";\n    overflow: hidden;\n    width: 100%;\n  " + _emotionSourceMap3$4], ["\n    height: 100%;\n    position: absolute;\n    z-index: 3;\n    min-width: ", ";\n    overflow: hidden;\n    width: 100%;\n  " + _emotionSourceMap3$4])), mobile ? '320px' : '576px');
  },
  contextWrapper: /*#__PURE__*/css(templateObject_4$2 || (templateObject_4$2 = __makeTemplateObject(["\n    width: 100%;\n    height: 100%;\n    display: flex;\n    justify-content: space-between;\n    align-items: center;\n  ;label:contextWrapper;" + _emotionSourceMap4$2], ["\n    width: 100%;\n    height: 100%;\n    display: flex;\n    justify-content: space-between;\n    align-items: center;\n  ;label:contextWrapper;" + _emotionSourceMap4$2]))),
  innerContent: function (mobile) {
    return /*#__PURE__*/css(templateObject_5$1 || (templateObject_5$1 = __makeTemplateObject(["\n    position: absolute;\n    width: 100%;\n    height: 100%;\n    overflow-x: auto;\n    z-index: 3;\n    padding-bottom: ", ";\n  " + _emotionSourceMap5$1], ["\n    position: absolute;\n    width: 100%;\n    height: 100%;\n    overflow-x: auto;\n    z-index: 3;\n    padding-bottom: ", ";\n  " + _emotionSourceMap5$1])), mobile && '70px');
  },
  overlay: function (innerWidth) {
    return /*#__PURE__*/css(templateObject_6$1 || (templateObject_6$1 = __makeTemplateObject(["\n    width: 100%;\n    height: ", ";\n    position: absolute;\n    bottom: 0;\n    z-index: 4;\n  " + _emotionSourceMap6$1], ["\n    width: 100%;\n    height: ", ";\n    position: absolute;\n    bottom: 0;\n    z-index: 4;\n  " + _emotionSourceMap6$1])), innerWidth > 576 ? '100%' : 'calc(100% - 46px)');
  },
  dragConstraints: function (menuSize) {
    return /*#__PURE__*/css(templateObject_7 || (templateObject_7 = __makeTemplateObject(["\n    width: ", "px;\n    height: 100%;\n    display: flex;\n    justify-content: flex-end;\n    flex-direction: column;\n    align-items: center;\n    background: transparent;\n    position: absolute;\n    bottom: 0;\n    z-index: 1;\n  " + _emotionSourceMap7], ["\n    width: ", "px;\n    height: 100%;\n    display: flex;\n    justify-content: flex-end;\n    flex-direction: column;\n    align-items: center;\n    background: transparent;\n    position: absolute;\n    bottom: 0;\n    z-index: 1;\n  " + _emotionSourceMap7])), menuSize);
  }
};
var templateObject_1$f, templateObject_2$6, templateObject_3$4, templateObject_4$2, templateObject_5$1, templateObject_6$1, templateObject_7;

var Content = function (_a) {
  var children = _a.children,
    x = _a.x,
    isMobile = _a.isMobile,
    isOpen = _a.isOpen,
    onDragEnd = _a.onDragEnd,
    menuSize = _a.menuSize,
    logo = _a.logo,
    isFullScreen = _a.isFullScreen;
  var innerWidth = useWindowSize().width;
  // transform inputs
  var xInput = [0, menuSize];
  // scale outputs
  var mobileScaleOutput = [1, 0.75];
  var desktopScaleOutput = [1, 1];
  // radius outputs
  var borderRadiusOutput = [0, 24];
  var desktopBorderRadiusOutput = [0, 0];
  // offset x for when menu scales down it also shifts right, this
  var xOffsetOutput = [0, -(innerWidth * 0.25) / 2];
  var xOffset = useTransform(x, xInput, xOffsetOutput);
  var reverseOffset = useTransform(x, xInput, [0, 0]);
  // Allow scale/reverse scale to transform at exact timing that x is moving
  var scale = useTransform(x, xInput, mobileScaleOutput);
  var reverseScale = useTransform(x, xInput, desktopScaleOutput);
  // Allow radius to be applied/removed at exact timing that x is moving
  var borderRadius = useTransform(x, xInput, borderRadiusOutput);
  var noRadius = useTransform(x, xInput, desktopBorderRadiusOutput);
  var variants = {
    open: {
      x: menuSize + 50,
      width: "calc(100% - " + (menuSize + 50) + "px)",
      transition: {
        springTransition: springTransition,
        delayChildren: 0.5
      }
    },
    closed: {
      x: 50,
      width: 'calc(100% - 50px)',
      transition: {
        springTransition: springTransition
      }
    },
    mobileOpen: {
      width: '100%',
      x: menuSize,
      transition: {
        springTransition: springTransition,
        delayChildren: 0.5
      }
    },
    mobileClosed: {
      width: 'calc(100% - 0px)',
      x: 0,
      transition: {
        springTransition: springTransition
      }
    }
  };
  return jsx(Section, {
    variants: variants,
    initial: isFullScreen ? 'mobileClosed' : isMobile ? isOpen ? 'mobileOpen' : 'mobileClosed' : isOpen ? 'open' : 'closed',
    animate: isFullScreen ? 'mobileClosed' : isMobile ? isOpen ? 'mobileOpen' : 'mobileClosed' : isOpen ? 'open' : 'closed',
    style: {
      x: isMobile ? x : 0,
      translateX: isMobile ? xOffset : reverseOffset,
      scale: isMobile ? scale : reverseScale,
      borderBottomLeftRadius: isMobile ? borderRadius : noRadius,
      borderTopLeftRadius: isMobile ? borderRadius : noRadius
    },
    drag: isMobile && isOpen && 'x',
    dragConstraints: {
      left: 0,
      right: menuSize
    },
    onDragEnd: onDragEnd,
    boxShadow: isOpen ? 'mediumLight' : undefined,
    backgroundColor: "background",
    css: styles$e.content(isMobile)
  }, !isFullScreen && isMobile && jsx(ContextBar, null, jsx("div", {
    css: styles$e.contextWrapper
  }, jsx(Span, {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
    onClick: function () {
      return onDragEnd();
    }
  }, jsx(Icon, {
    name: isOpen ? 'close' : 'hamburger',
    size: "small",
    primaryColor: "default"
  })), jsx(Icon, {
    name: logo,
    primaryColor: "primaryBrand",
    size: "medium"
  }), jsx(Span, {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer"
  }, jsx(Icon, {
    name: "information",
    primaryColor: "default",
    size: "small"
  })))), isOpen && innerWidth < 876 && jsx(Section, {
    css: styles$e.overlay(innerWidth),
    backgroundColor: innerWidth > 576 ? 'overlay' : 'transparent'
  }), jsx(Div, {
    css: styles$e.innerContent(isMobile)
  }, children));
};

var RevealingContent = function (_a) {
  var children = _a.children,
    zone = _a.zone,
    width = _a.width,
    height = _a.height,
    isMobile = _a.isMobile;
  return jsx(Div, {
    display: 'flex',
    outset: zone === 'left' && !isMobile ? {
      left: '50px'
    } : 'x0',
    justifyContent: zone === 'left' ? 'flex-start' : 'flex-end',
    alignItems: zone === 'top' ? 'flex-start' : 'flex-end',
    css: styles$e.revealingContent,
    backgroundColor: isMobile ? {
      swatch: 'primaryBrand',
      shade: 'base'
    } : 'foreground'
  }, jsx(Div, {
    width: width,
    height: height
  }, children));
};

var querySizes = {
  small: 576,
  medium: 768,
  large: 992,
  xLarge: 1200
};

/* DO NOT USE THIS HOOK - Will be depricated after implementing changes to QDSChrome, please use useDeviceSize hook instead */
// TODO Depricate this hook
var currentDevice = function (currentWidth) {
  if (currentWidth < querySizes.small) {
    return 'xSmall';
  } else if (currentWidth < querySizes.medium) {
    return 'small';
  } else if (currentWidth < querySizes.large) {
    return 'medium';
  } else if (currentWidth < querySizes.xLarge) {
    return 'large';
  } else if (currentWidth > querySizes.xLarge) {
    return 'xLarge';
  } else {
    return "Couldn't find device";
  }
};
var useCurrentDevice = function (innerWidth) {
  var device = currentDevice(innerWidth);
  return device;
};

var useEvent = function (event, handler) {
  useEffect(function () {
    // initiate the event handler
    window.addEventListener(event, handler);
    // this will clean up the event every time the component is re-rendered
    return function cleanup() {
      window.removeEventListener(event, handler);
    };
  }, [event, handler]);
};

var SubNavigationHeading = function (_a) {
  var title = _a.title,
    isActive = _a.isActive,
    icon = _a.icon,
    isMobile = _a.isMobile;
  return jsx(Div, {
    display: 'flex',
    justifyContent: "center",
    alignItems: "center",
    outset: {
      bottom: 'x1'
    }
  }, jsx(Span, {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    inset: {
      right: 'x1'
    },
    height: "100%"
  }, jsx(Icon, {
    size: "small",
    primaryColor: isMobile ? 'inverseBodyText' : 'primaryBodyText',
    name: icon
  })), jsx(Text, {
    type: "footnote",
    color: isMobile ? 'inverseHeading' : 'primaryHeading',
    content: title
  }), isActive && jsx(Span, {
    outset: {
      left: 'x1'
    },
    width: "6px",
    height: "6px",
    backgroundColor: isMobile ? 'foreground' : {
      swatch: 'primaryBrand',
      shade: 'base'
    },
    borderRadius: "circle",
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  }));
};

var _emotionSourceMap2$5 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFJd0IiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgX19tYWtlVGVtcGxhdGVPYmplY3QgfSBmcm9tIFwidHNsaWJcIjtcclxuaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vY29yZSc7XHJcbmV4cG9ydCBkZWZhdWx0IHtcclxuICAgIHN1Yk5hdkNvbnRhaW5lcjogZnVuY3Rpb24gKGlzTW9iaWxlKSB7IHJldHVybiBjc3ModGVtcGxhdGVPYmplY3RfMSB8fCAodGVtcGxhdGVPYmplY3RfMSA9IF9fbWFrZVRlbXBsYXRlT2JqZWN0KFtcIlxcbiAgICB3aWR0aDogMTAwJTtcXG4gICAgaGVpZ2h0OiBjYWxjKDEwMHZoIC0gMTI4cHgpO1xcbiAgICBtYXJnaW4tYm90dG9tOiAzMnB4O1xcbiAgICBvdmVyZmxvdy15OiBhdXRvO1xcbiAgICBwYWRkaW5nLWJvdHRvbTogXCIsIFwiO1xcbiAgXCJdLCBbXCJcXG4gICAgd2lkdGg6IDEwMCU7XFxuICAgIGhlaWdodDogY2FsYygxMDB2aCAtIDEyOHB4KTtcXG4gICAgbWFyZ2luLWJvdHRvbTogMzJweDtcXG4gICAgb3ZlcmZsb3cteTogYXV0bztcXG4gICAgcGFkZGluZy1ib3R0b206IFwiLCBcIjtcXG4gIFwiXSkpLCBpc01vYmlsZSAmJiAnNjBweCcpOyB9LFxyXG4gICAgYWNjb3JkaW9uQ29udGFpbmVyOiBjc3ModGVtcGxhdGVPYmplY3RfMiB8fCAodGVtcGxhdGVPYmplY3RfMiA9IF9fbWFrZVRlbXBsYXRlT2JqZWN0KFtcIlwiXSwgW1wiXCJdKSkpLFxyXG59O1xyXG52YXIgdGVtcGxhdGVPYmplY3RfMSwgdGVtcGxhdGVPYmplY3RfMjtcclxuLy8jIHNvdXJjZU1hcHBpbmdVUkw9c3R5bGVzLmpzLm1hcCJdfQ== */";
var _emotionSourceMap$e = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFHa0QiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgX19tYWtlVGVtcGxhdGVPYmplY3QgfSBmcm9tIFwidHNsaWJcIjtcclxuaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vY29yZSc7XHJcbmV4cG9ydCBkZWZhdWx0IHtcclxuICAgIHN1Yk5hdkNvbnRhaW5lcjogZnVuY3Rpb24gKGlzTW9iaWxlKSB7IHJldHVybiBjc3ModGVtcGxhdGVPYmplY3RfMSB8fCAodGVtcGxhdGVPYmplY3RfMSA9IF9fbWFrZVRlbXBsYXRlT2JqZWN0KFtcIlxcbiAgICB3aWR0aDogMTAwJTtcXG4gICAgaGVpZ2h0OiBjYWxjKDEwMHZoIC0gMTI4cHgpO1xcbiAgICBtYXJnaW4tYm90dG9tOiAzMnB4O1xcbiAgICBvdmVyZmxvdy15OiBhdXRvO1xcbiAgICBwYWRkaW5nLWJvdHRvbTogXCIsIFwiO1xcbiAgXCJdLCBbXCJcXG4gICAgd2lkdGg6IDEwMCU7XFxuICAgIGhlaWdodDogY2FsYygxMDB2aCAtIDEyOHB4KTtcXG4gICAgbWFyZ2luLWJvdHRvbTogMzJweDtcXG4gICAgb3ZlcmZsb3cteTogYXV0bztcXG4gICAgcGFkZGluZy1ib3R0b206IFwiLCBcIjtcXG4gIFwiXSkpLCBpc01vYmlsZSAmJiAnNjBweCcpOyB9LFxyXG4gICAgYWNjb3JkaW9uQ29udGFpbmVyOiBjc3ModGVtcGxhdGVPYmplY3RfMiB8fCAodGVtcGxhdGVPYmplY3RfMiA9IF9fbWFrZVRlbXBsYXRlT2JqZWN0KFtcIlwiXSwgW1wiXCJdKSkpLFxyXG59O1xyXG52YXIgdGVtcGxhdGVPYmplY3RfMSwgdGVtcGxhdGVPYmplY3RfMjtcclxuLy8jIHNvdXJjZU1hcHBpbmdVUkw9c3R5bGVzLmpzLm1hcCJdfQ== */";
var styles$d = {
  subNavContainer: function (isMobile) {
    return /*#__PURE__*/css(templateObject_1$e || (templateObject_1$e = __makeTemplateObject(["\n    width: 100%;\n    height: calc(100vh - 128px);\n    margin-bottom: 32px;\n    overflow-y: auto;\n    padding-bottom: ", ";\n  " + _emotionSourceMap$e], ["\n    width: 100%;\n    height: calc(100vh - 128px);\n    margin-bottom: 32px;\n    overflow-y: auto;\n    padding-bottom: ", ";\n  " + _emotionSourceMap$e])), isMobile && '60px');
  },
  accordionContainer: /*#__PURE__*/css(templateObject_2$5 || (templateObject_2$5 = __makeTemplateObject([";label:accordionContainer;" + _emotionSourceMap2$5], [";label:accordionContainer;" + _emotionSourceMap2$5])))
};
var templateObject_1$e, templateObject_2$5;

var listVariants = {
  open: {
    transition: {
      staggerChildren: 0.03
    }
  },
  closed: {
    transition: {
      staggerChildren: 0.01,
      staggerDirection: -1
    }
  }
};
var linkVariants = {
  open: {
    scale: 1,
    y: 0,
    opacity: 1,
    transition: {
      y: {
        stiffness: 1000,
        velocity: -100
      }
    }
  },
  closed: {
    scale: 0.9,
    y: 64,
    opacity: 0,
    transition: {
      y: {
        stiffness: 1000
      }
    }
  }
};
var SubNavigation = function (_a) {
  var sections = _a.sections,
    isMobile = _a.isMobile,
    setIsMobileExpanded = _a.setIsMobileExpanded;
  var activeKeys = sections.map(function (section) {
    return sections.indexOf(section);
  });
  return jsx("aside", {
    css: styles$d.subNavContainer(isMobile)
  }, jsx("div", {
    css: styles$d.accordionContainer
  }, sections.map(function (section, index) {
    return jsx(Accordion, {
      key: index,
      defaultActiveKeys: activeKeys,
      isExclusive: false,
      sections: [{
        key: index,
        heading: jsx(SubNavigationHeading, {
          icon: section.icon,
          title: section.title,
          isActive: section.isActive,
          isMobile: isMobile
        }),
        body: jsx(List, {
          alignment: "left",
          orientation: "vertical"
        }, jsx(Div, {
          animate: 'open',
          variants: listVariants,
          initial: 'closed'
        }, section.links.map(function (link, index) {
          return jsx(ListItem, {
            variants: linkVariants,
            inset: {
              vertical: 'x1_5',
              horizontal: 'x3'
            },
            orientation: "vertical",
            key: index
          }, jsx(Anchor, {
            cursor: "pointer",
            size: "small",
            color: link.isActive ? isMobile ? 'inverseBody' : 'primaryBrand' : isMobile ? 'inverseBody' : 'primaryBody',
            title: link.title,
            onClick: function () {
              link.onClick();
              setIsMobileExpanded(false);
            }
          }));
        })))
      }]
    });
  })));
};

var useInnerHeight = function () {
  var _a = React.useState(window.innerHeight),
    innerHeight = _a[0],
    setInnerHeight = _a[1];
  var getDevice = React.useCallback(function () {
    var innerHeight = window.innerHeight;
    setInnerHeight(innerHeight);
  }, []);
  useEvent('resize', getDevice);
  return innerHeight;
};

var QDSChrome = function (_a) {
  var children = _a.children,
    logo = _a.logo,
    navSections = _a.navSections,
    accountSections = _a.accountSections,
    user = _a.user,
    isExpanded = _a.isExpanded,
    setIsExpanded = _a.setIsExpanded,
    openMenuSize = _a.openMenuSize,
    isOnAccountRoute = _a.isOnAccountRoute,
    accountRouteOnClick = _a.accountRouteOnClick,
    quickLinks = _a.quickLinks,
    isFullScreen = _a.isFullScreen;
  // TODO Refactor getCurrentDevice hook to work with useDeviceSize, caused state issues with this component - Will need to deprecate getCurrentDevice after fixing
  var currentWidth = window.innerWidth;
  var _b = useState(false),
    shouldSnapToMobile = _b[0],
    setSnapToMobile = _b[1];
  var _c = useState(false),
    isMenuOpen = _c[0],
    setIsMenuOpen = _c[1];
  var _d = useState(useCurrentDevice(currentWidth)),
    deviceSize = _d[0],
    setDeviceSize = _d[1];
  var _e = useState(false),
    isSheetOpen = _e[0],
    setSheetOpen = _e[1];
  var handleX = useMotionValue(0);
  var innerHeight = useInnerHeight();
  var constraintsRef = useRef(null);
  var handleDragEnd = function () {
    setSnapToMobile(!shouldSnapToMobile);
  };
  var menuSize = openMenuSize ? openMenuSize : 250;
  var isMobile = deviceSize === 'xSmall' ? true : false;
  var getDevice = React.useCallback(function () {
    var currentDevice = useCurrentDevice(window.innerWidth);
    setDeviceSize(currentDevice);
    if (!isMobile) {
      setSnapToMobile(false);
    } else {
      handleX.set(0);
      setIsMenuOpen(false);
    }
  }, [handleX, isMobile]);
  useEvent('resize', getDevice);
  useEffect(function () {
    if (window.innerWidth < 876 && isExpanded) {
      setIsExpanded(false);
    }
  }, [isExpanded, setIsExpanded]);
  return jsx("main", {
    css: styles$e.chromeContainer(innerHeight)
  }, isFullScreen ? jsx(React.Fragment, null) : isMobile ? jsx(NavigationPill, {
    isSheetOpen: isSheetOpen,
    setSheetIsOpen: setSheetOpen,
    accountRouteOnClick: accountRouteOnClick,
    circleSize: 70,
    height: 90,
    quickLinks: quickLinks,
    links: navSections,
    user: user,
    setIsExpanded: setSnapToMobile
  }) : jsx(Navigation, {
    setIsExpanded: setIsExpanded,
    isExpanded: isExpanded,
    setMenuOpen: setIsMenuOpen,
    isMenuOpen: isMenuOpen,
    user: user,
    logo: logo,
    navLinks: navSections
  }), !isFullScreen && isMobile && jsx(DragBar, {
    x: handleX,
    onDragEnd: function () {
      return handleDragEnd();
    },
    ref: constraintsRef
  }), jsx(Content, {
    logo: logo,
    isMobile: isMobile,
    x: handleX,
    menuSize: menuSize,
    isOpen: isFullScreen ? false : isMobile ? shouldSnapToMobile : isExpanded,
    onDragEnd: handleDragEnd,
    isFullScreen: isFullScreen
  }, children), !isFullScreen && jsx(RevealingContent, {
    isMobile: isMobile,
    zone: 'left',
    height: '100%',
    width: menuSize + "px"
  }, jsx(Flex, {
    width: '100%',
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    inset: "x2"
  }, isOnAccountRoute ? jsx(FlexItem, {
    flexDirection: "column"
  }, jsx(Text, {
    content: "My Account",
    inset: {
      top: 'x1',
      right: 'x0',
      bottom: 'x5',
      left: 'x_5'
    },
    type: "largeBody",
    color: isMobile ? 'inverseBody' : 'primaryHeading'
  }), accountSections.map(function (section, index) {
    return jsx(List, {
      key: index,
      orientation: "vertical",
      alignment: "left"
    }, section.map(function (link, index) {
      return jsx(Anchor, {
        inset: "x_5",
        cursor: "pointer",
        key: index,
        onClick: link.onClick,
        title: link.title,
        color: isMobile ? 'inverseBody' : 'primaryHeading'
      });
    }));
  })) : navSections.map(function (section, index) {
    return section.isActive && jsx(FlexItem, {
      key: index,
      flexDirection: "column"
    }, jsx(Text, {
      inset: {
        top: 'x1',
        right: 'x0',
        bottom: 'x5',
        left: 'x_5'
      },
      type: "largeBody",
      color: isMobile ? 'inverseBody' : 'primaryHeading'
    }, section.title), jsx(SubNavigation, {
      setIsMobileExpanded: setSnapToMobile,
      isMobile: isMobile,
      sections: section.subNavigation
    }));
  }))), jsx(motion.div, {
    css: styles$e.dragConstraints(menuSize),
    ref: constraintsRef
  }), jsx(AccountMenu, {
    user: user,
    setIsOpen: function () {
      return setIsMenuOpen(!isMenuOpen);
    },
    isOpen: isMenuOpen,
    navOpen: false,
    sections: accountSections
  }), jsx(Sheet, {
    isOpen: isSheetOpen,
    setIsOpen: setSheetOpen,
    draggable: true,
    layer: 3,
    outsideClickBypassId: "circleMenu"
  }, jsx(List, {
    width: "100%",
    orientation: "vertical",
    alignment: "left",
    inset: {
      bottom: 'x8'
    }
  }, jsx(Text, {
    type: "footnote",
    content: "Quick Actions"
  }), quickLinks.map(function (link, index) {
    return jsx(ListItem, {
      key: index,
      orientation: "vertical",
      inset: "x1"
    }, jsx(IconLink, {
      onClick: function () {
        link.onClick();
        setIsExpanded(false);
        setSheetOpen(false);
      },
      icon: link.icon,
      iconOrientation: link.iconOrientation ? link.iconOrientation : 'left',
      isActive: link.isActive,
      fill: "default",
      title: link.title
    }));
  }))));
};

var _emotionSourceMap3$3 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFLbUQiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgX19tYWtlVGVtcGxhdGVPYmplY3QgfSBmcm9tIFwidHNsaWJcIjtcclxuaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vY29yZSc7XHJcbmV4cG9ydCBkZWZhdWx0IHtcclxuICAgIHBpbkZvcm06IGZ1bmN0aW9uIChpbnB1dEVudGl0eSkgeyByZXR1cm4gY3NzKHRlbXBsYXRlT2JqZWN0XzEgfHwgKHRlbXBsYXRlT2JqZWN0XzEgPSBfX21ha2VUZW1wbGF0ZU9iamVjdChbXCJcXG4gICAgZGlzcGxheTogZmxleDtcXG4gICAganVzdGlmeS1jb250ZW50OiBjZW50ZXI7XFxuICAgIGFsaWduLWl0ZW1zOiBjZW50ZXI7XFxuICAgIGZsZXgtZGlyZWN0aW9uOiBjb2x1bW47XFxuICAgIGlucHV0W3R5cGU9J3RlbCddOjotd2Via2l0LWlubmVyLXNwaW4tYnV0dG9uLFxcbiAgICBpbnB1dFt0eXBlPSd0ZWwnXTo6LXdlYmtpdC1vdXRlci1zcGluLWJ1dHRvbiB7XFxuICAgICAgLXdlYmtpdC1hcHBlYXJhbmNlOiBub25lO1xcbiAgICAgIG1hcmdpbjogMDtcXG4gICAgfVxcbiAgICBmb250LWZhbWlseTogXCIsIFwiO1xcbiAgXCJdLCBbXCJcXG4gICAgZGlzcGxheTogZmxleDtcXG4gICAganVzdGlmeS1jb250ZW50OiBjZW50ZXI7XFxuICAgIGFsaWduLWl0ZW1zOiBjZW50ZXI7XFxuICAgIGZsZXgtZGlyZWN0aW9uOiBjb2x1bW47XFxuICAgIGlucHV0W3R5cGU9J3RlbCddOjotd2Via2l0LWlubmVyLXNwaW4tYnV0dG9uLFxcbiAgICBpbnB1dFt0eXBlPSd0ZWwnXTo6LXdlYmtpdC1vdXRlci1zcGluLWJ1dHRvbiB7XFxuICAgICAgLXdlYmtpdC1hcHBlYXJhbmNlOiBub25lO1xcbiAgICAgIG1hcmdpbjogMDtcXG4gICAgfVxcbiAgICBmb250LWZhbWlseTogXCIsIFwiO1xcbiAgXCJdKSksIGlucHV0RW50aXR5LmZvbnRGYW1pbHkpOyB9LFxyXG4gICAgcGluQm94Q29udGFpbmVyOiBjc3ModGVtcGxhdGVPYmplY3RfMiB8fCAodGVtcGxhdGVPYmplY3RfMiA9IF9fbWFrZVRlbXBsYXRlT2JqZWN0KFtcIlxcbiAgICB3aWR0aDogMTAwJTtcXG4gICAgZGlzcGxheTogZmxleDtcXG4gIFwiXSwgW1wiXFxuICAgIHdpZHRoOiAxMDAlO1xcbiAgICBkaXNwbGF5OiBmbGV4O1xcbiAgXCJdKSkpLFxyXG4gICAgcGluQm94OiBmdW5jdGlvbiAoaW5wdXRFbnRpdHksIGZvY3VzKSB7IHJldHVybiBjc3ModGVtcGxhdGVPYmplY3RfMyB8fCAodGVtcGxhdGVPYmplY3RfMyA9IF9fbWFrZVRlbXBsYXRlT2JqZWN0KFtcIlxcbiAgICBmb250LXNpemU6IGluaGVyaXQ7XFxuICAgIGZvbnQtZmFtaWx5OiBpbmhlcml0O1xcbiAgICBoZWlnaHQ6IDUwcHg7XFxuICAgIHdpZHRoOiA0MHB4O1xcbiAgICB0ZXh0LWFsaWduOiBjZW50ZXI7XFxuICAgIG1hcmdpbjogMHB4IDhweDtcXG4gICAgYmFja2dyb3VuZDogXCIsIFwiO1xcbiAgICBib3JkZXItcmFkaXVzOiBcIiwgXCI7XFxuICAgIGFwcGVhcmFuY2U6IG5vbmU7XFxuICAgIG91dGxpbmU6IG5vbmU7XFxuICAgIGJvcmRlcjogXCIsIFwiO1xcbiAgXCJdLCBbXCJcXG4gICAgZm9udC1zaXplOiBpbmhlcml0O1xcbiAgICBmb250LWZhbWlseTogaW5oZXJpdDtcXG4gICAgaGVpZ2h0OiA1MHB4O1xcbiAgICB3aWR0aDogNDBweDtcXG4gICAgdGV4dC1hbGlnbjogY2VudGVyO1xcbiAgICBtYXJnaW46IDBweCA4cHg7XFxuICAgIGJhY2tncm91bmQ6IFwiLCBcIjtcXG4gICAgYm9yZGVyLXJhZGl1czogXCIsIFwiO1xcbiAgICBhcHBlYXJhbmNlOiBub25lO1xcbiAgICBvdXRsaW5lOiBub25lO1xcbiAgICBib3JkZXI6IFwiLFxyXG4gICAgICAgIFwiO1xcbiAgXCJdKSksIGlucHV0RW50aXR5LmJhY2tncm91bmRDb2xvciwgaW5wdXRFbnRpdHkudGV4dC5ib3JkZXJSYWRpdXMsIGZvY3VzXHJcbiAgICAgICAgPyBpbnB1dEVudGl0eS50ZXh0LmJvcmRlcldpZHRoICsgXCIgc29saWQgXCIgKyBpbnB1dEVudGl0eS50ZXh0LmRlZmF1bHRGb2N1c0NvbG9yXHJcbiAgICAgICAgOiAnbm9uZScpOyB9LFxyXG59O1xyXG52YXIgdGVtcGxhdGVPYmplY3RfMSwgdGVtcGxhdGVPYmplY3RfMiwgdGVtcGxhdGVPYmplY3RfMztcclxuLy8jIHNvdXJjZU1hcHBpbmdVUkw9c3R5bGVzLmpzLm1hcCJdfQ== */";
var _emotionSourceMap2$4 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap$d = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var styles$c = {
  pinForm: function (inputEntity) {
    return /*#__PURE__*/css(templateObject_1$d || (templateObject_1$d = __makeTemplateObject(["\n    display: flex;\n    justify-content: center;\n    align-items: center;\n    flex-direction: column;\n    input[type='tel']::-webkit-inner-spin-button,\n    input[type='tel']::-webkit-outer-spin-button {\n      -webkit-appearance: none;\n      margin: 0;\n    }\n    font-family: ", ";\n  " + _emotionSourceMap$d], ["\n    display: flex;\n    justify-content: center;\n    align-items: center;\n    flex-direction: column;\n    input[type='tel']::-webkit-inner-spin-button,\n    input[type='tel']::-webkit-outer-spin-button {\n      -webkit-appearance: none;\n      margin: 0;\n    }\n    font-family: ", ";\n  " + _emotionSourceMap$d])), inputEntity.fontFamily);
  },
  pinBoxContainer: /*#__PURE__*/css(templateObject_2$4 || (templateObject_2$4 = __makeTemplateObject(["\n    width: 100%;\n    display: flex;\n  ;label:pinBoxContainer;" + _emotionSourceMap2$4], ["\n    width: 100%;\n    display: flex;\n  ;label:pinBoxContainer;" + _emotionSourceMap2$4]))),
  pinBox: function (inputEntity, focus) {
    return /*#__PURE__*/css(templateObject_3$3 || (templateObject_3$3 = __makeTemplateObject(["\n    font-size: inherit;\n    font-family: inherit;\n    height: 50px;\n    width: 40px;\n    text-align: center;\n    margin: 0px 8px;\n    background: ", ";\n    border-radius: ", ";\n    appearance: none;\n    outline: none;\n    border: ", ";\n  " + _emotionSourceMap3$3], ["\n    font-size: inherit;\n    font-family: inherit;\n    height: 50px;\n    width: 40px;\n    text-align: center;\n    margin: 0px 8px;\n    background: ", ";\n    border-radius: ", ";\n    appearance: none;\n    outline: none;\n    border: ", ";\n  " + _emotionSourceMap3$3])), inputEntity.backgroundColor, inputEntity.text.borderRadius, focus ? inputEntity.text.borderWidth + " solid " + inputEntity.text.defaultFocusColor : 'none');
  }
};
var templateObject_1$d, templateObject_2$4, templateObject_3$3;

var PinBox = function (props) {
  var initialValue = props.initialValue,
    onChange = props.onChange,
    onBackspace = props.onBackspace,
    isSecret = props.isSecret,
    isFocused = props.isFocused;
  var ref = React.useRef(null);
  var _a = useState(initialValue || ''),
    value = _a[0],
    setValue = _a[1];
  var _b = useState(false),
    isFocus = _b[0],
    setFocus = _b[1];
  var inputs = useEntities().inputs;
  var onKeyDown = function (e) {
    if (e.keyCode === 8 && (!value || !value.length)) {
      onBackspace();
    }
  };
  var handleOnChange = function (e) {
    var inputValue = validate(e.target.value);
    if (value === inputValue) return;
    if (value.length < 2) {
      setValue(inputValue);
      onChange(inputValue);
    }
  };
  var onBlur = function () {
    setFocus(false);
  };
  var onFocus = function (e) {
    e.target.select();
    setFocus(true);
  };
  var validate = function (value) {
    var numCode = value.charCodeAt(0);
    var isInteger = numCode >= '0'.charCodeAt(0) && numCode <= '9'.charCodeAt(0);
    return isInteger ? value : '';
  };
  React.useLayoutEffect(function () {
    if (isFocused && ref.current) {
      ref.current.focus();
    }
  }, [isFocused]);
  return jsx("input", {
    ref: ref,
    name: "one-time-code",
    type: isSecret ? 'password' : 'tel',
    maxLength: 1,
    css: styles$c.pinBox(inputs, isFocus),
    onChange: handleOnChange,
    onKeyDown: onKeyDown,
    onFocus: onFocus,
    onBlur: onBlur,
    autoFocus: isFocused,
    placeholder: value,
    value: value,
    pattern: '[0-9]*'
  });
};

var PinInput = function (_a) {
  var length = _a.length,
    onChange = _a.onChange,
    hasFocus = _a.hasFocus,
    onComplete = _a.onComplete,
    isSecret = _a.isSecret,
    message = _a.message;
  var _b = React.useState(hasFocus ? 0 : undefined),
    focusedIndex = _b[0],
    setFocusedIndex = _b[1];
  var _c = useState(Array(length).fill('')),
    values = _c[0],
    setValues = _c[1];
  var inputs = useEntities().inputs;
  var handleOnChange = function (value, index) {
    if (value.length === 1 && index < length - 1) {
      setFocusedIndex(index + 1);
    }
    var updatedValues = __spreadArrays(values.slice(0, index), [value], values.slice(index + 1, values.length));
    var pin = updatedValues.join('');
    onChange(pin);
    if (pin.length === length && onComplete) {
      onComplete(pin);
    }
    setValues(updatedValues);
  };
  var onBackspace = function (index) {
    if (index > 0) {
      setFocusedIndex(index - 1);
    }
  };
  return jsx("div", {
    css: styles$c.pinForm(inputs)
  }, jsx("div", {
    css: styles$c.pinBoxContainer
  }, values.map(function (value, index) {
    return jsx(PinBox, {
      initialValue: value,
      onChange: function (value) {
        return handleOnChange(value, index);
      },
      onBackspace: function () {
        return onBackspace(index);
      },
      key: index,
      isSecret: isSecret,
      isFocused: focusedIndex === index
    });
  })), message && jsx(Flex, {
    width: "100%"
  }, jsx(Text, {
    inset: {
      left: 'x1',
      top: 'x_5'
    },
    content: message.content,
    type: "caption",
    color: message.type
  })));
};

var _emotionSourceMap2$3 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFJeUIiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgX19tYWtlVGVtcGxhdGVPYmplY3QgfSBmcm9tIFwidHNsaWJcIjtcclxuaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vY29yZSc7XHJcbmV4cG9ydCBkZWZhdWx0IHtcclxuICAgIHBob25lSW5wdXRXcmFwcGVyOiBjc3ModGVtcGxhdGVPYmplY3RfMSB8fCAodGVtcGxhdGVPYmplY3RfMSA9IF9fbWFrZVRlbXBsYXRlT2JqZWN0KFtcIlxcbiAgICB3aWR0aDogMTAwJTtcXG4gICAgZGlzcGxheTogZmxleDtcXG4gICAgZmxleC1kaXJlY3Rpb246IGNvbHVtbjtcXG4gIFwiXSwgW1wiXFxuICAgIHdpZHRoOiAxMDAlO1xcbiAgICBkaXNwbGF5OiBmbGV4O1xcbiAgICBmbGV4LWRpcmVjdGlvbjogY29sdW1uO1xcbiAgXCJdKSkpLFxyXG4gICAgcGhvbmVJbnB1dENvbnRhaW5lcjogY3NzKHRlbXBsYXRlT2JqZWN0XzIgfHwgKHRlbXBsYXRlT2JqZWN0XzIgPSBfX21ha2VUZW1wbGF0ZU9iamVjdChbXCJcXG4gICAgZGlzcGxheTogZmxleDtcXG4gICAganVzdGlmeS1jb250ZW50OiBjZW50ZXI7XFxuICAgIGFsaWduLWl0ZW1zOiBjZW50ZXI7XFxuICAgIHdpZHRoOiAxMDAlO1xcbiAgICBoZWlnaHQ6IDUwcHg7XFxuICBcIl0sIFtcIlxcbiAgICBkaXNwbGF5OiBmbGV4O1xcbiAgICBqdXN0aWZ5LWNvbnRlbnQ6IGNlbnRlcjtcXG4gICAgYWxpZ24taXRlbXM6IGNlbnRlcjtcXG4gICAgd2lkdGg6IDEwMCU7XFxuICAgIGhlaWdodDogNTBweDtcXG4gIFwiXSkpKSxcclxufTtcclxudmFyIHRlbXBsYXRlT2JqZWN0XzEsIHRlbXBsYXRlT2JqZWN0XzI7XHJcbi8vIyBzb3VyY2VNYXBwaW5nVVJMPXN0eWxlcy5qcy5tYXAiXX0= */";
var _emotionSourceMap$c = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFHdUIiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgX19tYWtlVGVtcGxhdGVPYmplY3QgfSBmcm9tIFwidHNsaWJcIjtcclxuaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vY29yZSc7XHJcbmV4cG9ydCBkZWZhdWx0IHtcclxuICAgIHBob25lSW5wdXRXcmFwcGVyOiBjc3ModGVtcGxhdGVPYmplY3RfMSB8fCAodGVtcGxhdGVPYmplY3RfMSA9IF9fbWFrZVRlbXBsYXRlT2JqZWN0KFtcIlxcbiAgICB3aWR0aDogMTAwJTtcXG4gICAgZGlzcGxheTogZmxleDtcXG4gICAgZmxleC1kaXJlY3Rpb246IGNvbHVtbjtcXG4gIFwiXSwgW1wiXFxuICAgIHdpZHRoOiAxMDAlO1xcbiAgICBkaXNwbGF5OiBmbGV4O1xcbiAgICBmbGV4LWRpcmVjdGlvbjogY29sdW1uO1xcbiAgXCJdKSkpLFxyXG4gICAgcGhvbmVJbnB1dENvbnRhaW5lcjogY3NzKHRlbXBsYXRlT2JqZWN0XzIgfHwgKHRlbXBsYXRlT2JqZWN0XzIgPSBfX21ha2VUZW1wbGF0ZU9iamVjdChbXCJcXG4gICAgZGlzcGxheTogZmxleDtcXG4gICAganVzdGlmeS1jb250ZW50OiBjZW50ZXI7XFxuICAgIGFsaWduLWl0ZW1zOiBjZW50ZXI7XFxuICAgIHdpZHRoOiAxMDAlO1xcbiAgICBoZWlnaHQ6IDUwcHg7XFxuICBcIl0sIFtcIlxcbiAgICBkaXNwbGF5OiBmbGV4O1xcbiAgICBqdXN0aWZ5LWNvbnRlbnQ6IGNlbnRlcjtcXG4gICAgYWxpZ24taXRlbXM6IGNlbnRlcjtcXG4gICAgd2lkdGg6IDEwMCU7XFxuICAgIGhlaWdodDogNTBweDtcXG4gIFwiXSkpKSxcclxufTtcclxudmFyIHRlbXBsYXRlT2JqZWN0XzEsIHRlbXBsYXRlT2JqZWN0XzI7XHJcbi8vIyBzb3VyY2VNYXBwaW5nVVJMPXN0eWxlcy5qcy5tYXAiXX0= */";
var styles$b = {
  phoneInputWrapper: /*#__PURE__*/css(templateObject_1$c || (templateObject_1$c = __makeTemplateObject(["\n    width: 100%;\n    display: flex;\n    flex-direction: column;\n  ;label:phoneInputWrapper;" + _emotionSourceMap$c], ["\n    width: 100%;\n    display: flex;\n    flex-direction: column;\n  ;label:phoneInputWrapper;" + _emotionSourceMap$c]))),
  phoneInputContainer: /*#__PURE__*/css(templateObject_2$3 || (templateObject_2$3 = __makeTemplateObject(["\n    display: flex;\n    justify-content: center;\n    align-items: center;\n    width: 100%;\n    height: 50px;\n  ;label:phoneInputContainer;" + _emotionSourceMap2$3], ["\n    display: flex;\n    justify-content: center;\n    align-items: center;\n    width: 100%;\n    height: 50px;\n  ;label:phoneInputContainer;" + _emotionSourceMap2$3])))
};
var templateObject_1$c, templateObject_2$3;

var PhoneInput = function (_a) {
  var countries = _a.countries,
    selectedCountryIndex = _a.selectedCountryIndex,
    onSelectedCountryIndexChange = _a.onSelectedCountryIndexChange,
    onPhoneNumberChange = _a.onPhoneNumberChange,
    phoneNumber = _a.phoneNumber,
    message = _a.message,
    maxHeight = _a.maxHeight;
  var options = countries.map(function (country) {
    return {
      value: country.isoCode,
      label: jsx(React.Fragment, null, jsx(ReactCountryFlag, {
        countryCode: country.isoCode,
        svg: true,
        style: {
          width: '24px',
          height: '19px',
          borderRadius: '3px'
        }
      }), jsx("div", {
        style: {
          minWidth: '70px'
        },
        className: "hide-on-active"
      }, jsx(Text, {
        type: "caption",
        inset: {
          left: 'x1'
        },
        content: "+" + country.callingCode + " " + country.isoCode
      }))),
      hasSeparator: country.hasSeparator
    };
  });
  var inputs = useEntities().inputs;
  return jsx(Div, {
    css: styles$b.phoneInputWrapper
  }, jsx(Div, {
    css: styles$b.phoneInputContainer,
    backgroundColor: inputs.backgroundColor,
    borderRadius: inputs.select.borderRadius
  }, jsx(Div, {
    width: "100%",
    height: "100%",
    position: "relative"
  }, jsx(Div, {
    height: "100%",
    position: "absolute",
    zIndex: 1
  }, jsx(Select, {
    dropDownMaxHeight: maxHeight,
    type: "reactNode",
    options: options,
    onChange: onSelectedCountryIndexChange,
    selectedOptionIndex: selectedCountryIndex,
    alignment: "left",
    selectWidth: "65px",
    scrollBarWidth: "thin",
    shouldAlignDropDownToLeft: true
  })), jsx(Div, {
    height: "100%",
    inset: {
      vertical: 'x_5'
    },
    position: "absolute",
    left: "65px",
    zIndex: 1
  }, jsx(Separator, {
    orientation: "vertical"
  })), jsx(Input, {
    message: message,
    name: "tel",
    placeholder: "Enter Phone Number",
    type: "text",
    value: phoneNumber,
    onChange: function (value) {
      return onPhoneNumberChange(value);
    },
    isFullWidth: true,
    isPhoneInput: true
  }))));
};

var _emotionSourceMap4$1 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap3$2 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFLK0MiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgX19tYWtlVGVtcGxhdGVPYmplY3QgfSBmcm9tIFwidHNsaWJcIjtcclxuaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vY29yZSc7XHJcbmV4cG9ydCBkZWZhdWx0IHtcclxuICAgIG1lZGlhRnJhbWU6IGZ1bmN0aW9uICh3aWR0aCwgaGVpZ2h0LCBtYXhIZWlnaHQsIG1pbldpZHRoKSB7IHJldHVybiBjc3ModGVtcGxhdGVPYmplY3RfMSB8fCAodGVtcGxhdGVPYmplY3RfMSA9IF9fbWFrZVRlbXBsYXRlT2JqZWN0KFtcIlxcbiAgICB3aWR0aDogXCIsIFwiO1xcbiAgICBoZWlnaHQ6IFwiLCBcIjtcXG4gICAgbWF4LWhlaWdodDogXCIsIFwiO1xcbiAgICBtaW4td2lkdGg6IFwiLCBcIjtcXG4gIFwiXSwgW1wiXFxuICAgIHdpZHRoOiBcIiwgXCI7XFxuICAgIGhlaWdodDogXCIsIFwiO1xcbiAgICBtYXgtaGVpZ2h0OiBcIiwgXCI7XFxuICAgIG1pbi13aWR0aDogXCIsIFwiO1xcbiAgXCJdKSksIHdpZHRoLCBoZWlnaHQgfHwgJ2F1dG8nLCBtYXhIZWlnaHQgfHwgJzM5NXB4JywgbWluV2lkdGgpOyB9LFxyXG4gICAgcG9zdGVyOiBmdW5jdGlvbiAoaW1hZ2UpIHsgcmV0dXJuIGNzcyh0ZW1wbGF0ZU9iamVjdF8yIHx8ICh0ZW1wbGF0ZU9iamVjdF8yID0gX19tYWtlVGVtcGxhdGVPYmplY3QoW1wiXFxuICAgIHdpZHRoOiAxMDAlO1xcbiAgICBoZWlnaHQ6IDEwMCU7XFxuICAgIG1hcmdpbi1ib3R0b206IDVweDtcXG4gICAgcGFkZGluZy1ib3R0b206IDY1JTtcXG4gICAgYmFja2dyb3VuZC1pbWFnZTogXCIsIFwiO1xcbiAgICBiYWNrZ3JvdW5kLXBvc2l0aW9uOiBjZW50ZXI7XFxuICAgIGJhY2tncm91bmQtc2l6ZTogY292ZXI7XFxuICAgIGJhY2tncm91bmQtcmVwZWF0OiBuby1yZXBlYXQ7XFxuICAgIHBvc2l0aW9uOiByZWxhdGl2ZTtcXG4gIFwiXSwgW1wiXFxuICAgIHdpZHRoOiAxMDAlO1xcbiAgICBoZWlnaHQ6IDEwMCU7XFxuICAgIG1hcmdpbi1ib3R0b206IDVweDtcXG4gICAgcGFkZGluZy1ib3R0b206IDY1JTtcXG4gICAgYmFja2dyb3VuZC1pbWFnZTogXCIsIFwiO1xcbiAgICBiYWNrZ3JvdW5kLXBvc2l0aW9uOiBjZW50ZXI7XFxuICAgIGJhY2tncm91bmQtc2l6ZTogY292ZXI7XFxuICAgIGJhY2tncm91bmQtcmVwZWF0OiBuby1yZXBlYXQ7XFxuICAgIHBvc2l0aW9uOiByZWxhdGl2ZTtcXG4gIFwiXSkpLCBcInVybChcIiArIGltYWdlICsgXCIpXCIpOyB9LFxyXG4gICAgdmlkZW9QbGF5ZXI6IGZ1bmN0aW9uIChpc1BsYXlpbmcpIHsgcmV0dXJuIGNzcyh0ZW1wbGF0ZU9iamVjdF8zIHx8ICh0ZW1wbGF0ZU9iamVjdF8zID0gX19tYWtlVGVtcGxhdGVPYmplY3QoW1wiXFxuICAgIHdpZHRoOiAxMDAlO1xcbiAgICBoZWlnaHQ6IDEwMCU7XFxuICAgIG1hcmdpbi1ib3R0b206IDVweDtcXG4gICAgcGFkZGluZy1ib3R0b206IFwiLCBcIjtcXG4gICAgcG9zaXRpb246IHJlbGF0aXZlO1xcbiAgICBvdmVyZmxvdzogXCIsIFwiO1xcbiAgICB0cmFuc2l0aW9uOiBhbGwgMC41cyBjdWJpYy1iZXppZXIoMCwgMC45OSwgMC42NywgMSk7XFxuICAgID4gaWZyYW1lIHtcXG4gICAgICBwb3NpdGlvbjogYWJzb2x1dGU7XFxuICAgICAgdG9wOiAwO1xcbiAgICAgIGxlZnQ6IDA7XFxuICAgICAgd2lkdGg6IDEwMCU7XFxuICAgICAgaGVpZ2h0OiAxMDAlO1xcbiAgICAgIGJvcmRlcjogbm9uZTtcXG4gICAgICBib3JkZXItcmFkaXVzOiBpbmhlcml0O1xcbiAgICB9XFxuICBcIl0sIFtcIlxcbiAgICB3aWR0aDogMTAwJTtcXG4gICAgaGVpZ2h0OiAxMDAlO1xcbiAgICBtYXJnaW4tYm90dG9tOiA1cHg7XFxuICAgIHBhZGRpbmctYm90dG9tOiBcIiwgXCI7XFxuICAgIHBvc2l0aW9uOiByZWxhdGl2ZTtcXG4gICAgb3ZlcmZsb3c6IFwiLCBcIjtcXG4gICAgdHJhbnNpdGlvbjogYWxsIDAuNXMgY3ViaWMtYmV6aWVyKDAsIDAuOTksIDAuNjcsIDEpO1xcbiAgICA+IGlmcmFtZSB7XFxuICAgICAgcG9zaXRpb246IGFic29sdXRlO1xcbiAgICAgIHRvcDogMDtcXG4gICAgICBsZWZ0OiAwO1xcbiAgICAgIHdpZHRoOiAxMDAlO1xcbiAgICAgIGhlaWdodDogMTAwJTtcXG4gICAgICBib3JkZXI6IG5vbmU7XFxuICAgICAgYm9yZGVyLXJhZGl1czogaW5oZXJpdDtcXG4gICAgfVxcbiAgXCJdKSksIGlzUGxheWluZyA/ICc2NSUnIDogMCwgaXNQbGF5aW5nID8gJ2hpZGRlbicgOiAnaW5pdGlhbCcpOyB9LFxyXG4gICAgcGxheUJ1dHRvbjogZnVuY3Rpb24gKHNpemUsIGljb25FbnRpdHkpIHsgcmV0dXJuIGNzcyh0ZW1wbGF0ZU9iamVjdF80IHx8ICh0ZW1wbGF0ZU9iamVjdF80ID0gX19tYWtlVGVtcGxhdGVPYmplY3QoW1wiXFxuICAgIHdpZHRoOiBcIiwgXCJweDtcXG4gICAgaGVpZ2h0OiBcIiwgXCJweDtcXG4gICAgZGlzcGxheTogZmxleDtcXG4gICAganVzdGlmeS1jb250ZW50OiBjZW50ZXI7XFxuICAgIGFsaWduLWl0ZW1zOiBjZW50ZXI7XFxuICAgIHBvc2l0aW9uOiBhYnNvbHV0ZTtcXG4gICAgdG9wOiA1MCU7XFxuICAgIGxlZnQ6IDUwJTtcXG4gICAgdHJhbnNmb3JtOiB0cmFuc2xhdGUoLTUwJSwgLTUwJSk7XFxuICAgIGJhY2tncm91bmQ6IHJnYmEoMjM0LCAyMzQsIDIzNCwgMC43Nyk7XFxuICAgIGJveC1zaGFkb3c6IDAgNHB4IDdweCByZ2JhKDE2OSwgMTcyLCAxNzUsIDAuNTgpO1xcbiAgICBjdXJzb3I6IHBvaW50ZXI7XFxuICAgIHBhZGRpbmc6IDRweDtcXG4gICAgPiBkaXYge1xcbiAgICAgIGRpc3BsYXk6IGZsZXg7XFxuICAgICAganVzdGlmeS1jb250ZW50OiBjZW50ZXI7XFxuICAgICAgYWxpZ24taXRlbXM6IGNlbnRlcjtcXG4gICAgICB3aWR0aDogMTAwJTtcXG4gICAgICBoZWlnaHQ6IDEwMCU7XFxuICAgICAgYmFja2dyb3VuZDogd2hpdGU7XFxuICAgICAgdHJhbnNpdGlvbjogYWxsIDAuNXMgY3ViaWMtYmV6aWVyKDAsIDAuOTksIDAuNjcsIDEpO1xcbiAgICB9XFxuICAgICY6aG92ZXIge1xcbiAgICAgID4gZGl2IHtcXG4gICAgICAgIHRyYW5zZm9ybTogc2NhbGUoMS4yOCk7XFxuICAgICAgICB0cmFuc2l0aW9uOiBhbGwgMC41cyBjdWJpYy1iZXppZXIoMCwgMC45OSwgMC42NywgMSk7XFxuICAgICAgICBiYWNrZ3JvdW5kOiBcIiwgXCI7XFxuICAgICAgICA+IHN2ZyA+IHBhdGgge1xcbiAgICAgICAgICBzdHJva2U6IFwiLCBcIjtcXG4gICAgICAgICAgZmlsbDogXCIsIFwiO1xcbiAgICAgICAgfVxcbiAgICAgIH1cXG4gICAgfVxcbiAgXCJdLCBbXCJcXG4gICAgd2lkdGg6IFwiLCBcInB4O1xcbiAgICBoZWlnaHQ6IFwiLCBcInB4O1xcbiAgICBkaXNwbGF5OiBmbGV4O1xcbiAgICBqdXN0aWZ5LWNvbnRlbnQ6IGNlbnRlcjtcXG4gICAgYWxpZ24taXRlbXM6IGNlbnRlcjtcXG4gICAgcG9zaXRpb246IGFic29sdXRlO1xcbiAgICB0b3A6IDUwJTtcXG4gICAgbGVmdDogNTAlO1xcbiAgICB0cmFuc2Zvcm06IHRyYW5zbGF0ZSgtNTAlLCAtNTAlKTtcXG4gICAgYmFja2dyb3VuZDogcmdiYSgyMzQsIDIzNCwgMjM0LCAwLjc3KTtcXG4gICAgYm94LXNoYWRvdzogMCA0cHggN3B4IHJnYmEoMTY5LCAxNzIsIDE3NSwgMC41OCk7XFxuICAgIGN1cnNvcjogcG9pbnRlcjtcXG4gICAgcGFkZGluZzogNHB4O1xcbiAgICA+IGRpdiB7XFxuICAgICAgZGlzcGxheTogZmxleDtcXG4gICAgICBqdXN0aWZ5LWNvbnRlbnQ6IGNlbnRlcjtcXG4gICAgICBhbGlnbi1pdGVtczogY2VudGVyO1xcbiAgICAgIHdpZHRoOiAxMDAlO1xcbiAgICAgIGhlaWdodDogMTAwJTtcXG4gICAgICBiYWNrZ3JvdW5kOiB3aGl0ZTtcXG4gICAgICB0cmFuc2l0aW9uOiBhbGwgMC41cyBjdWJpYy1iZXppZXIoMCwgMC45OSwgMC42NywgMSk7XFxuICAgIH1cXG4gICAgJjpob3ZlciB7XFxuICAgICAgPiBkaXYge1xcbiAgICAgICAgdHJhbnNmb3JtOiBzY2FsZSgxLjI4KTtcXG4gICAgICAgIHRyYW5zaXRpb246IGFsbCAwLjVzIGN1YmljLWJlemllcigwLCAwLjk5LCAwLjY3LCAxKTtcXG4gICAgICAgIGJhY2tncm91bmQ6IFwiLCBcIjtcXG4gICAgICAgID4gc3ZnID4gcGF0aCB7XFxuICAgICAgICAgIHN0cm9rZTogXCIsIFwiO1xcbiAgICAgICAgICBmaWxsOiBcIiwgXCI7XFxuICAgICAgICB9XFxuICAgICAgfVxcbiAgICB9XFxuICBcIl0pKSwgc2l6ZSwgc2l6ZSwgaWNvbkVudGl0eS5jb2xvcnMucHJpbWFyeUJyYW5kLCBpY29uRW50aXR5LmNvbG9ycy5pbnZlcnNlQm9keVRleHQsIGljb25FbnRpdHkuY29sb3JzLmludmVyc2VCb2R5VGV4dCk7IH0sXHJcbn07XHJcbnZhciB0ZW1wbGF0ZU9iamVjdF8xLCB0ZW1wbGF0ZU9iamVjdF8yLCB0ZW1wbGF0ZU9iamVjdF8zLCB0ZW1wbGF0ZU9iamVjdF80O1xyXG4vLyMgc291cmNlTWFwcGluZ1VSTD1zdHlsZXMuanMubWFwIl19 */";
var _emotionSourceMap2$2 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFJc0MiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgX19tYWtlVGVtcGxhdGVPYmplY3QgfSBmcm9tIFwidHNsaWJcIjtcclxuaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vY29yZSc7XHJcbmV4cG9ydCBkZWZhdWx0IHtcclxuICAgIG1lZGlhRnJhbWU6IGZ1bmN0aW9uICh3aWR0aCwgaGVpZ2h0LCBtYXhIZWlnaHQsIG1pbldpZHRoKSB7IHJldHVybiBjc3ModGVtcGxhdGVPYmplY3RfMSB8fCAodGVtcGxhdGVPYmplY3RfMSA9IF9fbWFrZVRlbXBsYXRlT2JqZWN0KFtcIlxcbiAgICB3aWR0aDogXCIsIFwiO1xcbiAgICBoZWlnaHQ6IFwiLCBcIjtcXG4gICAgbWF4LWhlaWdodDogXCIsIFwiO1xcbiAgICBtaW4td2lkdGg6IFwiLCBcIjtcXG4gIFwiXSwgW1wiXFxuICAgIHdpZHRoOiBcIiwgXCI7XFxuICAgIGhlaWdodDogXCIsIFwiO1xcbiAgICBtYXgtaGVpZ2h0OiBcIiwgXCI7XFxuICAgIG1pbi13aWR0aDogXCIsIFwiO1xcbiAgXCJdKSksIHdpZHRoLCBoZWlnaHQgfHwgJ2F1dG8nLCBtYXhIZWlnaHQgfHwgJzM5NXB4JywgbWluV2lkdGgpOyB9LFxyXG4gICAgcG9zdGVyOiBmdW5jdGlvbiAoaW1hZ2UpIHsgcmV0dXJuIGNzcyh0ZW1wbGF0ZU9iamVjdF8yIHx8ICh0ZW1wbGF0ZU9iamVjdF8yID0gX19tYWtlVGVtcGxhdGVPYmplY3QoW1wiXFxuICAgIHdpZHRoOiAxMDAlO1xcbiAgICBoZWlnaHQ6IDEwMCU7XFxuICAgIG1hcmdpbi1ib3R0b206IDVweDtcXG4gICAgcGFkZGluZy1ib3R0b206IDY1JTtcXG4gICAgYmFja2dyb3VuZC1pbWFnZTogXCIsIFwiO1xcbiAgICBiYWNrZ3JvdW5kLXBvc2l0aW9uOiBjZW50ZXI7XFxuICAgIGJhY2tncm91bmQtc2l6ZTogY292ZXI7XFxuICAgIGJhY2tncm91bmQtcmVwZWF0OiBuby1yZXBlYXQ7XFxuICAgIHBvc2l0aW9uOiByZWxhdGl2ZTtcXG4gIFwiXSwgW1wiXFxuICAgIHdpZHRoOiAxMDAlO1xcbiAgICBoZWlnaHQ6IDEwMCU7XFxuICAgIG1hcmdpbi1ib3R0b206IDVweDtcXG4gICAgcGFkZGluZy1ib3R0b206IDY1JTtcXG4gICAgYmFja2dyb3VuZC1pbWFnZTogXCIsIFwiO1xcbiAgICBiYWNrZ3JvdW5kLXBvc2l0aW9uOiBjZW50ZXI7XFxuICAgIGJhY2tncm91bmQtc2l6ZTogY292ZXI7XFxuICAgIGJhY2tncm91bmQtcmVwZWF0OiBuby1yZXBlYXQ7XFxuICAgIHBvc2l0aW9uOiByZWxhdGl2ZTtcXG4gIFwiXSkpLCBcInVybChcIiArIGltYWdlICsgXCIpXCIpOyB9LFxyXG4gICAgdmlkZW9QbGF5ZXI6IGZ1bmN0aW9uIChpc1BsYXlpbmcpIHsgcmV0dXJuIGNzcyh0ZW1wbGF0ZU9iamVjdF8zIHx8ICh0ZW1wbGF0ZU9iamVjdF8zID0gX19tYWtlVGVtcGxhdGVPYmplY3QoW1wiXFxuICAgIHdpZHRoOiAxMDAlO1xcbiAgICBoZWlnaHQ6IDEwMCU7XFxuICAgIG1hcmdpbi1ib3R0b206IDVweDtcXG4gICAgcGFkZGluZy1ib3R0b206IFwiLCBcIjtcXG4gICAgcG9zaXRpb246IHJlbGF0aXZlO1xcbiAgICBvdmVyZmxvdzogXCIsIFwiO1xcbiAgICB0cmFuc2l0aW9uOiBhbGwgMC41cyBjdWJpYy1iZXppZXIoMCwgMC45OSwgMC42NywgMSk7XFxuICAgID4gaWZyYW1lIHtcXG4gICAgICBwb3NpdGlvbjogYWJzb2x1dGU7XFxuICAgICAgdG9wOiAwO1xcbiAgICAgIGxlZnQ6IDA7XFxuICAgICAgd2lkdGg6IDEwMCU7XFxuICAgICAgaGVpZ2h0OiAxMDAlO1xcbiAgICAgIGJvcmRlcjogbm9uZTtcXG4gICAgICBib3JkZXItcmFkaXVzOiBpbmhlcml0O1xcbiAgICB9XFxuICBcIl0sIFtcIlxcbiAgICB3aWR0aDogMTAwJTtcXG4gICAgaGVpZ2h0OiAxMDAlO1xcbiAgICBtYXJnaW4tYm90dG9tOiA1cHg7XFxuICAgIHBhZGRpbmctYm90dG9tOiBcIiwgXCI7XFxuICAgIHBvc2l0aW9uOiByZWxhdGl2ZTtcXG4gICAgb3ZlcmZsb3c6IFwiLCBcIjtcXG4gICAgdHJhbnNpdGlvbjogYWxsIDAuNXMgY3ViaWMtYmV6aWVyKDAsIDAuOTksIDAuNjcsIDEpO1xcbiAgICA+IGlmcmFtZSB7XFxuICAgICAgcG9zaXRpb246IGFic29sdXRlO1xcbiAgICAgIHRvcDogMDtcXG4gICAgICBsZWZ0OiAwO1xcbiAgICAgIHdpZHRoOiAxMDAlO1xcbiAgICAgIGhlaWdodDogMTAwJTtcXG4gICAgICBib3JkZXI6IG5vbmU7XFxuICAgICAgYm9yZGVyLXJhZGl1czogaW5oZXJpdDtcXG4gICAgfVxcbiAgXCJdKSksIGlzUGxheWluZyA/ICc2NSUnIDogMCwgaXNQbGF5aW5nID8gJ2hpZGRlbicgOiAnaW5pdGlhbCcpOyB9LFxyXG4gICAgcGxheUJ1dHRvbjogZnVuY3Rpb24gKHNpemUsIGljb25FbnRpdHkpIHsgcmV0dXJuIGNzcyh0ZW1wbGF0ZU9iamVjdF80IHx8ICh0ZW1wbGF0ZU9iamVjdF80ID0gX19tYWtlVGVtcGxhdGVPYmplY3QoW1wiXFxuICAgIHdpZHRoOiBcIiwgXCJweDtcXG4gICAgaGVpZ2h0OiBcIiwgXCJweDtcXG4gICAgZGlzcGxheTogZmxleDtcXG4gICAganVzdGlmeS1jb250ZW50OiBjZW50ZXI7XFxuICAgIGFsaWduLWl0ZW1zOiBjZW50ZXI7XFxuICAgIHBvc2l0aW9uOiBhYnNvbHV0ZTtcXG4gICAgdG9wOiA1MCU7XFxuICAgIGxlZnQ6IDUwJTtcXG4gICAgdHJhbnNmb3JtOiB0cmFuc2xhdGUoLTUwJSwgLTUwJSk7XFxuICAgIGJhY2tncm91bmQ6IHJnYmEoMjM0LCAyMzQsIDIzNCwgMC43Nyk7XFxuICAgIGJveC1zaGFkb3c6IDAgNHB4IDdweCByZ2JhKDE2OSwgMTcyLCAxNzUsIDAuNTgpO1xcbiAgICBjdXJzb3I6IHBvaW50ZXI7XFxuICAgIHBhZGRpbmc6IDRweDtcXG4gICAgPiBkaXYge1xcbiAgICAgIGRpc3BsYXk6IGZsZXg7XFxuICAgICAganVzdGlmeS1jb250ZW50OiBjZW50ZXI7XFxuICAgICAgYWxpZ24taXRlbXM6IGNlbnRlcjtcXG4gICAgICB3aWR0aDogMTAwJTtcXG4gICAgICBoZWlnaHQ6IDEwMCU7XFxuICAgICAgYmFja2dyb3VuZDogd2hpdGU7XFxuICAgICAgdHJhbnNpdGlvbjogYWxsIDAuNXMgY3ViaWMtYmV6aWVyKDAsIDAuOTksIDAuNjcsIDEpO1xcbiAgICB9XFxuICAgICY6aG92ZXIge1xcbiAgICAgID4gZGl2IHtcXG4gICAgICAgIHRyYW5zZm9ybTogc2NhbGUoMS4yOCk7XFxuICAgICAgICB0cmFuc2l0aW9uOiBhbGwgMC41cyBjdWJpYy1iZXppZXIoMCwgMC45OSwgMC42NywgMSk7XFxuICAgICAgICBiYWNrZ3JvdW5kOiBcIiwgXCI7XFxuICAgICAgICA+IHN2ZyA+IHBhdGgge1xcbiAgICAgICAgICBzdHJva2U6IFwiLCBcIjtcXG4gICAgICAgICAgZmlsbDogXCIsIFwiO1xcbiAgICAgICAgfVxcbiAgICAgIH1cXG4gICAgfVxcbiAgXCJdLCBbXCJcXG4gICAgd2lkdGg6IFwiLCBcInB4O1xcbiAgICBoZWlnaHQ6IFwiLCBcInB4O1xcbiAgICBkaXNwbGF5OiBmbGV4O1xcbiAgICBqdXN0aWZ5LWNvbnRlbnQ6IGNlbnRlcjtcXG4gICAgYWxpZ24taXRlbXM6IGNlbnRlcjtcXG4gICAgcG9zaXRpb246IGFic29sdXRlO1xcbiAgICB0b3A6IDUwJTtcXG4gICAgbGVmdDogNTAlO1xcbiAgICB0cmFuc2Zvcm06IHRyYW5zbGF0ZSgtNTAlLCAtNTAlKTtcXG4gICAgYmFja2dyb3VuZDogcmdiYSgyMzQsIDIzNCwgMjM0LCAwLjc3KTtcXG4gICAgYm94LXNoYWRvdzogMCA0cHggN3B4IHJnYmEoMTY5LCAxNzIsIDE3NSwgMC41OCk7XFxuICAgIGN1cnNvcjogcG9pbnRlcjtcXG4gICAgcGFkZGluZzogNHB4O1xcbiAgICA+IGRpdiB7XFxuICAgICAgZGlzcGxheTogZmxleDtcXG4gICAgICBqdXN0aWZ5LWNvbnRlbnQ6IGNlbnRlcjtcXG4gICAgICBhbGlnbi1pdGVtczogY2VudGVyO1xcbiAgICAgIHdpZHRoOiAxMDAlO1xcbiAgICAgIGhlaWdodDogMTAwJTtcXG4gICAgICBiYWNrZ3JvdW5kOiB3aGl0ZTtcXG4gICAgICB0cmFuc2l0aW9uOiBhbGwgMC41cyBjdWJpYy1iZXppZXIoMCwgMC45OSwgMC42NywgMSk7XFxuICAgIH1cXG4gICAgJjpob3ZlciB7XFxuICAgICAgPiBkaXYge1xcbiAgICAgICAgdHJhbnNmb3JtOiBzY2FsZSgxLjI4KTtcXG4gICAgICAgIHRyYW5zaXRpb246IGFsbCAwLjVzIGN1YmljLWJlemllcigwLCAwLjk5LCAwLjY3LCAxKTtcXG4gICAgICAgIGJhY2tncm91bmQ6IFwiLCBcIjtcXG4gICAgICAgID4gc3ZnID4gcGF0aCB7XFxuICAgICAgICAgIHN0cm9rZTogXCIsIFwiO1xcbiAgICAgICAgICBmaWxsOiBcIiwgXCI7XFxuICAgICAgICB9XFxuICAgICAgfVxcbiAgICB9XFxuICBcIl0pKSwgc2l6ZSwgc2l6ZSwgaWNvbkVudGl0eS5jb2xvcnMucHJpbWFyeUJyYW5kLCBpY29uRW50aXR5LmNvbG9ycy5pbnZlcnNlQm9keVRleHQsIGljb25FbnRpdHkuY29sb3JzLmludmVyc2VCb2R5VGV4dCk7IH0sXHJcbn07XHJcbnZhciB0ZW1wbGF0ZU9iamVjdF8xLCB0ZW1wbGF0ZU9iamVjdF8yLCB0ZW1wbGF0ZU9iamVjdF8zLCB0ZW1wbGF0ZU9iamVjdF80O1xyXG4vLyMgc291cmNlTWFwcGluZ1VSTD1zdHlsZXMuanMubWFwIl19 */";
var _emotionSourceMap$b = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var styles$a = {
  mediaFrame: function (width, height, maxHeight, minWidth) {
    return /*#__PURE__*/css(templateObject_1$b || (templateObject_1$b = __makeTemplateObject(["\n    width: ", ";\n    height: ", ";\n    max-height: ", ";\n    min-width: ", ";\n  " + _emotionSourceMap$b], ["\n    width: ", ";\n    height: ", ";\n    max-height: ", ";\n    min-width: ", ";\n  " + _emotionSourceMap$b])), width, height || 'auto', maxHeight || '395px', minWidth);
  },
  poster: function (image) {
    return /*#__PURE__*/css(templateObject_2$2 || (templateObject_2$2 = __makeTemplateObject(["\n    width: 100%;\n    height: 100%;\n    margin-bottom: 5px;\n    padding-bottom: 65%;\n    background-image: ", ";\n    background-position: center;\n    background-size: cover;\n    background-repeat: no-repeat;\n    position: relative;\n  " + _emotionSourceMap2$2], ["\n    width: 100%;\n    height: 100%;\n    margin-bottom: 5px;\n    padding-bottom: 65%;\n    background-image: ", ";\n    background-position: center;\n    background-size: cover;\n    background-repeat: no-repeat;\n    position: relative;\n  " + _emotionSourceMap2$2])), "url(" + image + ")");
  },
  videoPlayer: function (isPlaying) {
    return /*#__PURE__*/css(templateObject_3$2 || (templateObject_3$2 = __makeTemplateObject(["\n    width: 100%;\n    height: 100%;\n    margin-bottom: 5px;\n    padding-bottom: ", ";\n    position: relative;\n    overflow: ", ";\n    transition: all 0.5s cubic-bezier(0, 0.99, 0.67, 1);\n    > iframe {\n      position: absolute;\n      top: 0;\n      left: 0;\n      width: 100%;\n      height: 100%;\n      border: none;\n      border-radius: inherit;\n    }\n  " + _emotionSourceMap3$2], ["\n    width: 100%;\n    height: 100%;\n    margin-bottom: 5px;\n    padding-bottom: ", ";\n    position: relative;\n    overflow: ", ";\n    transition: all 0.5s cubic-bezier(0, 0.99, 0.67, 1);\n    > iframe {\n      position: absolute;\n      top: 0;\n      left: 0;\n      width: 100%;\n      height: 100%;\n      border: none;\n      border-radius: inherit;\n    }\n  " + _emotionSourceMap3$2])), isPlaying ? '65%' : 0, isPlaying ? 'hidden' : 'initial');
  },
  playButton: function (size, iconEntity) {
    return /*#__PURE__*/css(templateObject_4$1 || (templateObject_4$1 = __makeTemplateObject(["\n    width: ", "px;\n    height: ", "px;\n    display: flex;\n    justify-content: center;\n    align-items: center;\n    position: absolute;\n    top: 50%;\n    left: 50%;\n    transform: translate(-50%, -50%);\n    background: rgba(234, 234, 234, 0.77);\n    box-shadow: 0 4px 7px rgba(169, 172, 175, 0.58);\n    cursor: pointer;\n    padding: 4px;\n    > div {\n      display: flex;\n      justify-content: center;\n      align-items: center;\n      width: 100%;\n      height: 100%;\n      background: white;\n      transition: all 0.5s cubic-bezier(0, 0.99, 0.67, 1);\n    }\n    &:hover {\n      > div {\n        transform: scale(1.28);\n        transition: all 0.5s cubic-bezier(0, 0.99, 0.67, 1);\n        background: ", ";\n        > svg > path {\n          stroke: ", ";\n          fill: ", ";\n        }\n      }\n    }\n  " + _emotionSourceMap4$1], ["\n    width: ", "px;\n    height: ", "px;\n    display: flex;\n    justify-content: center;\n    align-items: center;\n    position: absolute;\n    top: 50%;\n    left: 50%;\n    transform: translate(-50%, -50%);\n    background: rgba(234, 234, 234, 0.77);\n    box-shadow: 0 4px 7px rgba(169, 172, 175, 0.58);\n    cursor: pointer;\n    padding: 4px;\n    > div {\n      display: flex;\n      justify-content: center;\n      align-items: center;\n      width: 100%;\n      height: 100%;\n      background: white;\n      transition: all 0.5s cubic-bezier(0, 0.99, 0.67, 1);\n    }\n    &:hover {\n      > div {\n        transform: scale(1.28);\n        transition: all 0.5s cubic-bezier(0, 0.99, 0.67, 1);\n        background: ", ";\n        > svg > path {\n          stroke: ", ";\n          fill: ", ";\n        }\n      }\n    }\n  " + _emotionSourceMap4$1])), size, size, iconEntity.colors.primaryBrand, iconEntity.colors.inverseBodyText, iconEntity.colors.inverseBodyText);
  }
};
var templateObject_1$b, templateObject_2$2, templateObject_3$2, templateObject_4$1;

var PlayButton = function (_a) {
  var buttonSize = _a.buttonSize,
    iconSize = _a.iconSize,
    onClick = _a.onClick;
  var icons = useEntities().icons;
  return jsx(Div, {
    borderRadius: "circle",
    onClick: onClick,
    css: styles$a.playButton(buttonSize, icons)
  }, jsx(Div, {
    borderRadius: "circle"
  }, jsx(Icon, {
    name: "play",
    size: iconSize ? iconSize : 'medium',
    primaryColor: "primaryBrand"
  })));
};

var Poster = function (_a) {
  var poster = _a.poster,
    playButton = _a.playButton,
    hasShadow = _a.hasShadow,
    onClick = _a.onClick,
    borderRadius = _a.borderRadius;
  return jsx(Div, {
    width: "100%",
    height: "100%"
  }, jsx(Div, {
    onClick: onClick,
    css: styles$a.poster(poster),
    borderRadius: borderRadius ? borderRadius : 'default',
    boxShadow: hasShadow ? 'mediumDark' : undefined
  }), playButton && onClick && jsx(PlayButton, {
    buttonSize: 65,
    onClick: onClick
  }));
};

var VideoPlayer = function (_a) {
  var poster = _a.poster,
    videoId = _a.videoId,
    hasShadow = _a.hasShadow,
    duration = _a.duration,
    width = _a.width,
    height = _a.height,
    maxHeight = _a.maxHeight,
    borderRadius = _a.borderRadius,
    minWidth = _a.minWidth,
    shouldAutoplay = _a.shouldAutoplay;
  var _b = useState(shouldAutoplay !== null && shouldAutoplay !== void 0 ? shouldAutoplay : false),
    isPlaying = _b[0],
    setIsPlaying = _b[1];
  return jsx(Div, {
    width: width,
    height: height,
    minWidth: minWidth,
    maxHeight: maxHeight
  }, jsx(Div, {
    css: styles$a.videoPlayer(isPlaying),
    borderRadius: borderRadius ? borderRadius : 'default',
    boxShadow: isPlaying ? 'mediumDark' : undefined
  }, isPlaying ? jsx("iframe", {
    src: "https://fast.wistia.net/embed/iframe/" + videoId + "?autoplay=1",
    allowFullScreen: true,
    title: "video"
  }) : jsx(Poster, {
    borderRadius: borderRadius,
    poster: poster,
    hasShadow: hasShadow,
    playButton: true,
    duration: duration,
    onClick: function () {
      return setIsPlaying(!isPlaying);
    }
  })));
};

var _emotionSourceMap$a = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFHNkIiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgX19tYWtlVGVtcGxhdGVPYmplY3QgfSBmcm9tIFwidHNsaWJcIjtcclxuaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vY29yZSc7XHJcbmV4cG9ydCBkZWZhdWx0IHtcclxuICAgIHNlbGVjdGFibGVMaXN0Q29udGFpbmVyOiBjc3ModGVtcGxhdGVPYmplY3RfMSB8fCAodGVtcGxhdGVPYmplY3RfMSA9IF9fbWFrZVRlbXBsYXRlT2JqZWN0KFtcIlwiXSwgW1wiXCJdKSkpLFxyXG59O1xyXG52YXIgdGVtcGxhdGVPYmplY3RfMTtcclxuLy8jIHNvdXJjZU1hcHBpbmdVUkw9c3R5bGVzLmpzLm1hcCJdfQ== */";
var styles$9 = {
  selectableListContainer: /*#__PURE__*/css(templateObject_1$a || (templateObject_1$a = __makeTemplateObject([";label:selectableListContainer;" + _emotionSourceMap$a], [";label:selectableListContainer;" + _emotionSourceMap$a])))
};
var templateObject_1$a;

var SelectableList = function (_a) {
  var _b = _a.limit,
    limit = _b === void 0 ? 1 : _b,
    _c = _a.orientation,
    orientation = _c === void 0 ? 'vertical' : _c,
    children = _a.children,
    selectedIndexes = _a.selectedIndexes,
    setSelectedIndexes = _a.setSelectedIndexes;
  var isFadeNonSelected = limit > 1 ? true : false;
  var _d = useState(false),
    isDisabled = _d[0],
    setIsDisabled = _d[1];
  var handleSelect = function (index) {
    var hasFoundDuplicate = selectedIndexes.some(function (selectedIndex) {
      return selectedIndex === index;
    });
    if (hasFoundDuplicate) {
      var filteredIndexes = selectedIndexes.filter(function (selectedIndex) {
        return selectedIndex !== index;
      });
      setSelectedIndexes(filteredIndexes);
    } else {
      if (limit === 1) {
        setSelectedIndexes([index]);
      } else {
        if (selectedIndexes.length < limit) {
          setSelectedIndexes(__spreadArrays(selectedIndexes, [index]));
        }
      }
    }
  };
  useEffect(function () {
    if (selectedIndexes.length >= limit) {
      setIsDisabled(true);
    } else {
      setIsDisabled(false);
    }
  }, [selectedIndexes, limit]);
  return jsx(List, {
    orientation: orientation,
    alignment: "left",
    css: styles$9.selectableListContainer
  }, React.Children.map(children, function (child, index) {
    var isActive = selectedIndexes.some(function (selected) {
      return selected === index;
    });
    return jsx(ListItem, {
      onClick: function () {
        return handleSelect(index);
      },
      key: index,
      orientation: "vertical",
      inset: {
        vertical: 'x1'
      }
    }, jsx(Div, {
      animate: isFadeNonSelected && isDisabled && !isActive ? {
        opacity: 0.3
      } : {
        opacity: 1
      },
      initial: isFadeNonSelected && isDisabled && !isActive ? {
        opacity: 0.3
      } : {
        opacity: 1
      },
      cursor: "pointer"
    }, child));
  }));
};

var SelectableCard = function (_a) {
  var isActive = _a.isActive,
    children = _a.children,
    onClick = _a.onClick,
    _b = _a.shouldShowActiveIcons,
    shouldShowActiveIcons = _b === void 0 ? true : _b;
  return jsx(Div, {
    cursor: "pointer",
    onClick: function () {
      return onClick === null || onClick === void 0 ? void 0 : onClick();
    }
  }, jsx(Card, {
    overflow: "hidden",
    boxShadow: "mediumLight",
    borderRadius: "default",
    width: "100%",
    inset: "x2",
    borderWidth: 'default',
    borderColor: isActive ? {
      swatch: 'success',
      shade: 'base'
    } : 'transparent',
    borderStyle: isActive ? 'solid' : 'none' // TODO - Fix this, causes jump when active but w/o the featured ribbon in RadioCard looks weird
  }, jsx(Flex, {
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%"
  }, children, shouldShowActiveIcons && jsx(Div, {
    backgroundColor: isActive ? {
      swatch: 'success',
      shade: 'base'
    } : {
      swatch: 'grayScale',
      shade: '_900'
    },
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "24px",
    height: "24px",
    borderRadius: "small"
  }, jsx(Icon, {
    size: "xSmall",
    name: isActive ? 'check' : 'add',
    primaryColor: "inverseBodyText"
  })))));
};

var ConfirmDialog = function (_a) {
  var additionalLink = _a.additionalLink,
    accept = _a.accept,
    decline = _a.decline,
    title = _a.title,
    description = _a.description,
    _b = _a.zIndex,
    zIndex = _b === void 0 ? 1000 : _b,
    isOpen = _a.isOpen,
    primaryAction = _a.primaryAction,
    onClose = _a.onClose,
    requireScroll = _a.requireScroll,
    _c = _a.maxWidth,
    maxWidth = _c === void 0 ? '552px' : _c,
    _d = _a.hasStackedActions,
    hasStackedActions = _d === void 0 ? false : _d,
    maxHeight = _a.maxHeight,
    rest = __rest(_a, ["additionalLink", "accept", "decline", "title", "description", "zIndex", "isOpen", "primaryAction", "onClose", "requireScroll", "maxWidth", "hasStackedActions", "maxHeight"]);
  var isPrimaryAccept = primaryAction === 'accept' ? true : false;
  var _e = useState(false),
    hasScrolled = _e[0],
    setHasScrolled = _e[1];
  var handleScroll = function (progress) {
    if (progress === 1) {
      setHasScrolled(true);
    }
  };
  return jsx(Dialog, _extends({
    isOpen: isOpen,
    zIndex: zIndex,
    onClose: onClose,
    maxWidth: maxWidth,
    maxHeight: maxHeight
  }, rest), jsx(Flex, {
    flexDirection: "column"
  }, jsx(Text, {
    weight: "semiBold",
    color: 'primaryHeading',
    type: "largeBody",
    content: title,
    inset: {
      bottom: 'x2'
    }
  }), jsx(Scrollable, {
    maxHeight: 216,
    onChange: function (progress) {
      return handleScroll(progress);
    }
  }, jsx(Text, {
    content: description,
    type: "caption",
    color: "primaryBody"
  }))), jsx(Flex, null, additionalLink && jsx(Flex, {
    flexDirection: {
      xSmall: additionalLink ? 'column-reverse' : 'row',
      small: 'row'
    },
    justifyContent: {
      xSmall: 'center',
      small: additionalLink ? 'space-between' : 'flex-end'
    },
    outset: {
      top: 'x3'
    },
    alignItems: 'center'
  }, jsx(Anchor, {
    outset: {
      top: {
        xSmall: 'x2',
        small: 'x0'
      }
    },
    onClick: additionalLink.onClick,
    size: "xSmall",
    title: additionalLink.title,
    color: "anchorBlue",
    cursor: "pointer",
    isDecorated: true
  })), jsx(Flex, {
    width: "100%",
    justifyContent: hasStackedActions ? 'center' : isPrimaryAccept ? 'flex-start' : 'flex-end',
    outset: hasStackedActions ? {
      vertical: 'x2'
    } : {
      horizontal: 'x2'
    },
    flexDirection: hasStackedActions ? isPrimaryAccept ? 'column-reverse' : 'column' : isPrimaryAccept ? 'row-reverse' : 'row'
  }, jsx(Flex, {
    outset: hasStackedActions ? {
      vertical: 'x2'
    } : {
      horizontal: 'x2'
    }
  }, jsx(Button, {
    fullWidth: hasStackedActions,
    id: accept.id,
    type: isPrimaryAccept ? 'primary' : 'shadow',
    outlined: isPrimaryAccept,
    onClick: accept.onClick,
    size: "small",
    title: accept.title,
    hover: !isPrimaryAccept,
    disabled: accept.isDisabled || (requireScroll ? isPrimaryAccept ? hasScrolled ? false : true : false : false)
  })), decline && jsx(Button, {
    fullWidth: hasStackedActions,
    id: decline.id,
    type: isPrimaryAccept ? 'shadow' : 'primary',
    outlined: isPrimaryAccept ? false : true,
    onClick: decline.onClick,
    size: "small",
    title: decline.title,
    hover: isPrimaryAccept,
    disabled: decline.isDisabled || (requireScroll ? isPrimaryAccept ? false : hasScrolled ? false : true : false)
  }))));
};

var IncrementSetting = function (_a) {
  var value = _a.value,
    title = _a.title,
    setValue = _a.setValue,
    min = _a.min,
    max = _a.max,
    _b = _a.isDisabled,
    isDisabled = _b === void 0 ? false : _b,
    layout = _a.layout,
    activeColor = _a.activeColor,
    _c = _a.inputWidth,
    inputWidth = _c === void 0 ? 88 : _c,
    _d = _a.sliderOrigin,
    sliderOrigin = _d === void 0 ? 'left' : _d,
    outset = _a.outset;
  var spaceOffset = inputWidth + 24;
  var _e = useResizeObserver(),
    ref = _e.ref,
    width = _e.width;
  var titleProps = typeof title !== 'string' ? title : {};
  return jsx(Div, {
    ref: ref,
    display: "flex",
    flexDirection: layout === 'block' ? 'column' : 'row',
    alignItems: layout === 'inline' ? 'center' : 'flex-start',
    flexWrap: layout === 'inline' ? 'nowrap' : 'wrap',
    inset: "x_25",
    outset: outset
  }, jsx(Div, {
    width: layout === 'inlineBlock' ? '100%' : 'auto'
  }, jsx(Text, _extends({
    type: "footnote",
    outset: {
      bottom: layout !== 'inline' ? 'x1' : 'x0'
    }
  }, titleProps), typeof title === 'string' && title)), jsx(Spacer, {
    orientation: "horizontal",
    space: layout === 'inline' ? 'x3' : 'x0'
  }), jsx(Input, {
    type: "number",
    value: value.toString(),
    onChange: function (value) {
      return setValue(parseInt(value));
    },
    min: min,
    max: max,
    isDisabled: isDisabled,
    width: inputWidth,
    height: 40,
    focusColor: activeColor,
    isTypingDisabled: true
  }), jsx(Spacer, {
    orientation: "horizontal",
    space: layout !== 'block' ? 'x3' : 'x0'
  }), width && jsx(Div, {
    width: layout === 'block' ? '100%' : "calc(100% - " + spaceOffset + "px)"
  }, jsx(Slider, {
    value: value,
    min: min,
    max: max,
    setValue: function (value) {
      return setValue(value);
    },
    isDisabled: isDisabled,
    activeColor: activeColor,
    origin: sliderOrigin,
    width: width - spaceOffset
  })));
};

var Pill = function (_a) {
  var title = _a.title,
    items = _a.items,
    _b = _a.flexDirection,
    flexDirection = _b === void 0 ? 'row' : _b,
    boxShadow = _a.boxShadow,
    children = _a.children;
  var _c = useState(-1),
    hoverIndex = _c[0],
    setHoverIndex = _c[1];
  var tooltipVariants = {
    hoverOn: {
      opacity: 1
    },
    hoverOff: {
      opacity: 0
    }
  };
  var height = '44px';
  return jsx(React.Fragment, null, jsx(Flex, {
    height: height,
    borderRadius: "circle",
    backgroundColor: "foreground",
    justifyContent: "center",
    alignItems: "center",
    inset: {
      horizontal: 'x1'
    },
    flexDirection: flexDirection,
    boxShadow: boxShadow
  }, children, jsx(Flex, {
    flexDirection: "row"
  }, items.map(function (item, index) {
    var _a;
    return jsx(Div, {
      onHoverStart: function () {
        setHoverIndex(index + 1);
      },
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      flexDirection: "column",
      key: item.title,
      position: "relative",
      onHoverEnd: function () {
        setHoverIndex(-1);
      }
    }, hoverIndex === index + 1 && jsx(Flex, {
      variants: tooltipVariants,
      animate: hoverIndex ? 'hoverOn' : 'hoverOff',
      initial: 'hoverOff',
      transition: {
        ease: 'easeIn',
        duration: 0.2
      },
      top: "-" + height,
      position: "absolute"
    }, jsx(Tooltip, {
      direction: "bottom",
      title: item.title,
      background: "dark"
    })), jsx(Div, {
      inset: "x1"
    }, jsx(IconLink, {
      icon: item.icon,
      iconOrientation: item.iconOrientation ? item.iconOrientation : 'left',
      size: "small",
      fill: "default",
      activeColor: (_a = item.activeColor) !== null && _a !== void 0 ? _a : 'success',
      hasBackgroundColor: false,
      onClick: function () {
        item.onClick();
      },
      isActive: item.isActive
    })));
  }))), title && jsx(Text, {
    type: "body"
  }, title));
};

var Page = function (_a) {
  var width = _a.width,
    _b = _a.direction,
    direction = _b === void 0 ? 'horizontal' : _b,
    contentWidth = _a.contentWidth,
    contentHeight = _a.contentHeight,
    alignment = _a.alignment,
    currentPage = _a.currentPage,
    gap = _a.gap,
    padding = _a.padding,
    paddingTop = _a.paddingTop,
    paddingRight = _a.paddingRight,
    paddingBottom = _a.paddingBottom,
    paddingLeft = _a.paddingLeft,
    momentum = _a.momentum,
    animationType = _a.animationType,
    directionLock = _a.directionLock,
    dragEnabled = _a.dragEnabled,
    wheelEnabled = _a.wheelEnabled,
    contentOffsetX = _a.contentOffsetX,
    contentOffsetY = _a.contentOffsetY,
    children = _a.children,
    height = _a.height;
  return jsx(Page$1, {
    width: width,
    height: height,
    direction: direction,
    contentWidth: contentWidth,
    contentHeight: contentHeight,
    alignment: alignment,
    currentPage: currentPage,
    gap: gap,
    padding: padding,
    paddingPerSide: true,
    paddingTop: paddingTop,
    paddingRight: paddingBottom,
    paddingBottom: paddingRight,
    paddingLeft: paddingLeft,
    momentum: momentum,
    dragEnabled: dragEnabled,
    defaultEffect: animationType,
    directionLock: directionLock,
    wheelEnabled: wheelEnabled,
    contentOffsetX: contentOffsetX,
    contentOffsetY: contentOffsetY
  }, React.Children.map(children, function (child, index) {
    return jsx(Div, {
      key: index
    }, child);
  }));
};

var _emotionSourceMap$9 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFHaUQiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgX19tYWtlVGVtcGxhdGVPYmplY3QgfSBmcm9tIFwidHNsaWJcIjtcclxuaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vY29yZSc7XHJcbmV4cG9ydCBkZWZhdWx0IHtcclxuICAgIGljb25Db250YWluZXI6IGZ1bmN0aW9uIChob2NFbnRpdHkpIHsgcmV0dXJuIGNzcyh0ZW1wbGF0ZU9iamVjdF8xIHx8ICh0ZW1wbGF0ZU9iamVjdF8xID0gX19tYWtlVGVtcGxhdGVPYmplY3QoW1wiXFxuICAgICY6aG92ZXIge1xcbiAgICAgIGJhY2tncm91bmQ6IFwiLCBcIjtcXG4gICAgfVxcbiAgXCJdLCBbXCJcXG4gICAgJjpob3ZlciB7XFxuICAgICAgYmFja2dyb3VuZDogXCIsIFwiO1xcbiAgICB9XFxuICBcIl0pKSwgaG9jRW50aXR5LmNvbG9ycy5iYWNrZ3JvdW5kKTsgfSxcclxufTtcclxudmFyIHRlbXBsYXRlT2JqZWN0XzE7XHJcbi8vIyBzb3VyY2VNYXBwaW5nVVJMPXN0eWxlcy5qcy5tYXAiXX0= */";
var styles$8 = {
  iconContainer: function (hocEntity) {
    return /*#__PURE__*/css(templateObject_1$9 || (templateObject_1$9 = __makeTemplateObject(["\n    &:hover {\n      background: ", ";\n    }\n  " + _emotionSourceMap$9], ["\n    &:hover {\n      background: ", ";\n    }\n  " + _emotionSourceMap$9])), hocEntity.colors.background);
  }
};
var templateObject_1$9;

var Zoom = function (_a) {
  var onChange = _a.onChange,
    items = _a.items,
    dropDownMaxHeight = _a.dropDownMaxHeight,
    background = _a.background,
    selectedOptionIndex = _a.selectedOptionIndex,
    _b = _a.isDropDownAbove,
    isDropDownAbove = _b === void 0 ? true : _b;
  var hocs = useEntities().hocs;
  var hasHitMax = selectedOptionIndex === 0;
  var hasHitMin = selectedOptionIndex === items.length - 1;
  return jsx(AnimateSharedLayout, null, jsx(Flex, {
    inset: "x_5",
    borderRadius: "circle",
    height: "40px",
    justifyContent: "space-between",
    alignItems: "center",
    boxShadow: "light",
    backgroundColor: background,
    layout: true
  }, jsx(Flex, {
    css: styles$8.iconContainer(hocs),
    borderRadius: "circle",
    cursor: hasHitMin ? 'not-allowed' : 'pointer',
    alignItems: "center",
    onClick: selectedOptionIndex < items.length - 1 ? function () {
      return onChange(selectedOptionIndex + 1);
    } : undefined,
    opacity: hasHitMin ? 0.5 : 1,
    inset: "x_75"
  }, jsx(Icon, {
    name: "minus",
    size: "xSmall",
    primaryColor: "default"
  })), jsx(Div, {
    outset: {
      top: 'x1',
      right: 'x_25',
      bottom: 'x1',
      left: 'x_25'
    },
    layout: true
  }, jsx(Select, {
    selectedOptionIndex: selectedOptionIndex,
    onChange: function (selectedOptionIndex) {
      return onChange(selectedOptionIndex);
    },
    alignment: "center",
    options: items,
    type: "text",
    isDropDownAbove: isDropDownAbove,
    dropDownMaxHeight: dropDownMaxHeight,
    selectHeight: '35px'
  })), jsx(Flex, {
    css: styles$8.iconContainer(hocs),
    inset: "x_75",
    borderRadius: "circle",
    cursor: hasHitMax ? 'not-allowed' : 'pointer',
    alignItems: "center",
    opacity: hasHitMax ? 0.5 : 1,
    onClick: selectedOptionIndex > 0 ? function () {
      return onChange(selectedOptionIndex - 1);
    } : undefined
  }, jsx(Icon, {
    name: "plus",
    size: "xSmall",
    primaryColor: "default"
  }))));
};

var _emotionSourceMap$8 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFHd0IiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgX19tYWtlVGVtcGxhdGVPYmplY3QgfSBmcm9tIFwidHNsaWJcIjtcclxuaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vY29yZSc7XHJcbmV4cG9ydCBkZWZhdWx0IHtcclxuICAgIHRodW1ibmFpbENvbnRhaW5lcjogY3NzKHRlbXBsYXRlT2JqZWN0XzEgfHwgKHRlbXBsYXRlT2JqZWN0XzEgPSBfX21ha2VUZW1wbGF0ZU9iamVjdChbXCJcXG4gICAgOjotd2Via2l0LXNjcm9sbGJhciB7XFxuICAgICAgd2lkdGg6IDA7XFxuICAgICAgYmFja2dyb3VuZDogdHJhbnNwYXJlbnQ7XFxuICAgIH1cXG4gICAgc2Nyb2xsYmFyLXdpZHRoOiBub25lO1xcbiAgXCJdLCBbXCJcXG4gICAgOjotd2Via2l0LXNjcm9sbGJhciB7XFxuICAgICAgd2lkdGg6IDA7XFxuICAgICAgYmFja2dyb3VuZDogdHJhbnNwYXJlbnQ7XFxuICAgIH1cXG4gICAgc2Nyb2xsYmFyLXdpZHRoOiBub25lO1xcbiAgXCJdKSkpLFxyXG59O1xyXG52YXIgdGVtcGxhdGVPYmplY3RfMTtcclxuLy8jIHNvdXJjZU1hcHBpbmdVUkw9c3R5bGVzLmpzLm1hcCJdfQ== */";
var styles$7 = {
  thumbnailContainer: /*#__PURE__*/css(templateObject_1$8 || (templateObject_1$8 = __makeTemplateObject(["\n    ::-webkit-scrollbar {\n      width: 0;\n      background: transparent;\n    }\n    scrollbar-width: none;\n  ;label:thumbnailContainer;" + _emotionSourceMap$8], ["\n    ::-webkit-scrollbar {\n      width: 0;\n      background: transparent;\n    }\n    scrollbar-width: none;\n  ;label:thumbnailContainer;" + _emotionSourceMap$8])))
};
var templateObject_1$8;

var DropperIcon = function (props) {
  return jsx(Flex, {
    backgroundColor: 'background',
    borderRadius: "small",
    width: "40px",
    height: "40px",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
    onClick: function () {
      return props.onClick();
    }
  }, jsx(Icon, {
    name: "dropper",
    size: "small",
    primaryColor: !props.disabled ? 'primaryBodyText' : 'primaryBrand'
  }));
};
var QuantumColorPicker = function (_a) {
  var _b, _c, _d;
  var color = _a.color,
    onChange = _a.onChange,
    convertedColorCallback = _a.convertedColorCallback,
    _e = _a.convertedColorType,
    convertedColorType = _e === void 0 ? 'hex' : _e,
    _f = _a.showEyeDropper,
    showEyeDropper = _f === void 0 ? false : _f,
    palette = _a.palette;
  var _g = useResizeObserver(),
    ref = _g.ref,
    width = _g.width;
  var formattedHexValue = function (value) {
    return value.startsWith('#') ? value : "#" + value;
  };
  var handleOnChange = function (color) {
    try {
      var convertedHex = Color$1(color);
      if (convertedColorType === 'hex') {
        convertedColorCallback === null || convertedColorCallback === void 0 ? void 0 : convertedColorCallback(color);
      } else if (convertedColorType === 'hsl') {
        var hsl = {
          h: convertedHex.hue(),
          s: convertedHex.saturationl(),
          l: convertedHex.lightness()
        };
        convertedColorCallback === null || convertedColorCallback === void 0 ? void 0 : convertedColorCallback(hsl);
      } else {
        var rgb = {
          r: convertedHex.red(),
          g: convertedHex.green(),
          b: convertedHex.blue()
        };
        convertedColorCallback === null || convertedColorCallback === void 0 ? void 0 : convertedColorCallback(rgb);
      }
    } catch (err) {
      // Fail silently when color cannot be converted
    } finally {
      onChange(color);
    }
  };
  var paletteArray = (_b = palette === null || palette === void 0 ? void 0 : palette.colors) !== null && _b !== void 0 ? _b : Array.from(Array((_c = palette === null || palette === void 0 ? void 0 : palette.count) !== null && _c !== void 0 ? _c : 12));
  return jsx(Div, {
    inset: "x2",
    display: "flex",
    borderRadius: "default",
    boxShadow: "mediumDark",
    flexDirection: "column",
    width: "100%",
    ref: ref
  }, jsx(HexColorPicker, {
    style: {
      width: "100%"
    },
    color: formattedHexValue(color),
    onChange: function (value) {
      return handleOnChange(formattedHexValue(value));
    }
  }), jsx(Spacer, {
    space: "x2_5"
  }), jsx(Flex, {
    width: "100%",
    alignItems: "center",
    justifyContent: "space-between"
  }, showEyeDropper && jsx(EyeDropper, {
    customComponent: DropperIcon,
    cursorActive: "crosshair",
    once: true,
    onChange: function (value) {
      return handleOnChange(formattedHexValue(value.hex));
    }
  }), width && jsx(Input, {
    height: 40,
    width: showEyeDropper ? width - 54 : width,
    value: color,
    onChange: function (value) {
      return handleOnChange(formattedHexValue(value));
    },
    type: "text"
  })), (palette && palette.colors || (palette === null || palette === void 0 ? void 0 : palette.isLoading)) && jsx(React.Fragment, null, jsx(Spacer, {
    space: "x1"
  }), jsx(Text, {
    type: "caption",
    content: (_d = palette.title) !== null && _d !== void 0 ? _d : 'Presets',
    color: "primaryBody",
    lineHeight: 1.2,
    inset: {
      left: 'x1',
      vertical: 'x1'
    },
    weight: "semiBold"
  }), jsx(Flex, {
    css: styles$7.thumbnailContainer,
    overflowX: "auto",
    width: "100%",
    justifyContent: "flex-start",
    flexWrap: 'wrap',
    alignItems: "center",
    inset: {
      bottom: '6px'
    }
  }, paletteArray.map(function (paletteColor, index) {
    return jsx(ColorThumbnail, {
      key: index,
      isRounded: true,
      isLoading: palette.isLoading,
      backgroundColor: paletteColor,
      isSelected: paletteColor === color,
      onSelect: function (value) {
        handleOnChange(formattedHexValue(value));
      },
      outset: {
        horizontal: 'x1',
        top: 'x1'
      }
    });
  }))));
};

var AnimatedGrid = function (_a) {
  var _b;
  var gridImages = _a.gridImages,
    _c = _a.shouldAnimate,
    shouldAnimate = _c === void 0 ? true : _c,
    _d = _a.animationDirection,
    animationDirection = _d === void 0 ? 'vertical' : _d,
    _e = _a.rotation,
    rotation = _e === void 0 ? {
      degrees: 0,
      offset: 0
    } : _e,
    _f = _a.gap,
    gap = _f === void 0 ? 24 : _f,
    _g = _a.columnSize,
    columnSize = _g === void 0 ? 240 : _g,
    _h = _a.rowSize,
    rowSize = _h === void 0 ? 240 : _h,
    _j = _a.maxWidth,
    maxWidth = _j === void 0 ? 1000 : _j,
    children = _a.children;
  var gridChildren = React.Children.toArray(children);
  var numChildren = gridChildren.length + (gridImages ? gridImages.length : 0);
  var rowHeight = rowSize + gap;
  var colWidth = columnSize + gap;
  var numCols = Math.floor(maxWidth / colWidth);
  var numRows = numChildren / numCols;
  var gridHeight = numRows * rowHeight;
  var yAnimationValue = gridHeight - rowHeight * 3 - ((_b = rotation.offset) !== null && _b !== void 0 ? _b : 0);
  return jsx(Div, {
    transform: "rotateZ(" + rotation.degrees + "deg)",
    width: "100%"
  }, jsx(Flex, {
    animate: shouldAnimate ? animationDirection === 'vertical' ? {
      y: "-" + yAnimationValue + "px"
    } : {
      x: maxWidth + "px"
    } : undefined,
    transition: {
      repeatType: 'mirror',
      type: 'tween',
      duration: animationDirection === 'vertical' ? gridHeight / 45 : maxWidth / 45,
      ease: 'linear'
    },
    width: "100%",
    justifyContent: "center",
    position: "relative",
    left: rotation.offset + "px",
    top: "-" + rotation.offset + "px"
  }, jsx(Grid, {
    gap: gap,
    columnSize: columnSize,
    rowSize: rowSize,
    maxWidth: maxWidth
  }, gridImages && gridImages.map(function (gridImage, index) {
    return jsx(GridItem, {
      key: index,
      gridColSpan: gridImage.colSpan,
      gridRowSpan: gridImage.rowSpan
    }, jsx(Div, {
      height: "100%",
      width: "100%",
      borderRadius: "default"
    }, jsx(Image$1, {
      height: "100%",
      width: "100%",
      image: {
        url: gridImage.url
      },
      isActive: true
    })));
  }), gridChildren.length > 0 && gridChildren.map(function (child, index) {
    return jsx(GridItem, {
      key: index
    }, jsx(Div, {
      height: "100%",
      width: "100%",
      borderRadius: "default"
    }, child));
  }))));
};

var ButtonWithMenu = function (_a) {
  var menuLocation = _a.menuLocation,
    menuOptions = _a.menuOptions,
    isExpanded = _a.isExpanded,
    isLoading = _a.isLoading,
    loadingMessage = _a.loadingMessage,
    onClick = _a.onClick,
    type = _a.type,
    title = _a.title,
    _b = _a.size,
    size = _b === void 0 ? 'small' : _b,
    outlined = _a.outlined;
  var variants = {
    expanded: {
      transform: menuLocation === 'top' ? 'rotate(270deg)' : 'rotate(90deg)'
    },
    collapsed: {
      transform: menuLocation === 'top' ? 'rotate(90deg)' : 'rotate(270deg)'
    }
  };
  var menuVariants = {
    expanded: {
      opacity: 1
    },
    collapsed: {
      opacity: 0
    }
  };
  var mapPositionToButtonSize = function (size) {
    switch (size) {
      case 'xSmall':
        return '48px';
      case 'small':
        return '56px';
      case 'medium':
        return '64px';
      case 'large':
        return '72px';
      case 'xLarge':
        return '88px';
      default:
        return '56px';
    }
  };
  return jsx(AnimatePresence, null, jsx(Flex, {
    position: "relative",
    justifyContent: "center"
  }, jsx(Button, {
    onClick: onClick,
    type: type,
    size: size,
    outlined: outlined,
    disabled: isLoading
  }, jsx(Flex, {
    alignItems: "center",
    width: "100%"
  }, isLoading ? loadingMessage : title, jsx(Spacer, {
    space: "x1_5",
    orientation: "horizontal"
  }), isLoading ? jsx(LoadingSpinner, {
    size: size === 'xSmall' || size === 'small' ? 'small' : 'medium',
    hasNoColor: true
  }) : jsx(Flex, {
    alignItems: "center",
    justifyContent: "center",
    variants: variants,
    initial: isExpanded ? 'expanded' : 'collapsed',
    animate: isExpanded ? 'expanded' : 'collapsed'
  }, jsx(Icon, {
    name: "chevron",
    orientation: "left",
    size: size === 'xSmall' || size === 'small' ? 14 : 16,
    primaryColor: outlined ? 'primaryBrand' : type === 'shadow' ? 'primaryHeadingText' : 'inverseBodyText'
  })))), isExpanded && jsx(Div, {
    position: "absolute",
    inset: "x1",
    bottom: menuLocation === 'top' ? mapPositionToButtonSize(size) : undefined,
    top: menuLocation === 'bottom' ? mapPositionToButtonSize(size) : undefined,
    variants: menuVariants,
    initial: "collapsed",
    animate: isExpanded ? 'expanded' : 'collapsed',
    exit: "collapsed",
    minWidth: "max-content",
    style: {
      whiteSpace: 'nowrap'
    }
  }, jsx(Menu, null, jsx(Div, {
    inset: {
      top: 'x1',
      bottom: 'x1'
    }
  }, jsx(MenuSection, {
    divider: false
  }, menuOptions.map(function (option, index) {
    return jsx(MenuItem, {
      key: index,
      onClick: function () {
        option.onClick();
        onClick();
      },
      title: option.title,
      icon: option.icon
    });
  })))))));
};

var Stepper = function (_a) {
  var _b, _c;
  var canContinue = _a.canContinue,
    setCanContinue = _a.setCanContinue,
    activeItemIndex = _a.activeItemIndex,
    setActiveItemIndex = _a.setActiveItemIndex,
    onStepsComplete = _a.onStepsComplete,
    completionMessage = _a.completionMessage,
    children = _a.children,
    _d = _a.isSkippable,
    isSkippable = _d === void 0 ? false : _d,
    skipButton = _a.skipButton;
  var stepperChildren = React.Children.toArray(children);
  var percentage = Math.round(activeItemIndex / stepperChildren.length * 100);
  var orderedChildren = stepperChildren.sort(function (a, b) {
    return a.props.order && b.props.order ? a.props.order > b.props.order ? 1 : -1 : 1;
  });
  var handleStepChange = function (direction) {
    setActiveItemIndex(activeItemIndex + direction);
  };
  var handleIsComplete = function (direction) {
    if (direction === 1) {
      if (activeItemIndex + 1 === stepperChildren.length) {
        setIsComplete(true);
      }
      if (isComplete) {
        onStepsComplete();
      } else {
        setCanContinue(false);
      }
    } else {
      if (isComplete) {
        setIsComplete(false);
      }
    }
  };
  var handleSkip = function () {
    if (activeItemIndex + 1 === stepperChildren.length) {
      setIsComplete(true);
      return;
    }
    handleStepChange(1);
  };
  var handleContinue = function () {
    var direction = 1;
    handleStepChange(direction);
    handleIsComplete(direction);
    setCanContinue(false);
  };
  var handleBack = function () {
    if (isComplete) {
      setIsComplete(false);
    }
    handleStepChange(-1);
  };
  var _e = useState(false),
    isComplete = _e[0],
    setIsComplete = _e[1];
  return jsx(Flex, {
    height: "100%",
    width: "100%",
    position: "relative",
    flexDirection: "column"
  }, jsx(Flex, {
    width: "100%",
    height: "100%",
    flexDirection: "column",
    overflowY: "auto"
  }, isComplete ? jsx(Flex, {
    height: "100%",
    width: "100%",
    justifyContent: "center",
    alignItems: "center"
  }, typeof completionMessage === 'string' ? jsx(Text, {
    type: {
      xSmall: 'body',
      medium: 'largeBody'
    }
  }, completionMessage) : jsx(React.Fragment, null, completionMessage)) : orderedChildren[activeItemIndex]), jsx(Spacer, {
    orientation: "vertical",
    space: "x1"
  }), jsx(Flex, {
    flexDirection: "column"
  }, jsx(Spacer, {
    orientation: "vertical",
    space: "x2"
  }), jsx(VStack, {
    gap: "x4"
  }, jsx(Flex, {
    width: "100%",
    justifyContent: "flex-end"
  }, jsx(HStack, {
    gap: "x2",
    justify: "flex-end"
  }, activeItemIndex !== 0 && jsx(React.Fragment, null, jsx(Button, {
    size: "small",
    type: "primary",
    outlined: true,
    onClick: function () {
      handleBack();
    }
  }, "Go Back")), isSkippable && !isComplete && jsx(Button, {
    size: "small",
    type: (_b = skipButton === null || skipButton === void 0 ? void 0 : skipButton.type) !== null && _b !== void 0 ? _b : 'primary',
    outlined: true,
    onClick: function () {
      return handleSkip();
    },
    disabled: canContinue
  }, (_c = skipButton === null || skipButton === void 0 ? void 0 : skipButton.title) !== null && _c !== void 0 ? _c : 'Skip'), jsx(Button, {
    size: "small",
    type: "primary",
    onClick: function () {
      handleContinue();
    },
    disabled: isComplete ? false : !canContinue
  }, isComplete ? 'Finish' : 'Continue'))), jsx(Flex, {
    width: "100%",
    justifyContent: "flex-end",
    alignItems: "center",
    height: "x4"
  }, jsx(Flex, {
    width: "100%"
  }, jsx(Progress, {
    percentage: isComplete ? 100 : percentage,
    hasLabel: false
  })), !isComplete && jsx(React.Fragment, null, jsx(Spacer, {
    space: "x4",
    orientation: "horizontal"
  }), jsx(Flex, null, jsx(Text, {
    type: "caption",
    whiteSpace: "nowrap"
  }, "Step ", activeItemIndex + 1, " of ", stepperChildren.length)))))));
};

var PricingTile = function (_a) {
  var _b;
  var title = _a.title,
    price = _a.price,
    features = _a.features,
    _c = _a.accentColor,
    accentColor = _c === void 0 ? 'primaryBrand' : _c,
    _d = _a.backgroundColor,
    backgroundColor = _d === void 0 ? 'inverseBody' : _d,
    _e = _a.isFeatured,
    isFeatured = _e === void 0 ? false : _e,
    _f = _a.featuredItemStyles,
    featuredItemStyles = _f === void 0 ? {
      backgroundColor: 'primaryBrand',
      accentColor: 'inverseBody',
      textColor: 'white'
    } : _f,
    _g = _a.textColor,
    textColor = _g === void 0 ? 'gray' : _g,
    description = _a.description,
    priceSubtext = _a.priceSubtext,
    billingInterval = _a.billingInterval,
    primaryAction = _a.primaryAction,
    secondaryAction = _a.secondaryAction,
    children = _a.children,
    _h = _a.hasShadow,
    hasShadow = _h === void 0 ? true : _h,
    disclaimer = _a.disclaimer,
    _j = _a.isSelected,
    isSelected = _j === void 0 ? false : _j,
    _k = _a.isFullLength,
    isFullLength = _k === void 0 ? false : _k;
  var hocs = useEntities().hocs;
  var getIconColorFromAccentColor = function (accentColor) {
    switch (accentColor) {
      case 'primaryBody':
        return 'primaryBodyText';
      case 'primaryHeading':
        return 'primaryHeadingText';
      case 'inverseBody':
        return 'inverseBodyText';
      case 'inverseHeading':
        return 'inverseHeadingText';
      default:
        return accentColor;
    }
  };
  var getButtonTypeFromAccentColor = function (accentColor) {
    switch (accentColor) {
      case 'primaryBrand':
      case 'primaryBody':
      case 'primaryHeading':
        return 'primary';
      case 'secondaryBrand':
        return 'secondary';
      case 'inverseBody':
      case 'inverseHeading':
        return 'shadow';
      default:
        return accentColor;
    }
  };
  var isFeatureWithSubFeaturesType = function (valueToCheck) {
    var value = valueToCheck;
    return value.feature !== undefined && value.subFeatures !== undefined;
  };
  var featureTextColor = isFeatured ? featuredItemStyles.textColor === 'white' ? 'inverseBody' : 'primaryBody' : textColor === 'white' ? 'inverseBody' : 'primaryBody';
  var _l = useState(false),
    isDisclaimerOpen = _l[0],
    setDisclaimerOpen = _l[1];
  return jsx(Flex, {
    id: "pricingTileContainerQDS",
    inset: "x5",
    backgroundColor: isFeatured ? hocs.textColors[featuredItemStyles.backgroundColor] : hocs.textColors[backgroundColor],
    width: "100%",
    position: "relative",
    height: isFullLength ? 'auto' : 'fit-content',
    borderStyle: 'solid',
    borderColor: isSelected ? {
      swatch: 'success',
      shade: 'base'
    } : 'transparent',
    borderWidth: 'default',
    borderRadius: "default",
    boxShadow: hasShadow ? 'mediumLight' : '',
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "center",
    minWidth: {
      xSmall: '100%',
      small: '400px'
    },
    outset: {
      horizontal: {
        xSmall: 'x2',
        small: 'x0'
      },
      vertical: {
        xSmall: 'x1',
        small: 'x0'
      }
    }
  }, jsx(VStack, {
    gap: "x4"
  }, jsx(VStack, {
    gap: "x2"
  }, jsx(Flex, {
    justifyContent: billingInterval ? 'space-between' : 'flex-start',
    width: "100%",
    alignContent: "flex-start"
  }, isSelected && jsx(Div, {
    right: "x1",
    position: "absolute",
    top: "x1"
  }, jsx(AnimatedCheckMark, {
    color: "accent"
  })), jsx(VStack, {
    gap: "x2"
  }, jsx(Text, {
    type: {
      xSmall: 'subtitle',
      medium: 'title'
    },
    color: isFeatured ? featuredItemStyles.accentColor : accentColor,
    weight: "bold",
    content: title
  }), jsx(Text, {
    lineHeight: 1.5,
    type: "caption",
    color: featureTextColor
  }, description !== null && description !== void 0 ? description : children), jsx(Flex, {
    width: "100%",
    alignItems: "center"
  }, price && jsx(Text, {
    type: {
      xSmall: 'subtitle',
      medium: 'title'
    },
    color: featureTextColor,
    weight: "bold",
    content: price
  }), priceSubtext && jsx(React.Fragment, null, jsx(Spacer, {
    space: "x2",
    orientation: "horizontal"
  }), jsx(Flex, {
    opacity: 0.75
  }, jsx(Text, {
    type: {
      xSmall: 'footnote',
      medium: 'caption'
    },
    color: featureTextColor
  }, priceSubtext))))), billingInterval && jsx(Flex, {
    height: "100%"
  }, jsx(Spacer, {
    orientation: "horizontal",
    space: "x2"
  }), jsx(Flex, {
    borderRadius: "circle",
    alignItems: "center",
    height: "fit-content",
    backgroundColor: backgroundColor !== 'inverseBody' && backgroundColor !== 'inverseHeading' || isFeatured ? '#FFFFFF44' : undefined,
    boxShadow: !isFeatured ? 'light' : undefined,
    inset: {
      vertical: 'x_5',
      horizontal: 'x2'
    }
  }, jsx(Text, {
    type: {
      xSmall: 'caption',
      medium: 'body'
    },
    color: featureTextColor
  }, billingInterval))))), jsx(Flex, {
    width: "100%"
  }, jsx(VStack, {
    gap: 'x2'
  }, features.map(function (feature, index) {
    return jsx(Flex, {
      flexDirection: 'row',
      key: index
    }, jsx(Flex, {
      alignItems: "start",
      flexDirection: 'row'
    }, jsx(Div, {
      position: 'relative',
      inset: {
        top: 'x_5'
      }
    }, jsx(Icon, {
      name: isFeatureWithSubFeaturesType(feature) && (feature === null || feature === void 0 ? void 0 : feature.isNotIncluded) ? 'closeCircle' : 'check',
      size: "xSmall",
      primaryColor: getIconColorFromAccentColor(isFeatured ? featuredItemStyles.accentColor : accentColor)
    })), jsx(Spacer, {
      orientation: "horizontal",
      space: "x2_5"
    })), isFeatureWithSubFeaturesType(feature) ? jsx(React.Fragment, null, jsx(Div, {
      display: "inline-block",
      flexDirection: 'row',
      flexWrap: 'wrap'
    }, jsx(Text, {
      type: {
        xSmall: 'caption',
        medium: 'body'
      },
      color: featureTextColor,
      weight: "bold"
    }, feature.feature), jsx(List, {
      orientation: "vertical",
      alignment: "left",
      width: "100%",
      outset: {
        left: 'x2'
      },
      listStyle: "circle"
    }, feature.subFeatures.map(function (subFeature, index) {
      return jsx(ListItem, {
        orientation: "vertical",
        key: index,
        inset: {
          right: 'x4',
          left: 'x0',
          top: '0',
          bottom: '0'
        }
      }, jsx(Text, {
        color: featureTextColor,
        type: "body"
      }, subFeature));
    })))) : jsx(Text, {
      type: {
        xSmall: 'caption',
        medium: 'body'
      },
      color: featureTextColor,
      weight: "bold"
    }, feature));
  }))), primaryAction && jsx(Flex, {
    width: "100%"
  }, jsx(Button, {
    fullWidth: true,
    title: primaryAction.text,
    size: "medium",
    onClick: primaryAction.onClick,
    disabled: primaryAction.isDisabled,
    type: getButtonTypeFromAccentColor(isFeatured ? featuredItemStyles.accentColor : accentColor),
    textColorOverride: primaryAction.shouldTextMatchBackground && isFeatured ? featuredItemStyles.backgroundColor : primaryAction.shouldTextMatchBackground ? backgroundColor : undefined
  })), secondaryAction && jsx(Flex, {
    justifyContent: 'center',
    alignItems: 'center'
  }, jsx(Anchor, {
    title: secondaryAction.text,
    onClick: secondaryAction.isDisabled ? undefined : secondaryAction.onClick,
    color: secondaryAction.shouldTextMatchBackground && isFeatured ? featuredItemStyles.backgroundColor : secondaryAction.shouldTextMatchBackground ? backgroundColor : undefined,
    isDecorated: true,
    target: '_blank'
  })), disclaimer && jsx(Div, {
    onClick: function () {
      return setDisclaimerOpen(true);
    }
  }, jsx(Anchor, {
    isDecorated: true,
    size: 'xSmall'
  }, (_b = disclaimer.linkTitle) !== null && _b !== void 0 ? _b : '**View Disclaimer')), jsx(AnimatePresence, null, isDisclaimerOpen && disclaimer && jsx(Div, {
    exit: {
      opacity: 0
    },
    initial: {
      opacity: 0
    },
    animate: {
      opacity: 1
    },
    display: 'flex',
    inset: "x5",
    backgroundColor: "foreground",
    borderRadius: "default",
    flexDirection: "column",
    justifyContent: "flex-start",
    position: 'absolute',
    top: '0px',
    left: '0px',
    width: '100%',
    height: '100%',
    transition: {
      duration: 0.25
    }
  }, jsx(Text, {
    type: 'title',
    weight: 'bold',
    content: disclaimer.termsTitle
  }), jsx(Text, {
    type: 'body',
    content: disclaimer.termsDescription
  }), jsx(Div, {
    outset: {
      top: 'auto'
    },
    width: '100%'
  }, jsx(Button, {
    type: 'secondary',
    onClick: function () {
      return setDisclaimerOpen(false);
    },
    fullWidth: true
  }, "Back"))))));
};

function usePrevious(value) {
  var ref = useRef();
  useEffect(function () {
    ref.current = value;
  });
  return ref.current;
}

var ContentPusher = function (_a) {
  var zones = _a.zones,
    zoneToOpen = _a.zoneToOpen,
    isOpen = _a.isOpen,
    setIsOpen = _a.setIsOpen,
    children = _a.children;
  var isVerticalZoneType = function (valueToCheck) {
    if (typeof valueToCheck === 'undefined') {
      return false;
    }
    return valueToCheck.height !== undefined;
  };
  var _b = useState(zoneToOpen),
    zoneControl = _b[0],
    setZoneControl = _b[1];
  var previousZone = usePrevious(zoneToOpen);
  useEffect(function () {
    if (previousZone === undefined) {
      return;
    }
    if (previousZone !== zoneToOpen) {
      setIsOpen(false);
    }
    // wait till closed to swap out zone component
    setTimeout(function () {
      setZoneControl(zoneToOpen);
    }, springTransition.duration * 1000);
  }, [zoneToOpen]); // eslint-disable-line
  var openZone = zones[zoneControl];
  return jsx(Flex, {
    width: "100%",
    height: "100%",
    position: "relative",
    overflow: "hidden"
  }, jsx(Flex, {
    animate: isOpen ? isVerticalZoneType(openZone) ? zoneControl === 'top' ? {
      y: openZone.height,
      x: 0
    } : {
      y: typeof openZone.height === 'string' ? "-" + openZone.height : -openZone.height,
      x: 0
    } : zoneControl === 'left' ? {
      x: openZone === null || openZone === void 0 ? void 0 : openZone.width,
      y: 0
    } : {
      x: typeof (openZone === null || openZone === void 0 ? void 0 : openZone.width) === 'string' ? "-" + ((openZone === null || openZone === void 0 ? void 0 : openZone.width) && openZone.width) : (openZone === null || openZone === void 0 ? void 0 : openZone.width) && -openZone.width,
      y: 0
    } : isVerticalZoneType(openZone) ? {
      y: 0,
      x: 0
    } : {
      x: 0,
      y: 0
    },
    transition: springTransition,
    width: "100%",
    height: "100%",
    position: "absolute",
    zIndex: 2,
    top: 0,
    left: 0,
    backgroundColor: "white"
  }, children), jsx(Flex, {
    width: "100%",
    height: "100%",
    justifyContent: zoneControl === 'left' ? 'flex-start' : zoneControl === 'right' ? 'flex-end' : 'center',
    alignItems: zoneControl === 'top' ? 'flex-start' : zoneControl === 'bottom' ? 'flex-end' : 'center'
  }, jsx(Flex, {
    position: "absolute",
    zIndex: 1,
    height: isVerticalZoneType(openZone) ? typeof openZone.height === 'string' ? openZone.height : openZone.height + "px" : '100%',
    width: isVerticalZoneType(openZone) ? '100%' : typeof (openZone === null || openZone === void 0 ? void 0 : openZone.width) === 'string' ? openZone.width : (openZone === null || openZone === void 0 ? void 0 : openZone.width) + "px"
  }, openZone === null || openZone === void 0 ? void 0 : openZone.component)));
};

var NavigationLink = function (_a) {
  var title = _a.title,
    isActive = _a.isActive,
    linkTitleColor = _a.linkTitleColor,
    href = _a.href,
    target = _a.target;
  return jsx(Flex, {
    height: "100%",
    alignItems: "center",
    cursor: "pointer",
    style: {
      userSelect: 'none'
    }
  }, href ? jsx(StaticLink, {
    href: href,
    title: title,
    linkTitleColor: linkTitleColor,
    target: target
  }) : jsx(Flex, {
    inset: {
      horizontal: 'x1'
    }
  }, jsx(Flex, {
    position: "relative",
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  }, jsx(Span, {
    style: {
      visibility: isActive ? 'hidden' : 'visible'
    }
  }, jsx(Text, {
    content: title,
    lineHeight: 1.6,
    type: 'body',
    weight: "regular",
    color: "primaryBody",
    inset: {
      horizontal: 'x_5'
    },
    whiteSpace: "nowrap"
  })), jsx(Span, {
    position: "absolute",
    style: {
      visibility: isActive ? 'visible' : 'hidden'
    }
  }, jsx(Text, {
    content: title,
    lineHeight: 1.6,
    type: 'body',
    weight: "semiBold",
    color: "primaryBrand"
  }))), jsx(Div, {
    outset: {
      top: 'x1_5'
    }
  }, jsx(Icon, {
    name: "chevron",
    orientation: isActive ? 'up' : 'down',
    size: 'xSmall',
    primaryColor: isActive ? 'primaryBrand' : 'primaryBodyText'
  }))));
};
function StaticLink(_a) {
  var href = _a.href,
    title = _a.title,
    target = _a.target;
  var _b = useState(false),
    isHovering = _b[0],
    setIsHovering = _b[1];
  return jsx(React.Fragment, null, jsx(Flex, {
    position: "relative",
    justifyContent: "center",
    alignItems: "center",
    onHoverStart: function () {
      return setIsHovering(true);
    },
    onHoverEnd: function () {
      return setIsHovering(false);
    },
    inset: "x1"
  }, jsx(Div, {
    style: {
      visibility: isHovering ? 'hidden' : 'visible'
    }
  }, jsx(Text, {
    content: title,
    lineHeight: 1.6,
    type: 'body',
    color: "primaryBody"
  })), jsx(Span, {
    position: "absolute",
    style: {
      visibility: isHovering ? 'visible' : 'hidden'
    }
  }, jsx(Anchor, {
    position: "relative",
    target: target !== null && target !== void 0 ? target : '_blank',
    href: href
  }, jsx(Text, {
    content: title,
    lineHeight: 1.6,
    type: 'body',
    color: "primaryBrand",
    weight: "semiBold"
  })))));
}

var borderBottom = {
  borderBottom: '1px solid #E3E3E3'
};
var MobileView = function (_a) {
  var isOpen = _a.isOpen,
    toggle = _a.toggle,
    links = _a.links;
  var _b = useState(true),
    showOpenSection = _b[0],
    setOpenSection = _b[1];
  var _c = useState(0),
    currentSection = _c[0],
    setCurrentSection = _c[1];
  var showAndSetSection = function (sectionIndex) {
    setCurrentSection(sectionIndex);
    setOpenSection(false);
  };
  var section = links ? links[currentSection] : undefined;
  var isLinkType = function (valueToCheck) {
    var link = valueToCheck;
    return link.href !== undefined && link.title !== undefined;
  };
  return jsx(Drawer, {
    isOpen: isOpen,
    onClick: toggle,
    location: 'left',
    openValue: 320,
    showTab: false,
    inset: "x0"
  }, jsx(ContentPusher, {
    zoneToOpen: "left",
    isOpen: showOpenSection,
    setIsOpen: setOpenSection,
    zones: {
      left: {
        component: jsx(Div, {
          width: "100%"
        }, jsx(VStack, {
          gap: "x0"
        }, links && links.map(function (item, i) {
          return isLinkType(item) ? typeof item.title === 'string' ? jsx(Anchor, _extends({
            inset: {
              horizontal: 'x3'
            },
            key: "pusher-anchor-" + i,
            target: "_blank"
          }, item, {
            title: item.title,
            height: "70px",
            display: "flex",
            alignItems: "center",
            style: borderBottom
          })) : jsx(Anchor, {
            href: item.href,
            onClick: item.onClick,
            target: item.target
          }, item.title) : jsx(Div, {
            cursor: "pointer",
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            flexDirection: "row",
            key: "pusher-" + i,
            onClick: function () {
              return showAndSetSection(i);
            },
            inset: {
              horizontal: 'x3'
            },
            height: "70px",
            alignItems: "center",
            style: borderBottom
          }, jsx(Text, {
            content: item.title,
            type: "body"
          }), jsx(Icon, {
            name: "rightChevron",
            size: "medium",
            primaryColor: "primaryBodyText"
          }));
        }))),
        width: '100%'
      }
    }
  }, jsx(Div, {
    width: "100%"
  }, section && !isLinkType(section) && jsx(React.Fragment, null, jsx(Div, {
    cursor: "pointer",
    display: "flex",
    width: "100%",
    flexDirection: "row",
    onClick: function () {
      return setOpenSection(true);
    },
    inset: {
      horizontal: 'x3'
    },
    height: "70px",
    alignItems: "center",
    style: borderBottom,
    backgroundColor: "#F5F6F7"
  }, jsx(Icon, {
    name: "leftChevron",
    size: "medium",
    primaryColor: "primaryBodyText"
  }), jsx(Text, {
    content: "Back",
    type: "body",
    inset: "x3"
  })), jsx(AccordionSection, {
    section: section
  })))));
};
var AccordionSection = function (_a) {
  var section = _a.section;
  var sections = [];
  if (section.featuredLinkColumn !== undefined) sections.push(createSection(0, 'Featured', section.featuredLinkColumn));
  section.linkColumns.forEach(function (_a) {
    var title = _a.title,
      links = _a.links;
    return sections.push(createSection(sections.length, title, links));
  });
  function createSection(i, title, links) {
    return {
      heading: jsx(Div, null, title),
      key: i,
      body: jsx(Div, {
        width: "100%"
      }, links.map(function (link, i) {
        return typeof link.title === 'string' ? jsx(Div, {
          display: "flex",
          alignItems: "center",
          inset: {
            horizontal: 'x3',
            vertical: 'x1'
          },
          key: "accordion-section-" + i
        }, jsx(Anchor, _extends({}, link, {
          title: link.title,
          target: "_blank"
        }))) : jsx(Anchor, {
          href: link.href,
          onClick: link.onClick
        }, link.title);
      }))
    };
  }
  return jsx(Accordion, {
    isExclusive: true,
    sections: sections
  });
};
// Make sure types are correctly rendered
// hard code the featured title rendering
// pass dummy data in storybook
// IconLink - dynamic link type
// Anchor - regular link

var _emotionSourceMap$7 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFHd0UiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgX19tYWtlVGVtcGxhdGVPYmplY3QgfSBmcm9tIFwidHNsaWJcIjtcclxuaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vY29yZSc7XHJcbmV4cG9ydCBkZWZhdWx0IHtcclxuICAgIHN0YXRpY05hdmlnYXRpb25Db250YWluZXI6IGZ1bmN0aW9uIChob2NFbnRpdHksIGlzRnJvc3RlZCkgeyByZXR1cm4gY3NzKHRlbXBsYXRlT2JqZWN0XzEgfHwgKHRlbXBsYXRlT2JqZWN0XzEgPSBfX21ha2VUZW1wbGF0ZU9iamVjdChbXCJcXG4gICAgYmFja2Ryb3AtZmlsdGVyOiBcIiwgXCI7XFxuICAgIGJhY2tncm91bmQ6IFwiLCBcIjtcXG4gIFwiXSwgW1wiXFxuICAgIGJhY2tkcm9wLWZpbHRlcjogXCIsIFwiO1xcbiAgICBiYWNrZ3JvdW5kOiBcIixcclxuICAgICAgICBcIjtcXG4gIFwiXSkpLCBpc0Zyb3N0ZWQgPyAnYmx1cigxMHB4KScgOiAnbm9uZScsIGlzRnJvc3RlZFxyXG4gICAgICAgID8gJ3JnYmEoMjU1LCAyNTUsIDI1NSwgMC43KSdcclxuICAgICAgICA6IGhvY0VudGl0eS5jb2xvcnMuZm9yZWdyb3VuZCk7IH0sXHJcbn07XHJcbnZhciB0ZW1wbGF0ZU9iamVjdF8xO1xyXG4vLyMgc291cmNlTWFwcGluZ1VSTD1zdHlsZXMuanMubWFwIl19 */";
var styles$6 = {
  staticNavigationContainer: function (hocEntity, isFrosted) {
    return /*#__PURE__*/css(templateObject_1$7 || (templateObject_1$7 = __makeTemplateObject(["\n    backdrop-filter: ", ";\n    background: ", ";\n  " + _emotionSourceMap$7], ["\n    backdrop-filter: ", ";\n    background: ", ";\n  " + _emotionSourceMap$7])), isFrosted ? 'blur(10px)' : 'none', isFrosted ? 'rgba(255, 255, 255, 0.7)' : hocEntity.colors.foreground);
  }
};
var templateObject_1$7;

var StaticNavigation = function (_a) {
  var _b, _c;
  var accountSection = _a.accountSection,
    logo = _a.logo,
    links = _a.links,
    _d = _a.zIndex,
    zIndex = _d === void 0 ? 3 : _d,
    openValueOverride = _a.openValueOverride,
    _e = _a.drawerOffset,
    drawerOffset = _e === void 0 ? 60 : _e,
    isFrosted = _a.isFrosted,
    menuDropdownLinks = _a.menuDropdownLinks;
  var _f = useState(0),
    selectedTab = _f[0],
    setSelectedTab = _f[1];
  var _g = useState(false),
    isOpen = _g[0],
    setIsOpen = _g[1];
  var _h = React.useState(false),
    isMenuOpen = _h[0],
    setIsMenuOpen = _h[1];
  var size = useDeviceSize().device;
  var _j = useResizeObserver(),
    ref = _j.ref,
    height = _j.height;
  var hocs = useEntities().hocs;
  var isDropdownLinkType = function (valueToCheck) {
    var dropDownLink = valueToCheck;
    return dropDownLink.linkColumns !== undefined && dropDownLink.title !== undefined;
  };
  var isMobile = ['xSmall', 'small', 'medium'].includes(size);
  return jsx(Div, {
    display: "flex",
    flexDirection: "column"
  }, jsx(Flex, {
    css: styles$6.staticNavigationContainer(hocs, isFrosted),
    width: "100%",
    height: "60px",
    boxShadow: "mediumLight",
    alignItems: "center",
    justifyContent: "space-between",
    inset: {
      horizontal: 'x3'
    },
    zIndex: zIndex
  }, links && isMobile && jsx(Div, null, jsx(IconLink, {
    size: isOpen ? 'xSmall' : 'small',
    icon: isOpen ? 'close' : 'hamburger',
    onClick: function () {
      return setIsOpen(!isOpen);
    }
  })), !links && !isMobile && jsx("div", null), jsx(Flex, {
    alignItems: "center",
    flexDirection: "row",
    columnGap: "x1"
  }, menuDropdownLinks && jsx(Div, {
    maxWidth: "300px"
  }, jsx("button", {
    style: {
      backgroundColor: '#fff',
      borderRadius: '40px',
      border: 'none',
      cursor: 'pointer'
    },
    onClick: function () {
      return setIsMenuOpen(!isMenuOpen);
    }
  }, jsx(Flex, {
    flexDirection: "row"
  }, jsx(Icon, {
    name: "promptingsIcon",
    primaryColor: "primaryHeadingText",
    size: "xSmall"
  }), jsx(Div, {
    style: {
      transform: isMenuOpen ? 'rotate(90deg)' : 'rotate(-90deg)',
      transition: 'transform 0.3s ease-in-out'
    }
  }, jsx(Icon, {
    name: "chevron",
    primaryColor: "primaryHeadingText",
    size: "xSmall"
  })))), jsx(DropDownMenu, {
    linkColumn: menuDropdownLinks.linkColumn,
    isOpen: isMenuOpen,
    top: (_b = menuDropdownLinks.top) !== null && _b !== void 0 ? _b : 40,
    left: menuDropdownLinks.left
  })), jsx(Logo, logo)), links && !isMobile && jsx(Flex, {
    height: "100%",
    width: "100%",
    alignItems: "center",
    justifyContent: "center"
  }, links && links.map(function (link, index) {
    return jsx(Div, {
      inset: {
        horizontal: size === 'large' ? 'x1' : 'x2'
      }
    }, 'href' in link && typeof link.title === 'string' ? jsx(NavigationLink, {
      title: link.title,
      isActive: selectedTab === index && isOpen,
      href: link.href,
      target: link.target
    }) : isDropdownLinkType(link) ? jsx(Div, {
      onHoverStart: function () {
        setIsOpen(true);
        setSelectedTab(index);
      },
      key: index,
      onClick: function () {
        return isOpen ? setIsOpen(false) : (setIsOpen(true), setSelectedTab(index));
      }
    }, jsx(NavigationLink, {
      title: link.title,
      isActive: selectedTab === index && isOpen
    })) : jsx(Anchor, {
      href: link.href,
      onClick: link.onClick,
      target: link.target
    }, link.title));
  })), accountSection && jsx(Div, {
    display: "flex"
  }, accountSection)), jsx(Div, {
    position: 'absolute',
    top: drawerOffset + "px",
    width: '100%',
    zIndex: zIndex - 1,
    onHoverEnd: function () {
      return setIsOpen(false);
    }
  }, !isMobile && jsx(Drawer, {
    isOpen: isOpen && !isMobile,
    location: "top",
    onClick: function () {
      return setIsOpen(false);
    },
    openValue: openValueOverride ? openValueOverride : (_c = height && height + 64 < 248 ? 248 : height && height + 64) !== null && _c !== void 0 ? _c : 280,
    isTabHidden: true,
    showTab: false,
    zIndex: 10
  }, jsx(Div, {
    outset: "x3",
    ref: ref
  }, links && links.map(function (link, index) {
    return selectedTab === index && jsx(HStack, {
      key: index,
      gap: "x9",
      justify: "center",
      alignItems: "flex-start"
    }, isDropdownLinkType(link) && link.featuredLinkColumn && jsx(HStack, {
      gap: "x9",
      justify: "center"
    }, jsx(VStack, {
      gap: "x2"
    }, link.featuredLinkColumn.map(function (featuredColumn) {
      return jsx(Anchor, {
        cursor: 'pointer',
        onClick: featuredColumn.onClick,
        href: featuredColumn.href,
        target: featuredColumn.target
      }, typeof featuredColumn.title === 'string' ? jsx(Text, {
        type: "body",
        content: featuredColumn.title,
        color: "primaryHeading",
        weight: "semiBold"
      }) : featuredColumn.title);
    })), jsx(Div, {
      height: "160px"
    }, jsx(Separator, {
      orientation: "vertical"
    }))), isDropdownLinkType(link) && link.linkColumns.map(function (column, index) {
      return jsx(VStack, {
        gap: "x8",
        key: index
      }, jsx(Flex, {
        alignItems: "center",
        width: "100%",
        justifyContent: "space-between"
      }, jsx(LinkColumn, {
        linkColumn: column,
        linkTitleColor: column.titleColor ? column.titleColor : 'primaryBrand',
        subLinkColor: column.linkColor ? column.linkColor : 'primaryBody'
      })));
    }));
  })))), links && isMobile && jsx(Div, {
    height: "calc(100% - " + drawerOffset + "px)",
    position: "fixed",
    left: "0px",
    top: drawerOffset + "px"
  }, jsx(MobileView, {
    isOpen: isOpen,
    toggle: function () {
      return setIsOpen(!isOpen);
    },
    links: links
  })));
};

var _emotionSourceMap$6 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFHWSIsImZpbGUiOiJzdHlsZXMudHMiLCJzb3VyY2VzQ29udGVudCI6WyJpbXBvcnQgeyBfX21ha2VUZW1wbGF0ZU9iamVjdCB9IGZyb20gXCJ0c2xpYlwiO1xyXG5pbXBvcnQgeyBjc3MgfSBmcm9tICdAZW1vdGlvbi9jb3JlJztcclxuZXhwb3J0IGRlZmF1bHQge1xyXG4gICAgcmliYm9uOiBjc3ModGVtcGxhdGVPYmplY3RfMSB8fCAodGVtcGxhdGVPYmplY3RfMSA9IF9fbWFrZVRlbXBsYXRlT2JqZWN0KFtcIlxcbiAgICBjbGlwLXBhdGg6IGluc2V0KDApO1xcbiAgICB0cmFuc2Zvcm06IHJvdGF0ZSg0NWRlZyk7XFxuICAgIHJpZ2h0OiAtMzNweDtcXG4gICAgdG9wOiAxMnB4O1xcbiAgICBsaW5lLWhlaWdodDogaW5pdGlhbDtcXG4gICAgdGV4dC1hbGlnbjogY2VudGVyO1xcbiAgICBwIHtcXG4gICAgICBsaW5lLWhlaWdodDogaW5pdGlhbDtcXG4gICAgfVxcbiAgXCJdLCBbXCJcXG4gICAgY2xpcC1wYXRoOiBpbnNldCgwKTtcXG4gICAgdHJhbnNmb3JtOiByb3RhdGUoNDVkZWcpO1xcbiAgICByaWdodDogLTMzcHg7XFxuICAgIHRvcDogMTJweDtcXG4gICAgbGluZS1oZWlnaHQ6IGluaXRpYWw7XFxuICAgIHRleHQtYWxpZ246IGNlbnRlcjtcXG4gICAgcCB7XFxuICAgICAgbGluZS1oZWlnaHQ6IGluaXRpYWw7XFxuICAgIH1cXG4gIFwiXSkpKSxcclxufTtcclxudmFyIHRlbXBsYXRlT2JqZWN0XzE7XHJcbi8vIyBzb3VyY2VNYXBwaW5nVVJMPXN0eWxlcy5qcy5tYXAiXX0= */";
var styles$5 = {
  ribbon: /*#__PURE__*/css(templateObject_1$6 || (templateObject_1$6 = __makeTemplateObject(["\n    clip-path: inset(0);\n    transform: rotate(45deg);\n    right: -33px;\n    top: 12px;\n    line-height: initial;\n    text-align: center;\n    p {\n      line-height: initial;\n    }\n  ;label:ribbon;" + _emotionSourceMap$6], ["\n    clip-path: inset(0);\n    transform: rotate(45deg);\n    right: -33px;\n    top: 12px;\n    line-height: initial;\n    text-align: center;\n    p {\n      line-height: initial;\n    }\n  ;label:ribbon;" + _emotionSourceMap$6])))
};
var templateObject_1$6;

var RadioOptionCard = function (_a) {
  var title = _a.title,
    subtitle = _a.subtitle,
    isFeatured = _a.isFeatured,
    optionId = _a.optionId,
    onSelect = _a.onSelect,
    isSelected = _a.isSelected,
    infoAction = _a.infoAction;
  return jsx(React.Fragment, null, jsx(SelectableCard, {
    isActive: isSelected,
    onClick: function () {
      return onSelect === null || onSelect === void 0 ? void 0 : onSelect(optionId);
    },
    shouldShowActiveIcons: false
  }, jsx(HStack, {
    gap: "x2",
    justify: "flex-start"
  }, !!infoAction && jsx(Div, {
    position: "absolute",
    top: "x_25",
    right: "x_5",
    onClick: infoAction
  }, jsx(Icon, {
    name: "information",
    size: "small",
    primaryColor: "primaryBodyText"
  })), jsx(VStack, {
    gap: "x_5"
  }, jsx(Text, {
    type: {
      xSmall: 'body',
      medium: 'largeBody'
    },
    color: "primaryHeading",
    weight: "semiBold",
    content: title,
    style: {
      lineHeight: 'initial'
    }
  }), jsx(Text, {
    type: {
      xSmall: 'caption',
      small: 'body'
    },
    color: "primaryBody",
    weight: "regular",
    content: subtitle,
    style: {
      lineHeight: 'initial'
    }
  }))), isFeatured && jsx(Div, {
    backgroundColor: {
      swatch: 'success',
      shade: 'base'
    },
    overflow: "hidden",
    css: styles$5.ribbon,
    position: "absolute",
    width: "150px",
    inset: "0.25rem"
  }, jsx(Div, {
    outset: {
      left: '2px'
    }
  }, jsx(Text, {
    type: "caption",
    content: "Most",
    whiteSpace: "normal",
    weight: "bold",
    alignment: "center",
    color: "inverseBody"
  }), jsx(Text, {
    type: "caption",
    content: "Popular",
    whiteSpace: "normal",
    weight: "bold",
    alignment: "center",
    color: "inverseBody"
  })))));
};

var RadioOptionList = function (_a) {
  var options = _a.options,
    submitAction = _a.submitAction,
    _b = _a.defaultNumOptionsShown,
    defaultNumOptionsShown = _b === void 0 ? options.length : _b,
    globalInfoActionOverride = _a.globalInfoActionOverride,
    extraLink = _a.extraLink;
  var _c = useState([-1]),
    selectedIndexes = _c[0],
    setSelectedIndex = _c[1];
  var handleOnSelect = function (index, optionId, onSelect) {
    setSelectedIndex([index]);
    onSelect === null || onSelect === void 0 ? void 0 : onSelect(optionId);
  };
  var firstSelected = selectedIndexes[0];
  return jsx(VStack, {
    gap: "x2"
  }, jsx(SelectableList, {
    limit: 1,
    selectedIndexes: selectedIndexes,
    setSelectedIndexes: setSelectedIndex // This assertion is because SelectableList expects an array of numbers(s), but we are locking down a fixed array (1 item) for this use case
  }, options.filter(function (_, index) {
    return index < defaultNumOptionsShown;
  }).map(function (option, index) {
    var isSelected = selectedIndexes.some(function (selectedIndex) {
      return selectedIndex === index;
    });
    return jsx(RadioOptionCard, {
      title: option.title,
      subtitle: option.subtitle,
      isFeatured: option.isFeatured,
      onSelect: function (optionId) {
        return handleOnSelect(index, optionId, option.onSelect);
      },
      isSelected: isSelected,
      key: index,
      optionId: option.optionId,
      infoAction: globalInfoActionOverride ? function () {
        return globalInfoActionOverride(option.optionId);
      } : option.infoAction
    });
  })), extraLink && jsx(Anchor, {
    title: extraLink.title,
    onClick: extraLink.onClick,
    href: extraLink.href,
    size: "small",
    color: "primaryBody",
    outset: "0 auto"
  }), jsx(Button, {
    onClick: function () {
      return submitAction.onClick(options[firstSelected].optionId);
    },
    disabled: selectedIndexes[0] === -1 || selectedIndexes.length <= 0,
    type: submitAction.type,
    size: "large",
    title: submitAction.title,
    fullWidth: true
  }));
};

var InviteCard = function (_a) {
  var user = _a.user,
    avatar = _a.avatar,
    extraLink = _a.extraLink,
    action = _a.action;
  return jsx(Flex, null, jsx(VStack, {
    gap: "x0",
    outset: "x2"
  }, jsx(HStack, {
    gap: "x2",
    justify: "initial"
  }, jsx(Avatar, {
    size: avatar.size,
    initials: avatar.initials,
    isActive: avatar.isActive,
    isSelected: avatar.isSelected,
    avatarImage: avatar.avatarImage
  }), jsx(Text, {
    type: "title",
    color: "primaryHeading",
    content: user.name,
    weight: "semiBold"
  })), jsx(Spacer, {
    space: "x2"
  }), jsx(VStack, {
    gap: "x0"
  }, jsx(Text, {
    type: "caption",
    color: "primaryBody",
    content: user.profileDate
  }), jsx(Text, {
    type: "largeBody",
    color: "primaryHeading",
    content: user.activityFeed,
    style: {
      lineHeight: 'initial'
    }
  }), jsx(Text, {
    type: "caption",
    color: "primaryBody",
    content: user.activityDescription
  })), jsx(Spacer, {
    space: "x2"
  }), jsx(HStack, {
    gap: "x2",
    justify: "initial",
    outset: "0 auto"
  }, jsx(Icon, {
    name: "loading",
    size: "medium",
    primaryColor: "success"
  }), jsx(Icon, {
    name: "loading",
    size: "medium",
    primaryColor: "success"
  }), jsx(Icon, {
    name: "loading",
    size: "medium",
    primaryColor: "success"
  }), jsx(Icon, {
    name: "loading",
    size: "medium",
    primaryColor: "success"
  })), jsx(Spacer, {
    space: "x2"
  }), jsx(VStack, {
    gap: "x1"
  }, jsx(Text, {
    type: "body",
    color: "primaryBody",
    content: "Contact " + user.name + ":"
  }), jsx(HStack, {
    gap: "x4",
    justify: "initial"
  }, jsx(Icon, {
    name: "envelope",
    size: "small",
    primaryColor: "default"
  }), jsx(Icon, {
    name: "facebook",
    size: "small",
    primaryColor: "default"
  }), jsx(Icon, {
    name: "twitter",
    size: "small",
    primaryColor: "default"
  }))), jsx(Spacer, {
    space: "x2"
  }), jsx(VStack, {
    gap: "x2"
  }, jsx(Button, {
    type: action.type,
    title: action.title,
    onClick: action.onClick,
    fullWidth: true
  }), jsx(Anchor, {
    size: extraLink.size,
    href: extraLink.href,
    isDecorated: extraLink.isDecorated,
    title: extraLink.title,
    outset: "0 auto"
  }))));
};

var PromotionWidget = function (_a) {
  var title = _a.title,
    capsuleTitle = _a.capsuleTitle,
    description = _a.description,
    primaryAction = _a.primaryAction,
    secondaryAction = _a.secondaryAction,
    _b = _a.minWidth,
    minWidth = _b === void 0 ? '320px' : _b,
    _c = _a.width,
    width = _c === void 0 ? '100%' : _c,
    type = _a.type,
    children = _a.children,
    _d = _a.backgroundColor,
    backgroundColor = _d === void 0 ? 'background' : _d;
  var widget = useEntities().widget;
  var widgetProperties = widget[type];
  return jsx(Card, {
    display: "flex",
    justifyItems: "start",
    borderRadius: "default",
    backgroundColor: backgroundColor,
    inset: "x4",
    width: width,
    minWidth: minWidth
  }, jsx(VStack, {
    gap: "x3"
  }, jsx(Flex, null, jsx(Capsule, {
    title: capsuleTitle,
    backgroundColor: widgetProperties.backgroundColor,
    fontColor: widgetProperties.fontColor,
    boxShadow: type === 'shadow' ? 'mediumLight' : undefined
  })), jsx(Div, {
    width: "100%"
  }, jsx(Text, {
    weight: "bold",
    type: {
      xSmall: 'subtitle',
      medium: 'title'
    }
  }, title)), jsx(Div, {
    width: "100%",
    flexWrap: "wrap"
  }, jsx(Text, {
    type: "caption"
  }, description)), jsx(Flex, {
    justifyContent: "flex-start"
  }, primaryAction && jsx(Button, {
    type: "success",
    onClick: function () {
      return primaryAction.onClick();
    }
  }, primaryAction.title), secondaryAction && jsx(Flex, {
    alignItems: "center",
    inset: {
      left: 'x3'
    }
  }, jsx(Anchor, {
    href: secondaryAction.href,
    title: secondaryAction.title,
    size: 'small',
    color: 'primaryBody'
  })), children)));
};

var FeatureAnnouncement = function (_a) {
  var _b;
  var title = _a.title,
    description = _a.description,
    onClose = _a.onClose,
    imageURL = _a.imageURL,
    button = _a.button,
    _c = _a.backgroundColor,
    backgroundColor = _c === void 0 ? 'foreground' : _c,
    maxWidth = _a.maxWidth;
  return jsx(Div, {
    maxWidth: maxWidth,
    position: "relative"
  }, onClose && jsx(CloseButton, {
    top: "-8px",
    right: "0",
    onClose: onClose
  }), jsx(Card, {
    outset: "x1_5",
    backgroundColor: backgroundColor
  }, jsx(Image$1, {
    isActive: true,
    image: {
      url: imageURL
    },
    height: "124px",
    width: "224px"
  }), jsx(Spacer, {
    space: "x2"
  }), jsx(Flex, {
    flexDirection: "column",
    display: "inline-flex"
  }, jsx(Text, {
    alignment: "center",
    weight: "bold",
    type: "body",
    content: title
  }), jsx(Text, {
    alignment: "center",
    content: description,
    type: "footnote",
    lineHeight: 1.4
  })), jsx(Spacer, {
    space: "x1_5"
  }), button && jsx(Button, {
    title: button.title,
    onClick: function () {
      var _a;
      (_a = button.onClick) === null || _a === void 0 ? void 0 : _a.call(button);
      onClose === null || onClose === void 0 ? void 0 : onClose();
    },
    size: "xSmall",
    fullWidth: true,
    type: (_b = button.type) !== null && _b !== void 0 ? _b : 'primary'
  }), jsx(Spacer, {
    space: "x1"
  })));
};

var _emotionSourceMap$5 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFHcUIiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgX19tYWtlVGVtcGxhdGVPYmplY3QgfSBmcm9tIFwidHNsaWJcIjtcclxuaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vY29yZSc7XHJcbmV4cG9ydCBkZWZhdWx0IHtcclxuICAgIGZpbGxSdWxlV3JhcHBlcjogY3NzKHRlbXBsYXRlT2JqZWN0XzEgfHwgKHRlbXBsYXRlT2JqZWN0XzEgPSBfX21ha2VUZW1wbGF0ZU9iamVjdChbXCJcXG4gICAgcG9zaXRpb246IHJlbGF0aXZlO1xcbiAgICBiYWNrZ3JvdW5kOiAjZDRkNGQ0O1xcbiAgICBjdXJzb3I6IHBvaW50ZXI7XFxuICAgIDo6YmVmb3JlIHtcXG4gICAgICBjb250ZW50OiAnJztcXG4gICAgICBwb3NpdGlvbjogYWJzb2x1dGU7XFxuICAgICAgd2lkdGg6IDUwJTtcXG4gICAgICBoZWlnaHQ6IDUwJTtcXG4gICAgICBiYWNrZ3JvdW5kOiAjYTFhMWExO1xcbiAgICAgIHRvcDogMDtcXG4gICAgICBsZWZ0OiAwO1xcbiAgICAgIGJvcmRlci1yYWRpdXM6IDRweCAwIDAgMDtcXG4gICAgfVxcbiAgICA6OmFmdGVyIHtcXG4gICAgICBjb250ZW50OiAnJztcXG4gICAgICBwb3NpdGlvbjogYWJzb2x1dGU7XFxuICAgICAgd2lkdGg6IDUwJTtcXG4gICAgICBoZWlnaHQ6IDUwJTtcXG4gICAgICBiYWNrZ3JvdW5kOiAjYTFhMWExO1xcbiAgICAgIGJvdHRvbTogMDtcXG4gICAgICByaWdodDogMDtcXG4gICAgICBib3JkZXItcmFkaXVzOiAwIDAgNHB4IDA7XFxuICAgIH1cXG4gIFwiXSwgW1wiXFxuICAgIHBvc2l0aW9uOiByZWxhdGl2ZTtcXG4gICAgYmFja2dyb3VuZDogI2Q0ZDRkNDtcXG4gICAgY3Vyc29yOiBwb2ludGVyO1xcbiAgICA6OmJlZm9yZSB7XFxuICAgICAgY29udGVudDogJyc7XFxuICAgICAgcG9zaXRpb246IGFic29sdXRlO1xcbiAgICAgIHdpZHRoOiA1MCU7XFxuICAgICAgaGVpZ2h0OiA1MCU7XFxuICAgICAgYmFja2dyb3VuZDogI2ExYTFhMTtcXG4gICAgICB0b3A6IDA7XFxuICAgICAgbGVmdDogMDtcXG4gICAgICBib3JkZXItcmFkaXVzOiA0cHggMCAwIDA7XFxuICAgIH1cXG4gICAgOjphZnRlciB7XFxuICAgICAgY29udGVudDogJyc7XFxuICAgICAgcG9zaXRpb246IGFic29sdXRlO1xcbiAgICAgIHdpZHRoOiA1MCU7XFxuICAgICAgaGVpZ2h0OiA1MCU7XFxuICAgICAgYmFja2dyb3VuZDogI2ExYTFhMTtcXG4gICAgICBib3R0b206IDA7XFxuICAgICAgcmlnaHQ6IDA7XFxuICAgICAgYm9yZGVyLXJhZGl1czogMCAwIDRweCAwO1xcbiAgICB9XFxuICBcIl0pKSksXHJcbn07XHJcbnZhciB0ZW1wbGF0ZU9iamVjdF8xO1xyXG4vLyMgc291cmNlTWFwcGluZ1VSTD1zdHlsZXMuanMubWFwIl19 */";
var styles$4 = {
  fillRuleWrapper: /*#__PURE__*/css(templateObject_1$5 || (templateObject_1$5 = __makeTemplateObject(["\n    position: relative;\n    background: #d4d4d4;\n    cursor: pointer;\n    ::before {\n      content: '';\n      position: absolute;\n      width: 50%;\n      height: 50%;\n      background: #a1a1a1;\n      top: 0;\n      left: 0;\n      border-radius: 4px 0 0 0;\n    }\n    ::after {\n      content: '';\n      position: absolute;\n      width: 50%;\n      height: 50%;\n      background: #a1a1a1;\n      bottom: 0;\n      right: 0;\n      border-radius: 0 0 4px 0;\n    }\n  ;label:fillRuleWrapper;" + _emotionSourceMap$5], ["\n    position: relative;\n    background: #d4d4d4;\n    cursor: pointer;\n    ::before {\n      content: '';\n      position: absolute;\n      width: 50%;\n      height: 50%;\n      background: #a1a1a1;\n      top: 0;\n      left: 0;\n      border-radius: 4px 0 0 0;\n    }\n    ::after {\n      content: '';\n      position: absolute;\n      width: 50%;\n      height: 50%;\n      background: #a1a1a1;\n      bottom: 0;\n      right: 0;\n      border-radius: 0 0 4px 0;\n    }\n  ;label:fillRuleWrapper;" + _emotionSourceMap$5])))
};
var templateObject_1$5;

var SettingInput = function (_a) {
  var value = _a.value,
    _b = _a.settingsType,
    settingsType = _b === void 0 ? 'color' : _b,
    inactiveTitle = _a.inactiveTitle,
    onRemove = _a.onRemove,
    onClick = _a.onClick;
  var label = settingsType === 'color' ? 'Color' : 'Image';
  var size = '24px';
  return jsx(Div, {
    minWidth: "230px",
    cursor: "pointer"
  }, jsx(Text, {
    type: "caption",
    content: label
  }), jsx(Flex, {
    justifyContent: "space-between",
    minWidth: "230px",
    onClick: onClick,
    width: "100%",
    borderRadius: "small",
    inset: "x1",
    display: "inline-flex",
    backgroundColor: "background",
    borderStyle: "solid",
    borderColor: "lightgrey",
    borderWidth: "thin",
    alignItems: "center"
  }, value ? jsx(Flex, {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between"
  }, jsx(HStack, {
    gap: "0",
    justify: "start"
  }, jsx(Div, {
    backgroundPosition: "center",
    backgroundSize: "cover",
    backgroundRepeat: "no-repeat",
    backgroundColor: settingsType === 'color' ? value : undefined,
    backgroundImage: settingsType === 'image' ? value : undefined,
    borderRadius: "4px",
    width: "24px",
    height: "24px"
  }), jsx(Text, {
    alignment: "left",
    outset: {
      left: 'x2'
    },
    type: "body",
    content: settingsType === 'color' ? value : 'Image'
  })), jsx(Div, {
    display: "flex",
    alignItems: "center",
    onMouseEvent: function (e) {
      e.stopPropagation();
      onRemove();
    },
    onClick: onRemove
  }, jsx(Icon, {
    name: "closeCircle",
    size: "xSmall",
    primaryColor: "danger"
  }))) : jsx(Flex, {
    justifyContent: "center",
    alignItems: "center"
  }, settingsType === 'color' ? jsx(Div, {
    borderRadius: "4px",
    width: size,
    height: size,
    overflow: "visible",
    backgroundColor: {
      swatch: 'grayScale',
      shade: '_900'
    }
  }) : jsx(Div, {
    css: styles$4.fillRuleWrapper,
    width: size,
    height: size,
    borderRadius: "4px"
  }), jsx(Text, {
    alignment: "left",
    outset: {
      left: 'x2'
    },
    type: "body",
    content: inactiveTitle
  }))));
};

var ReleaseNoteDialog = function (_a) {
  var title = _a.title,
    _b = _a.versionTitle,
    versionTitle = _b === void 0 ? 'Version' : _b,
    versionNumber = _a.versionNumber,
    releaseDate = _a.releaseDate,
    description = _a.description,
    noteSections = _a.noteSections,
    button = _a.button,
    isOpen = _a.isOpen,
    onClose = _a.onClose,
    _c = _a.maxWidth,
    maxWidth = _c === void 0 ? 665 : _c,
    _d = _a.maxHeight,
    maxHeight = _d === void 0 ? 500 : _d;
  return jsx(Dialog, {
    closeButtonId: versionTitle,
    isOpen: isOpen,
    onClose: onClose
  }, jsx(Flex, {
    maxHeight: maxHeight + "px",
    maxWidth: maxWidth + "px",
    minWidth: "320px",
    flexDirection: "column"
  }, jsx(Text, {
    type: {
      xSmall: 'largeBody',
      small: 'subtitle'
    },
    color: "primaryHeading",
    weight: "bold",
    content: title,
    alignment: "center"
  }), jsx(Spacer, {
    space: "x1"
  }), jsx(Text, {
    type: "caption",
    color: "primaryBody",
    content: description,
    alignment: "center",
    lineHeight: 1.6
  }), jsx(Spacer, {
    space: "x3"
  }), jsx(Separator, {
    orientation: "horizontal"
  }), jsx(Spacer, {
    space: "x2_5"
  }), jsx(Flex, null, jsx(Text, {
    type: {
      xSmall: 'caption',
      small: 'body'
    },
    color: "primaryHeading",
    weight: "semiBold",
    content: versionTitle + ": " + versionNumber
  }), jsx(Text, {
    type: "caption",
    color: "primaryBody",
    weight: "regular",
    content: releaseDate,
    outset: {
      left: 'auto'
    },
    alignment: "right"
  })), jsx(Spacer, {
    space: "x2_5"
  }), jsx(Flex, {
    flexDirection: "column",
    overflowY: "auto",
    height: "100%",
    inset: {
      right: 'x2'
    }
  }, noteSections.map(function (noteSection) {
    return noteSection.notes.map(function (note, index) {
      return jsx(React.Fragment, null, jsx(ReleaseNote, {
        key: index,
        capsule: {
          title: noteSection.releaseType
        },
        note: {
          title: note
        }
      }), jsx(Spacer, {
        orientation: "vertical",
        space: "x3"
      }));
    });
  })), jsx(Flex, {
    width: "35%",
    justifyContent: "center",
    style: {
      margin: '1rem auto 0 auto'
    }
  }, jsx(Button, {
    size: "medium",
    title: button.title,
    onClick: function () {
      var _a;
      onClose();
      (_a = button.onClick) === null || _a === void 0 ? void 0 : _a.call(button);
    },
    type: button.type,
    fullWidth: true
  }))));
};

var isDragEventType = function (event) {
  return 'dataTransfer' in event;
};
var ImageUploader = function (props) {
  var _a, _b;
  var _c = React.useState(null),
    error = _c[0],
    setError = _c[1];
  var onUpload = props.onUpload;
  var onDrop = useCallback(function (acceptedFiles, rejectedFiles, event) {
    var element = isDragEventType(event) ? event.dataTransfer : event.target;
    var fileList = element === null || element === void 0 ? void 0 : element.files;
    var hasMaxFilesBeenExceeded = rejectedFiles.some(function (rejectedFile) {
      return rejectedFile.errors.some(function (file) {
        return file.code === 'too-many-files';
      });
    });
    if (hasMaxFilesBeenExceeded) {
      setError('Too many files were attempted to upload');
    } else {
      onUpload({
        accepted: acceptedFiles,
        rejected: rejectedFiles
      }, fileList);
    }
  }, [onUpload]);
  var parseAcceptedFiles = function (acceptedFiles) {
    return acceptedFiles.map(function (file) {
      return "image/" + file;
    });
  };
  var onDragEnter = useCallback(function () {
    setError(null);
  }, []);
  var _d = useDropzone({
      onDrop: onDrop,
      accept: parseAcceptedFiles(props.acceptedFiles),
      multiple: props.hasMultiupload,
      maxFiles: props.maxAcceptedFiles,
      maxSize: props.maxFileSize,
      onDragEnter: onDragEnter
    }),
    getRootProps = _d.getRootProps,
    getInputProps = _d.getInputProps,
    isDragActive = _d.isDragActive;
  return jsx(React.Fragment, null, jsx(Flex, {
    inset: "x1",
    flexWrap: "wrap",
    width: '100%',
    height: '100%',
    backgroundColor: (_a = props.backgroundColor) !== null && _a !== void 0 ? _a : 'background',
    borderColor: isDragActive ? {
      swatch: 'success',
      shade: 'base'
    } : {
      swatch: 'grayScale',
      shade: '_1000'
    },
    borderStyle: "solid",
    borderWidth: "default",
    borderRadius: "large",
    overflowY: "auto",
    cursor: "pointer",
    justifyContent: "center",
    alignItems: "center"
  }, jsx("div", _extends({}, getRootProps(), {
    style: {
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }
  }), jsx("input", _extends({}, getInputProps(), {
    onClick: onDragEnter
  })), jsx(Flex, {
    flexDirection: "column",
    alignItems: "center"
  }, jsx(Icon, {
    name: "upload",
    size: "medium",
    primaryColor: "primaryBodyText"
  }), jsx(Text, {
    type: "largeBody",
    content: (_b = props.uploadTitle) !== null && _b !== void 0 ? _b : 'Upload Photo'
  })))), error && jsx(Text, {
    type: "footnote",
    content: error,
    color: "danger",
    outset: {
      top: 'x1',
      left: 'x_5'
    }
  }));
};

var _emotionSourceMap$4 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFHdUIiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgX19tYWtlVGVtcGxhdGVPYmplY3QgfSBmcm9tIFwidHNsaWJcIjtcclxuaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vY29yZSc7XHJcbmV4cG9ydCBkZWZhdWx0IHtcclxuICAgIHN0aWNrZXJzQ29udGFpbmVyOiBjc3ModGVtcGxhdGVPYmplY3RfMSB8fCAodGVtcGxhdGVPYmplY3RfMSA9IF9fbWFrZVRlbXBsYXRlT2JqZWN0KFtcIlxcbiAgICA6Oi13ZWJraXQtc2Nyb2xsYmFyIHtcXG4gICAgICB3aWR0aDogMDsgLyogUmVtb3ZlIHNjcm9sbGJhciBzcGFjZSAqL1xcbiAgICAgIGJhY2tncm91bmQ6IHRyYW5zcGFyZW50OyAvKiBPcHRpb25hbDoganVzdCBtYWtlIHNjcm9sbGJhciBpbnZpc2libGUgKi9cXG4gICAgfVxcbiAgXCJdLCBbXCJcXG4gICAgOjotd2Via2l0LXNjcm9sbGJhciB7XFxuICAgICAgd2lkdGg6IDA7IC8qIFJlbW92ZSBzY3JvbGxiYXIgc3BhY2UgKi9cXG4gICAgICBiYWNrZ3JvdW5kOiB0cmFuc3BhcmVudDsgLyogT3B0aW9uYWw6IGp1c3QgbWFrZSBzY3JvbGxiYXIgaW52aXNpYmxlICovXFxuICAgIH1cXG4gIFwiXSkpKSxcclxufTtcclxudmFyIHRlbXBsYXRlT2JqZWN0XzE7XHJcbi8vIyBzb3VyY2VNYXBwaW5nVVJMPXN0eWxlcy5qcy5tYXAiXX0= */";
var styles$3 = {
  stickersContainer: /*#__PURE__*/css(templateObject_1$4 || (templateObject_1$4 = __makeTemplateObject(["\n    ::-webkit-scrollbar {\n      width: 0; /* Remove scrollbar space */\n      background: transparent; /* Optional: just make scrollbar invisible */\n    }\n  ;label:stickersContainer;" + _emotionSourceMap$4], ["\n    ::-webkit-scrollbar {\n      width: 0; /* Remove scrollbar space */\n      background: transparent; /* Optional: just make scrollbar invisible */\n    }\n  ;label:stickersContainer;" + _emotionSourceMap$4])))
};
var templateObject_1$4;

var ImageUploadWidget = function (_a) {
  var _b = _a.title,
    title = _b === void 0 ? 'Image Background' : _b,
    onFinished = _a.onFinished,
    onUpload = _a.onUpload,
    x = _a.x,
    y = _a.y,
    isOpen = _a.isOpen,
    _c = _a.hasMultiUpload,
    hasMultiUpload = _c === void 0 ? false : _c,
    userImages = _a.userImages,
    hasMore = _a.hasMore,
    loadMore = _a.loadMore,
    endMessage = _a.endMessage,
    dataLength = _a.dataLength,
    _d = _a.width,
    width = _d === void 0 ? 264 : _d;
  var _e = useState(false),
    shouldShowImageGrid = _e[0],
    setShouldShowImageGrid = _e[1];
  var inputRef = useRef(null);
  return jsx(AnimatePresence, null, isOpen && jsx(Div, {
    initial: {
      opacity: 0
    },
    animate: {
      opacity: 1
    },
    exit: {
      opacity: 0
    },
    top: y + "px",
    left: x + "px",
    display: "flex",
    position: "absolute",
    flexDirection: "column",
    height: shouldShowImageGrid ? '450px' : '300px',
    width: width + "px",
    backgroundColor: "foreground",
    boxShadow: "mediumDark",
    borderRadius: "medium",
    inset: "x1"
  }, jsx(CloseButton, {
    fill: "primaryHeadingText",
    top: "-16px",
    size: "xSmall",
    right: "-16px",
    onClose: function () {
      return onFinished();
    }
  }), jsx(Flex, {
    outset: "x1"
  }, jsx(Text, {
    lineHeight: 1.2,
    color: "primaryHeading",
    weight: "bold",
    type: "caption",
    content: title
  })), jsx(Flex, {
    outset: {
      vertical: 'x2'
    },
    height: "100%",
    overflow: "auto",
    borderRadius: "default"
  }, shouldShowImageGrid ? jsx(AnimatePresence, null, jsx(Div, {
    overflowY: "auto",
    id: "scrollAble",
    height: "100%",
    css: styles$3.stickersContainer,
    width: "100%"
  }, jsx(InfiniteScroll$1, {
    hasMore: !!hasMore,
    dataLength: dataLength !== null && dataLength !== void 0 ? dataLength : 0,
    loader: jsx(Div, {
      inset: "x8"
    }, jsx(LoadingSpinner, {
      size: "xLarge"
    })),
    endMessage: jsx(Flex, {
      position: "relative",
      width: "100%",
      flexDirection: "column",
      alignItems: "center",
      outset: {
        top: 'x4'
      }
    }, jsx(AnimatedCheckMark, {
      size: "small"
    }), jsx(Spacer, {
      space: "x1"
    }), jsx(Text, {
      type: "body"
    }, endMessage)),
    next: function () {
      return loadMore === null || loadMore === void 0 ? void 0 : loadMore();
    },
    scrollableTarget: "scrollAble"
  }, jsx(Grid, {
    columnSize: 35,
    rowSize: 30,
    gap: 8,
    initial: {
      opacity: 0
    },
    animate: {
      opacity: 1
    },
    exit: {
      opacity: 0
    }
  }, userImages === null || userImages === void 0 ? void 0 : userImages.map(function (image, index) {
    return jsx(GridItem, {
      key: index,
      gridColSpan: 2,
      gridRowSpan: image.isHorizontal ? 2 : 4,
      borderRadius: 'medium'
    }, jsx(Div, {
      width: "100%",
      height: "100%"
    }, jsx(Image$1, {
      onClick: image.onClick,
      isActive: true,
      height: "100%",
      width: "100%",
      image: {
        url: image.url
      }
    })));
  }))))) : jsx(ImageUploader, {
    onUpload: onUpload,
    acceptedFiles: ['gif', 'jpeg', 'jpg', 'png'],
    hasMultiupload: hasMultiUpload
  })), jsx(HStack, {
    outset: "x1",
    justify: "center",
    gap: "x1"
  }, jsx(Div, {
    width: "120px"
  }, jsx(Button, {
    onClick: function () {
      var _a;
      setShouldShowImageGrid(false);
      if (inputRef.current) {
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.click();
      }
    },
    fullWidth: true,
    size: "small",
    type: "smoke",
    title: "Upload..."
  }), jsx("input", {
    ref: inputRef,
    type: "file",
    accept: "image/*",
    style: {
      display: 'none'
    },
    onChange: function (e) {
      var _a, _b;
      var file = (_b = (_a = e.target) === null || _a === void 0 ? void 0 : _a.files) === null || _b === void 0 ? void 0 : _b[0];
      if (file) onUpload({
        accepted: [file],
        rejected: []
      });
    }
  })), jsx(Div, {
    width: "120px"
  }, jsx(Button, {
    onClick: function () {
      return setShouldShowImageGrid(true);
    },
    fullWidth: true,
    size: "small",
    type: "smoke",
    title: "My Images"
  })))));
};

var ConfigurationCard = function (_a) {
  var graphic = _a.graphic,
    description = _a.description,
    cost = _a.cost,
    title = _a.title,
    secondaryTitle = _a.secondaryTitle,
    onSelect = _a.onSelect,
    configurationId = _a.configurationId,
    itemPriceTitle = _a.itemPriceTitle,
    layout = _a.layout,
    _b = _a.type,
    type = _b === void 0 ? 'addon' : _b,
    featureCaption = _a.featureCaption,
    _c = _a.backgroundColor,
    backgroundColor = _c === void 0 ? 'background' : _c,
    isSelected = _a.isSelected;
  var width = useWindowSize().width;
  var responsiveLayout = width < 420 ? 'vertical' : layout;
  return jsx(Flex, {
    onClick: function () {
      onSelect === null || onSelect === void 0 ? void 0 : onSelect(configurationId);
    },
    position: "relative",
    boxShadow: "mediumDark",
    backgroundColor: backgroundColor,
    cursor: "pointer",
    inset: {
      vertical: 'x2_5',
      horizontal: 'x2'
    },
    borderWidth: "default",
    borderRadius: "default",
    borderStyle: "solid",
    borderColor: isSelected && type === 'upsale' ? {
      swatch: 'primaryBrand',
      shade: 'base'
    } : isSelected && type === 'addon' ? {
      swatch: 'success',
      shade: 'base'
    } : 'transparent'
  }, jsx(Div, {
    right: "x1",
    position: "absolute",
    top: "x_75"
  }, isSelected && type === 'addon' ? jsx(AnimatedCheckMark, null) : type === 'info' ? jsx(Icon, {
    name: "information",
    size: "small",
    primaryColor: "secondaryBrand"
  }) : type === 'upsale' && featureCaption && jsx(Capsule, {
    backgroundColor: {
      swatch: 'primaryBrand',
      shade: 'base'
    },
    fontColor: "inverseBody",
    title: featureCaption
  })), jsx(Flex, {
    justifyContent: "flex-start",
    alignItems: "center",
    width: "100%",
    flexDirection: responsiveLayout || layout === 'vertical' ? 'column' : 'row'
  }, graphic, jsx(Div, {
    minWidth: {
      small: '216px'
    },
    outset: responsiveLayout ? {
      vertical: 'x2',
      horizontal: 'x2'
    } : {
      horizontal: 'x2',
      left: 'x2'
    }
  }, jsx(Flex, null, jsx(Text, {
    color: "primaryBody",
    alignment: responsiveLayout ? 'center' : 'left',
    weight: "bold",
    type: "largeBody",
    content: title
  }), jsx(Text, {
    outset: {
      left: 'x1'
    },
    color: "primaryBody",
    alignment: responsiveLayout ? 'center' : 'left',
    weight: "thin",
    type: "largeBody",
    content: secondaryTitle
  })), jsx(Text, {
    color: "primaryBody",
    alignment: responsiveLayout ? 'center' : 'left',
    type: {
      xSmall: 'footnote',
      medium: 'caption'
    },
    content: description
  })), cost && type !== 'info' && jsx(Flex, {
    flexDirection: "row",
    outset: responsiveLayout ? 'x0' : {
      left: 'auto'
    },
    inset: "x_5",
    alignItems: "center"
  }, jsx(Text, {
    color: "primaryBody",
    lineHeight: 1,
    weight: "semiBold",
    inset: "0",
    type: "body",
    content: (typeof cost === 'number' ? "$" + cost.toFixed(2) : cost) + " "
  }), jsx(Text, {
    color: "primaryBody",
    lineHeight: 1,
    inset: "0",
    type: "body",
    content: itemPriceTitle,
    whiteSpace: "nowrap"
  }))));
};

var ComparisonIncludesList = function (_a) {
  var items = _a.items,
    isMinified = _a.isMinified;
  return jsx(Div, {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "background",
    inset: "1.5rem"
  }, jsx(Div, {
    width: "100%"
  }, jsx(VStack, {
    gap: "x2_5",
    alignItems: isMinified ? 'start' : 'center',
    justifyContent: isMinified ? 'start' : 'center'
  }, items.sort(function (a, b) {
    return Number(b.isIncluded) - Number(a.isIncluded);
  }).map(function (item, index) {
    return jsx(ComparisonItem, _extends({
      key: index
    }, item, {
      isMinified: isMinified
    }));
  }))));
};

var ImageGrid = function (_a) {
  var images = _a.images,
    width = _a.width,
    _b = _a.borderRadius,
    borderRadius = _b === void 0 ? '8px' : _b,
    _c = _a.gap,
    gap = _c === void 0 ? '10px' : _c;
  var imageStyles = {
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    borderRadius: borderRadius
  };
  return jsx(Div, {
    position: "relative",
    overflow: "hidden",
    width: "100%",
    height: "100%"
  }, jsx(Flex, {
    width: width,
    height: "100%",
    columnGap: gap,
    rowGap: gap,
    position: "absolute",
    transform: "translate(-50%, -50%)",
    top: "50%",
    left: "50%"
  }, jsx(Flex, {
    id: "left_column",
    width: "100%",
    height: "100%",
    justifyContent: "center",
    columnGap: gap
  }, jsx(Flex, {
    flexDirection: "column",
    width: "100%",
    flexGrow: 1,
    rowGap: gap
  }, jsx(Flex, {
    flexGrow: 1,
    backgroundImage: images.leftColumn.topLeft,
    style: imageStyles
  }), jsx(Flex, {
    id: "bottom_image",
    width: "100%",
    flexGrow: 2
  }, jsx(Flex, {
    flexGrow: 1,
    backgroundImage: images.leftColumn.bottomLeft,
    style: imageStyles
  }))), jsx(Flex, {
    flexDirection: "column",
    width: "100%",
    flexGrow: 1,
    rowGap: gap
  }, jsx(Flex, {
    flexGrow: 2,
    backgroundImage: images.leftColumn.topRight,
    style: imageStyles
  }), jsx(Flex, {
    id: "bottom_image",
    width: "100%",
    flexGrow: 1
  }, jsx(Flex, {
    flexGrow: 1,
    backgroundImage: images.leftColumn.bottomRight,
    style: imageStyles
  })))), jsx(Flex, {
    id: "center_column",
    width: "100%",
    height: "100%",
    justifyContent: "center",
    alignItems: "center" // Add this line
    ,
    flexDirection: "column",
    rowGap: gap
  }, jsx(Flex, {
    id: "center_top_images",
    width: "100%",
    flexGrow: 1,
    columnGap: gap
  }, jsx(Flex, {
    flexGrow: 1,
    backgroundImage: images.centerColumn.topLeft,
    style: imageStyles
  }), jsx(Flex, {
    flexGrow: 1,
    backgroundImage: images.centerColumn.topRight,
    style: imageStyles
  })), jsx(Flex, {
    id: "bottom_image",
    width: "100%",
    flexGrow: 2
  }, jsx(Flex, {
    flexGrow: 1,
    backgroundImage: images.centerColumn.mainImage,
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    backgroundPosition: "center top",
    borderRadius: borderRadius
  }))), jsx(Flex, {
    id: "right_column",
    width: "100%",
    height: "100%",
    justifyContent: "center",
    columnGap: gap
  }, jsx(Flex, {
    flexDirection: "column",
    width: "100%",
    flexGrow: 1,
    rowGap: gap
  }, jsx(Flex, {
    flexGrow: 2,
    backgroundImage: images.rightColumn.topLeft,
    style: imageStyles
  }), jsx(Flex, {
    id: "bottom_image",
    width: "100%",
    flexGrow: 1
  }, jsx(Flex, {
    flexGrow: 1,
    backgroundImage: images.rightColumn.bottomLeft,
    style: imageStyles
  }))), jsx(Flex, {
    flexDirection: "column",
    width: "100%",
    flexGrow: 1,
    rowGap: gap
  }, jsx(Flex, {
    flexGrow: 1,
    backgroundImage: images.rightColumn.topRight,
    style: imageStyles
  }), jsx(Flex, {
    id: "bottom_image",
    width: "100%",
    flexGrow: 2
  }, jsx(Flex, {
    flexGrow: 1,
    backgroundImage: images.rightColumn.bottomRight,
    style: imageStyles
  }))))));
};

var QuantitySlider = function (_a) {
  var quantity = _a.quantity,
    min = _a.min,
    max = _a.max,
    setQuantity = _a.setQuantity,
    salePrice = _a.salePrice,
    unitPrice = _a.unitPrice,
    sliderFill = _a.sliderFill,
    width = _a.width,
    hasIncrement = _a.hasIncrement,
    head = _a.head;
  var subtotal = salePrice ? salePrice * quantity : unitPrice * quantity;
  return jsx(Flex, {
    justifyContent: "center",
    flexDirection: "column"
  }, head && jsx(Flex, {
    width: "100%",
    justifyContent: "center",
    flexDirection: "column",
    outset: {
      bottom: 'x2'
    }
  }, head), salePrice ? jsx(React.Fragment, null, jsx(Text, {
    type: "caption",
    color: "primaryHeading",
    content: "Sale",
    alignment: "center",
    style: {
      letterSpacing: '1px'
    }
  }), jsx(Flex, {
    justifyContent: "center"
  }, jsx(Spacer, {
    orientation: "horizontal",
    space: "x1"
  }), jsx(Text, {
    type: "caption",
    color: "primaryHeading",
    weight: "semiBold",
    content: "$" + (unitPrice === null || unitPrice === void 0 ? void 0 : unitPrice.toFixed(2)),
    isStrikeThrough: true,
    alignment: "center",
    style: {
      letterSpacing: '1px'
    }
  }), jsx(Spacer, {
    orientation: "horizontal",
    space: "x1"
  }), jsx(Text, {
    type: "largeBody",
    color: "danger",
    weight: "bold",
    content: "$" + salePrice.toFixed(2) + " /each",
    style: {
      letterSpacing: '1px'
    }
  }))) : jsx(Text, {
    type: "body",
    color: "primaryHeading",
    weight: "semiBold",
    content: "$" + (unitPrice === null || unitPrice === void 0 ? void 0 : unitPrice.toFixed(2)) + " /each",
    alignment: "center",
    style: {
      letterSpacing: '1px'
    }
  }), jsx(Text, {
    type: "body",
    content: "Subtotal: $" + subtotal.toFixed(2),
    alignment: "center"
  }), jsx(Flex, {
    justifyContent: "center"
  }, hasIncrement ? jsx(Div, {
    width: "370px"
  }, jsx(Spacer, {
    orientation: "vertical",
    space: "x2"
  }), jsx(IncrementSetting, {
    title: "",
    min: min,
    max: max,
    value: quantity,
    layout: "inline",
    setValue: function (quantity) {
      return setQuantity(quantity);
    },
    activeColor: sliderFill,
    inputWidth: width
  })) : jsx(Slider, {
    min: min,
    max: max,
    value: quantity,
    setValue: function (quantity) {
      return setQuantity(quantity);
    },
    width: width,
    activeColor: sliderFill
  })));
};

var FeatureBadge = function (_a) {
  var title = _a.title,
    _b = _a.backgroundColor,
    backgroundColor = _b === void 0 ? 'success' : _b,
    _c = _a.color,
    color = _c === void 0 ? 'inverseBody' : _c,
    size = _a.size,
    icon = _a.icon,
    onClick = _a.onClick;
  var hocs = useEntities().hocs;
  return jsx(Flex, {
    backgroundColor: hocs.colors.swatches[backgroundColor].base,
    inset: icon ? {
      left: 'x1',
      vertical: 'x1',
      right: 'x2'
    } : 'x2',
    borderRadius: "medium",
    display: "inline-flex",
    justifyContent: "flex-start",
    alignItems: "center",
    onClick: onClick,
    cursor: onClick ? 'pointer' : 'initial',
    minWidth: "max-content"
  }, icon && jsx(Div, {
    alignItems: "center",
    style: {
      lineHeight: 0
    },
    inset: {
      right: 'x1'
    }
  }, jsx(Icon, {
    name: icon,
    primaryColor: convertTextColorToIconColor(color),
    size: size !== null && size !== void 0 ? size : 'small'
  })), jsx(Text, {
    style: {
      lineHeight: 0,
      letterSpacing: 0.5
    },
    type: "caption",
    color: color,
    weight: "bold"
  }, title));
};

var TitledList = function (_a) {
  var title = _a.title,
    listItems = _a.listItems,
    _b = _a.listStyle,
    listStyle = _b === void 0 ? 'unordered' : _b,
    _c = _a.titleSize,
    titleSize = _c === void 0 ? 'body' : _c;
  return jsx(React.Fragment, null, jsx(Text, {
    color: "primaryHeading",
    weight: "bold",
    content: title,
    type: {
      xSmall: 'caption',
      small: 'body',
      medium: titleSize
    }
  }), jsx(List, {
    inset: "x0",
    alignment: "left",
    listStyle: "none",
    orientation: "vertical"
  }, listItems.map(function (item, index) {
    index += 1;
    return jsx(ListItem, {
      inset: "x_5",
      key: index + " " + item,
      display: "flex",
      flexDirection: "column",
      orientation: "vertical"
    }, jsx(Text, {
      color: "primaryBody",
      type: {
        xSmall: 'footnote',
        small: 'caption',
        medium: titleSize === 'subtitle' ? 'largeBody' : titleSize === 'largeBody' ? 'body' : 'caption'
      }
    }, listStyle === 'unordered' ? '-' : index + ".", " ", item));
  })));
};

var ImageThumbnail = function (_a) {
  var url = _a.url,
    width = _a.width,
    height = _a.height,
    onRemove = _a.onRemove,
    uploadProgress = _a.uploadProgress;
  var isWithinRange = uploadProgress >= 0 && uploadProgress <= 100;
  var hasUploaded = uploadProgress === 100;
  return jsx(Flex, {
    flexDirection: "column",
    justifyContent: "center",
    width: width + "px",
    height: height + "px"
  }, !!onRemove && jsx(Span, {
    position: "relative",
    top: "0px",
    right: "1px"
  }, jsx(CloseButton, {
    onClose: onRemove,
    size: "xSmall"
  })), jsx(React.Fragment, null, jsx(Image$1, {
    isActive: true,
    width: '100%',
    height: '100%',
    image: {
      url: url
    },
    backgroundSize: "cover"
  }), isWithinRange && hasUploaded ? jsx(Flex, {
    zIndex: 3,
    position: "absolute",
    top: height / 2 + "px",
    right: "400px"
  }, jsx(AnimatedCheckMark, {
    size: "medium"
  })) : jsx(Flex, {
    width: '100%',
    outset: {
      top: 'x2',
      left: 'x1'
    }
  }, jsx(Progress, {
    percentage: uploadProgress,
    hasLabel: false
  }))));
};

var DisplayCard = function (_a) {
  var title = _a.title,
    description = _a.description,
    children = _a.children;
  return jsx(Card, {
    backgroundColor: "background",
    minWidth: "280px"
  }, jsx(Flex, {
    width: "100%",
    borderRadius: "default",
    flexDirection: "column"
  }, jsx(Text, {
    type: "caption",
    weight: "bold",
    color: "primaryHeading",
    content: title,
    alignment: "center"
  }), jsx(Text, {
    type: "footnote",
    color: "primaryBody",
    content: description,
    lineHeight: 1.4,
    inset: {
      top: 'x_25'
    },
    alignment: "center"
  }), jsx(Flex, {
    width: '196px',
    height: '275px',
    style: {
      margin: '1.75rem auto'
    }
  }, children)));
};

var ReleaseNote = function (_a) {
  var _b, _c, _d;
  var capsule = _a.capsule,
    note = _a.note;
  var getCapsuleSwatch = function (type) {
    switch (type) {
      case 'new':
        return 'success';
      case 'fix':
        return 'secondaryBrand';
      case 'note':
        return 'danger';
    }
  };
  return jsx(Flex, {
    width: "100%"
  }, jsx(Div, null, jsx(Capsule, {
    title: capsule.title.toUpperCase(),
    backgroundColor: {
      swatch: (_b = capsule.backgroundColor) !== null && _b !== void 0 ? _b : getCapsuleSwatch(capsule.title),
      shade: capsule.title === 'note' ? '_100' : 'base'
    },
    boxShadow: capsule.boxShadow,
    fontColor: (_c = capsule.fontColor) !== null && _c !== void 0 ? _c : 'inverseBody'
  })), jsx(Div, {
    outset: {
      left: 'x_5'
    },
    left: "x2",
    position: "relative"
  }, jsx(Text, {
    type: "body",
    content: note.title,
    color: (_d = note.color) !== null && _d !== void 0 ? _d : 'primaryBody'
  })));
};

var ComparisonItem = function (_a) {
  var title = _a.title,
    description = _a.description,
    isIncluded = _a.isIncluded,
    _b = _a.checkMarkColor,
    checkMarkColor = _b === void 0 ? 'success' : _b,
    maxWidth = _a.maxWidth,
    isMinified = _a.isMinified;
  return jsx(Flex, {
    width: "100%",
    maxWidth: maxWidth,
    justifyContent: "flex-start"
  }, jsx(Flex, {
    justifyContent: "flex-start",
    flexWrap: 'wrap',
    width: isMinified ? 'unset' : 'calc(100% - 100px)',
    order: isMinified ? 2 : 1
  }, jsx(Text, {
    type: "body",
    color: isIncluded ? 'primaryHeading' : 'primaryBody',
    content: title,
    weight: isIncluded ? 'bold' : 'semiBold',
    lineHeight: 1.58,
    style: {
      width: '100%',
      letterSpacing: '.235px'
    }
  }), typeof description === 'string' ? jsx(Text, {
    inset: "x_25",
    type: "caption",
    color: "primaryBody",
    content: description,
    lineHeight: 1.58,
    style: {
      width: '100%'
    }
  }) : description === null || description === void 0 ? void 0 : description.map(function (bullet) {
    return jsx(ListItem, {
      orientation: "horizontal"
    }, jsx(Text, {
      isInline: true,
      type: "caption"
    }, bullet));
  })), jsx(Flex, {
    outset: isMinified ? {
      right: 'x1'
    } : {
      left: 'auto'
    },
    order: isMinified ? 1 : 2,
    alignItems: isMinified ? 'flex-start' : 'center'
  }, isIncluded ? jsx(AnimatedCheckMark, {
    filled: true,
    color: checkMarkColor
  }) : jsx(Icon, {
    name: "closeCircle",
    size: "small",
    primaryColor: 'gray'
  })));
};

var _emotionSourceMap6 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap5 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap4 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap3$1 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap2$1 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap$3 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var styles$2 = {
  wrapper: /*#__PURE__*/css(templateObject_1$3 || (templateObject_1$3 = __makeTemplateObject(["\n    position: relative;\n    height: 100%;\n    width: 100%;\n    overflow: hidden;\n    cursor: pointer;\n    display: flex;\n  ;label:wrapper;" + _emotionSourceMap$3], ["\n    position: relative;\n    height: 100%;\n    width: 100%;\n    overflow: hidden;\n    cursor: pointer;\n    display: flex;\n  ;label:wrapper;" + _emotionSourceMap$3]))),
  container: /*#__PURE__*/css(templateObject_2$1 || (templateObject_2$1 = __makeTemplateObject(["\n    position: absolute;\n    left: 0;\n    height: 100%;\n    width: 100%;\n    border-radius: 8px;\n    display: flex;\n    justify-content: space-between;\n    align-items: center;\n    padding: 0 2rem;\n    overflow: hidden;\n  ;label:container;" + _emotionSourceMap2$1], ["\n    position: absolute;\n    left: 0;\n    height: 100%;\n    width: 100%;\n    border-radius: 8px;\n    display: flex;\n    justify-content: space-between;\n    align-items: center;\n    padding: 0 2rem;\n    overflow: hidden;\n  ;label:container;" + _emotionSourceMap2$1]))),
  upload: /*#__PURE__*/css(templateObject_3$1 || (templateObject_3$1 = __makeTemplateObject(["\n    background: transparent;\n    color: #eb417b;\n    z-index: 1;\n    top: 0;\n    padding: 0 2rem;\n  ;label:upload;" + _emotionSourceMap3$1], ["\n    background: transparent;\n    color: #eb417b;\n    z-index: 1;\n    top: 0;\n    padding: 0 2rem;\n  ;label:upload;" + _emotionSourceMap3$1]))),
  loading: /*#__PURE__*/css(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n    background: #ff059f;\n    color: white;\n    z-index: 2;\n    top: -100%;\n    position: relative;\n    padding: 0 2rem 0 3rem;\n  ;label:loading;" + _emotionSourceMap4], ["\n    background: #ff059f;\n    color: white;\n    z-index: 2;\n    top: -100%;\n    position: relative;\n    padding: 0 2rem 0 3rem;\n  ;label:loading;" + _emotionSourceMap4]))),
  loadingBar: /*#__PURE__*/css(templateObject_5 || (templateObject_5 = __makeTemplateObject(["\n    position: absolute;\n    bottom: 0;\n    left: 0;\n    width: 0%;\n    height: 3px;\n    background: white;\n  ;label:loadingBar;" + _emotionSourceMap5], ["\n    position: absolute;\n    bottom: 0;\n    left: 0;\n    width: 0%;\n    height: 3px;\n    background: white;\n  ;label:loadingBar;" + _emotionSourceMap5]))),
  done: /*#__PURE__*/css(templateObject_6 || (templateObject_6 = __makeTemplateObject(["\n    background: #2abb8b;\n    color: white;\n    z-index: 3;\n    top: -100%;\n    padding: 0 2rem 0 3.5rem;\n  ;label:done;" + _emotionSourceMap6], ["\n    background: #2abb8b;\n    color: white;\n    z-index: 3;\n    top: -100%;\n    padding: 0 2rem 0 3.5rem;\n  ;label:done;" + _emotionSourceMap6])))
};
var templateObject_1$3, templateObject_2$1, templateObject_3$1, templateObject_4, templateObject_5, templateObject_6;

var _emotionSourceMap3 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap2 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var _emotionSourceMap$2 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */";
var UploadButton = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var onUpload = props.onUpload,
    title = props.title,
    _a = props.width,
    width = _a === void 0 ? '162px' : _a,
    height = props.height,
    _b = props.boxShadow,
    boxShadow = _b === void 0 ? 'mediumLight' : _b,
    backgroundColor = props.backgroundColor;
  var inputRef = useRef(null);
  var _c = useState(),
    preview = _c[0],
    setPreview = _c[1];
  var _d = useState(false),
    isAnimating = _d[0],
    setIsAnimating = _d[1];
  var uploadControls = useAnimation();
  var loadingControls = useAnimation();
  var doneControls = useAnimation();
  var loadingBarControls = useAnimation();
  var startPreUploadAnimations = function () {
    return __awaiter(void 0, void 0, void 0, function () {
      return __generator(this, function (_a) {
        switch (_a.label) {
          case 0:
            uploadControls.start({
              zIndex: 1
            });
            return [4 /*yield*/, loadingControls.start({
              top: 0,
              transition: {
                duration: 0.3
              }
            })];
          case 1:
            _a.sent();
            loadingBarControls.start({
              width: '100%',
              transition: {
                duration: 1.35
              }
            });
            return [2 /*return*/];
        }
      });
    });
  };

  var startUploadAnimations = function () {
    return __awaiter(void 0, void 0, void 0, function () {
      return __generator(this, function (_a) {
        switch (_a.label) {
          case 0:
            uploadControls.start({
              top: '-100%',
              transition: {
                duration: 0
              }
            });
            return [4 /*yield*/, doneControls.start({
              top: 0,
              transition: {
                delay: 1.5,
                duration: 0.3
              }
            })];
          case 1:
            _a.sent();
            loadingControls.start({
              top: '-100%',
              transition: {
                duration: 0
              }
            });
            return [2 /*return*/];
        }
      });
    });
  };

  var startPostUploadAnimations = function () {
    return __awaiter(void 0, void 0, void 0, function () {
      return __generator(this, function (_a) {
        switch (_a.label) {
          case 0:
            loadingBarControls.start({
              width: '0%'
            });
            return [4 /*yield*/, uploadControls.start({
              top: 0,
              zIndex: 4,
              transition: {
                delay: 2,
                duration: 0.3
              }
            })];
          case 1:
            _a.sent();
            doneControls.start({
              top: '-100%',
              transition: {
                duration: 0
              }
            });
            return [2 /*return*/];
        }
      });
    });
  };

  var handleUpload = function (event) {
    return __awaiter(void 0, void 0, void 0, function () {
      var file, reader_1;
      return __generator(this, function (_a) {
        switch (_a.label) {
          case 0:
            event.preventDefault();
            if (event.target.files == null) {
              return [2 /*return*/];
            }

            file = event.target.files[0];
            if (!!!onUpload) return [3 /*break*/, 4];
            setIsAnimating(true);
            return [4 /*yield*/, startPreUploadAnimations()];
          case 1:
            _a.sent();
            onUpload(file);
            reader_1 = new FileReader();
            reader_1.onloadend = function () {
              setPreview(reader_1.result);
            };
            return [4 /*yield*/, startUploadAnimations()];
          case 2:
            _a.sent();
            reader_1.readAsDataURL(file);
            return [4 /*yield*/, startPostUploadAnimations()];
          case 3:
            _a.sent();
            setIsAnimating(false);
            return [3 /*break*/, 5];
          case 4:
            setPreview(null);
            _a.label = 5;
          case 5:
            return [2 /*return*/];
        }
      });
    });
  };

  useImperativeHandle(ref, function () {
    return {
      clearPreview: function () {
        setPreview(null);
      }
    };
  });
  return jsx(Div, {
    backgroundColor: backgroundColor !== null && backgroundColor !== void 0 ? backgroundColor : 'background',
    boxShadow: boxShadow,
    borderRadius: 'default',
    width: width,
    height: height,
    cursor: "pointer",
    onClick: function () {
      var _a;
      return !isAnimating && ((_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.click());
    },
    display: "grid",
    style: {
      placeItems: 'center center'
    }
  }, jsx(Div, {
    css: styles$2.wrapper,
    alignItems: "center",
    borderRadius: 'default'
  }, jsx("input", {
    type: "file",
    accept: "image/*",
    style: {
      display: 'none'
    },
    ref: inputRef,
    onChange: handleUpload
  }), jsx(Div, {
    width: "35px",
    height: "35px",
    display: "flex",
    justifyContent: "center",
    borderRadius: "default",
    alignItems: "center",
    backgroundColor: 'background',
    borderStyle: "solid",
    borderColor: "gray",
    borderWidth: "thin",
    inset: !preview ? 'x_5' : 'x0',
    zIndex: 5,
    position: "absolute",
    left: "2%"
  }, !preview ? jsx(Icon, {
    name: "upload",
    size: "xSmall",
    primaryColor: "primaryHeadingText"
  }) : jsx(Image$1, {
    width: "35px",
    height: "35px",
    isActive: true,
    image: {
      url: preview
    },
    backgroundSize: "cover"
  })), jsx(motion.div, {
    whileHover: "hover",
    animate: uploadControls,
    css: /*#__PURE__*/css(templateObject_1$2 || (templateObject_1$2 = __makeTemplateObject(["\n              ", ";\n              ", "\n            ;label:UploadButton;" + _emotionSourceMap$2], ["\n              ", ";\n              ", "\n            ;label:UploadButton;" + _emotionSourceMap$2])), styles$2.container, styles$2.upload)
  }, jsx(Text, {
    type: "caption",
    content: title,
    color: "primaryHeading",
    inset: {
      left: 'x1_5'
    },
    alignment: "center",
    whiteSpace: "nowrap"
  })), jsx(motion.div, {
    animate: loadingControls,
    css: /*#__PURE__*/css(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n              ", ";\n              ", "\n            ;label:UploadButton;" + _emotionSourceMap2], ["\n              ", ";\n              ", "\n            ;label:UploadButton;" + _emotionSourceMap2])), styles$2.container, styles$2.loading)
  }, jsx(LoadingSpinner, {
    hasNoColor: true,
    size: "xSmall"
  }), jsx(Text, {
    type: "caption",
    content: "Uploading",
    color: "inverseHeading",
    inset: {
      left: 'x1'
    },
    whiteSpace: "nowrap"
  }), jsx(motion.div, {
    className: "loading-bar",
    animate: loadingBarControls,
    css: styles$2.loadingBar
  })), jsx(motion.div, {
    animate: doneControls,
    css: /*#__PURE__*/css(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n              ", ";\n              ", "\n            ;label:UploadButton;" + _emotionSourceMap3], ["\n              ", ";\n              ", "\n            ;label:UploadButton;" + _emotionSourceMap3])), styles$2.container, styles$2.done)
  }, jsx(Text, {
    type: "caption",
    content: "Done",
    color: "primaryHeading",
    inset: {
      left: 'x1_5'
    },
    whiteSpace: "nowrap"
  }))));
});
var templateObject_1$2, templateObject_2, templateObject_3;

var ColorThumbnailLoader = function (_a) {
  var diameter = _a.diameter,
    outset = _a.outset;
  var radius = diameter / 2;
  return jsx(Flex, {
    outset: outset
  }, jsx(Loader, {
    width: diameter,
    height: diameter
  }, jsx("circle", {
    cy: radius,
    cx: radius,
    r: radius
  })));
};

var ColorThumbnail = function (_a) {
  var backgroundColor = _a.backgroundColor,
    isSelected = _a.isSelected,
    onSelect = _a.onSelect,
    _b = _a.thumbnailSize,
    thumbnailSize = _b === void 0 ? 32 : _b,
    outset = _a.outset,
    _c = _a.isRounded,
    isRounded = _c === void 0 ? false : _c,
    _d = _a.isLoading,
    isLoading = _d === void 0 ? false : _d;
  var selectedThumbnailSize = "calc(" + thumbnailSize + " / 2.75)";
  if (isLoading) {
    return jsx(ColorThumbnailLoader, {
      diameter: thumbnailSize,
      outset: outset
    });
  }
  return jsx(Flex, {
    onClick: function () {
      return onSelect(backgroundColor);
    },
    backgroundColor: backgroundColor,
    whileHover: {
      scale: 1.2
    },
    cursor: "pointer",
    borderRadius: isRounded ? 'circle' : 'small',
    width: thumbnailSize + "px",
    height: thumbnailSize + "px",
    minWidth: thumbnailSize + "px",
    outset: outset,
    justifyContent: "center",
    alignItems: "center"
  }, isSelected && jsx(Div, {
    backgroundColor: "foreground",
    width: selectedThumbnailSize,
    height: selectedThumbnailSize,
    borderRadius: isRounded ? 'circle' : '3px',
    boxShadow: "mediumLight"
  }));
};

var _emotionSourceMap$1 = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFHd0IiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgX19tYWtlVGVtcGxhdGVPYmplY3QgfSBmcm9tIFwidHNsaWJcIjtcclxuaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vY29yZSc7XHJcbmV4cG9ydCBkZWZhdWx0IHtcclxuICAgIHRodW1ibmFpbENvbnRhaW5lcjogY3NzKHRlbXBsYXRlT2JqZWN0XzEgfHwgKHRlbXBsYXRlT2JqZWN0XzEgPSBfX21ha2VUZW1wbGF0ZU9iamVjdChbXCJcIl0sIFtcIlwiXSkpKSxcclxufTtcclxudmFyIHRlbXBsYXRlT2JqZWN0XzE7XHJcbi8vIyBzb3VyY2VNYXBwaW5nVVJMPXN0eWxlcy5qcy5tYXAiXX0= */";
var styles$1 = {
  thumbnailContainer: /*#__PURE__*/css(templateObject_1$1 || (templateObject_1$1 = __makeTemplateObject([";label:thumbnailContainer;" + _emotionSourceMap$1], [";label:thumbnailContainer;" + _emotionSourceMap$1])))
};
var templateObject_1$1;

var OrderCard = function (_a) {
  var thumbnail = _a.thumbnail,
    title = _a.title,
    subtitle = _a.subtitle,
    price = _a.price,
    discountPrice = _a.discountPrice,
    _b = _a.backgroundColor,
    backgroundColor = _b === void 0 ? 'background' : _b;
  return jsx(Div, {
    minWidth: "280px",
    width: "100%",
    borderRadius: "small",
    backgroundColor: backgroundColor
  }, jsx(Div, {
    minHeight: "48px",
    inset: "x1",
    width: "100%",
    display: "flex",
    justifyContent: "space-between"
  }, jsx(Div, {
    display: "flex",
    alignItems: "center",
    css: styles$1.thumbnailContainer
  }, jsx(Div, {
    backgroundImage: thumbnail.image,
    backgroundPosition: "center",
    backgroundSize: "contain",
    backgroundRepeat: "no-repeat",
    borderRadius: 'small',
    width: thumbnail.orientation === 'portrait' ? '36px' : '60px',
    height: thumbnail.orientation === 'portrait' ? '48px' : '40px'
  }), jsx(Div, {
    inset: {
      left: 'x1'
    }
  }, jsx(InfoBlock, {
    caption: subtitle !== null && subtitle !== void 0 ? subtitle : '',
    heading: title,
    size: "small"
  }))), jsx(Span, {
    display: "flex",
    alignItems: "center",
    inset: {
      left: 'x1'
    }
  }, jsx(Text, {
    content: "" + price,
    weight: "semiBold",
    type: "footnote",
    color: "primaryBody",
    isStrikeThrough: discountPrice ? true : false
  }), discountPrice && jsx(Text, {
    inset: {
      left: 'x_5'
    },
    content: "" + discountPrice,
    type: "footnote",
    color: "primaryBody",
    weight: "semiBold"
  }))));
};

var SelectedOption = function (_a) {
  var title = _a.title,
    description = _a.description,
    option = _a.option,
    children = _a.children,
    removeSelectedOption = _a.removeSelectedOption;
  return jsx(Div, {
    width: "100%"
  }, removeSelectedOption && jsx(Div, {
    position: "absolute",
    top: "x3",
    left: "x3"
  }, jsx(IconLink, {
    icon: "chevron",
    iconOrientation: "left",
    title: "Back",
    size: "small",
    onClick: removeSelectedOption
  })), jsx(VStack, {
    gap: "x1"
  }, jsx(Text, {
    style: {
      lineHeight: 'initial'
    },
    type: "title",
    color: "primaryHeading",
    content: title,
    alignment: "center"
  }), jsx(Text, {
    type: "body",
    color: "primaryBody",
    content: description,
    alignment: "center"
  }), jsx(Spacer, {
    space: "x1"
  }), jsx(RadioOptionCard, {
    title: option.title,
    subtitle: option.subtitle,
    isFeatured: option.isFeatured,
    isSelected: true,
    optionId: option.optionId
  }), children));
};

var UpsaleSheet = function (_a) {
  var title = _a.title,
    description = _a.description,
    options = _a.options,
    isOpen = _a.isOpen,
    setIsOpen = _a.setIsOpen,
    backgroundElement = _a.backgroundElement,
    submitAction = _a.submitAction,
    extraLink = _a.extraLink,
    selectedOptionTitle = _a.selectedOptionTitle,
    selectedOptionDescription = _a.selectedOptionDescription,
    children = _a.children,
    defaultNumOptionsShown = _a.defaultNumOptionsShown,
    selectedOption = _a.selectedOption,
    removeSelectedOption = _a.removeSelectedOption,
    successComponent = _a.successComponent,
    _b = _a.zIndex,
    zIndex = _b === void 0 ? 103 : _b,
    // SOC Nav is 102
    width = _a.width,
    inset = _a.inset,
    outset = _a.outset;
  var _c = React.useState(''),
    expandedOption = _c[0],
    setExpandedOption = _c[1];
  var _d = useResizeObserver(),
    ref = _d.ref,
    childrenWrapperHeight = _d.height;
  var findOption = function (optionToFind) {
    return options.find(function (option) {
      return option.optionId === optionToFind;
    });
  };
  var activeSelectedOption = findOption(selectedOption);
  var activeExpandedOption = findOption(expandedOption);
  var height = useWindowSize().height;
  return jsx(Sheet, {
    isOpen: isOpen,
    backgroundElement: backgroundElement,
    setIsOpen: function () {
      return setIsOpen(true);
    },
    draggable: true,
    initialSnapPoint: "description",
    zIndex: zIndex,
    width: width,
    inset: inset,
    outset: outset
  }, successComponent ? jsx(Flex, {
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '641px',
    width: '100%'
  }, successComponent) : jsx(Div, {
    position: "static",
    width: "100%"
  }, selectedOption && activeSelectedOption ? jsx(SelectedOption, {
    title: selectedOptionTitle,
    description: selectedOptionDescription,
    option: activeSelectedOption,
    removeSelectedOption: removeSelectedOption
  }, children) : expandedOption && (activeExpandedOption === null || activeExpandedOption === void 0 ? void 0 : activeExpandedOption.expandedChild) ? jsx(Div, {
    width: "100%",
    height: "100%",
    borderRadius: {
      bottomLeft: 'default',
      topLeft: 'default'
    },
    overflowY: "auto",
    overflowX: "hidden",
    backgroundColor: "foreground"
  }, jsx(Span, {
    top: "6px",
    right: "5px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    inset: "x1",
    position: "absolute"
  }, jsx(Text, {
    type: "footnote",
    content: "Close",
    weight: "semiBold",
    style: {
      color: 'black'
    }
  })), jsx(CloseButton, {
    onClose: function () {
      return setExpandedOption('');
    },
    top: "8px",
    right: "52px",
    size: "xSmall",
    backgroundColor: {
      swatch: 'danger',
      shade: 'base'
    }
  }), activeExpandedOption.expandedChild) : jsx(Div, {
    ref: ref
  }, jsx(VStack, {
    gap: "x2"
  }, jsx(Text, {
    type: {
      xSmall: 'subtitle',
      small: 'title'
    },
    weight: "bold",
    color: "primaryHeading",
    content: title,
    alignment: "center"
  }), jsx(Spacer, {
    space: "x2"
  }), jsx(Text, {
    type: {
      xSmall: 'caption',
      small: 'body'
    },
    color: "primaryBody",
    content: description,
    alignment: "center"
  }), childrenWrapperHeight && childrenWrapperHeight >= height && jsx(SnapPoint, {
    name: "description"
  }), jsx(Spacer, {
    space: "x2"
  }), jsx(RadioOptionList, {
    options: options,
    submitAction: submitAction,
    defaultNumOptionsShown: defaultNumOptionsShown,
    globalInfoActionOverride: function (optionId) {
      return setExpandedOption(optionId);
    },
    extraLink: {
      title: extraLink.title,
      onClick: extraLink.onClick,
      href: extraLink.href && !extraLink.onClick ? extraLink.href : undefined
    }
  })))));
};

var CardEntryDialog = function (_a) {
  var _b, _c, _d, _e;
  var title = _a.title,
    description = _a.description,
    primaryAction = _a.primaryAction,
    secondaryAction = _a.secondaryAction,
    isOpen = _a.isOpen,
    onClose = _a.onClose;
  var device = useDeviceSize().device;
  return jsx(Div, null, jsx(Dialog, {
    onClose: onClose,
    isOpen: isOpen,
    maxWidth: {
      medium: '800px'
    }
  }, jsx(VStack, {
    gap: "x2"
  }, jsx(Text, {
    color: "primaryHeading",
    content: title,
    type: device === 'xSmall' ? 'subtitle' : 'title',
    weight: "bold",
    alignment: "center"
  }), jsx(Text, {
    color: "primaryBody",
    content: description,
    type: device === 'xSmall' ? 'caption' : 'body',
    weight: "regular",
    alignment: "center"
  }), jsx(Spacer, {
    space: "x2"
  }), jsx(HStack, {
    gap: device === 'xSmall' ? {
      bottom: 'x2'
    } : 'x3',
    justify: "center",
    shouldWrap: true,
    childWidth: device === 'xSmall' ? '100%' : 'initial'
  }, jsx(Button, {
    title: secondaryAction.title,
    onClick: secondaryAction.onClick,
    type: (_b = secondaryAction === null || secondaryAction === void 0 ? void 0 : secondaryAction.type) !== null && _b !== void 0 ? _b : 'shadow',
    outlined: (_c = secondaryAction === null || secondaryAction === void 0 ? void 0 : secondaryAction.outlined) !== null && _c !== void 0 ? _c : true,
    fullWidth: device === 'xSmall'
  }), jsx(Button, {
    title: primaryAction.title,
    onClick: primaryAction.onClick,
    type: (_d = primaryAction === null || primaryAction === void 0 ? void 0 : primaryAction.type) !== null && _d !== void 0 ? _d : 'secondary',
    outlined: (_e = primaryAction === null || primaryAction === void 0 ? void 0 : primaryAction.outlined) !== null && _e !== void 0 ? _e : false,
    fullWidth: device === 'xSmall'
  })))));
};

var AffiliateShareWidget = function (_a) {
  var title = _a.title,
    shareLink = _a.shareLink;
  return jsx(PromotionWidget, {
    capsuleTitle: "Share",
    title: title,
    type: "secondary",
    description: "",
    backgroundColor: "foreground"
  }, jsx(Clipboard, {
    copyText: shareLink
  }));
};

var StackedCardHead = function (_a) {
  var cardImage = _a.cardImage,
    cardOrientation = _a.cardOrientation,
    quantity = _a.quantity,
    quantityDescription = _a.quantityDescription,
    secondaryAction = _a.secondaryAction;
  return jsx(VStack, {
    gap: "x2"
  }, jsx(StackedCards, {
    width: cardOrientation === 'vertical' ? 200 : 285,
    height: cardOrientation === 'vertical' ? 285 : 200,
    stackBackgroundColor: "background",
    backgroundImage: cardImage
  }), secondaryAction && secondaryAction, jsx(Text, {
    type: "largeBody",
    weight: "bold",
    color: "primaryHeading",
    content: quantity + " " + quantityDescription,
    alignment: "center",
    style: {
      letterSpacing: '1px'
    },
    whiteSpace: "nowrap"
  }));
};

var CardQuantitySlider = function (props) {
  var cardOrientation = props.cardOrientation,
    cardImage = props.cardImage,
    quantityDescription = props.quantityDescription,
    secondaryAction = props.secondaryAction,
    rest = __rest(props, ["cardOrientation", "cardImage", "quantityDescription", "secondaryAction"]);
  return jsx(React.Fragment, null, jsx(QuantitySlider, _extends({}, rest, {
    head: jsx(StackedCardHead, {
      cardOrientation: cardOrientation,
      quantity: rest.quantity,
      quantityDescription: quantityDescription,
      cardImage: cardImage,
      secondaryAction: secondaryAction
    })
  })));
};

var BulkSend = function (_a) {
  var quantity = _a.quantity,
    setQuantity = _a.setQuantity,
    quantityDescription = _a.quantityDescription,
    minQuantity = _a.minQuantity,
    maxQuantity = _a.maxQuantity,
    sliderFill = _a.sliderFill,
    sliderWidth = _a.sliderWidth,
    unitPrice = _a.unitPrice,
    salePrice = _a.salePrice,
    title = _a.title,
    description = _a.description,
    configurationSections = _a.configurationSections,
    isActive = _a.isActive,
    setIsActive = _a.setIsActive,
    addDialog = _a.addDialog,
    removeDialog = _a.removeDialog,
    hasIncrement = _a.hasIncrement,
    isConfigurable = _a.isConfigurable,
    disableMessage = _a.disableMessage,
    sliderOrientation = _a.sliderOrientation,
    sliderImage = _a.sliderImage,
    secondaryAction = _a.secondaryAction;
  var _b = React.useState(false),
    isAddDialogOpen = _b[0],
    setIsAddDialogOpen = _b[1];
  var _c = React.useState(false),
    isRemoveDialogOpen = _c[0],
    setIsRemoveDialogOpen = _c[1];
  var clearBulkOption = function () {
    configurationSections.forEach(function (section) {
      return section.setActiveConfigurationId('');
    });
    setIsActive(false);
  };
  var handleSetRemoveDialog = function () {
    setIsRemoveDialogOpen(true);
  };
  var handleSetAddDialog = function () {
    setIsAddDialogOpen(true);
  };
  var overlayVariants = {
    open: {
      opacity: 0.5
    },
    closed: {
      opacity: 0
    }
  };
  return jsx(Flex, {
    inset: "x3",
    flexDirection: {
      xSmall: 'column',
      large: 'row'
    }
  }, isAddDialogOpen && addDialog ? jsx(ConfirmDialog, {
    title: addDialog === null || addDialog === void 0 ? void 0 : addDialog.title,
    description: addDialog.description,
    isOpen: true,
    accept: {
      title: addDialog.accept.title,
      onClick: function () {
        setIsActive(true);
        setIsAddDialogOpen(!isAddDialogOpen);
        addDialog.accept.onClick();
      }
    },
    decline: {
      title: 'Cancel',
      onClick: function () {
        var _a;
        setIsAddDialogOpen(!isAddDialogOpen);
        (_a = addDialog.decline) === null || _a === void 0 ? void 0 : _a.onClick();
      }
    },
    requireScroll: false
  }) : isRemoveDialogOpen && removeDialog && jsx(ConfirmDialog, {
    title: removeDialog === null || removeDialog === void 0 ? void 0 : removeDialog.title,
    description: removeDialog === null || removeDialog === void 0 ? void 0 : removeDialog.description,
    isOpen: true,
    accept: {
      title: removeDialog.accept.title,
      onClick: function () {
        clearBulkOption();
        removeDialog.accept.onClick();
        setIsRemoveDialogOpen(!isRemoveDialogOpen);
      }
    },
    decline: {
      title: 'Cancel',
      onClick: function () {
        var _a;
        (_a = removeDialog.decline) === null || _a === void 0 ? void 0 : _a.onClick();
        setIsRemoveDialogOpen(!isRemoveDialogOpen);
      }
    },
    requireScroll: false
  }), jsx(Flex, {
    width: {
      xSmall: '100%',
      large: '50%'
    },
    flexDirection: "column",
    outset: {
      top: {
        xSmall: 'x4',
        large: 'x0'
      }
    },
    order: {
      xSmall: 2,
      large: 0
    }
  }, jsx(AnimatePresence$1, null, !isConfigurable && jsx(Overlay, {
    variants: overlayVariants,
    initial: 'closed',
    animate: !isConfigurable ? 'open' : 'closed',
    exit: 'closed',
    position: "absolute",
    zIndex: 1
  })), jsx(Div, {
    outset: {
      bottom: 'x3'
    },
    position: "relative"
  }, jsx(AnimatePresence$1, null, !isConfigurable && jsx(Flex, {
    position: "absolute",
    top: "-32px",
    initial: {
      opacity: 0
    },
    animate: !isConfigurable ? {
      opacity: 1
    } : {
      opacity: 0
    },
    exit: {
      opacity: 0
    },
    transition: {
      delay: 0.3,
      duration: 0.3
    }
  }, jsx(Tooltip, {
    title: disableMessage,
    direction: "bottom",
    background: "light",
    zIndex: 2
  }))), jsx(Text, {
    type: {
      xSmall: 'largeBody',
      medium: 'subtitle',
      large: 'title'
    },
    weight: "bold",
    color: "primaryHeading",
    content: title
  }), jsx(Text, {
    type: {
      xSmall: 'caption',
      medium: 'body',
      large: 'largeBody'
    },
    content: description
  })), isActive && isConfigurable && jsx(React.Fragment, null, configurationSections.map(function (section, index) {
    return jsx(React.Fragment, null, jsx(Flex, {
      flexDirection: "column",
      maxWidth: "1200px",
      key: index
    }, jsx(Div, {
      outset: {
        bottom: 'x3'
      }
    }, section.tag && jsx(Text, {
      type: "caption",
      color: "secondaryBrand",
      weight: "semiBold",
      content: section.tag
    }), jsx(Text, {
      type: {
        xSmall: 'largeBody',
        medium: 'subtitle',
        large: 'title'
      },
      weight: "bold",
      color: "primaryHeading",
      content: section.title,
      lineHeight: 1.4,
      outset: {
        bottom: 'x1'
      }
    }), jsx(Text, {
      type: {
        xSmall: 'caption',
        medium: 'body',
        large: 'largeBody'
      },
      content: section.description
    })), jsx(VStack, {
      gap: "x1"
    }, section.configurationList.map(function (config, index) {
      return jsx(ConfigurationCard, {
        key: index,
        graphic: config.graphicType,
        title: config.configurationTitle,
        configurationId: config.configurationId,
        description: config.configurationDescription,
        onSelect: function (configurationId) {
          section.setActiveConfigurationId(configurationId);
        },
        isSelected: config.configurationId === section.activeConfigurationId,
        cost: config.configurationPrice,
        itemPriceTitle: config.configurationItemPriceTitle
      });
    })), index !== configurationSections.length - 1 && jsx(Div, {
      outset: {
        vertical: 'x2'
      }
    }, jsx(Separator, {
      orientation: "horizontal"
    }))));
  }))), jsx(Flex, {
    width: {
      xSmall: '100%',
      large: '50%'
    },
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
    order: {
      xSmall: 1,
      large: 0
    }
  }, jsx(CardQuantitySlider, {
    unitPrice: unitPrice,
    salePrice: salePrice,
    min: minQuantity,
    width: sliderWidth,
    max: maxQuantity,
    quantityDescription: quantityDescription,
    quantity: quantity,
    setQuantity: setQuantity,
    sliderFill: sliderFill,
    hasIncrement: hasIncrement,
    cardImage: sliderImage,
    cardOrientation: sliderOrientation,
    secondaryAction: secondaryAction
  }), jsx(Flex, {
    outset: {
      top: 'x4'
    }
  }, jsx(Button, {
    type: "secondary",
    size: "medium",
    title: isActive ? 'Remove Bulk Option' : 'Add Bulk Option',
    onClick: isActive && isConfigurable ? handleSetRemoveDialog : !isConfigurable ? undefined : handleSetAddDialog,
    fullWidth: true,
    outlined: true
  }))));
};

var _emotionSourceMap = process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInN0eWxlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFHNEIiLCJmaWxlIjoic3R5bGVzLnRzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IHsgX19tYWtlVGVtcGxhdGVPYmplY3QgfSBmcm9tIFwidHNsaWJcIjtcclxuaW1wb3J0IHsgY3NzIH0gZnJvbSAnQGVtb3Rpb24vY29yZSc7XHJcbmV4cG9ydCBkZWZhdWx0IHtcclxuICAgIHBhcGVyVHlwZUNhcmRDb250YWluZXI6IGNzcyh0ZW1wbGF0ZU9iamVjdF8xIHx8ICh0ZW1wbGF0ZU9iamVjdF8xID0gX19tYWtlVGVtcGxhdGVPYmplY3QoW1wiXFxuICAgIDo6LXdlYmtpdC1zY3JvbGxiYXIge1xcbiAgICAgIHdpZHRoOiAwcHg7XFxuICAgICAgYmFja2dyb3VuZDogdHJhbnNwYXJlbnQ7XFxuICAgIH1cXG4gIFwiXSwgW1wiXFxuICAgIDo6LXdlYmtpdC1zY3JvbGxiYXIge1xcbiAgICAgIHdpZHRoOiAwcHg7XFxuICAgICAgYmFja2dyb3VuZDogdHJhbnNwYXJlbnQ7XFxuICAgIH1cXG4gIFwiXSkpKSxcclxufTtcclxudmFyIHRlbXBsYXRlT2JqZWN0XzE7XHJcbi8vIyBzb3VyY2VNYXBwaW5nVVJMPXN0eWxlcy5qcy5tYXAiXX0= */";
var styles = {
  paperTypeCardContainer: /*#__PURE__*/css(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n    ::-webkit-scrollbar {\n      width: 0px;\n      background: transparent;\n    }\n  ;label:paperTypeCardContainer;" + _emotionSourceMap], ["\n    ::-webkit-scrollbar {\n      width: 0px;\n      background: transparent;\n    }\n  ;label:paperTypeCardContainer;" + _emotionSourceMap])))
};
var templateObject_1;

var PaperTypeModal = function (_a) {
  var title = _a.title,
    subtitle = _a.subtitle,
    isOpen = _a.isOpen,
    onClose = _a.onClose,
    zIndex = _a.zIndex,
    options = _a.options;
  var overlayVariants = {
    open: {
      opacity: 1
    },
    closed: {
      opacity: 0
    }
  };
  var isEven = options.length % 2 === 0;
  return jsx(AnimatePresence$1, null, isOpen && jsx(Overlay, {
    variants: overlayVariants,
    animate: isOpen ? 'open' : 'closed',
    initial: 'closed',
    zIndex: zIndex !== null && zIndex !== void 0 ? zIndex : 3,
    exit: 'closed'
  }, jsx(Flex, {
    maxWidth: "650px",
    maxHeight: "750px",
    minWidth: "320px",
    top: 0,
    left: 0,
    backgroundColor: "foreground",
    borderRadius: "default",
    flexDirection: "column",
    position: "fixed",
    inset: "x4"
  }, onClose && jsx(CloseButton, {
    onClose: onClose
  }), jsx(Flex, {
    flexWrap: "wrap",
    width: "100%",
    justifyContent: "center"
  }, jsx(Text, {
    type: "subtitle",
    weight: "bold",
    color: "primaryHeading",
    content: title
  }), jsx(Text, {
    type: "body",
    color: "primaryBody",
    content: subtitle,
    alignment: "left",
    lineHeight: 2
  })), jsx(Flex, {
    css: styles.paperTypeCardContainer,
    outset: {
      top: 'x2'
    },
    overflowY: "auto",
    width: "100%",
    inset: {
      horizontal: 'x1',
      vertical: 'x3'
    }
  }, jsx(Grid, {
    columnSize: 280,
    rowSize: 425,
    gap: 8
  }, options.map(function (option, index) {
    return jsx(GridItem, {
      key: index,
      gridRowSpan: 1,
      gridColSpan: !isEven && index === options.length - 1 ? 2 : 1
    }, jsx(Div, {
      style: {
        flexBasis: '100%'
      },
      flexShrink: 1
    }, jsx(DisplayCard, {
      key: index,
      title: option.title,
      description: option.description
    }, jsx(Image$1, {
      width: '100%',
      height: '100%',
      isActive: true,
      image: {
        url: option.image
      }
    }))));
  }))))));
};

var comparisonItems37 = [{
  title: '20 Monthly Cards (includes both Heartfelt and System Cards)',
  description: '$1.75 for each additional card over 20. Stamps not included on any System Card sends.',
  isIncluded: true
}, {
  title: 'Contact Manager',
  description: 'Store your contact information, birthdays, special occasions, reminders, include notes, and get a history of every card sent.',
  isIncluded: true
}, {
  title: '20% off gifts',
  description: 'Each gift that you add to greeting cards gets you even more savings!',
  isIncluded: true
}, {
  title: 'Unlimited Photo Postcards',
  description: 'Send Unlimited 4x6 postcards - stamps included, mailed out when you hit SEND!',
  isIncluded: false
}, {
  title: 'Unlimited Heartfelt Prompting cards',
  description: 'Send an unlimited amount of 5x7 greeting cards, also with stamps included. We stuff, stamp, and mail them for you immediately!',
  isIncluded: false
}, {
  title: '30% Savings on Shipping',
  description: 'Get even more savings when you ship your greeting card with a gift. We have you covered!',
  isIncluded: false
}];
var comparisonItems97 = [{
  title: 'UNLIMITED Photo Postcards!',
  description: 'Send Unlimited 4x6 postcards - stamps included, mailed out when you hit SEND!',
  isIncluded: true
}, {
  title: 'UNLIMITED Heartfelt Prompting Cards!',
  description: 'Send an unlimited amount of 5x7 greeting cards, also with stamps included. We stuff, stamp, and mail them for you immediately!',
  isIncluded: true
}, {
  title: 'Contact Manager',
  description: 'Store your contact information, birthdays, special occasions, reminders, include notes, and get a history of every card sent.',
  isIncluded: true
}, {
  title: '30% Discount on Gifts',
  description: 'Each gift that you add to greeting cards gets you even more savings!',
  isIncluded: true
}, {
  title: '30% Savings on Shipping',
  description: 'Get even more savings when you ship your greeting card with a gift. We have you covered!',
  isIncluded: true
}];
var comparisonItems10420 = [{
  title: '10 Card Credits can be Applied to:',
  description: 'Heartfelt Prompting Cards - stamps included',
  isIncluded: true
}, {
  title: 'Contact Manager',
  description: 'Store your contact information, birthdays, special occasions, reminders, include notes, and get a history of every card sent.',
  isIncluded: true
}, {
  title: 'Premium Storage',
  description: 'Premium storage allows you to save unlimited cards, pictures, campaigns, and contacts.',
  isIncluded: true
}];

var SubscriptionUpsaleDialog = function (_a) {
  var title = _a.title,
    description = _a.description,
    checkoutTitle = _a.checkoutTitle,
    checkoutDescription = _a.checkoutDescription,
    selectableOptions = _a.selectableOptions,
    upsaleContentTitle = _a.upsaleContentTitle,
    isOpen = _a.isOpen,
    setIsDialogOpen = _a.setIsDialogOpen,
    _b = _a.optionFormLocation,
    optionFormLocation = _b === void 0 ? 'right' : _b,
    children = _a.children,
    selectedOption = _a.selectedOption,
    removeSelectedOption = _a.removeSelectedOption,
    successComponent = _a.successComponent,
    insetOverride = _a.insetOverride,
    handleSelectedOption = _a.handleSelectedOption,
    sponsorId = _a.sponsorId;
    _a.upsaleContent;
    var rest = __rest(_a, ["title", "description", "checkoutTitle", "checkoutDescription", "selectableOptions", "upsaleContentTitle", "isOpen", "setIsDialogOpen", "optionFormLocation", "children", "selectedOption", "removeSelectedOption", "successComponent", "insetOverride", "handleSelectedOption", "sponsorId", "upsaleContent"]);
  var device = useWindowSize().width;
  return jsx("div", null, jsx(UpsaleDialog, _extends({
    sponsorId: sponsorId,
    title: title,
    description: description,
    checkoutTitle: checkoutTitle,
    checkoutDescription: checkoutDescription,
    selectableOptions: selectableOptions,
    selectedOption: selectedOption,
    handleSelectedOption: function (optionId) {
      return handleSelectedOption(optionId);
    },
    removeSelectedOption: removeSelectedOption,
    upsaleContentTitle: upsaleContentTitle,
    upsaleContent: jsx(ComparisonIncludesList, {
      isMinified: device <= 1017,
      items: selectedOption === '6' //this is the item code for the 37 subscription
      ? comparisonItems37 : selectedOption === '122' ? comparisonItems10420 : comparisonItems97
    }),
    optionFormLocation: optionFormLocation,
    setIsDialogOpen: function () {
      return setIsDialogOpen(!isOpen);
    },
    isOpen: isOpen,
    successComponent: successComponent,
    insetOverride: insetOverride,
    maxWidth: '1500px'
  }, rest), children));
};

var UpsaleOptions = function (_a) {
  var selectableOptions = _a.selectableOptions,
    selectedOption = _a.selectedOption,
    handleSelectedOption = _a.handleSelectedOption,
    removeSelectedOption = _a.removeSelectedOption,
    showCheckoutView = _a.showCheckoutView,
    handleShouldShowCheckoutView = _a.handleShouldShowCheckoutView,
    children = _a.children,
    activeSelectedOption = _a.activeSelectedOption,
    extraLink = _a.extraLink,
    _b = _a.defaultNumOptionsShown,
    defaultNumOptionsShown = _b === void 0 ? selectableOptions.length : _b,
    submitAction = _a.submitAction;
  var shouldShowCheckoutScreen = selectedOption && showCheckoutView;
  return jsx(Div, {
    height: "100%",
    position: "relative",
    width: "100%"
  }, jsx(Div, null, shouldShowCheckoutScreen && removeSelectedOption &&
  // Back Button
  jsx(Div, {
    outset: {
      top: 'x4'
    },
    position: "relative",
    bottom: "25px",
    right: "7px",
    height: "20px",
    cursor: "pointer",
    onClick: function () {
      removeSelectedOption();
      handleShouldShowCheckoutView(false);
    }
  }, jsx(HStack, {
    gap: "x_25",
    justify: "start",
    alignItems: "center"
  }, jsx(Icon, {
    name: "leftChevron",
    size: "small",
    primaryColor: "primaryBodyText"
  }), jsx(Text, {
    type: 'caption',
    weight: "semiBold",
    color: "primaryBody",
    content: "Back",
    alignment: "center",
    style: {
      position: 'relative',
      bottom: 4
    }
  })), jsx(Spacer, {
    space: "x2"
  })), jsx(VStack, {
    gap: "x2"
  }, shouldShowCheckoutScreen && activeSelectedOption ? jsx(Div, null, jsx(ConfigurationCard, _extends({
    description: activeSelectedOption.subtitle,
    configurationId: activeSelectedOption.optionId,
    type: "upsale"
  }, activeSelectedOption, {
    isSelected: true
  })), children) : jsx(VStack, {
    gap: "x2"
  }, selectableOptions.filter(function (_, index) {
    return index < defaultNumOptionsShown;
  }).map(function (option, index) {
    return jsx(ConfigurationCard, _extends({}, option, {
      key: index,
      type: "upsale",
      description: option.subtitle,
      configurationId: option.optionId,
      onSelect: function () {
        return handleSelectedOption(option.optionId);
      },
      isSelected: selectedOption === option.optionId
    }));
  })), extraLink && jsx(Anchor, {
    title: extraLink.title,
    onClick: extraLink.onClick,
    href: extraLink.href,
    size: "small",
    color: "primaryBody",
    outset: "0 auto"
  }), submitAction && jsx(Flex, {
    width: "100%",
    justifyContent: "center",
    alignItems: "center"
  }, jsx(Button, {
    title: submitAction.title,
    type: submitAction.type,
    onClick: submitAction.onClick,
    fullWidth: submitAction.isFullWidth,
    disabled: submitAction.isDisabled,
    size: "large"
  })))));
};

var SOC_FRONT_Z_INDEX = 103;
var UpsaleDialog = function (_a) {
  var _b;
  var title = _a.title,
    description = _a.description,
    checkoutTitle = _a.checkoutTitle,
    checkoutDescription = _a.checkoutDescription,
    selectableOptions = _a.selectableOptions,
    isOpen = _a.isOpen,
    setIsDialogOpen = _a.setIsDialogOpen,
    upsaleContent = _a.upsaleContent,
    children = _a.children,
    selectedOption = _a.selectedOption,
    handleSelectedOption = _a.handleSelectedOption,
    removeSelectedOption = _a.removeSelectedOption,
    insetOverride = _a.insetOverride,
    maxWidth = _a.maxWidth,
    maxHeight = _a.maxHeight,
    _c = _a.zIndex,
    zIndex = _c === void 0 ? SOC_FRONT_Z_INDEX : _c,
    //Soc-frontend
    successComponent = _a.successComponent,
    upsaleContentTitle = _a.upsaleContentTitle,
    _d = _a.sponsorId,
    sponsorId = _d === void 0 ? '9070' : _d,
    extraLink = _a.extraLink,
    defaultNumOptionsShown = _a.defaultNumOptionsShown,
    submitAction = _a.submitAction,
    acceptSelectedAction = _a.acceptSelectedAction,
    shouldHideSendFreeCard = _a.shouldHideSendFreeCard,
    upsaleBanner = _a.upsaleBanner,
    checkoutOnly = _a.checkoutOnly;
  var _e = React.useState(checkoutOnly !== null && checkoutOnly !== void 0 ? checkoutOnly : false),
    shouldShowCheckoutView = _e[0],
    setShouldShowCheckoutView = _e[1];
  var _f = useResizeObserver(),
    ref = _f.ref,
    width = _f.width,
    height = _f.height;
  var dialogFooterHeight = shouldHideSendFreeCard ? '120px' : '170px';
  var device = useWindowSize().width;
  var isMobile = device <= 910;
  var findOption = function (selectedOption) {
    return selectableOptions.filter(function (option) {
      return option.optionId === selectedOption;
    });
  };
  var activeSelectedOption = findOption(selectedOption);
  var handleOnClick = function () {
    setShouldShowCheckoutView(true);
    acceptSelectedAction === null || acceptSelectedAction === void 0 ? void 0 : acceptSelectedAction.onClick();
  };
  return jsx(Dialog, {
    ref: ref,
    isOpen: isOpen,
    onClose: function () {
      return setIsDialogOpen(!isOpen);
    },
    zIndex: zIndex,
    insetOverride: insetOverride,
    maxWidth: maxWidth,
    maxHeight: maxHeight,
    height: isMobile ? 'unset' : '80%',
    shouldScroll: true,
    width: "100%"
  }, successComponent ? jsx(Flex, {
    width: width + "px",
    height: height + "px",
    justifyContent: "center",
    alignItems: 'center'
  }, successComponent) : jsx(React.Fragment, null, jsx(Div, {
    width: "100%",
    inset: {
      bottom: 'x4',
      top: 'x8'
    }
  }, jsx(Text, {
    type: "subtitle",
    weight: "bold",
    content: shouldShowCheckoutView ? checkoutTitle : title,
    alignment: "center"
  }), jsx(Text, {
    type: "body",
    content: shouldShowCheckoutView ? checkoutDescription : description,
    alignment: "center"
  })), jsx(Scrollable, null, jsx(Flex, {
    justifyContent: 'center',
    alignItems: 'flex-start',
    width: '100%',
    inset: device <= 1367 && device > 580 ? {
      top: 'x4',
      horizontal: 'x8',
      bottom: dialogFooterHeight
    } : device <= 580 ? {
      top: 'x4',
      horizontal: 'x4',
      bottom: dialogFooterHeight
    } : {
      top: 'x4',
      horizontal: '10rem',
      bottom: dialogFooterHeight
    },
    flexWrap: "wrap",
    overflowY: "hidden",
    flexDirection: isMobile ? 'column' : 'row'
  }, jsx(Flex, {
    flexWrap: "wrap",
    inset: {
      top: 'x3',
      horizontal: 'x3'
    },
    overflowY: "auto",
    width: isMobile || !upsaleContent ? '100%' : 'calc(50% - 12px)',
    order: 3
  }, jsx(UpsaleOptions, {
    selectableOptions: selectableOptions,
    selectedOption: selectedOption,
    handleSelectedOption: function (optionId) {
      return handleSelectedOption(optionId);
    },
    removeSelectedOption: removeSelectedOption,
    showCheckoutView: shouldShowCheckoutView,
    handleShouldShowCheckoutView: function (value) {
      return setShouldShowCheckoutView(value);
    },
    activeSelectedOption: activeSelectedOption[0],
    extraLink: {
      title: extraLink === null || extraLink === void 0 ? void 0 : extraLink.title,
      onClick: extraLink === null || extraLink === void 0 ? void 0 : extraLink.onClick,
      href: extraLink === null || extraLink === void 0 ? void 0 : extraLink.href
    },
    defaultNumOptionsShown: defaultNumOptionsShown,
    submitAction: submitAction
  }, children), !shouldShowCheckoutView && jsx(Flex, {
    justifyContent: "center",
    position: isMobile ? 'fixed' : 'relative',
    bottom: isMobile ? '0' : 'unset',
    height: "100px",
    alignItems: "center",
    width: "100%",
    backgroundColor: isMobile ? 'foreground' : undefined,
    boxShadow: isMobile ? 'mediumDark' : undefined,
    left: "0",
    zIndex: isMobile ? 10 : undefined
  }, jsx(Button, {
    type: 'primary',
    size: "large",
    fullWidth: true,
    title: (_b = acceptSelectedAction === null || acceptSelectedAction === void 0 ? void 0 : acceptSelectedAction.title) !== null && _b !== void 0 ? _b : 'Continue to Checkout',
    disabled: !selectedOption.length,
    onClick: handleOnClick
  })), upsaleBanner && jsx(Div, {
    width: "100%"
  }, upsaleBanner)), !isMobile && jsx(Div, {
    width: "x3",
    order: 2
  }), upsaleContent && jsx(Flex, {
    overflowY: "auto",
    width: isMobile ? '100%' : 'calc(50% - 12px)',
    order: 1
  }, jsx(Div, {
    width: "100%",
    inset: {
      top: '1.4rem'
    }
  }, jsx(Accordion, {
    defaultActiveKeys: [1],
    arrowLocation: "right",
    background: "background",
    inset: "x0",
    isExclusive: true,
    sections: [{
      body: jsx(React.Fragment, null, upsaleContent),
      heading: jsx(Div, {
        inset: isMobile ? {
          left: '4.1rem'
        } : {
          left: 'x4'
        },
        width: "100%",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center"
      }, jsx(Text, {
        weight: "bold",
        type: "caption",
        content: upsaleContentTitle
      }), jsx(Div, {
        width: "34px",
        height: "34px",
        borderRadius: "small",
        backgroundSize: "cover",
        backgroundPosition: "center",
        outset: {
          right: 'x2'
        }
      })),
      key: 1
    }]
  }))))), !shouldHideSendFreeCard && jsx(Div, {
    backgroundColor: "foreground",
    width: device <= 1367 ? '100%' : 'calc(100% - 10rem)',
    position: isMobile ? 'relative' : 'absolute',
    bottom: isMobile ? dialogFooterHeight : 'x0',
    inset: isMobile ? 'x1' : {
      bottom: 'x4'
    },
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: !isMobile ? SOC_FRONT_Z_INDEX : undefined
  }, jsx(Separator, {
    orientation: "horizontal",
    caption: "Want to try it first?",
    shouldCaptionWrap: false
  }), jsx(Flex, {
    width: "100%",
    justifyContent: "center",
    alignItems: "center",
    inset: {
      top: 'x2_5'
    }
  }, jsx(Button, {
    type: "secondary",
    outlined: true,
    hover: true,
    size: "medium",
    title: "Send a Free Card",
    onClick: function () {
      return window.open("https://www.sendoutcards.com/" + sponsorId + "/send/");
    }
  })))));
};

var useColorSwatch = function () {
  var hocs = useEntities().hocs;
  var colors = hocs.colors;
  var _a = colors.swatches,
    primaryBrand = _a.primaryBrand,
    secondaryBrand = _a.secondaryBrand,
    success = _a.success,
    danger = _a.danger,
    warning = _a.warning,
    accent = _a.accent,
    grayScale = _a.grayScale,
    blueScale = _a.blueScale,
    purpleScale = _a.purpleScale;
  return {
    primaryBrand: primaryBrand,
    secondaryBrand: secondaryBrand,
    success: success,
    danger: danger,
    warning: warning,
    accent: accent,
    grayScale: grayScale,
    blueScale: blueScale,
    purpleScale: purpleScale
  };
};

export { Accordion, AccordionBody, AccordionHeading, AccountMenu, AffiliateShareWidget, Anchor, AnimatedCheckMark, AnimatedGrid, ArticleCard, Aside, Avatar, AwardCard, AwardGroupLabel, Badge, Banner, Bar, BlockQuote, BulkSend, Button, ButtonWithMenu, Capsule, Card, CardEntryDialog, CardQuantitySlider, Checkbox, Circle, CircleMenu, Clipboard, CloseButton, ColorOption, ColorThumbnail, ComparisonIncludesList, ComparisonItem, ConfigurationCard, ConfirmDialog, Content, ContentPusher, ContextBar, CountdownTimer, Dialog, DisplayCard, Div, DownloadButton, DragBar, DragBox, Drawer, DropDownMenu, ExpandedAwardCard, FeatureAnnouncement, FeatureBadge, Flex, Grid, GridItem, HStack, Icon, IconLink, Image$1 as Image, ImageGrid, ImageThumbnail, ImageUploadWidget, ImageUploader, IncrementSetting, InfiniteScroller, InfoBlock, Input, InviteCard, LinkColumn, List, ListItem, Loader, LoadingSpinner, Logo, MasonryGrid, Menu, MenuItem, MenuSection, Navigation, NavigationPill, Option, OrderCard, Overlay, Page, PaperTypeModal, PhoneInput, Pill, PinInput, PricingTile, Progress, PromotionWidget, QDSChrome, QuantitySlider, QuantumColorPicker, QuantumThemeWrapper, QuickLink, RadioOptionCard, RadioOptionList, ReleaseNote, ReleaseNoteDialog, RevealingContent, RowItem, Scrollable, ScrollingIndicator, Section, Select, SelectableCard, SelectableList, Separator, SettingInput, Sheet, Sidebar, Slider, SnapPoint, Spacer, Span, Sphere, StackedCards, StaticNavigation, Stepper, Stoplight, SubNavigation, SubscriptionUpsaleDialog, Tab, Table, TableRow, Tabs, Text, TitledList, Todo, Toggle, Tooltip, Transition, UploadButton, UpsaleDialog, UpsaleSheet, UserCard, UserCardLoader, VStack, VideoPlayer, Zoom, entities$6 as bellaCadaeuxTheme, entities$5 as blue42Theme, getSize, entities$7 as greenerStillTheme, entities$4 as promptingUTheme, entities as promptingsTheme, entities$1 as sendOutCardsTheme, entities$8 as sendogoTheme, entities$3 as streamMarketingTheme, entities$2 as streamVATheme, useColorSwatch };
